// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { esModuleTips, esFrameworkRequirements } from './es-extras.js';
export const es = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Inicie sesión en su cuenta',
        email: 'Correo electrónico',
        password: 'Contraseña',
        sign_in: 'Iniciar sesión',
        signing_in: 'Iniciando sesión...',
        mfa_code: 'Código MFA',
        mfa_placeholder: 'Introduzca el código de 6 dígitos',
        mfa_instruction: 'Introduzca el código de 6 dígitos de su aplicación de autenticación.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Verificar',
        verifying: 'Verificando...',
        forgot_password: '¿Olvidó su contraseña?',
        account_locked: 'La cuenta está bloqueada. Inténtelo de nuevo más tarde.',
        account_deactivated: 'La cuenta ha sido desactivada. Contacte con su administrador.',
        invalid_mfa: 'Código MFA no válido. Inténtelo de nuevo.',
        invalid_credentials: 'Correo electrónico o contraseña no válidos.',
        login_failed: 'Error de inicio de sesión',
        session_limit_reached: 'Tu cuenta ha alcanzado el límite de sesiones activas. Cierra sesión en otro dispositivo o pide al administrador que revoque una sesión existente y vuelve a intentarlo.',
        session_expired: 'Su sesión ha expirado. Recargue la página e inténtelo de nuevo.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Contraseña olvidada',
            subtitle: 'Introduzca su dirección de correo electrónico y le enviaremos un enlace para restablecer su contraseña.',
            send: 'Enviar enlace de restablecimiento',
            sending: 'Enviando...',
            success: 'Si el correo electrónico existe, se ha enviado un enlace para restablecer la contraseña.',
            back_to_login: 'Volver al inicio de sesión',
        },
        reset_password: {
            title: 'Restablecer contraseña',
            subtitle: 'Introduzca su nueva contraseña a continuación.',
            new_password: 'Nueva contraseña',
            confirm_password: 'Confirmar contraseña',
            reset: 'Restablecer contraseña',
            resetting: 'Restableciendo...',
            success: 'Su contraseña se ha restablecido correctamente.',
            go_to_login: 'Ir al inicio de sesión',
            password_mismatch: 'Las contraseñas no coinciden.',
            invalid_token: 'Token de restablecimiento no válido o ausente. Solicite un nuevo enlace de restablecimiento de contraseña.',
            requirements_title: 'Requisitos de la contraseña:',
            password_requirements: 'La contraseña debe cumplir los siguientes requisitos:',
            rules: {
                min_length: 'Al menos 12 caracteres',
                uppercase: 'Al menos una letra mayúscula',
                lowercase: 'Al menos una letra minúscula',
                digit: 'Al menos un dígito',
                special: 'Al menos un carácter especial',
            },
        },
        verify_email: {
            title: 'Verificación de correo electrónico',
            verifying: 'Verificando su dirección de correo electrónico...',
            success: 'Su correo electrónico se ha verificado correctamente.',
            invalid_token: 'Token de verificación no válido o ausente.',
            failed: 'La verificación del correo electrónico ha fallado. El token puede no ser válido o haber caducado.',
            go_to_login: 'Ir al inicio de sesión',
        },
        offline: {
            title: 'Sin conexión',
            message: 'Parece que ha perdido su conexión a Internet. Compruebe su red e inténtelo de nuevo.',
        },
    },
    intake: {
        title: 'Solicitud de derechos de datos',
        subtitle: 'Envíe una solicitud de derechos de datos a nuestra organización',
        fields: {
            full_name: 'Nombre completo',
            email: 'Dirección de correo electrónico',
            phone: 'Número de teléfono (opcional)',
            request_type: 'Tipo de solicitud',
            description: 'Describa su solicitud',
            preferred_language: 'Idioma preferido',
            consent: 'Doy mi consentimiento para el tratamiento de esta solicitud',
        },
        submit: 'Enviar solicitud',
        validation: {
            name_required: 'El nombre completo es obligatorio',
            email_required: 'Se requiere una dirección de correo electrónico válida',
            type_required: 'Por favor, seleccione un tipo de solicitud',
            description_required: 'Por favor, describa su solicitud',
            consent_required: 'Debe dar su consentimiento para el tratamiento para continuar',
        },
        error_title: 'error',
        config_load_error: 'No se pudo cargar la configuración. Por favor, verifique la URL.',
        organization_label: 'Organización',
        customer_reference: 'Número de referencia del cliente',
        country: 'País / Jurisdicción',
        preferred_channel: 'Canal de respuesta preferido',
        channels: {
            email: 'Correo electrónico',
            postal: 'Correo postal',
            phone: 'Teléfono',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Solicitud recibida',
            message: 'Su solicitud ha sido registrada correctamente. Su número de referencia es:',
            next_steps: 'Tramitaremos su solicitud lo antes posible de conformidad con la legislación aplicable. El plazo estimado de tramitación es de 30 días hábiles.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Acceso (DSAR)',
        deletion: 'Supresión',
        correction: 'Rectificación / Actualización',
        objection: 'Oposición',
        portability: 'Portabilidad de datos',
        restriction: 'Limitación del tratamiento',
        consent_withdrawal: 'Retirada del consentimiento',
        protective_measures: 'Solicitar medidas y soluciones de protección de datos personales',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Nuevo',
        in_review: 'En revisión',
        waiting_on_requester: 'Pendiente del solicitante',
        waiting_on_internal_data: 'Pendiente de datos internos',
        approved: 'Aprobado',
        closed: 'Cerrado',
        rejected: 'Rechazado',
    },
    admin: {
        nav: {
            rights_requests: 'Solicitudes de derechos',
            templates: 'Plantillas',
            response_templates: 'Plantillas de Respuesta',
            data_mapping: 'Registro de actividades de tratamiento',
            governance: 'Gobernanza',
            consent: 'Consentimiento',
            assessments: 'Evaluaciones',
            classification: 'Clasificación',
            monitoring: 'Supervisión',
            incidents: 'Incidentes',
            onboarding: 'Incorporación',
            connectors: 'Conectores',
            vendor_risk: 'Riesgo de proveedores',
            deletion_orchestration: 'Orquestación de supresión',
            discovery: 'Descubrimiento de datos',
            incident_ops: 'Operaciones de incidentes',
            program_governance: 'Gobernanza del programa',
            access_accountability: 'Responsabilidad de acceso',
            consent_properties: 'Propiedades del consentimiento',
            localization: 'Localización',
            regulatory: 'Regulatorio',
            partner_workspace: 'Espacio de socios',
            audit_trail: 'Pista de auditoría',
            forseti: 'Forseti',
            settings: 'Configuración',
            help_center: 'Centro de ayuda',
            dashboard: 'Panel de control',
            platform: 'Plataforma',
            integrations: 'Integraciones',
            cmp_dsar_sync: 'Sincronización CMP DSAR',
            cmp_integration: 'Integración CMP',
            purpose_mapping: 'Asignación de finalidades',
            webhook_config: 'Configuración de webhooks',
            bulk_sync: 'Sincronización masiva',
            privacy_scanner: 'Escáner de privacidad',
            privacy_monitoring: 'Supervisión de privacidad',
            real_time_scanner: 'Escáner en tiempo real',
            compliance_forms: 'Formularios de cumplimiento',
            forms_dashboard: 'Panel de formularios',
            form_templates: 'Plantillas',
            form_catalog: 'Catálogo de formularios',
            pack_catalog: 'Catálogo de paquetes',
            org_groups: 'Grupos organizativos',
            consolidation: 'Consolidación',
            organizations: 'Organizaciones',
            org_list: 'Todas las organizaciones',
            intake_configs: 'Configuraciones de recepción',
            task_management: 'Gestión de tareas',
            unified_audit: 'Auditoría Unificada',
            evidence_packs: 'Paquetes de Evidencia',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Períodos de Auditoría',
            regulatory_updates: 'Actualizaciones Regulatorias',
            departments: 'Departamentos',
        },
        sidebar_category_data_privacy_rights: 'Privacidad de Datos & Derechos',
        sidebar_category_vendor_third_party: 'Gestión de Proveedores & Terceros',
        sidebar_category_risk_assessment: 'Riesgo & Evaluación',
        sidebar_category_operations_automation: 'Operaciones & Automatización',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Gestión de Tareas & Flujo de Trabajo',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP y Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Registro de actividades',
            data_mapping_classification: 'Clasificación',
            data_mapping_governance: 'Gobernanza',
            access_live_monitoring: 'Monitorización en vivo',
            access_reviews_investigations: 'Revisiones e Investigaciones',
            access_evidence_policies: 'Evidencias y Políticas',
            classification_taxonomies: 'Taxonomías y Etiquetas',
            classification_workflow: 'Flujo de trabajo',
            classification_insights: 'Análisis',
            connectors_runtime: 'Tiempo de ejecución',
            connectors_due_diligence: 'Debida diligencia',
            connectors_coverage: 'Cobertura',
            cp_definitions: 'Definiciones',
            cp_lifecycle: 'Ciclo de vida',
            cp_quality: 'Calidad y Evidencias',
            deletion_execution: 'Ejecución',
            deletion_vendors: 'Proveedores',
            deletion_evidence: 'Evidencias e Integración',
            discovery_rules: 'Reglas y Plantillas',
            discovery_scanning: 'Escaneo',
            discovery_review: 'Flujo de revisión',
            discovery_insights: 'Análisis',
            governance_oversight: 'Supervisión',
            governance_reporting: 'Informes',
            governance_vendors: 'Proveedores y Transferencias internacionales',
            governance_lifecycle: 'Ciclo de vida',
            incident_ops_dashboards: 'Paneles',
            incident_ops_sla: 'Configuración SLA',
            incident_ops_playbooks: 'Libros de jugadas',
            incident_ops_vendor_regulator: 'Proveedor y Autoridad',
            incident_ops_evidence: 'Evidencias y Revisión',
            incidents_workload: 'Carga de trabajo',
            incidents_regulatory: 'Regulatorio',
            incidents_reporting: 'Informes',
            localization_content: 'Contenido',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observabilidad',
            monitoring_metrics: 'Métricas',
            monitoring_programs: 'Programas',
            monitoring_tasks: 'Tareas',
            monitoring_controls: 'Controles',
            org_entities: 'Entidades',
            org_config: 'Configuración',
            org_frameworks: 'Marcos y Consolidación',
            partner_overview: 'Vista general',
            partner_partners: 'Socios',
            partner_delivery: 'Entrega',
            partner_support: 'Soporte',
            platform_identity: 'Identidad y Roles',
            platform_access_control: 'Control de acceso',
            platform_auth_session: 'Autenticación y Sesión',
            platform_access_reviews: 'Revisiones de acceso',
            platform_files: 'Archivos',
            platform_notifications: 'Notificaciones',
            platform_module_config: 'Configuración de módulos',
            platform_ops: 'Operación y Registros',
            pg_health: 'Estado del programa',
            pg_maturity: 'Madurez',
            pg_audit: 'Auditoría y Evidencias',
            pg_training: 'Formación',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Obligaciones',
            pg_documents: 'Documentos',
            vr_overview: 'Vista general',
            vr_onboarding: 'Incorporación',
            vr_assessment: 'Evaluación',
            vr_oversight: 'Supervisión',
        },
        tickets: {
            title: 'Solicitudes de derechos',
            create: 'Crear solicitud',
            filters: 'Filtros',
            search_placeholder: 'Search tickets...',
            export_csv: 'Exportar CSV',
            exporting: 'Exportando...',
            bulk_export_failed: 'Error en la exportación masiva',
            bulk_export: {
                label: 'Exportar',
                format: {
                    pdf: 'PDF Resumen',
                    zip: 'ZIP (paquetes de evidencia por caso)',
                    csv: 'CSV',
                },
                warning_over_50: 'Esto generará aproximadamente {count} paquetes de cajas; puede tardar varios minutos. ¿Continuar?',
                blocked_over_500: 'Limite el filtro a 500 casos o menos antes de exportar ZIP (actual: {count}).',
                success: 'Exportación masiva guardada en el Explorador de archivos ({count} casos)',
                failed: 'Error en la exportación masiva',
                continue: 'Continuar',
                cancel: 'Cancelar',
            },
            no_results: 'No se encontraron solicitudes',
            ticket_id: 'ID del ticket',
            request_type: 'Tipo de solicitud',
            requester: 'Solicitante',
            source: 'Fuente',
            all_sources: 'Todas las fuentes',
            assigned_to: 'Asignado a',
            created: 'Creado',
            load_error: 'Error al cargar los casos. Por favor, inténtelo de nuevo.',
            case_creation_failed: 'Error al crear el caso',
            business_unit: 'Unidad de negocio',
            reference: 'Referencia',
            sla: {
                title: 'Estado SLA',
                on_track: '{days} días restantes',
                approaching: 'Plazo próximo',
                overdue: '{days} días de retraso',
                ack_overdue: '{hours}h pasado el plazo de acuse de recibo',
                ack_warning: 'Acuse de recibo pronto vencido',
                closed: 'Cerrado',
                completed_in: 'Completado en {days} días',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Plantillas de respuesta',
            new: 'Nueva plantilla',
            edit: 'Editar plantilla',
            all_types: 'Todos los tipos',
            all_locales: 'Todos los idiomas',
            type_access: 'Acceso/DSAR',
            type_deletion: 'Supresión',
            type_correction: 'Rectificación',
            type_objection: 'Oposición',
            type_portability: 'Portabilidad',
            type_other: 'Otro',
            type_restriction: 'Restricción',
            type_all: 'Todos',
            locale_en: 'Inglés',
            locale_vi: 'Vietnamita',
            locale_all: 'Ambos',
            name: 'Nombre',
            request_type: 'Tipo de solicitud',
            locale: 'Idioma',
            subject: 'Asunto',
            body: 'Cuerpo',
            name_placeholder: 'Nombre de la plantilla...',
            subject_placeholder: 'Asunto del correo/carta...',
            body_placeholder: 'Cuerpo de la plantilla. Use {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} como marcadores de posición.',
            available_placeholders: 'Marcadores de posición disponibles:',
            create_template: 'Crear plantilla',
            preview: 'Vista previa',
            close_preview: 'Cerrar vista previa',
            default_badge: 'Predeterminada',
            no_templates: 'No se encontraron plantillas',
            confirm_delete: '¿Eliminar esta plantilla?',
            load_error: 'Error al cargar las plantillas. Por favor, inténtelo de nuevo.',
            create_error: 'Error al crear la plantilla',
            update_error: 'Error al actualizar la plantilla',
            delete_error: 'Error al eliminar la plantilla',
            preview_error: 'Error al previsualizar la plantilla',
            response_templates_badge: 'Plantillas de Respuesta',
            browse_form_templates: 'Explorar Plantillas de Formularios de Cumplimiento',
            templates_loaded: 'Plantillas cargadas',
            sample_organization: 'Organización de ejemplo',
        },
        detail: {
            requester_info: 'Información del solicitante',
            status: 'Estado',
            assignment: 'Asignación',
            notes: 'Notas',
            audit_trail: 'Pista de auditoría',
            add_note: 'Agregar Note',
            case_not_found: 'Caso no encontrado',
            load_error: 'Error al load case. Por favor, inténtelo de nuevo.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Error al add note. Por favor, inténtelo de nuevo.',
            submitted_from: 'Enviado desde',
            via_cmp: 'vía AesirX CMP',
            cmp_ticket: 'Ticket CMP:',
            select_template: 'Seleccionar una plantilla',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detalles',
                evidence: 'Evidencia',
                communications: 'Comunicaciones',
                escalations: 'Escalamientos',
                audit_trail: 'Pista de auditoría',
                fulfillment: 'Cumplimiento',
                response: 'Respuesta',
                extension: 'Extensión',
            },
            requester: {
                name: 'Nombre',
                email: 'Correo electrónico',
                phone: 'Teléfono',
                language: 'Idioma',
                source: 'Fuente',
                customer_ref: 'Ref. del cliente',
                country: 'País',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Canal preferido',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'En Rastrear',
                due_soon: 'Próximo a vencer',
                overdue: 'Vencido',
                days_remaining: 'd restantes',
                due_date: 'Fecha de vencimiento',
                breached: 'Violado',
                paused: 'Pausado',
                total_paused_days: 'Total de días en pausa',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Días transcurridos',
                progress: 'Progreso',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identidad Verificación',
                verified: 'Verificado',
                not_verified: 'No verificado',
                method: 'Método',
                notes: 'Notas',
                verified_by: 'Verificado Por',
                verified_at: 'Verificado En',
                warning: 'Identidad no aún verificado.',
                verify_button: 'Verificar Identidad',
                select_method: '-- Seleccionar --',
                methods: {
                    email_confirmation: 'Confirmación por correo electrónico',
                    id_document: 'ID Documento',
                    phone_call: 'Llamada telefónica',
                    in_person: 'En persona',
                    other: 'Otro',
                },
            },
            closure: {
                reason: 'Motivo de cierre',
                summary: 'Resumen de cierre',
                summary_placeholder: 'Resumen de cómo el request fue resuelto...',
                reason_required: 'El resumen y el motivo de cierre son obligatorios',
                response_not_sent: 'Se debe enviar una respuesta formal al interesado antes de cerrar este caso',
                reasons: {
                    fulfilled: 'Cumplido',
                    partially_fulfilled: 'Parcialmente cumplido',
                    unfounded: 'Infundado',
                    excessive: 'Excesivo',
                    withdrawn: 'Retirado',
                    duplicate: 'Duplicar',
                    identity_unverified: 'Identidad No verificado',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exportar',
            export_pdf: 'Exportar PDF',
            export_zip: 'Exportar ZIP',
            case_file_pdf: 'Exportar PDF (Archivo de caso)',
            evidence_pack_zip: 'Exportar ZIP (Paquete de evidencia)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Marcar como causante de infracción',
                modal: {
                    title: 'Marcar caso como causante de infracción',
                    type_label: 'Tipo de infracción',
                    type: {
                        misdirected_response: 'Respuesta mal dirigida',
                        unauthorised_disclosure: 'Divulgación no autorizada',
                        identity_mismatch: 'Falta de coincidencia de identidad',
                        other: 'Otro',
                    },
                    description: 'Descripción (opcional)',
                    description_placeholder: '¿Qué pasó? (máximo 1000 caracteres)',
                    confirm: 'Marcar e iniciar el reloj de notificación',
                    cancel: 'Cancelar',
                    flagging: 'Marcando...',
                },
                banner: {
                    title: 'Infracción marcada: {type}',
                    detected_at: 'Detectado en {timestamp}',
                    filing_deadline: 'Plazo de notificación: en {hours} horas',
                    overdue: 'Plazo de notificación: VENCIDO',
                },
                toasts: {
                    flagged: 'Caso marcado como causante de infracción: se inició el reloj de notificación',
                    flag_failed: 'No se pudo marcar el caso como causante de una infracción',
                },
            },
            export: {
                generating: 'Generando... por favor espera',
                success: 'Exportar guardado al Explorador de archivos',
                failed: 'Error al exportar: verifique el registro de auditoría',
            },
            no_notes: 'Sin notas aún',
            assign_placeholder: 'ID del usuario a asignar...',
            reassign: 'Reasignar',
            evidence: {
                checklist_title: 'Lista de verificación de evidencias',
                complete_pct: '% completado',
                no_checklist: 'Sin elementos de lista',
                attachments: 'Adjuntos',
                upload_attachment: 'Subir adjunto',
                upload_button: 'Subir',
                uploading: 'Subiendo...',
                attachment_uploaded: 'Adjunto subido correctamente',
                upload_error: 'Error al subir el adjunto',
                category: 'Categoría',
                categories: {
                    identity_proof: 'Prueba de identidad',
                    correspondence: 'Correspondencia',
                    internal_data: 'Datos internos',
                    legal: 'Legales',
                    other: 'Otro',
                },
                no_attachments: 'Sin adjuntos',
                references_title: 'Referencias de evidencias',
                no_references: 'Sin referencias',
                add_reference: 'Agregar Reference',
                ref_type: 'Tipo',
                ref_value: 'Valor',
                ref_desc: 'Descripción',
                ref_value_placeholder: 'URL, ID de correo electrónico o referencia...',
                ref_desc_placeholder: 'Descripción...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Correo electrónico ID',
                    crm_record: 'Registro de CRM',
                    ticket_ref: 'Referencia del ticket',
                    other: 'Otro',
                },
            },
            communications: {
                title: 'Comunicaciones',
                add: 'Agregar Message',
                no_records: 'Sin comunicaciones registradas',
                direction: 'Dirección',
                channel: 'Canal',
                visibility: 'Visibilidad',
                subject: 'Sujeto',
                body: 'Cuerpo',
                send: 'Enviar',
                inbound: 'Entrante',
                outbound: 'Saliente',
                internal: 'Interno',
                external: 'Externo',
                tab_all: 'All',
                tab_internal: 'Interno',
                tab_external: 'Externo',
                subject_placeholder: 'Sujeto (optional)',
                body_placeholder: 'Cuerpo del mensaje...',
                channels: {
                    email: 'Correo electrónico',
                    portal: 'Portal',
                    phone: 'Teléfono',
                    postal: 'Correo postal',
                    in_person: 'En persona',
                },
                apply_template: 'Aplicar Plantilla',
                select_template: 'Seleccionar plantilla...',
                no_templates: 'No hay plantillas disponibles',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Plantilla aplicada a la respuesta.',
                template_apply_error: 'No se pudo aplicar la plantilla.',
            },
            escalations: {
                title: 'Escalamiento Historial',
                no_events: 'No hay eventos de escalado',
                tier: 'Nivel',
                threshold: 'Umbral',
                action: 'Acción',
                target: 'Destino',
                triggered_at: 'Activado el',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'Sincronización AesirX CMP',
                sync_status: 'Sincronizar Estado',
                last_synced: 'Último Sincronizado',
                not_synced: 'No Sincronizado',
                resync: 'Resincronizar ahora',
                syncing: 'Sincronizando...',
                sync_history: 'Sincronizar Historial (Last 10)',
                direction: 'Dirección',
                time: 'Hora',
                retries: 'Reintentos',
                error_col: 'error',
            },
            audit: {
                filter_event_placeholder: 'Filtrar por tipo de evento...',
                filter_actor_placeholder: 'Filtrar por actor...',
            },
            fulfillment: {
                title: 'Cumplimiento departamental',
                assign_departments: 'Asignar departamentos',
                department: 'Departamento',
                assignee: 'Responsable',
                deadline: 'Fecha límite',
                evidence_count: 'Evidencia',
                signoff: 'Aprobación',
                signed_off: 'Aprobado',
                pending_signoff: 'Pendiente',
                start_button: 'Iniciar',
                complete_button: 'Marcar como completado',
                signoff_button: 'Cerrar sesión',
                no_tasks: 'Aún no se han asignado tareas de cumplimiento.',
                tasks_table: 'Tareas de cumplimiento',
                progress_label: 'Progreso de cumplimiento',
                overdue: 'vencido',
                assigned_success: 'Tarea de cumplimiento departamental asignada exitosamente.',
                assign_error: 'Error al asignar la tarea de cumplimiento.',
                signoff_success: 'Aprobación departamental registrada.',
                signoff_error: 'Error al registrar la aprobación.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Agregar evidencia',
                evidence_uploaded: 'Evidencia subida y vinculada',
                evidence_upload_error: 'Error al subir evidencia',
                suggest_data_stores: 'Sugerir almacenes de datos',
                suggest_error: 'Error al cargar sugerencias de almacenamiento.',
                suggest_stores_title: 'Almacenes de datos sugeridos',
                suggest_stores_none: 'Ningún almacén de datos coincide con este caso. Configurar sistemas en Data Mapping para mejorar las sugerencias.',
                suggest_stores_add: 'Añadir al caso',
                suggest_stores_added: 'Almacén de datos agregado al caso',
                no_departments_title: 'No hay departamentos configurados',
                no_departments_body: 'Los departamentos le permiten dirigir las tareas de cumplimiento de DSR a una función (Privacidad, Legal, Recursos Humanos, Operaciones de TI). Instale departamentos de muestra o solicite a su administrador que configure departamentos en la configuración de la organización.',
                no_departments_cta: 'Instalar datos de muestra',
                department_placeholder: 'Nombre del departamento...',
                assignee_placeholder: 'ID de usuario a asignar...',
                assign_button: 'Asignar',
                status_pending: 'Pendiente',
                status_in_progress: 'En progreso',
                status_completed: 'Completado',
                status_verified: 'Verificado',
                confirm_approve: '¿Está seguro de que desea aprobar esta prórroga?',
                confirm_deny: '¿Está seguro de que desea denegar esta prórroga?',
            },
            response: {
                title: 'Generación de respuesta',
                select_template: 'Plantilla de respuesta',
                choose_template: 'Seleccionar una plantilla...',
                generate: 'Generar respuesta',
                preview: 'Vista previa de respuesta',
                send: 'Enviar respuesta',
                delivery_method: {
                    label: 'Método de entrega',
                    email: 'Correo electrónico',
                    portal: 'Portal',
                    postal: 'correo postal',
                    in_person: 'En persona',
                },
                recipient: {
                    email: 'Correo electrónico del destinatario',
                    portal: 'ID de usuario del portal',
                    address: 'dirección postal',
                },
                auto_generate_btn: 'Generar y enviar respuesta',
                auto_generate_success: 'Respuesta generada y entregada',
                auto_generate_missing_recipient: 'Proporcione un destinatario antes de enviar.',
                auto_fields: 'Campos autocompletados',
                case_id: 'ID del caso',
                requester_name: 'Nombre del solicitante',
                rights_type: 'Tipo de derecho',
                history: 'Respuestas enviadas',
                generated_at: 'Generado',
                confirmation: 'Confirmación',
                sent_label: 'Enviado',
                draft_label: 'Borrador',
                generated_success: 'Respuesta generada exitosamente.',
                generate_error: 'Error al generar la respuesta.',
                sent_success: 'Respuesta enviada exitosamente.',
                send_error: 'Error al enviar la respuesta.',
                load_error: 'Error al cargar las plantillas de respuesta.',
                pdf_title: 'Carta de respuesta al interesado',
                download_pdf: 'Descargar PDF',
                download_all: 'Descargar todo',
                download_error: 'Error al descargar la respuesta',
            },
            extension: {
                title: 'Extensiones de plazo',
                acknowledgment_deadline: 'Plazo de acuse de recibo',
                fulfillment_deadline: 'Plazo de cumplimiento',
                request_extension: 'Solicitar extensión',
                reason: 'Motivo',
                reason_placeholder: 'Motivo para solicitar tiempo adicional...',
                additional_days: 'Días adicionales',
                submit_request: 'Enviar solicitud de extensión',
                history: 'Historial de extensiones',
                requested_at: 'Solicitado',
                approve: 'Aprobar',
                deny: 'Rechazar',
                request_success: 'Solicitud de extensión enviada.',
                request_error: 'Error al enviar la solicitud de extensión.',
                approved_success: 'Extensión aprobada.',
                denied_success: 'Extensión rechazada.',
                decision_error: 'Error al procesar la decisión de extensión.',
                status_pending: 'Pendiente',
                status_approved: 'Aprobado',
                status_denied: 'Denegado',
                confirm_approve: '¿Está seguro de que desea aprobar esta solicitud de prórroga?',
                confirm_deny: '¿Está seguro de que desea denegar esta solicitud de prórroga?',
                decided_by: 'Decidido por',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exportar CSV',
            exporting: 'Exportando...',
            search_placeholder: 'Buscar detalles de auditoría...',
            filter_by_actor: 'Filtrar por actor...',
            all_event_types: 'Todos Evento Types',
            seq: '#',
            timestamp: 'Marca de tiempo',
            event: 'Evento',
            case_col: 'Caso',
            actor: 'Actor',
            summary: 'Resumen',
            no_events: 'No se encontraron audit events',
            export_success: 'Seguimiento de auditoría exportado correctamente.',
            export_error: 'Error al export audit csv. Por favor, inténtelo de nuevo.',
            load_error: 'Error al load audit trail. Por favor, inténtelo de nuevo.',
            announce_loaded: 'Registro de auditoría cargado',
            start_date: 'Fecha de inicio',
            end_date: 'Fecha de fin',
            per_page: '{count} / página',
            unknown: 'Desconocido',
            source_filter: {
                label: 'Fuente',
                all: 'Todos',
                module: 'Módulo',
                forseti: 'Forseti',
                aria_label: 'Filtrar eventos de auditoría por fuente',
            },
            event_types: {
                case_created: 'Caso creado',
                status_changed: 'Estado cambiado',
                case_assigned: 'Caso asignado',
                note_added: 'Nota añadida',
                attachment_added: 'Adjunto añadido',
                attachment_deleted: 'Adjunto Eliminado',
                reference_added: 'Referencia añadida',
                checklist_updated: 'Lista de verificación actualizada',
                identity_verified: 'Identidad Verificado',
                closure_summary_added: 'Resumen de cierre',
                approval_granted: 'Aprobación concedida',
                template_applied: 'Plantilla aplicada',
                export_generated: 'Exportación generada',
                sla_warning: 'SLA Advertencia',
                sla_overdue: 'SLA Vencido',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Pista de Auditoría Unificada',
                module_filter: 'Módulo',
                framework_filter: 'Marco normativo',
                select_framework: 'Seleccionar un marco...',
                entity_type_filter: 'Tipo de entidad',
                action_filter: 'Acción',
                actor_filter: 'Actor',
                select_actor: 'Seleccionar un actor...',
                search_filter: 'Buscar',
                search_placeholder: 'Buscar eventos de auditoría...',
                filters_heading: 'Filtros',
                date_range: 'Rango de fechas',
                no_events_found: 'No se encontraron eventos de auditoría.',
                event_count: 'Cantidad de eventos',
                action: 'Acción',
                actor: 'Actor',
                date_range_start: 'Inicio del rango de fechas',
                date_range_end: 'Fin del rango de fechas',
                filtering_events: 'Filtrando eventos de auditoría',
                framework_codes: 'Códigos de marco',
                entity_type_col: 'Tipo de entidad',
                aria_table: 'Eventos de auditoría unificados',
                page_of: 'Página {page} de {total}',
                entity_types: {
                    user: 'Usuario',
                    case: 'Caso',
                    incident: 'Incidente',
                    assessment: 'Evaluación',
                    vendor: 'vendedor',
                    consent: 'Consentimiento',
                    policy: 'Política',
                    data_map: 'Mapa de datos',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'crear',
                    update: 'Actualizar',
                    delete: 'Eliminar',
                    view: 'Ver',
                    export: 'Exportar',
                    status_change: 'Cambio de estado',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Paquetes de Evidencia de Auditoría',
                assemble_pack: 'Ensamblar Paquete de Evidencia',
                framework: 'Marco normativo',
                select_framework: 'Seleccionar un marco...',
                framework_code_label: 'Código del marco',
                entity_types: 'Tipos de entidad',
                select_entity_types: 'Seleccionar tipos de entidad...',
                period: 'Período',
                period_start: 'Inicio del período',
                period_end: 'Fin del período',
                completeness: 'Completitud',
                no_packs_found: 'No se encontraron paquetes de evidencia.',
                verify: 'Verificar',
                export_pack: 'Exportar',
                exporting: 'Exportando...',
                pack_assembled: 'Paquete de evidencias ensamblado',
                pack_exported: 'Paquete de evidencias exportado',
                aria_table: 'Paquetes de evidencias de auditoría',
                aria_export_pack: 'Exportar paquete',
                export_format: 'Formato de exportación',
                export_in_progress: 'Exportación en progreso...',
                status_draft: 'Borrador',
                status_assembled: 'Ensamblado',
                status_verified: 'Verificado',
                status_submitted: 'Enviado',
                entity_type_labels: {
                    user: 'Usuario',
                    case: 'Caso',
                    incident: 'Incidente',
                    assessment: 'Evaluación',
                    vendor: 'vendedor',
                    consent: 'Consentimiento',
                    policy: 'Política',
                    data_map: 'Mapa de datos',
                },
                template_id: 'ID de plantilla',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Módulo',
                    action: 'acción',
                    actor_id: 'Actor',
                    actor_role: 'Actor Role',
                    entity_type: 'Tipo de entidad',
                    entity_id: 'ID de entidad',
                    summary: 'Resumen',
                    timestamp: 'Marca de tiempo',
                    record_type: 'Record Type',
                    framework_code: 'Marco',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Paquete de evidencia — Marco: {framework}',
                export_header_period: 'Período: {start} a {end}',
                export_header_completeness: 'Completitud: {score}%',
                export_header_status: 'Estado: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Períodos de Auditoría',
                create_period: 'Crear Período de Auditoría',
                framework_code_label: 'Código del marco',
                select_framework: 'Seleccionar un marco...',
                period_label: 'Etiqueta de período',
                period_start: 'Inicio del período',
                period_end: 'Fin del período',
                start: 'Empezar',
                end: 'Fin',
                framework: 'Marco',
                retention_days: 'Días de retención',
                section_period_dates: 'Fechas del período',
                no_periods_found: 'No se encontraron períodos de auditoría.',
                period_created: 'Período de auditoría creado',
                aria_table: 'Períodos de auditoría',
                suggested_boundaries: 'Límites sugeridos',
                suggested_boundaries_info: 'Los límites de período sugeridos se derivan del ciclo de informes regulatorios de cada marco. Ajuste según las necesidades de su organización.',
                reporting_cycle: 'Ciclo de informes',
                suggested_start: 'Inicio sugerido',
                suggested_end: 'Fin sugerido',
                suggested_next: 'Próximo período sugerido',
                cycle_monthly: 'Mensual',
                cycle_quarterly: 'Trimestral',
                cycle_semi_annual: 'Semestral',
                cycle_annual: 'Anual',
                cycle_custom: 'personalizado',
                period_deleted: 'Período de auditoría eliminado',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Referencia del cliente',
            country: 'País / Jurisdicción',
            preferred_channel: 'Canal preferido',
            channels: {
                email: 'Correo electrónico',
                postal: 'Correo postal',
                phone: 'Teléfono',
                portal: 'Portal',
            },
            source: 'Fuente',
            sources: {
                admin_manual: 'Manual de administración',
                public_form: 'Formulario público',
                cmp_gateway: 'CMP Puerta de enlace',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Teléfono recibido',
                postal_received: 'Postal recibida',
                email_received: 'Correo electrónico recibido',
                in_person: 'En persona',
                legacy_import: 'Importación heredada',
            },
            required_fields: 'El tipo de solicitud, el nombre y el correo electrónico son obligatorios',
            create_error: 'Error al create case. Por favor, inténtelo de nuevo.',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Configuración',
            request_types: 'Tipos de solicitud',
            request_types_desc: 'Configure qué tipos de solicitud están habilitados para el formulario público de solicitud.',
            notifications: 'Notificaciones',
            ticket_numbering: 'Numeración de tickets',
            ticket_format: 'Formato actual',
            ticket_prefix: 'Prefijo',
            ticket_prefix_note: 'no configurable',
            sla_deadlines: 'Plazos de SLA',
            sla_desc: 'Establezca el tiempo máximo de respuesta (en días naturales) para cada tipo de solicitud. Los casos que se acerquen al umbral mostrarán avisos.',
            days: 'días',
            warning_threshold: 'Umbral de aviso',
            escalation_label: 'Habilitar escalado automático por incumplimiento de SLA',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Guardar configuración de SLA',
            saving: 'Guardando...',
            saved: 'Guardado correctamente',
            announce_loaded: 'Configuración cargada',
            email_on_creation: 'Notificación por correo electrónico al crear ticket',
            send_confirmation: 'Enviar correo de confirmación al solicitante',
            users: 'Usuarios',
            roles: 'Roles',
            api_keys: 'API Claves',
            mfa: 'MFA',
            sessions: 'Sesiones',
            webhooks: 'Ganchos web',
            modules: 'Módulos',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Acceso (DSAR)',
                deletion: 'Supresión',
                correction: 'Rectificación',
                portability: 'Portabilidad',
                objection: 'Oposición',
                restriction: 'Limitación',
                consent_withdrawal: 'Retirada del consentimiento',
                protective_measures: 'Medidas de protección',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Asignaciones de sección',
            assign_section: 'Asignar sección',
            section_ref: 'Referencia de sección',
            section_label: 'Etiqueta de sección',
            department_id: 'Departamento',
            assignee_id: 'Responsable',
            mandatory: 'Obligatorio',
            assign: 'Asignar',
            no_assignments: 'Sin asignaciones de sección',
            status: 'Estado',
            completion: 'Completado',
            mark_complete: 'Completar',
        },
        consolidation: {
            title: 'Progreso de consolidación',
            add_child: 'Agregar tarea secundaria',
            child_task_id: 'ID de tarea secundaria',
            department_id: 'Departamento',
            link: 'Enlace',
            overall_completion: 'Completado total',
            departments_complete: 'Departamentos completados',
            overdue: 'Vencido',
            ready: 'Listo',
            not_ready: 'No listo',
            master_status: 'Estado general',
            department_breakdown: 'Desglose por departamento',
            department: 'Departamento',
            status: 'Estado',
            completion: 'Completado',
            due_date: 'Fecha de vencimiento',
            overdue_flag: 'Vencido',
            child_tasks: 'Tareas secundarias',
            task: 'Tarea',
        },
        conflict_detection: {
            title: 'Detección de conflictos',
            unresolved: 'sin resolver',
            no_conflicts: 'No se detectaron conflictos',
            section: 'Sección',
            field: 'Campo',
            dept_a: 'Departamento A',
            value_a: 'Valor A',
            dept_b: 'Departamento B',
            value_b: 'Valor B',
            severity: 'Gravedad',
            status: 'Estado',
            resolved: 'Resuelto',
            open: 'Abierto',
            resolve: 'Resolver',
        },
        org_maturity: {
            title: 'Madurez Organizacional',
            departments: 'Departamentos',
            department_hierarchy: 'Jerarquía de Departamentos',
            create_department: 'Crear Departamento',
            department_name: 'Nombre del Departamento',
            department_code: 'Código',
            level: 'Nivel',
            division: 'División',
            department: 'Departamento',
            team: 'Equipo',
            parent_department: 'Departamento Superior',
            head_user: 'Jefe de Departamento',
            no_departments: 'No hay departamentos configurados.',
            user_mappings: 'Asignaciones de Usuarios',
            map_user: 'Asignar Usuario',
            user_id: 'ID de Usuario',
            role: 'Rol',
            no_users: 'No hay usuarios asignados a este departamento.',
            remove: 'Eliminar',
            framework_assignments: 'Asignaciones de Marcos',
            assign_framework: 'Asignar Marco',
            entity_id: 'ID de Entidad',
            entity_type: 'Tipo de Entidad',
            framework_id: 'ID del Marco',
            framework_code: 'Código del Marco',
            no_assignments: 'No hay asignaciones de marcos.',
            compliance_rollup: 'Resumen de Cumplimiento',
            entity_name: 'Entidad',
            total_assessments: 'Evaluaciones Totales',
            completed: 'Completado',
            completion_pct: 'Completado %',
            open_gaps: 'Brechas Abiertas',
            critical_gaps: 'Críticas',
            compliance_score: 'Puntuación',
            no_rollup: 'No hay datos de resumen de cumplimiento disponibles.',
            entity_summary: 'Resumen de Cumplimiento de la Entidad',
            frameworks_assigned: 'Marcos',
            overall_score: 'Puntuación General',
            load_departments_error: 'Error al cargar los departamentos',
            load_users_error: 'Error al cargar los usuarios',
            department_created: 'Departamento creado',
            department_created_announce: 'Departamento creado',
            create_department_error: 'Error al crear el departamento',
            user_mapped: 'Usuario asignado',
            user_mapped_announce: 'Usuario asignado al departamento',
            map_user_error: 'Error al asignar el usuario',
            user_removed: 'Usuario eliminado',
            load_rollup_error: 'Error al cargar el resumen',
            load_assignments_error: 'Error al cargar las asignaciones',
            framework_assigned: 'Marco asignado',
            framework_assigned_announce: 'Marco asignado a la entidad',
            assign_framework_error: 'Error al asignar el marco normativo',
            assignment_removed: 'Asignación eliminada',
            enter_entity_id_placeholder: 'Introduzca el ID de la entidad para ver las asignaciones',
            entity_type_organization: 'Organización',
            entity_type_department: 'Departamento',
        },
        onboarding_maturity: {
            title: 'Madurez de incorporación',
            quick_starts: 'Inicios rápidos',
            progress: 'Progreso de incorporación',
            personas: 'Rutas de persona',
            persona_onboarding: 'Incorporación de personas',
            quick_start_scenarios: 'Escenarios de inicio rápido',
            start: 'Iniciar',
            restart: 'Reiniciar',
            estimated_minutes: 'Minutos estimados',
            steps: 'Pasos',
            scenario: 'Escenario',
            no_scenarios: 'No hay escenarios de inicio rápido disponibles.',
            persona_id: 'ID de persona',
            persona_label: 'persona',
            role_description: 'Descripción del rol',
            primary_modules: 'Módulos primarios',
            recommended_tours: 'Tours recomendados',
            sample_data_modules: 'Módulos de datos de muestra',
            quick_start_scenario: 'Escenario de inicio rápido',
            no_personas: 'No hay rutas de persona disponibles.',
            framework_setup: 'Marcos regulatorios',
            framework_code: 'Código marco',
            framework_name: 'Nombre del marco',
            related_modules: 'Módulos relacionados',
            persona_relevance: 'Personas relevantes',
            no_frameworks: 'No hay opciones de configuración de marco disponibles.',
            tier_config: 'Configuración del módulo de niveles',
            tier: 'Nivel',
            available_modules: 'Módulos disponibles',
            module_count: 'Recuento de módulos',
            overall_completion: 'Finalización general',
            wizard_completed: 'Asistente completado',
            tours_completed: 'Tours completados',
            quick_starts_completed: 'Inicios rápidos completados',
            selected_frameworks: 'Marcos seleccionados',
            no_progress: 'No hay datos de progreso de incorporación disponibles.',
            detected_persona: 'Persona detectada',
            current_tier: 'Nivel actual',
            completion_pct: 'Completado',
            progress_page: {
                loaded: 'Progreso cargado',
                no_progress: 'No hay datos de progreso de incorporación disponibles.',
                overall: 'Progreso general',
                detected_persona: 'Persona detectada',
                tier: 'Nivel',
                modules: 'módulos',
                wizard: 'Asistente',
                completed: 'Completado',
                pending: 'Pendiente',
                tours: 'Recorridos',
                quick_starts: 'Inicios rápidos',
                frameworks: 'Marcos seleccionados',
                no_frameworks: 'Ningún marco seleccionado.',
                go_to_wizard: 'Completar el asistente de configuración',
                framework_selection: 'Selección de marco',
                framework_toggle: 'Habilitar o deshabilitar marcos para su programa de cumplimiento.',
                framework_updated: 'Selección de marco actualizada.',
                view_tours: 'Ver recorridos',
                view_quick_starts: 'Ver inicios rápidos',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Seleccione una persona para ver módulos, recorridos y escenarios de inicio rápido recomendados.',
                no_personas: 'No hay rutas de persona disponibles.',
                modules: 'Módulos',
                tours: 'Recorridos',
                quick_start: 'Inicio rápido',
                details: 'Detalles',
                primary_modules: 'Módulos principales',
                recommended_tours: 'Recorridos recomendados',
                framework_options: 'Marcos regulatorios',
                no_frameworks: 'No hay opciones de configuración del marco disponibles.',
                no_frameworks_explain: 'Aún no hay marcos regulatorios instalados para su organización, por lo que no hay nada que configurar aquí.',
                no_frameworks_action: 'Instale un paquete de mercado para añadir los marcos que le sean aplicables',
                start_tour: 'Iniciar',
                no_tours: 'No hay recorridos disponibles.',
                help_center: 'Centro de ayuda',
                no_articles: 'No se encontraron artículos.',
                primary: 'Principal',
                framework_code: 'Código del marco',
                framework_label: 'Etiqueta',
                region: 'Región',
                category: 'Categoría',
                enabled: 'Habilitado',
                disabled: 'Deshabilitado',
                tier_modules: 'Configuración de módulos por nivel',
                tiers_loaded: 'Configuraciones de nivel cargadas',
                no_tier_config: 'No hay configuración de nivel disponible.',
                view_tours: 'Ver recorridos de módulos',
                view_quick_start: 'Iniciar inicio rápido',
                toggle_framework: 'Alternar marco',
                framework_updated: 'Selección de marco actualizada.',
                tier_desc_light: 'Fundamentos de privacidad — Cumplimiento básico de PDPL',
                tier_desc_standard: 'Suite de cumplimiento completa — todos los módulos con flujos de trabajo',
                tier_desc_enterprise: 'Gobernanza avanzada y automatización — multi-entidad, informes de grupo',
                persona_highlight: 'Sus módulos',
                aria_go_to_module: '{name} — ir al módulo',
                aria_start_tour: 'Iniciar {title}',
                aria_quick_start: 'Inicio rápido: {title}',
                aria_help_article: 'Artículo de ayuda: {title}',
                aria_view_framework: '{label}{primary} — ver detalles del marco',
                aria_primary_for_persona: '{name} (principal para esta persona)',
                primary_for_persona_suffix: ' (principal para esta persona)',
                aria_view_in_persona_paths: '{name} — ver en Persona Paths',
                progress_of_total: '{completed} de {total} completado',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Practique flujos de trabajo comunes con escenarios de inicio rápido guiados.',
                no_scenarios: 'No hay escenarios de inicio rápido disponibles.',
                started: 'Inicio rápido iniciado',
                status_completed: 'Completado',
                status_in_progress: 'En progreso',
                status_not_started: 'No iniciado',
                minutes: 'min',
                steps: 'pasos',
                step: 'Paso',
                restart: 'Reiniciar',
                start: 'Iniciar',
                go_to_module: 'Ir al módulo',
                next_step: 'Siguiente paso',
                complete: 'Completar',
                reset: 'Restablecer',
                step_of: 'de',
                action_navigate: 'Navegar',
                action_form_fill: 'Rellenar formulario',
                action_review: 'Revisar',
                action_confirm: 'Confirmar',
                step_changed: 'Paso {current} de {total}: {title}',
                completed_message: '¡Inicio rápido completado con éxito!',
                reset_message: 'El inicio rápido se ha restablecido.',
            },
            persona_p_vn_01_label: 'Empresa DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Dueño del Departamento',
            persona_p_vn_02_description: 'Líder del departamento responsable de los informes de cumplimiento seccionales',
            persona_p_vn_03_label: 'Cumplimiento del comercio electrónico',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Líder de Gobernanza de Datos',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Ciberseguridad y operaciones legales',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Líder de Gobernanza',
            persona_p_vn_07_description: 'Líder en la supervisión de AI gobierno del sistema, evaluaciones de riesgos y clasificación.',
            persona_p_vn_08_label: 'Gobierno transfronterizo y de proveedores',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Líder de respuesta a infracciones',
            persona_p_vn_09_description: 'Liderar la coordinación de la notificación de infracciones y la respuesta a incidentes.',
            persona_p_vn_10_label: 'Líder de Auditoría Interna',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'Responsable de datos de RRHH y empleados',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Responsable de cumplimiento de filial multinacional',
            persona_p_vn_12_description: 'DPO en filial vietnamita de empresa extranjera que necesita capa de cumplimiento local',
            persona_p_vn_13_label: 'Responsable de cumplimiento educativo',
            persona_p_vn_13_description: 'Responsable de cumplimiento en escuela extranjera que gestiona protección de datos de estudiantes y padres',
            qs_hr_consent_title: 'Registrar consentimientos de empleados',
            qs_hr_consent_description: 'Recorrido del registro de consentimiento de empleados y generación de informe de cobertura.',
            qs_subsidiary_dpia_title: 'Crear evaluación EIPD de filial',
            qs_subsidiary_dpia_description: 'Recorrido de la creación de una EIPD para una actividad de tratamiento de filial.',
            qs_student_consent_title: 'Configurar recogida de consentimiento estudiantil',
            qs_student_consent_description: 'Recorrido de la creación de políticas de consentimiento parental para datos de estudiantes.',
            persona_p_vn_14_label: 'Asesor Jurídico Externo',
            persona_p_vn_14_description: 'Socio senior de una firma de abogados que asesora a múltiples organizaciones de clientes sobre el cumplimiento de PDPL',
            persona_p_vn_15_label: 'Gerente de Operaciones de la Firma de Abogados',
            persona_p_vn_15_description: 'Coordinador de participación del cliente que gestiona el seguimiento de entregables y la coordinación de plazos.',
            persona_p_vn_16_label: 'Propietario de empresa / CEO',
            persona_p_vn_16_description: 'CEO o GM, quien es legalmente el responsable del tratamiento de datos según PDPL, responsable de la inversión en cumplimiento',
            qs_dpia_filing_title: 'Complete su primera presentación DPIA',
            qs_dept_compliance_title: 'Envíe la primera sección de cumplimiento del departamento',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Configurar el manejo de datos de suscriptores de telecomunicaciones',
            qs_data_flow_title: 'Mapee su primer flujo de datos',
            qs_incident_response_title: 'Configurar el flujo de trabajo de respuesta a incidentes',
            qs_ai_risk_title: 'Ejecute una evaluación de riesgos del sistema AI',
            qs_cross_border_title: 'Evaluar una transferencia de datos transfronteriza',
            qs_breach_notification_title: 'Simular un flujo de trabajo de notificación de incumplimiento',
            qs_audit_trail_title: 'Revisar una pista de auditoría de cumplimiento',
            qs_legal_advisor_portfolio_title: 'Revisar la cartera de cumplimiento de múltiples clientes',
            qs_legal_advisor_portfolio_description: 'Revise el estado de cumplimiento en múltiples organizaciones de clientes.',
            qs_law_firm_ops_title: 'Seguimiento del estado de entrega del cliente',
            qs_law_firm_ops_description: 'Supervisar el progreso de los entregables de asesoramiento y la participación del cliente.',
            qs_ceo_dashboard_title: 'Ver el panel de cumplimiento ejecutivo',
            qs_ceo_dashboard_description: 'Revise el estado de cumplimiento de alto nivel y las métricas de inversión.',
        },
        rights_requests: {
            response_templates: {
                title: 'Plantillas de respuesta',
                subtitle: 'Administre las plantillas de cartas de respuesta de DSR que ResponseGenerator utiliza para redactar respuestas a los interesados.',
                create_btn: 'Nueva plantilla',
                edit_btn: 'Editar plantilla',
                filter_right_type: 'Filtrar por tipo correcto',
                filter_locale: 'Filtrar por configuración regional',
                filter_status: 'Filtrar por estado',
                all_right_types: 'Todos los tipos correctos',
                any_right_type: 'Cualquiera (se aplica a todos los tipos)',
                all_locales: 'Todos los locales',
                all_statuses: 'Todos',
                status_active: 'Activo',
                status_inactive: 'Inactivo',
                empty_state: 'Ninguna plantilla coincide con los filtros actuales.',
                columns: {
                    template_id: 'ID de plantilla',
                    outcome: 'Resultado',
                    locale: 'Localidad',
                    right_type: 'Tipo correcto',
                    is_active: 'Estado',
                    last_updated: 'Última actualización',
                    actions: 'Acciones',
                },
                actions: {
                    edit: 'Editar',
                    clone: 'clonar',
                    deactivate: 'Desactivar',
                    reactivate: 'Reactivar',
                    preview: 'Vista previa',
                },
                form: {
                    template_id: 'ID de plantilla',
                    template_id_help: 'Identificador en minúscula, p. dsr_access_fulfilled_vi',
                    outcome: 'Resultado',
                    locale: 'Localidad',
                    right_type: 'Tipo correcto',
                    subject: 'Asunto',
                    body: 'Plantilla de cuerpo',
                    body_help: 'Utilice marcadores de posición como {case_reference}, {data_subject_name}, {fulfilled_at}. Mínimo 15 líneas para cartas de varias secciones.',
                    is_active: 'Activo',
                    official_form_id: 'Identificación oficial del formulario (opcional)',
                },
                outcomes: {
                    fulfilled: 'Cumplido',
                    partially_fulfilled: 'Cumplido parcialmente',
                    denied: 'denegado',
                },
                clone_modal_title: 'Plantilla de clonación',
                clone_target_locale: 'Configuración regional de destino',
                clone_help: 'Se creará una nueva plantilla en la configuración regional de destino utilizando esta plantilla como punto de partida.',
                preview_modal_title: 'Vista previa de plantilla',
                preview_note: 'La vista previa utiliza marcadores de posición de mayúsculas y minúsculas DSR-DEMO-0020. La entrega real sustituye a los datos de casos reales.',
                preview_unavailable: 'La vista previa requiere un caso existente. Adjunte esta plantilla a un caso y utilice la acción Borrador de respuesta en la página de detalles del caso.',
                deactivate_confirm: '¿Desactivar esta plantilla? Continuará aplicándose a los casos existentes que hacen referencia a él.',
                toasts: {
                    created: 'Plantilla creada',
                    updated: 'Plantilla actualizada',
                    deactivated: 'Plantilla desactivada',
                    reactivated: 'Plantilla reactivada',
                    cloned: 'Plantilla clonada',
                    load_error: 'No se pudieron cargar las plantillas',
                    save_error: 'No se pudo guardar la plantilla',
                    clone_error: 'No se pudo clonar la plantilla',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Departamentos',
                subtitle: 'Administre los departamentos que dirigen los casos de DSR, las tareas DPIA y los flujos de trabajo de cumplimiento.',
                create_btn: 'Nuevo Departamento',
                edit_btn: 'Editar departamento',
                columns: {
                    name: 'Nombre',
                    description: 'Descripción',
                    active_cases: 'Casos activos',
                    members: 'Miembros',
                    status: 'Estado',
                    actions: 'Acciones',
                },
                status_active: 'Activo',
                status_inactive: 'Inactivo',
                form: {
                    name: 'Nombre',
                    description: 'Descripción',
                },
                actions: {
                    edit: 'Editar',
                    deactivate: 'Desactivar',
                    reactivate: 'Reactivar',
                    delete: 'Eliminar',
                    manage_members: 'Administrar miembros',
                },
                delete_modal_title: 'Eliminar departamento',
                delete_warning: 'Este departamento todavía tiene casos activos. Elija un departamento al que reasignarlos antes de eliminarlos.',
                delete_no_cases: 'Este departamento no tiene casos activos y se puede eliminar de forma segura.',
                reassign_prompt: 'Reasignar casos activos a:',
                reassign_none_available: 'No hay otros departamentos activos disponibles. Primero cree otro departamento.',
                member_section_title: 'Miembros',
                add_member: 'Agregar miembro',
                remove_member: 'Quitar',
                member_role: 'Rol',
                no_members: 'No hay miembros asignados a este departamento.',
                toasts: {
                    created: 'Departamento creado',
                    updated: 'Departamento actualizado',
                    deactivated: 'Departamento desactivado',
                    reactivated: 'Departamento reactivado',
                    deleted: 'Departamento eliminado',
                    reassigned_count: '{count} caso(s) reasignado(s)',
                    member_added: 'Miembro agregado',
                    member_removed: 'Miembro eliminado',
                    load_error: 'No se pudieron cargar los departamentos',
                    save_error: 'No se pudo guardar el departamento',
                    delete_error: 'No se pudo eliminar el departamento',
                },
            },
            retention_policies: {
                title: 'Políticas de retención',
                subtitle: 'Configure ventanas de retención por tipo de objetivo. Los documentos de verificación de identidad utilizan esto para sellar retention_expires_at en cargas futuras.',
                create_btn: 'Nueva política',
                edit_btn: 'Editar política',
                columns: {
                    target_type: 'Tipo de objetivo',
                    retention_days: 'Retención (días)',
                    owner: 'Propietario de la póliza',
                    effective_from: 'Efectivo desde',
                    last_reviewed: 'Última revisión',
                    status: 'Estado',
                    actions: 'Acciones',
                },
                status_active: 'Activo',
                status_inactive: 'Inactivo',
                form: {
                    target_type: 'Tipo de objetivo',
                    target_type_help: 'A qué categoría de datos se aplica la ventana de retención. Actualmente, solo se admiten documentos de verificación de identidad DSR.',
                    retention_days: 'Ventana de retención (días)',
                    retention_days_help: 'Entre 1 y 3650 días (10 años máximo).',
                    owner: 'Propietario de la póliza',
                    owner_help: 'Generalmente el DPO. Responsable de la decisión de retención conforme a DG-2.',
                    effective_from: 'Efectivo desde',
                },
                target_types: {
                    dsr_identity_verification_document: 'Documentos de verificación de identidad DSR',
                },
                deactivate_notice: 'La desactivación no afecta los archivos adjuntos que ya están sellados; solo las cargas futuras ya no recibirán una ventana de retención.',
                deactivate_confirm: '¿Desactivar esta política de retención?',
                toasts: {
                    created: 'Política de retención creada',
                    updated: 'Política de retención actualizada',
                    deactivated: 'Política de retención desactivada',
                    load_error: 'No se pudieron cargar las políticas de retención',
                    save_error: 'No se pudo guardar la política de retención',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Sistemas',
            categories: 'Categorías de datos',
            registry: 'Registro de datos',
            purposes: 'Fines del procesamiento',
            retention: 'Conservación',
            flows: 'Flujos de datos',
            activities: 'Actividades de tratamiento',
            evidence: 'Evidencias',
            exports: 'Exportaciones',
            attestation: 'Ciclos de atestación',
            governance: 'Gobernanza',
            review: 'Tareas de revisión',
            inventory: 'Inventario',
            exceptions: 'Excepciones',
            history: 'Historial',
            ropa_completeness: 'Integridad ROPA',
        },
        ropa_completeness_autofix: {
            ask_button: 'Pregúntale a Forseti',
            drafting: 'Redacción…',
            accept: 'Aceptar',
            reject: 'Rechazar',
            accepted: 'Aceptado: aplicar a través del editor de actividades.',
            rejected: 'Rechazado.',
            needs_human_input: 'Forseti necesita más contexto de actividad (por ejemplo, un propósito documentado) antes de redactar este campo.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'Informe de integridad de ROPA',
            subtitle: 'Auditoría de higiene por campo de sus Registros de actividades de procesamiento, dentro del alcance del marco regulatorio.',
            framework_label: 'Marco',
            kpi_total: 'Actividades puntuadas',
            kpi_critical: 'Crítico',
            kpi_high: 'Alto',
            kpi_conditional: 'Condicional',
            field_chart_heading: 'Campos faltantes por frecuencia',
            drilldown_heading: 'Actividades con lagunas',
            col_activity: 'Actividad',
            col_field: 'campo',
            col_severity: 'Gravedad',
            col_condition: 'Condición',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Crítico',
            severity_high: 'Alto',
            severity_conditional: 'Condicional',
            severity_informational: 'Informativo',
            include_informational: 'Incluir informativo',
            export_csv: 'Descargar CSV',
            exporting: 'Exportando...',
            loading: 'Cargando…',
            empty_state: 'Todas las actividades activas cuentan con registros ROPA completos para este marco.',
            error_fetch: 'No se pudo cargar el informe de integridad.',
            error_unsupported_framework: 'Este marco no es compatible.',
            error_framework_not_installed: 'Este marco no está instalado para su organización.',
            no_misses_for_field: 'A ninguna actividad le falta este campo.',
            framework_PDPL: 'PDPL (Ley 91/2025 + Decreto 356)',
            framework_GDPR: 'GDPR Artículo 30',
            framework_DECREE_165: 'Decreto 165 (Ley de Datos de Vietnam)',
            framework_ISO_27701: 'ISO 27701 (informativo)',
        },
        registry: {
            // Sprint 64.72.0 — encabezados de columna de la exportación del registro de datos.
            export_col: {
                registry_item_id: 'ID del elemento del registro',
                name: 'Nombre',
                description: 'Descripción',
                field_identifiers: 'Identificadores de campo',
                category_id: 'ID de categoría',
                category_name: 'Nombre de la categoría',
                data_type: 'Tipo de dato',
                effective_sensitivity: 'Sensibilidad efectiva',
                sensitivity_override: 'Sensibilidad sustitutiva',
                data_subjects: 'Interesados',
                volume_band: 'Rango de volumen',
                source_system_ids: 'ID de los sistemas de origen',
                processing_activity_ids: 'ID de las actividades de tratamiento',
                retention_policy_id: 'ID de la política de conservación',
                effective_retention_policy_id: 'ID de la política de conservación efectiva',
                purposes: 'Fines',
                collection_method: 'Método de recogida',
                legal_basis: 'Base jurídica',
                data_tier: 'Nivel de datos',
                data_tier_source: 'Origen del nivel de datos',
                storage_locations: 'Ubicaciones de almacenamiento',
                cross_border: 'Transfronterizo',
                status: 'Estado',
                source_form_submission_id: 'ID de envío del formulario de origen',
                source_form_template_id: 'ID de plantilla del formulario de origen',
                last_reviewed_at: 'Última revisión',
                next_review_at: 'Próxima revisión',
                created_at: 'Creado el',
                updated_at: 'Actualizado el',
                data_element: 'Elemento de datos',
                data_category: 'Categoría de datos',
                systems: 'Sistemas',
                retention_policy: 'Política de conservación',
                name_of_processing: 'Nombre del tratamiento',
                categories_of_personal_data: 'Categorías de datos personales',
                categories_of_data_subjects: 'Categorías de interesados',
                recipients: 'Destinatarios',
                third_country_transfers: 'Transferencias a terceros países',
                retention_period: 'Plazo de conservación',
                doc_title: 'Registro de datos',
                doc_generated: 'Exportación generada el {timestamp}',
                doc_item_count: '{count} elemento(s) del registro',
                doc_template_full: 'Completo',
            },
            export_vocab: {
                data_type: {
                    text: 'Texto',
                    number: 'Número',
                    date: 'Fecha',
                    datetime: 'Fecha y hora',
                    boolean: 'Sí/no',
                    binary: 'Binario',
                    structured: 'Estructurado',
                    file: 'Archivo',
                    identifier: 'Identificador',
                    geolocation: 'Geolocalización',
                },
                collection_method: {
                    direct_from_subject: 'Directamente del interesado',
                    third_party: 'De un tercero',
                    derived: 'Derivado de otros datos',
                    observed: 'Observado',
                    inferred: 'Inferido',
                    public_source: 'Fuente pública',
                },
                data_subject_type: {
                    customer: 'Cliente',
                    employee: 'Empleado',
                    contractor: 'Contratista',
                    prospect: 'Cliente potencial',
                    minor: 'Menor de edad',
                    patient: 'Paciente',
                    student: 'Estudiante',
                    beneficiary: 'Beneficiario',
                    vendor_contact: 'Contacto del proveedor',
                    website_visitor: 'Visitante del sitio web',
                    other: 'Otro',
                },
                volume_band: {
                    lt_1k: 'Menos de 1.000 registros',
                    '1k_to_10k': 'De 1.000 a 10.000 registros',
                    '10k_to_100k': 'De 10.000 a 100.000 registros',
                    '100k_to_1m': 'De 100.000 a 1 millón de registros',
                    gt_1m: 'Más de 1 millón de registros',
                    unknown: 'No se sabe',
                },
                tier_source: {
                    assessed_and_approved: 'Evaluado y aprobado',
                    manual_legacy: 'Adoptado de un valor heredado',
                    legacy_platform_field: 'Valor heredado de la plataforma',
                    scheme_scoped_only: 'Evaluado según niveles propios de cada esquema — ningún código único abarca varias escalas de niveles legales',
                },
            },
            page: {
                title: 'Registro de datos',
                subtitle: 'Elementos de datos personales que posee su organización, organizados en Categorías de datos.',
                empty_state: 'Aún no hay elementos de registro. Agregue un artículo manualmente o impórtelo desde un registro regulatorio.',
                empty_filter_state: 'Ningún artículo coincide con tus filtros.',
            },
            filter: {
                category: 'categoría',
                sensitivity: 'Sensibilidad',
                data_subject: 'Interesado',
                status: 'Estado',
                data_tier: 'Nivel de datos',
                cross_border: 'transfronterizo',
                has_owner: 'tiene dueño',
                has_form_source: 'Importado del formulario',
                search_placeholder: 'Buscar nombre, descripción o identificador de campo...',
                clear: 'Limpiar filtros',
                option_all: 'Todos',
            },
            col: {
                name: 'Nombre',
                category: 'categoría',
                sensitivity: 'Sensibilidad',
                data_subjects: 'Interesados',
                systems: 'Sistemas',
                activities: 'Actividades',
                owner: 'propietario',
                retention: 'Retención',
                data_tier: 'Nivel de datos',
                status: 'Estado',
                last_reviewed: 'Última revisión',
                actions: 'Acciones',
            },
            action: {
                add_manual: 'Agregar artículo',
                add_from_form: 'Agregar desde el registro regulatorio',
                import_history: 'Importar envíos anteriores',
                export: 'Exportar',
                archive: 'Archivo',
                restore: 'Restaurar',
                edit: 'Editar',
                assign_owner: 'Asignar propietario',
                attach_evidence: 'Adjuntar evidencia',
                open_lineage: 'Linaje abierto',
            },
            modal_editor: {
                section_identity: 'Identidad',
                section_classification: 'Clasificación',
                section_subjects: 'Temas y volumen',
                section_location: 'Ubicación y traslado',
                section_systems: 'Sistemas y actividades',
                section_retention: 'Retención y Legal',
                section_governance: 'Gobernanza',
                section_notes: 'Notas',
                hint_inherited: '(heredado de la categoría)',
                hint_overridden: '(anulado)',
                placeholder_field_identifiers: 'por ejemplo, clientes.primary_email, hrs.employee_id',
                validation_name_required: 'El nombre es obligatorio',
                validation_category_required: 'Se requiere categoría',
                validation_data_subjects_required: 'Se requiere al menos un tipo de interesado',
                label_data_type: 'tipo de datos',
                label_field_identifiers: 'Identificadores de campo (separados por comas)',
                label_data_subject_types: 'Tipos de interesados',
                label_volume_band: 'banda de volumen',
                label_storage_locations: 'Ubicaciones de almacenamiento (separadas por comas)',
                placeholder_storage_locations: 'ISO-3166 códigos o texto libre (p. ej., VN, Hetzner EU)',
                label_cross_border: 'Transferencia transfronteriza',
                label_source_systems: 'Sistemas fuente',
                label_processing_activities: 'Actividades de procesamiento',
                label_collection_method: 'Método de recolección',
                label_purposes: 'Propósitos',
                hint_multi_select: 'Mantenga presionado Cmd/Ctrl para seleccionar varios.',
                option_inherit_category: '— (heredar de categoría)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Vista previa de importación',
                label_to_create: 'para crear',
                label_to_update: 'Para actualizar',
                label_skipped_archived: 'Omitido (archivado)',
                label_conflicts: 'Conflictos',
                commit: 'Importar',
                cancel: 'Cancelar',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'categoría',
                section_systems: 'Sistemas fuente',
                section_activities: 'Actividades de procesamiento',
                section_flows: 'Flujos de datos',
                section_retention: 'Retención efectiva',
                section_governance: 'Gobernanza',
                section_evidence: 'evidencia',
                section_audit: 'Pista de auditoría reciente',
                section_source_form: 'Envío del formulario fuente',
                badge_inherited: 'heredado',
                badge_overridden: 'Anulado',
            },
            toast: {
                created: 'Elemento de registro creado.',
                updated: 'Elemento de registro actualizado.',
                archived: 'Elemento de registro archivado.',
                imported: '{count} elementos importados.',
                conflict_warning: 'Los {count} campos mantuvieron sus ediciones; consulte la lista de conflictos.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Inventario de sistemas',
            new: 'Nuevo sistema',
            edit: 'Editar sistema',
            name: 'Nombre',
            owner: 'Responsable',
            criticality: 'Criticidad',
            dpa_status: 'Estado del DPA',
            location: 'Ubicación',
            system_type: 'Tipo de sistema',
            metadata: 'Metadatos (JSON)',
            department: 'Departamento',
            hosting_location: 'Ubicación del alojamiento',
            hosting_provider: 'Proveedor de alojamiento',
            data_residency_country: 'País de residencia de datos',
            hosting: 'Alojamiento',
            no_systems: 'No se encontraron sistemas',
            confirm_archive: '¿Archivar el sistema "{name}"?',
            count: 'sistemas',
            count_one: 'sistema',
            type_internal: 'Interno',
            type_external: 'Externo',
            type_cloud_service: 'Servicio en la nube',
            type_on_premise: 'En las instalaciones',
            type_hybrid: 'Híbrido',
            status_active: 'Activo',
            status_decommissioning: 'En proceso de retiro',
            status_decommissioned: 'Retirado',
            status_planned: 'Planificado',
            dpa_not_required: 'No requerido',
            dpa_required_missing: 'Requerido faltante',
            dpa_in_progress: 'En curso',
            dpa_signed: 'Firmado',
            all_criticalities: 'Todas las criticidades',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Categorías de datos',
            new: 'Nueva categoría',
            edit: 'Editar categoría',
            name: 'Nombre',
            sensitivity: 'Sensibilidad',
            tags: 'Etiquetas',
            used_in: 'Utilizada en',
            examples: 'Ejemplos',
            examples_hint: 'Ejemplos (separados por comas)',
            default: 'Predeterminado',
            all_sensitivities: 'Todas las sensibilidades',
            all_statuses: 'Todos los estados',
            no_categories: 'No se encontraron categorías',
            confirm_delete: '¿Eliminar la categoría "{name}"?',
            count: 'categorías',
            count_one: 'categoría',
            status_draft: 'Borrador',
            status_proposed: 'Propuesto',
            status_under_review: 'En revisión',
            status_approved: 'Aprobado',
            status_deprecated: 'Obsoleto',
            status_updated: 'Estado de categoría actualizado',
            propose: 'Proponer',
            approve: 'Aprobar',
            deprecate: 'Obsoleto',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Finalidades del tratamiento',
            new: 'Nueva finalidad',
            edit: 'Editar finalidad',
            name: 'Nombre',
            legal_basis: 'Base jurídica',
            retention_period: 'Período de conservación (días)',
            justification: 'Justificación del interés legítimo',
            legal_basis_detail: 'Detalle de la base jurídica',
            all_legal_bases: 'Todas las bases jurídicas',
            no_purposes: 'No se encontraron finalidades',
            confirm_delete: '¿Eliminar "{name}"?',
            default: 'Predeterminado',
            count: '{count} finalidades',
            count_one: '{count} finalidad',
        },
        retention: {
            title: 'Políticas de conservación',
            new: 'Nueva política',
            edit: 'Editar política',
            name: 'Nombre',
            retention_days: 'Días de conservación',
            disposal_method: 'Método de eliminación',
            data_categories: 'Categorías de datos',
            activities: 'Actividades',
            review_frequency: 'Frecuencia de revisión',
            review_frequency_days: 'Frecuencia de revisión (días)',
            no_policies: 'No se encontraron políticas de conservación',
            confirm_delete: '¿Eliminar "{name}"?',
            count: '{count} políticas',
            count_one: '{count} política',
            days: 'días',
            disposal_deletion: 'Eliminación',
            disposal_anonymization: 'Anonimización',
            disposal_archival: 'Archivado',
            review_overdue: 'Revisión atrasada',
            review_status: 'Estado de revisión',
            review_ok: 'En curso',
            department: 'Departamento',
            all_departments: 'Todos los departamentos',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Vista de flujos',
            view_list: 'Lista',
            view_map: 'Mapa',
            title: 'Flujos de datos',
            new: 'Nuevo flujo',
            edit: 'Editar flujo',
            name: 'Nombre',
            from_system: 'Sistema de origen',
            to_system: 'Sistema de destino',
            flow_type: 'Tipo de flujo',
            cross_border: 'Transferencia internacional',
            risk_level: 'Nivel de riesgo',
            mechanism: 'Mecanismo',
            transfer_mechanism: 'Mecanismo de transferencia',
            frequency: 'Frecuencia',
            source_country: 'País de origen',
            destination_country: 'País de destino',
            transfer_safeguard: 'Garantía de transferencia',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Todos los niveles de riesgo',
            no_flows: 'No se encontraron flujos de datos',
            confirm_archive: '¿Archivar "{name}"?',
            count: '{count} flujos',
            count_one: '{count} flujo',
            status_active: 'Activo',
            status_inactive: 'Inactivo',
            status_under_review: 'En revisión',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Transferencia de archivos',
            mechanism_database_replication: 'Replicación de base de datos',
            mechanism_manual: 'manuales',
            mechanism_email: 'Correo electrónico',
            mechanism_other: 'Otro',
            frequency_real_time: 'Tiempo real',
            frequency_hourly: 'Cada hora',
            frequency_daily: 'Diario',
            frequency_weekly: 'Semanal',
            frequency_monthly: 'Mensual',
            frequency_on_demand: 'Bajo demanda',
            frequency_one_time: 'Puntual',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdicción',
        },
        activities: {
            title: 'Actividades de tratamiento',
            new: 'Nueva actividad',
            edit: 'Editar actividad',
            activity_title: 'Título',
            category: 'Categoría',
            legal_basis: 'Base jurídica',
            dpa_required: 'DPIA Required',
            ropa_generated: 'RAT generado',
            version: 'Versión',
            generate_ropa: 'Generar RAT',
            controller: 'Responsable del tratamiento',
            controller_name: 'Nombre del responsable del tratamiento',
            controller_contact: 'Contacto del responsable del tratamiento',
            legal_basis_detail: 'Detalle de la base jurídica',
            organization_info: 'Organization Information',
            org_address: 'Dirección de la organización',
            tax_code: 'Código fiscal',
            contact_person: 'Persona de contacto',
            contact_email: 'Correo de contacto',
            contact_phone: 'Teléfono de contacto',
            select_entity: 'Seleccione…',
            no_entities: 'No se encontraron artículos',
            dpo_name: 'Nombre del DPO',
            dpo_contact: 'Contacto del DPO',
            processor_name: 'Nombre del encargado',
            processor_contact: 'Contacto del encargado',
            data_subjects: 'Interesados',
            data_subjects_hint: 'Interesados (separados por comas)',
            recipients: 'Destinatarios',
            recipients_hint: 'Destinatarios (separados por comas)',
            security_measures: 'Medidas de seguridad',
            dpia_reference: 'Referencia EIPD',
            no_activities: 'No se encontraron actividades de tratamiento',
            confirm_archive: '¿Archivar "{name}"?',
            count: '{count} actividades',
            count_one: '{count} actividad',
            ropa_success: 'Registro RoPA generado. Ve a Exportaciones para descargarlo.',
            ropa_error: 'Error al generar el RAT',
            required_label: 'Requerido',
            status_draft: 'Borrador',
            status_active: 'Activo',
            status_under_review: 'En revisión',
            status_archived: 'Archivado',
            decree_165_heading: 'Nivel de datos del Decreto 165 (Ley de Datos de Vietnam)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Nivel de datos',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Nivel de datos',
            data_tier_regular: 'Regular',
            data_tier_important: 'Importante',
            data_tier_core: 'Núcleo',
            transfer_mechanism_label: 'Mecanismo de transferencia transfronteriza',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Referencia de evaluación de seguridad',
            security_assessment_reference_placeholder: 'por ej. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Repositorio de evidencias',
            new: 'Nueva evidencia',
            edit: 'Editar evidencia',
            name: 'Nombre',
            evidence_type: 'Tipo de evidencia',
            linked_entities: 'Entidades vinculadas',
            uploaded: 'Subida',
            expiry: 'Vencimiento',
            file_url: 'URL del archivo',
            source_mode_label: 'Fuente de la evidencia',
            source_mode_file: 'Subir archivo',
            source_mode_url: 'Enlazar URL externa',
            attachment_label: 'Archivo adjunto',
            link_to: 'Vincular evidencia a',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de entidad',
            no_evidence: 'No se encontraron elementos de evidencia',
            manage_links: 'Administrar enlaces',
            unlink: 'Desvincular',
            confirm_delete: '¿Eliminar la evidencia "{name}"?',
            count: '{count} elementos',
            count_one: '{count} elemento',
            type_document: 'Documento',
            type_screenshot: 'Captura de pantalla',
            type_policy: 'Política',
            type_contract: 'Contrato',
            type_assessment: 'Evaluación',
            type_correspondence: 'Correspondencia',
            type_other: 'Otro',
            linked_type_system: 'Sistema',
            linked_type_data_flow: 'Flujo de datos',
            linked_type_processing_activity: 'Actividad de procesamiento',
            linked_type_data_category: 'Categoría de datos',
            linked_type_purpose: 'Propósito',
            linked_type_retention_policy: 'Política de retención',
            link_entity: 'Enlace a la entidad',
            link_success: 'Entidad vinculada correctamente',
            confirm_unlink: '¿Está seguro de que desea desvincular esta entidad?',
            unlink_success: 'Entidad desvinculada exitosamente',
            no_linked_entities: 'Sin entidades vinculadas',
            select_entity: 'Seleccione…',
            no_entities: 'Sin artículos',
        },
        exports: {
            title: 'Exportaciones de datos',
            recent: 'Exportaciones recientes',
            export_type: 'Tipo de exportación',
            format: 'Formato',
            requested_by: 'Solicitado por',
            requested_at: 'Fecha de solicitud',
            download: 'Descargar',
            csv_export: 'Exportación CSV',
            csv_description: 'Descargar datos en formato CSV para análisis en hoja de cálculo.',
            pdf_reports: 'Informes PDF',
            pdf_description: 'Generar informes formateados para la documentación de cumplimiento.',
            csv_systems: 'Inventario de sistemas',
            csv_categories: 'Categorías de datos',
            csv_flows: 'Flujos de datos',
            csv_activities: 'Actividades de tratamiento',
            csv_cross_border: 'Flujos internacionales',
            csv_retention_policies: 'Políticas de retención',
            csv_evidence: 'Artículos de evidencia',
            csv_governance_assignments: 'Asignaciones de gobernanza',
            pdf_ropa: 'Registro de actividades de tratamiento',
            pdf_systems: 'Informe de sistemas',
            pdf_cross_border: 'Informe de transferencias internacionales',
            pdf_flow_map: 'Mapa de flujos de datos',
            export_failed: 'Error en la exportación',
            pdf_generated: 'PDF generado correctamente.',
            ropa: 'Registro RoPA',
            retention_policies: 'Políticas de retención',
            governance_assignments: 'Asignaciones de gobernanza',
            col_entity: 'Entidad Exportadora',
            col_description: 'Descripción',
            desc_ropa: 'Registro ROPA completo de todas las actividades de procesamiento con base legal, DPIA estado y controles.',
            desc_systems: 'Inventario de sistemas con propietario, etiquetas de categoría de datos y estado de cumplimiento.',
            desc_activities: 'Actividades de tratamiento individuales con responsable, base jurídica y clasificación DPIA.',
            desc_categories: 'Categorías de datos y niveles de sensibilidad utilizados en clasificaciones y auditorías.',
            desc_purposes: 'Finalidades del tratamiento con la base jurídica registrada para cada una.',
            desc_flows: 'Flujos de datos entre sistemas, incluidas rutas internas y transfronterizas.',
            desc_cross_border: 'Únicamente flujos transfronterizos, con mecanismo de transferencia y evaluación de riesgos.',
            desc_retention_policies: 'Políticas de retención, métodos de eliminación y cadencia de revisión.',
            desc_evidence: 'Pruebas adjuntas a las actividades de procesamiento: certificados, contratos DPA, informes DPIA.',
            desc_governance_assignments: 'Asignaciones de gobernanza: propietarios, aprobadores y referencias ISO 27001.',
            unified_heading: 'Entidades Exportadoras',
            unified_description: 'Exporte cualquier entidad de mapeo de datos en formato CSV, XLSX, PDF o DOCX. Todas las exportaciones se guardan en el Explorador de archivos.',
            export_button_label: 'Exportar',
            csv_download_success: 'CSV descargado correctamente.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Ciclos de Atestación',
            create: 'Nuevo Ciclo',
            name: 'Nombre del Ciclo',
            year: 'Año',
            deadline: 'Fecha Límite',
            scope: 'Alcance',
            status: 'Estado',
            tasks: 'Tareas de Departamento',
            assign_tasks: 'Asignar Tareas',
            department: 'Departamento',
            assignee: 'Asignado',
            progress: 'Progreso',
            stale_entries: 'Entradas Desactualizadas',
            detect_stale: 'Detectar Desactualizadas',
            export_bundle: 'Exportar Paquete',
            no_cycles: 'Aún no hay ciclos de atestación.',
            create_success: 'Ciclo de atestación creado.',
            assign_success: 'Tareas asignadas correctamente.',
            complete_success: 'Tarea completada.',
            stale_detected: 'Entradas desactualizadas detectadas.',
            stale_systems: 'Sistemas desactualizados',
            stale_flows: 'Flujos desactualizados',
            no_stale: 'No se detectaron entradas desactualizadas.',
            stale_name: 'Nombre',
            stale_announcement: 'Se encontraron {0} sistemas desactualizados y {1} flujos desactualizados',
            last_updated: 'Última actualización',
            status_draft: 'Borrador',
            status_active: 'Activo',
            status_completed: 'Completado',
            task_status_pending: 'Pendiente',
            task_status_in_progress: 'En progreso',
            task_status_completed: 'Completado',
            scope_systems: 'Sistemas',
            scope_flows: 'Flujos de datos',
            scope_activities: 'Actividades',
            scope_categories: 'Categorías',
            scope_purposes: 'Finalidades',
            scope_retention: 'Retención',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Asignaciones de Gobernanza',
            assign: 'Asignar Propiedad',
            entity_type: 'Tipo de Entidad',
            entity: 'Entidad',
            role: 'Rol',
            assigned_by: 'Asignado por',
            assigned_at: 'Asignado el',
            remove: 'Eliminar',
            no_assignments: 'Sin asignaciones de gobernanza.',
            assign_success: 'Propiedad asignada.',
            remove_success: 'Asignación eliminada.',
            assignee: 'Asignado',
            department: 'Departamento',
            all_departments: 'Todos los departamentos',
            roles: {
                data_owner: 'Propietario de Datos',
                data_steward: 'Administrador de Datos',
                data_custodian: 'Custodio de Datos',
                informed: 'Informado',
            },
            entity_types: {
                system: 'Sistema',
                flow: 'Flujo de datos',
                activity: 'Actividad',
                category: 'Categoría',
                purpose: 'Finalidad',
                retention_policy: 'Política de retención',
            },
        },
        risk_assessment: {
            title: 'Evaluación de Riesgos',
            create: 'Nueva Evaluación',
            safeguard_type: 'Tipo de Salvaguarda',
            adequacy_status: 'Estado de Adecuación',
            risk_factors: 'Factores de Riesgo',
            mitigations: 'Medidas de Mitigación',
            reviewer: 'Revisor',
            decision: 'Decisión',
            approve: 'Aprobar',
            reject: 'Rechazar',
            no_assessments: 'Sin evaluaciones de riesgos.',
            create_success: 'Evaluación de riesgos creada.',
            approve_success: 'Evaluación aprobada.',
            reject_success: 'Evaluación rechazada.',
            one_per_line: 'Uno por línea',
            adequacy_adequate: 'Adecuado',
            adequacy_inadequate: 'Inadecuado',
            adequacy_under_review: 'En revisión',
            adequacy_not_assessed: 'No evaluado',
            decision_approved: 'Aprobado',
            decision_rejected: 'Rechazado',
        },
        review: {
            title: 'Tareas de revisión',
            description: 'Las tareas de revisión se crean automáticamente cuando se propone una categoría de datos. Para crear una nueva tarea, vaya a Categorías de datos y haga clic en Proponer en una categoría borrador.',
            count: 'tareas de revisión',
            summary: 'Resumen de revisión',
            pending: 'Pendientes',
            resolved_today: 'Resueltas hoy',
            escalated: 'Escaladas',
            overdue: 'Atrasadas (>5 días)',
            all_statuses: 'Todos los estados',
            status_pending: 'Pendiente',
            status_resolved: 'Resuelta',
            status_escalated: 'Escalada',
            category_name: 'Categoría',
            system_name: 'Sistema',
            owner: 'Responsable',
            resolve: 'Resolver',
            resolve_task: 'Resolver tarea de revisión',
            resolution: 'Resolución',
            resolution_approve: 'Aprobar',
            resolution_modify: 'Modificar y aprobar',
            resolution_reject: 'Rechazar',
            resolution_escalate: 'Escalar',
            notes: 'Notas de resolución',
            notes_required: 'Notas obligatorias (mínimo 10 caracteres) para Modificar, Rechazar o Escalar',
            resolved_success: 'Tarea de revisión resuelta',
            bulk_approve: 'Aprobación masiva',
            bulk_reject: 'Rechazo masivo',
            bulk_success: '{count} tareas procesadas',
            no_tasks: 'No se encontraron tareas de revisión',
        },
        inventory: {
            title: 'Inventario de clasificación',
            count: 'sistemas',
            summary: 'Resumen del inventario',
            total_systems: 'Total de sistemas',
            total_labels: 'Total de etiquetas',
            system_name: 'Sistema',
            label_count: 'Etiquetas',
            approved_count: 'Aprobados',
            pending_count: 'Pendientes',
            no_items: 'No se encontraron elementos de inventario',
        },
        exceptions: {
            title: 'Excepciones de clasificación',
            count: 'excepciones',
            all_severities: 'Todas las severidades',
            all_statuses: 'Todos los estados',
            system_name: 'Sistema',
            severity_label: 'Severidad',
            status_open: 'Abierta',
            status_resolved: 'Resuelta',
            status_accepted_risk: 'Riesgo aceptado',
            resolve: 'Resolver',
            resolve_exception: 'Resolver excepción',
            resolution_type: 'Tipo de resolución',
            notes: 'Notas',
            justification_required: 'Justificación obligatoria (mínimo 10 caracteres) para riesgo aceptado',
            resolved_success: 'Excepción resuelta',
            no_exceptions: 'No se encontraron excepciones',
        },
        history: {
            title: 'Historial de clasificación',
            count: 'eventos',
            actor: 'Actor',
            action: 'Acción',
            entity_type: 'Tipo de entidad',
            changes: 'Cambios',
            view_lineage: 'Ver linaje',
            lineage_title: 'Linaje de clasificación',
            no_events: 'No se encontraron eventos de historial',
            no_lineage: 'No hay datos de linaje disponibles',
            modified: 'Modificado',
            created: 'Creado',
            removed: 'Eliminado',
            action_created: 'Creado',
            action_updated: 'Actualizado',
            action_archived: 'Archivado',
            action_approved: 'Aprobado',
            action_deprecated: 'Obsoleto',
            entity_label: 'Etiqueta',
            entity_taxonomy: 'Taxonomía',
            entity_review_task: 'Tarea de revisión',
            entity_exception: 'Excepción',
            before: 'Antes',
            after: 'Después',
        },
    },
    governance: {
        audit_trail_title: 'Pista de auditoría de gobernanza',
        audit_trail_subtitle: 'Flujo de auditoría multi-entidad para acciones de gobernanza: aprobaciones, ejecuciones de playbook, enlaces de evidencia y más.',
        audit_trail_item_count: 'eventos de auditoría',
        audit_trail_fetch_error: 'No se pudieron obtener los eventos de auditoría.',
        audit_trail_no_events: 'No se encontraron eventos de auditoría con los filtros actuales.',
        audit_trail_entity_type: 'Tipo de entidad',
        audit_trail_entity_id: 'ID de entidad',
        audit_trail_action: 'Acción',
        audit_trail_actor: 'Actor',
        audit_trail_timestamp: 'Marca de tiempo',
        audit_trail_date_from: 'Desde',
        audit_trail_date_to: 'Hasta',
        audit_trail_no_rows_to_export: 'No hay filas para exportar.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Proveedores',
            subprocessors: 'Subencargados',
            cross_border: 'Transferencias internacionales',
            drift: 'Monitor de desviaciones',
            scores: 'Puntuación de cumplimiento',
            playbooks: 'Guías de actuación',
            approvals: 'Aprobaciones',
            audit_trail: 'Pista de auditoría',
            board_report: 'Informe del Consejo',
            evidence: 'Evidencias',
            onboarding: 'Pipeline de incorporación',
            dpa_reviews: 'Revisiones de DPA',
            periodic_reviews: 'Revisiones periódicas',
            offboarding: 'Desvinculación',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Registro de proveedores',
            new: 'Nuevo proveedor',
            edit: 'Editar proveedor',
            name: 'Nombre',
            jurisdiction: 'Jurisdicción',
            dpa_status: 'Estado del DPA',
            risk_level: 'Nivel de riesgo',
            onboarding_status: 'Estado de incorporación',
            contact_email: 'Correo de contacto',
            contact_name: 'Nombre de contacto',
            contact_phone: 'Teléfono de contacto',
            website: 'Sitio web',
            services_csv: 'Servicios (separados por comas)',
            country: 'País',
            country_placeholder: 'ej. VN',
            last_reviewed: 'Última revisión',
            offboard: 'Dar de baja',
            offboard_vendor: 'Dar de baja al proveedor',
            offboard_reason: 'Motivo de baja',
            no_vendors: 'No se encontraron proveedores',
            vendor_count: '{count} proveedor(es)',
            all_dpa_status: 'Todos los estados DPA',
            all_risk_levels: 'Todos los niveles de riesgo',
            all_onboarding: 'Todos los estados de incorporación',
            fetch_error: 'Error. Por favor, inténtelo de nuevo.',
            save_error: 'Error al guardar el proveedor',
            offboard_error: 'Error al dar de baja',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Encargado del Tratamiento',
            sub_processor: 'Subencargado',
            controller: 'Responsable del Tratamiento',
            joint_controller: 'Corresponsable del Tratamiento',
        },
        onboarding_statuses: {
            pending_review: 'Pendiente de Revisión',
            approved: 'Aprobado',
            active: 'Activo',
            suspended: 'Suspendido',
            offboarded: 'Dado de Baja',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'No Requerido',
            required_missing: 'Requerido Faltante',
            draft: 'Borrador',
            under_review: 'En Revisión',
            signed: 'Firmado',
            expired: 'Vencido',
        },
        risk_levels: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
        },
        approval_statuses: {
            pending: 'Pendiente',
            approved: 'Aprobado',
            rejected: 'Rechazado',
            expired: 'Vencido',
        },
        approval_action_types: {
            vendor_onboarding: 'Alta de Proveedor',
            vendor_offboarding: 'Baja de Proveedor',
            cross_border_acceptance: 'Aceptación Transfronteriza',
            playbook_activation: 'Activación del Playbook',
            drift_accept_risk: 'Desviación Riesgo Aceptado',
            activity_activation: 'Activación de Actividad',
        },
        drift_check_types: {
            dpa_expiry: 'Vencimiento del DPA',
            dpa_missing: 'DPA Faltante',
            review_overdue: 'Revisión Vencida',
            cross_border_gap: 'Brecha Transfronteriza',
            sub_processor_chain: 'Cadena de Subencargados',
            risk_assessment_overdue: 'Evaluación de Riesgos Vencida',
            retention_policy_missing: 'Política de Retención Faltante',
            evidence_expiring: 'Evidencia por Vencer',
        },
        drift_statuses: {
            open: 'Abierto',
            acknowledged: 'Confirmado',
            in_progress: 'En Progreso',
            resolved: 'Resuelto',
            accepted_risk: 'Riesgo Aceptado',
        },
        drift_entity_types: {
            vendor: 'Proveedor',
            system: 'Sistema',
            data_flow: 'Flujo de Datos',
            processing_activity: 'Actividad de Tratamiento',
            evidence_item: 'Elemento de Evidencia',
        },
        playbook_statuses: {
            draft: 'Borrador',
            active: 'Activo',
            archived: 'Archivado',
        },
        playbook_action_types: {
            delete_data: 'Eliminar Datos',
            verify_deletion: 'Verificar Eliminación',
            notify_vendor: 'Notificar al Proveedor',
            confirm_receipt: 'Confirmar Recepción',
            document_evidence: 'Documentar',
            other: 'Otro',
        },
        playbook_target_types: {
            system: 'Sistema',
            vendor: 'Proveedor',
        },
        dpa_chain_statuses: {
            complete: 'Completo',
            incomplete: 'Incompleto',
            unknown: 'Desconocido',
        },
        sub_processor_statuses: {
            active: 'Activo',
            terminated: 'Terminado',
        },
        evidence_types: {
            dpa_agreement: 'Acuerdo DPA',
            risk_assessment: 'Evaluación de Riesgos',
            audit_report: 'Informe de Auditoría',
            certification: 'Certificación',
            deletion_confirmation: 'Confirmación de Eliminación',
            training_record: 'Registro de Capacitación',
            incident_report: 'Informe de Incidente',
            document: 'Documento',
            screenshot: 'Captura de Pantalla',
            policy: 'Política',
            contract: 'Contrato',
            assessment: 'Evaluación',
            correspondence: 'Correspondencia',
            other: 'Otro',
        },
        linkable_entity_types: {
            vendor: 'Proveedor',
            transfer_mechanism: 'Mecanismo de Transferencia',
            playbook: 'Libro de procedimientos',
            execution: 'Ejecución',
            system: 'Sistema',
            activity: 'Actividad',
        },
        subprocessors: {
            title: 'Relaciones con subencargados',
            new: 'Nuevo vínculo',
            vendor: 'Proveedor',
            subprocessor: 'Subencargado',
            relationship_type: 'Tipo de relación',
            countries: 'Países',
            countries_csv: 'Países (separados por comas)',
            dpa_chain: 'Cadena DPA',
            dpa_chain_status: 'Estado de la cadena DPA',
            no_processors: 'No se encontraron encargados.',
            no_subprocessors: 'No se encontraron subencargados.',
            processor_vendor: 'Proveedor encargado',
            select_processor: 'Seleccionar encargado',
            select_vendor: 'Seleccionar proveedor',
            services: 'Servicios',
            sub_processor_vendor_id: 'ID del subencargado',
            terminate: 'Rescindir',
            confirm_terminate: '¿Está seguro de que desea rescindir esta relación?',
            create_error: 'Error al crear el vínculo.',
            fetch_error: 'Error al obtener los subencargados.',
            processor: 'Encargado',
            services_csv: 'Servicios (separados por comas)',
            terminate_error: 'Error al rescindir.',
            version_history_heading: 'Versiones de cambio de subprocesador',
            change_tracking_note: 'Todos los cambios en la relación del subprocesador están versionados. Cada modificación registra el estado antes/después para el seguimiento de auditoría.',
            history_retention_note: 'El historial de cambios se conserva durante todo el período de retención de datos. Revise los registros de cambios a través de la vista de detalles del proveedor.',
            vendor_risk_cross_view_heading: 'Registro de subprocesadores (Vendor Risk)',
            vendor_risk_cross_view_source: 'Fuente: Vendor Risk Register',
            vendor_risk_cross_view_empty: 'Sin entradas en el registro de Vendor Risk.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Informe de gobernanza de transferencias internacionales',
            flows: 'Flujos internacionales',
            transfers: 'Transferencias internacionales',
            detected_gaps: 'Deficiencias detectadas',
            safeguards: 'Garantías',
            cross_border_vendors: 'Proveedores con transferencias internacionales',
            destination: 'Destino',
            no_cross_border_vendors: 'No se encontraron proveedores con transferencias internacionales.',
            no_safeguard: 'Sin garantía',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Ubicaciones de tratamiento',
            export_error: 'Error al exportar el informe.',
            export_pdf: 'Exportar PDF',
            export_success: 'Informe exportado correctamente.',
            exporting: 'Exportando...',
            fetch_error: 'Error al obtener los datos de transferencias internacionales.',
            source: 'Origen',
            pdpl_p2_heading: 'PDPL-P2 Cumplimiento de transferencias transfronterizas',
            dossier_prep_note: 'Preparación del expediente: reunir la evaluación de impacto, la base jurídica y la documentación de salvaguardia antes de presentarlo.',
            filing_tracking_note: 'Seguimiento de la presentación: supervise el estado de la presentación ante la Autoridad (PDPC) desde la solicitud inicial hasta la aprobación.',
            supplement_loop_note: 'Bucle complementario: responder a las solicitudes de información de la Autoridad y volver a presentar los expedientes modificados según sea necesario.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Búsqueda de proveedor',
            vendor_search_placeholder: 'Buscar por nombre de proveedor…',
            jurisdiction_filter_label: 'Jurisdicción',
            filings_section_heading: 'Presentaciones transfronterizas',
            filings_empty: 'Aún no hay presentaciones. Cree uno para iniciar un expediente.',
            filings_create_button: 'Nueva presentación',
            filings_table_id: 'ID de presentación',
            filings_table_jurisdiction: 'Jurisdicción',
            filings_table_type: 'tipo de formulario',
            filings_table_status: 'Estado',
            filings_table_updated: 'Actualizado',
            filings_table_open: 'Abierto',
            filings_detail_title: 'Presentación transfronteriza',
            filings_back_to_list: 'Volver a Transfronterizo',
            filings_status_heading: 'Ciclo de vida',
            filings_evidence_heading: 'evidencia',
            filings_history_heading: 'Pista de auditoría',
            filings_field_filing_id: 'ID de presentación',
            filings_field_jurisdiction: 'Jurisdicción',
            filings_field_type: 'tipo de formulario',
            filings_field_status: 'Estado',
            filings_field_reference: 'Referencia de autoridad',
            filings_field_mps_receipt: 'MPS recibo',
            filings_field_submitted_at: 'Presentado en',
            filings_field_acknowledged_at: 'Reconocido en',
            filings_field_archived_at: 'Archivado en',
            filings_field_version: 'Versión',
            filings_field_notes: 'Notas',
            filings_action_advance: 'Estado de avance',
            filings_action_reject: 'Rechazar',
            filings_action_archive: 'Archivo',
            filings_action_submit: 'Marcar enviado',
            filings_action_acknowledge: 'Marcos reconoció',
            filings_action_resume_draft: 'Reanudar como borrador',
            filings_status_draft: 'Borrador',
            filings_status_assessed: 'Evaluado',
            filings_status_pending_review: 'Pendiente de revisión',
            filings_status_ready_to_file: 'Listo para presentar',
            filings_status_submitted: 'Enviado',
            filings_status_acknowledged: 'Reconocido',
            filings_status_rejected: 'Rechazado',
            filings_status_archived: 'Archivado',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Nueva presentación transfronteriza',
            filings_new_form_jurisdiction: 'Jurisdicción',
            filings_new_form_type: 'tipo de formulario',
            filings_new_form_notes: 'Notas (opcional)',
            filings_new_submit: 'crear',
            filings_new_cancel: 'Cancelar',
            filings_reason_label: 'Razón',
            filings_reference_label: 'Número de referencia de la autoridad',
            filings_mps_receipt_label: 'MPS referencia del recibo',
            filings_create_error: 'No se pudo crear el archivo.',
            filings_create_success: 'Archivo creado.',
            filings_update_error: 'No se pudo actualizar la presentación.',
            filings_update_success: 'Archivo actualizado.',
            filings_fetch_error: 'No se pudieron recuperar las presentaciones.',
            filings_archive_confirm: '¿Archivar este archivo? Esto no se puede deshacer.',
            filings_status_rejected_detour: 'La presentación fue rechazada después de la revisión. Reanudar como borrador para reiniciar el flujo.',
            recent_exports_heading: 'Exportaciones recientes',
            recent_exports_empty: 'Aún no hay exportaciones. Haga clic en "Exportar PDF" para generar uno.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Monitor de desviaciones de configuración',
            drift_type: 'Tipo de desviación',
            severity: 'Gravedad',
            detected_at: 'Detectada el',
            resolve: 'Resolver',
            all_check_types: 'Todos los tipos de verificación',
            all_entity_types: 'Todos los tipos de entidad',
            event_count: 'Número de eventos',
            accept_risk: 'Aceptar riesgo',
            acknowledge: 'Confirmar',
            assign_error: 'Error al asignar.',
            assign_event: 'Asignar',
            assigned: 'Asignado',
            enter_resolution_notes: 'Introduzca las notas de resolución...',
            enter_user_id: 'Introduzca el ID de usuario...',
            entity: 'Entidad',
            fetch_error: 'Error al obtener los eventos de desviación.',
            no_events: 'No se encontraron eventos de desviación.',
            resolution_notes: 'Notas de resolución',
            update_status_error: 'Error al actualizar el estado.',
        },
        scores: {
            title: 'Panel de puntuación de cumplimiento',
            overall: 'Puntuación global de cumplimiento',
            breakdown: 'Desglose por módulo',
            trend: 'Tendencia histórica',
            module: 'Módulo',
            score: 'Puntuación',
            grade: 'Calificación',
            calculated_at: 'Calculado el',
            generate_board_report: 'Generar informe para el consejo',
            lang_english: 'Inglés',
            lang_vietnamese: 'Vietnamita',
            fetch_error: 'Error al obtener las puntuaciones de cumplimiento.',
            generate_report: 'Generar informe',
            generating: 'Generando...',
            recommendations: 'Recomendaciones',
            report_error: 'Error al generar el informe.',
            factor_owner_assigned: 'Propietario asignado',
            factor_data_categories_linked: 'Categorías de datos vinculadas',
            factor_purposes_linked: 'Finalidades vinculadas',
            factor_data_flows_documented: 'Flujos de datos documentados',
            factor_processing_activity_linked: 'Actividad de tratamiento vinculada',
            factor_dpa_status: 'Estado del DPA',
            factor_retention_policy_linked: 'Política de retención vinculada',
            factor_last_reviewed: 'Última revisión dentro del umbral',
            factor_risk_assessment_current: 'Evaluación de riesgos actualizada',
            factor_sub_processors_documented: 'Subencargados documentados',
            factor_cross_border_safeguards: 'Salvaguardas transfronterizas',
            factor_contact_info_complete: 'Información de contacto completa',
            factor_onboarding_approved: 'Incorporación aprobada',
            factor_avg_system_score: 'Puntuación media del sistema',
            factor_avg_vendor_score: 'Puntuación media del proveedor',
            factor_processing_activity_coverage: 'Cobertura de actividades de tratamiento',
            factor_ropa_completeness: 'Completitud del RoPA',
            factor_open_drift_events: 'Eventos de desviación abiertos',
        },
        playbooks: {
            title: 'Guías de supresión',
            new: 'Nueva guía',
            edit: 'Editar guía',
            steps: 'Pasos',
            add_step: 'Añadir paso',
            remove_step: 'Eliminar paso',
            data_categories_csv: 'Categorías de datos (separadas por comas)',
            estimated_duration: 'Duración estimada',
            estimated_duration_label: 'Duración estimada',
            evidence_description: 'Descripción de la evidencia',
            no_playbooks: 'No se encontraron guías de actuación.',
            playbook_count: 'Número de guías',
            requires_evidence: 'Requiere evidencia',
            step_label: 'Etiqueta del paso',
            target_name: 'Nombre de destino',
            target_type: 'Tipo de objetivo',
            action_type: 'Tipo de acción',
            archive_error: 'Error al archivar.',
            confirm_archive: '¿Está seguro de que desea archivar esta guía de actuación?',
            data_categories: 'Categorías de datos',
            days: 'días',
            fetch_error: 'Error al obtener las guías de actuación.',
            sla_days: 'SLA (días)',
            target_id: 'ID del objetivo',
            template_class_heading: 'Clasificación de plantillas',
            official: 'Oficial',
            internal: 'Interno',
            classification: 'Clasificación',
            official_note: 'Oficial: plantillas reglamentarias obligatorias alineadas con PDPL, GDPR o requisitos específicos del sector.',
            internal_note: 'Interno: procedimientos específicos de la organización no derivados de plantillas regulatorias.',
            active_executions_heading: 'Ejecuciones activas',
            no_active_executions: 'No hay ejecuciones activas.',
            playbook_label: 'Plan',
            ticket_id_label: 'Boleto',
            progress_label: 'Progreso',
            actions_label: 'Acciones',
            view_steps_label: 'Ver pasos',
            execution_detail_title: 'Detalle de ejecución',
            playbook_missing: 'Plan no encontrado.',
            evidence_required_label: 'Evidencia requerida',
            upload_evidence_label: 'Subir evidencia',
            mark_complete_label: 'Marcar como completado',
            evidence_required_toast: 'Se requiere evidencia antes de completar.',
            evidence_blocking_message: 'Adjunta un archivo de evidencia para continuar.',
            step_completed_toast: 'Paso completado.',
            executions_fetch_error: 'No se pudieron cargar las ejecuciones.',
            step_evidence_default_name: 'Evidencia del paso de ejecución',
            attached_evidence_label: 'Evidencias adjuntas',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Flujos de aprobación',
            new: 'Nueva aprobación',
            edit: 'Editar aprobación',
            request_type: 'Tipo de solicitud',
            requester: 'Solicitante',
            approve: 'Aprobar',
            reject: 'Rechazar',
            justification: 'Justificación',
            all_action_types: 'Todos los tipos de acción',
            decided_by: 'Decidido por',
            no_approvals: 'No se encontraron aprobaciones.',
            approval_count: 'Número de aprobaciones',
            approval_notes_optional: 'Notas (opcional)',
            approve_error: 'Error al aprobar.',
            approve_request: 'Aprobar solicitud',
            decision: 'Decisión',
            entity: 'Entidad',
            fetch_error: 'Error al obtener las aprobaciones.',
            reject_error: 'Error al rechazar.',
            reject_request: 'Rechazar solicitud',
            rejection_notes_required: 'Notas de rechazo obligatorias',
            rejection_notes_required_msg: 'Por favor, proporcione un motivo de rechazo.',
            requested_at: 'Solicitado el',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Evidencia de apoyo (opcional)',
            decision_evidence_name: 'Evidencia de decisión',
        },
        evidence: {
            title: 'Evidencias de gobernanza',
            new: 'Nueva evidencia',
            link_to: 'Vincular a',
            entity_id_placeholder: 'ID de entidad',
            file_path_placeholder: '/ruta/al/archivo',
            evidence_label: 'Etiqueta de la evidencia',
            file_path: 'Ruta del archivo',
            source_mode_label: 'Fuente de la evidencia',
            source_mode_file: 'Subir archivo',
            source_mode_url: 'Enlazar URL externa',
            attachment_label: 'Archivo adjunto',
            item_count: 'Número de elementos',
            link_button: 'Vincular evidencia',
            confirm_delete: '¿Está seguro de que desea eliminar esta evidencia?',
            enter_entity_id: 'Introduzca el ID de entidad...',
            entity_id: 'ID de entidad',
            entity_type: 'Tipo de entidad',
            expires: 'Vence',
            expires_at: 'Vence el',
            fetch_error: 'Error al obtener las evidencias.',
            link_error: 'Error al vincular la evidencia.',
            no_evidence: 'No se encontraron evidencias.',
            tags_csv: 'Etiquetas (separadas por comas)',
            uploaded: 'Subida',
            url: 'URL',
            framework_tag_heading: 'Evidencia etiquetada en el marco',
            framework_tag_note: 'Cada registro de evidencia captura el código marco, el actor y la marca de tiempo. Filtre por marco para aislar registros relevantes para la auditoría.',
            framework_code: 'Código marco',
            filter_by_framework: 'Filtrar por código marco...',
            actor: 'Actor',
        },
        certification: {
            not_applicable: 'No aplica',
            not_certified: 'No certificado',
            pending: 'Pendiente',
            certified: 'Certificado',
            revoked: 'Revocado',
        },
        onboarding_pipeline: {
            title: 'Pipeline de incorporación',
            new_workflow: 'Nuevo flujo de incorporación',
            vendor_id: 'ID de proveedor',
            stages: 'Etapas',
            current_stage: 'Etapa actual',
            advance: 'Avanzar etapa',
            advance_success: 'Etapa avanzada con éxito',
            workflow_status: 'Estado del flujo',
            initiated: 'Iniciado',
            in_progress: 'En progreso',
            completed: 'Completado',
            cancelled: 'Cancelado',
            no_workflows: 'No se encontraron flujos de incorporación',
            stages_placeholder: 'Etapa 1, Etapa 2, Etapa 3',
            assigned_to: 'Asignado a',
            notes: 'Notas',
            pdpl_lifecycle_heading: 'PDPL Ciclo de vida del proveedor: incorporación',
            due_diligence_note: 'La etapa de diligencia debida es obligatoria antes de incorporar cualquier procesador bajo PDPL. Verificar el alcance del tratamiento de los datos, la base legal y las medidas de seguridad.',
            contract_review_note: 'La revisión del contrato debe confirmar las cláusulas de DPA, los límites de retención de datos y las restricciones del subprocesador.',
            create_dpa_review_cta: 'Crear revisión de DPA',
            dpa_review_linked: 'Revisión de DPA vinculada',
        },
        dpa_review: {
            title: 'Revisiones de DPA',
            new_review: 'Nueva revisión de DPA',
            review_type: 'Tipo de revisión',
            findings: 'Hallazgos',
            outcome: 'Resultado',
            complete_review: 'Completar revisión',
            complete_success: 'Revisión completada con éxito',
            link_assessment: 'Vincular evaluación de seguridad',
            link_success: 'Evaluación vinculada con éxito',
            assessment_id: 'ID de evaluación',
            combined_title: 'Evaluación combinada',
            no_reviews: 'No se encontraron revisiones de DPA',
            telecom_disclosure_heading: 'Gobernanza de la divulgación de telecomunicaciones',
            disclosure_trigger_note: 'Las revisiones activadas por divulgación capturan eventos de intercambio de datos de suscriptores de telecomunicaciones según la Ley de VN Telecom.',
            telecom_t2_note: 'Se requiere aprobación de nivel TELECOM-T2 antes de revelar los datos del suscriptor a las autoridades.',
            quarterly_log_note: 'Utilice el registro trimestral para documentar todos los eventos de divulgación y mantener un seguimiento de auditoría continuo.',
        },
        periodic_review: {
            title: 'Revisiones periódicas',
            new_review: 'Nueva revisión periódica',
            review_type: 'Tipo de revisión',
            due_date: 'Fecha de vencimiento',
            next_review: 'Próxima fecha de revisión',
            trigger_reassessment: 'Desencadenar reevaluación',
            reassessment_reason: 'Motivo de reevaluación',
            reassessment_success: 'Reevaluación desencadenada con éxito',
            complete_success: 'Revisión completada con éxito',
            scheduled: 'Programada',
            in_progress: 'En progreso',
            completed: 'Completada',
            overdue: 'Vencida',
            material_change: 'Cambio significativo',
            no_reviews: 'No se encontraron revisiones periódicas',
            mark_complete: 'Marcar como completado',
            pdpl_periodic_heading: 'PDPL Ciclo de vida del proveedor: revisiones periódicas',
            scheduled_review_note: 'Se deben programar revisiones periódicas al menos una vez al año para todos los procesadores activos. Las revisiones vencidas activan alertas de cumplimiento.',
            material_change_note: 'Los cambios materiales en el procesamiento de datos requieren una reevaluación inmediata independientemente del cronograma.',
            overdue_badge: 'Vencido',
            overdue_aria: 'Revisión vencida',
            only_overdue_filter: 'Solo vencidos',
        },
        offboarding: {
            title: 'Desvinculación de proveedor',
            initiate: 'Iniciar desvinculación',
            initiate_success: 'Desvinculación iniciada con éxito',
            reason: 'Motivo de desvinculación',
            data_return_required: 'Devolución de datos requerida',
            verify_data: 'Verificar devolución de datos',
            verify_success: 'Devolución de datos verificada con éxito',
            sign_off: 'Aprobar',
            sign_off_success: 'Desvinculación aprobada con éxito',
            status_initiated: 'Iniciada',
            status_data_pending: 'Devolución de datos pendiente',
            status_data_verified: 'Devolución de datos verificada',
            status_completed: 'Completada',
            status_cancelled: 'Cancelada',
            task_id: 'ID de tarea',
            no_tasks: 'No se encontraron tareas de desvinculación',
            evidence_id: 'ID de evidencia',
            pdpl_offboarding_heading: 'PDPL Ciclo de vida del proveedor: baja y eliminación',
            deletion_capture_note: 'La baja debe capturar la confirmación de eliminación de datos del proveedor. Verifique la devolución de datos antes de cerrar sesión.',
            evidence_attachment_note: 'Adjunte pruebas de eliminación (certificados, capturas de pantalla, confirmaciones de proveedores) para completar el registro de baja.',
            verify_mode_label: 'Método de verificación',
            verify_mode_upload: 'Subir nuevo archivo',
            verify_mode_link: 'Enlazar evidencia existente',
            verify_mode_none: 'No hay datos que devolver',
            upload_data_return_evidence: 'Subir evidencia de devolución de datos',
            no_data_justification_label: 'Justificación',
            no_data_justification_placeholder: 'Explica por qué no hay datos que devolver',
            no_data_justification_hint: 'Mínimo 20 caracteres',
            upload_required_toast: 'Adjunta un archivo para continuar',
            evidence_link_required_toast: 'Selecciona una evidencia para continuar',
            no_data_justification_required_toast: 'Proporciona una justificación de al menos 20 caracteres',
            data_return_evidence_name: 'Evidencia de devolución de datos',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Inicial',
            annual: 'Anual',
            renewal: 'Renovación',
            incident_triggered: 'Por incidente',
        },
        periodic_review_types: {
            initial: 'Inicial',
            annual: 'Anual',
            renewal: 'Renovación',
            incident_triggered: 'Por incidente',
            material_change: 'Cambio material',
            disclosure_triggered: 'Por divulgación',
            quarterly_log: 'Registro trimestral',
        },
        dpa_review_statuses: {
            pending: 'Pendiente',
            in_progress: 'En progreso',
            completed: 'Completado',
            cancelled: 'Cancelado',
        },
        onboarding_stage_names: {
            due_diligence: 'Debida diligencia',
            contract_review: 'Revisión de contrato',
            security_assessment: 'Evaluación de seguridad',
            data_mapping: 'Mapeo de datos',
            approval: 'Aprobación',
            integration: 'Integración',
        },
        access_denied_title: 'Acceso denegado',
        access_denied: 'No tiene permiso para ver esta página. Comuníquese con su administrador.',
    },
    consent: {
        nav: {
            policies: 'Políticas',
            purposes: 'Catálogo de finalidades',
            workflows: 'Flujos de trabajo',
            snapshots: 'Instantáneas CMP',
            change_awareness: 'Detección de cambios',
            audit: 'Auditoría del consentimiento',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Análisis de brechas',
            page_title: 'Análisis de brechas de consentimiento',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Marcos',
            org_flags_label: 'Contexto de la organización',
            flag_withdrawal: '¿Se ha documentado el mecanismo de retiro global?',
            flag_parental: 'Tiene flujo de consentimiento de los padres para sujetos menores de 16 años.',
            flag_cross_border: 'Tiene registrada una presentación transfronteriza',
            run_label: 'Ejecutar análisis de brechas',
            running_label: 'Corriendo…',
            requires_advisor_label: 'Requiere ai_advisor:permiso de chat.',
            results_heading: 'Surgieron lagunas ({count})',
            tokens_label: '≈ {count} fichas',
            empty_label: 'No se detectaron lagunas para los marcos seleccionados.',
            remediation_label: 'Remediación sugerida:',
            dismiss_label: 'Descartar por no aplicar',
            resolve_label: 'resolver',
            dismissed_label: 'Desestimado por no aplicar',
            resolved_label: 'Resuelto',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importar flujos de consentimiento',
            nav_title: 'Importación masiva',
            description: 'Suba un archivo CSV para importar en masa flujos de trabajo de consentimiento.',
            select_file: 'Seleccionar archivo CSV',
            file_help: 'CSV con encabezados que coincidan con los campos del flujo de trabajo.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Columnas obligatorias: Tipo de flujo, Interesado, Canal.',
            file_too_large: 'Archivo demasiado grande. El tamaño máximo es 2MB.',
            preview: 'Vista previa',
            columns_detected: 'Columnas detectadas: {count}',
            column_mapping: 'Asignación de columnas',
            import_button: 'Importar',
            import_complete: 'Importación completada',
            import_error: 'La importación falló',
            result: 'Resultado de la importación',
            total: 'totales',
            imported: 'Importado',
            skipped: 'Omitido',
            failed: 'Fallido',
            errors: 'Errores',
            error_row: 'Fila',
            error_field: 'Campo',
            error_message: 'Mensaje',
            history: 'Historial de importación',
            no_history: 'Sin historial de importación.',
            history_file: 'Nombre del archivo',
            history_date: 'Fecha',
            create_profiles_label: 'Crear perfiles de Interesado para nuevos sujetos',
            create_profiles_help: 'Las importaciones sin perfil coincidente crearán uno automáticamente. Desmarque para importar solo flujos de trabajo.',
            profiles_created: 'Perfiles creados',
            profiles_matched: 'Perfiles coincidentes',
            subject_kind_help: 'Columnas opcionales: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Correo electrónico no válido en la columna subject_email.',
            error_subject_unknown_kind: 'Valor de subject_kind desconocido. Esperado: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key es obligatorio cuando subject_kind=external_ref',
        },
        policies: {
            title: 'Políticas de consentimiento',
            new: 'Nueva política',
            edit: 'Editar política',
            name: 'Nombre',
            version: 'Versión',
            policy_text: 'Texto de la política',
            publish: 'Publicar',
            versions: 'Versiones',
            hide_versions: 'Ocultar versiones',
            submit_for_review: 'Enviar para Revisar',
            suspend: 'Suspender',
            retire: 'Retirar',
            jurisdictions: 'Jurisdicciones',
            jurisdictions_csv: 'Jurisdicciones (separadas por comas)',
            approval_mode: 'Modo de aprobación',
            reviewer_ids: 'IDs de revisores (separados por comas)',
            tags_csv: 'Etiquetas (separadas por comas)',
            effective_date: 'Fecha de vigencia',
            review_date: 'Revisar Fecha',
            consent_model_statement: 'Declaración del modelo de consentimiento',
            consent_model_placeholder: 'Declaración opcional del modelo de consentimiento...',
            default_settings: 'Predeterminado Configuración (JSON)',
            changes: 'Cambios',
            fetch_error: 'Error al fetch consentimiento políticas. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save consentimiento política. Por favor, inténtelo de nuevo.',
            submit_review_error: 'Error al submit policy for review. Por favor, inténtelo de nuevo.',
            activate_error: 'Error al activate policy. Por favor, inténtelo de nuevo.',
            suspend_error: 'Error al suspend policy. Por favor, inténtelo de nuevo.',
            retire_error: 'Error al retire policy. Por favor, inténtelo de nuevo.',
            submit_review_success: 'Política enviada para revisión.',
            activate_success: 'Política activada.',
            suspend_success: 'Política suspendida.',
            retire_success: 'Política retirada.',
            versions_error: 'Error al fetch policy versions. Por favor, inténtelo de nuevo.',
            invalid_json: 'JSON no válido en el campo de configuración predeterminada. Inténtelo de nuevo.',
            page_title: 'Políticas de consentimiento',
            version_updated: 'Actualizado',
            version_initial: 'Versión inicial',
            json_format: 'JSON formato',
            sensitive: 'sensible',
            flag_sensitive: 'Bandera sensible',
            flag_sensitive_title: 'Marcar política como sensible',
            require_enhanced_review: 'Requerir revisión mejorada',
            enhanced_review_success: 'Requisito de revisión mejorado habilitado.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Seleccione las categorías de datos confidenciales del PDPL artículo 2 aplicables:',
            no_policies: 'No se encontraron políticas de consentimiento. Cree su primera política para comenzar.',
            approval_success: 'Política aprobada.',
            reviewers_label: 'Revisores',
            enhanced_review_title: 'Revisión mejorada requerida',
            enhanced_review_badge: 'Revisión mejorada requerida',
            enhanced_review_status_pending: 'Revisión pendiente',
            enhanced_review_status_approved: 'Revisión aprobada',
            enhanced_review_status_rejected: 'Revisión rechazada',
            enhanced_review_reviewed_by: 'Revisado por',
            enhanced_review_notes: 'Notas de revisión',
            enhanced_review_approve: 'Aprobar revisión',
            enhanced_review_reject: 'Rechazar revisión',
            enhanced_review_notes_required: 'Las notas de revisión son obligatorias',
            enhanced_review_approved: 'Revisión mejorada aprobada',
            enhanced_review_rejected: 'Revisión mejorada rechazada — política restablecida a borrador',
            enhanced_review_reset_to_draft: 'Revisión mejorada rechazada — política restablecida a borrador',
            enhanced_review_reject_warning: 'El rechazo restablecerá esta política al estado Borrador. El DPO debe volver a enviarla para revisión después de abordar los problemas.',
            submitted_modal_title: 'Enviado para revisión mejorada',
            submitted_modal_body: 'Esta política ha sido enviada. Un revisor Legal/Privacidad debe aprobarla antes de que pueda ser activada.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Datos de salud',
            biometric: 'Datos biometricos',
            political: 'Opiniones politicas',
            ethnic: 'Origen etnico',
            genetic: 'Datos geneticos',
            criminal: 'Antecedentes penales',
        },
        purposes: {
            title: 'Catálogo de finalidades',
            new: 'Nueva finalidad',
            edit: 'Editar finalidad',
            name: 'Nombre',
            legal_basis: 'Base jurídica',
            retention_days: 'Período de conservación (días)',
            is_active: 'Activa',
            filter_by_category: 'Filtrar por categoría',
            policy_ids: 'IDs de políticas (separados por comas)',
            ropa_activity_ids: 'IDs de actividades ROPA (separados por comas)',
            policies: 'Políticas',
            deprecate: 'Deprecar',
            fetch_error: 'Error al fetch consent purposes. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save consent purpose. Por favor, inténtelo de nuevo.',
            deprecate_error: 'Error al deprecate consent purpose. Por favor, inténtelo de nuevo.',
            page_title: 'Catálogo de Propósitos',
            no_purposes: 'No se encontraron propósitos de consentimiento. Cree su primer propósito para comenzar.',
            category_personal_data: 'Datos personales',
            category_sensitive_data: 'Datos confidenciales',
            category_health_data: 'Datos de salud',
            category_financial_data: 'Datos financieros',
            category_biometric_data: 'Datos biométricos',
            category_children_data: 'Datos de los niños',
            category_location_data: 'Datos de ubicación',
            category_communications_data: 'Datos de comunicaciones',
            category_other: 'Otro',
        },
        workflows: {
            title: 'Flujos de trabajo del consentimiento',
            new: 'Nuevo flujo de trabajo',
            edit: 'Editar flujo de trabajo',
            workflow_type: 'Tipo de flujo de trabajo',
            user_id: 'ID de usuario',
            purpose_id: 'ID de finalidad',
            purpose_ids_csv: 'Finalidad ID (comma-separated)',
            reason: 'Motivo',
            process: 'Procesar',
            data_subject_ref: 'Datos Sujeto Reference',
            data_subject: 'Datos Sujeto',
            channel: 'Canal',
            policy_id: 'Política ID',
            scope: 'Alcance',
            legal_grounds: 'Bases legales',
            assessment: 'Evaluación',
            select_outcome: 'Seleccione Outcome',
            partially_approved: 'Aprobado parcialmente',
            denied: 'Denegado',
            outcome_upheld: 'Confirmado',
            outcome_pending: 'Pendiente',
            outcome_denied: 'Denegado',
            status_count: 'Conteo por estado',
            channel_filter: 'Filtrar por canal',
            add_action: '+ Acción',
            by_type: 'Por Tipo',
            by_status: 'Por Estado',
            downstream_actions: 'Acciones posteriores',
            downstream_actions_for: 'Downstream Acciones para {subject} ({type})',
            add_downstream_action: 'Agregar Downstream Action',
            assigned_to: 'Asignado Hasta',
            fetch_error: 'Error al fetch consent workflows. Por favor, inténtelo de nuevo.',
            dashboard_error: 'Error al fetch workflow dashboard. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save consent workflow. Por favor, inténtelo de nuevo.',
            status_update_error: 'Error al update workflow status. Por favor, inténtelo de nuevo.',
            assessment_error: 'Error al update workflow assessment. Por favor, inténtelo de nuevo.',
            add_action_error: 'Error al add workflow action. Por favor, inténtelo de nuevo.',
            update_action_error: 'Error al update workflow action. Por favor, inténtelo de nuevo.',
            type_refusal: 'Rechazo',
            type_withdrawal: 'Retiro',
            type_restriction: 'Restricción',
            type_objection: 'Oposición',
            status_received: 'Recibido',
            status_processing: 'Procesamiento',
            status_under_assessment: 'En evaluación',
            status_implemented: 'Implementado',
            evidence_button: 'evidencia',
            evidence_modal_title: 'Evidencia de flujo de trabajo',
            no_evidence: 'No hay pruebas adjuntas a este flujo de trabajo.',
            upload_evidence_label: 'Cargar evidencia de flujo de trabajo',
            completed_at: 'Completado en',
            effective_date: 'Fecha de vigencia',
            page_title: 'Flujos de trabajo de consentimiento',
            no_workflows: 'No se encontraron flujos de consentimiento. Los flujos se crean cuando los interesados ejercen sus derechos.',
            type_grant: 'Concesión',
            propagation_none: 'Ninguno',
            propagation_pending: 'Pendiente',
            propagation_pending_warning: 'No se puede completar: la propagación de retirada aún está pendiente',
            propagation_completed: 'Completado',
            propagation_partial: 'Parcial',
            propagation_incomplete_error: 'No se puede completar el flujo mientras la propagación de retirada aún esté pendiente',
            propagation_status: 'Estado de propagación',
        },
        snapshots: {
            title: 'Instantáneas CMP',
            new: 'Nueva instantánea',
            label: 'Etiqueta',
            snapshot_date: 'Fecha de la instantánea',
            purposes_count: 'Número de finalidades',
            compare: 'Comparar',
            snapshot_data: 'Datos de la instantánea (JSON)',
            select_left: 'Seleccione Left Snapshot',
            select_right: 'Seleccione Right Snapshot',
            comparison_results: 'Resultados de comparación',
            field: 'Campo',
            left: 'Izquierda',
            right: 'Derecha',
            close_comparison: 'Cerrar comparación',
            trigger: 'Activador',
            trigger_manual: 'manuales',
            trigger_policy_approval: 'Aprobación de políticas',
            cmp_version: 'Versión de CMP',
            policy_version_id: 'Política Versión ID',
            banner_config: 'Configuración del banner (JSON)',
            active_purposes: 'Activo Finalidades (JSON)',
            active_legal_bases: 'Bases legales activas (JSON)',
            domain_scope: 'Ámbito del dominio',
            domain_scope_csv: 'Ámbito del dominio (separado por comas)',
            raw_config: 'Configuración sin procesar (JSON)',
            checksum: 'Suma de verificación',
            created: 'Creado',
            fetch_error: 'Error al fetch cmp snapshots. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save cmp snapshot. Por favor, inténtelo de nuevo.',
            compare_error: 'Error al compare snapshots. Por favor, inténtelo de nuevo.',
            page_title: 'Instantáneas de consentimiento',
            json_format: 'JSON formato',
            json_array_format: 'JSON formato de matriz',
            manual_snapshot_note: 'Solo para administradores/desarrolladores. En funcionamiento normal, las instantáneas se crean automáticamente a través del webhook CMP o al aprobar políticas. Pegue el JSON exportado desde su proveedor CMP (p.ej. OneTrust, Cookiebot).',
            banner_config_hint: 'Pegue el objeto de configuración del banner desde su exportación CMP.',
            active_purposes_hint: 'Array JSON de objetos de propósitos activos copiados de su exportación CMP.',
            active_legal_bases_hint: 'Array JSON de objetos de bases legales activas copiados de su exportación CMP.',
            raw_config_hint: 'JSON de configuración completo sin procesar de su CMP. Los demás campos JSON se extraen de aquí. Se calcula automáticamente una suma de verificación SHA-256.',
            changed_field: 'Campo modificado: ',
            no_snapshots: 'No se encontraron instantáneas CMP. Las instantáneas se crean cuando se aprueban políticas o se generan manualmente.',
            detail_title: 'Detalle de la instantánea',
            view_snapshot_detail: 'Ver detalle de la instantánea',
            policy_version: 'Versión de la política',
            removed: 'Antes',
            added: 'Después',
        },
        change_awareness: {
            title: 'Detección de cambios en el consentimiento',
            change_type: 'Tipo de cambio',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de entidad',
            detected_at: 'Detectado el',
            acknowledge: 'Confirmar',
            new_alert: 'Nuevo alerta',
            new_alert_form: 'Nuevo Change Awareness Alert',
            total_open: 'Total Abrir',
            by_severity: 'Por Gravedad',
            snapshot_id: 'ID de instantánea',
            update_status: 'Actualizar Estado',
            resolution_notes: 'Notas de resolución',
            resolution_required: 'Notas de resolución (obligatorias)',
            resolution_required_msg: 'Las notas de resolución son obligatorias al resolver un elemento.',
            status_open: 'Abrir',
            status_under_review: 'En revisión',
            status_resolved: 'Resuelto',
            status_accepted: 'Aceptado',
            severity_informational: 'Informativo',
            severity_requires_review: 'Requiere revisión',
            severity_policy_violation: 'Infracción de política',
            create_reconciliation: 'Crear tarea de conciliación',
            reconciliation_created: 'La tarea de conciliación se creó correctamente.',
            reconciliation_error: 'No se pudo crear la tarea de conciliación. Por favor inténtalo de nuevo.',
            fetch_error: 'Error al fetch change awareness items. Por favor, inténtelo de nuevo.',
            dashboard_error: 'Error al fetch change awareness dashboard. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create change awareness item. Por favor, inténtelo de nuevo.',
            status_update_error: 'Error al update change awareness status. Por favor, inténtelo de nuevo.',
            page_title: 'Conciencia del cambio',
            no_items: 'No se encontraron elementos de conciencia de cambios. Los elementos se crean cuando se detectan cambios en la configuración del consentimiento.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Pista de auditoría del consentimiento',
            event_type: 'Tipo de evento',
            user_id: 'ID de usuario',
            timestamp: 'Fecha y hora',
            details: 'Detalles',
            actor: 'Actor',
            filter_by_entity_type: 'Filtrar por tipo de entidad',
            filter_by_action: 'Filtrar por acción',
            fetch_error: 'Error al fetch consent audit trail. Por favor, inténtelo de nuevo.',
            page_title: 'Auditoría de consentimiento',
            export_csv: 'Exportar CSV',
            exporting: 'Exportando…',
            export_success: 'Seguimiento de auditoría exportado correctamente.',
            export_error: 'No se pudo exportar la pista de auditoría de consentimiento. Por favor inténtalo de nuevo.',
            no_entries: 'No se encontraron entradas de auditoría. Los eventos de auditoría se registran automáticamente cuando cambian los datos de consentimiento.',
            entity_policy: 'Política',
            entity_purpose: 'Propósito',
            entity_workflow: 'Flujo de trabajo',
            entity_consent_record: 'Registro de consentimiento',
            entity_change_awareness: 'Conciencia de cambios',
            entity_cmp_snapshot: 'Instantánea CMP',
            action_created: 'Creado',
            action_updated: 'Actualizado',
            action_status_changed: 'Estado cambiado',
            action_drift_detected: 'Desviación detectada',
            action_snapshot_created: 'Instantánea creada',
            action_approval_submitted: 'Aprobación enviada',
            action_archived: 'Archivado',
            action_purpose_auto_mapped: 'Finalidad asignada automáticamente',
            action_purpose_mapping_updated: 'Asignación de finalidad actualizada',
            action_webhook_configured: 'Webhook configurado',
            entity_propagation: 'Propagación',
            entity_reconciliation: 'reconciliación',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Propagación confirmada',
            action_propagation_failed: 'Error de propagación',
            action_consent_captured: 'Consentimiento capturado',
            action_reconciliation_created: 'Reconciliación creada',
            action_reconciliation_resolved: 'Reconciliación resuelta',
            action_submitted_for_review: 'Enviado para revisión',
            action_activated: 'Activado',
            action_suspended: 'Suspendido',
            action_retired: 'Retirado',
            action_enhanced_review_required: 'Revisión mejorada requerida',
            action_enhanced_review_approved: 'Revisión mejorada aprobada',
            action_enhanced_review_rejected: 'Revisión mejorada rechazada — política restablecida a borrador',
            action_consented: 'Consentido',
            action_consented_customize: 'Consentimiento personalizado',
            action_revoked: 'Revocado',
            action_rejected: 'Rechazado',
        },
        web_records: {
            title: 'Registros de consentimiento web',
            page_title: 'Registros de consentimiento web',
            domain: 'Dominio',
            visitor_id: 'Identificación de visitante',
            consent_action: 'Acción de consentimiento',
            received_at: 'Recibido en',
            admin_status: 'Estado de administrador',
            revoke: 'Revocar',
            reject: 'Rechazar',
            reason: 'Razón',
            reason_placeholder: 'Motivo de esta acción (opcional)',
            revoke_confirm: '¿Revocar este registro de consentimiento?',
            reject_confirm: '¿Rechazar este registro de consentimiento?',
            override_success: 'Estado del registro actualizado exitosamente.',
            override_error: 'No se pudo actualizar el estado del registro.',
            no_records: 'No se encontraron registros de consentimiento web.',
            fetch_error: 'No se pudieron cargar los registros de consentimiento web.',
            filter_by_domain: 'Filtrar por dominio',
            filter_by_action: 'Filtrar por acción',
            active: 'Activo',
        },
        legal_bases: {
            all: 'Todas las bases legales',
        },
        common_statuses: {
            suspended: 'Suspendido',
            retired: 'Retirado',
            deprecated: 'Obsoleto',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'Integración AesirX CMP',
            back_to_consent: 'Volver a Consentimiento',
            back_to_integration: 'Volver a Integración',
            webhook_status: 'Webhook Estado',
            events_received: 'Eventos Recibido',
            web_consent_records: 'Web Consentimiento Registros',
            unmapped_purposes: 'Finalidades sin mapear',
            integration_management: 'Integración Gestión',
            webhook_configuration: 'Webhook Configuración',
            webhook_config_desc: 'Configure el endpoint del webhook de CMP y gestione el secreto HMAC.',
            purpose_mapping: 'Finalidad Mapeo',
            purpose_mapping_desc: 'Asigne los identificadores de finalidad de CMP a las finalidades del catálogo de consentimiento de GRC.',
            bulk_sync: 'Sincronización masiva',
            bulk_sync_desc: 'Importe datos históricos de consentimiento y vea el historial de sincronización.',
            consent_analytics: 'Análisis de consentimiento web (últimos 30 días)',
            consent_grant_rate: 'Consentimiento Grant Tasa',
            top_domain: 'Dominio principal',
            records: 'registros',
            banner_interactions: 'Interacciones del banner',
            consent_actions: 'Acciones de consentimiento',
            opt_in_trend: 'Tendencia opt-in/opt-out',
            records_by_domain: 'Registros por dominio',
            date: 'Fecha',
            grants: 'Consentimientos otorgados',
            denials: 'Denegaciones',
            not_configured: 'No configurado',
            load_error: 'Error al load integration data. Por favor, inténtelo de nuevo.',
            webhook_not_configured: 'Webhook no configured.',
            webhook_not_configured_msg: 'Por favor, primero {link}.',
            configure_webhook_link: 'configurar el webhook',
            setup_wizard: 'Asistente de configuración',
            step_configure_webhook: 'Configurar Webhook',
            step_map_purposes: 'Mapa Finalidades',
            step_run_sync: 'Ejecutar sincronización masiva inicial',
            step_verify_data: 'Verificar Datos en Panel de control',
            configure: 'Configurar',
            view_dashboard: 'Ver panel de control',
            syncing: 'Sincronizando...',
            sync_now: 'Sincronizar Ahora',
            sync_completed: 'Sincronizar completado: {imported} importado, {skipped} skipped, {failed} falló.',
            sync_failed: 'Sincronizar falló',
            last_sync_result: 'Último Sincronizar Resultado',
            imported: 'Importado',
            skipped: 'Omitido',
            errors: 'Errores',
            index: 'Índice',
            error: 'error',
            sync_history: 'Sincronizar Historial',
            no_sync_jobs: 'Aún no se han registrado trabajos de sincronización.',
            load_sync_error: 'Error al load sync data. Por favor, inténtelo de nuevo.',
            purpose_mapping_title: 'CMP Finalidad Mapeo',
            auto_mapping: 'Mapeando auto...',
            auto_map_purposes: 'Auto-Map Finalidades',
            unmapped_warning: '{count} propósitos no asignados: estos CMP propósitos deben asignarse a propósitos de GRC.',
            mapping_saved: 'Mapeo guardado.',
            mapping_deleted: 'Mapeo eliminado.',
            auto_map_complete: 'Mapeo automático completado: {mapped} mapeados, {unmatched} sin mapear.',
            auto_map_failed: 'Auto-map falló',
            save_mapping_error: 'Error al save mapping. Por favor, inténtelo de nuevo.',
            delete_mapping_error: 'Error al delete mapping. Por favor, inténtelo de nuevo.',
            load_data_error: 'Error al load data. Por favor, inténtelo de nuevo.',
            cmp_purpose: 'Propósito CMP',
            grc_purpose: 'Propósito GRC',
            select_grc_purpose: '-- Seleccionar GRC Finalidad --',
            auto_mapped: 'Mapeado auto.',
            mapped: 'Mapeado',
            unmapped: 'Sin mapear',
            no_mappings: 'No se encontraron asignaciones de finalidad. Las asignaciones se crean automáticamente cuando se reciben eventos de consentimiento.',
            total_mappings: 'Total: {count} asignación(es)',
            webhook_config_title: 'Webhook Configuración',
            current_configuration: 'Actual Configuración',
            setup_webhook: 'Configurar webhook',
            setup_webhook_desc: 'Configure el webhook de AesirX CMP para recibir eventos de consentimiento.',
            allowed_domains: 'Dominios permitidos (uno por línea)',
            webhook_secret_warning: 'Webhook Secreto (save este ahora -- ello será no ser shown again):',
            last_event: 'Último Evento',
            none_received: 'Ninguno',
            update_domains: 'Actualizar dominios',
            pause_webhook: 'Pausar webhook',
            resume_webhook: 'Reanudar webhook',
            regenerate_secret: 'Regenerar secreto',
            configuring: 'Configurando...',
            create_webhook_config: 'Agregar webhook configuración',
            webhook_endpoint: 'Endpoint del webhook',
            webhook_endpoint_desc: 'Configure su AesirX CMP para enviar eventos de consentimiento a:',
            webhook_signature_note: 'Incluya la firma HMAC-SHA256 en la cabecera {header}.',
            configured_success: 'Webhook configured con éxito',
            config_updated: 'Configuración actualizado.',
            config_error: 'Error al configure webhook. Por favor, inténtelo de nuevo.',
            update_error: 'Error al update configuration. Por favor, inténtelo de nuevo.',
            toggle_error: 'Error al toggle webhook. Por favor, inténtelo de nuevo.',
            deactivated: 'Webhook desactivado.',
            deactivate_error: 'Error al deactivate webhook. Por favor, inténtelo de nuevo.',
            page_title: 'CMP Integración',
            webhook_config_page_title: 'CMP Configuración del webhook',
            bulk_sync_page_title: 'Reasignar registros',
            purpose_mapping_page_title: 'Mapeo de Propósitos',
            data_loaded: 'CMP datos de integración cargados',
            sync_all: 'Sincronizar todo',
            sync_selected: 'Sincronizar seleccionados',
            sync_status: 'Estado de sincronización',
            last_sync: 'Última sincronización',
            no_records: 'No se encontraron registros.',
            sync_in_progress: 'Sincronización en curso...',
            sync_complete: 'Sincronización completada.',
            sync_error: 'La sincronización falló. Por favor, inténtelo de nuevo.',
            map_purpose: 'Asignar propósito',
            unmap: 'Desasignar',
            mapping_status: 'Estado de asignación',
            save_mapping: 'Guardar asignación',
            remap_unmapped_records: 'Reasignar registros no asignados',
            remap_records: 'Reasignar registros',
            add_mappings_first: 'Primero agregue asignaciones de propósitos (Paso 2)',
            remap_result: 'Resultado de reasignación',
            remap_result_desc: 'Procesó todos los registros de consentimiento con fines no asignados o parcialmente asignados.',
            total_processed: 'Total procesado',
            newly_mapped: 'Recién mapeado',
            still_unmapped_label: 'Aún sin mapear',
            still_unmapped_msg: '{count} registros todavía tienen propósitos sin asignar. {link} luego haz clic en Reasignar nuevamente.',
            add_more_mappings: 'Agregar más asignaciones',
            remap_complete: 'Reasignación completa: {remapped} recién asignada, {still_unmapped} aún sin asignar.',
            add_mapping: 'Agregar mapeo',
            cmp_purpose_id_label: 'CMP ID de propósito *',
            cmp_purpose_id_required: 'CMP Se requiere ID de propósito',
            cmp_purpose_name_label: 'CMP Propósito Nombre',
            grc_purpose_label: 'Propósito del GRC',
        },
        propagation: {
            title: 'Propagación de retirada',
            systems: 'Sistemas vinculados',
            status: 'Estado',
            confirmed: 'Confirmado',
            pending: 'Pendiente',
            failed: 'Fallido',
            confirm: 'Confirmar retirada',
            fail: 'Marcar como fallido',
            failure_reason: 'Razón del fallo',
            can_close: 'Todos los sistemas confirmados — el flujo puede cerrarse',
            cannot_close: 'Esperando confirmaciones del sistema',
            no_tasks: 'No hay tareas de propagación.',
            confirm_success: 'Retirada del sistema confirmada.',
            fail_success: 'Sistema marcado como fallido.',
            propagate_success: 'Retirada propagada a los sistemas vinculados.',
            propagate: 'Propagar a sistemas',
            add_system: 'Agregar sistema',
            remove_system: 'Quitar',
            system_id: 'ID del sistema',
            system_name: 'Nombre del sistema',
            owner_id: 'ID del propietario',
            propagate_error: 'No se pudo propagar el retiro. Por favor inténtalo de nuevo.',
            no_systems: 'Agregue al menos un sistema.',
            page_title: 'Propagación de retiro',
            workflow_id: 'Seleccione el flujo de trabajo de retiro',
            system_id_hint: 'p. ej. crm-001, erp-prod',
            owner_id_hint: 'Propietario del sistema',
            elapsed: 'Transcurrido',
        },
        sensitive: {
            title: 'Políticas de datos sensibles',
            flag: 'Marcar como sensible',
            categories: 'Categorías sensibles',
            enhanced_review: 'Requerir revisión ampliada',
            enhanced_review_success: 'Revisión ampliada requerida.',
            flag_success: 'Política marcada como sensible.',
            no_sensitive: 'No hay políticas de datos sensibles.',
        },
        reconciliation: {
            title: 'Reconciliación de discrepancias',
            create: 'Nueva tarea de reconciliación',
            system: 'Sistema',
            owner: 'Propietario',
            description: 'Descripción',
            resolution: 'Resolución',
            resolve: 'Resolver',
            update_grc: 'Actualizar GRC',
            update_cmp: 'Actualizar CMP',
            accept_difference: 'Aceptar diferencia',
            escalate: 'Escalar',
            resolution_notes: 'Notas de resolución',
            no_tasks: 'No hay tareas de reconciliación.',
            resolve_success: 'Tarea de reconciliación resuelta.',
            create_success: 'Tarea de reconciliación creada.',
            open: 'Abierto',
            resolved: 'Resuelto',
            escalated: 'Escalado',
            dashboard_open: 'Tareas abiertas',
            dashboard_resolved: 'Tareas resueltas',
            dashboard_escalated: 'Tareas escaladas',
            page_title: 'Conciliación de desajustes',
            my_tasks: 'Mis tareas',
            all_tasks: 'Todas las tareas',
            severity: 'Gravedad',
        },
        sync: {
            title: 'Sincronización CMP',
            direction: 'Dirección',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflicto',
            resolve_conflict: 'Resolver conflicto',
            no_events: 'No hay eventos de sincronización.',
            resolve_success: 'Conflicto de sincronización resuelto.',
            create_success: 'Evento de sincronización registrado.',
            page_title: 'CMP Sincronizar',
            detect_drift: 'Detectar conflictos',
            detecting: 'Detectando...',
            drift_result: '{detected} conflicto(s) detectado(s) a partir de {total} propósito(s) comparado(s).',
            no_drift: 'No se detectaron nuevos conflictos.',
            drift_error: 'No se pudo ejecutar la detección de conflictos.',
            resolve_modal_title: 'Resolver Conflicto',
            resolve_field: 'campo',
            resolve_grc_value: 'Valor GRC',
            resolve_cmp_value: 'CMP Valor',
            accept_cmp: 'Aceptar CMP Valor',
            accept_cmp_desc: 'Actualice GRC para que coincida con CMP: el valor CMP se convierte en el nuevo valor de GRC.',
            keep_grc: 'Mantenga el valor de GRC',
            keep_grc_desc: 'Cierra este conflicto sin cambiar GRC: notifica al propietario de CMP para alinearlo.',
            resolve_applied: 'Conflicto resuelto. Propósito de GRC actualizado para que coincida con CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Este informe de preparación es orientativo. No aprueba, ensambla, materializa, presenta ni envía el expediente.',
            run_label: 'Ejecutar pre-vuelo',
            running: 'Ejecutando antes del vuelo...',
            assemble_label: 'Paquete de autoensamblaje',
            assembling: 'Ensamblando…',
            verdict_label: 'Veredicto previo al vuelo',
            tokens_label: '≈ {count} fichas',
            remediation_label: 'Remediación',
            no_gaps: 'No se detectaron espacios previos al vuelo. El archivo está listo para ensamblar.',
            override_label: 'Anular el veredicto de bloqueo: la asamblea recibirá el sello de auditoría.',
            accept_label: 'Aceptar informe',
            reject_label: 'Rechazar informe',
            assembled_label: 'Paquete de presentación ensamblado',
            package_id_label: 'Identificación del paquete',
            package_filename_label: 'Nombre de archivo',
            package_hash_label: 'hash del paquete',
            evidence_count_label: '{count} elementos de evidencia agrupados',
            error_generic: 'No se pudo ejecutar el vuelo previo ni ensamblar el paquete. Inténtelo de nuevo o verifique el estado civil para efectos de la declaración.',
        },
        errors: {
            fetch: 'Error al load assessments. Por favor, inténtelo de nuevo.',
            save: 'Error al save assessment. Por favor, inténtelo de nuevo.',
            fetch_gap_report_failed: 'Error al fetch gap report. Por favor, inténtelo de nuevo.',
            fetch_transfers_failed: 'Error al fetch transfers. Por favor, inténtelo de nuevo.',
            save_transfer_failed: 'Error al save transfer. Por favor, inténtelo de nuevo.',
            submit_review_failed: 'Error al submit for review. Por favor, inténtelo de nuevo.',
            process_decision_failed: 'Error al process decision. Por favor, inténtelo de nuevo.',
            deprecate_failed: 'Error al deprecate. Por favor, inténtelo de nuevo.',
            evaluate_risk_failed: 'Error al evaluate risk. Por favor, inténtelo de nuevo.',
            fetch_safeguards_failed: 'Error al fetch safeguards. Por favor, inténtelo de nuevo.',
            save_safeguard_failed: 'Error al save safeguard. Por favor, inténtelo de nuevo.',
            update_safeguard_status_failed: 'Error al actualizar el estado de la salvaguarda. Por favor, inténtelo de nuevo.',
        },
        nav: {
            dpias: 'EIPD',
            transfers: 'Evaluaciones de transferencias',
            dashboard: 'Panel de control',
            gap_report: 'Informe de deficiencias',
        },
        risk_level_critical: 'Riesgo crítico',
        risk_level_high: 'Riesgo alto',
        risk_level_medium: 'Riesgo medio',
        risk_level_low: 'Riesgo bajo',
        severity_critical: 'Severidad crítica',
        severity_high: 'Severidad alta',
        severity_medium: 'Severidad media',
        severity_low: 'Severidad baja',
        overdue: 'Vencido',
        framework_filter_label: 'Marco',
        aria_filter_by_framework: 'Filtrar por marco',
        dashboard: {
            title: 'Panel de evaluaciones',
            page_title: 'Panel de evaluaciones',
            announce_loaded: 'Panel de evaluaciones cargado',
            announce_chart_view: 'Mostrando vista de gráfico',
            announce_table_view: 'Mostrando vista de tabla de datos',
            aria_metrics_table: 'Métricas del panel de evaluaciones',
            metric_col: 'Métrica',
            value_col: 'Valor',
            dpia_prefix: 'EIPD',
            transfer_prefix: 'Transferencia',
            risk_prefix: 'Riesgo',
            aria_dpias_by_status: 'EIPD por estado',
            aria_transfers_by_status: 'Transferencias por estado',
            aria_top_risks: 'Principales riesgos',
            aria_top_risks_table: 'Principales riesgos en las EIPD',
            overdue_dpias_attention: '{count} evaluaciones EIPD vencidas requieren atención',
            no_overdue_dpias: 'Sin evaluaciones EIPD vencidas',
            overdue_transfers_attention: '{count} evaluaciones de transferencia vencidas requieren atención',
            no_overdue_transfers: 'Sin evaluaciones de transferencia vencidas',
            dpias_overview: 'Resumen de EIPD',
            transfers_overview: 'Resumen de evaluaciones de transferencias',
            overdue_items: 'Elementos vencidos',
            overdue_dpias: 'EIPD vencidas',
            overdue_transfers: 'Transferencias vencidas',
            count: 'Recuento',
            no_data: 'Sin datos disponibles.',
            no_risks: 'No se encontraron riesgos',
            no_dashboard_data: 'No hay datos del panel disponibles.',
            fetch_error: 'Error al fetch dashboard data. Por favor, inténtelo de nuevo.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'EIPDs',
            announce_submitted_for_review: 'EIPD enviada para revisión',
            announce_deprecated: 'EIPD obsoleta',
            announce_risks_loaded: '{count} riesgo(s) cargado(s)',
            announce_mitigations_loaded: '{count} medida(s) de mitigación cargada(s)',
            announce_risk_deleted: 'Riesgo eliminado',
            announce_mitigation_status_updated: 'Estado de la medida de mitigación actualizado',
            aria_filter: 'Filtrar EIPDs',
            aria_filter_by_status: 'Filtrar por estado',
            aria_filter_by_lead: 'Filtrar por líder de evaluación',
            aria_table: 'Evaluaciones de impacto en la protección de datos',
            announce_found: '{count} EIPD(s) encontrada(s)',
            announce_decision: 'EIPD {decision}',
            mitigation_start: 'Iniciar',
            mitigation_mark_implemented: 'Marcar como implementado',
            mitigation_verify: 'Verificar',
            title: 'Evaluaciones de Impacto en la Protección de Datos',
            new: 'Nueva EIPD',
            edit: 'Editar EIPD',
            dpia_title: 'Título',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Actividad',
            risk_score: 'Puntuación de riesgo',
            risks: 'Riesgos',
            mitigations: 'Mitigaciones',
            add_risk: 'Añadir riesgo',
            add_mitigation: 'Añadir mitigación',
            necessity: 'Necesidad / Proporcionalidad',
            likelihood: 'Probabilidad',
            impact: 'Impacto',
            severity_label: 'Gravedad',
            purpose: 'Finalidad',
            consultation_notes: 'Notas de consulta',
            dpo_opinion: 'Opinión del DPO',
            processing_activity_ids: 'IDs de actividades de tratamiento (separados por comas)',
            system_ids: 'IDs de sistema (separados por comas)',
            vendor_ids: 'IDs de proveedores (separados por comas)',
            data_types: 'Tipos de datos (separados por comas)',
            data_types_short: 'Tipos de datos',
            data_subjects: 'Interesados (separados por comas)',
            assessment_lead_id: 'ID del líder de evaluación',
            assessment_lead: 'Líder de evaluación',
            dpo_id: 'ID DPO',
            reviewer_ids: 'IDs de revisores (separados por comas)',
            approval_mode: 'Modo de aprobación',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Requerir aprobación del DPO',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Revisar Fecha',
            submit_for_review: 'Enviar para Revisar',
            approve_reject: 'Aprobar/Rechazar',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Deprecar',
            deprecated: 'Obsoleto',
            hide_risks: 'Ocultar Riesgos',
            hide_mitigations: 'Ocultar mitigaciones',
            comments: 'Comentarios',
            submit_decision: 'Enviar decisión',
            category_label: 'Categoría',
            category_data_breach: 'Datos Brecha',
            category_unauthorized_access: 'Acceso no autorizado',
            category_function_creep: 'Expansión de funciones',
            category_re_identification: 'Re-identificación',
            category_discrimination: 'Discriminación',
            category_surveillance: 'Vigilancia',
            category_other: 'Otro',
            affected_rights: 'Derechos afectados (separados por comas)',
            no_risks: 'No se encontraron riesgos',
            no_mitigations: 'No se encontraron mitigations',
            no_dpias: 'No se encontraron dpias',
            mitigation_type: 'Tipo',
            mitigation_technical: 'Técnico',
            mitigation_organizational: 'Organizativo',
            mitigation_contractual: 'contractual',
            risk_ids: 'IDs de riesgos (separados por comas)',
            responsible_party: 'Parte responsable',
            responsible_party_placeholder: 'O introducir nombre manualmente',
            affected_rights_placeholder: 'Los derechos seleccionados aparecen aquí',
            affected_rights_freetext_placeholder: 'Introducir derechos afectados (separados por comas)',
            responsible: 'Responsable',
            target_date: 'Fecha objetivo',
            created: 'Creado',
            fetch_error: 'Error al fetch dpias. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save dpia. Por favor, inténtelo de nuevo.',
            submit_review_error: 'Error al submit dpia for review. Por favor, inténtelo de nuevo.',
            decision_error: 'Error al process decision. Por favor, inténtelo de nuevo.',
            deprecate_error: 'Error al deprecate dpia. Por favor, inténtelo de nuevo.',
            fetch_risks_error: 'Error al fetch risks. Por favor, inténtelo de nuevo.',
            save_risk_error: 'Error al save risk. Por favor, inténtelo de nuevo.',
            delete_risk_error: 'Error al delete risk. Por favor, inténtelo de nuevo.',
            fetch_mitigations_error: 'Error al fetch mitigations. Por favor, inténtelo de nuevo.',
            save_mitigation_error: 'Error al save mitigation. Por favor, inténtelo de nuevo.',
            update_mitigation_status_error: 'Error al actualizar el estado de la mitigación. Por favor, inténtelo de nuevo.',
            link_form_template: 'Plantilla de formulario regulatorio de enlace',
            link_form_template_placeholder: 'Seleccione una plantilla de formulario DPIA (opcional)',
            link_form_template_none: 'Ninguno',
            linked_form: 'Formulario vinculado',
            linked_form_creating: 'Creando formulario vinculado...',
            linked_form_error: 'No se pudo crear la instancia de formulario vinculado. El DPIA se creó sin un formulario vinculado.',
            view_linked_form: 'Ver formulario',
            announce_created_with_form: 'DPIA creado con instancia de formulario vinculado',
            creation_mode: 'DPIA Modo de creación',
            mode_standard: 'Estándar DPIA',
            mode_template: 'Utilice la plantilla de formulario reglamentario',
            select_form_template: 'Seleccionar plantilla de formulario',
            create_and_fill_form: 'Crear y completar formulario',
            template_sections_title: 'Secciones de plantilla',
            field_singular: 'campo',
            field_plural: 'campos',
            no_sections: 'No se encontraron secciones en esta plantilla.',
            template_mode_hint: 'Después de la creación, será redirigido para que complete el formulario reglamentario completo.',
            linked_dossier_filings: 'Presentaciones de expediente vinculadas',
            no_linked_dossier_filings: 'No hay presentaciones vinculadas a esta EIPD.',
            view_dossier_filing: 'Ver presentación',
            hide_dossier_filings: 'Ocultar presentaciones',
            supporting_evidence: 'Evidencia de respaldo',
            hide_supporting_evidence: 'Ocultar evidencia',
            no_supporting_evidence: 'No se adjunta ninguna evidencia de respaldo.',
            upload_supporting_evidence: 'Cargar evidencia de respaldo',
            view_form_responses: 'Ver respuestas del formulario',
            regulatory_framework: 'Marco regulatorio',
            all_frameworks: 'Todos los marcos',
            no_templates_for_market: 'No hay plantillas disponibles para el mercado seleccionado. Seleccione un mercado diferente o cree una plantilla.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Transferencias de datos',
            announce_found: '{count} evaluación(es) de transferencia encontrada(s)',
            announce_submitted_for_review: 'Transferencia enviada para revisión',
            announce_deprecated: 'Transferencia obsoleta',
            announce_decision: 'Transferencia {decision}',
            announce_risk_eval_completed: 'Evaluación de riesgos completada',
            announce_safeguard_status_updated: 'Estado de la medida de protección actualizado',
            announce_safeguards_loaded: '{count} medida(s) de protección cargada(s)',
            aria_filter: 'Filtrar evaluaciones de transferencia',
            aria_filter_by_status: 'Filtrar por estado',
            aria_filter_by_mechanism: 'Filtrar por mecanismo de transferencia',
            aria_table: 'Evaluaciones de transferencia de datos',
            title: 'Evaluaciones de transferencias internacionales',
            new: 'Nueva transferencia',
            edit: 'Editar transferencia',
            name: 'Nombre',
            from_country: 'País de origen',
            to_country: 'País de destino',
            safeguards: 'Garantías',
            risk_level: 'Nivel de riesgo',
            assessment_notes: 'Notas de la evaluación',
            mechanism: 'Mecanismo',
            transfer_mechanism: 'Mecanismo de transferencia',
            transfer_mechanism_detail: 'Detalle del mecanismo de transferencia',
            all_mechanisms: 'Todos los mecanismos',
            mechanism_other: 'Otro',
            data_types: 'Tipos de datos (separados por comas)',
            data_subjects: 'Interesados (separados por comas)',
            purpose: 'Finalidad',
            recipient_name: 'Nombre del destinatario',
            create_success: 'Evaluación de transferencia creada.',
            update_success: 'Evaluación de transferencia actualizada.',
            recipient_details: 'Detalles del destinatario',
            recipient_details_placeholder: 'Función, ubicación y sistemas del destinatario, y detalles de transferencia ulterior',
            vendor_id: 'Proveedor ID',
            processing_activity_ids: 'IDs de actividades de tratamiento (separados por comas)',
            reviewer_ids: 'IDs de revisores (separados por comas)',
            approval_mode: 'Modo de aprobación',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Requerir aprobación del DPO',
            review_date: 'Revisar Fecha',
            created: 'Creado',
            deprecated: 'Obsoleto',
            submit_for_review: 'Enviar para Revisar',
            approve_reject: 'Aprobar/Rechazar',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Deprecar',
            risk_eval: 'Evaluación de riesgos',
            hide_safeguards: 'Ocultar salvaguardas',
            mechanism_proof: 'Prueba del mecanismo',
            hide_mechanism_proof: 'Ocultar prueba del mecanismo',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'No se adjunta ninguna prueba de mecanismo.',
            upload_mechanism_proof: 'Subir prueba del mecanismo',
            comments: 'Comentarios',
            submit_decision: 'Enviar decisión',
            data_sensitivity: 'Sensibilidad de los datos (1-5)',
            country_risk: 'País Riesgo (1-5)',
            safeguard_adequacy: 'Adecuación de la salvaguarda (1-5)',
            volume_factor: 'Factor de volumen (1-5)',
            evaluate: 'Evaluar',
            add_safeguard: 'Agregar Safeguard',
            responsible_party: 'Parte responsable',
            responsible_party_placeholder: 'O introducir nombre manualmente',
            responsible: 'Responsable',
            implementation_date: 'Fecha de implementación',
            implementation: 'Implementación',
            expiry_date: 'Fecha de caducidad',
            expiry: 'Caducidad',
            expire: 'Caducar',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'No se encontraron safeguards',
            no_transfers: 'No se encontraron transferencia evaluaciones',
            fetch_error: 'Error al fetch transferencia evaluaciones. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save transferencia evaluación. Por favor, inténtelo de nuevo.',
            submit_review_error: 'Error al submit for review. Por favor, inténtelo de nuevo.',
            decision_error: 'Error al process decision. Por favor, inténtelo de nuevo.',
            deprecate_error: 'Error al deprecate transfer. Por favor, inténtelo de nuevo.',
            risk_eval_error: 'Error al evaluate risk. Por favor, inténtelo de nuevo.',
            fetch_safeguards_error: 'Error al fetch safeguards. Por favor, inténtelo de nuevo.',
            save_safeguard_error: 'Error al save safeguard. Por favor, inténtelo de nuevo.',
            update_safeguard_status_error: 'Error al actualizar el estado de la salvaguarda. Por favor, inténtelo de nuevo.',
            safeguard_type_encryption_in_transit: 'Cifrado en tránsito',
            safeguard_type_encryption_at_rest: 'Cifrado en reposo',
            safeguard_type_access_controls: 'Acceso Controles',
            safeguard_type_contractual_clauses: 'Cláusulas contractuales',
            safeguard_type_data_minimization: 'Minimización de datos',
            safeguard_type_pseudonymization: 'Seudonimización',
            safeguard_type_audit_rights: 'Derechos de auditoría',
            safeguard_type_breach_notification: 'Brecha Notificación',
            safeguard_type_data_localization: 'Localización de datos',
            safeguard_type_other: 'Otro',
            activate: 'Activar',
            all_statuses: 'Todos los estados',
            any: 'Any',
            mechanism_detail: 'Detalle del mecanismo',
            safeguard_types: {
                encryption_in_transit: 'Cifrado en tránsito',
                encryption_at_rest: 'Cifrado en reposo',
                access_controls: 'Controles de acceso',
                contractual_clauses: 'Cláusulas contractuales',
                data_minimization: 'Minimización de datos',
                pseudonymization: 'Seudonimización',
                audit_rights: 'Derechos de auditoría',
                breach_notification: 'Notificación de infracción',
                data_localization: 'Localización de datos',
                other: 'Otro',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Informe de brechas de evaluaciones',
            announce_loaded: 'Informe de brechas cargado. {count} brecha(s) identificada(s)',
            aria_activities_without_dpia: 'Actividades sin evaluación EIPD',
            aria_activities_unclassified: 'Actividades con requisito de EIPD sin clasificar',
            activities_unclassified_section_title: 'Actividades con requisito de EIPD sin clasificar',
            activities_unclassified_description: 'Estas actividades no se han revisado para determinar si una EIPD es legalmente obligatoria. Clasifique cada una para eliminar el aviso.',
            activities_unclassified_empty: 'Todas las actividades activas han sido clasificadas.',
            aria_unmitigated_risks: 'Riesgos altos y críticos no mitigados',
            aria_transfers_without_safeguards: 'Transferencias sin medidas de protección adecuadas',
            aria_expired_assessments: 'Evaluaciones vencidas que requieren renovación',
            title: 'Informe de deficiencias de evaluación',
            no_dpia: 'Actividades sin EIPD',
            no_assessment: 'Transferencias sin evaluación',
            unmitigated: 'EIPD con riesgos no mitigados',
            activity_id: 'ID de actividad',
            dpia_id: 'ID EIPD',
            assessment_id: 'Evaluación ID',
            expired_assessments: 'Caducado Evaluaciones',
            no_gaps_activities: 'No se encontraron brechas. Todas las actividades tienen DPIAs.',
            no_unmitigated_risks: 'No se encontraron unmitigated high/critical risks',
            no_gaps_safeguards: 'No se encontraron brechas. Todas las transferencias tienen salvaguardas adecuadas.',
            no_expired: 'No se encontraron caducado evaluaciones',
            no_report_data: 'No hay datos del informe de brechas disponibles.',
            fetch_error: 'Error al fetch gap report. Por favor, inténtelo de nuevo.',
            no_expired_assessments: 'No caducado evaluaciones.',
            no_gaps_dpia: 'No se encontraron dpia gaps',
            no_data: 'Sin datos disponibles.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Detalle de presentación del expediente',
            page_title_list: 'Casos de presentación del expediente',
            announce_filings_found: '{count} presentación(es) encontrada(s)',
            announce_results: '{count} resultados',
            aria_filter_by_status: 'Filtrar por estado',
            title: 'Casos de presentación de expedientes',
            nav_label: 'Presentaciones de expedientes',
            create: 'Crear archivo de expediente',
            form_type: 'Tipo de formulario',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referencia',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Fecha de envío',
            outcome: 'Resultado',
            parent_filing: 'Padre',
            no_filings: 'No se encontraron expedientes.',
            fetch_error: 'No se pudieron recuperar las presentaciones del expediente. Por favor inténtalo de nuevo.',
            save_error: 'No se pudo crear el archivo del expediente. Por favor inténtalo de nuevo.',
            created: 'Presentación del expediente creada.',
            loaded: 'Archivo de expediente cargado.',
            not_found: 'No se encontró el archivo del expediente.',
            back_to_list: 'volver a la lista',
            section_assignments: 'Asignaciones de sección',
            assign_sections: 'Asignar secciones',
            section_id: 'Sección',
            department: 'Departamento',
            assignee: 'Cesionario',
            deadline: 'Plazo',
            no_assignments: 'Aún no hay asignaciones de sección.',
            sections_assigned_msg: 'Secciones asignadas.',
            assign_error: 'No se pudieron asignar secciones. Por favor inténtalo de nuevo.',
            lifecycle: 'Ciclo de vida de presentación',
            submission: 'Detalles de envío',
            submission_channel: 'Canal de envío',
            confirmation_number: 'Número de confirmación',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Enviar para revisión',
            submitted_for_review: 'Enviado para revisión interna.',
            submit_review_error: 'No se pudo enviar para revisión. Por favor inténtalo de nuevo.',
            legal_signoff: 'Aprobación legal',
            legal_signoff_done: 'Aprobación legal completada.',
            legal_signoff_error: 'No se pudo completar la aprobación legal. Por favor inténtalo de nuevo.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'No se pudo aprobar la presentación. Por favor inténtalo de nuevo.',
            materialisation_status_column: 'Artefacto de presentación',
            materialisation_na: 'No aplicable',
            materialisation_pending: 'Pendiente',
            materialisation_success: 'Materializado',
            materialisation_failed: 'Fallido',
            materialisation_banner_title: 'No se pudo crear el artefacto de envío',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Reintentar',
            materialisation_retry_count: 'Intentos de reintento anteriores:',
            materialisation_retry_success: 'El artefacto de envío se materializó con éxito.',
            materialisation_retry_announce: 'El reintento fue exitoso',
            filter_materialisation_failed: 'Mostrar sólo fallos de materialización',
            column_title: 'Título',
            column_form_type: 'Formulario',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Solicitud de suplemento',
            supplement_requests: 'Solicitudes de suplementos',
            supplement_response: 'Respuesta complementaria',
            record_supplement: 'Solicitud de suplemento de registro',
            supplement_recorded: 'Solicitud de suplemento registrada.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'No se pudo registrar la solicitud de suplemento. Por favor inténtalo de nuevo.',
            submit_supplement: 'Enviar respuesta complementaria',
            supplement_submitted_msg: 'Respuesta suplementaria enviada.',
            supplement_submit_error: 'No se pudo enviar la respuesta complementaria. Por favor inténtalo de nuevo.',
            response_status: 'Estado de respuesta',
            response_notes: 'Notas de respuesta',
            resubmit: 'Reenviar',
            resubmitted_msg: 'Expediente reenviado.',
            resubmit_error: 'No se pudo volver a enviar el expediente. Por favor inténtalo de nuevo.',
            outcome_error: 'No se pudo registrar el resultado. Por favor inténtalo de nuevo.',
            accepted: 'Aceptado',
            rejected: 'Rechazado',
            close: 'Cerrar',
            closed_msg: 'Presentación cerrada.',
            close_error: 'No se pudo cerrar la presentación. Por favor inténtalo de nuevo.',
            change_filing: 'Crear archivo de cambio',
            change_filing_created: 'Archivo de cambios creado.',
            change_filing_error: 'No se pudo crear la presentación de cambios. Por favor inténtalo de nuevo.',
            change_reason: 'Razón del cambio',
            filing_lineage: 'Linaje de presentación',
            timers: 'Temporizadores activos',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Fecha límite del suplemento',
            timer_resubmission_review: 'Fecha límite para volver a enviar',
            timer_department_input: 'Fecha límite del departamento',
            days_remaining: 'días restantes',
            days_overdue: 'días de retraso',
            cover_letter: 'Carta de presentación',
            checklist: 'Lista de verificación',
            evidence_index: 'Índice de evidencia',
            status_draft: 'Borrador',
            status_internal_review: 'Revisión interna',
            status_legal_signoff: 'Aprobación legal',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Suplemento solicitado',
            status_supplement_submitted: 'Suplemento enviado',
            status_resubmitted: 'Reenviado',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Cerrado',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Borrador',
            step_internal_review: 'Revisión interna',
            step_legal_signoff: 'Aprobación legal',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Suplemento solicitado',
            step_supplement_submitted: 'Suplemento enviado',
            step_resubmitted: 'Reenviado',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Cerrado',
            linked_dpia: 'EIPD vinculada',
            linked_dpia_none: 'No hay EIPD vinculada a esta presentación.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Ver EIPD',
            linked_dpia_label: 'Esta presentación de expediente está vinculada a la siguiente EIPD:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Estado del artefacto de envío',
            page_title: 'Estado del artefacto de envío',
            page_description: 'Detectar y remediar problemas de materialización de expedientes. Las listas tienen como ámbito su organización.',
            failed_title: 'Materializaciones fallidas',
            failed_empty: 'No hay materializaciones fallidas, bien.',
            pending_title: 'Aprobado pero no materializado',
            pending_empty: 'Todas las presentaciones aprobadas cuentan con un expediente de respaldo.',
            orphan_title: 'Expedientes huérfanos',
            orphan_description: 'Expedientes creados por materialización a los que ya no se hace referencia en ninguna presentación (normalmente el perdedor de una carrera concurrente). Listado para investigación; la limpieza es manual.',
            orphan_empty: 'No se han detectado expedientes huérfanos.',
            materialise_button: 'Materializar ahora',
            column_failure_reason: 'Motivo del fracaso',
            column_failed_at: 'Falló en',
            column_retries: 'Reintentos',
            column_approved_at: 'Aprobado en',
            column_dossier_id: 'ID del expediente',
            column_source_filing_id: 'Presentación de fuentes',
            column_dossier_type: 'Tipo',
            column_created_at: 'Creado en',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Acceso denegado',
        access_denied: 'No tienes los permisos necesarios para acceder al módulo de Clasificación. Póngase en contacto con su administrador.',
        nav: {
            taxonomies: 'Taxonomías',
            labeling: 'Etiquetado',
            lifecycle: 'Ciclo de vida',
            gaps: 'Informe de deficiencias',
            exports: 'Exportaciones',
        },
        exports: {
            title: 'Exportaciones de clasificación',
            csv_section: 'Exportaciones CSV',
            pdf_section: 'Informes PDF',
            inventory: 'Inventario',
            classification_register: 'Registro de clasificación',
            raci: 'Matriz RACI',
            exceptions: 'Excepciones',
            evidence_pack: 'Paquete de evidencias',
            delta_report: 'Informe delta',
            executive_summary: 'Resumen ejecutivo',
            exporting: 'Exportando…',
            export_success: 'Exportación completada con éxito.',
            export_error: 'La exportación falló. Por favor, inténtelo de nuevo.',
            csv_download: 'Descarga CSV lista.',
            pdf_download: 'Generación de PDF iniciada.',
            no_data: 'No hay datos disponibles para exportar.',
        },
        delta: {
            title: 'Detalle de cambios',
            added: 'Añadidos',
            removed: 'Eliminados',
            changed: 'Modificados',
            added_count: 'Añadidos ({count})',
            removed_count: 'Eliminados ({count})',
            changed_count: 'Modificados ({count})',
            object_id: 'ID de objeto',
            object_type: 'Tipo de objeto',
            taxonomy: 'Taxonomía',
            level: 'Nivel',
            previous: 'Anterior',
            current: 'Actual',
            no_records: 'No hay registros en esta sección.',
            empty_section: 'Esta sección está vacía.',
        },
        schedule: {
            title: 'Programar ciclo de descubrimiento',
            enabled: 'Activado',
            interval_days: 'Intervalo (días)',
            interval_label: 'Ejecutar cada N días',
            manual_only: 'Solo manual',
            every_n_days: 'Cada {n} días',
            save: 'Guardar programación',
            cancel: 'Cancelar',
            schedule_updated: 'Programación actualizada con éxito.',
        },
        taxonomies: {
            title: 'Taxonomías de clasificación',
            new: 'Nueva taxonomía',
            edit: 'Editar taxonomía',
            name: 'Nombre',
            levels: 'Niveles de clasificación',
            seed_defaults: 'Valores iniciales predeterminados',
            scope: 'Alcance',
            scope_organization: 'Organización',
            scope_module_specific: 'Específico del módulo',
            deprecate: 'Deprecar',
            hide_levels: 'Ocultar niveles',
            versions: 'Versiones',
            hide_versions: 'Ocultar versiones',
            add_level: 'Agregar nivel',
            new_level: 'Nuevo nivel',
            edit_level: 'Editar Nivel',
            code: 'Código',
            label: 'Etiqueta',
            color: 'Color',
            ordinal: 'ordinal',
            handling_requirements: 'Requisitos de tratamiento',
            version_history: 'Versión Historial',
            changes: 'Cambios',
            failed_fetch: 'Error al fetch taxonomies. Por favor, inténtelo de nuevo.',
            failed_save: 'Error al save taxonomy. Por favor, inténtelo de nuevo.',
            failed_activate: 'Error al activate taxonomy. Por favor, inténtelo de nuevo.',
            failed_deprecate: 'Error al deprecate taxonomy. Por favor, inténtelo de nuevo.',
            failed_seed: 'Error al seed default taxonomies. Por favor, inténtelo de nuevo.',
            failed_fetch_levels: 'Error al fetch taxonomy levels. Por favor, inténtelo de nuevo.',
            failed_save_level: 'Error al save taxonomy level. Por favor, inténtelo de nuevo.',
            failed_delete_level: 'Error al delete taxonomy level. Por favor, inténtelo de nuevo.',
            failed_fetch_versions: 'Error al fetch taxonomy versions. Por favor, inténtelo de nuevo.',
            loaded_count: 'Cargadas {count} taxonomías',
            defaults_seeded: 'Taxonomías por defecto inicializadas.',
            no_levels: 'No hay niveles de clasificación definidos — agregue niveles para definir su jerarquía de clasificación.',
            no_versions: 'Aún no hay historial de versiones — las versiones se crean cuando se actualiza una taxonomía activa.',
        },
        labeling: {
            title: 'Etiquetado de datos',
            new: 'Nueva etiqueta',
            edit: 'Editar etiqueta',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de entidad',
            taxonomy: 'Taxonomía',
            classification_level: 'Nivel de clasificación',
            applied_by: 'Aplicada por',
            applied_at: 'Fecha de aplicación',
            bulk_apply: 'Aplicar en bloque',
            bulk_remove: 'Eliminar en bloque',
            all_taxonomies: 'Todas las taxonomías',
            system: 'Sistema',
            dataset: 'Conjunto de datos',
            level_code_placeholder: 'Código de nivel...',
            select_taxonomy: 'Seleccione taxonomía...',
            select_level: 'Seleccione level...',
            rationale: 'Justificación',
            review_date: 'Revisar Fecha',
            review: 'Revisar',
            selected: 'seleccionado',
            select_all_labels: 'Seleccionar todas las etiquetas',
            loaded_count: 'Cargadas {count} etiquetas',
            hide_stale_labels: 'Ocultar etiquetas obsoletas',
            show_conflicts: 'Mostrar conflictos',
            hide_conflicts: 'Ocultar conflictos',
            label_conflicts: 'Conflictos de etiquetas',
            conflict_reason: 'Motivo del conflicto',
            labels: 'Etiquetas',
            failed_fetch: 'Error al fetch labels. Por favor, inténtelo de nuevo.',
            failed_fetch_taxonomies: 'Error al fetch taxonomies. Por favor, inténtelo de nuevo.',
            failed_fetch_levels: 'Error al fetch taxonomy levels. Por favor, inténtelo de nuevo.',
            failed_save: 'Error al save label. Por favor, inténtelo de nuevo.',
            failed_review: 'Error al review label. Por favor, inténtelo de nuevo.',
            failed_bulk: 'Error al bulk label. Por favor, inténtelo de nuevo.',
            failed_stale: 'Error al fetch stale labels. Por favor, inténtelo de nuevo.',
            failed_conflicts: 'Error al fetch label conflicts. Por favor, inténtelo de nuevo.',
            review_permission_denied: 'No tiene permiso para revisar esta etiqueta.',
            review_not_found: 'Etiqueta no encontrada — puede haber sido eliminada.',
            approve_selected: 'Aprobar seleccionadas',
            reject_selected: 'Rechazar seleccionadas',
            approved: 'Aprobada',
            rejected: 'Rechazada',
            labels_count: 'etiquetas',
            failed_count: 'fallidas',
            confirm_approve: 'Confirmar aprobación',
            confirm_reject: 'Confirmar rechazo',
            approve_n_labels: '¿Aprobar {count} etiquetas?',
            reject_n_labels: '¿Rechazar {count} etiquetas?',
            submit_for_review: 'Enviar para revisión',
            approve: 'Aprobar',
            reject: 'Rechazar',
            parent_system: 'Sistema principal',
            parent_system_placeholder: 'Seleccionar sistema principal...',
            parent_system_help: 'Usado para deteccion de conflictos.',
            dataset_level: 'Nivel del conjunto de datos',
            parent_level: 'Nivel principal',
            bulk_created: 'Se crearon {created} etiqueta(s)',
            bulk_skipped: '{skipped} omitidas',
            bulk_all_skipped: 'Todas las {skipped} omitidas',
            ids_hint: 'Ingrese IDs separados por comas',
            delete_confirm: 'Eliminar etiqueta para "{id}"?',
        },
        lifecycle: {
            title: 'Supervisión del ciclo de vida de los datos',
            new: 'Nueva entrada',
            current_stage: 'Etapa actual',
            last_transition: 'Última transición',
            transition: 'Transición',
            view_history: 'Ver historial',
            stages: {
                creation: 'Creación',
                collection: 'Recogida',
                processing: 'Tratamiento',
                storage: 'Almacenamiento',
                retention: 'Conservación',
                disposal: 'Eliminación',
                archived: 'Archivado',
                active: 'Activo',
                pending_deletion: 'Pendiente Eliminación',
                deleted: 'Eliminado',
                on_hold: 'En espera',
            },
            all_stages: 'Todas las etapas',
            object_id: 'ID del objeto',
            object_type: 'Tipo de objeto',
            policy_reference: 'Referencia de política',
            retention_period_days: 'Retención Período (days)',
            retention_basis: 'Retención Base',
            deletion_method: 'Método de eliminación',
            deletion_secure_erase: 'Borrado seguro',
            deletion_anonymization: 'Anonimización',
            deletion_physical_destruction: 'Destrucción física',
            deletion_other: 'Otro',
            responsible_party: 'Parte responsable',
            responsible_user_id: 'ID del usuario responsable',
            retention_expiry: 'Expiración de la retención',
            hold_reason: 'Motivo de la espera',
            hold: 'En espera',
            release: 'Lanzamiento',
            hide_events: 'Ocultar Eventos',
            events_timeline: 'Eventos Línea de tiempo',
            add_event: 'Agregar evento',
            event_type: 'Evento Tipo',
            create_event: 'Agregar evento',
            from_stage: 'Desde la etapa',
            to_stage: 'Hacia la etapa',
            actor: 'Actor',
            reason_placeholder: 'Motivo...',
            go: 'Go',
            show_expired_retentions: 'Mostrar Caducado Retentions',
            hide_expired_retentions: 'Ocultar Caducado Retentions',
            expired_retentions_alert: 'Caducado Retentions Alerta',
            failed_fetch: 'Error al fetch lifecycle records. Por favor, inténtelo de nuevo.',
            failed_save: 'Error al save lifecycle record. Por favor, inténtelo de nuevo.',
            failed_transition: 'Error al transition lifecycle stage. Por favor, inténtelo de nuevo.',
            failed_hold: 'Error al place hold. Por favor, inténtelo de nuevo.',
            failed_release: 'Error al release hold. Por favor, inténtelo de nuevo.',
            failed_fetch_events: 'Error al fetch lifecycle events. Por favor, inténtelo de nuevo.',
            failed_create_event: 'Error al create lifecycle event. Por favor, inténtelo de nuevo.',
            failed_expired: 'Error al fetch expired retentions. Por favor, inténtelo de nuevo.',
            hold_reason_prompt: 'Introduzca hold reason:',
            loaded_count: 'Cargados {count} registros de ciclo de vida',
            stage_transition_for: 'Transición de etapa para {id}',
            event_created: 'Creado',
            event_updated: 'Actualizado',
            event_approved: 'Aprobado',
            event_deprecated: 'Obsoleto',
            event_proposed: 'Propuesto',
            event_reviewed: 'Revisado',
            event_archived: 'Archivado',
            event_restored: 'Restaurado',
        },
        gap_report: {
            title: 'Informe de deficiencias de clasificación',
            unlabeled: 'Datos sin etiquetar',
            missing_lifecycle: 'Ciclo de vida ausente',
            stale_labels: 'Etiquetas obsoletas',
            run_detection: 'Ejecutar detección de brechas',
            detecting: 'Detectando...',
            detection_complete: 'Detección de brechas completada. {count} brecha(s) detectada(s).',
            detection_failed: 'La detección de brechas falló.',
            by_severity: 'Por Gravedad',
            by_gap_type: 'Por tipo de brecha',
            all_gap_types: 'Todos los tipos de brecha',
            conflicting_labels: 'Etiquetas en conflicto',
            expired_retention: 'Caducado Retención',
            missing_handling: 'Tratamiento de valores faltantes',
            info: 'Información',
            gap_type: 'Tipo de brecha',
            object_id: 'ID del objeto',
            object_type: 'Tipo de objeto',
            detected_at: 'Detectado el',
            open: 'Abrir',
            acknowledged: 'Reconocido',
            remediated: 'Remediado',
            accepted: 'Aceptado',
            acknowledge: 'Reconocer',
            remediate: 'Remediar',
            accept_risk: 'Aceptar riesgo',
            accept_risk_prompt: 'Introduzca risk acceptance justification:',
            failed_fetch: 'Error al fetch gaps. Por favor, inténtelo de nuevo.',
            failed_acknowledge: 'Error al acknowledge gap. Por favor, inténtelo de nuevo.',
            failed_remediate: 'Error al remediate gap. Por favor, inténtelo de nuevo.',
            failed_accept_risk: 'Error al accept gap risk. Por favor, inténtelo de nuevo.',
            failed_detect: 'Error al run gap detection. Por favor, inténtelo de nuevo.',
            loaded_count: 'Cargadas {count} brechas de clasificación',
            related_object_id: 'ID del objeto relacionado',
            related_object_type: 'Tipo de objeto relacionado',
            gap_types: {
                unlabeled_asset: 'Activo sin etiquetar',
                missing_lifecycle: 'Ciclo de vida faltante',
                expired_retention: 'Retencion expirada',
                extended_pending_deletion: 'Eliminacion pendiente extendida',
            },
        },
        label_workflow: {
            title: 'Flujo de etiquetas',
            proposed: 'Propuesta',
            under_review: 'En revisión',
            approved: 'Aprobada',
            deprecated: 'Obsoleta',
            submit_review: 'Enviar a revisión',
            approve: 'Aprobar etiqueta',
            deprecate: 'Marcar como obsoleta',
            bulk_approve: 'Aprobación masiva',
            bulk_approve_success: 'Etiquetas aprobadas correctamente.',
            submit_success: 'Etiqueta enviada a revisión.',
            approve_success: 'Etiqueta aprobada.',
            deprecate_success: 'Etiqueta marcada como obsoleta.',
        },
        review_tasks: {
            title: 'Tareas de revisión',
            create: 'Nueva tarea de revisión',
            system: 'Sistema',
            owner: 'Propietario',
            description: 'Descripción',
            resolution: 'Resolución',
            resolve: 'Resolver',
            approve: 'Aprobar',
            modify: 'Modificar',
            reject: 'Rechazar',
            escalate: 'Escalar',
            escalated: 'Tareas escaladas',
            no_tasks: 'No hay tareas de revisión.',
            resolve_success: 'Tarea de revisión resuelta.',
            status_pending: 'Pendiente',
            status_resolved: 'Resuelta',
            status_escalated: 'Escalada',
            notes: 'Notas',
            create_success: 'Tarea de revisión creada.',
            no_tasks_detail: 'Aún no hay tareas de revisión — cree una tarea para asignar revisiones de etiquetas a los propietarios de sistemas.',
            label_id: 'ID de etiqueta',
            system_id: 'ID de sistema',
            system_name: 'Nombre del sistema',
            owner_id: 'ID del propietario',
            resolution_col: 'Resolucion',
            notes_col: 'Notas',
        },
        exceptions: {
            title: 'Cola de excepciones',
            create: 'Nueva excepción',
            severity: 'Gravedad',
            system: 'Sistema',
            owner: 'Propietario',
            resolve: 'Resolver',
            no_exceptions: 'No se encontraron excepciones.',
            resolve_success: 'Excepción resuelta.',
            dashboard_open: 'Excepciones abiertas',
            dashboard_resolved: 'Excepciones resueltas',
            dashboard_total: 'totales',
            dashboard_by_severity: 'Por gravedad',
            status_open: 'Abierta',
            status_resolved: 'Resuelta',
            status_accepted_risk: 'Riesgo aceptado',
            create_success: 'Excepción creada.',
            no_exceptions_detail: 'Aún no hay excepciones — las excepciones se crean a partir de brechas de alta severidad que requieren un manejo especial.',
            gap_id: 'ID de brecha',
            system_id: 'ID de sistema',
            system_name: 'Nombre del sistema',
            owner_id: 'ID del propietario',
            resolution_notes: 'Notas de resolución',
            resolution_notes_placeholder: 'Describa cómo se resolvió esta excepción...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Mapa de datos empresarial',
            inventory: 'Inventario de datos',
            total_systems: 'Total de sistemas',
            total_labels: 'Total de etiquetas',
            approved_count: 'Etiquetas aprobadas',
            proposed_count: 'Etiquetas propuestas',
            no_data: 'No hay datos de clasificación disponibles.',
            no_approved_labels: 'Aún no hay objetos etiquetados — un objeto aparece en el mapa de datos en cuanto tiene una etiqueta de clasificación, ya sea propuesta, en revisión o aprobada.',
            loaded_count: 'Mapa de datos empresariales cargado',
        },
        discovery: {
            title: 'Ciclos de descubrimiento',
            create: 'Nuevo ciclo',
            cycle_name: 'Nombre del ciclo',
            run_delta: 'Ejecutar detección delta',
            delta_success: 'Detección delta completada.',
            create_success: 'Ciclo de descubrimiento creado.',
            no_cycles: 'No hay ciclos de descubrimiento.',
            status_created: 'Creado',
            status_scanning: 'Escaneo',
            status_review: 'Revisión',
            status_approved: 'Aprobado',
            status_published: 'Publicado',
            status_draft: 'Borrador',
            running: 'En ejecución',
            completed: 'Completado',
            cancelled: 'Cancelado',
            delta_result_summary: 'Se encontraron {added} agregadas, {removed} eliminadas, {changed} etiquetas cambiadas.',
            delta_results: 'Resultados delta',
            view_results: 'Ver resultados',
            scope_type: 'Tipo de alcance',
            scope_type_all: 'Todos',
            scope_type_systems: 'Sistemas',
            scope_type_datasets: 'Conjuntos de datos',
            scope_ids: 'ID de alcance',
            scope_ids_placeholder: 'Introduzca los ID separados por comas',
            cycle_explanation: 'Un ciclo de descubrimiento escanea su infraestructura de datos para identificar, clasificar y rastrear los activos de datos. Configure el alcance para apuntar a sistemas o conjuntos de datos específicos.',
            empty_state_title: 'Aún no hay ciclos de descubrimiento',
            empty_state_description: 'Cree su primer ciclo de descubrimiento para comenzar a escanear y clasificar sus activos de datos.',
            empty_state_cta: 'Crear primer ciclo',
            validation_name_required: 'El nombre del ciclo es obligatorio.',
            validation_scope_type_required: 'Se requiere tipo de alcance',
            validation_scope_ids_required: 'Se requiere al menos un ID de alcance',
        },
        regulatory: {
            sensitivity_importance_info: 'La Ley de Datos (Ley 60/2024/QH15, req. 18e4bfd1) requiere clasificación tanto por sensibilidad como por importancia como dimensiones separadas. Cree una taxonomía para cada dimensión para satisfacer este requisito.',
            handling_rules_info: 'Cada nivel de clasificación debe definir reglas de manejo por solicitud. b89981ac ("Definir reglas de manejo por nivel de clasificación").',
            template_internal_ids_info: 'Todas las plantillas de clasificación utilizan ID internos (por ejemplo, vn_data_law_data_classification_record). No se presenta ninguna plantilla como formulario oficial del ministerio.',
            ai_risk_tiers_info: 'La Ley de IA (Ley 134/2025/QH15, req. 3a87ad63) define cuatro niveles de riesgo de los sistemas de IA: mínimo, limitado, alto e inaceptable. Cree una taxonomía de riesgo de IA con estos niveles.',
            ai_risk_tier_minimal: 'Minimo',
            ai_risk_tier_limited: 'Limitado',
            ai_risk_tier_high: 'Alto',
            ai_risk_tier_unacceptable: 'Inaceptable',
            ai_register_info: 'Los registros de clasificación AI deben vincularse a una entrada del Registro del sistema AI (req. 0f4475d3) que capture: nombre del sistema, propietario, propósito, contexto de implementación, nivel de riesgo, participación del proveedor.',
            ai_template_internal_info: 'Las plantillas de clasificación de IA utilizan ID internos (por ejemplo, AI-RISK-01). No se utilizan ID de marcador de posición oficiales de IA (AI-HC-01 a AI-HC-08).',
            ai_audit_trail_info: 'Los cambios de clasificación de AI producen un seguimiento de auditoría versionado por UC-VN-06: actor, marca de tiempo, valor anterior, valor nuevo y justificación.',
            lifecycle_data_d1_info: 'Las transiciones del ciclo de vida se asignan al procedimiento de ley de datos DATA-D1 pasos: catalogar, clasificar, definir reglas de manejo, capturar evidencia, programar revisiones.',
            lifecycle_deletion_link_info: 'Un activo de datos clasificados que llega al final de su vida útil debería activar un registro de programación de eliminación a través del módulo de orquestación de eliminación.',
            lifecycle_evidence_info: 'Cada cambio de estado del ciclo de vida (borrador, activo, en revisión, archivado, eliminado) captura el actor, la marca de tiempo y la justificación como una entrada de seguimiento de auditoría.',
            escalation_timers_info: 'UC-VN-10 plazos de escalamiento: 5 días hábiles para la revisión del propietario, 48 horas para el escalamiento de excepciones, 72 horas para elementos sensibles o de alto riesgo.',
            escalation_owner_review: '5 dias laborables',
            escalation_exception: '48 horas',
            escalation_high_risk: '72 horas',
            gap_risk_areas_info: 'Requisito de la ley de datos. f6c3060a ("Evaluación de riesgos de datos") requiere un análisis de brechas en 4 áreas de riesgo: privacidad, ciberseguridad, control de acceso y riesgo operativo.',
            gap_risk_area_privacy: 'Privacidad',
            gap_risk_area_cybersecurity: 'Ciberseguridad',
            gap_risk_area_access: 'Control de acceso',
            gap_risk_area_operational: 'Operacional',
            gap_framework_filter_info: 'Filtre las brechas por marco regulatorio (VN_DATA_LAW o VN_AI_LAW_2026) para ver las brechas relevantes para cada alcance regulatorio.',
            framework_vn_data_law: 'Ley de Datos VN',
            framework_vn_ai_law: 'Ley de IA VN 2026',
            enterprise_map_approved_info: 'Este mapa de datos muestra solo registros de clasificación aprobados por UC-VN-10 paso 10 del flujo de trabajo ("Las etiquetas aprobadas se convierten en la verdad empresarial actual"). Se excluyen los borradores y propuestas de expedientes.',
            exports_bundle_info: 'Los paquetes de exportación incluyen: registro de clasificación, reglas de manejo, propiedad/RACI y paquete de evidencia, que coinciden con los requisitos de exportación UC-VN-10 y el formato de hoja de evidencia DATA-EVD-01.',
        },
    },
    monitoring: {
        no_permission_title: 'Permisos insuficientes',
        no_permission_message: 'No tiene permiso para acceder a este modulo de monitoreo.',
        errors: {
            fetch: 'Error al load. Por favor, inténtelo de nuevo.',
            save: 'Error al save. Por favor, inténtelo de nuevo.',
            fetch_programs_failed: 'Error al fetch programs. Por favor, inténtelo de nuevo.',
            fetch_kpi_failed: 'Error al fetch kpi data. Por favor, inténtelo de nuevo.',
            create_snapshot_failed: 'Error al create snapshot. Por favor, inténtelo de nuevo.',
            fetch_periods_failed: 'Error al fetch periods. Por favor, inténtelo de nuevo.',
            create_period_failed: 'Error al create period. Por favor, inténtelo de nuevo.',
            auto_generate_failed: 'Error al auto-generate periods. Por favor, inténtelo de nuevo.',
            start_period_failed: 'Error al start period. Por favor, inténtelo de nuevo.',
            close_period_failed: 'Error al close period. Por favor, inténtelo de nuevo.',
            save_program_failed: 'Error al save program. Por favor, inténtelo de nuevo.',
            activate_program_failed: 'Error al activate program. Por favor, inténtelo de nuevo.',
            pause_program_failed: 'Error al pause program. Por favor, inténtelo de nuevo.',
            complete_program_failed: 'Error al complete program. Por favor, inténtelo de nuevo.',
            archive_program_failed: 'Error al archive program. Por favor, inténtelo de nuevo.',
            fetch_ric_templates_failed: 'Error al fetch ric templates. Por favor, inténtelo de nuevo.',
            fetch_ric_cases_failed: 'Error al fetch ric cases. Por favor, inténtelo de nuevo.',
            create_ric_case_failed: 'Error al create ric case. Por favor, inténtelo de nuevo.',
            transition_ric_failed: 'Error al transition ric case. Por favor, inténtelo de nuevo.',
            add_follow_up_failed: 'Error al add follow-up. Por favor, inténtelo de nuevo.',
            respond_follow_up_failed: 'Error al respond to follow-up. Por favor, inténtelo de nuevo.',
            link_evidence_failed: 'Error al link evidence. Por favor, inténtelo de nuevo.',
            fetch_task_templates_failed: 'Error al fetch task templates. Por favor, inténtelo de nuevo.',
            save_task_template_failed: 'Error al save task template. Por favor, inténtelo de nuevo.',
            delete_task_template_failed: 'Error al delete task template. Por favor, inténtelo de nuevo.',
            delete_framework_link_failed: 'Error al eliminar el enlace del marco normativo. Por favor, inténtelo de nuevo.',
            seed_defaults_failed: 'Error al seed defaults. Por favor, inténtelo de nuevo.',
            fetch_tasks_failed: 'Error al fetch tasks. Por favor, inténtelo de nuevo.',
            create_task_failed: 'Error al create task. Por favor, inténtelo de nuevo.',
            instantiate_tasks_failed: 'Error al instantiate tasks. Por favor, inténtelo de nuevo.',
            start_task_failed: 'Error al start task. Por favor, inténtelo de nuevo.',
            complete_task_failed: 'Error al complete task. Por favor, inténtelo de nuevo.',
            defer_task_failed: 'Error al defer task. Por favor, inténtelo de nuevo.',
            block_task_failed: 'Error al block task. Por favor, inténtelo de nuevo.',
            override_complete_failed: 'Error al override complete. Por favor, inténtelo de nuevo.',
            assign_task_failed: 'Error al assign task. Por favor, inténtelo de nuevo.',
            update_checklist_failed: 'Error al update checklist. Por favor, inténtelo de nuevo.',
        },
        nav: {
            programs: 'Programas',
            periods: 'Períodos',
            task_templates: 'Plantillas de tareas',
            tasks: 'Tareas',
            kpi: 'Panel de KPI',
            ric: 'Consultas del regulador',
            framework_link: 'Supervisión de marco',
            authority_readiness: 'Preparación autoridad',
            change_review: 'Revisión de cambios',
            raci: 'Matriz RACI',
        },
        programs: {
            page_title: 'Programas de supervisión',
            title: 'Programas de supervisión',
            new: 'Nuevo programa',
            edit: 'Editar programa',
            name: 'Nombre',
            scope: 'Alcance',
            frequency: 'Frecuencia',
            owner: 'Responsable',
            activate: 'Activar',
            pause: 'Pausar',
            resume: 'Reanudar',
            complete: 'Completar',
            archive: 'Archivar',
            custom_frequency_days: 'Personalizado Frecuencia (days)',
            select_user: 'Seleccionar usuario…',
            assign_training: 'Asignar formación',
            linked_forms: 'Formularios vinculados',
            linked_forms_none: 'Aún no hay formularios vinculados',
            loaded_count: '{0} programas cargados',
            scope_labels: {
                full: 'Completo',
                partial: 'Parcial',
                targeted: 'Dirigido',
                consent: 'Consentimiento',
                dpia: 'DPIA',
                vendors: 'Proveedores',
                classification: 'Clasificación',
                rights_requests: 'Solicitudes de derechos',
                data_mapping: 'Mapeo de datos',
                transfers: 'Transferencias',
                security_monitoring: 'Monitoreo de seguridad',
                authority_response: 'Respuesta de la autoridad',
                evidence_preservation: 'Preservación de evidencia',
                localization_review: 'Revisión de localización',
                ai_system_registration: 'AI Registro del sistema',
                ai_pre_deployment: 'AI Evaluación previa a la implementación',
                ai_transparency: 'AI Verificación de transparencia',
                ai_incident_review: 'AI Revisión de incidentes',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Períodos de seguimiento',
            title: 'Períodos de revisión',
            new: 'Nuevo período',
            label: 'Etiqueta del período',
            start_date: 'Fecha de inicio',
            end_date: 'Fecha de fin',
            reviewers: 'Revisores',
            auto_generate: 'Generar períodos automáticamente',
            start: 'Iniciar',
            close: 'Cerrar',
            change_digest: 'Resumen de cambios',
            no_digest: 'No hay resumen disponible.',
            reviewers_count: 'Número de revisores',
            select_program: 'Seleccione Program',
            select_program_prompt: 'Seleccione program to view periods.',
            hide_digest: 'Ocultar resumen',
            loaded_count: '{0} períodos cargados',
            periods_auto_generated: 'Períodos generados automáticamente',
            label_placeholder: 'p. ej. 2026-Q1',
        },
        task_templates: {
            page_title: 'Plantillas de tareas',
            title: 'Plantillas de tareas',
            new: 'Nueva plantilla',
            edit: 'Editar plantilla',
            category: 'Categoría',
            priority: 'Prioridad',
            checklist_items: 'Elementos de la lista de verificación',
            effort: 'Esfuerzo estimado (horas)',
            pre_seeded: 'Precargada',
            seed_defaults: 'Cargar valores predeterminados',
            add_checklist: 'Añadir elemento a la lista',
            guidance: 'Notas de orientación',
            all_categories: 'Todas las categorías',
            loaded_count: 'Plantillas de tareas {0} cargadas',
            defaults_seeded: 'Plantillas de tareas predeterminadas sembradas',
            checklist_item_label: 'Elemento de la lista de verificación {0}',
            remove_checklist_item: 'Eliminar elemento de la lista de verificación {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Tareas de revisión',
            title: 'Tareas de revisión',
            new: 'Nueva tarea',
            assigned_to: 'Asignada a',
            due_date: 'Fecha de vencimiento',
            checklist: 'Lista de verificación',
            start: 'Iniciar',
            complete: 'Completar',
            defer: 'Aplazar',
            block: 'Bloquear',
            override_complete: 'Completar con anulación',
            link_evidence: 'Vincular evidencia',
            direct_upload_label: 'Subir archivo de evidencia',
            instantiate: 'Crear desde plantillas',
            defer_reason: 'Motivo del aplazamiento',
            override_justification: 'Justificación de la anulación',
            filter_assignee: 'Filtrar por asignado',
            select_period: 'Seleccione período',
            select_program: 'Seleccione Program',
            select_program_period_prompt: 'Seleccione program and period to view tasks.',
            assign_placeholder: 'Asignar a...',
            loaded_count: '{0} tareas cargadas',
            tasks_instantiated: 'Tareas creadas desde plantillas',
            overdue: 'Vencida',
            checklist_for: 'lista de verificación para',
            manage_checklist: 'Gestionar lista de verificación',
            add_checklist_item: 'Añadir elemento',
            checklist_item_label: 'Elemento',
            checklist_item_description: 'Descripción',
            remove_checklist_item: 'Eliminar elemento',
            save_checklist: 'Guardar lista de verificación',
            no_checklist_items: 'Aún no hay elementos.',
            confirm_delete_attachment: '¿Está seguro de que desea eliminar este archivo de evidencia? Esta acción no se puede deshacer.',
        },
        kpi: {
            page_title: 'Panel de KPI',
            title: 'Panel de KPI',
            kpi_name: 'Nombre del KPI',
            current_value: 'Valor actual',
            target: 'Objetivo',
            trends: 'Tendencias',
            capture_snapshot: 'Capturar instantánea',
            select_program: 'Seleccione Program',
            select_program_prompt: 'Seleccione program to view KPIs.',
            snapshot: 'Instantánea',
            kpi_labels: {
                on_track: 'En Rastrear',
                at_risk: 'En Riesgo',
                behind: 'Atrasado',
                rights_request_sla_rate: 'Solicitud de derechos SLA Tarifa',
                vendor_review_currency: 'Moneda de revisión del proveedor',
                dpia_completion_rate: 'DPIA Tasa de finalización',
                classification_coverage: 'Cobertura de clasificación',
                consent_policy_currency: 'Moneda de la política de consentimiento',
                security_event_detection_rate: 'Tasa de detección de eventos de seguridad',
                evidence_preservation_compliance_rate: 'Tasa de cumplimiento de la preservación de pruebas',
                authority_response_timeliness: 'Oportunidad de respuesta de la autoridad',
                localization_readiness_score: 'Puntuación de preparación para la localización',
                ai_register_completeness: 'AI Integridad del registro del sistema',
                pre_deployment_gate_pass_rate: 'Tasa de paso de puerta previa a la implementación',
                transparency_disclosure_coverage: 'Cobertura de divulgación de transparencia',
                ai_incident_readiness_score: 'AI Puntuación de preparación para incidentes',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPI de la ley de ciberseguridad: tasa de detección de eventos de seguridad, tasa de cumplimiento de la preservación de pruebas, puntualidad de la respuesta de la autoridad, puntuación de preparación para la localización.',
            ai_law_kpi_info: 'KPI de la Ley de IA: integridad del registro de sistemas de IA, tasa de aprobación de la puerta previa a la implementación, cobertura de divulgación de transparencia, puntuación de preparación ante incidentes de IA.',
            no_data: 'Sin datos disponibles.',
            snapshot_date: 'Fecha de instantánea',
            loaded_count: '{0} instantáneas KPI cargadas',
            snapshot_captured: 'Instantánea KPI capturada',
            showing_chart: 'Mostrando vista de gráfico',
            showing_table: 'Mostrando vista de tabla',
            aria_current_values: 'Valores actuales KPI',
            kpi_label: 'KPI',
            actual: 'Real',
            trend: 'Tendencia',
            notes: 'Notas',
            trend_up: 'En alza',
            trend_down: 'En baja',
            trend_stable: 'Estable',
        },
        ric: {
            page_title: 'Casos RIC',
            title: 'Casos de consulta del regulador',
            new: 'Nuevo caso RIC',
            reference: 'Referencia',
            regulator: 'Regulador',
            inquiry_type: 'Tipo de consulta',
            deadline: 'Plazo de respuesta',
            lead: 'Responsable',
            follow_ups: 'Seguimientos',
            add_follow_up: 'Añadir seguimiento',
            respond: 'Responder',
            question: 'Pregunta',
            response: 'Respuesta',
            asked_at: 'Preguntado el',
            no_follow_ups: 'No follow-ups aún.',
            received_date: 'Recibido Fecha',
            responded_at: 'Respondido el',
            hide_follow_ups: 'Ocultar Follow-ups',
            inquiry_types: {
                general: 'generales',
                investigation: 'Investigación',
                audit: 'Auditoría',
                complaint: 'Queja',
                general_audit: 'Auditoría general',
                rights_requests_review: 'Revisión de solicitudes de derechos',
                cross_border_transfer_review: 'Revisión de transferencias transfronterizas',
                vendor_governance_review: 'Revisión de gobernanza de proveedores',
                consent_management_review: 'Revisión de gestión de consentimiento',
                data_mapping_ropa_review: 'Revisión de mapeo de datos / ROPA',
                incident_readiness_review: 'Revisión de preparación ante incidentes',
                security_access_control_review: 'Revisión de seguridad y control de acceso',
                targeted_processing_review: 'Revisión de procesamiento dirigido',
                bank_readiness_pack: 'Paquete de preparación bancaria',
            },
            link_evidence: 'Vincular Evidencia',
            loaded_count: 'Cargado {0} casos RIC',
            to_follow_up: 'a la pregunta de seguimiento',
            overdue: 'Vencido',
            overdue_indicator: 'Se superó el plazo de respuesta',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Recibido',
                in_triage: 'En triaje',
                evidence_gathering: 'Recopilación de evidencia',
                response_drafting: 'Borrador de respuesta',
                internal_review: 'Revisión interna',
                submitted: 'Enviado',
                follow_up: 'Seguimiento',
                closed: 'Cerrado',
            },
        },
        framework_link: {
            title: 'Supervisión vinculada al marco',
            link_program: 'Vincular programa al marco',
            framework_code: 'Código del marco',
            select_framework: 'Seleccionar un marco...',
            select_program: 'Seleccionar un programa...',
            readiness_score: 'Puntuación de preparación',
            no_links: 'No se encontraron programas vinculados al marco',
            seed_defaults: 'Crear plantillas predeterminadas',
            program_id: 'ID del programa',
            program: 'Programa',
        },
        authority_readiness: {
            title: 'Preparación para solicitudes de autoridad',
            create_entry: 'Nueva entrada de preparación',
            select_framework: 'Seleccionar un marco...',
            framework_code: 'Marco',
            readiness_status: 'Estado de preparación',
            readiness_package: 'Paquete de preparación',
            run_drill: 'Ejecutar simulacro de preparación',
            drill_success: 'Simulacro de preparación registrado',
            last_drill: 'Último simulacro',
            never_drilled: 'Nunca',
            no_readiness: 'No se encontraron datos de preparación de autoridad',
            entry_created: 'Entrada de preparación creada',
            ready: 'Listo',
            partial: 'Parcial',
            not_ready: 'No listo',
            items: 'items',
        },
        change_review: {
            title: 'Revisión de cambios regulatorios',
            new_review: 'Nueva revisión de cambios',
            change_id: 'ID de cambio',
            select_change: 'Seleccionar un cambio...',
            select_framework: 'Seleccionar un marco...',
            impact_assessment: 'Evaluación de impacto',
            decision: 'Decisión',
            no_action: 'Sin acción',
            internal_update: 'Actualización interna',
            authority_filing: 'Presentación ante autoridad',
            no_reviews: 'No se encontraron revisiones de cambios',
            auto_cycle: 'Ciclo de revisión automático',
            cadence: 'Frecuencia de revisión',
            framework: 'Marco',
        },
        export_csv: 'Exportar CSV',
        exporting: 'Exportando…',
        export_success: 'CSV exportado exitosamente.',
        export_failed: 'CSV exportación fallida.',
        export_attestation_pack: 'Paquete de certificación de exportación',
        staleness: {
            badge_label: '{0} elementos obsoletos',
            days_since: '{0} días desde la última actualización',
            detail_title: 'Artículos obsoletos',
            accept_justification: 'Aceptar con Justificación',
            justification_label: 'Justificación',
            no_stale: 'No se encontraron elementos obsoletos.',
            threshold_label: 'Umbral de estancamiento (días)',
            threshold_min: 'Mínimo 30 días',
            threshold_max: 'Máximo 730 días',
            threshold_saved: 'Umbral de estancamiento actualizado.',
        },
        csv_columns: {
            name: 'Nombre',
            status: 'Estado',
            owner: 'propietario',
            created_at: 'Creado en',
            updated_at: 'Actualizado en',
            scope: 'Alcance',
            frameworks: 'Marcos',
            period_label: 'Etiqueta de período',
            start_date: 'Fecha de inicio',
            end_date: 'Fecha de finalización',
            task_count: 'Recuento de tareas',
            completion_rate: 'Tasa de finalización',
            title: 'Título',
            assigned_to: 'Asignado a',
            due_date: 'Fecha de vencimiento',
            priority: 'Prioridad',
            completed_at: 'Completado en',
            kpi_code: 'KPI Código',
            kpi_label: 'KPI Etiqueta',
            target: 'Objetivo',
            actual: 'real',
            trend: 'Tendencia',
            reference: 'Referencia',
            regulator: 'Regulador',
            inquiry_type: 'Tipo de consulta',
            lead: 'plomo',
            received_date: 'Fecha de recepción',
            deadline: 'Fecha límite',
            framework_code: 'Código marco',
            readiness_status: 'Estado de preparación',
            package_count: 'Recuento de paquetes',
            change_id: 'Cambiar ID',
            impact_assessment: 'Evaluación de impacto',
            decision: 'decisión',
            program: 'Programa',
            readiness_score: 'Puntuación de preparación',
            category: 'categoría',
            checklist_count: 'Recuento de lista de verificación',
            effort_hours: 'Esfuerzo (horas)',
            version: 'Versión',
            department: 'Departamento',
            function_area: 'Área de funciones',
            role: 'Rol',
            assignee: 'Cesionario',
            frequency: 'Frecuencia',
        },
        raci: {
            page_title: 'Matriz RACI de Gobernanza',
            title: 'Matriz RACI de Gobernanza',
            department: 'Departamento',
            function_area: 'Área de funciones',
            responsible: 'Responsable',
            accountable: 'responsable',
            consulted: 'Consultado',
            informed: 'informado',
            assign_role: 'Asignar rol RACI',
            select_program: 'Seleccionar programa',
            select_program_prompt: 'Seleccione un programa para ver la matriz RACI.',
            no_assignments: 'No se encontraron asignaciones RACI.',
            role_updated: 'Asignación RACI actualizada.',
            role_labels: {
                R: 'Responsable',
                A: 'responsable',
                C: 'Consultado',
                I: 'informado',
            },
            loaded_count: 'Cargado {0} Asignaciones RACI',
            single_accountability_warning: 'Solo se permite un rol de Responsable (A) por área funcional. La asignación de un nuevo rol de Responsable reemplazará al existente.',
            cybersec_function_areas: 'Áreas funcionales de la Ley de Ciberseguridad: Respuesta a Incidentes, Preservación de Evidencias, Cooperación de Autoridades, Revisión de Localización.',
            ai_law_function_areas: 'AI Áreas de función de la ley: Registro del sistema, Evaluación previa al despliegue, Transparencia, Manejo de incidentes.',
            suggested_function_areas: 'Áreas de funciones sugeridas',
        },
        prior_cycle: {
            link_label: 'Enlace al ciclo anterior',
            select_prior: 'Seleccionar ciclo anterior...',
            linked: 'Año tras año',
            no_prior: 'No hay ciclos anteriores disponibles.',
            linked_success: 'Vinculación de ciclo anterior.',
        },
        digest_table: {
            module: 'Módulo',
            entity_type: 'Tipo de entidad',
            change_type: 'Tipo de cambio',
            description: 'Descripción',
            changed_at: 'Cambiado en',
        },
        view_related_ric: 'Ver casos RIC relacionados',
        linked_program: 'Programa vinculado',
        nav_raci: 'Matriz RACI',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Borrador de respuesta',
            drafting: 'Redacción…',
            results_heading: 'Respuestas redactadas ({count})',
            tokens_label: '≈ {count} fichas',
            counsel_label: 'Se requiere revisión del abogado:',
            counsel_default: 'Al menos una respuesta requiere la revisión del abogado antes de enviarla.',
            requires_counsel_label: 'Requiere revisión del abogado.',
            accept_label: 'Aceptar borrador',
            reject_label: 'Rechazar borrador',
            error_generic: 'No se pudo redactar la respuesta. Inténtelo de nuevo o refine el texto de la consulta.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Calcular el cronograma de notificación de incumplimiento',
            computing: 'Computación…',
            results_heading: 'Plazos de notificación de incumplimiento ({count})',
            tokens_label: '≈ {count} fichas',
            handoff_label: 'Forseti sugiere:',
            empty_label: 'Ningún marco instalado declara un deber de notificación para este incidente.',
            mandatory_label: 'Campos obligatorios:',
            draft_label: 'Borrador de notificación',
            remaining_label: '{count}h restantes',
            overdue_label: 'VENCIDO por {count}h',
            acknowledge_label: 'Reconocer el cronograma',
            acknowledging: 'Reconociendo…',
            error_generic: 'No se pudo calcular el cronograma de infracción. Intentar otra vez.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmado Brecha',
            no: 'No un Brecha',
            uncertain: 'Pendiente',
        },
        errors: {
            fetch: 'Error al load incidents. Por favor, inténtelo de nuevo.',
            save: 'Error al save incident. Por favor, inténtelo de nuevo.',
            fetch_failed: 'Error al fetch incidents. Por favor, inténtelo de nuevo.',
            save_failed: 'Error al save incident. Por favor, inténtelo de nuevo.',
            transition_failed: 'Error al transition incident. Por favor, inténtelo de nuevo.',
            close_failed: 'Error al close incident. Por favor, inténtelo de nuevo.',
            fetch_timeline_failed: 'Error al fetch timeline. Por favor, inténtelo de nuevo.',
            fetch_decisions_failed: 'Error al fetch decisions. Por favor, inténtelo de nuevo.',
            triage_failed: 'Error al update triage. Por favor, inténtelo de nuevo.',
            impact_update_failed: 'Error al update impacto evaluación. Por favor, inténtelo de nuevo.',
            breach_update_failed: 'Error al update brecha evaluación. Por favor, inténtelo de nuevo.',
            create_timeline_failed: 'Error al create timeline entry. Por favor, inténtelo de nuevo.',
            create_decision_failed: 'Error al create decision. Por favor, inténtelo de nuevo.',
            notification_update_failed: 'Error al update notification. Por favor, inténtelo de nuevo.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Error al fetch tasks. Por favor, inténtelo de nuevo.',
            create_task_failed: 'Error al create task. Por favor, inténtelo de nuevo.',
            update_task_status_failed: 'Error al actualizar el estado de la tarea. Por favor, inténtelo de nuevo.',
            update_checklist_failed: 'Error al update checklist. Por favor, inténtelo de nuevo.',
            assign_task_failed: 'Error al assign task. Por favor, inténtelo de nuevo.',
            escalate_task_failed: 'Error al escalate task. Por favor, inténtelo de nuevo.',
            fetch_templates_failed: 'Error al fetch templates. Por favor, inténtelo de nuevo.',
            permission_denied: 'No tiene permisos para acceder a esta seccion. Contacte con su administrador para solicitar acceso.',
        },
        harm_likelihood: {
            unlikely: 'Improbable',
            possible: 'Posible',
            likely: 'Probable',
            very_likely: 'Muy probable',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'Correo electrónico',
            phone: 'Teléfono',
            internal_system: 'Interno Sistema',
            third_party: 'Tercero',
            automated_detection: 'Detección automatizada',
        },
        task_transition_labels: {
            start: 'Inicio',
            complete: 'Completar',
            block: 'Bloquear',
            defer: 'Diferir',
            cancel: 'Cancelar',
            unblock: 'Desbloquear',
        },
        transition_labels: {
            start: 'Inicio',
            complete: 'Completar',
            block: 'Bloquear',
            defer: 'Diferir',
        },
        nav: {
            all: 'Todos los incidentes',
            tasks: 'Tareas de respuesta',
            reports: 'Informes',
            breach_notifications: 'Notificaciones de violación',
            supplements: 'Suplementos',
            remediation: 'Remediación',
            authority_requests: 'Solicitudes de autoridad',
            disclosure_register: 'Registro de divulgación',
        },
        list: {
            page_title: 'Incidentes',
            title: 'Incidentes',
            new: 'Nuevo incidente',
            edit: 'Editar incidente',
            incident_title: 'Título',
            severity: 'Gravedad',
            reported_at: 'Fecha de notificación',
            reported_by: 'Notificado por',
            assigned_to: 'Asignado a',
            view_detail: 'Ver detalle',
            reference: 'Referencia',
            lead: 'Responsable',
            reported_via: 'Reportado vía',
            discovery_date: 'Fecha de descubrimiento',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Ocultar Detalles',
            close_incident: 'Cerrar Incidente',
            close_justification_prompt: 'Por favor, proporcione una justificación para cerrar este incidente.',
            justification: 'Justificación',
            lead_id: 'ID del responsable',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Contacto',
            category: 'Categoría',
            not_assessed: 'No evaluado',
        },
        detail: {
            page_title: 'Detalle del incidente',
            title: 'Detalle del incidente',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Resumen',
                triage: 'Triaje',
                impact: 'Evaluación de impacto',
                breach: 'Evaluación de brecha',
                timeline: 'Cronología',
                decisions: 'Decisiones',
                evidence: 'Evidencia',
                audit_trail: 'Pista de auditoría',
            },
            evidence_upload_label: 'Adjuntar archivo de evidencia',
            evidence_uploaded: 'Evidencia subida',
            evidence_deleted: 'Evidencia eliminada',
            evidence_delete_error: 'No se pudo eliminar la evidencia',
            evidence_uploaded_at: 'Subido',
            evidence_empty: 'No hay archivos de evidencia adjuntos a este incidente.',
            evidence_size: 'Tamaño',
            triage_notes: 'Notas de triaje',
            initial_classification: 'Clasificación inicial',
            individuals_affected: 'Número de interesados afectados',
            data_categories: 'Categorías de datos afectadas',
            systems_compromised: 'Sistemas comprometidos',
            geographic_scope: 'Alcance geográfico',
            breach_determination: 'Determinación de brecha',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Notificación requerida',
            notification_deadline: 'Plazo de notificación',
            pdpc_reportable: 'Notificable a la autoridad de control',
            add_timeline: 'Añadir entrada de cronología',
            add_decision: 'Añadir decisión',
            not_found: 'Incidente no found.',
            team_members: 'Miembros del equipo',
            checklist: 'Lista de verificación',
            completed: 'Completado',
            impact_severity_score: 'Calculated Impacto Gravedad Puntuación',
            records_affected: 'Registros afectados',
            potential_consequences: 'Consecuencias potenciales',
            harm_likelihood: 'Probabilidad de daño',
            legal_basis: 'Base legal',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdicciones',
            jurisdictions_comma: 'Jurisdicciones (separadas por comas)',
            jurisdiction: 'Jurisdicción',
            authority_status: 'Estado de la autoridad',
            subject_status: 'Sujeto Estado',
            authority_deadline: 'Plazo de la autoridad',
            subject_deadline: 'Sujeto Fecha límite',
            mark_authority_done: 'Marcar autoridad como completada',
            mark_subject_done: 'Marcar interesado como completado',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'No evaluado',
            event_type: 'Evento Tipo',
            timestamp: 'Marca de tiempo',
            actor: 'Actor',
            no_timeline_events: 'Aún no hay eventos en la línea de tiempo.',
            decision_type: 'Tipo de decisión',
            decision: 'Decisión',
            rationale: 'Justificación',
            decision_made: 'Decisión tomada',
            no_decisions: 'Aún no se han registrado decisiones.',
            no_audit_events: 'Aún no se han registrado eventos de auditoría.',
            export_pdf: 'Exportar PDF',
            pdf_title: 'Informe Resumen de Incidente',
            description: 'Descripción',
            linked_records: 'Registros Vinculados',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Tareas de respuesta a incidentes',
            title: 'Tareas de respuesta a incidentes',
            new: 'Nueva tarea',
            edit: 'Editar tarea',
            task_type: 'Tipo de tarea',
            assignee: 'Responsable',
            due_date: 'Fecha de vencimiento',
            dependencies: 'Dependencias',
            mark_complete: 'Marcar como completada',
            templates: 'Plantillas de tareas',
            select_incident: 'Seleccionar incidente',
            all_statuses: 'Todos los estados',
            all_categories: 'Todas las categorías',
            new_response_task: 'Nueva tarea de respuesta',
            category: 'Categoría',
            priority: 'Prioridad',
            depends_on: 'Depende de (IDs de tareas separados por comas)',
            checklist_items: 'Elementos de la lista de verificación',
            remove: 'Eliminar',
            add: 'Añadir',
            new_checklist_item: 'Nuevo elemento de la lista',
            no_templates: 'No hay plantillas disponibles.',
            assign_task: 'Asignar tarea',
            assign_to: 'Asignar a',
            assign: 'Asignar',
            select_incident_prompt: 'Por favor, seleccione un incidente para ver sus tareas de respuesta.',
            checklist: 'Lista de verificación',
            hide_checklist: 'Ocultar lista de verificación',
            escalate: 'Escalar',
            escalations: 'Escalados',
            no_checklist_items: 'No hay elementos en la lista de verificación.',
        },
        reports: {
            page_title: 'Informes de incidentes',
            title: 'Informes de incidentes',
            summary: 'Estadísticas resumidas',
            by_severity: 'Por gravedad',
            by_status: 'Por estado',
            recent: 'Incidentes recientes',
            total_incidents: 'Total de incidentes',
            open: 'Abiertos',
            closed: 'Cerrados',
            export_csv: 'Exportar CSV',
            export_pdf: 'Exportar PDF',
            export_success: 'Informe exportado correctamente.',
            export_error: 'Error al exportar el informe.',
            reference: 'Referencia',
            view_report: 'Ver informe',
        },
        statuses: {
            reported: 'Notificado',
            triage: 'Triaje',
            contained: 'Contenido',
            investigation: 'Investigación',
            remediation: 'Remediación',
            closed: 'Cerrado',
            false_positive: 'Falso positivo',
        },
        task_statuses: {
            pending: 'Pendiente',
            in_progress: 'En curso',
            completed: 'Completada',
            blocked: 'Bloqueada',
            cancelled: 'Cancelada',
        },
        severity: {
            low: 'Bajo',
            medium: 'Medio',
            high: 'Alto',
            critical: 'Crítico',
        },
        task_types: {
            containment: 'Contención',
            investigation: 'Investigación',
            remediation: 'Remediación',
            communication: 'Comunicación',
            documentation: 'Documentación',
            notification: 'Notificación',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Datos Brecha',
            unauthorized_access: 'Acceso no autorizado',
            data_loss: 'Pérdida de datos',
            system_compromise: 'Compromiso del sistema',
            malware: 'malware',
            phishing: 'Suplantación',
            insider_threat: 'Amenaza interna',
            other: 'Otro',
        },
        reported_via_options: {
            email: 'Correo electrónico',
            phone: 'Teléfono',
            internal_system: 'Interno Sistema',
            third_party: 'Tercero',
            automated_detection: 'Detección automatizada',
        },
        harm_likelihood_options: {
            unlikely: 'Improbable',
            possible: 'Posible',
            likely: 'Probable',
            very_likely: 'Muy probable',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmado Brecha',
            suspected_breach: 'Brecha sospechada',
            not_a_breach: 'No un Brecha',
            pending: 'Pendiente',
        },
        timeline_event_types: {
            detection: 'Detección',
            escalation: 'Escalamiento',
            containment: 'Contención',
            investigation_start: 'Inicio de la investigación',
            evidence_collected: 'Evidencias recopiladas',
            notification_sent: 'Notificación Enviado',
            remediation_action: 'Remediación Acción',
            status_change: 'Cambio de estado',
            communication: 'Comunicación',
            other: 'Otro',
        },
        decision_types: {
            containment: 'Contención',
            notification: 'Notificación',
            remediation: 'Remediación',
            escalation: 'Escalamiento',
            closure: 'Cierre',
            other: 'Otro',
        },
        task_transitions: {
            start: 'Inicio',
            complete: 'Completar',
            block: 'Bloquear',
            cancel: 'Cancelar',
            unblock: 'Desbloquear',
        },
        announce: {
            loaded: '{count} incidentes cargados',
            reports_loaded: '{count} informes de incidentes cargados',
            tasks_loaded: '{count} tareas cargadas',
            created: 'Incidente creado exitosamente',
            updated: 'Incidente actualizado exitosamente',
            closed: 'Incidente cerrado exitosamente',
            status_changed: 'El estado del incidente cambió',
            triage_completed: 'Triaje completado con éxito',
            impact_updated: 'Evaluación de impacto actualizada',
            breach_updated: 'Evaluación de infracciones actualizada',
            timeline_added: 'Evento de línea de tiempo agregado',
            decision_recorded: 'Decisión registrada',
            notification_updated: 'Estado de notificación actualizado',
            task_created: 'Tarea creada exitosamente',
            task_status_changed: 'El estado de la tarea cambió',
            checklist_completed: 'Elemento de la lista de verificación completado',
            checklist_unchecked: 'Elemento de la lista de verificación sin marcar',
            task_assigned: 'Tarea asignada exitosamente',
            task_escalated: 'Tarea escalada',
            export_initiated: 'Exportación iniciada',
        },
        breach_notifications: {
            page_title: 'Notificaciones de infracción',
            title: 'Notificaciones de infracción',
            new_notification: 'Nueva Notificación',
            no_notifications: 'No se encontraron notificaciones de incumplimiento',
            status: 'Estado',
            submit_to_mps: 'Enviar a MPS',
            decision: 'decisión',
            decision_maker: 'Tomador de decisiones',
            notify: 'Notificar',
            do_not_notify: 'No notificar',
            rationale: 'Justificación',
            filing_deadline: 'Fecha límite de presentación',
            overdue: 'Vencido',
            due_soon: 'Próximo a vencer',
            on_track: 'En plazo',
            legal_review: 'Revisión legal',
            incident_id: 'ID de incidente',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Evaluación',
                decision_to_notify: 'Decisión de notificar',
                notification_drafted: 'Notificación redactada',
                legal_review: 'Revisión legal',
                submitted_to_authority: 'Presentado a la autoridad',
                supplement_requested: 'Suplemento solicitado',
                supplement_submitted: 'Suplemento enviado',
                acknowledged: 'Reconocido',
                closed: 'Cerrado',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Solicitudes de suplementos',
            title: 'Solicitudes de suplementos',
            new_supplement: 'Nuevo suplemento',
            no_supplements: 'No se encontraron solicitudes de suplementos',
            request_details: 'Detalles de la solicitud',
            response_package: 'Paquete de respuesta',
            submit_response: 'Enviar respuesta',
            deadline: 'Fecha límite',
            notification_id: 'ID de notificación',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Ingrese un ID de notificación para ver los suplementos.',
            supplement_statuses: {
                requested: 'Solicitado',
                drafting: 'Redacción',
                submitted: 'Enviado',
                accepted: 'Aceptado',
                rejected: 'Rechazado',
            },
        },
        remediation: {
            page_title: 'Acciones de remediación',
            title: 'Acciones de remediación',
            new_action: 'Nueva acción',
            no_actions: 'No se encontraron acciones de remediación',
            description: 'Descripción',
            owner: 'propietario',
            department: 'Departamento',
            deadline: 'Fecha límite',
            statuses: {
                planned: 'planeado',
                in_progress: 'En progreso',
                completed: 'Completado',
                verified: 'Verificado',
            },
            verify: 'verificar',
            incident_id: 'ID de incidente',
            enter_incident_id: 'Ingrese un ID de incidente para ver las acciones correctivas.',
            start: 'Empezar',
            complete_action: 'Completar',
        },
        authority_requests: {
            page_title: 'Solicitudes de autoridad',
            title: 'Solicitudes de autoridad',
            new_request: 'Nueva Solicitud',
            no_requests: 'No se encontraron solicitudes de autoridad',
            authority_name: 'Nombre de la autoridad',
            request_details: 'Detalles de la solicitud',
            response_package: 'Paquete de respuesta',
            submission_channel: 'Canal de envío',
            submit_response: 'Enviar respuesta',
            deadline: 'Fecha límite',
            confirmation: 'Confirmación',
            incident_id_optional: 'ID de incidente (opcional)',
            response_context_label: 'Contexto de respuesta (para materialización de documento)',
            response_context_none: '(omitir materialización)',
            response_context_supplement: 'Respuesta suplementaria',
            response_context_information: 'Respuesta a solicitud de información',
            response_context_inspection: 'Respuesta a inspección',
            materialisation_status_column: 'Documento de envío',
            materialisation_pending: 'Pendiente',
            materialisation_success: 'Materializado',
            materialisation_failed: 'Fallido',
            materialisation_retry_button: 'Reintentar',
            ar_statuses: {
                received: 'Recibido',
                verified: 'Verificado',
                assembling: 'Montaje',
                submitted: 'Enviado',
                closed: 'Cerrado',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Revisión legal',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Lista de verificación',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Registro de divulgación',
            title: 'Registro de divulgación',
            new_entry: 'Nueva entrada',
            no_entries: 'No se encontraron entradas en el registro de divulgación',
            disclosure_type: 'Tipo de divulgación',
            disclosure_date: 'Fecha de divulgación',
            recipient: 'Destinatario',
            authority_name: 'Nombre de la autoridad',
            channel: 'canal',
            content_summary: 'Resumen de contenido',
            incident_id_optional: 'ID de incidente (opcional)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Asistente de configuración',
            tours: 'Visitas guiadas por módulo',
            sample_data: 'Datos de ejemplo',
        },
        wizard: {
            title: 'Asistente de configuración',
            welcome: 'Bienvenido a AesirX ComplianceOne',
            welcome_message: 'Configuremos su organización. Este asistente le guiará a través de la configuración inicial.',
            setup_complete: 'Configuración completada',
            skipped_message: 'El asistente de configuración fue omitido. Puede ejecutarlo en cualquier momento para configurar su organización.',
            start_setup: 'Iniciar configuración',
            skip_wizard: 'Omitir asistente',
            go_to_admin: 'Ir a administrador',
            skip_for_now: 'Omitir por ahora',
            next_step: 'Siguiente paso',
            previous_step: 'Paso anterior',
            finish_setup: 'Finalizar configuración',
            rerun: 'Reiniciar asistente de configuración',
            step_label: 'Paso {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'El nombre de la organización es obligatorio.',
            validation_admin_email_required: 'El correo electrónico del administrador es obligatorio.',
            validation_admin_email_invalid: 'Por favor, introduzca una dirección de correo electrónico válida.',
            validation_cmp_endpoint_required: 'El punto de acceso CMP es obligatorio cuando CMP está habilitado.',
            steps: {
                license_activation: 'Activación de licencia',
                org_profile: 'Perfil de la organización',
                module_selection: 'Selección de módulos',
                cmp_integration: 'Integración CMP',
                notification_preferences: 'Notificaciones',
                review: 'Revisión y confirmación',
            },
            step_descriptions: {
                license_activation: 'Ingrese su clave de licencia para activar su paquete y determinar qué módulos están disponibles.',
                org_profile: 'Configure el nombre de la organización, jurisdicción, sector, tamaño e idioma preferido.',
                module_selection: 'Elija qué módulos de cumplimiento activar para su organización.',
                cmp_integration: 'Configure la integración con la Consent Management Platform (opcional).',
                notification_preferences: 'Configure cómo recibe notificaciones sobre eventos de cumplimiento.',
                review: 'Revise su configuración y finalice la instalación.',
            },
            license_step: {
                validate_button: 'Validar licencia',
                skip_warning: 'El acceso al módulo se limitará al nivel de paquete predeterminado. Puede activar una licencia más adelante en Plataforma > Configuración de licencia.',
            },
            module_descriptions: {
                rights_requests: 'Gestione solicitudes de acceso y eliminación de datos personales.',
                data_mapping: 'Mapee sistemas, flujos de datos y actividades de procesamiento.',
                governance: 'Supervise proveedores, aprobaciones, desviaciones y los registros de evidencia.',
                consent: 'Administre políticas de consentimiento y catálogos de finalidades.',
                consent_properties: 'Gestione el consentimiento en múltiples propiedades digitales.',
                assessments: 'Realice evaluaciones de impacto y transferencias transfronterizas de datos.',
                classification: 'Clasifique y etiquete activos de datos con seguimiento del ciclo de vida.',
                monitoring: 'Revisiones de cumplimiento programadas y seguimiento de KPI.',
                incidents: 'Reporte y gestione incidentes de seguridad y privacidad.',
                connectors: 'Conéctese a sistemas externos para el intercambio automatizado de datos.',
                vendor_risk: 'Puntúe proveedores, rastree contratos y gestione la diligencia debida.',
                deletion_orchestration: 'Coordine la eliminación de datos entre sistemas y proveedores.',
                discovery: 'Descubra y clasifique datos en toda su infraestructura.',
                incident_ops: 'Playbooks avanzados de incidentes, seguimiento de SLA y escalaciones.',
                program_governance: 'Evaluaciones de madurez, informes ejecutivos y preparación para auditorías.',
                access_accountability: 'Supervise el acceso de usuarios, sesiones y flujos de justificación.',
                localization: 'Paquetes de cumplimiento específicos del mercado y marcos regulatorios.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Espacio de trabajo de socios y revendedores.',
                privacy_scanner: 'Monitoreo de privacidad en tiempo real de propiedades digitales.',
                onboarding: 'Asistente de configuración, recorridos guiados y gestión de datos de ejemplo.',
                compliance_forms: 'Plantillas de cumplimiento interactivas y generación de documentos.',
                audit_trail: 'Registro de auditoría inmutable completo y retención.',
                organizations: 'Gestión multi-organización y de grupos.',
                task_management: 'Planificación unificada de tareas, motor de flujo de trabajo y vistas de calendario.',
            },
            module_categories: {
                data_privacy_rights: 'Privacidad y derechos de datos',
                vendor_third_party: 'Gestión de proveedores y terceros',
                risk_assessment: 'Riesgo y evaluación',
                operations_automation: 'Operaciones y Automatización',
                platform_admin: 'Plataforma y administración',
            },
            package_tier_label: 'Su Paquete',
            package_module_unavailable: 'No incluido en su paquete',
            select_all: 'Seleccionar Todo',
            deselect_all: 'Deseleccionar Todo',
            load_sample_data: 'Cargar datos de ejemplo después de la configuración',
            load_sample_data_description: 'Instale datos de demostración realistas para explorar la plataforma. Puede eliminarse más tarde desde Incorporación > Datos de Ejemplo.',
            language_label: 'Idioma Preferido',
            language_description: 'Elija el idioma predeterminado para su organización.',
            admin_account_title: 'Cuenta de Administrador',
            admin_account_created: 'Cuenta de administrador creada',
            admin_full_name: 'Nombre completo',
            admin_email: 'Dirección de correo electrónico',
            admin_password: 'Contraseña',
            admin_confirm_password: 'Confirmar contraseña',
            admin_show_password: 'Mostrar contraseña',
            admin_hide_password: 'Ocultar contraseña',
            admin_name_placeholder: 'p.ej. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Mínimo 8 caracteres',
            admin_password_confirm_placeholder: 'Volver a introducir la contraseña',
            validation_admin_name_required: 'El nombre del administrador es obligatorio',
            validation_admin_password_required: 'La contraseña es obligatoria',
            validation_admin_password_min_length: 'La contraseña debe tener al menos 8 caracteres',
            validation_passwords_mismatch: 'Las contraseñas no coinciden',
            notification_email_enabled: 'Activar Notificaciones por Correo',
            notification_email_description: 'Reciba notificaciones por correo sobre asignaciones de tareas, incumplimientos de SLA y eventos de cumplimiento.',
            notification_digest_frequency: 'Frecuencia del Resumen',
            notification_digest_daily: 'Diario',
            notification_digest_weekly: 'Semanal',
            notification_digest_none: 'Sin resumen',
            notification_in_app_enabled: 'Activar Notificaciones de Plataforma',
            notification_in_app_description: 'Mostrar insignia y cajón de notificaciones para alertas en tiempo real.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'UE/EEE',
                global: 'Mundial',
            },
            languages: {
                en: 'ingles',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenská',
                nb: 'Bokmål noruego',
                nn: 'Norsk Nynorsk',
                de: 'alemán',
                fi: 'Suomi',
                nl: 'Países Bajos',
                fr: 'francés',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'catalán',
                eu: 'euskera',
                pt: 'portugués',
            },
            industries: {
                financial_services: 'Servicios financieros',
                healthcare: 'Salud',
                technology: 'Tecnología',
                telecommunications: 'Telecomunicaciones',
                government: 'Gobierno',
                education: 'Educación',
                manufacturing: 'Manufactura',
                retail: 'Comercio minorista',
                other: 'Otro',
            },
            placeholders: {
                org_name: 'por ejemplo, ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'usuario1@empresa.vn, usuario2@empresa.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Nombre de la organización',
                jurisdiction: 'Jurisdicción',
                industry: 'Sector',
                org_size: 'Tamaño de la organización',
                language: 'Idioma Preferido',
                admin_email: 'Correo electrónico del administrador',
                team_emails: 'Correos de los miembros del equipo (separados por comas)',
                enable_cmp: 'Habilitar integración CMP',
                cmp_endpoint: 'Punto de acceso de la API CMP',
                email_notifications: 'Notificaciones por Correo',
                in_app_notifications: 'Notificaciones de Plataforma',
                digest_frequency: 'Frecuencia del Resumen',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Visitas guiadas por módulo',
            start: 'Iniciar visita',
            continue_tour: 'Continuar visita',
            replay: 'Repetir visita',
            exit: 'Salir de la visita',
            skip: 'Omitir visita',
            next: 'Siguiente',
            previous: 'Anterior',
            finish: 'Finalizar visita',
            step_of: 'Paso {current} de {total}',
            info: 'Realice visitas guiadas de cada módulo para conocer las funcionalidades y flujos de trabajo principales. Las visitas se pueden repetir en cualquier momento.',
            autoplay: 'Reproducción automática',
            interactive: 'Interactivo',
            play_all: 'Reproducir todo',
            search_tours: 'Buscar recorridos...',
            duration: 'Duración',
            steps_label: '{count} pasos',
            mode: 'Modo',
            pause: 'Pausa',
            resume: 'Currículum',
            speed: 'Velocidad',
            restart: 'Reiniciar',
            reset: 'Reiniciar',
            reset_success: 'El progreso del recorrido se ha reiniciado.',
            category_core_privacy: 'Privacidad principal',
            category_governance: 'Gobernanza',
            category_operations: 'Operaciones',
            category_platform: 'Plataforma',
            category_advanced: 'Avanzado',
            category_data_privacy_rights: 'Privacidad y derechos de datos',
            category_vendor_third_party: 'Gestión de proveedores y terceros',
            category_risk_assessment: 'Riesgo y evaluación',
            category_operations_automation: 'Operaciones y Automatización',
            category_platform_admin: 'Plataforma y administración',
            steps_count: '{count} pasos',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Gestión de datos de ejemplo',
            select_modules: 'Seleccionar módulos',
            current_status: 'Estado actual',
            operation_history: 'Historial de operaciones',
            load: 'Cargar datos de ejemplo',
            clear: 'Eliminar datos de ejemplo',
            processing: 'Procesando...',
            loaded: 'Cargados',
            not_loaded: 'No cargados',
            records: 'Registros de ejemplo',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: '¿Está seguro de que desea eliminar todos los datos de ejemplo de los módulos seleccionados?',
            confirm_clear_title: 'Borrar datos de ejemplo',
            confirm_load_title: 'Cargar datos de ejemplo',
            confirm_load: '¿Está seguro de que desea cargar datos de ejemplo para los módulos seleccionados?',
            action_load: 'Cargar',
            action_clear: 'Borrar',
            info: 'Cargue datos de demostración realistas para formación y demostraciones. Los datos de ejemplo están etiquetados y se pueden eliminar de forma segura sin afectar a los datos reales.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Centro de ayuda',
            search: 'Buscar',
            search_placeholder: 'Buscar artículos de ayuda...',
            clear: 'Limpiar',
            all_categories: 'Todas las categorías',
            all_modules: 'Todos los Módulos',
            back: 'Volver al centro de ayuda',
            callout_important: 'importante',
            callout_tip: 'consejo',
            no_results: 'No se encontraron artículos que coincidan con su búsqueda.',
            no_articles: 'Aún no hay artículos de ayuda disponibles. Los artículos aparecerán aquí a medida que se añada contenido.',
            results_for: '{count} resultado(s) para "{query}"',
            quick_links: {
                getting_started: 'Primeros pasos',
                getting_started_desc: 'Instalación, configuración y guía de inicio rápido.',
                user_guide: 'Guía del usuario',
                user_guide_desc: 'Documentación de uso módulo a módulo.',
                admin_guide: 'Guía del administrador',
                admin_guide_desc: 'Configuración, mantenimiento y resolución de problemas.',
                faq: 'Preguntas frecuentes',
                faq_desc: 'Preguntas y respuestas frecuentes.',
            },
            search_articles_aria: 'Buscar artículos de ayuda',
            article_metadata_aria: 'Metadatos del artículo',
            faq_editor_title: 'Editor de preguntas frecuentes del centro de ayuda',
            faq_editor_description: 'Añada, edite y elimine pares de pregunta y respuesta en cualquier artículo del centro de ayuda. El editor aún no está disponible; esta dirección está reservada para él.',
            faq_editor_empty: 'El editor de preguntas frecuentes aún no está disponible. Mientras tanto, pida a su administrador que añada secciones de preguntas frecuentes a un artículo del centro de ayuda.',
            articles_list_aria: 'Artículos de ayuda',
            category_nav_aria: 'Navegación por categorías',
            quick_links_aria: 'Enlaces rápidos a categorías de ayuda',
            returned_to_list: 'Vuelto a la lista de artículos',
            opening_article: 'Abriendo artículo: {title}',
            read_article_aria: 'Leer artículo: {title}',
            pagination_nav_aria: 'Paginación de artículos',
            error_unavailable: 'El centro de ayuda no está disponible actualmente. Por favor, inténtelo de nuevo más tarde.',
            retry: 'Reintentar',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'p. ej. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Guardar',
        cancel: 'Cancelar',
        system_actor: 'Sistema',
        delete: 'Eliminar',
        edit: 'Editar',
        create: 'Crear',
        update: 'Actualizar',
        loading: 'Cargando...',
        error: 'Se ha producido un error',
        success: 'Correcto',
        logout: 'Cerrar sesión',
        actions: 'Acciones',
        retrying: 'Reintentando…',
        permission_denied: 'No tienes acceso a esta página.',
        status: 'Estado',
        description: 'Descripción',
        name: 'Nombre',
        created_at: 'Fecha de creación',
        updated_at: 'Fecha de actualización',
        yes: 'Sí',
        no: 'No',
        confirm: 'Confirmar',
        confirm_dialog_title: 'Confirm action',
        back: 'Atrás',
        next: 'Siguiente',
        previous: 'Anterior',
        search: 'Buscar',
        filter: 'Filtrar',
        page_x_of_y: 'Página {page} de {total}',
        pagination_label: 'Paginación',
        view_mode_label: 'Modo de vista',
        export: 'Exportar',
        exporting: 'Exportando…',
        sidebar_navigation: 'Navegación lateral de administración',
        items_count: '{count} elementos',
        items_count_one: '{count} elemento',
        export_success: 'Documento generado exitosamente.',
        generate_pdf: 'Generar PDF',
        import: 'Importar',
        download: 'Descargar',
        upload: 'Subir',
        view: 'Ver',
        close: 'Cerrar',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Enviar',
        saved: 'Guardado correctamente',
        saving: 'Guardando...',
        submitting: 'Enviando...',
        created: 'Creado correctamente',
        all: 'Todos',
        none: 'Ninguno',
        select: 'Seleccionar',
        select_user: 'Seleccionar usuario...',
        custom_input: 'Introducir manualmente',
        required: 'Obligatorio',
        optional: 'Opcional',
        notes: 'Notas',
        tags: 'Etiquetas',
        archive: 'Archivar',
        clone: 'Clonar',
        refresh: 'Actualizar',
        retry: 'Reintentar',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Restablecer',
        add: 'Añadir',
        remove: 'Eliminar',
        removing: 'Eliminando…',
        attachment: 'Archivo adjunto',
        upload_file: 'Subir archivo',
        upload_file_required: 'Subir archivo (obligatorio)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Arrastra y suelta o haz clic para examinar',
        upload_max_size: 'Tamaño máximo: {size} MB',
        upload_accepted_types: 'Aceptados: {types}',
        upload_in_progress: 'Subiendo {name}...',
        upload_size_exceeded: 'El tamaño del archivo supera el tamaño máximo permitido de {size} MB.',
        upload_type_not_accepted: 'Tipo de archivo no aceptado. Tipos aceptados: {types}',
        upload_succeeded: 'El archivo "{name}" se subió correctamente.',
        upload_failed: 'No se pudo subir el archivo.',
        select_entity_type_first: 'Seleccione primero un tipo de entidad',
        failed_to_remove_file: 'No se pudo eliminar el archivo.',
        entity_type_label: 'Tipo de entidad',
        entity_label: 'Entidad',
        entity_reference: 'Referencia de entidad',
        failed_to_load_entities: 'No se pudieron cargar las entidades',
        enable: 'Habilitar',
        disable: 'Deshabilitar',
        no_data: 'No se encontraron datos',
        confirm_delete: '¿Está seguro de que desea eliminar este elemento?',
        confirm_action: '¿Está seguro de que desea continuar?',
        save_success: 'Guardado correctamente',
        save_error: 'Error al guardar',
        delete_success: 'Eliminado correctamente',
        delete_error: 'Error al eliminar',
        hour: 'hora',
        hours: 'horas',
        load_error: 'Error al cargar los datos',
        operation_error: 'Operación fallida',
        something_went_wrong: 'Algo ha salido mal',
        go_home: 'Ir al panel de control',
        access_denied: 'Acceso denegado',
        no_permission: 'No tiene permisos para acceder a esta página.',
        no_permission_title: 'Permisos insuficientes',
        no_permission_message: 'No tiene permiso para acceder a esta página.',
        unknown: 'Desconocido',
        back_to_dashboard: 'Volver al panel de control',
        id: 'ID',
        type: 'Tipo',
        date: 'Fecha',
        created_by: 'Creado por',
        updated_by: 'Actualizado por',
        page_of: 'Página {page} de {total}',
        showing_items: 'Mostrando {count} elementos',
        items_per_page: 'Elementos por página',
        no_results: 'Ningún registro coincide con sus filtros',
        no_items: 'No se encontraron elementos',
        sort_by: 'Ordenar por',
        ascending: 'Ascendente',
        descending: 'Descendente',
        all_statuses: 'Todos los estados',
        all_types: 'Todos los tipos',
        all_categories: 'Todas las categorías',
        all_severities: 'Todas las gravedades',
        date_range: 'Rango de fechas',
        from_date: 'Desde',
        to_date: 'Hasta',
        clear_filters: 'Borrar filtros',
        apply_filters: 'Aplicar filtros',
        save_changes: 'Guardar cambios',
        discard_changes: 'Descartar cambios',
        confirm_discard: 'Tiene cambios sin guardar. ¿Desea descartarlos?',
        copy: 'Copiar',
        duplicate: 'Duplicar',
        move: 'Mover',
        assign: 'Asignar',
        unassign: 'Desasignar',
        approve: 'Aprobar',
        reject: 'Rechazar',
        deleting: 'Eliminando...',
        processing: 'Procesando...',
        loading_page: 'Cargando página...',
        operation_success: 'Operación completada correctamente',
        operation_failed: 'La operación ha fallado. Inténtelo de nuevo.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Error de red. Compruebe su conexión.',
        session_expired: 'La sesión ha caducado. Inicie sesión de nuevo.',
        test: 'Probar',
        pause: 'Pausar',
        resume: 'Reanudar',
        activate: 'Activar',
        last_sync: 'Última sincronización',
        health: 'Estado de salud',
        email: 'Correo electrónico',
        role: 'Rol',
        title: 'Título',
        owner: 'Responsable',
        priority: 'Prioridad',
        category: 'Categoría',
        details: 'Detalles',
        overview: 'Resumen',
        configuration: 'Configuración',
        history: 'Historial',
        new_item: 'Nuevo',
        edit_item: 'Editar',
        select_option: 'Seleccionar...',
        total: 'totales',
        active: 'Activo',
        inactive: 'Inactivo',
        enabled: 'Habilitado',
        disabled: 'Deshabilitado',
        last_login: 'Último inicio de sesión',
        never: 'Nunca',
        password_changed: 'Contraseña cambiada. Se cerrará su sesión.',
        password_change_failed: 'Error al cambiar la contraseña',
        passwords_mismatch: 'Las contraseñas no coinciden',
        install: 'Instalar',
        install_named: 'Instalar {name}',
        uninstall: 'Desinstalar',
        installed: 'Instalado',
        available: 'Disponible',
        catalog: 'Catálogo',
        published: 'Publicado',
        version: 'Versión',
        market: 'Mercado',
        language: 'Idioma',
        framework: 'Marco normativo',
        template: 'Plantilla',
        connector_type: 'Tipo de conector',
        polling_interval: 'Intervalo de consulta (minutos)',
        new_instance: 'Nueva instancia',
        change_password: 'Cambiar contraseña',
        current_password: 'Contraseña actual',
        new_password: 'Nueva contraseña',
        confirm_password: 'Confirmar contraseña',
        comments: 'Comentarios',
        completed: 'Completado',
        contact: 'Contacto',
        hide: 'Ocultar',
        hide_details: 'Ocultar Detalles',
        label: 'Etiqueta',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Mostrando',
        page: 'Página',
        preview: 'Vista previa',
        print: 'Imprimir',
        show: 'Mostrar',
        status_active: 'Activo',
        status_completed: 'Completado',
        status_draft: 'Borrador',
        status_in_progress: 'En curso',
        status_pending: 'Pendiente',
        view_details: 'Ver detalles',
        all_priorities: 'Todas las prioridades',
        pipeline: 'Canalización',
        all_requests: 'Todos Solicitudes',
        schedule: 'Programar',
        deploy: 'Desplegar',
        rollback: 'Revertir',
        advance_stage: 'Avanzar etapa',
        submit_for_approval: 'Enviar para Approval',
        approval_log: 'Registro de aprobación',
        approver: 'Aprobador',
        decision: 'Decisión',
        decided_at: 'Decidido el',
        rollout_stages: 'Etapas de despliegue',
        approval_comment: 'Comentario de aprobación',
        optional_comment: 'Comentario opcional...',
        impact_summary: 'Impacto Resumen',
        target_property_ids: 'IDs de propiedades de destino (separados por comas)',
        policy_id: 'Política ID',
        target_version: 'Versión de destino',
        target_version_placeholder: 'p. ej. 2.0.0',
        scheduled_label: 'Programado',
        scheduled_start: 'Programado Inicio',
        scheduled_end: 'Programado Fin',
        properties_count: '{count} propiedades',
        results: 'Resultados',
        comparison_results: 'Resultados de comparación',
        past_comparison_reports: 'Informes de comparación anteriores',
        scanning: 'Escaneando...',
        run_inconsistency_scan: 'Ejecutar Inconsistency Escanear',
        property_ids_label: 'IDs de propiedad (separados por comas, mín 2)',
        comparison_type: 'Tipo de comparación',
        golden_template_id: 'ID de plantilla dorada (opcional)',
        leave_blank_compare: 'Deje en blanco para comparar propiedades entre sí',
        add_notes_placeholder: 'Agregar your analysis notes here...',
        covered_of_active: '{covered} de {active} propiedades activas cubiertas',
        fetch_error: 'Error al fetch data. Por favor, inténtelo de nuevo.',
        run_comparison_error: 'Error al run comparison. Por favor, inténtelo de nuevo.',
        save_notes_error: 'Error al save notes. Por favor, inténtelo de nuevo.',
        scan_complete: 'Inconsistency scan completado con éxito',
        scan_error: 'Error al run inconsistency scan. Por favor, inténtelo de nuevo.',
        min_two_properties: 'Proporcione al menos 2 IDs de propiedad para la comparación.',
        comma_separated: 'separados por comas',
        language_english: 'Inglés',
        language_vietnamese: 'Vietnamita',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Autor',
        dependencies: 'Dependencias',
        checksum: 'Suma de verificación',
        changelog: 'Registro de cambios',
        tier: 'Nivel',
        phone: 'Teléfono',
        launch: 'Lanzar',
        complete: 'Completar',
        back_to_list: 'Volver a Lista',
        clear_selection: 'Borrar selección',
        deactivate: 'Desactivar',
        scope: 'Alcance',
        deadline: 'Fecha límite',
        progress: 'Progreso',
        frequency: 'Frecuencia',
        reviewer: 'Revisor',
        user: 'Usuario',
        campaign: 'Campaña',
        items: 'Elementos',
        next_run: 'Siguiente Ejecutar',
        last_run: 'Último Ejecutar',
        overdue: 'VENCIDO',
        creating: 'Creando...',
        launching: 'Lanzando...',
        loaded: 'loaded',
        page_size: 'Tamaño de página',
        per_page: '/ página',
        urgent: 'Urgente',
        pagination: 'Paginación',
        metric: 'Métrica',
        value: 'Valor',
        country: 'País',
        showing_chart_view: 'Mostrando vista de gráfico',
        showing_table_view: 'Mostrando vista de tabla de datos',
        policies: 'Políticas',
        compliant: 'Cumple',
        non_compliant_status: 'No conforme',
        all_data: 'Todos los datos',
        sr_urgent: 'Urgente',
        n_per_page: '{n} / página',
        select_user_placeholder: '-- Seleccionar usuario --',
        field_required: 'Este campo es obligatorio',
        results_found: 'resultados encontrados',
        select_placeholder: '-- Seleccionar --',
        unknown_user: 'Usuario desconocido',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Exportar CSV',
        not_applicable: 'N/A',
        aria_overdue_since: 'Vencido desde {date}',
        placeholders: {
            enter_justification: 'Introduzca justification',
            justification: 'Justificación',
            campaign_name_example: 'Q1 2026 Acceso Revisar',
            optional_description: 'Descripción opcional',
            enter_user_id: 'Introduzca user ID of the reviewer',
            enter_user_id_short: 'Introduzca user ID',
            schedule_name_example: 'Trimestral Acceso Attestation',
            enter_property_id: 'Introduzca property ID',
            enter_policy_id: 'Introduzca política id',
            property_or_group_id: 'ID de propiedad o grupo',
            describe_template_purpose: 'Describa el propósito de la plantilla...',
            step_title: 'Título del paso...',
            detailed_instructions: 'Instrucciones detalladas...',
            filter_by_taxonomy_id: 'Filtrar por Taxonomía ID...',
            describe_classification_rule: 'Describa la regla de clasificación...',
            describe_dedup_rule: 'Describa la regla de deduplicación...',
            describe_import_template: 'Describa la plantilla de importación...',
            reason_for_rejection: 'Motivo del rechazo...',
            jurisdiction_codes_example: 'VN, SG, UE',
            filter_by_locale: 'Filtrar por idioma...',
            filter_by_event_type: 'Filtrar por tipo de evento',
            filter_by_actor_id: 'Filtrar por ID de actor',
            filter_by_market_code: 'Filtrar por código de mercado...',
            filter_by_framework_id: 'Filtrar por marco ID...',
            add_comment: 'Agregar comment...',
            provide_rejection_reason: 'Proporcione un motivo para rechazar esta presentación...',
            filter_by_jurisdiction: 'Filtrar por código de jurisdicción...',
            enter_report_title: 'Introduzca report title',
            enter_name: 'Introduzca name',
            enter_description: 'Introduzca description',
            enter_org_id: 'Introduzca Org ID to add',
            describe_evidence_gap: 'Describa la brecha de evidencia...',
            expected_evidence: 'Qué evidencia se espera',
            entity_identifier: 'Identificador de entidad',
            remediation_steps: 'Pasos para remediar esta brecha',
            user_id_or_name: 'ID de usuario o nombre',
            reporting_period_id: 'ID del período de reporte',
            maturity_model_id: 'ID del modelo de madurez',
            describe_maturity_model: 'Describa este modelo de madurez...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Proporcione un motivo para denegar esta solicitud',
            describe_emergency: 'Describa la situación de emergencia que requiere acceso elevado (mínimo 20 caracteres)',
            enter_delegate_id: 'Introduzca user ID of the delegate',
            provide_delegation_reason: 'Proporcione un motivo para esta delegación',
            client_identifier: 'Identificador de cliente',
            client_secret: 'Secreto del cliente',
            time_format: 'HH:MM',
            describe_sod_rule: 'Describa el propósito de esta regla SoD...',
            enter_role_id: 'Introduzca role ID',
            explain_exception: 'Explain por qué este excepción es needed...',
            describe_compensating_controls: 'Describa los controles compensatorios existentes...',
            full_name: 'Nombre completo',
            min_chars: 'Mín 12 caracteres',
            enter_override_justification: 'Introduzca justification for overriding this gate evaluation',
            enter_evidence_id: 'Ingrese el ID de la evidencia para vincular:',
            date_format: 'DD/MM/AAAA',
            describe_pack_contents: 'Describa el contenido y propósito del paquete',
            select_reviewers: 'Seleccionar revisores...',
        },
        aria_previous_page: 'Pagina anterior',
        aria_next_page: 'Página siguiente',
        aria_results_per_page: 'Resultados por página',
        per_page_25: '25 / página',
        per_page_50: '50 / página',
        per_page_100: '100 / página',
        select_language: 'Seleccionar idioma',
        today: 'Hoy',
        tomorrow: 'Mañana',
        dashboard_load_error: 'No se pudo cargar el panel. Por favor, inténtelo de nuevo.',
        enum_grade: {
            red: 'Rojo',
            amber: 'Ámbar',
            yellow: 'Amarillo',
            green: 'Verde',
        },
        enum_severity: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
        },
        enum_priority: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
        },
        enum_status: {
            pending: 'Pendiente',
            in_progress: 'En progreso',
            completed: 'Completado',
            blocked: 'Bloqueado',
            escalated: 'Escalado',
            cancelled: 'Cancelado',
            overdue: 'Atrasado',
            not_started: 'No iniciado',
            new: 'Nuevo',
            open: 'Abierto',
            closed: 'Cerrado',
            acknowledged: 'Reconocido',
            resolved: 'Resuelto',
        },
        enum_department: {
            unassigned: 'No asignado',
        },
        enum_form_category: {
            assessment: 'Evaluación',
            assessment_report: 'Informe de evaluación',
            dpia: 'DPIA',
            authority_submission: 'Presentación de autoridad',
            cross_border_transfer: 'Transferencia transfronteriza',
            vendor: 'vendedor',
            breach: 'incumplimiento',
            consent: 'Consentimiento',
            governance: 'Gobernanza',
            register: 'Registrarse',
            monitoring: 'Monitoreo',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Privacidad',
            incident: 'Incidente',
            org: 'Organización',
            sector: 'Sector',
            classification: 'Clasificación',
            discovery: 'Descubrimiento',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Solicitudes de derechos',
            assessments: 'Evaluaciones',
            incidents: 'Incidentes',
            incident_ops: 'Operaciones de incidentes',
            monitoring: 'Monitoreo',
            vendor_risk: 'Riesgo del proveedor',
            governance: 'Gobernanza',
            deletion_orchestration: 'Eliminación',
            access_review: 'Revisión de acceso',
            consent: 'Consentimiento',
            consent_properties: 'Propiedades de consentimiento',
            forms: 'Formularios',
            localization: 'Localización',
            program_governance: 'Gobernanza del programa',
            privacy_scanner: 'Escáner de privacidad',
            classification: 'Clasificación',
            discovery: 'Descubrimiento',
            manual: 'manuales',
            data_mapping: 'Mapeo de datos',
        },
        enum_risk_level: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
        },
        enum_request_type: {
            all: 'All',
            access: 'Acceso (DSAR)',
            deletion: 'Eliminación',
            correction: 'Corrección',
            objection: 'Objeción',
            portability: 'Portabilidad',
            restriction: 'Restricción',
            consent_withdrawal: 'Retiro del consentimiento',
            protective_measures: 'Medidas de protección',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'ingles',
            vi: 'vietnamita',
            all: 'ambos',
        },
        enum_event_type: {
            case_created: 'Caso creado',
            status_changed: 'Estado cambiado',
            case_assigned: 'Caso asignado',
            note_added: 'Nota agregada',
            attachment_added: 'Adjunto agregado',
            attachment_deleted: 'Adjunto eliminado',
            reference_added: 'Referencia agregada',
            checklist_updated: 'Lista de verificación actualizada',
            identity_verified: 'Identidad verificada',
            closure_summary_added: 'Resumen de cierre',
            approval_granted: 'Aprobación concedida',
            template_applied: 'Plantilla aplicada',
            export_generated: 'Exportación generada',
            sla_warning: 'SLA Advertencia',
            sla_overdue: 'SLA Vencido',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Error en la exportación',
        you: 'You',
        export_failed: 'La exportación falló. Por favor, inténtelo de nuevo.',
        filters: 'Filtros',
        failed: 'Fallido',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Eliminado',
        draft: 'Borrador',
        active: 'Activo',
        inactive: 'Inactivo',
        pending: 'Pendiente',
        in_progress: 'En curso',
        completed: 'Completado',
        archived: 'Archivado',
        published: 'Publicado',
        approved: 'Aprobado',
        rejected: 'Rechazado',
        cancelled: 'Cancelado',
        blocked: 'Bloqueado',
        deferred: 'Aplazado',
        not_started: 'No iniciado',
        dismissed: 'Descartado',
        failed: 'Fallido',
        in_review: 'En Revisar',
        deprecated: 'Obsoleto',
        scheduled: 'Programado',
        deploying: 'Desplegando',
        deployed: 'Desplegado',
        paused: 'Pausado',
        rolled_back: 'Revertido',
        received: 'Recibido',
        processing: 'Procesamiento',
        under_assessment: 'En evaluación',
        implemented: 'Implementado',
        pending_approval: 'Pendiente de aprobación',
        pending_deletion: 'Pendiente Eliminación',
        on_hold: 'En espera',
        deleted: 'Eliminado',
        expired: 'Caducado',
        outdated: 'Desactualizado',
        drifted: 'Desviado',
        decommissioned: 'Desmantelado',
        match: 'Coincidencia',
        different: 'Diferente',
        missing: 'Faltante',
        confirmed: 'Confirmado',
        revoked: 'Revocado',
        escalated: 'Escalado',
        system: 'Sistema',
        custom: 'Personalizado',
        warning: 'Advertencia',
        blocking: 'Bloqueante',
        analyst: 'Analista',
        viewer: 'Visor',
        daily: 'Diario',
        weekly: 'Semanal',
        policies: 'Políticas',
        denied: 'Denegado',
        verified: 'Verificado',
        detected: 'Detectado',
        ignored: 'ignorado',
        open: 'Abierto',
        resolved: 'Resuelto',
        suspended: 'suspendido',
        retired: 'jubilado',
        review: 'Revisión',
        generated: 'generado',
        exported: 'Exportado',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Bajo',
        medium: 'Medio',
        high: 'Alto',
        critical: 'Crítico',
    },
    sensitivity: {
        standard: 'Estándar',
        sensitive: 'Sensible',
        special_category: 'Categoría especial',
        badge_sensitive: 'Sensible',
    },
    change_types: {
        policy_update: 'Política Actualizar',
        new_binding: 'Nuevo Binding',
        remove_binding: 'Eliminar vinculación',
        configuration_change: 'Cambio de configuración',
        banner_update: 'Banner Actualizar',
    },
    comparison_types: {
        full: 'Completo',
        banner_only: 'Banner Solo',
        purposes_only: 'Finalidades Solo',
        legal_bases_only: 'Solo bases legales',
        consent_settings: 'Consentimiento Configuración',
    },
    lifecycle_stages: {
        active: 'Activo',
        archived: 'Archivado',
        pending_deletion: 'Pendiente Eliminación',
        deleted: 'Eliminado',
        on_hold: 'En espera',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Datos personales',
        consent_record: 'Consentimiento Registro',
        user_account: 'Usuario Cuenta',
        audit_log: 'Auditoría Registro',
        document: 'Documento',
        backup: 'Copia de seguridad',
    },
    risk_categories: {
        data_breach: 'Datos Brecha',
        unauthorized_access: 'Acceso no autorizado',
        data_loss: 'Pérdida de datos',
        regulatory_non_compliance: 'Incumplimiento regulatorio',
        third_party_risk: 'Third-Party Riesgo',
        operational: 'Operacional',
        technical: 'Técnico',
        legal: 'Legales',
    },
    mitigation_types: {
        technical: 'Técnico',
        organizational: 'Organizativo',
        contractual: 'contractual',
        procedural: 'Procedimental',
    },
    safeguard_types: {
        encryption_in_transit: 'Cifrado en tránsito',
        encryption_at_rest: 'Cifrado en reposo',
        access_controls: 'Controles de acceso',
        contractual_clauses: 'Cláusulas contractuales',
        data_minimization: 'Minimización de datos',
        pseudonymization: 'Seudonimización',
        audit_rights: 'Derechos de auditoría',
        breach_notification: 'Notificación de infracción',
        data_localization: 'Localización de datos',
        other: 'Otro',
    },
    scoring_methods: {
        weighted_average: 'Promedio ponderado',
        simple_average: 'Promedio simple',
        maximum: 'Máximo',
        manual: 'manuales',
    },
    dpa_status: {
        not_required: 'No requerido',
        required: 'Requerido',
        in_progress: 'En curso',
        completed: 'Completado',
        signed: 'Firmado',
    },
    disposal_methods: {
        anonymize: 'Anonimizar',
        pseudonymize: 'Seudonimizar',
        aggregate: 'Agregar',
        delete: 'Eliminar',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Panel de control',
        no_data: 'No hay datos disponibles en el panel.',
        no_data_heading: 'Sin datos en tiempo real aún',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Cobertura de consentimiento',
        ctia_filings: 'Presentaciones CTIA',
        next_dpia_review: 'Próxima revisión DPIA',
        next_review_none: 'Ninguna programada',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Presentaciones MPS de incidentes',
        compliance_score: 'Puntuación de cumplimiento',
        grade: 'Calificación',
        no_score: 'Aún no se ha calculado la puntuación de cumplimiento.',
        rights_requests: 'Solicitudes de derechos',
        open: 'Abiertas',
        sla_breached: 'SLA incumplido',
        sla_approaching: 'SLA próximo a vencer',
        closed_month: 'Cerradas (mes)',
        incidents: 'Incidentes y brechas',
        active: 'Activos',
        breaches: 'Brechas',
        overdue_tasks: 'Tareas vencidas',
        breach_72h_notice: 'Se aplica un plazo de notificación reglamentaria de 72 horas a las infracciones activas',
        recommendations: 'Recomendaciones',
        take_action: 'Tomar acción',
        drift_events: 'Desviaciones y aprobaciones',
        open_events: 'Eventos abiertos',
        pending_approvals: 'Aprobaciones pendientes',
        monitoring: 'Tareas de supervisión',
        programs: 'Programas',
        overdue: 'Vencidas',
        pending: 'Pendientes',
        ric_cases: 'Casos RIC',
        coverage: 'Cobertura de datos',
        systems: 'Sistemas',
        activities: 'Actividades',
        vendors: 'Proveedores',
        dpias: 'EIPD',
        open_gaps: 'deficiencias abiertas',
        view_as_table: 'Ver como tabla',
        view_as_chart: 'Ver como grafico',
        table_metric_header: 'Metrica',
        table_value_header: 'Valor',
        announce_chart_view: 'Mostrando vista de grafico',
        announce_table_view: 'Mostrando vista de tabla',
        section_core_metrics: 'Metricas principales',
        section_task_management: 'Gestion de tareas',
        section_compliance_insights: 'Perspectivas de cumplimiento',
        configure_dashboard: 'Configurar panel',
        config_col_active: 'Activo',
        config_col_widget: 'widget',
        collapse_section: 'Contraer',
        expand_section: 'Expandir',
        reset_to_defaults: 'Restablecer valores predeterminados',
        preferences_saved: 'Preferencias del panel guardadas',
        preferences_reset: 'Panel restablecido a valores predeterminados del rol',
        preferences_load_error: 'Error al cargar las preferencias del panel',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Puntuación media del sistema',
        factor_avg_vendor_score: 'Puntuación media del proveedor',
        factor_processing_coverage: 'Cobertura de la actividad de procesamiento',
        factor_ropa_completeness: 'Completitud de RoPA',
        factor_drift_penalty: 'Penalización por eventos de deriva',
        rec_improve_system_gov: 'Mejorar la documentación de gobernanza del sistema',
        rec_improve_vendor_gov: 'Mejorar la documentación de gobernanza de proveedores',
        rec_document_processing: 'Actividades de procesamiento de documentos para sistemas descubiertos.',
        rec_generate_ropa: 'Genere registros RoPA para actividades de procesamiento activo',
        rec_resolve_drift: 'Resolver eventos abiertos de deriva críticos y de alta gravedad',
        score_good: 'Bueno',
        score_fair: 'Aceptable',
        score_poor: 'Deficiente',
        score_critical: 'Crítico',
        drag_to_reorder: 'Arrastrar para reordenar',
        widget_moved: 'Widget movido',
        press_space_to_grab: 'Pulse Espacio para agarrar, teclas de flecha para mover, Espacio para soltar',
        reorder_instructions: 'Use arrastrar y soltar o atajos de teclado para reordenar los widgets',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Resumen de tareas',
            pending: 'Pendiente',
            in_progress: 'En progreso',
            overdue: 'Vencido',
            done_7d: 'Hecho (7d)',
            by_priority: 'Por prioridad',
            view_all_tasks: 'Ver todas las tareas',
            upcoming_tasks: 'Próximas tareas (próximos 7 días)',
            no_upcoming: 'No hay tareas pendientes en los próximos 7 días.',
            view_calendar: 'Ver calendario',
            overdue_alerts: 'Vencido',
            no_overdue: 'Sin tareas atrasadas. Todo va por buen camino.',
            assigned_to: 'Asignado a',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Ver todo vencido',
            notifications: 'Notificaciones recientes',
            unread: 'unread',
            no_notifications: 'Sin notificaciones. ¡Estáis todos atrapados!',
            view_all_notifications: 'Ver todas las notificaciones',
            load_error: 'Error al cargar los datos.',
            day_late: 'día tarde',
            days_late_plural: 'días de retraso',
            due_today: 'Vencimiento hoy',
            just_now: 'justo ahora',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 're',
            cross_org_stats: 'Estadísticas entre organizaciones',
            by_organization: 'Por organización',
            overdue_across_orgs: 'Tareas atrasadas en todas las organizaciones.',
            overdue_count_label: '{count} overdue tasks',
            total: 'totales',
            tasks: 'tasks',
            organization: 'Organización',
            current_org: 'Organización actual',
            all_orgs: 'Todas las organizaciones',
            all_organizations: 'Todas las organizaciones',
            filter_by_org: 'Filtrar por organización',
            cross_org: 'Entre organizaciones',
            empty_calendar: 'No hay tareas para este período. Crea tu primera tarea o revisa tus filtros.',
            empty_my_tasks: 'No tienes tareas asignadas. ¡Gran trabajo!',
            empty_overdue: 'Sin tareas atrasadas. Todo va por buen camino.',
            empty_notifications: 'Sin notificaciones. ¡Estáis todos atrapados!',
            empty_workflows: 'No hay flujos de trabajo configurados. Explore las plantillas para comenzar.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mis Tareas',
            no_tasks: 'No hay tareas asignadas a usted.',
            load_error: 'No se pudieron cargar las tareas.',
            overdue: 'vencida',
            remaining: 'restante',
            view_all: 'Ver Todas las Tareas',
        },
        my_section_status_widget: {
            title: 'Estado de Mis Secciones',
            overall_completion: 'General',
            sections_count: 'Secciones',
            no_sections: 'No hay secciones asignadas a su departamento.',
            load_error: 'No se pudo cargar el estado de las secciones.',
            department_tasks_title: 'Tareas del departamento',
            no_department_tasks: 'Su departamento no tiene tareas.',
            department_tasks_error: 'No se pudieron cargar las tareas del departamento.',
            no_department: 'No está asignado a ningún departamento.',
        },
        revision_requests_widget: {
            title: 'Solicitudes de Revisión',
            no_revisions: 'No hay solicitudes de revisión. Todas las secciones van por buen camino.',
            load_error: 'No se pudieron cargar las solicitudes de revisión.',
        },
        consent_summary: {
            title: 'Consentimiento',
            active: 'Activo',
            withdrawn_30d: 'Retirado (30d)',
            expiring_7d: 'Venciendo (7d)',
        },
        transfer_assessments: {
            title: 'Evaluaciones de transferencia',
            open: 'Abierto',
            approved: 'Aprobado',
            gaps_flagged: 'Brechas señaladas',
        },
        breach_notifications: {
            title: 'Notificaciones de infracción',
            deadline_72h: 'plazo de 72h',
            submitted: 'Enviado',
            pending: 'Pendiente',
        },
        authority_requests: {
            title: 'Solicitudes de autoridad',
            pending: 'Pendiente',
            overdue: 'Vencido',
            responded_30d: 'Respondido (30d)',
        },
        data_subjects_widget: {
            title: 'Sujetos de datos',
            total: 'totales',
            high_risk: 'Alto riesgo',
            active_30d: 'Activo (30d)',
        },
        audit_activity_summary: {
            title: 'Actividad de auditoría',
            events_today: 'Eventos hoy',
            events_7d: 'Eventos (7d)',
            unique_actors_30d: 'Actores únicos (30d)',
        },
        onboarding_progress: {
            title: 'Progreso de incorporación',
            completion_pct: 'completo',
            status_label: 'Estado',
            current_step: 'Paso actual',
            status: {
                in_progress: 'En progreso',
                completed: 'Completado',
                skipped: 'Saltado',
                not_started: 'No iniciado',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sector Overlay Reports Due [ES]',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrar por marco normativo',
            all_frameworks: 'Todos los marcos',
            filing_lifecycle: 'Ciclo de vida de presentaciones',
            filing_pipeline: 'Pipeline de presentaciones',
            draft: 'Borrador',
            internal_review: 'Revisión interna',
            approved: 'Aprobado',
            submitted: 'Enviado',
            supplement_requested: 'Suplemento solicitado',
            resubmitted: 'Reenviado',
            accepted: 'Aceptado',
            rejected: 'Rechazado',
            closed: 'Cerrado',
            deadline: 'Fecha límite',
            days_remaining: 'días restantes',
            days_overdue: 'días de retraso',
            no_filings: 'No hay presentaciones activas.',
            department_consolidation: 'Consolidación de departamentos',
            departments_completed: 'Departamentos completados',
            departments_pending: 'Departamentos pendientes',
            departments_overdue: 'Departamentos con retraso',
            no_consolidation_cases: 'No hay casos multidepartamentales en curso.',
            vendor_risk: 'Riesgo de proveedores y terceros',
            pending_assessment: 'Evaluación pendiente',
            overdue_questionnaires: 'Cuestionarios atrasados',
            dpa_review_queue: 'Cola de revisión DPA',
            risk_distribution: 'Distribución de riesgos',
            total_vendors: 'Total de proveedores',
            by_risk_level: 'Por nivel de riesgo',
            no_vendor_data: 'No hay datos de riesgo de proveedores disponibles.',
            discovery_summary: 'Descubrimiento de datos',
            discovery_classification: 'Descubrimiento y clasificación de datos',
            active_scans: 'Escaneos activos',
            labels_pending: 'Etiquetas pendientes de revisión',
            exceptions_unresolved: 'Excepciones no resueltas',
            pending_review: 'Revisión pendiente',
            exceptions: 'Excepciones',
            approved_labels: 'Etiquetas aprobadas',
            total_classified: 'Total clasificado',
            no_discovery_data: 'No hay datos de descubrimiento disponibles.',
            ai_governance: 'Gobernanza de sistemas de IA',
            systems_inventory: 'Sistemas en inventario',
            systems_in_inventory: 'Sistemas en inventario',
            classification_breakdown: 'Desglose de clasificación',
            dossier_completion: 'Compleción del expediente',
            by_classification: 'Por clasificación',
            labeling_status: 'Estado de etiquetado',
            not_started: 'No iniciado',
            no_ai_data: 'No hay datos de gobernanza de IA disponibles.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Respuesta a brechas e incidentes',
            active_breaches: 'Brechas activas',
            notification_status: 'Estado de notificación',
            remediation_progress: 'Progreso de remediación',
            supplement_loops: 'Bucles de suplemento',
            no_breach_data: 'No hay casos de brechas activos.',
            audit_readiness: 'Preparación para auditorías y evaluaciones',
            assessments: 'Evaluaciones',
            gap_findings: 'Hallazgos de brechas',
            evidence_completion: 'Compleción de evidencias',
            export_ready: 'Listo para exportar',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'No listo',
            no_audit_data: 'No hay datos de preparación de auditoría disponibles.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Ciberseguridad 2018',
            framework_label_vn_ecommerce: 'VN Comercio electronico 2023',
            framework_label_vn_telecom: 'VN Telecomunicaciones 2023',
            framework_label_vn_ai: 'VN Ley de IA 2025',
            framework_label_gdpr: 'RGPD',
            framework_label_unspecified: 'No especificado',
            my_department: 'Mi departamento',
            my_tasks: 'Mis tareas',
            section_status: 'Estado de la sección',
            revision_requests: 'Solicitudes de revisión',
            completion: 'Compleción',
            reason: 'Motivo',
            requested_at: 'Solicitado el',
            no_tasks_assigned: 'No hay tareas asignadas.',
            no_sections: 'No hay secciones asignadas a su departamento.',
            no_revisions: 'No hay solicitudes de revisión.',
            load_error: 'No se pudieron cargar los datos.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instancias',
            credentials: 'Almacén de credenciales',
            event_bus: 'Bus de eventos',
            definitions: 'Definiciones',
            brds: 'Captura de BRD',
            security_reviews: 'Revisiones de seguridad',
            poc_scorecards: 'Tarjetas de puntuación POC',
            automation_planning: 'Planificación automatización',
            system_inventory: 'Inventario de sistemas',
            traceability_matrix: 'Matriz de trazabilidad',
            gap_register: 'Registro de brechas',
        },
        instances: {
            title: 'Instancias de conectores',
            page_title: 'Conectores',
            add: 'Añadir instancia',
            test: 'Probar conexión',
            sync_now: 'Sincronizar ahora',
            new_instance: 'Nuevo Connector Instance',
            no_instances: 'No se encontraron connector instances',
            instance_count: '{count} instancia(s)',
            confirm_delete: '¿Eliminar la instancia del conector "{name}"?',
            all_connector_types: 'Todos Conector Types',
            select_connector: 'Seleccione connector...',
            fetch_error: 'Error al fetch connector instances. Por favor, inténtelo de nuevo.',
            fetch_definitions_error: 'Error al fetch connector definitions. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create connector instance. Por favor, inténtelo de nuevo.',
            test_error: 'Error al test connector. Por favor, inténtelo de nuevo.',
            status_error: 'Error al actualizar el estado del conector. Por favor, inténtelo de nuevo.',
            delete_error: 'Error al delete connector instance. Por favor, inténtelo de nuevo.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Estado actualizado',
            reactivate: 'Reactivar',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Almacén de credenciales',
            page_title: 'Credenciales del conector',
            add: 'Añadir credencial',
            rotate: 'Rotar',
            expired: 'Caducada',
            expiring: 'Próxima a caducar',
            orphaned: 'Huérfana',
            new_credential: 'Nuevo Credential',
            no_credentials: 'No se encontraron credentials',
            no_expiry: 'Sin caducidad',
            linked_connectors: 'Conectores vinculados',
            expires_at: 'Caduca En',
            value_label: 'Valor * (solo escritura)',
            confirm_delete_title: 'Confirmar eliminación',
            confirm_delete_message: '¿Eliminar esta credencial? Los conectores vinculados perderán el acceso.',
            rotate_title: 'Rotar credencial',
            new_credential_value: 'Nuevo valor de credencial',
            fetch_error: 'Error al load credentials. Por favor, inténtelo de nuevo.',
            health_error: 'Error al load health. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create credential. Por favor, inténtelo de nuevo.',
            rotate_error: 'Error al rotate credential. Por favor, inténtelo de nuevo.',
            delete_error: 'Error al delete credential. Por favor, inténtelo de nuevo.',
        },
        event_bus: {
            title: 'Bus de eventos',
            page_title: 'Bus de eventos',
            events: 'Eventos',
            subscriptions: 'Suscripciones',
            dlq: 'Cola de mensajes fallidos',
            publish: 'Publicar evento',
            retry: 'Reintentar',
            dismiss: 'Descartar',
            search_events: 'Buscar eventos...',
            filter_event_type: 'Filtrar por tipo de evento...',
            event_type: 'Evento Tipo',
            source_module: 'Módulo fuente',
            timestamp: 'Marca de tiempo',
            delivery_status: 'Estado de entrega',
            correlation_id: 'ID de correlación',
            tracking_id: 'ID de seguimiento',
            delivery_status_hint: 'El estado de entrega se actualiza automáticamente a medida que se procesan los eventos.',
            no_events: 'No se encontraron events',
            total_with_count: '{count} en total',
            prev: 'Ant.',
            new_subscription: '+ Nuevo Suscripción',
            subscriber_type: 'Tipo de suscriptor',
            subscriber_id: 'ID del suscriptor',
            event_type_pattern: 'Evento Tipo Pattern',
            event_type_pattern_placeholder: 'p. ej. incident.*',
            filter_expression: 'Expresión de filtro',
            filter_expression_placeholder: 'Filtro opcional',
            subscriber_type_hint: 'Elija cómo se entregarán los eventos: webhook envía solicitudes HTTP, correo electrónico envía notificaciones, cola enruta a una cola de mensajes.',
            subscriber_id_hint: 'Introduzca el destino de los eventos. Para webhooks, use una URL. Para correo, use una dirección. Para colas, use el nombre de la cola.',
            event_type_pattern_hint: 'Coincida eventos por tipo usando comodines. Use * para coincidir con cualquier segmento (ej. incident.* coincide con incident.created, incident.resolved).',
            filter_expression_hint: 'Opcional. Filtre eventos adicionales por contenido (ej. severity=high).',
            subscriber_id_placeholder_webhook: 'ej. https://su-app.com/webhook',
            subscriber_id_placeholder_email: 'ej. alertas@su-empresa.com',
            subscriber_id_placeholder_queue: 'ej. cola-eventos',
            event_pattern: 'Patrón de evento',
            filter: 'Filtrar',
            enabled: 'Habilitado',
            delivery_stats: 'Estadísticas de entrega',
            no_subscriptions: 'No se encontraron subscriptions',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} entregados / {failed} fallidos',
            confirm_delete_subscription: 'Eliminar este subscription?',
            unresolved: 'Sin resolver',
            resolved: 'Resuelto',
            event_id: 'Evento ID',
            subscription_id: 'Suscripción ID',
            error_code: 'Código de error',
            error_message: 'Mensaje de error',
            attempts: 'Intentos',
            last_failed: 'Último Falló',
            no_dlq_entries: 'No se encontraron dead letter entries',
            dlq_count: '{count} entrada(s) en total',
            fetch_events_error: 'Error al fetch events. Por favor, inténtelo de nuevo.',
            fetch_subscriptions_error: 'Error al fetch subscriptions. Por favor, inténtelo de nuevo.',
            fetch_dlq_error: 'Error al fetch dlq entries. Por favor, inténtelo de nuevo.',
            create_subscription_error: 'Error al create subscription. Por favor, inténtelo de nuevo.',
            toggle_subscription_error: 'Error al toggle subscription. Por favor, inténtelo de nuevo.',
            delete_subscription_error: 'Error al delete subscription. Por favor, inténtelo de nuevo.',
            retry_error: 'Reintentar falló',
            dismiss_error: 'Descarte fallido',
            event_published: 'Evento publicado',
            publish_event_error: 'No se pudo publicar el evento. Inténtelo de nuevo.',
            priority_label: 'Prioridad',
            priority_low: 'Baja',
            priority_normal: 'normales',
            priority_high: 'Alta',
            payload_helper_text: 'Introduzca los datos del evento en formato JSON. Ejemplo: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Suscripción creada',
            subscription_toggled: 'Suscripción cambiada',
            subscription_deleted: 'Suscripción eliminada',
            dlq_retried: 'Mensaje DLQ reintentado',
            dlq_dismissed: 'Mensaje DLQ descartado',
        },
        definitions: {
            title: 'Definiciones de conectores',
            page_title: 'Definiciones de conectores',
            add: 'Agregar definición',
            new_definition: 'Nueva definición de conector',
            edit_definition: 'Editar definición de conector',
            no_definitions: 'No se encontraron definiciones de conectores',
            definition_count: '{count} definición(es)',
            all_categories: 'Todas las categorías',
            select_category: 'Seleccione una categoría...',
            capabilities: 'Capacidades',
            capabilities_hint: 'Capacidades (separadas por comas)',
            capabilities_placeholder: 'por ej. leer, escribir, sincronizar',
            created: 'Definición de conector creada',
            updated: 'Definición del conector actualizada',
            fetch_error: 'No se pudieron recuperar las definiciones de los conectores. Por favor inténtalo de nuevo.',
            create_error: 'No se pudo crear la definición del conector',
            update_error: 'Error al actualizar la definición del conector',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'Captura de BRD',
            page_title: 'Captura BRD',
            create_brd: 'Crear BRD',
            brd_title: 'Título del BRD',
            description: 'Descripción',
            requirements: 'Requisitos',
            status: 'Estado',
            stakeholders: 'Partes interesadas',
            connector_label: 'Conector',
            select_connector: 'Seleccionar un conector...',
            approval: 'Aprobación',
            no_brds_found: 'No se encontraron BRDs',
            decision_records: 'Registros de decisiones',
            create_decision: 'Crear decisión',
            alternatives_considered: 'Alternativas consideradas',
            alternative_name: 'Nombre de la alternativa',
            reason_rejected: 'Motivo de rechazo',
            add_alternative: 'Añadir alternativa',
            remove_alternative: 'Eliminar alternativa {name}',
            alternatives: 'Alternativas',
            rationale: 'Justificación',
            selected_connector: 'Conector seleccionado',
            comma_separated_names: 'Nombres separados por comas',
            brd_created: 'BRD creado',
            decision_record_created: 'Registro de decisión creado',
            no_decision_records_found: 'No se encontraron registros de decisión',
            aria_brds_table: 'BRD',
            aria_decision_records_table: 'Registros de decisión',
            aria_approve: 'Aprobar {name}',
            aria_archive: 'Archivar {name}',
            brd_title_help: 'Un nombre breve y legible por humanos para este BRD, p. "Integración de emisión de tickets de Jira", "Lanzamiento de Okta SSO".',
            brd_title_placeholder: 'p.ej. Integración de emisión de billetes de Jira',
            description_help: 'El problema empresarial que resuelve este conector. ¿Qué resultado espera una vez que esté activo?',
            description_placeholder: 'por ej. Automatice la creación de tickets de incidentes a partir de alertas de monitoreo para que los guardias sean avisados ​​en 60 segundos.',
            connector_help: 'Opcional. Elija un conector para el que sea este BRD; déjelo vacío si aún no se ha elegido el conector.',
            requirements_help: 'Lista separada por comas de capacidades imprescindibles, p. "Inicio de sesión de OAuth2, entrega de webhook, exportación de auditoría".',
            requirements_placeholder: 'Inicio de sesión OAuth2, entrega de webhook, exportación de auditoría',
            stakeholders_help: 'Nombres o correos electrónicos separados por comas de las personas que deben cerrar sesión.',
            selected_connector_help: 'Elija el conector al que se aplica esta decisión.',
            rationale_help: 'Por qué se eligió este conector. Cubre los factores decisivos para que un futuro lector comprenda la llamada.',
            rationale_placeholder: 'por ej. Eligió Jira porque ya contiene nuestro historial de tickets y el equipo está capacitado en ello.',
            alternatives_considered_help: 'Otros conectores evaluados y por qué no fueron elegidos. Añade defensa para la auditoría.',
            alternative_name_placeholder: 'por ej. Servicio ahora',
            reason_rejected_placeholder: 'por ej. Costo 3 veces mayor, sin ventaja para nuestro caso de uso',
        },
        security_reviews: {
            title: 'Revisiones de seguridad',
            page_title: 'Revisiones de seguridad',
            create_review: 'Crear revisión',
            connector_name: 'Nombre del conector',
            connector_label: 'Conector',
            select_connector: 'Seleccionar un conector...',
            reviewer: 'Revisor',
            select_reviewer: 'Seleccionar un revisor...',
            findings: 'Hallazgos (uno por línea)',
            risk_level: 'Nivel de riesgo',
            risk_level_placeholder: 'p. ej. alto, medio, bajo',
            notes: 'Notas',
            review_status: 'Estado de la revisión',
            checklist: 'Lista de verificación',
            privacy_impact: 'Impacto en la privacidad',
            approval_gate: 'Puerta de aprobación',
            no_reviews_found: 'No se encontraron revisiones',
            review_created: 'Revisión de seguridad creada',
            create_control_mapping: 'Crear mapeo de control',
            control_mapping_created: 'Mapeo de control creado',
            connector_id_label: 'ID del conector',
            control_id_label: 'ID del control',
            control_name_label: 'Nombre del control',
            aria_approve: 'Aprobar {name}',
            aria_reject: 'Rechazar {name}',
            scan_imported: 'Escaneo importado con éxito',
            import_scan: 'Importar escaneo',
            scan_format: 'Formato del escaneo',
            scan_data: 'Datos del escaneo',
            import_results: 'Resultados de importación',
            recommendations: 'Recomendaciones',
            auto_populated_from: 'Autocompletado desde {tool}',
            manual_entry: 'Entrada manual',
            automation_type: 'Tipo de automatización',
            select_automation_type: 'Seleccione el tipo de automatización...',
            automation_full: 'Completo: el conector automatiza completamente este control',
            automation_partial: 'Parcial: el conector aporta pruebas; los humanos todavía actúan',
            automation_monitoring: 'Solo monitoreo: el conector observa, no aplica',
            control_id_help: 'Utilice el código de su esquema de cumplimiento (por ejemplo, ISO-27001-A.12.1, SOC2-CC6.1 o su ID de control interno).',
            control_name_help: 'Nombre corto legible por humanos (por ejemplo, "restricción de acceso a la red").',
            description_label: 'Descripción',
            load_sarif_example: 'Cargar ejemplo SARIF',
            load_json_example: 'Cargar ejemplo personalizado JSON',
            scan_format_help: 'SARIF es el estándar OASIS emitido por la mayoría de los escáneres (Snyk, CodeQL, Semgrep). Utilice JSON personalizado para herramientas que no emiten SARIF.',
            scan_data_help: 'Pegue el resultado completo del escaneo, cargue un archivo .sarif/.json o haga clic en el botón Cargar ejemplo para ver la forma.',
            upload_file_label: 'Cargue el archivo .sarif o .json',
            invalid_scan_json: 'No se pudieron analizar los datos escaneados como JSON. Compruebe si hay errores de sintaxis cerca del carácter resaltado.',
            save_as_review: 'Guardar como revisión de seguridad',
            discard_preview: 'Descartar vista previa',
            preview_hint: 'Esta es una vista previa: todavía no se ha guardado nada. Haga clic en "Guardar como revisión de seguridad" para crear una revisión pendiente precargada con estos valores.',
            reviewer_required_for_save: 'Elija un revisor para guardar este análisis como revisión de seguridad.',
        },
        poc_scorecards: {
            title: 'Tarjetas de puntuación POC',
            page_title: 'Cuadros POC',
            create_scorecard: 'Crear tarjeta de puntuación',
            connector_label: 'Conector',
            select_connector: 'Seleccionar un conector...',
            vendor_name: 'Nombre del proveedor',
            recommendation: 'Recomendación',
            recommendation_placeholder: 'p. ej. Continuar, No continuar, Necesita evaluación adicional',
            criteria: 'Criterios de evaluación',
            criterion_name: 'Nombre del criterio',
            score: 'Puntuación',
            max_score: 'Puntuación máxima',
            notes: 'Notas',
            add_criterion: 'Añadir criterio',
            remove_criterion: 'Eliminar criterio {name}',
            total_score: 'Puntuación total',
            api_quality: 'Calidad de API',
            reliability: 'Fiabilidad',
            support_quality: 'Calidad del soporte',
            cost_score: 'Puntuación de coste',
            overall_score: 'Puntuación global',
            evidence: 'Evidencia',
            no_scorecards_found: 'No se encontraron tarjetas de puntuación',
            scorecard_created: 'Cuadro POC creado',
            connector_help: 'Elija el conector para el que es esta evaluación POC.',
            vendor_name_help: 'El proveedor o producto que se está evaluando, p. "Okta", "Datadog", "Copo de nieve".',
            vendor_name_placeholder: 'por ej. Okta, Datadog, Copo de nieve',
            recommendation_help: 'Tu llamada final después de la evaluación. Sea breve y decisivo.',
            criteria_help: 'Califique al proveedor en cada criterio de evaluación. Agregue una fila por criterio (API calidad, confiabilidad, soporte, costo, etc.).',
            criterion_name_help: 'Lo que estás puntuando, p. "API Calidad", "Confiabilidad", "Soporte", "Costo".',
            criterion_name_placeholder: 'por ej. API Calidad',
            score_help: 'Puntuación otorgada por este criterio. No debe exceder la puntuación máxima.',
            max_score_help: 'Puntuación máxima disponible para este criterio. Utilice la misma escala en todas las filas para que los totales sean comparables.',
            notes_help: 'Opcional. Agregue evidencia, advertencias o enlaces: cualquier cosa que explique la puntuación.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrar instancias de conectores',
            aria_filter_definitions: 'Filtrar definiciones de conectores',
            aria_filter_credentials: 'Filtrar credenciales',
            aria_filter_events: 'Filtrar eventos',
            aria_filter_dlq: 'Filtrar cola de mensajes muertos',
            aria_instance_summary: 'Resumen de la instancia del conector',
            aria_active_connectors: 'Conectores activos',
            aria_error_connectors: 'Conectores con errores',
            aria_credential_health: 'Resumen de estado de credenciales',
            aria_items_per_page: 'Elementos por página',
            show_password: 'Mostrar contraseña',
            hide_password: 'Ocultar contraseña',
            payload_json_label: 'Carga útil (JSON)',
            payload_invalid_json: 'La carga útil debe ser JSON válido',
            placeholder_event_type: 'p. ej. incident.created',
            placeholder_source_module: 'p. ej. monitoring',
            placeholder_version: 'p. ej. 1.0',
        },
        statuses: {
            configuring: 'Configurando',
            testing: 'Pruebas',
            active: 'Activo',
            paused: 'En pausa',
            error: 'error',
            disabled: 'Discapacitado',
            draft: 'Borrador',
            approved: 'Aprobado',
            archived: 'Archivado',
            pending: 'Pendiente',
            rejected: 'Rechazado',
        },
        system_types: {
            application: 'Aplicación',
            database: 'Base de datos',
            service: 'Service',
            infrastructure: 'Infraestructura',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Local',
            cloud: 'Nube',
            hybrid: 'Híbrido',
            managed: 'Gestionado',
        },
        system_statuses: {
            active: 'Activo',
            decommissioned: 'Retirado',
            planned: 'Planificado',
            under_review: 'En revisión',
        },
        automation_statuses: {
            planning: 'Planificación',
            in_progress: 'En curso',
            completed: 'Completado',
            on_hold: 'En espera',
        },
        gap_types: {
            functional: 'Funcional',
            technical: 'Técnico',
            security: 'Seguridad',
            compliance: 'Cumplimiento',
            process: 'Proceso',
        },
        severity_labels: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
        },
        gap_statuses: {
            open: 'Abierto',
            in_progress: 'En curso',
            mitigated: 'Mitigado',
            closed: 'Cerrado',
            accepted: 'Aceptado',
        },
        verification_statuses: {
            not_started: 'No iniciado',
            in_progress: 'En curso',
            verified: 'Verificado',
            failed: 'Fallido',
        },
        subscriber_types: {
            webhook: 'gancho web',
            internal: 'Interno',
            email: 'Correo',
            queue: 'Cola',
        },
        credential_types: {
            password: 'Contraseña',
            api_key: 'Clave API',
            oauth2_client: 'Cliente OAuth2',
            certificate: 'Certificado',
            token: 'ficha',
            ssh_key: 'Clave SSH',
            imap_credentials: 'Credenciales IMAP',
        },
        categories: {
            hr: 'recursos humanos',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'nube',
            security: 'Seguridad',
            itsm: 'ITSM',
            custom: 'personalizado',
        },
        delivery_statuses: {
            pending: 'Pendiente',
            delivered: 'Entregado',
            partial: 'Parcial',
            failed: 'Fallido',
        },
        automation_planning: {
            title: 'Planificación de automatización',
            page_title: 'Planificación de automatización',
            program_name: 'Nombre del programa',
            program_status: 'Estado',
            description: 'Descripción',
            linked_artifacts: 'Artefactos vinculados',
            create_program: 'Crear programa',
            edit_program: 'Editar programa',
            no_programs_found: 'No se encontraron programas',
            program_created: 'Programa creado',
            program_updated: 'Programa actualizado',
            target_date: 'Fecha objetivo',
            owner: 'Propietario',
            select_status: 'Seleccionar estado...',
            aria_programs_table: 'Programas de automatización',
            aria_edit: 'Editar {name}',
            export_programs: 'Exportar programas',
            title_label: 'Título del programa',
            title_help: 'Un nombre breve y legible por humanos para el programa de automatización, p. "Lanzamiento del incidente del tercer trimestre en Jira", "PDPL automatización de solicitudes de acceso".',
            title_placeholder: 'por ej. Lanzamiento de incidentes a Jira en el tercer trimestre',
            description_help: 'Qué cubre este programa y el resultado que espera cuando esté activo. Opcional pero recomendable.',
            description_placeholder: 'por ej. Transfiera alertas de monitoreo a Jira para que los de guardia sean avisados ​​en 60 segundos.',
            owner_label: 'Propietario del programa',
            owner_help: 'El responsable de este programa. Recibirán notificaciones sobre el progreso y las puertas.',
            select_owner: 'Seleccione un propietario...',
            frameworks_in_scope: 'Marcos en alcance',
            frameworks_in_scope_help: 'Marcos de cumplimiento que este programa apoya. Ayuda a que el registro de auditoría muestre por qué regulaciones se rige la automatización.',
            select_frameworks: 'Seleccionar marcos...',
            status_draft: 'Borrador',
            status_active: 'Activo',
            status_completed: 'Completado',
            status_archived: 'Archivado',
            change_status: 'Cambiar estado',
            status_updated: 'Estado actualizado',
            aria_change_status: 'Cambiar estado para {name}',
        },
        system_inventory: {
            title: 'Inventario de sistemas',
            page_title: 'Inventario de sistemas',
            system_name: 'Nombre del sistema',
            system_type: 'Tipo de sistema',
            hosting: 'Alojamiento',
            status: 'Estado',
            description: 'Descripción',
            create_system: 'Crear sistema',
            edit_system: 'Editar sistema',
            no_systems_found: 'No se encontraron sistemas',
            system_created: 'Sistema creado',
            system_updated: 'Sistema actualizado',
            select_type: 'Seleccionar tipo...',
            select_hosting: 'Seleccionar alojamiento...',
            select_status: 'Seleccionar estado...',
            owner: 'Propietario',
            department: 'Departamento',
            data_categories: 'Categorías de datos',
            data_categories_placeholder: 'Separados por comas, ej. PII, Financiero, Salud',
            jurisdiction: 'Jurisdicción',
            jurisdiction_placeholder: 'ej. UE, EE. UU., Global',
            interfaces: 'Interfaces',
            interfaces_placeholder: 'Separados por comas, ej. API REST, SFTP, JDBC',
            owner_placeholder: 'Seleccione un propietario responsable...',
            department_placeholder: 'Seleccione la unidad organizativa responsable...',
            system_name_hint: 'Utilice el nombre del sistema o aplicación reconocido por la empresa.',
            system_type_hint: 'Clasifique el sistema para que los informes puedan agrupar activos similares.',
            hosting_hint: 'Identifique dónde se ejecuta el sistema para la revisión de residencia, seguridad y proveedores.',
            status_hint: 'Establezca el estado del ciclo de vida utilizado por revisiones, exportaciones y evidencia de auditoría.',
            owner_hint: 'Elija al usuario activo responsable de la gobernanza del sistema y las tareas de seguimiento.',
            department_hint: 'Utilice la unidad organizativa responsable de operar o gobernar este sistema.',
            data_categories_hint: 'Enumere las clases de datos procesadas por este sistema, separadas por comas.',
            jurisdiction_hint: 'Registre la jurisdicción legal o de alojamiento principal para el mapeo de cumplimiento.',
            interfaces_hint: 'Enumere las rutas de conector o integración utilizadas para intercambiar datos.',
            description_hint: 'Resuma lo que hace el sistema y por qué es importante para el mapa de datos.',
            owner_department_required: 'El propietario y el departamento son obligatorios.',
            aria_systems_table: 'Inventario de sistemas',
            aria_edit: 'Editar {name}',
            export_systems: 'Exportar sistemas',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Matriz de trazabilidad',
            page_title: 'Matriz de trazabilidad',
            brd_selector: 'Seleccionar BRD',
            requirement: 'Requisito',
            linked_artifact: 'Artefacto vinculado',
            verification_status: 'Estado de verificación',
            verified_by: 'Verificado por',
            create_entry: 'Crear entrada',
            edit_entry: 'Editar entrada',
            no_entries_found: 'No se encontraron entradas',
            entry_created: 'Entrada creada',
            entry_updated: 'Entrada actualizada',
            select_brd: 'Seleccionar un BRD...',
            select_status: 'Seleccionar estado...',
            aria_matrix_table: 'Matriz de trazabilidad',
            aria_edit: 'Editar entrada para {name}',
            export_matrix: 'Exportar matriz',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'ID del requisito',
        },
        gap_register: {
            title: 'Registro de brechas',
            page_title: 'Registro de brechas',
            gap_title: 'Título de brecha',
            gap_type: 'Tipo de brecha',
            severity: 'Gravedad',
            status: 'Estado',
            description: 'Descripción',
            remediation_plan: 'Plan de remediación',
            assigned_to: 'Asignado a',
            due_date: 'Fecha de vencimiento',
            create_gap: 'Crear brecha',
            edit_gap: 'Editar brecha',
            no_gaps_found: 'No se encontraron brechas',
            gap_created: 'Brecha creada',
            gap_updated: 'Brecha actualizada',
            select_type: 'Seleccionar tipo...',
            select_severity: 'Seleccionar gravedad...',
            select_status: 'Seleccionar estado...',
            aria_gaps_table: 'Registro de brechas',
            aria_edit: 'Editar {name}',
            export_gaps: 'Exportar brechas',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Vinculación de marco',
            framework_linkage_placeholder: 'ej. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Alcance del marco',
            framework_scope_placeholder: 'ej. GDPR, PCI-DSS',
            procedure_linkage: 'Vinculación de procedimiento',
            procedure_linkage_placeholder: 'ej. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Fuente regulatoria',
            regulatory_source_placeholder: 'ej. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Mapeo de controles',
            control_mappings_section: 'Asignaciones de control',
            no_control_mappings: 'Aún no hay asignaciones de control. Haga clic en "Crear asignación de control" para agregar una.',
            automation_type_col: 'Automatización',
        },
        gap_register_extra: {
            framework_filter: 'Marco',
            framework_id: 'ID del marco',
            framework_id_placeholder: 'ej. GDPR, ISO 27001',
            all_frameworks: 'Todos los marcos',
        },
        poc_scorecards_extra: {
            criteria_count: 'Criterios',
        },
        export_labels: {
            export_brds: 'Exportar BRDs',
            export_decision_records: 'Exportar registros de decisiones',
            export_security_reviews: 'Exportar revisiones de seguridad',
            export_poc_scorecards: 'Exportar tarjetas POC',
            export_instances: 'Exportar instancias',
            export_definitions: 'Definiciones de exportación',
            export_credentials: 'Exportar credenciales',
            export_events: 'Exportar eventos',
            exporting: 'Exportando...',
            export_success: 'Exportación completada',
            export_error: 'Error en la exportación',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extraer obligaciones',
            extracting: 'Extrayendo…',
            results_heading: 'Obligaciones extraídas ({count})',
            tokens_label: '≈ {count} fichas',
            no_obligations: 'No se detectan patrones de obligación en este texto de contrato.',
            col_class: 'clase',
            col_summary: 'Resumen (editable)',
            col_excerpt: 'Extracto de cláusula',
            col_due_date: 'Fecha de vencimiento (editable)',
            col_priority: 'Prioridad',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Plazo de aviso',
            class_audit: 'Derecho de auditoría',
            class_insurance: 'Seguro',
            class_sla: 'Respuesta SLA',
            class_breach: 'Notificación de incumplimiento',
            class_renewal: 'Ventana de renovación',
            citations_label: 'Citas',
            accept_hint: 'Aceptar creará una tarea por obligación (cap. 8) con la prioridad + fecha de vencimiento mostrada.',
            accept_label: 'Aceptar obligaciones',
            reject_label: 'Rechazar informe',
            resolve_summary: 'Resolución: {status} · {tasks} tarea(s) creada(s)',
            error_generic: 'No se pudieron extraer obligaciones. Inténtalo de nuevo o consulta el texto del contrato.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Proyecto de razonamiento',
            drafting: 'Redacción del razonamiento…',
            results_heading: 'AI-razonamiento redactado ({risk_level})',
            tokens_label: '≈ {count} fichas',
            narrative_label: 'narrativa de justificación',
            controls_heading: 'Controles de compensación',
            no_controls: 'No se sugieren controles compensatorios.',
            citations_label: 'Citas',
            accept_label: 'aceptar el razonamiento',
            reject_label: 'Rechazar borrador',
            accept_hint: 'Aceptar registra el razonamiento sobre el borrador persistente para el linaje IAT. Los controles narrativos + no actualizan automáticamente el registro de aceptación; se requiere el pegado por parte del operador.',
            resolve_summary: 'Resolución: {status}',
            error_generic: 'No se pudo redactar el razonamiento. Inténtalo de nuevo o comprueba el estado de aceptación del riesgo.',
        },
        redline_assistant: {
            run_label: 'Línea roja con biblioteca',
            running: 'Ejecutando línea roja...',
            results_heading: 'Resaltar sugerencias ({count})',
            tokens_label: '≈ {count} fichas',
            no_suggestions: 'No se detectaron lagunas en las cláusulas estándar.',
            jurisdiction_label: 'Jurisdicción',
            col_kind: 'amable',
            col_severity: 'Gravedad',
            col_title: 'sugerencia',
            col_excerpt: 'Extracto coincidente',
            col_apply: 'Aplicar',
            kind_missing: 'Estándar faltante',
            kind_weaker: 'Más débil que el estándar',
            kind_jurisdiction: 'Superposición de jurisdicción',
            proposed_label: 'Texto propuesto',
            citations_label: 'Citas',
            accept_label: 'Aceptar seleccionado',
            reject_label: 'Rechazar informe',
            partial_label: 'Aplicar selección',
            accept_hint: 'Aplicar registros de selección las sugerencias elegidas en el borrador del linaje IAT. No se aplica ninguna edición automática al texto del contrato: el operador controla la línea roja.',
            resolve_summary: 'Resolución: {status} · {count} sugerencia(s) aplicada(s)',
            error_generic: 'No se pudo generar una línea roja. Inténtalo de nuevo o consulta el estado del contrato.',
            clause_library_link: 'Administrar biblioteca de cláusulas',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Biblioteca de cláusulas',
            add_label: 'Agregar cláusula',
            edit_label: 'Editar',
            delete_label: 'Eliminar',
            slug_label: 'babosa',
            title_label: 'Título',
            body_label: 'cuerpo',
            jurisdiction_label: 'Jurisdicción',
            tags_label: 'Etiquetas (separadas por comas)',
            must_contain_label: 'Marcadores que deben contener (separados por comas)',
            severity_label: 'Gravedad cuando falta',
            no_clauses: 'Aún no hay cláusulas. Agregue uno para inicializar el asistente de línea roja.',
            save_label: 'Guardar',
            cancel_label: 'Cancelar',
            load_error: 'No se pudo cargar la biblioteca de cláusulas.',
            save_error: 'No se pudo guardar la cláusula.',
            delete_error: 'No se pudo eliminar la cláusula.',
            delete_confirm: '¿Eliminar la cláusula "{slug}"? Esto no se puede deshacer.',
        },
        subprocessor_impact: {
            assess_label: 'Evaluar el impacto',
            assessing: 'Evaluando el impacto…',
            results_heading: 'Impacto del subprocesador ({severity})',
            tokens_label: '≈ {count} fichas',
            risk_delta_label: 'Delta de riesgo',
            notification_heading: 'Notificación al cliente',
            subject_label: 'Asunto',
            deadline_label: 'Fecha límite de notificación',
            body_label: 'Organismo de notificación',
            required_fields_label: 'Campos de la notificación (artículo 28, apartado 2)',
            mitigation_heading: 'Pasos de mitigación',
            citations_label: 'Citas',
            accept_label: 'Aceptar evaluación',
            reject_label: 'Rechazar evaluación',
            accept_hint: 'Aceptar creará una tarea de notificación al cliente con la fecha límite SLA como fecha de vencimiento.',
            resolve_summary: 'Resolución: {status}{task}',
            error_generic: 'No se pudo evaluar este cambio de subprocesador. Inténtelo de nuevo o verifique el registro de cambios.',
        },
        onboarding_evidence: {
            collect_label: 'Ejecute AI recopilación de pruebas',
            collecting: 'Recolectando evidencia…',
            results_heading: 'Recopilación de pruebas ({tier})',
            tokens_label: '≈ {count} fichas',
            findings_heading: 'Evidencia existente',
            missing_heading: 'Artefactos faltantes',
            no_findings: 'No se ha detectado ninguna evidencia existente.',
            no_missing: 'No faltan artefactos obligatorios para este nivel de riesgo.',
            next_step_label: 'Siguiente paso',
            class_dpa: 'Acuerdo de procesamiento de datos',
            class_soc2: 'Informe SOC 2',
            class_iso27001: 'Certificado ISO 27001',
            class_insurance: 'Póliza de seguro cibernético',
            class_incident_history: 'Incidente anterior',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Aceptar informe',
            reject_label: 'Rechazar informe',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'No se pudo ejecutar la recopilación de pruebas AI. Inténtelo de nuevo o verifique el estado del caso de incorporación.',
        },
        no_permission_title: 'Permisos insuficientes',
        no_permission_message: 'No tiene permiso para acceder a la gestión de riesgos de proveedores.',
        unknown_vendor: 'Proveedor desconocido',
        nav: {
            dashboard: 'Panel de riesgos',
            scoring: 'Puntuación de riesgos',
            questionnaires: 'Cuestionarios',
            contracts: 'Contratos',
            gates: 'Puertas de aprobación',
            reviews: 'Revisiones de proveedores',
            signals: 'Señales de riesgo',
            reassessments: 'Reevaluaciones',
            portfolio: 'Portafolio de riesgos',
            acceptances: 'Aceptación de riesgos',
            review_routing: 'Enrutamiento de revisiones',
            onboarding_cases: 'Casos de incorporación',
            sub_processor_register: 'Registro de subprocesadores',
        },
        dashboard: {
            title: 'Panel de riesgo de proveedores',
            page_title: 'Panel de riesgo del proveedor',
            loading: 'Cargando dashboard data...',
            risk_tier_header: 'Nivel de riesgo',
            count_header: 'contar',
            percentage_header: 'Porcentaje',
            sections_nav: 'Secciones de riesgo del proveedor',
            urgent_expiry: 'Urgente: vence en 14 días',
            no_previous_tier: 'Sin nivel anterior',
            view_as_chart: 'Ver como gráfico',
            view_as_table: 'Ver como tabla',
            showing_chart: 'Mostrando vista de gráfico',
            showing_table: 'Mostrando vista de tabla',
            load_error: 'Error al load vendor risk dashboard. Por favor, inténtelo de nuevo.',
            risk_distribution: 'Riesgo Distribution Resumen',
            recent_tier_changes: 'Reciente Nivel Changes',
            no_tier_changes: 'Sin cambios de nivel recientes.',
            review_kpis: 'KPIs de revisión',
            total_vendors_reviewed: 'Total Proveedores Reviewed',
            reviews_by_outcome: 'Revisiones por resultado',
            contract_coverage: 'Cobertura contractual',
            total_vendors: 'Total Proveedores',
            with_active_dpa: 'Con Activo DPA',
            without_dpa: 'Sin DPA',
            dpa_coverage: 'Cobertura DPA',
            active_contracts_by_type: 'Activo Contratos por Tipo',
            expiring_contracts: 'Contratos que caducan (próximos 90 días)',
            no_expiring_contracts: 'Sin contratos que caduquen en los próximos 90 días.',
            vendor: 'Proveedor',
            previous_tier: 'Anterior Nivel',
            current_tier: 'Actual Nivel',
            changed_at: 'Cambiado el',
            expires_at: 'Caduca En',
            days_remaining: 'Días restantes',
            days: 'días',
            vendors_label: 'proveedores',
            no_data: 'Sin datos',
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
            expiring_acceptances_heading: 'Aceptaciones de riesgo en vencimiento',
            expiring_30d_label: 'Vence en 30 días',
            expiring_60d_label: 'Vence en 60 días',
            expiring_90d_label: 'Vence en 90 días',
        },
        scoring: {
            title: 'Puntuación de riesgos',
            page_title: 'Puntuación de riesgo del proveedor',
            filter_scores: 'Filtrar puntuaciones de riesgo',
            filter_by_tier: 'Filtrar por nivel de riesgo',
            scores_pagination: 'Paginación de puntuaciones de riesgo',
            config_dimensions: 'Configuración de dimensiones de puntuación',
            scores_loaded: '{count} puntuaciones de riesgo cargadas',
            weight_must_equal_100: 'El peso total debe ser igual a 100 (actual: {current})',
            out_of_10: 'sobre 10, clasificado {tier}',
            dimension_name: 'Nombre de la dimensión {index}',
            dimension_weight: '{name} peso (porcentaje)',
            must_equal_100: 'debe ser igual a 100',
            config_title: 'Configuración de puntuación',
            scores_title: 'Proveedor Riesgo Scores',
            new_score_title: 'Puntuación un Proveedor',
            new: 'Nuevo puntuación',
            vendor_id: 'Proveedor ID',
            risk_tier: 'Riesgo Nivel',
            score: 'Puntuación',
            scored_at: 'Puntuado el',
            no_scores: 'No se encontraron risk scores',
            all_tiers: 'Todos los niveles',
            load_config_error: 'Error al load risk scoring configuration. Por favor, inténtelo de nuevo.',
            update_config: 'Actualizar configuración de puntuación',
            update_config_error: 'Error al update risk scoring configuration. Por favor, inténtelo de nuevo.',
            load_scores_error: 'Error al load vendor risk scores. Por favor, inténtelo de nuevo.',
            calculate_error: 'Error al calculate vendor risk score. Por favor, inténtelo de nuevo.',
            weight: 'peso',
            score_range: 'Puntuación (1-5)',
            method: 'Método',
        },
        questionnaires: {
            title: 'Cuestionarios',
            page_title: 'Cuestionarios de proveedores',
            filter_questionnaires: 'Filtros de cuestionario',
            questionnaire_views: 'Vistas del cuestionario',
            templates_list: 'Lista de plantillas de cuestionarios',
            instances_list: 'Lista de instancias del cuestionario',
            filter_by_status: 'Filtrar por estado del cuestionario',
            templates_per_page: 'Plantillas por página',
            instances_per_page: 'Instancias por página',
            select_template: 'Seleccionar plantilla de cuestionario',
            template_category: 'Categoría de plantilla',
            templates_tab: 'Plantillas',
            instances_tab: 'Instancias',
            new: 'Nuevo Questionnaire',
            new_template: 'Nueva Plantilla',
            new_instance: 'Nueva Instancia',
            vendor_id: 'Proveedor ID',
            template_id: 'Plantilla ID',
            assigned_to: 'Asignado Hasta',
            due_date: 'Fecha de vencimiento',
            responses: 'Respuestas',
            risk_score: 'Finalización',
            no_questionnaires: 'No se encontraron questionnaires',
            load_templates_error: 'Error al load questionnaire templates. Por favor, inténtelo de nuevo.',
            load_instances_error: 'Error al load questionnaires. Por favor, inténtelo de nuevo.',
            create_template_error: 'Error al create questionnaire template. Por favor, inténtelo de nuevo.',
            initiate_error: 'Error al initiate questionnaire. Por favor, inténtelo de nuevo.',
            submit_error: 'Error al submit questionnaire. Por favor, inténtelo de nuevo.',
            review_error: 'Error al review questionnaire. Por favor, inténtelo de nuevo.',
            resubmit: 'Reenviar',
            approve_error: 'No se pudo aprobar el cuestionario',
            reject_error: 'No se pudo rechazar el cuestionario',
            resubmit_error: 'No se pudo reenviar el cuestionario',
            templates_loaded: '{count} plantillas de cuestionario cargadas',
            instances_loaded: '{count} instancias de cuestionario cargadas',
            fill_responses_cta: 'Rellenar respuestas',
            fill_responses_title: 'Rellenar respuestas del cuestionario',
            template_missing: 'Plantilla no encontrada.',
            responses_saved: 'Respuestas guardadas.',
            load_template_error: 'No se pudo cargar la plantilla.',
            version_history: 'Historial de versiones',
            download_previous_version: 'Descargar versión anterior',
            trigger: 'gatillo',
            template_label: 'Plantilla',
            reviewer: 'Revisor',
            reviewer_required: 'Se debe asignar un revisor para los cuestionarios {trigger} (separación de funciones).',
            select_option: 'Seleccione una opción',
            multi_choice_hint: 'Seleccione todo lo que corresponda',
            category_initial_onboarding: 'Incorporación inicial',
            category_annual_review: 'Revisión anual',
            category_incident_triggered: 'Desencadenado por incidente',
            category_contract_renewal: 'Renovación de contrato',
            template_editor: {
                page_title: 'Editar plantilla de cuestionario',
                preview_page_title: 'Vista previa de la plantilla del cuestionario',
                edit_structure: 'Editar estructura',
                preview: 'Vista previa',
                actions: 'Acciones',
                back_to_list: 'Volver a plantillas',
                load_error: 'No se pudo cargar la plantilla',
                save_error: 'No se pudo guardar la estructura de la plantilla',
                saved: 'Estructura de plantilla guardada.',
                sections_heading: 'Secciones',
                questions_heading: 'Preguntas',
                add_section: 'Agregar sección',
                edit_section: 'Editar sección',
                delete_section: 'Eliminar sección',
                move_section_up: 'Mover sección hacia arriba',
                move_section_down: 'Mover sección hacia abajo',
                confirm_delete_section: '¿Eliminar la sección "{title}"? Todas sus preguntas serán eliminadas.',
                section_title: 'Título de la sección',
                section_description: 'Descripción de la sección',
                section_id: 'ID de sección',
                section_id_hint: 'Generado automáticamente a menos que establezca uno explícitamente.',
                no_sections: 'Aún no hay secciones. Agrega una sección para comenzar.',
                no_questions: 'Aún no hay preguntas en esta sección.',
                add_question: 'Agregar pregunta',
                edit_question: 'Editar pregunta',
                delete_question: 'Eliminar pregunta',
                move_question_up: 'Mover pregunta hacia arriba',
                move_question_down: 'Mover pregunta hacia abajo',
                confirm_delete_question: '¿Eliminar la pregunta "{text}"?',
                question_text: 'Texto de la pregunta',
                question_type: 'Tipo de pregunta',
                question_id: 'ID de pregunta',
                evidence_required: 'Se requiere evidencia',
                risk_weight: 'Peso de riesgo',
                scoring_map: 'Mapa de puntuación',
                scoring_map_json_hint: 'JSON objeto que asigna valores de respuesta a puntuaciones numéricas.',
                scoring_map_invalid_json: 'El mapa de puntuación debe ser válido JSON.',
                options_heading: 'Opciones de respuesta',
                add_option: 'Agregar opción',
                remove_option: 'Eliminar opción',
                option_label: 'Etiqueta',
                option_value: 'Valor',
                option_risk_weight: 'Peso de riesgo',
                min_options_error: 'Las preguntas de elección deben tener al menos 2 opciones.',
                option_label_required: 'Cada opción necesita una etiqueta.',
                option_value_required: 'Cada opción necesita un valor.',
                option_value_unique: 'Los valores de las opciones deben ser únicos.',
                branching_rules_heading: 'Reglas de ramificación',
                branching_rules_hint: 'Muestra u oculta esta pregunta según la respuesta a una pregunta anterior.',
                add_branching_rule: 'Agregar regla de ramificación',
                remove_branching_rule: 'Eliminar regla de bifurcación',
                branching_condition_question: 'si pregunta',
                branching_condition_value: 'tiene valor',
                branching_action: 'entonces',
                branching_action_show: 'muestra esta pregunta',
                branching_action_hide: 'ocultar esta pregunta',
                branching_select_condition_question: 'Seleccionar pregunta anterior',
                branching_select_condition_value: 'Seleccionar valor',
                save: 'Guardar estructura',
                saving: 'Guardando...',
                cancel: 'Cancelar',
                close: 'Cerrar',
                question_text_required: 'Se requiere el texto de la pregunta.',
                section_title_required: 'El título de la sección es obligatorio.',
                type_single_choice: 'elección única',
                type_multi_choice: 'opción múltiple',
                type_free_text: 'texto libre',
                type_numeric: 'numérico',
                type_date: 'Fecha',
                type_file_upload: 'Carga de archivos',
                type_yes_no: 'Sí/No',
                preview_heading: 'Vista previa de la plantilla',
                preview_subtitle: 'Vista previa de solo lectura de cómo le aparece el cuestionario a un proveedor.',
                preview_no_content: 'Esta plantilla aún no tiene secciones ni preguntas.',
                preview_branching_notice: 'Las reglas de bifurcación se evalúan durante el llenado; La vista previa muestra cada pregunta.',
            },
            review_modal: {
                title: 'Revisar las respuestas al cuestionario',
                open_cta: 'Revisión',
                loading: 'Cargando cuestionario...',
                template_missing: 'Plantilla no encontrada.',
                load_error: 'No se pudo cargar el cuestionario para su revisión.',
                annotation_heading: 'Anotaciones',
                annotation_placeholder: 'Agregue una anotación de revisor para esta pregunta...',
                annotation_status_label: 'Estado de anotación',
                annotation_status_acceptable: 'Aceptable',
                annotation_status_concern: 'Preocupación',
                annotation_status_requires_action: 'Requiere acción',
                review_comments_label: 'Comentarios generales de revisión',
                review_comments_placeholder: 'Resuma los hallazgos de su revisión...',
                justification_label: 'Justificación de la decisión',
                justification_placeholder: 'Explique el motivo de su decisión...',
                reject_requires_comments: 'Rechazar un cuestionario requiere comentarios de revisión.',
                decision_heading: 'decisión',
                decision: {
                    approved: 'Aprobar',
                    rejected: 'Rechazar',
                    additional_info_requested: 'Solicitar información adicional',
                },
                submit: 'Enviar reseña',
                submitting: 'Enviando reseña...',
                cancel: 'Cancelar',
                submit_success: 'Revisión enviada.',
                submit_error: 'No se pudo enviar la revisión.',
                separation_of_duties_error: 'No puede revisar un cuestionario que se le asignó completar. La separación de funciones requiere un revisor diferente.',
                decision_required: 'Seleccione una decisión de revisión.',
                announce_opened: 'Modal de revisión abierto.',
                announce_submitted: 'Revisión enviada.',
            },
            instance_detail: {
                submitted_at: 'Enviado',
                reviewed_at: 'Revisado',
                reviewer: 'Revisor',
                review_decision: 'decisión',
                review_comments: 'Revisar comentarios',
                decision_approved: 'Aprobado',
                decision_rejected: 'Rechazado',
                decision_additional_info_requested: 'Más información solicitada',
                decision_pending: 'Pendiente de revisión',
                expand_comments: 'Mostrar comentarios completos',
                collapse_comments: 'Ocultar comentarios completos',
                no_reviewer: 'No hay revisor asignado',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Borrador',
                in_progress: 'En curso',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Aprobado',
                rejected: 'Rechazado',
                expired: 'Caducado',
            },
            export: {
                portfolio_label: 'Cartera de exportación',
                instance_label: 'Exportar',
                portfolio_success: 'Exportación de cartera lista.',
                instance_success: 'Exportación del cuestionario listo.',
                portfolio_error: 'No se pudo exportar la cartera de cuestionarios.',
                instance_error: 'No se pudo exportar el cuestionario.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Algunas preguntas están ocultas por reglas de bifurcación.',
                cycle_warning: 'Se detectó un ciclo en las reglas de bifurcación. Las preguntas afectadas son visibles de forma predeterminada.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Contratos',
            page_title: 'Contratos de proveedores',
            new: 'Nuevo contrato',
            vendor_id: 'Proveedor ID',
            filter_contracts: 'Filtrar contratos',
            filter_by_document_type: 'Filtrar por tipo de documento',
            filter_by_status: 'Filtrar por estado del contrato',
            per_page: 'Contratos por página',
            expired_notice: 'Este contrato ha expirado',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Documento Tipo',
            expiry_date: 'Fecha de caducidad',
            no_contracts: 'No se encontraron contracts',
            all_document_types: 'Todos Documento Types',
            load_error: 'Error al load contracts. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create contract. Por favor, inténtelo de nuevo.',
            delete_error: 'Error al delete contract. Por favor, inténtelo de nuevo.',
            update_error: 'Error al actualizar el estado. Por favor, inténtelo de nuevo.',
            confirm_delete: '¿Eliminar contrato "{title}"?',
            renewal_date: 'Fecha de renovación',
            effective_date: 'Fecha de vigencia',
            parties: 'Partes',
            summary: 'Resumen',
            n_contracts: '{count} contratos',
            n_contract: '{count} contrato',
            contracts_loaded: '{count} contratos cargados',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Adenda de seguridad',
            doc_type_scc_annex: 'Anexo SCC',
            doc_type_sub_processor_list: 'Lista de subencargados',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Otro',
            status_draft: 'Borrador',
            status_under_negotiation: 'En negociación',
            status_pending_signature: 'Pendiente de firma',
            status_active: 'Activo',
            status_expired: 'Expirado',
            status_terminated: 'Terminado',
            status_superseded: 'Reemplazado',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Puertas de aprobación',
            page_title: 'Evaluaciones de puertas',
            new: 'Nuevo Gate Evaluation',
            vendor_id: 'Proveedor ID',
            gate_name: 'Nombre de puerta',
            outcome: 'Resultado',
            evaluator: 'Evaluador',
            no_gates: 'No se encontraron gate evaluations',
            load_error: 'Error al load gate evaluations. Por favor, inténtelo de nuevo.',
            evaluate_error: 'Error al evaluate vendor gate. Por favor, inténtelo de nuevo.',
            detail_error: 'Error al fetch evaluation detail. Por favor, inténtelo de nuevo.',
            override_error: 'Error al override gate evaluation. Por favor, inténtelo de nuevo.',
            criteria_results: 'Resultados de criterios',
            evaluate_form: 'Evaluar la puerta del proveedor',
            per_page: 'Evaluaciones por página',
            passed: 'Aprobado',
            failed: 'Falló',
            override: 'Anulación',
            override_justification: 'Justificación de anulación',
            override_placeholder: 'Introduzca justification for overriding this gate evaluation',
            approval_id: 'ID de aprobación',
            overall_passed: 'Aprobado',
            overall_failed: 'Fallido',
            overall_overridden: 'Anulado',
            evaluations_loaded: '{count} evaluaciones de puerta cargadas',
            create_approval_cta: 'Crear aprobación',
        },
        reviews: {
            title: 'Revisiones de proveedores',
            page_title: 'Reseñas de proveedores',
            new: 'Nuevo revisar',
            vendor_id: 'Proveedor ID',
            filter_by_outcome: 'Filtrar por resultado de revisión',
            per_page: 'Reseñas por página',
            kpi_summary: 'Revisar el resumen de KPI',
            review_outcome_label: 'Resultado de la revisión',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Editar resultado de la revisión',
            reviews_loaded: '{count} reseñas de proveedores cargadas',
            reviewer: 'Revisor',
            review_type: 'Revisar Tipo',
            outcome: 'Resultado',
            findings: 'Hallazgos',
            no_reviews: 'No se encontraron vendor reviews',
            load_kpis_error: 'Error al fetch vendor review kpis. Por favor, inténtelo de nuevo.',
            load_error: 'Error al fetch vendor reviews. Por favor, inténtelo de nuevo.',
            update_error: 'No se pudo actualizar la revisión del proveedor',
            create_error: 'Error al create vendor review. Por favor, inténtelo de nuevo.',
            total_reviewed: 'Total Proveedores Reviewed',
            total_reviews: 'Total de revisiones',
            satisfactory: 'Satisfactorio',
            concerns_identified: 'Preocupaciones identificadas',
            remediation_required: 'Remediación Obligatorio',
            escalated: 'Escalado',
            program_id: 'ID del programa',
            period_id: 'Período ID',
            task_id: 'Tarea ID',
            recommendations: 'Recomendaciones',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Señales de Riesgo',
            page_title: 'Señales de riesgo',
            new_signal: 'Nueva señal de riesgo',
            signal_type: 'Tipo de señal',
            severity: 'Gravedad',
            description: 'Descripción',
            source: 'Fuente',
            vendor_id: 'ID del proveedor',
            certification_expiry: 'Vencimiento de certificación',
            breach_notification: 'Notificación de violación',
            financial_instability: 'Inestabilidad financiera',
            compliance_change: 'Cambio de cumplimiento',
            custom: 'Personalizado',
            active: 'Activo',
            acknowledged: 'Reconocido',
            resolved: 'Resuelto',
            no_signals: 'No se encontraron señales de riesgo',
            source_external_feed: 'Fuente externa',
            source_manual: 'manuales',
            source_integration: 'Integración',
            source_regulatory: 'Regulatorio',
            source_media: 'Medios',
            trigger_reassessment_prompt: 'Esta señal es de alta gravedad. ¿Iniciar una reevaluación del proveedor ahora?',
            reassessment_triggered: 'Reevaluación iniciada.',
            reassessment_trigger_error: 'No se pudo iniciar la reevaluación.',
        },
        reassessments: {
            title: 'Tareas de Reevaluación',
            page_title: 'Tareas de reevaluación',
            trigger: 'Iniciar reevaluación',
            trigger_success: 'Reevaluación iniciada con éxito',
            reason: 'Motivo',
            deadline: 'Fecha límite',
            assigned_to: 'Asignado a',
            vendor_id: 'ID del proveedor',
            outcome: 'Resultado',
            new_risk_level: 'Nuevo nivel de riesgo',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Completar reevaluación',
            complete_success: 'Reevaluación completada con éxito',
            pending: 'Pendiente',
            in_progress: 'En progreso',
            completed: 'Completado',
            cancelled: 'Cancelado',
            no_tasks: 'No se encontraron tareas de reevaluación',
            risk_trend: 'Tendencia de riesgo',
            notes: 'Notas',
        },
        portfolio: {
            title: 'Portafolio de Riesgos',
            page_title: 'Portafolio de Riesgos',
            dashboard: 'Panel del portafolio',
            vendor_id: 'ID del proveedor',
            total_vendors: 'Total de proveedores',
            average_score: 'Puntuación media de riesgo',
            high_risk_count: 'Proveedores de alto riesgo',
            trend_direction: 'Dirección de la tendencia',
            improved: 'Mejorado',
            stable: 'Estable',
            degraded: 'Degradado',
            vendor_trend: 'Tendencia de proveedores',
            outstanding_questionnaires: 'Cuestionarios pendientes',
            overdue_assessments: 'Evaluaciones vencidas',
            no_outstanding: 'Sin cuestionarios pendientes',
            no_overdue: 'Sin evaluaciones vencidas',
            due_date: 'Fecha de vencimiento',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Registro de Aceptación de Riesgos',
            page_title: 'Registro de Aceptación de Riesgos',
            new_acceptance: 'Nueva aceptación de riesgo',
            vendor_id: 'ID del proveedor',
            risk_level: 'Nivel de riesgo',
            conditions: 'Condiciones',
            justification: 'Justificación',
            expiry_date: 'Fecha de vencimiento',
            create_success: 'Aceptación de riesgo creada con éxito',
            review_condition: 'Revisar condición',
            condition_met: 'Condición cumplida',
            condition_not_met: 'Condición no cumplida',
            review_success: 'Condición revisada con éxito',
            export_register: 'Exportar registro',
            active: 'Activo',
            expired: 'Expirado',
            revoked: 'Revocado',
            no_acceptances: 'No se encontraron aceptaciones de riesgo',
            met_count: '{met}/{total} cumplió',
        },
        review_routing: {
            title: 'Enrutamiento de Revisiones',
            page_title: 'Revisar la ruta',
            route_to_reviewers: 'Enviar a revisores',
            route_success: 'Enviado a revisores con éxito',
            vendor_id: 'ID del proveedor',
            reviewer_id: 'ID del revisor',
            reviewer_role: 'Rol del revisor',
            questionnaire_id: 'ID del cuestionario',
            review_status: 'Estado de la revisión',
            reviewers_label: 'Revisores (user_id, rol por línea)',
            pending: 'Pendiente',
            in_review: 'En revisión',
            completed: 'Completado',
            no_routing: 'No se encontró enrutamiento de revisiones',
            primary_reviewer: 'Revisor principal',
            secondary_reviewer: 'Revisor secundario',
            approver: 'Aprobador',
        },
        onboarding: {
            title: 'Casos de Incorporación de Proveedores',
            page_title: 'Casos de Incorporación de Proveedores',
            new_case: 'Nuevo caso de incorporación',
            case_detail: 'Caso de Incorporación',
            open_mps_dossier_filing: 'Abrir presentación MPS',
            dossier_filing_description: 'Crea una nueva presentación MPS pre-rellenada desde este caso de onboarding. La presentación se vinculará al caso.',
            dossier_filing_dpia_id: 'ID DPIA',
            dossier_filing_dpia_id_placeholder: 'p. ej. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Título de la presentación',
            dossier_filing_form_type: 'Tipo de formulario',
            dossier_filing_submit: 'Crear presentación',
            dossier_filing_validation_error: 'Se requieren ID DPIA y título.',
            dossier_filing_created: 'Presentación MPS creada',
            dossier_filing_error: 'No se pudo crear la presentación MPS.',
            linked_questionnaire_due_date: 'Fecha límite',
            linked_questionnaire_assignee: 'Asignado a',
            linked_questionnaire_submitted_at: 'Enviado el',
            score_last_scored: 'Última puntuación',
            score_dimensions: 'Dimensiones',
            linked_contract_document_type: 'Tipo de documento',
            linked_contract_effective_date: 'Fecha de vigencia',
            linked_contract_expiry_date: 'Fecha de vencimiento',
            linked_sub_processor_countries: 'Países de procesamiento',
            linked_sub_processor_last_assessment: 'Última evaluación',
            linked_sub_processor_next_assessment: 'Próxima evaluación',
            case_id: 'ID del caso',
            vendor: 'Proveedor',
            case_type: 'Tipo de caso',
            service_description: 'Descripción del servicio',
            data_categories: 'Categorías de datos',
            systems_accessed: 'Sistemas accedidos',
            jurisdiction: 'Jurisdicción',
            hosting_model: 'Modelo de alojamiento',
            controller_role: 'Rol de responsable/encargado',
            status_pipeline: 'Pipeline de estado',
            tabs_label: 'Secciones del caso de incorporación',
            per_page: 'Casos por página',
            filter_cases: 'Filtrar casos de incorporación',
            cases_count: 'casos',
            cases_loaded: 'casos de incorporación cargados',
            no_cases: 'No se encontraron casos de incorporación.',
            vendor_lookup_placeholder: 'Introduzca ID del proveedor',
            data_categories_placeholder: 'p.ej., datos_personales, financieros, salud',
            systems_placeholder: 'p.ej., CRM, ERP, Sistema HR',
            status_open: 'Abierto',
            status_in_progress: 'En progreso',
            status_awaiting_approval: 'Esperando aprobación',
            status_approved: 'Aprobado',
            status_remediation_required: 'Remediación requerida',
            status_closed: 'Cerrado',
            type_new_vendor: 'Nuevo proveedor',
            type_service_change: 'Cambio de servicio',
            type_renewal: 'Renovación',
            type_reassessment: 'Reevaluación',
            tab_overview: 'Resumen',
            tab_due_diligence: 'Diligencia debida',
            tab_security_assessment: 'Evaluación de seguridad',
            tab_contractual_controls: 'Controles contractuales',
            tab_sub_processors: 'Subprocesadores',
            tab_approval_gates: 'Puertas de aprobación',
            tab_evidence: 'Evidencia',
            tab_placeholder: 'El contenido de esta sección se completará durante el proceso de incorporación.',
            completion_not_started: 'No iniciado',
            completion_in_progress: 'En progreso',
            completion_completed: 'Completado',
            completion_requiring_attention: 'Requiere atención',
            gate_name: 'Nombre de la puerta',
            approver: 'Aprobador',
            decision: 'Decisión',
            comments: 'Comentarios',
            decide: 'Tomar decisión',
            decision_approved: 'Aprobado',
            decision_rejected: 'Rechazado',
            decision_deferred: 'Diferido',
            decision_pending: 'Pending',
            no_approvals: 'No hay puertas de aprobación configuradas para este caso.',
            approval_success: 'Decisión de aprobación registrada correctamente',
            approval_error: 'No se pudo registrar la decisión de aprobación',
            transition_status: 'Transitar estado',
            confirm_transition: 'Transitar caso de "{from}" a "{to}"?',
            transition_success: 'Estado del caso transitado correctamente',
            transition_error: 'No se pudo transitar el estado del caso',
            create_success: 'Caso de incorporación creado correctamente',
            create_error: 'No se pudo crear el caso de incorporación',
            load_error: 'No se pudieron cargar los casos de incorporación',
            detail_error: 'No se pudieron cargar los detalles del caso',
            export: 'Exportar',
            export_error: 'No se pudo exportar el caso de incorporación',
            evidence_tab_description: 'Sube y exporta evidencias para el caso de incorporación.',
            upload_evidence_label: 'Subir evidencia',
            attached_evidence_heading: 'Evidencias adjuntas',
            no_evidence_attached: 'Aún no hay evidencias adjuntas.',
            export_bundle_button: 'Exportar paquete de evidencias',
            load_attachments_error: 'No se pudieron cargar las evidencias.',
            linked_questionnaires_heading: 'Cuestionarios vinculados',
            no_linked_questionnaires: 'Sin cuestionarios vinculados.',
            linked_scores_heading: 'Puntuaciones vinculadas',
            no_linked_scores: 'Sin puntuaciones vinculadas.',
            latest_score_label: 'Última puntuación',
            linked_contracts_heading: 'Contratos vinculados',
            no_linked_contracts: 'Sin contratos vinculados.',
            linked_sub_processors_heading: 'Subprocesadores vinculados',
            no_linked_sub_processors: 'Sin subprocesadores vinculados.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Marcar como completado',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Registro de Subprocesadores',
            page_title: 'Registro de Subprocesadores',
            new_record: 'Nuevo subprocesador',
            edit_record: 'Editar subprocesador',
            onboarding_case: 'Caso de incorporación',
            services: 'Servicios proporcionados',
            data_categories: 'Categorías de datos',
            processing_countries: 'Países de procesamiento',
            dpa_chain_status: 'Estado de cadena DPA',
            certifications: 'Certificaciones',
            last_assessment: 'Última evaluación',
            next_assessment: 'Próxima evaluación',
            change_history: 'Historial de cambios',
            history: 'Historial',
            changed_by: 'Cambiado por',
            comma_separated: 'Valores separados por comas',
            filter_records: 'Filtrar registros de subprocesadores',
            filter_by_dpa: 'Filtrar por estado de cadena DPA',
            all_dpa_statuses: 'Todos los estados DPA',
            per_page: 'Registros por página',
            records_count: 'subprocesadores',
            records_loaded: 'registros de subprocesadores cargados',
            no_records: 'No se encontraron registros de subprocesadores.',
            no_history: 'No hay historial de cambios disponible.',
            dpa_active: 'Activo',
            dpa_pending: 'Pendiente',
            dpa_expired: 'Expirado',
            dpa_not_required: 'No requerido',
            create_success: 'Subprocesador creado correctamente',
            create_error: 'No se pudo crear el subprocesador',
            update_success: 'Subprocesador actualizado correctamente',
            update_error: 'No se pudo actualizar el subprocesador',
            load_error: 'No se pudo cargar el registro de subprocesadores',
            history_error: 'No se pudo cargar el historial de cambios',
            export_csv: 'Exportar CSV',
            export_json: 'Exportar JSON',
            export_error: 'No se pudieron exportar los datos del subprocesador',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Panel de orquestación',
            runs: 'Ejecuciones de orquestación',
            tasks: 'Tareas de ejecución',
            templates: 'Plantillas de lista de verificación',
            vendor_requests: 'Solicitudes de proveedores',
            proof_chain: 'Cadena de pruebas',
            dsr_integration: 'Integración DSR',
            vendor_verification: 'Verificación de proveedores',
            completeness: 'Panel de completitud',
        },
        dashboard: {
            title: 'Panel de orquestación',
            loading: 'Cargando dashboard data...',
            active_runs: 'Ejecuciones activas',
            completion_rate: 'Tasa de finalización',
            avg_completion_time: 'Tiempo medio de finalización',
            overdue_tasks: 'Vencido Tareas',
            vendor_compliance: 'Proveedor Cumplimiento',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Ejecuciones recientes',
            no_recent_runs: 'Sin ejecuciones de orquestación recientes.',
            run_id: 'Ejecutar ID',
            ticket_id: 'ID del ticket',
            progress: 'Progreso',
            systems: 'Sistemas',
            vendors: 'Proveedores',
            load_error: 'No se pudo cargar el panel de orquestación. Por favor inténtalo de nuevo.',
            aria_table: 'Ejecuciones de orquestación recientes',
        },
        runs: {
            title: 'Ejecuciones de orquestación',
            new: 'Nuevo ejecutar',
            run_id: 'Ejecutar ID',
            ticket_id: 'ID del ticket',
            progress: 'Progreso',
            systems: 'Sistemas',
            vendors: 'Proveedores',
            data_subject_ref: 'Datos Sujeto Ref',
            data_categories: 'Categorías de datos',
            enable_proof_chain: 'Habilitar cadena de prueba',
            start: 'Iniciar',
            started: 'Ejecución iniciada',
            mark_ready: 'Marcar listo',
            marked_ready: 'Ejecutar marcado como listo',
            complete: 'completo',
            complete_exceptions: 'Completo con excepciones',
            pause: 'Pausa',
            resume: 'Currículum',
            cancel: 'Cancelar',
            no_runs: 'No se encontraron orchestration runs',
            load_error: 'No se pudieron cargar ejecuciones de orquestación. Por favor inténtalo de nuevo.',
            create_error: 'No se pudo crear la ejecución de orquestación',
            aria_filter_status: 'Filtrar por estado de ejecución',
            aria_table: 'Ejecuciones de orquestación',
            placeholder_ticket_id: 'por ejemplo, TKT-000123',
            placeholder_data_subject_ref: 'por ejemplo, DS-REF-001',
            placeholder_data_categories: 'por ejemplo, personal_data, financiero, salud',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Tareas de ejecución',
            task_id: 'Tarea ID',
            run_id: 'Ejecutar ID',
            system_name: 'Sistema Nombre',
            assigned_to: 'Asignado Hasta',
            sla_deadline: 'SLA Fecha límite',
            progress: 'Progreso',
            enter_run_id: 'Introduzca Run ID to filter...',
            select_run: 'Seleccione una ejecución para ver las tareas',
            unassigned: 'No asignado',
            overdue: 'Vencido',
            assign_task: 'Asignar Tarea',
            assignee_id: 'Asignado ID',
            update_status: 'Actualizar Estado',
            new_status: 'Nuevo estado',
            no_transitions: 'Esta tarea está en un estado final y no se puede cambiar.',
            select_status_placeholder: 'Seleccione un nuevo estado',
            skip_justification: 'Justificación de la omisión',
            failure_reason: 'Motivo del fallo',
            no_tasks: 'No se encontraron execution tasks',
            new_task: 'Nueva tarea',
            system_id: 'ID del sistema',
            data_classification: 'Clasificación de datos',
            load_error: 'Error al cargar execution tasks. Inténtelo de nuevo.',
            assign_error: 'Error al assign task',
            status_error: 'Error al actualizar el estado de la tarea',
            no_tasks_for_run: 'Esta ejecución de eliminación aún no tiene tareas de ejecución.',
            aria_filter_run_id: 'Filtrar por ID de ejecución',
            aria_filter_status: 'Filtrar por estado de tarea',
            aria_table: 'Tareas de ejecución',
            view_steps: 'Ver pasos',
            steps_modal_title: 'Pasos de ejecución',
            step_title: 'Paso',
            step_status: 'Estado del paso',
            step_notes: 'Notas',
            step_evidence: 'Archivos de evidencia',
            step_evidence_required: 'Se requiere evidencia para este paso',
            step_evidence_missing: 'Aún no se ha subido evidencia',
            step_save: 'Guardar paso',
            step_saved: 'Paso actualizado',
            step_save_error: 'No se pudo actualizar el paso',
            create_success: 'Tarea de ejecución creada',
            create_error: 'No se pudo crear la tarea de ejecución',
            step_upload_label: 'Adjuntar evidencia',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Plantillas de lista de verificación',
            new: 'Nuevo plantilla',
            template_id: 'Plantilla ID',
            system_type: 'Sistema Tipo',
            est_duration: 'Duración est.',
            steps: 'Pasos',
            action_type: 'Acción Tipo',
            instructions: 'Instrucciones',
            mandatory: 'Obligatorio',
            add_step: 'Agregar Step',
            step_n: 'Paso',
            no_templates: 'No se encontraron checklist templates',
            all_system_types: 'Todos Sistema Types',
            load_error: 'No se pudieron cargar las plantillas de lista de verificación. Por favor inténtalo de nuevo.',
            create_error: 'No se pudo crear la plantilla de lista de verificación',
            aria_filter_system_type: 'Filtrar por tipo de sistema',
            aria_filter_status: 'Filtrar por estado de plantilla',
            aria_table: 'Plantillas de lista de verificación',
            placeholder_name: 'por ejemplo, lista de verificación de eliminación de bases de datos',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Plantilla actualizada con éxito',
            create_success_msg: 'Plantilla creada con éxito',
            update_error: 'Error al actualizar la plantilla',
            activate_success: 'Plantilla activada con éxito',
            activate_error: 'Error al activar la plantilla',
            archive_success: 'Plantilla archivada con éxito',
            archive_error: 'Error al archivar la plantilla',
            clone_success: 'Plantilla clonada con éxito',
            clone_error: 'Error al clonar la plantilla',
        },
        vendor_requests: {
            title: 'Proveedor Eliminación Solicitudes',
            new: 'Nuevo solicitud',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Solicitud ID',
            vendor_name: 'Proveedor Nombre',
            vendor_id: 'Proveedor ID',
            data_subject_ref: 'Datos Sujeto Ref',
            data_categories: 'Categorías de datos',
            legal_basis: 'Base legal',
            sla_days: 'Días SLA',
            requested_completion: 'Finalización solicitada',
            communications: 'Comunicaciones',
            total_requests: 'Total Solicitudes',
            no_requests: 'No se encontraron vendor deletion requests',
            filter_vendor_id: 'Filtrar por Proveedor ID...',
            change_status: 'Cambiar Estado',
            new_status: 'Nuevo Estado',
            actions: 'Acciones',
            load_error: 'No se pudieron cargar las solicitudes de eliminación de proveedores. Por favor inténtalo de nuevo.',
            create_error: 'No se pudo crear la solicitud de eliminación del proveedor',
            dashboard_error: 'No se pudo cargar el panel de eliminación de proveedores. Por favor inténtalo de nuevo.',
            aria_filter_status: 'Filtrar por estado de solicitud',
            aria_filter_vendor_id: 'Filtrar por ID de proveedor',
            aria_table: 'Solicitudes de eliminación de proveedores',
            placeholder_vendor_id: 'por ejemplo, VND-001',
            placeholder_vendor_name: 'por ejemplo, Acme Analytics',
            placeholder_data_subject_ref: 'por ejemplo, DS-REF-001',
            placeholder_data_categories: 'por ejemplo, personal_data, análisis',
            placeholder_legal_basis: 'por ejemplo, PDPL Artículo 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Visor de cadena de prueba',
            enter_run_id: 'Introduzca ejecutar id...',
            load_chain: 'Cargar cadena',
            verify_chain: 'Verificar cadena',
            verifying: 'Verificando...',
            chain_valid: 'La cadena es válida',
            chain_invalid: 'La cadena está rota',
            chain_length: 'Longitud de cadena',
            broken_at_index: 'Roto en el índice',
            no_proof_records: 'No se encontraron registros de prueba para esta ejecución.',
            proof_id: 'ID de prueba',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de la entidad',
            event_hash: 'Hash del evento',
            previous_hash: 'Hash anterior',
            attestation: 'Atestación',
            attestation_actor: 'Realizado por',
            attestation_signed_at: 'Firmado el',
            attestation_signature: 'Firma',
            attestation_evidence_refs: 'Referencias de evidencia',
            run_field_label: 'Ejecución de eliminación',
            select_run: 'Seleccione una ejecución de eliminación…',
            aria_select_run: 'Ejecución de eliminación para la búsqueda de la cadena de pruebas',
            chain_broken_here: 'Cadena rota aquí',
            load_error: 'No se pudo cargar la cadena de prueba. Por favor inténtalo de nuevo.',
            verify_error: 'No se pudo verificar la cadena de prueba. Por favor inténtalo de nuevo.',
            aria_run_id: 'Ejecutar ID para búsqueda de cadena de prueba',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Cambiar estado',
            request_type: 'Tipo de solicitud',
            data_subject_hint: 'Identificador del interesado cuyos datos se eliminarán (p. ej., correo o ID de cliente). Suele coincidir con el solicitante del caso DSR seleccionado.',
            title: 'Integración de eliminación DSR',
            create_run: 'Crear ejecución de eliminación DSR',
            dsr_case_id: 'ID de caso DSR',
            data_subject_id: 'ID del interesado',
            systems: 'Sistemas objetivo',
            route_to_department: 'Enviar al departamento',
            confirm_deletion: 'Confirmar eliminación',
            no_runs: 'No se encontraron ejecuciones de eliminación DSR',
            status: 'Estado de eliminación',
            deadline: 'Fecha límite',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Verificación de eliminación de proveedores',
            create_followup: 'Crear seguimiento',
            deadline: 'Plazo de confirmación',
            escalation_contact: 'Contacto de escalación',
            escalate: 'Escalar',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'No se encontraron seguimientos de proveedores',
            vendor_request: 'Solicitud de proveedor',
            vendor: 'Proveedor',
            unknown_vendor: 'Proveedor desconocido',
            overdue_badge: 'Vencido',
            mark_confirmed: 'Marcar como confirmado',
            mark_confirmed_for: 'Marcar la eliminación como confirmada para {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Eliminación de proveedor confirmada',
            confirmation_failed: 'No se pudo confirmar la eliminación del proveedor',
            select_request: 'Seleccione una solicitud de proveedor…',
            escalation_contact_placeholder: 'p. ej. dpo@vendor.example',
            create_success: 'Seguimiento de proveedor creado correctamente',
            create_failed: 'No se pudo crear el seguimiento de proveedor',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Eliminación de proveedor escalada correctamente',
            escalation_failed: 'Error al escalar la eliminación de proveedor',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Completitud de eliminación',
            check_conflicts: 'Comprobar conflictos de retención',
            no_conflicts: 'No se encontraron conflictos de retención',
            conflict_resolution: 'Resolución de conflictos',
            extend_retention: 'Ampliar retención',
            proceed_deletion: 'Proceder con la eliminación',
            escalate_conflict: 'Escalar',
            resolve: 'Resolver conflicto',
            data_subject_id: 'ID del interesado',
            placeholder_data_subject_id: 'Introduzca el ID del interesado',
            data_subject_hint: 'Identificador de la persona cuya eliminación está verificando (p. ej., correo, ID de cliente o referencia de DSR). Elija un sujeto conocido o escriba uno.',
            guidance: 'Introduzca o seleccione un ID de sujeto de datos, opcionalmente elija sistemas y ejecute la comprobación para ver la integridad de la eliminación y los conflictos de retención.',
            completion: 'Finalización de eliminación',
            pending_items: 'Elementos pendientes',
            system: 'Sistema',
            policy: 'Política',
            retention_end: 'Fin de retención',
            completed_items: 'Elementos Completados',
            last_review_label: 'Última revisión',
            days_ago: 'hace dias',
            review_overdue: 'Se superó el ciclo de revisión. Es necesario volver a revisarlo.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Pendiente',
            planning: 'Planificación',
            ready: 'Listo',
            in_progress: 'En Progreso',
            paused: 'En Pausa',
            completed: 'Completado',
            completed_with_exceptions: 'Completado con Excepciones',
            cancelled: 'Cancelado',
        },
        task_status_labels: {
            pending: 'Pendiente',
            assigned: 'Asignado',
            in_progress: 'En Progreso',
            awaiting_verification: 'Esperando Verificación',
            verified: 'Verificado',
            failed: 'Fallido',
            skipped: 'Omitido',
        },
        system_type_labels: {
            database: 'Base de Datos',
            saas_application: 'Aplicación SaaS',
            file_storage: 'Almacenamiento',
            email_system: 'Sistema de Correo',
            analytics_platform: 'Plataforma Analítica',
            backup_system: 'Sistema de Respaldo',
            vendor_managed: 'Gestionado por Proveedor',
            custom: 'Personalizado',
        },
        template_status_labels: {
            draft: 'Borrador',
            active: 'Activo',
        },
        action_type_labels: {
            manual_check: 'Verificación Manual',
            api_call: 'Llamada API',
            sql_query: 'Consulta SQL',
            script_execution: 'Ejecución de Script',
            file_deletion: 'Eliminación de Archivo',
            verification: 'Verificación',
            notification: 'Notificación',
            approval: 'Aprobación',
        },
        request_status_labels: {
            draft: 'Borrador',
            sent: 'Enviado',
            acknowledged: 'Confirmado',
            in_progress: 'En Progreso',
            completed: 'Completado',
            overdue: 'Vencido',
            escalated: 'Escalado',
            disputed: 'Disputado',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Iniciación',
            data_deletion: 'Eliminación de Datos',
            completion: 'Finalización',
            verification: 'Verificación',
            rollback: 'Reversión',
        },
        count_labels: {
            one_template: '1 plantilla',
            n_templates: '{n} plantillas',
            one_run: '1 ejecución',
            n_runs: '{n} ejecuciones',
            one_task: '1 tarea',
            n_tasks: '{n} tareas',
            one_request: '1 solicitud',
            n_requests: '{n} solicitudes',
        },
        aria: {
            completion_rate: 'Tasa de finalización: {pct}%',
            vendor_compliance: 'Cumplimiento del proveedor: {pct}%',
            run_progress: 'Progreso: {pct}%',
            run_progress_systems: 'Progreso: {completed} de {total} sistemas procesados',
            chain_broken: 'Cadena rota en este registro',
            chain_valid: 'Cadena válida en este registro',
            chain_unknown: 'Estado de cadena desconocido',
            escalate_vendor: 'Escalar {id}',
            resolve_conflict: 'Resolver conflicto para {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'La integridad de la eliminación debe revisarse al menos cada 365 días. Los registros que superen este umbral se marcan para una nueva revisión obligatoria para mantener el cumplimiento normativo.',
            runs_data_d1_lifecycle_info: 'Los estados de ejecución de la orquestación se asignan al ciclo de vida de DATA-D1: planificación = catalogar, listo = clasificar, in_progress = aplicar reglas, completado = evidencia generada, completed_with_exceptions = revisión requerida. Cada transición se registra en la cadena de prueba.',
            vendor_record_linkage_info: 'Cada solicitud de eliminación de proveedor está vinculada a su registro de proveedor a través de vendor_id. La confirmación de finalización requiere evidencia criptográfica de la cadena de prueba antes de que la solicitud pueda marcarse como completada.',
            templates_internal_classification_info: 'Todas las plantillas de listas de verificación de eliminación se clasifican como documentos organizativos internos. No llevan identificaciones de formularios reglamentarios oficiales y no deben presentarse como presentaciones reglamentarias.',
            proof_chain_evidence_info: 'Cada registro de prueba captura el sujeto/categoría de los datos, la marca de tiempo, el actor, la base legal y un hash SHA-256. Juntos, estos campos producen evidencia de eliminación criptográfica que satisface los requisitos de auditoría reglamentarios.',
            retention_data_law_info: 'Los programas de retención en este panel se derivan de la clasificación de la Ley de Datos asignada a cada categoría de datos. Cuando cambia una clasificación, las reglas de retención vinculadas se actualizan automáticamente para garantizar la alineación regulatoria.',
            dsr_erasure_route_info: 'Las ejecuciones de borrado de DSR están vinculadas al caso de Solicitud de derechos de origen. El plazo reglamentario (normalmente 30 días según GDPR Art. 17) se realiza un seguimiento automático. Los casos atrasados ​​se marcan para su escalada.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Panel de descubrimiento',
            scans: 'Escaneos de descubrimiento',
            staging_area: 'Área de preparación',
            dedup_rules: 'Reglas de deduplicación',
            classification_rules: 'Reglas de clasificación',
            suggestions: 'Cola de sugerencias',
            analytics: 'Análisis de sugerencias',
            import_templates: 'Plantillas de importación',
            connectors: 'Gestión de conectores',
            discovery_cycles: 'Ciclos de descubrimiento',
            owner_review: 'Revisión del propietario',
            classification_lineage: 'Historial de clasificación',
        },
        dashboard: {
            title: 'Panel de descubrimiento',
            loading: 'Cargando dashboard data...',
            total_scans: 'Total de escaneos',
            active_scans: 'Escaneos activos',
            completed_30d: 'Completado (30d)',
            failed_30d: 'Falló (30d)',
            systems_discovered: 'Sistemas descubiertos',
            pending_staged_records: 'Pendiente Staged Registros',
            recent_scans: 'Escaneos recientes',
            no_recent_scans: 'Sin escaneos recientes.',
            scan_id: 'Escanear ID',
            discovered: 'Descubierto',
            view_all_scans: 'Ver All Scans',
            staging_area: 'Área de staging',
            suggestion_queue: 'Cola de sugerencias',
            records_reviewed: 'Registros revisados',
            records_approved: 'Registros aprobados',
            exceptions_pending: 'Excepciones pendientes',
            view_as_chart: 'Ver como gráfico',
            view_as_table: 'Ver como tabla',
        },
        scans: {
            title: 'Escaneos de descubrimiento',
            new: 'Nuevo escanear',
            scan_id: 'Escanear ID',
            scan_type: 'Escanear Tipo',
            progress: 'Progreso',
            discovered: 'Descubierto',
            no_scans: 'No se encontraron discovery scans',
            all_scan_types: 'Todos Escanear Types',
            source_config: 'Config. de origen (JSON)',
            invalid_json: 'JSON inválido en la configuración de origen',
            load_error: 'Error al load discovery scans. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create discovery scan. Por favor, inténtelo de nuevo.',
            change_status: 'Cambiar estado',
            new_status: 'Nuevo estado',
            actions: 'Acciones',
            type_endpoint: 'Punto final',
            type_spreadsheet_import: 'Importación de hoja de cálculo',
            type_cmdb_sync: 'Sincronización CMDB',
            type_cloud_sync: 'Sincronización en la nube',
            status_pending: 'Pendiente',
            status_running: 'En ejecución',
            status_completed: 'Completado',
            status_failed: 'Fallido',
            status_cancelled: 'Cancelado',
            source_config_placeholder: '{"host": "...", "puerto": 443}',
            adapter_not_implemented_toast: 'Este tipo de análisis aún no está disponible. Solo se pueden crear análisis de puntos finales.',
            adapter_not_implemented_warning: 'Aún no disponible. No se pueden crear análisis de este tipo hasta que se publique su compatibilidad.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Área de preparación',
            record_id: 'Registro ID',
            source_scan: 'Escaneo fuente',
            entity_type: 'Tipo de entidad',
            suggested_name: 'Nombre sugerido',
            confidence: 'Confianza',
            no_records: 'No se encontraron staged records',
            promote: 'Promover',
            discard: 'Descartar',
            review: 'Revisar',
            merge: 'Fusionar',
            merge_title: 'Fusionar registro',
            merge_target_id: 'ID del objetivo de fusión',
            reject_reason_placeholder: 'Motivo del rechazo...',
            promotion_status: 'Estado de promoción',
            dedup_status: 'Estado de dedup.',
            all_source_types: 'Todos los tipos de fuente',
            all_promotion_statuses: 'Todos los estados de promoción',
            all_dedup_statuses: 'Todos los estados de dedup.',
            load_error: 'Error al load staged records. Por favor, inténtelo de nuevo.',
            promote_error: 'Error al promote record. Por favor, inténtelo de nuevo.',
            reject_error: 'Error al reject record. Por favor, inténtelo de nuevo.',
            merge_error: 'Error al merge record. Por favor, inténtelo de nuevo.',
            review_error: 'Error al mark record for review. Por favor, inténtelo de nuevo.',
            bulk_promote_error: 'Error al bulk promote records. Por favor, inténtelo de nuevo.',
            bulk_reject_error: 'Error al bulk reject records. Por favor, inténtelo de nuevo.',
            service_type_web_server: 'Servidor web',
            service_type_database: 'Base de datos',
            service_type_api_gateway: 'Puerta de enlace API',
            service_type_storage: 'Almacenamiento',
            service_type_authentication: 'Autenticación',
            service_type_analytics: 'Analítica',
            service_type_unknown: 'Desconocido',
            source_type_endpoint_scan: 'Escaneo de punto final',
            source_type_spreadsheet: 'Hoja de cálculo',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Inventario en la nube',
            promotion_status_new: 'Nuevo',
            promotion_status_under_review: 'En revisión',
            promotion_status_promoted: 'Promovido',
            promotion_status_rejected: 'Rechazado',
            promotion_status_merged: 'Fusionado',
            dedup_status_unmatched: 'Sin coincidencia',
            dedup_status_potential_match: 'Coincidencia potencial',
            dedup_status_exact_match: 'Coincidencia exacta',
            dedup_status_resolved: 'Resuelto',
            confirm_bulk_reject: '¿Está seguro de que desea rechazar los registros seleccionados?',
            scan_batch_id: 'ID de lote',
            promote_to_registry_title: 'Promocionar al Registro de Datos',
            promote_modal_instructions: 'Crea un elemento de Registro de datos con collection_method: vínculo observado y de procedencia con este registro de descubrimiento. El elemento comienza en estado de borrador para su revisión.',
            promote_label_category: 'categoría',
            promote_label_name_override: 'Nombre (anulación opcional)',
            placeholder_name_override: 'El valor predeterminado es el nombre/nombre de host del registro de descubrimiento.',
            promote_label_sensitivity: 'Sensibilidad propuesta',
            inherit_from_category: 'Heredar de categoría',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Reglas de deduplicación',
            new: 'Nuevo Rule',
            rule_name: 'Nombre de la regla',
            match_fields: 'Campos de coincidencia',
            threshold: 'Umbral',
            no_rules: 'No se encontraron dedup rules',
            seed_defaults: 'Valores iniciales predeterminados',
            match_type: 'Tipo de coincidencia',
            similarity_threshold: 'Umbral de similitud',
            field_name: 'Campo Nombre',
            load_error: 'Error al load dedup rules. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create dedup rule. Por favor, inténtelo de nuevo.',
            toggle_error: 'Error al toggle dedup rule. Por favor, inténtelo de nuevo.',
            seed_error: 'Error al seed defaults. Por favor, inténtelo de nuevo.',
            priority: 'Prioridad',
            enabled_status: 'Habilitado',
            disabled_status: 'Discapacitado',
            default_badge: 'Predeterminado',
            enable: 'Habilitar',
            disable: 'Desactivar',
            match_type_exact: 'Exacto',
            match_type_fuzzy: 'Aproximado',
            match_type_regex: 'expresión regular',
            match_type_ip_range: 'Rango IP',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Reglas de clasificación',
            new: 'Nuevo Rule',
            rule_name: 'Nombre de la regla',
            pattern: 'Patrón',
            classification: 'Clasificación',
            no_rules: 'No se encontraron classification rules',
            seed_defaults: 'Valores iniciales predeterminados',
            run_rules_now: 'Ejecutar reglas ahora',
            run_rules_success: 'Se generaron {count} nuevas sugerencias de clasificación',
            run_rules_no_results: 'No se generaron nuevas sugerencias: todas las reglas coincidentes ya tienen sugerencias pendientes',
            run_rules_error: 'No se pudieron ejecutar las reglas de clasificación. Por favor inténtalo de nuevo.',
            taxonomy_id: 'Taxonomía ID',
            level_id: 'Nivel ID',
            condition_logic: 'Lógica de condición',
            weight: 'Peso',
            conditions: 'Condiciones',
            condition_field: 'Campo',
            condition_operator: 'Operador',
            condition_value: 'Valor',
            effectiveness: 'Eficacia',
            match_count: 'Coincidencias',
            approval_count: 'Aprobaciones',
            rejection_count: 'Rechazos',
            filter_taxonomy: 'Filtrar por Taxonomía ID...',
            load_error: 'Error al load classification rules. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create classification rule. Por favor, inténtelo de nuevo.',
            seed_error: 'Error al seed defaults. Por favor, inténtelo de nuevo.',
            toggle_error: 'Error al toggle rule. Por favor, inténtelo de nuevo.',
            priority: 'Prioridad',
            enabled_status: 'Habilitado',
            disabled_status: 'Discapacitado',
            enable: 'Habilitar',
            disable: 'Desactivar',
            logic_and: 'Y',
            logic_or: 'O',
            operator_equals: 'Igual a',
            operator_contains: 'Contiene',
            operator_matches_regex: 'Coincide con Regex',
            operator_in_list: 'En la lista',
            operator_not_equals: 'No es igual a',
            operator_not_contains: 'No contiene',
            enabled_label: 'Activado',
            disabled_label: 'Desactivado',
            taxonomy_label: 'Taxonomía',
            operator_label: 'Operador',
            value_label: 'Valor',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Cola de sugerencias',
            entity_type: 'Tipo de entidad',
            suggested_name: 'Nombre sugerido',
            source: 'Fuente',
            confidence: 'Confianza',
            accept: 'Aceptar',
            reject: 'Rechazar',
            no_suggestions: 'Sin sugerencias en cola.',
            modify_approve: 'Modificar y aprobar',
            delegate: 'Delegar',
            delegate_user_id: 'ID del usuario delegado',
            new_level_id: 'Nuevo nivel id',
            reject_reason_placeholder: 'Motivo del rechazo...',
            current_level: 'Actual Nivel',
            suggested_level: 'Nivel sugerido',
            filter_taxonomy: 'Filtrar por Taxonomía ID...',
            all_confidence: 'Todas las bandas de confianza',
            load_error: 'Error al load suggestions. Por favor, inténtelo de nuevo.',
            approve_error: 'Error al approve suggestion. Por favor, inténtelo de nuevo.',
            reject_error: 'Error al reject suggestion. Por favor, inténtelo de nuevo.',
            modify_error: 'Error al modify & approve suggestion. Por favor, inténtelo de nuevo.',
            delegate_error: 'Error al delegate suggestion. Por favor, inténtelo de nuevo.',
            bulk_approve_error: 'Error al bulk approve suggestions. Por favor, inténtelo de nuevo.',
            bulk_reject_error: 'Error al bulk reject suggestions. Por favor, inténtelo de nuevo.',
            suggestion_type_new_label: 'Etiqueta nueva',
            suggestion_type_validation: 'Validación',
            suggestion_type_reclassification: 'Reclasificación',
            status_pending: 'Pendiente',
            status_approved: 'Aprobado',
            status_rejected: 'Rechazado',
            status_superseded: 'Reemplazado',
            confidence_high: 'Alta',
            confidence_medium: 'Media',
            confidence_low: 'Baja',
            confirm_bulk_reject: '¿Está seguro de que desea rechazar las sugerencias seleccionadas?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Todas las taxonomías',
        },
        analytics: {
            title: 'Análisis de sugerencias',
            total_suggestions: 'Total de sugerencias',
            accepted: 'Aceptado',
            rejected: 'Rechazado',
            pending: 'Pendiente',
            acceptance_rate: 'Tasa de aceptación',
            by_entity_type: 'Por tipo de entidad',
            by_source: 'Por fuente',
            avg_decision_time: 'Tiempo medio de decisión',
            rule_effectiveness: 'Eficacia de la regla',
            by_status: 'Por Estado',
            by_confidence: 'Por banda de confianza',
            load_error: 'Error al load suggestion analytics. Por favor, inténtelo de nuevo.',
            confidence_high: 'Alta',
            confidence_medium: 'Media',
            confidence_low: 'Baja',
            by_confidence_band: 'Por banda de confianza',
            approval_rate: 'Tasa de aprobación',
            by_label_state: 'Por estado de etiqueta',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Plantillas de importación',
            new: 'Nuevo plantilla',
            template_name: 'Plantilla Nombre',
            file_format: 'Formato de archivo',
            column_mappings: 'Asignaciones de columnas',
            no_templates: 'No se encontraron import templates',
            source_column: 'Columna fuente',
            target_field: 'Campo de destino',
            transform: 'Transformar',
            source_column_label: 'Columna de origen',
            target_field_label: 'Campo objetivo',
            transform_label: 'transformar',
            confirm_delete: '¿Está seguro de que desea eliminar this import template?',
            load_error: 'Error al load import templates. Por favor, inténtelo de nuevo.',
            save_error: 'Error al save import template. Por favor, inténtelo de nuevo.',
            delete_error: 'Error al delete import template. Por favor, inténtelo de nuevo.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Gestión de conectores',
            new: 'Nuevo conector',
            connector_name: 'Conector Nombre',
            connector_type: 'Tipo',
            last_sync: 'Último Sincronizar',
            no_connectors: 'No se encontraron connectors',
            cmdb_syncs: 'Sincronizaciones CMDB',
            cloud_syncs: 'Sincronizaciones cloud',
            new_sync: 'Nuevo sincronizar',
            no_syncs: 'No se encontraron syncs',
            cmdb_error: 'Error al load cmdb scans. Por favor, inténtelo de nuevo.',
            cloud_error: 'Error al load cloud scans. Por favor, inténtelo de nuevo.',
            trigger_error: 'Error al trigger sync. Por favor, inténtelo de nuevo.',
            trigger_sync_error: 'Error al trigger {type}',
            cmdb_section_title: 'Sincronizaciones CMDB',
            cloud_section_title: 'Sincronizaciones en la nube',
            column_connector: 'Conector',
            action_start: 'Iniciar',
            confirm_cancel_sync: '¿Cancelar esta sincronización?',
            start_error: 'No se pudo iniciar la sincronización',
            cancel_error: 'No se pudo cancelar la sincronización',
            type_endpoint: 'Escaneo de endpoint',
            type_spreadsheet_import: 'Importación de hoja de cálculo',
            type_cmdb_sync: 'Sincronización CMDB',
            type_cloud_sync: 'Sincronización en la nube',
        },
        discovery_cycles: {
            title: 'Ciclos de descubrimiento',
            new_cycle: 'Nuevo ciclo de descubrimiento',
            cycle_name: 'Nombre del ciclo',
            scope_type: 'Tipo de alcance',
            scope_ids: 'IDs de alcance',
            cadence_days: 'Frecuencia (días)',
            status: 'Estado',
            no_cycles: 'No se encontraron ciclos de descubrimiento',
            compare: 'Comparar ciclos',
            delta_report: 'Informe de cambios',
            new_records: 'Registros nuevos',
            changed_records: 'Registros modificados',
            removed_records: 'Registros eliminados',
            schedule: 'Programar escaneo',
            scope_organization: 'Organización',
            scope_department: 'Departamento',
            scope_system: 'Sistema',
            scope_systems: 'Sistemas',
            bulk_actions: 'Acciones masivas',
            selected: '{count} seleccionado(s)',
            bulk_create: 'Creación masiva',
            bulk_assign_scope: 'Asignar alcance masivamente',
            bulk_advance: 'Avanzar estado masivamente',
            confirm_bulk_action: 'Confirmar acción masiva',
            bulk_confirm_message: '¿Está seguro de que desea realizar esta acción?',
            cycles_affected: '{count} ciclos serán afectados',
            select_all: 'Seleccionar todo',
            select_cycle: 'Seleccionar ciclo {name}',
            from_template: 'desde plantilla',
            bulk_results: 'Resultados masivos',
            failed_label: 'fallido',
            template_id: 'ID de plantilla',
            count: 'Cantidad',
            advance_to: 'Estado objetivo',
            bulk_success: 'Operación masiva completada',
        },
        owner_review: {
            title: 'Tareas de revisión del propietario',
            assign_owner: 'Asignar al propietario',
            owner_id: 'Propietario',
            decision: 'Decisión',
            approve: 'Aprobar',
            modify: 'Modificar',
            reject: 'Rechazar',
            escalate: 'Escalar',
            no_tasks: 'No se encontraron tareas de revisión',
            bulk_resolve: 'Resolución masiva',
            exceptions: 'Elementos de excepción',
            no_exceptions: 'No se encontraron elementos de excepción',
            record_id_label: 'ID de registro',
            selected: 'seleccionado',
            notes: 'Notas',
            select_all: 'Seleccionar Todo',
            select: 'Seleccionar',
            confirm_resolve: 'Confirmar',
        },
        classification_lineage: {
            title: 'Historial de clasificación',
            link_suggestion: 'Vincular a etiqueta propuesta',
            suggestion_id: 'Sugerencia',
            proposed_label_id: 'Etiqueta propuesta',
            no_lineage: 'No se encontraron datos de historial',
            full_lineage: 'Historial completo',
            record_id_label: 'ID de registro',
            staged_record_label: 'Registro en escena',
            enter_staged_record_id: 'Introduzca el ID del registro preparado',
            scan_id_label: 'Escanear ID',
            select_record: 'Seleccionar un registro',
            select_suggestion: 'Seleccionar una sugerencia',
            select_label: 'Seleccionar una etiqueta',
        },
        common_labels: {
            n_rules: '{n} reglas',
            one_rule: '1 regla',
            n_scans: '{n} escaneos',
            one_scan: '1 escaneo',
            n_syncs: '{n} sincronizaciones',
            one_sync: '1 sincronización',
            n_records: '{n} registros',
            one_record: '1 registro',
            n_templates: '{n} plantillas',
            one_template: '1 plantilla',
            n_suggestions: '{n} sugerencias',
            one_suggestion: '1 sugerencia',
            n_selected: '{n} seleccionado',
            dashboard_loaded: 'Panel de descubrimiento cargado',
            n_scans_loaded: '{n} escaneos cargados',
            n_staged_records_loaded: '{n} registros preparados cargados',
            confirm_delete_import_template: '¿Está seguro de que desea eliminar esta plantilla de importación?',
            invalid_json_source_config: 'JSON no válido en source_config',
            scope_organization: 'Organización',
            scope_department: 'Departamento',
            scope_system: 'Sistema',
            aria_filter_scans: 'Filtrar escaneos',
            aria_filter_staged_records: 'Filtrar registros preparados',
            aria_filter_suggestions: 'Filtros',
            aria_filter_by_taxonomy_id: 'Filtrar por ID de taxonomía',
            aria_filter_by_enabled_status: 'Filtrar por estado habilitado',
            aria_filter_by_source_type: 'Filtrar por tipo de fuente',
            aria_filter_by_promotion_status: 'Filtrar por estado de promoción',
            aria_filter_by_dedup_status: 'Filtrar por estado de deduplicación',
            aria_filter_by_scan_type: 'Filtrar por tipo de escaneo',
            aria_filter_by_status: 'Filtrar por estado',
            aria_filter_by_confidence: 'Filtrar por banda de confianza',
            aria_select_all_records: 'Seleccionar todos los registros',
            aria_select_record: 'Seleccionar registro {id}',
            aria_select_all_suggestions: 'Seleccionar todas las sugerencias',
            aria_select_suggestion: 'Seleccionar sugerencia {id}',
            aria_promote_record: 'Promocionar registro {id}',
            aria_discard_record: 'Descartar registro {id}',
            aria_merge_record: 'Fusionar registro {id}',
            aria_review_record: 'Revisar registro {id}',
            aria_accept_suggestion: 'Aceptar sugerencia {id}',
            aria_reject_record: 'Rechazar registro',
            aria_merge_record_dialog: 'Fusionar registro',
            aria_create_new_scan: 'Crear nuevo escaneo',
            aria_sync_progress: 'Progreso de la sincronización: {processed} de {total} elementos procesados',
            aria_scan_progress: 'Progreso del escaneo: {processed} de {total} elementos procesados',
            aria_confidence: 'Confianza: {pct}%',
            aria_effectiveness: 'Efectividad: {pct}',
            aria_promote_n_records: 'Promocionar {n} registros seleccionados',
            aria_discard_n_records: 'Descartar {n} registros seleccionados',
            placeholder_pii_detection_rule: 'por ejemplo, regla de detección de PII',
            placeholder_tax_id: 'por ejemplo, TAX-001',
            placeholder_lvl_id: 'por ejemplo, LVL-001',
            placeholder_service_type: 'por ejemplo, service_type',
            placeholder_database: 'por ejemplo, base de datos',
            placeholder_hostname_match: 'por ejemplo, Coincidencia exacta del nombre de host',
            placeholder_hostname: 'por ejemplo, nombre de host',
            placeholder_hr_systems_csv: 'por ejemplo, sistemas de recursos humanos CSV Importar',
            placeholder_server_name: 'por ejemplo, nombre del servidor',
            placeholder_hostname_field: 'por ejemplo, nombre de host',
            placeholder_lowercase: 'por ejemplo, minúscula',
            placeholder_source_config: '{"host": "...", "puerto": 443}',
            placeholder_stg_id: 'por ejemplo, STG-000123',
            placeholder_lvl_002: 'por ejemplo, LVL-002',
            placeholder_usr_id: 'por ejemplo, USR-000123',
            decision_for: 'Decisión para {id}',
        },
        exports: {
            title: 'Exportar',
            inventory: 'Inventario de datos',
            classification_register: 'Registro de clasificación',
            raci: 'Matriz RACI',
            exceptions: 'Informe de excepciones',
            evidence_pack: 'Paquete de evidencias',
            delta_report: 'Informe de cambios',
            executive_summary: 'Resumen ejecutivo',
            export_button: 'Exportar',
            downloading: 'Descargando...',
            download_complete: 'Descarga completada.',
            download_error: 'Error al descargar la exportación. Inténtelo de nuevo.',
        },
        flags: {
            title: 'Marcadores derivados',
            pending: 'Marcadores pendientes',
            cross_border_review_needed: 'Revisión transfronteriza necesaria',
            sharing_disclosure_needed: 'Divulgación de datos compartidos necesaria',
            dpia_review_needed: 'Revisión EIPD necesaria',
            acknowledge: 'Reconocer',
            resolve: 'Resolver',
            no_flags: 'No hay marcadores derivados.',
        },
        delta: {
            title: 'Resumen de cambios',
            new_count: '{n} nuevos',
            removed_count: '{n} eliminados',
            changed_count: '{n} modificados',
            re_review_triggered: 'Revisión repetida activada',
            create_re_review: 'Crear revisión repetida',
            recent_alerts: 'Alertas de cambios recientes',
            no_alerts: 'No hay alertas de cambios recientes.',
            threshold_exceeded: 'Umbral de cambios superado (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Este panel muestra el estado de gobernanza a nivel de ciclo: sistemas analizados, registros preparados, registros revisados, registros aprobados y excepciones pendientes. Esto coincide con los criterios de éxito del líder de gobernanza: "panel de control en vivo de quién ha actualizado su inventario, clasificación y registros compartidos" (P-VN-05).',
            export_artifacts_info: 'Las exportaciones de descubrimiento incluyen todos los tipos de artefactos requeridos por UC-VN-10 y DATA-EVD-01: inventario de datos aprobado, registro de clasificación, propiedad/mapeo RACI, cola de excepciones, resumen ejecutivo, paquete de evidencia e informe delta.',
            scan_inventory_pipeline_info: 'Cada escaneo completado produce registros compatibles con la plantilla vn_data_law_data_inventory_register (requisito 88dd9877). Los activos de datos descubiertos completan automáticamente el registro de inventario de datos para satisfacer la obligación de la Ley de Datos de mantener un inventario de datos digitales.',
            connector_linkage_info: 'La configuración de cada conector debe incluir: tipo de sistema (p. ej., CMDB, plataforma en la nube), referencia del almacén de datos (base de datos, almacén de archivos, SaaS) y vinculación del proceso empresarial (UC-VN-10 registros de respaldo requeridos). Estos campos garantizan que los activos descubiertos sean rastreables hasta su sistema de origen y contexto empresarial.',
            owner_routing_info: 'Las tareas de revisión se envían al departamento o propietario de la aplicación correcto según la propiedad del sistema/datos (UC-VN-10 paso 5). El enrutamiento utiliza el registro de propiedad del sistema, no una asignación ad hoc.',
            owner_actions_audit_info: 'Los propietarios pueden aprobar, modificar, rechazar o escalar cada clasificación (UC-VN-10 paso 6). Cada acción captura el actor, la marca de tiempo y el motivo, lo que produce eventos de evidencia de auditoría de gobernanza según el requisito f12fc090 (Certificación de gobernanza anual). La pista de auditoría muestra quién revisó, qué se cambió y cuándo.',
            escalation_timers_info: 'Temporizadores de escalamiento: la fecha límite de revisión del propietario es de 5 días hábiles; la escalada de excepciones es de 48 horas (UC-VN-10 temporizadores). Los elementos no resueltos se escalan automáticamente cuando vence el plazo. Los temporizadores se pueden configurar según la política organizacional.',
            original_suggestion_preserved_info: 'La sugerencia de clasificación original de la máquina/sistema se conserva junto con cada anulación del propietario (UC-VN-10 requisito de evidencia). La sugerencia original nunca se sobrescribe y permanece visible para fines de auditoría.',
            dedup_lineage_info: 'Las reglas de deduplicación evitan entradas de inventario duplicadas cuando se descubre el mismo activo de datos en varios análisis o conectores. Los metadatos del escaneo más reciente se conservan como registro principal y, al mismo tiempo, se mantienen las referencias de linaje a todos los descubrimientos anteriores para la trazabilidad de la auditoría.',
            label_states_info: 'Analytics distingue cuatro estados de etiquetas para la revisión de gobernanza: sugerido por la máquina (pendiente), aprobado por el propietario, modificado por el propietario y rechazado. Estos estados permiten a los auditores evaluar la precisión de la automatización y el compromiso del propietario.',
            import_pipeline_info: 'Las plantillas de importación deben producir registros compatibles con el canal de descubrimiento. Los registros importados ingresan a la etapa de preparación con los mismos requisitos de metadatos que los registros escaneados: identificación del sistema de origen, ID de lote y marca de tiempo. Las asignaciones de columnas deben apuntar a los campos definidos en vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'El linaje de clasificación rastrea la ruta completa: escaneo/importación -> preparación -> revisión del propietario -> etiqueta aprobada -> entrada de registro (UC-VN-10 pasos 1-10). Cada etapa muestra el ID del registro, el actor, la marca de tiempo y la transición de estado para una trazabilidad completa de la auditoría.',
            staging_workflow_info: 'Los registros preparados se distinguen visualmente de las entradas de inventario aprobadas por su distintivo de estado de promoción (UC-VN-10 paso 3). Cada registro conserva los metadatos de procedencia: sistema de origen, ID del lote de escaneo y marca de tiempo de descubrimiento para una trazabilidad de auditoría completa.',
            suggestion_data_d1_info: 'Las sugerencias se alinean con el procedimiento de clasificación de la Ley de datos DATA-D1: (1) catalogar activos de datos, (2) clasificar por nivel de sensibilidad, (3) definir reglas de manejo. Cada sugerencia incluye dimensiones de sensibilidad e importancia según el requisito 18e4bfd1.',
            downstream_flags_info: 'Los elementos marcados para compartir, transferencia transfronteriza o tratamiento de alto riesgo desencadenan flujos de trabajo posteriores vinculados: evaluación de transferencia transfronteriza, evaluación de riesgo de intercambio y revisión DPIA (UC-VN-10 paso 8).',
            delta_detection_info: 'Los ciclos de descubrimiento admiten la detección delta según UC-VN-10 paso 11: cada ciclo muestra qué registros son nuevos, modificados o eliminados desde la última línea base aprobada. Los cambios de material (>10 % delta) activan automáticamente flujos de trabajo de nueva revisión.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Error al load. Por favor, inténtelo de nuevo.',
            save: 'Error al save. Por favor, inténtelo de nuevo.',
            fetch_escalation_log_failed: 'Error al fetch escalation log. Por favor, inténtelo de nuevo.',
            fetch_activations_failed: 'Error al fetch activations. Por favor, inténtelo de nuevo.',
            deactivate_failed: 'Error al deactivate. Por favor, inténtelo de nuevo.',
            complete_activation_failed: 'Error al complete activation. Por favor, inténtelo de nuevo.',
            fetch_playbook_templates_failed: 'Error al fetch playbook templates. Por favor, inténtelo de nuevo.',
            create_playbook_template_failed: 'Error al create playbook template. Por favor, inténtelo de nuevo.',
            clone_template_failed: 'Error al clone template. Por favor, inténtelo de nuevo.',
            update_status_failed: 'Error al actualizar el estado. Por favor, inténtelo de nuevo.',
            deprecate_template_failed: 'Error al deprecate template. Por favor, inténtelo de nuevo.',
            seed_defaults_failed: 'Error al seed defaults. Por favor, inténtelo de nuevo.',
            fetch_regulator_exports_failed: 'Error al fetch regulator exports. Por favor, inténtelo de nuevo.',
            create_regulator_export_failed: 'Error al create regulator export. Por favor, inténtelo de nuevo.',
            update_submission_status_failed: 'Error al update submission status. Por favor, inténtelo de nuevo.',
            fetch_response_metrics_failed: 'Error al fetch response metrics. Por favor, inténtelo de nuevo.',
            fetch_sla_dashboard_failed: 'Error al fetch sla dashboard. Por favor, inténtelo de nuevo.',
            fetch_sla_definitions_failed: 'Error al fetch sla definitions. Por favor, inténtelo de nuevo.',
            create_sla_definition_failed: 'Error al create sla definition. Por favor, inténtelo de nuevo.',
            seed_sla_defaults_failed: 'Error al seed sla defaults. Por favor, inténtelo de nuevo.',
            fetch_vendor_coordination_failed: 'Error al fetch vendor coordination. Por favor, inténtelo de nuevo.',
            fetch_vendor_detail_failed: 'Error al fetch vendor detail. Por favor, inténtelo de nuevo.',
            create_vendor_coordination_failed: 'Error al create vendor coordination. Por favor, inténtelo de nuevo.',
            add_communication_failed: 'Error al add communication. Por favor, inténtelo de nuevo.',
            add_info_request_failed: 'Error al add info request. Por favor, inténtelo de nuevo.',
            add_action_request_failed: 'Error al add action request. Por favor, inténtelo de nuevo.',
        },
        nav: {
            playbooks: 'Plantillas de guía operativa',
            sla_definitions: 'Definiciones de SLA',
            sla_dashboard: 'Panel de SLA',
            escalation_log: 'Registro de escalamiento',
            regulator_exports: 'Exportaciones regulatorias',
            deadlines: 'Notificación Fechas límite',
            vendor_coordination: 'Coordinación de proveedores',
            metrics: 'Métricas de respuesta',
            playbook_links: 'Enlaces de Playbook',
            regulatory_sla: 'SLA regulatorio',
            coordination_dashboard: 'Coordinación',
            evidence_chain: 'Cadena de evidencia',
            post_review: 'Revisión post-incidente',
        },
        playbooks: {
            page_title: 'Plantillas de libro de jugadas',
            activations_page_title: 'Activaciones del libro de jugadas',
            title: 'Plantillas de guía operativa',
            new: 'Nuevo plantilla',
            seed_defaults: 'Valores iniciales predeterminados',
            scenario_type: 'Tipo de escenario',
            severity_applicability: 'Aplicabilidad de la gravedad',
            all_scenario_types: 'Todos los tipos de escenario',
            new_playbook: 'Nuevo Playbook Template',
            clone: 'Clonar',
            clone_template: 'Clonar Plantilla',
            template_name: 'Nombre de la plantilla',
            deprecate: 'Deprecar',
            confirm_deprecate: '¿Deprecar plantilla "{name}"?',
            create_template: 'Agregar plantilla',
            update_status: 'Actualizar Estado',
            no_templates: 'No se encontraron playbook templates',
            deactivation_reason_placeholder: 'Motivo de desactivación...',
            description_placeholder: 'Descripción del libro de procedimientos...',
            name_placeholder: 'Nombre del libro de procedimientos...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'Definiciones SLA',
            title: 'Definiciones de SLA',
            new: 'Nuevo SLA Definition',
            sla_name: 'SLA Nombre',
            target_hours: 'Horas objetivo',
            escalation_after: 'Escalamiento Después',
            applies_to: 'Se aplica a',
            no_definitions: 'No se encontraron sla definitions',
            target_value_placeholder: 'Valor de destino',
            name_placeholder: 'Nombre del SLA...',
            threshold_hours_label: 'Horas umbral',
            severity_label: 'Gravedad',
            all_types: 'Todos los tipos',
            all_severities: 'Todas las gravedades',
            default_label: 'Predeterminado',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Cumple',
            page_title: 'Panel SLA',
            title: 'Panel de SLA',
            loading: 'Cargando sla data...',
            compliance_rate: 'Cumplimiento Tasa',
            breached: 'Violado',
            at_risk: 'En Riesgo',
            on_track: 'En Rastrear',
            by_severity: 'Por Gravedad',
            total_records: 'Total de registros',
            met: 'Cumplido',
            sla_records: 'Registros SLA',
            sla_reference: 'Referencia SLA',
            record_id: 'ID de registro',
            metric_type: 'Tipo de métrica',
            severity: 'Gravedad',
            elapsed: 'Transcurrido',
            clock_start: 'Inicio del reloj',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Registro de escalada',
            title: 'Registro de escalamiento',
            incident_id: 'Incidente ID',
            escalated_by: 'Escalado Por',
            escalated_to: 'Escalado Hasta',
            reason: 'Motivo',
            escalated_at: 'Escalado En',
            no_escalations: 'No se encontraron escalations',
            filter_incident_id: 'Filtrar por Incidente ID',
            escalation_id: 'ID de escalada',
            level: 'Nivel',
            level_range: 'Nivel (L1–L4)',
            create_escalation: 'Crear escalada',
            incident_id_placeholder: 'p. ej. INC-001',
            target_role_placeholder: 'p. ej. dpo, incident_manager',
            select_placeholder: '— seleccionar —',
        },
        regulator_exports: {
            page_title: 'Exportaciones regulatorias',
            title: 'Exportaciones regulatorias',
            new: 'Nuevo exportar',
            incident_id: 'Incidente ID',
            regulator: 'Regulador',
            export_format: 'Formato',
            exported_at: 'Exportado En',
            no_exports: 'No se encontraron regulator exports',
            enter_incident_id: 'Introduzca incidente id',
            export_id: 'ID de exportación',
            watermark: 'Marca de agua',
            submission_status: 'Estado de envío',
            all_jurisdictions: 'Todas las jurisdicciones',
            no_transitions: 'No hay más transiciones disponibles desde',
            select_new_status: '— seleccionar nuevo estado —',
            sections_included: 'Secciones incluidas',
            form_classification: 'Clasificación',
            submission_channel: 'canal',
        },
        deadlines: {
            title: 'Notificación Fechas límite',
            incident_id: 'Incidente ID',
            deadline: 'Fecha límite',
            regulator: 'Regulador',
            remaining: 'Restante',
            no_deadlines: 'No se encontraron notification deadlines',
        },
        vendor_coordination: {
            page_title: 'Coordinación de proveedores',
            title: 'Coordinación de proveedores',
            new: 'Nuevo Coordination',
            incident_id: 'Incidente ID',
            vendor_name: 'Proveedor Nombre',
            contact: 'Contacto',
            coordination_status: 'Estado de coordinación',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'No se encontraron vendor coordination items',
            assessment_placeholder: 'Evaluación inicial del proveedor...',
            coordination_id: 'ID de coordinación',
            communications: 'Comunicaciones',
            info_requests: 'Solicitudes de información',
            action_requests: 'Solicitudes de acción',
            direction: 'Dirección',
            channel_label: 'Canal',
            no_transitions: 'No hay más transiciones disponibles.',
            vendor_ref_number: 'Número de referencia',
            report_date: 'Fecha del informe',
            contact_email: 'Correo',
            contact_phone: 'Teléfono',
            channel_email: 'Correo electrónico',
            channel_meeting: 'Reunión',
            channel_phone: 'Teléfono',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filtrar por Incidente ID...',
            priority_critical: 'Crítico',
            priority_high: 'Alto',
            priority_low: 'Low',
            priority_medium: 'Medio',
            vendor_id_placeholder: 'Filtrar por Proveedor ID...',
            description_placeholder: 'Descripción...',
            filter_vendor_id: 'Filtrar por Proveedor ID',
            inbound: 'Entrante',
            outbound: 'Saliente',
            summary_placeholder: 'Resumen...',
            priority_label: 'Prioridad',
            due_date_label: 'fecha de vencimiento',
            info_request_desc: 'Descripción de la solicitud de información',
            action_request_desc: 'Descripción de la solicitud de acción',
            content_label: 'Contenido',
            select_incident: 'Seleccionar incidente',
            select_vendor: 'Seleccionar proveedor',
            discovery_date: 'Fecha de descubrimiento',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Actor',
        },
        metrics: {
            page_title: 'Métricas de respuesta',
            title: 'Métricas de respuesta',
            loading: 'Cargando metrics data...',
            avg_response_time: 'Avg Respuesta Hora',
            avg_resolution_time: 'Tiempo medio de resolución',
            total_incidents: 'Total Incidentes',
            by_severity: 'Por Gravedad',
            by_status: 'Por Estado',
            trend: 'Tendencia',
            start_sla_clock: 'Iniciar reloj SLA',
            sla_records: 'SLA Registros',
            no_sla_records: 'Aún no hay registros SLA.',
            record_id: 'ID de registro',
            severity: 'Gravedad',
            progress: 'Progreso',
            incident_id_required: 'El ID de incidente es obligatorio',
            sla_definition_required: 'La definición SLA es obligatoria',
            metric_type_required: 'El tipo de métrica es obligatorio',
            threshold_required: 'Las horas umbral son obligatorias',
            sla_clock_started: 'Reloj SLA iniciado',
            sla_definition: 'Definición SLA',
            no_definitions: 'No se encontraron definiciones SLA.',
            select_definition: 'Seleccionar definición SLA...',
            select_metric_type: 'Seleccionar tipo de métrica...',
            select_severity: 'Seleccionar gravedad...',
            threshold_hours: 'Umbral (horas)',
            warning_threshold_pct: 'Umbral de advertencia (%)',
            start_clock: 'Iniciar reloj',
            starting: 'Iniciando...',
            metric_label: 'Métrica',
            value_label: 'Valor',
            incident_id: 'ID de incidente',
            threshold_placeholder: 'p. ej. 24',
            metric_types: {
                triage_time: 'Tiempo de triaje',
                containment_time: 'Tiempo de contención',
                notification_time: 'Tiempo de notificación',
                resolution_time: 'Tiempo de resolución',
            },
            severities: {
                critical: 'Crítico',
                high: 'Alto',
                medium: 'Medio',
                low: 'Low',
            },
            sla_reference: 'Referencia SLA',
        },
        playbook_links: {
            page_title: 'Enlaces Playbook',
            title: 'Enlaces de notificación de playbook',
            new_link: 'Vincular playbook a notificación',
            activation_id: 'ID de activación',
            notification_id: 'ID de notificación',
            select_activation: 'Seleccionar una activación...',
            select_notification: 'Seleccionar una notificación...',
            no_links: 'No se encontraron enlaces de playbook',
            seed_pdpl: 'Añadir enlaces de playbook de plantilla',
            created: 'Enlace de playbook creado',
            seeded: 'Enlaces de playbook de plantilla añadidos',
            create_failed: 'Error al crear el enlace de playbook',
            seed_failed: 'Error al añadir los enlaces de playbook de plantilla',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Ningún marco instalado rige la respuesta a incidentes, por lo que no hay plazos que añadir.',
                declared_but_none_active: 'Hay un marco que rige la respuesta a incidentes instalado, pero no está en vigor.',
                no_declared_deadlines: 'Los marcos de respuesta a incidentes instalados no establecen plazos de notificación.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Fecha límite',
            deadline_units: {
                hours: '{count} horas',
                working_days: '{count} días hábiles',
                calendar_days: '{count} días naturales',
                days: '{count} días',
                weeks: '{count} semanas',
                months: '{count} meses',
                years: '{count} años',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Mapeos SLA regulatorios',
            title: 'Asignaciones de SLA regulatorio',
            new_mapping: 'Nueva asignación de SLA',
            sla_definition: 'Definición de SLA',
            select_sla_definition: 'Seleccionar una definición de SLA...',
            framework_code: 'Código del marco',
            deadline_type: 'Tipo de plazo',
            deadline_hours: 'Horas de plazo',
            seed_pdpl: 'Añadir asignaciones desde los plazos instalados',
            no_mappings: 'No se encontraron asignaciones de SLA regulatorio',
            dashboard: 'Panel de SLA regulatorio',
            deadline_types: {
                initial_notification: 'Notificación inicial',
                supplement_response: 'Respuesta suplementaria',
                authority_response: 'Respuesta de la autoridad',
                remediation_report: 'Informe de remediación',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Referencia estatutaria',
        },
        coordination_dashboard: {
            page_title: 'Panel de coordinación',
            title: 'Panel de coordinación',
            incident_id: 'ID de incidente',
            select_incident: 'Seleccionar un incidente...',
            team_role: 'Rol del equipo',
            members: 'Miembros',
            tasks: 'Tareas',
            sla_status: 'Estado de SLA',
            no_teams: 'No se encontraron equipos de coordinación',
            new_team: 'Nuevo equipo de coordinación',
            enter_incident_id: 'Introduzca un ID de incidente para ver los equipos de coordinación.',
            member_ids_placeholder: 'usuario1, usuario2',
            edit_team: 'Editar equipo',
            task_count: 'Recuento de tareas',
            tasks_completed: 'Completado',
            team_roles: {
                security: 'Seguridad',
                legal: 'Legales',
                it: 'IT',
                management: 'Dirección',
                department_owner: 'Responsable de departamento',
                external: 'Externo',
            },
        },
        evidence_chain: {
            page_title: 'Cadena de evidencia',
            title: 'Cadena de custodia de evidencia',
            new_entry: 'Nueva entrada de evidencia',
            select_incident: 'Seleccionar un incidente...',
            actor: 'Actor',
            select_actor: 'Seleccionar un actor...',
            evidence_id: 'ID de evidencia',
            action: 'Acción',
            recipient: 'Destinatario',
            no_entries: 'No se encontraron entradas de cadena de evidencia',
            enter_incident_id: 'Introduzca un ID de incidente para ver la cadena de evidencia.',
            evidence_id_placeholder: 'p. ej. EVD-2026-0001',
            evidence_id_help: 'Una referencia única para el elemento de evidencia: un archivo de registro, una imagen de disco o una captura de pantalla. Se ha rellenado una sugerencia; edítela para que coincida con su catálogo de evidencias.',
            select_incident_first: 'Seleccione un incidente arriba antes de añadir una entrada de evidencia.',
            for_incident: 'Para el incidente',
            actions: {
                collected: 'Recogido',
                shared: 'Compartido',
                exported: 'Exportado',
                archived: 'Archivado',
            },
        },
        post_review: {
            page_title: 'Revisión postincidente',
            title: 'Revisión post-incidente',
            new_review: 'Nueva revisión',
            select_incident: 'Seleccionar un incidente...',
            lessons_learned: 'Lecciones aprendidas',
            recommendations: 'Recomendaciones',
            improvement_actions: 'Acciones de mejora',
            submit_for_review: 'Enviar para revisión',
            aria_submit_review: 'Enviar revisión {name} para revisión',
            aria_sign_off: 'Aprobar revisión {name}',
            sign_off: 'Aprobación',
            no_reviews: 'No se encontraron revisiones post-incidente',
            enter_incident_id: 'Introduzca un ID de incidente para ver la revisión postincidente.',
            one_per_line: 'Uno por línea',
            statuses: {
                draft: 'Borrador',
                in_review: 'En revisión',
                signed_off: 'Aprobado',
                closed: 'Cerrado',
            },
        },
        activations: {
            title: 'Activaciones del libro de jugadas',
            activation_id: 'ID de activación',
            phases: 'Fases',
            activated_at: 'Activado en',
            activated_by: 'Activado por',
            deactivate: 'Desactivar',
            complete: 'Completar',
            deactivation_reason: 'Motivo de desactivación',
            evidence_button: 'evidencia',
            evidence_modal_title: 'Evidencia de activación',
            no_evidence: 'No hay evidencia adjunta a esta activación.',
            upload_evidence_label: 'Cargar evidencia de activación',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Guíame a través de los huecos',
            running: 'Brechas para caminar…',
            results_heading: 'Hoja de ruta de mejora ({count} brecha{plural})',
            tokens_label: '≈ {count} fichas',
            no_gaps: 'No se detectaron brechas: todos los dominios en el objetivo o por encima del mismo.',
            col_priority: 'Prioridad',
            col_domain: 'Dominio',
            col_levels: 'Actual → Objetivo',
            col_severity: 'Gravedad',
            col_actions: 'Acciones de remediación',
            citations_label: 'Citas',
            accept_label: 'Aceptar hoja de ruta',
            reject_label: 'Rechazar borrador',
            export_label: 'Exportar DOCX',
            exported_label: 'Hoja de ruta exportada',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Resolución: {status}',
            resolve_status_accepted: 'Aceptado',
            resolve_status_rejected: 'Rechazado',
            error_generic: 'No se pudo ejecutar el recorrido de brechas. Inténtelo de nuevo o verifique el estado de la evaluación.',
        },
        policy_gap: {
            run_label: 'Detectar brechas políticas',
            running: 'Detectando lagunas…',
            results_heading: 'Brechas de políticas ({count})',
            tokens_label: '≈ {count} fichas',
            no_gaps: 'No se detectaron brechas de cobertura.',
            col_severity: 'Gravedad',
            col_kind: 'amable',
            col_clause: 'Cláusula/requisito',
            kind_missing: 'Cláusula faltante',
            kind_weak: 'Cobertura débil',
            kind_stale: 'Obsoleto (revisión vencida)',
            remediation_label: 'Remediación sugerida',
            citations_label: 'Citas',
            accept_label: 'Aceptar informe de brecha',
            reject_label: 'Rechazar informe',
            export_label: 'Exportar DOCX',
            accept_hint: 'Aceptar registra el informe de brechas en el borrador persistente. Utilice Exportar DOCX para generar un informe de auditoría de políticas; el operador dirige la edición de la política real.',
            resolve_summary: 'Resolución: {status}',
            error_generic: 'No se pudieron detectar brechas en las políticas. Inténtelo nuevamente o verifique el estado del documento.',
        },
        remediation_plan_generator: {
            run_label: 'Generar plan a partir de brechas',
            running: 'Generando plan…',
            results_heading: 'Plan de remediación ({phase_count} fase{plural})',
            tokens_label: '≈ {count} fichas',
            phase_label: 'Fase {index}',
            window_label: 'ventana',
            owner_label: 'Rol de propietario',
            evidence_label: 'Tipos de evidencia requeridos',
            tasks_label: 'Tareas',
            no_phases: 'No se produjeron fases: el conjunto de espacios de entrada puede estar vacío.',
            citations_label: 'Citas',
            accept_label: 'Aceptar plan',
            reject_label: 'Rechazar plan',
            export_label: 'Exportar DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Resolución: {status} · {tasks} tarea(s) creada(s)',
            error_generic: 'No se pudo generar el plan de remediación. Inténtelo de nuevo o verifique la entrada del conjunto de espacios.',
            source_picker_label: 'Fuente',
            source_all: 'Todos',
            source_consent: 'Análisis de la brecha de consentimiento',
            source_policy: 'Detección de brechas de políticas',
            source_vendor: 'Incorporación de proveedores',
            load_upstream_label: 'Carga desde aguas arriba',
            loading_upstream: 'Cargando…',
            upstream_count: '{count} espacios cargados',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Modelos de madurez',
            maturity_assessments: 'Evaluaciones de madurez',
            trend_dashboard: 'Panel de tendencias',
            reporting_periods: 'Períodos de informe',
            executive_summaries: 'Resúmenes ejecutivos',
            audit_readiness: 'Preparación para auditoría',
            evidence_gaps: 'Brechas de evidencia',
            program_health: 'Estado del programa',
            obligation_mappings: 'Asignaciones de obligaciones',
            governance_documents: 'Documentos de gobernanza',
            remediation_plans: 'Planes de remediación',
            governance_model: 'Modelo de gobernanza',
            document_drafting: 'Redacción de documentos',
            document_issuance: 'Emisión de documentos',
            acknowledgment_tracking: 'Seguimiento de acuses',
            change_log: 'Registro de cambios',
            review_cadence: 'Cadencia de revisión',
        },
        maturity_models: {
            title: 'Modelos de madurez',
            new: 'Nuevo Model',
            seed_defaults: 'Valores iniciales predeterminados',
            domains: 'Dominios',
            activate: 'Activar',
            no_models: 'No se encontraron maturity models',
            new_maturity_model: 'Nuevo Maturity Model',
            create_model: 'Agregar Model',
            load_error: 'No se pudieron cargar los modelos de madurez. Inténtelo de nuevo.',
            create_error: 'No se pudo crear el modelo de madurez',
            status_error: 'No se pudo actualizar el estado del modelo',
            seed_error: 'No se pudieron generar los valores predeterminados',
            aria_seed_defaults: 'Generar modelos de madurez predeterminados',
            aria_create_new: 'Crear nuevo modelo de madurez',
            loading_sr: 'Cargando modelos de madurez',
            model_name_placeholder: 'p. ej., Modelo de madurez de privacidad',
            status_changed: 'Estado del modelo cambiado a {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Evaluaciones de madurez',
            new: 'Nuevo evaluación',
            model: 'Modelo',
            assessor: 'Evaluador',
            overall_score: 'Puntuación general',
            overall_score_help: 'Una calificación de madurez ponderada de 0 a 5, calculada automáticamente cuando se finaliza una evaluación. Auméntela puntuando más alto cada dominio durante la evaluación y luego llevando la evaluación a través de Enviar para revisión → Finalizar.',
            assessed_at: 'Evaluado el',
            no_assessments: 'No se encontraron madurez evaluaciones',
            load_error: 'No se pudieron cargar las evaluaciones. Inténtelo de nuevo.',
            create_error: 'No se pudo crear la evaluación',
            start_error: 'No se pudo iniciar la evaluación',
            archive_error: 'No se pudo archivar la evaluación',
            eval_error: 'No se pudo cargar la evaluación para evaluar',
            eval_min_score: 'Puntúe al menos un dominio',
            eval_save_error: 'No se pudieron guardar las evaluaciones de dominio',
            review_error: 'No se pudo enviar para revisión',
            finalize_error: 'No se pudo finalizar la evaluación',
            start: 'Iniciar',
            start_confirm_title: 'Iniciar evaluación',
            start_confirm_message: '¿Está seguro de que desea iniciar esta evaluación?',
            archive_confirm_title: 'Archivar evaluación',
            archive_confirm_message: '¿Está seguro de que desea archivar esta evaluación?',
            evaluate_domains: 'Evaluar',
            evaluate_domains_title: 'Evaluar dominios',
            submit_for_review: 'Enviar para revisión',
            finalize: 'Finalizar',
            archive: 'Archivar',
            all_reviewers: 'Todos los revisores deben aprobar',
            any_reviewer: 'Cualquier revisor puede aprobar',
            select_level: 'Seleccionar nivel de madurez...',
            findings_placeholder: 'Hallazgos, notas, referencias de evidencia...',
            no_domains: 'No se encontraron dominios para este modelo.',
        },
        trend_dashboard: {
            title: 'Panel de tendencias',
            loading: 'Cargando trend data...',
            score_trend: 'Tendencia de puntuación',
            domain_comparison: 'Comparación de dominios',
            period: 'Período',
            load_error: 'No se pudo cargar el resumen de tendencia. Inténtelo de nuevo.',
            metrics_load_error: 'No se pudieron cargar las métricas. Inténtelo de nuevo.',
            module: 'Módulo',
            metric: 'Métrica',
            value: 'Valor',
            trend: 'Tendencia',
            aria_filters: 'Filtros de métricas de tendencia',
            showing_chart: 'Mostrando vista de gráfico',
            showing_table: 'Mostrando vista de tabla',
            create_metric: 'Crear métrica',
            data_points: 'Puntos de datos',
            trend_direction: 'Dirección de la tendencia',
            unit: 'Unidad',
        },
        reporting_periods: {
            title: 'Períodos de informe',
            new: 'Nuevo período',
            period_name: 'Período Nombre',
            start_date: 'Inicio Fecha',
            end_date: 'Fin Fecha',
            no_periods: 'No se encontraron reporting periods',
            load_error: 'No se pudieron cargar los períodos. Inténtelo de nuevo.',
            create_error: 'No se pudo crear el período',
            auto_generate_error: 'No se pudieron generar automáticamente los períodos',
            status_error: 'No se pudo actualizar el estado del período',
            start_collecting: 'Comenzar recopilación',
            mark_as_draft: 'Marcar como borrador',
            submit_for_review: 'Enviar para revisión',
            publish: 'Publicar',
            auto_generate: 'Generar automáticamente períodos de informe',
            period_label_placeholder: 'p. ej., T1 2026',
            status_updated: 'Estado del período actualizado',
        },
        executive_summaries: {
            title: 'Resúmenes ejecutivos',
            new: 'Nuevo resumen',
            period: 'Período',
            generated_at: 'Generado el',
            generated_by: 'Generado por',
            no_summaries: 'No se encontraron executive summaries',
            generate: 'Generar',
            load_error: 'No se pudieron cargar los resúmenes ejecutivos. Inténtelo de nuevo.',
            generate_error: 'No se pudo generar el resumen ejecutivo',
            save_error: 'No se pudo guardar el resumen ejecutivo',
        },
        audit_readiness: {
            title: 'Preparación para auditoría',
            loading: 'Cargando audit readiness data...',
            overall_readiness: 'Preparación general',
            by_domain: 'Por Dominio',
            evidence_coverage: 'Cobertura de evidencias',
            gaps: 'Brechas',
            load_error: 'No se pudo cargar la preparación más reciente. Inténtelo de nuevo.',
            history_error: 'No se pudo cargar el historial. Inténtelo de nuevo.',
            calculate_error: 'No se pudo calcular la preparación de auditoría',
            aria_recalculate: 'Recalcular puntuación de preparación',
            aria_current_readiness: 'Preparación actual de auditoría',
            aria_history: 'Historial de puntuación',
            recalculated: 'Preparación de auditoría recalculada',
            loading_sr: 'Cargando datos de preparación',
        },
        evidence_gaps: {
            title: 'Brechas de evidencia',
            domain: 'Dominio',
            control: 'controlar',
            gap_type: 'Tipo de brecha',
            severity: 'Gravedad',
            no_gaps: 'No se encontraron evidence gaps',
            load_error: 'No se pudieron cargar los vacíos de evidencia. Inténtelo de nuevo.',
            stats_error: 'No se pudieron cargar las estadísticas. Inténtelo de nuevo.',
            create_error: 'No se pudo crear el vacío de evidencia',
            update_error: 'No se pudo actualizar el vacío',
            status_error: 'No se pudo actualizar el estado',
            aria_create_new: 'Crear nuevo vacío de evidencia',
            aria_statistics: 'Estadísticas de vacíos de evidencia',
            loading_sr: 'Cargando vacíos de evidencia',
            status_updated: 'Estado del vacío actualizado',
            entity_type_placeholder: 'p. ej., proveedor, sistema',
            all_modules: 'Todos los dominios',
            all_severities: 'Todas las gravedades',
        },
        program_health: {
            title: 'Estado del programa',
            loading: 'Cargando program health data...',
            overall_health: 'Salud general',
            by_module: 'Por módulo',
            risk_areas: 'Áreas de riesgo',
            recommendations: 'Recomendaciones',
            load_error: 'No se pudieron cargar los datos. Inténtelo de nuevo.',
            aria_key_metrics: 'Métricas clave de salud del programa',
            aria_module_scorecard: 'Desglose de puntuación por módulo',
            loading_sr: 'Cargando datos de salud del programa',
            trending_up: 'Tendencia al alza',
            trending_down: 'Tendencia a la baja',
            trending_stable: 'Estable',
            data_loaded: 'Datos de salud del programa cargados',
        },
        obligation_mappings: {
            title: 'Asignaciones de obligaciones',
            create_mapping: 'Crear asignación de obligación',
            framework: 'Marco normativo',
            select_framework: 'Seleccionar un marco...',
            obligation: 'Obligación',
            obligation_ref: 'Referencia de obligación',
            obligation_description: 'Descripción de obligación',
            role: 'Rol',
            assigned_role: 'Rol asignado',
            select_role: 'Seleccionar un rol...',
            assigned_department: 'Departamento asignado',
            select_department: 'Seleccionar un departamento...',
            control_ids: 'IDs de control',
            control_ids_placeholder: 'p. ej. A.5.1, CC6.1',
            control_ids_hint: 'Códigos de referencia de control separados por comas.',
            raci_assignments: 'Asignaciones RACI',
            create_raci: 'Crear asignación RACI',
            activity: 'Actividad',
            activity_name: 'Nombre de actividad',
            activity_description: 'Descripción de actividad',
            responsible: 'Responsable',
            select_users: 'Seleccionar usuarios...',
            accountable: 'Rendidor de cuentas',
            select_user: 'Seleccionar un usuario...',
            consulted: 'Consultado',
            informed: 'Informado',
            review_frequency: 'Frecuencia de revisión',
            frequency_monthly: 'Mensual',
            frequency_quarterly: 'Trimestral',
            frequency_semi_annual: 'Semestral',
            frequency_annual: 'Anual',
            no_mappings_found: 'No se encontraron asignaciones de obligaciones.',
            no_raci_found: 'No se encontraron asignaciones RACI.',
            mapping_created: 'Mapeo de obligación creado',
            mapping_updated: 'Asignación de obligación actualizada',
            mapping_deleted: 'Asignación de obligación eliminada',
            raci_created: 'Asignación RACI creada',
            raci_updated: 'Asignación RACI actualizada',
            raci_deleted: 'Asignación RACI eliminada',
            aria_edit_mapping: 'Editar asignación {name}',
            aria_delete_mapping: 'Eliminar asignación {name}',
            aria_edit_raci: 'Editar RACI {name}',
            aria_delete_raci: 'Eliminar RACI {name}',
            confirm_delete_mapping: 'Eliminar esta asignación de obligación?',
            confirm_delete_raci: 'Eliminar esta asignación RACI?',
            table_view: 'Vista de tabla',
            matrix_view: 'Vista de matriz',
            print_export: 'Imprimir / Exportar',
            filter_framework: 'Filtrar por marco',
            no_matrix_data: 'No hay asignaciones RACI para mostrar en la vista de matriz.',
            aria_print_export: 'Imprimir o exportar la matriz RACI',
        },
        governance_documents: {
            title: 'Documentos de gobernanza',
            create_document: 'Crear documento de gobernanza',
            document_title: 'Título del documento',
            document_type: 'Tipo de documento',
            content_url: 'URL del contenido',
            content_url_placeholder: 'https://...',
            framework_codes: 'Códigos de marco',
            select_frameworks: 'Seleccionar marcos...',
            effective_date: 'Fecha de vigencia',
            review_date: 'Fecha de revisión',
            status: 'Estado',
            version: 'Versión',
            acknowledgments: 'Acuses de recibo',
            acknowledge: 'Acusar recibo',
            acknowledged_by: 'Acusado por',
            department: 'Departamento',
            no_documents_found: 'No se encontraron documentos de gobernanza.',
            draft: 'Borrador',
            review: 'Revisión',
            approved: 'Aprobado',
            issued: 'Emitido',
            superseded: 'Reemplazado',
            document_created: 'Documento creado',
            document_acknowledged: 'Documento reconocido',
            policy: 'Política',
            procedure: 'Procedimiento',
            standard: 'Estándar',
            guideline: 'Directriz',
        },
        governance_model: {
            title: 'Modelo de gobernanza',
            create_model: 'Crear modelo de gobernanza',
            edit_model: 'Editar modelo de gobernanza',
            model_name: 'Nombre del modelo',
            description: 'Descripción',
            governance_type: 'Tipo de gobernanza',
            type_centralized: 'Centralizado',
            type_federated: 'Federado',
            type_hybrid: 'Híbrido',
            framework_codes: 'Códigos de marco',
            select_frameworks: 'Seleccionar marcos...',
            review_frequency: 'Frecuencia de revisión',
            freq_monthly: 'Mensual',
            freq_quarterly: 'Trimestral',
            freq_semi_annual: 'Semestral',
            freq_annual: 'Anual',
            committees: 'Comités',
            committee_name: 'Nombre del comité',
            committee_members: 'Miembros (separados por comas)',
            committee_mandate: 'Mandato',
            add_committee: 'Agregar comité',
            no_committees: 'Sin comités definidos.',
            delegation_rules: 'Reglas de delegación',
            from_role: 'Del rol',
            to_role: 'Al rol',
            scope: 'Alcance',
            add_delegation_rule: 'Agregar regla de delegación',
            control_owner_summary: 'Resumen de propietarios de control',
            no_raci_data: 'Sin datos RACI vinculados.',
            activity: 'Actividad',
            responsible: 'Responsable',
            accountable: 'Responsable final',
            consulted: 'Consultado',
            informed: 'Informado',
            no_models_found: 'No se encontraron modelos.',
            model_created: 'Modelo creado',
            model_updated: 'Modelo actualizado',
            model_deleted: 'Modelo eliminado',
            confirm_delete: '¿Eliminar este modelo?',
            expand: 'Expandir',
            collapse: 'Contraer',
            aria_expand: 'Expandir detalles de {name}',
            aria_edit: 'Editar modelo {name}',
            aria_delete: 'Eliminar modelo {name}',
            aria_export_csv: 'Exportar {name} como CSV',
            aria_export_json: 'Exportar {name} como JSON',
            framework_read_permission_warning: 'Necesita el regulatory_framework: permiso de lectura para seleccionar marcos. Pídale a un administrador que lo conceda.',
            select_members: 'Seleccionar miembros del comité',
            select_from_role: 'Seleccionar rol de delegación',
            select_to_role: 'Seleccione el rol de recepción',
            mandate_markdown_hint: 'Se admite Markdown: **negrita**, listas, enlaces.',
            scope_tab_obligation: 'Obligación',
            scope_tab_framework: 'Marco',
            scope_tab_free_text: 'texto libre',
            select_obligation: 'Seleccione una obligación',
            select_framework: 'Seleccione un marco',
            aria_remove_committee: 'Eliminar comité {n}',
            aria_remove_delegation_rule: 'Eliminar regla de delegación {n}',
            no_frameworks_available: 'No hay marcos disponibles. Agregue primero marcos regulatorios.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Redacción y aprobación',
            document_title: 'Título del documento',
            document_type: 'Tipo de documento',
            status: 'Estado',
            drafter: 'Redactor',
            deadline: 'Fecha límite',
            assign_drafter: 'Asignar redactor',
            select_drafter: 'Seleccionar redactor...',
            assign: 'Asignar',
            submit_for_review: 'Enviar para revisión',
            legal_review: 'Revisión legal',
            reviewer: 'Revisor',
            select_reviewer: 'Seleccionar revisor...',
            comments: 'Comentarios',
            review_action: 'Acción de revisión',
            approve: 'Aprobar',
            request_changes: 'Solicitar cambios',
            submit_review: 'Enviar revisión',
            executive_approval: 'Aprobación ejecutiva',
            no_drafts_found: 'Sin documentos en redacción, revisión o aprobación.',
            drafter_assigned: 'Redactor asignado',
            status_updated: 'Estado actualizado',
            review_approved: 'Revisión aprobada',
            changes_requested: 'Cambios solicitados',
            document_approved: 'Documento aprobado',
            aria_assign: 'Asignar redactor a {name}',
            aria_submit_review: 'Enviar {name} para revisión',
            aria_review: 'Revisar {name}',
            aria_approve: 'Aprobar {name}',
        },
        document_issuance: {
            title: 'Emisión y distribución',
            document_title: 'Título del documento',
            document_type: 'Tipo de documento',
            status: 'Estado',
            version: 'Versión',
            effective_date: 'Fecha de vigencia',
            distribution_scope: 'Alcance de distribución',
            distribution_placeholder: 'Ej.: Todos los departamentos',
            issuance_number: 'Número de emisión',
            issuance_number_placeholder: 'Ej.: ISS-2026-001',
            supersedes: 'Reemplaza documento',
            none: 'Ninguno',
            issue_document: 'Emitir documento',
            ready_for_issuance: 'Listo para emisión',
            issued_documents: 'Documentos emitidos',
            no_issued_documents: 'Sin documentos emitidos.',
            view_lineage: 'Ver linaje',
            supersession_lineage: 'Linaje de reemplazo',
            document_issued: 'Documento emitido',
            document_superseded: 'Documento reemplazado',
            aria_issue: 'Emitir {name}',
            aria_view_lineage: 'Ver linaje de {name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Seguimiento de acuses',
            document_title: 'Documento',
            acknowledged_count: 'Acusados',
            pending_count: 'Pendientes',
            percentage: 'Completado',
            details: 'Detalles',
            send_reminder: 'Enviar recordatorio',
            acknowledgment_details: 'Detalles de acuses',
            user: 'Usuario',
            department: 'Departamento',
            acknowledged_at: 'Acusado el',
            status: 'Estado',
            acknowledged: 'Acusado',
            no_issued_documents: 'Sin documentos para seguir.',
            no_acknowledgments: 'Sin acuses registrados.',
            reminder_sent: 'Recordatorio enviado',
            aria_expand: 'Detalles de acuses de {name}',
            aria_send_reminder: 'Enviar recordatorio para {name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Registro de cambios',
            date: 'Fecha',
            document: 'Documento',
            change_type: 'Tipo de cambio',
            actor: 'Actor',
            summary: 'Resumen',
            export_csv: 'Exportar CSV',
            filters: 'Filtros del registro',
            filter_document: 'Filtrar por documento',
            all_documents: 'Todos los documentos',
            filter_change_type: 'Filtrar por tipo',
            all_types: 'Todos los tipos',
            date_from: 'Fecha desde',
            date_to: 'Fecha hasta',
            version_history: 'Historial de versiones',
            version_archive: 'Archivo de versiones',
            no_entries: 'Sin entradas.',
            no_history: 'Sin historial.',
            exported: 'Registro exportado',
            aria_export: 'Exportar registro como CSV',
            aria_view_history: 'Ver historial de {name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Cadencia de revisión',
            configure_cadence: 'Configurar cadencia',
            edit_cadence: 'Editar cadencia',
            document: 'Documento',
            select_document: 'Seleccionar documento...',
            review_frequency: 'Frecuencia de revisión',
            freq_30: '30 días',
            freq_60: '60 días',
            freq_90: '90 días',
            freq_180: '180 días',
            freq_365: '365 días',
            last_reviewed: 'Última revisión',
            next_review: 'Próxima revisión',
            status: 'Estado',
            overdue: 'Vencido',
            on_track: 'Al día',
            days: 'días',
            horizon: 'Horizonte de revisión',
            overdue_warning: 'Revisiones vencidas',
            overdue_count: '{count} documento(s) vencidos.',
            no_cadences: 'Sin cadencias configuradas.',
            cadence_created: 'Cadencia creada',
            cadence_updated: 'Cadencia actualizada',
            aria_edit: 'Editar cadencia de {name}',
        },
        remediation_plans: {
            title: 'Planes de remediación',
            create_plan: 'Crear plan de remediación',
            gap_id: 'Referencia de brecha',
            select_gap: 'Seleccionar una brecha...',
            plan_title: 'Título',
            description: 'Descripción',
            priority: 'Prioridad',
            priority_critical: 'Crítica',
            priority_high: 'Alta',
            priority_medium: 'Media',
            priority_low: 'Baja',
            assigned_to: 'Asignado a',
            select_user: 'Seleccionar un usuario...',
            target_date: 'Fecha objetivo',
            framework_code: 'Marco',
            select_framework: 'Seleccionar un marco...',
            gap_finding: 'Hallazgo de brecha',
            action_items: 'Elementos de acción',
            owner: 'Propietario',
            deadline: 'Fecha límite',
            planned: 'Planificado',
            in_progress: 'En curso',
            completed: 'Completado',
            verified: 'Verificado',
            no_plans_found: 'No se encontraron planes de remediación.',
            evidence: 'Evidencia',
            plan_created: 'Plan de remediación creado',
            action_items_hint: 'Elementos de acción (uno por línea)',
        },
        statuses: {
            identified: 'Identificado',
            assigned: 'Asignado',
            in_remediation: 'En remediación',
            resolved: 'Resuelto',
            accepted_risk: 'Riesgo aceptado',
            planned: 'planeado',
            in_progress: 'En progreso',
            under_review: 'En revisión',
            finalized: 'Finalizado',
            archived: 'Archivado',
            draft: 'Borrador',
            active: 'Activo',
            upcoming: 'Próximo',
            collecting: 'Coleccionando',
            published: 'Publicado',
            review: 'Revisión',
            approved: 'Aprobado',
            issued: 'Emitido',
            superseded: 'Reemplazado',
            completed: 'Completado',
            verified: 'Verificado',
        },
        severities: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Low',
        },
        priorities: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Low',
        },
        gap_types: {
            missing: 'desaparecido',
            expired: 'Caducado',
            insufficient: 'insuficiente',
        },
        document_types: {
            policy: 'Política',
            procedure: 'Procedimiento',
            standard: 'Estándar',
            guideline: 'Directriz',
            template: 'Plantilla (interna)',
        },
        maturity_levels: {
            level_1: 'Nivel 1: Inicial',
            level_2: 'Nivel 2: en desarrollo',
            level_3: 'Nivel 3: Definido',
            level_4: 'Nivel 4: Gestionado',
            level_5: 'Nivel 5: Optimización',
        },
        grades: {
            excellent: 'Excelente',
            good: 'bueno',
            fair: 'Feria',
            poor: 'pobre',
            critical: 'Crítico',
            needs_improvement: 'Necesita mejorar',
            not_assessed: 'No evaluado',
        },
        modules: {
            data_mapping: 'Mapeo de datos',
            consent: 'Consentimiento',
            governance: 'Gobernanza',
            incidents: 'Incidentes',
            monitoring: 'Monitoreo',
            classification: 'Clasificación',
            vendor_risk: 'Riesgo de proveedores',
            discovery: 'Descubrimiento',
            deletion: 'Eliminación',
        },
        cadences: {
            monthly: 'Mensual',
            quarterly: 'Trimestral',
            semi_annual: 'Semestral',
            annual: 'Anual',
        },
        section_labels: {
            opening: 'Apertura',
            key_metrics: 'Métricas clave',
            highlights: 'Aspectos destacados',
            concerns: 'Preocupaciones',
            recommendations: 'Recomendaciones',
            outlook: 'perspectiva',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} brecha',
        count_gaps_plural: '{count} lagunas',
        count_summaries: '{count} resumen',
        count_summaries_plural: '{count} resúmenes',
        count_assessments: '{count} evaluación',
        count_assessments_plural: '{count} evaluaciones',
        count_models: '{count} modelo',
        count_models_plural: '{count} modelos',
        count_periods: '{count} período',
        count_periods_plural: '{count} períodos',
        count_metrics: '{count} métrica',
        count_metrics_plural: '{count} métricas',
        unknown_user: 'Usuario desconocido',
        unknown_gap: 'Brecha desconocida',
        no_permission_title: 'Permisos insuficientes',
        no_permission_message: 'No tienes permiso para acceder a esta página.',
        ac_vn_frameworks_title: 'Cobertura del marco VN',
        ac_vn_frameworks_body: 'Las evaluaciones de madurez cubren los 6 marcos regulatorios vietnamitas: PDPL, Ley de Datos, Ley de Telecomunicaciones, Ley de Ciberseguridad, Ley AI y Ley de Comercio Electrónico, cada una con criterios de evaluación específicos del marco.',
        ac_controller_processor_title: 'Controlador versus procesador',
        ac_controller_processor_body: 'Las asignaciones de obligaciones distinguen entre las responsabilidades del controlador de datos y del procesador de datos. Asigne roles utilizando la matriz RACI para garantizar que cada obligación tenga un propietario claro, ya sea que la organización actúe como controlador o procesador.',
        ac_versioned_evidence_title: 'Rastro de evidencia versionada',
        ac_versioned_evidence_body: 'Cada transición de estado (Borrador, Revisión, Aprobado, Emitido) crea un registro versionado con marca de tiempo que sirve como evidencia auditable. El Registro de cambios captura el historial completo del ciclo de vida del documento.',
        ac_remediation_gap_link_title: 'Vinculado a las brechas de evaluación',
        ac_remediation_gap_link_body: 'Cada plan de remediación se vincula directamente con una brecha de evaluación identificada. Asigne un propietario y establezca una fecha límite para realizar un seguimiento del progreso de la resolución desde la brecha hasta el cierre.',
        ac_annual_attestation_title: 'Ciclo de certificación anual',
        ac_annual_attestation_body: 'Los períodos de presentación de informes se alinean con el requisito de certificación anual de la Ley de Datos de Vietnam. Utilice la cadencia "Anual" (ciclo de 365 días) para generar automáticamente períodos alineados con la certificación.',
        ac_evidence_per_obligation_title: 'Pruebas por obligación marco',
        ac_evidence_per_obligation_body: 'Las lagunas en la evidencia identifican pruebas faltantes, vencidas o insuficientes para cada obligación marco. Filtre por dominio para ver qué requisitos reglamentarios carecen de evidencia que los respalde.',
        ac_cross_framework_title: 'Integridad entre marcos',
        ac_cross_framework_body: 'La preparación para la auditoría proporciona una visión de integridad entre marcos. Las puntuaciones de los componentes reflejan la cobertura de todos los marcos mapeados, destacando áreas que necesitan atención antes de una auditoría.',
        ac_cadence_default_title: 'Cadencia de revisión predeterminada',
        ac_cadence_default_body: 'La cadencia de revisión predeterminada es de 180 días. Las revisiones vencidas se marcan automáticamente. Ajuste la frecuencia por documento según sea necesario; las opciones varían de 30 a 365 días.',
        ac_templates_internal_title: 'Clasificación de plantillas',
        ac_templates_internal_body: 'Las plantillas se clasifican como documentos internos. Utilice el tipo de documento "Plantilla" para marcar artefactos de gobierno reutilizables con alcance de distribución solo interno.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Escrituras pendientes',
        subtitle: 'Los clientes Claude externos proponen escrituras aquí. Apruebe o rechace cada entrada — Forseti solo ejecuta tras la autorización del administrador.',
        no_pending: 'Sin escrituras pendientes.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Herramienta',
        col_preview: 'Vista previa',
        col_key: 'Tecla API',
        col_requested: 'Solicitado',
        col_expires: 'Expira',
        col_status: 'Estado',
        status_pending: 'Pendiente',
        status_approved: 'Aprobada',
        status_declined: 'Rechazada',
        status_expired: 'Expirada',
        status_executed: 'Ejecutada',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Aprobar',
        decline_label: 'Rechazar',
        refresh_label: 'Actualizar',
        error_generic: 'No se pudieron cargar las escrituras pendientes.',
        confirm_approve: 'Al aprobar se ejecutará la herramienta sobre su organización con los permisos del titular de la API key. ¿Continuar?',
        confirm_decline: '¿Rechazar esta escritura pendiente? Esta acción no se puede deshacer.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Las escrituras pendientes caducan 24 horas después de su creación. El barrido GC horario rechaza automáticamente las entradas caducadas.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Acciones',
    },
    audit_walkthrough: {
        title: 'Forseti — Generador de recorrido de auditoría',
        subtitle: 'Genere un recorrido estructurado para el inicio de una auditoría: pregunta de apertura por control, evidencia esperada, preguntas de seguimiento, posibles lagunas.',
        audit_picker_label: 'Auditoría',
        audit_picker_hint: 'Seleccione una auditoría planificada para generar un recorrido.',
        run_label: 'Generar recorrido',
        running: 'Generando…',
        results_heading: 'Recorrido — {count} control(es)',
        tokens_label: '≈ {count} fichas',
        no_controls: 'No hay controles en el alcance. Añada controles al registro de auditoría.',
        col_control: 'controlar',
        col_question: 'Pregunta de apertura',
        col_evidence: 'Evidencia',
        col_gaps: 'Posibles lagunas',
        expected_evidence_label: 'Evidencia esperada',
        followup_probes_label: 'Preguntas de seguimiento',
        accept_label: 'Aceptar recorrido',
        reject_label: 'Rechazar',
        export_label: 'Exportar a DOCX',
        resolve_summary: 'Resolución: {status}',
        resolve_status_accepted: 'Aceptado',
        resolve_status_rejected: 'Rechazado',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'No se pudo generar el recorrido. Inténtelo de nuevo o revise el estado de la auditoría.',
    },
    privilege_creep: {
        title: 'Forseti — Detección de acumulación de privilegios',
        subtitle: 'Análisis periódico de asignaciones de roles. Los hallazgos son consultivos — Forseti nunca revoca de forma automática.',
        run_label: 'Analizar ahora',
        running: 'Analizando…',
        findings_heading: '{count} hallazgo(s)',
        no_findings: 'Sin acumulación de privilegios detectada.',
        col_user: 'Usuario',
        col_job_function: 'Función',
        col_severity: 'Gravedad',
        col_unexpected: 'Permisos inesperados',
        col_remediation: 'Remediación sugerida',
        col_status: 'Estado',
        severity_critical: 'Crítica',
        severity_high: 'Alta',
        severity_medium: 'Media',
        severity_low: 'Baja',
        status_open: 'Abierto',
        status_dismissed: 'Descartado',
        status_remediated: 'Remediado',
        dismiss_label: 'Descartar',
        remediate_label: 'Marcar como remediado',
        note_label: 'Nota (opcional)',
        error_generic: 'No se pudo analizar la acumulación de privilegios. Inténtelo más tarde.',
        advisory_banner: 'Estos hallazgos son consultivos. Forseti nunca revoca permisos automáticamente — revise con el propietario del rol antes de actuar.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Asignación automática de reglas de cumplimiento',
            subtitle: 'Forseti recorre un paquete regulatorio instalado y sugiere reglas de cumplimiento nuevas o actualizadas basadas en los artículos originales.',
            pack_picker_label: 'Paquete regulatorio',
            pack_picker_hint: 'Seleccione un paquete instalado para mapear.',
            run_label: 'Mapa del paquete',
            running: 'Mapeo…',
            results_heading: '{count} sugerencia(s)',
            tokens_label: '≈ {count} tokens de tu depósito mensual',
            no_suggestions: 'No hay sugerencias: es posible que el contenido del paquete ya esté completamente cubierto por las reglas existentes.',
            kind_new: 'Nueva regla',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'No se requiere cambio',
            col_kind: 'amable',
            col_title: 'Título propuesto',
            col_existing: 'Regla existente',
            col_source: 'Fuente',
            proposed_body_label: 'Cuerpo propuesto',
            source_quote_label: 'Cita fuente',
            accept_selected: 'Aceptar seleccionado',
            reject_all: 'Rechazar todo',
            accept_all: 'aceptar todo',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'No se pudo ejecutar la asignación de reglas. Inténtalo de nuevo o comprueba el código del paquete.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Panel de sesión',
            access_log: 'Registro de acceso',
            justification_review: 'Revisión de justificaciones',
            investigations: 'Investigaciones',
            pattern_analysis: 'Análisis de patrones',
            anchoring_status: 'Estado de anclaje',
            configuration: 'Configuración',
            access_policies: 'Políticas de acceso',
            review_campaigns: 'Campañas de revisión',
            evidence_packages: 'Paquetes de evidencia',
        },
        session_dashboard: {
            title: 'Panel de sesión',
            loading: 'Cargando datos del panel...',
            total_sessions: 'Total de sesiones',
            active_sessions: 'Sesiones activas',
            avg_duration: 'Duración media',
            peak_day_sessions: 'Día con más actividad',
            auth_method_breakdown: 'Desglose de métodos de autenticación',
            geo_country_breakdown: 'Desglose por país',
            recent_session_events: 'Eventos de sesión recientes',
            user_id: 'ID de usuario',
            session_id: 'ID de sesión',
            ip_address: 'Dirección IP',
            auth_method: 'Método de autenticación',
            started_at: 'Iniciado el',
            event_type: 'Tipo de evento',
            all_event_types: 'Todos los tipos de evento',
            filter_by_user: 'Filtrar por usuario',
            no_active_sessions: 'Sin sesiones activas',
            no_session_events: 'No se encontraron eventos de sesión',
            revoke: 'Revocar',
            revoke_session: 'Revocar sesión',
            aria_revoke_session: 'Revocar la sesión {user}',
            revoke_reason: 'Motivo',
            revoke_placeholder: 'Proporcione un motivo para revocar esta sesión...',
            session_label: 'Sesión:',
            load_failed: 'Error al cargar el panel de sesión. Por favor, inténtelo de nuevo.',
            events_load_failed: 'Error al cargar los eventos de sesión. Por favor, inténtelo de nuevo.',
            revoke_failed: 'Error al revocar la sesión. Por favor, inténtelo de nuevo.',
            session_events_loaded: '{n} eventos de sesión cargados',
            event_types: {
                session_started: 'Sesión iniciada',
                session_refreshed: 'Sesión actualizada',
                session_expired: 'Sesión caducada',
                session_revoked: 'Sesión revocada',
                session_terminated: 'Sesión terminada',
            },
        },
        access_log: {
            title: 'Registro de acceso',
            user_id: 'Usuario ID',
            resource: 'Recurso',
            action: 'Acción',
            ip_address: 'IP Dirección',
            timestamp: 'Marca de tiempo',
            no_logs: 'No se encontraron access log entries',
            filter_by_user: 'Filtrar por Usuario ID...',
            load_failed: 'Error al load access log. Por favor, inténtelo de nuevo.',
            export_success: 'Exportar initiated con éxito',
            export_failed: 'Error al export access log. Por favor, inténtelo de nuevo.',
            record_count: 'registro',
            record_count_plural: 'registros',
            unclassified: 'Sin clasificar',
            classification_missing: 'Clasificación faltante',
            classification_level: 'Nivel de clasificación',
            response_code: 'Codigo de respuesta',
            access_surface: 'Canal de acceso',
            api_key: 'Clave API',
            classification_levels: {
                public: 'Publico',
                internal: 'Interno',
                confidential: 'Confidencial',
                restricted: 'Restringido',
                unclassified: 'Sin clasificar',
            },
        },
        justification_review: {
            title: 'Revisión de justificaciones',
            user_id: 'Usuario ID',
            resource: 'Recurso',
            justification: 'Justificación',
            submitted_at: 'Enviado el',
            reviewer: 'Revisor',
            no_reviews: 'No se encontraron justification reviews',
            load_failed: 'Error al load justifications. Por favor, inténtelo de nuevo.',
            review_failed: 'Error al review justification. Por favor, inténtelo de nuevo.',
            decline_failed: 'Error al decline access. Por favor, inténtelo de nuevo.',
            justification_count: 'justificación',
            justification_count_plural: 'justificaciones',
            reviewed: 'Revisado',
            concerning: 'Preocupante',
            escalated: 'Escalado',
            link_investigation_placeholder: 'Vincular a una investigación (opcional)',
            review_queue: 'Cola de revisión',
            access_id_label: 'ID de acceso',
            access_id_placeholder: 'ID de entrada del registro de acceso',
            resource_type_label: 'Tipo de recurso',
            resource_type_placeholder: 'p. ej. document, database',
            resource_id_label: 'ID del recurso',
            resource_id_placeholder: 'Identificador del recurso',
            reason_placeholder: 'Motivo para acceder a este recurso...',
            create_failed: 'Error al create justification',
            unclassified: 'Sin clasificar',
            review_statuses: {
                pending: 'Pendiente',
                reviewed: 'Revisado',
                concerning: 'En cuanto a',
                escalated: 'escalado',
            },
            reason_codes: {
                business_need: 'Necesidad empresarial',
                legal_obligation: 'Obligación Legal',
                data_subject_request: 'Solicitud del interesado',
                audit_requirement: 'Requisito de auditoría',
                incident_response: 'Respuesta a incidentes',
                research: 'Investigación',
                administrative: 'Administrativo',
                other: 'Otro',
            },
            classification_level: 'Nivel de clasificación',
            review_decision: 'Decisión de revisión',
        },
        investigations: {
            title: 'Espacio de investigación',
            new: 'Nuevo Investigation',
            subject: 'Sujeto',
            investigator: 'Investigador',
            opened_at: 'Abierto En',
            findings: 'Hallazgos',
            no_investigations: 'No se encontraron investigations',
            load_failed: 'Error al load investigations. Por favor, inténtelo de nuevo.',
            create_failed: 'Error al create investigation. Por favor, inténtelo de nuevo.',
            timeline_add_failed: 'Error al add timeline entry. Por favor, inténtelo de nuevo.',
            link_access_failed: 'Error al link access ids. Por favor, inténtelo de nuevo.',
            link_justification_failed: 'Error al link justification ids. Por favor, inténtelo de nuevo.',
            close_failed: 'Error al close investigation. Por favor, inténtelo de nuevo.',
            export_success: 'Exportar initiated con éxito',
            export_failed: 'Error al export investigation. Por favor, inténtelo de nuevo.',
            refresh_failed: 'Error al refresh investigation detail. Por favor, inténtelo de nuevo.',
            update_failed: 'Error al actualizar la investigación',
            investigation_count: 'investigación',
            investigation_count_plural: 'investigaciones',
            title_placeholder: 'Título de la investigación',
            description_placeholder: 'Descripción detallada de la investigación...',
            assignee_placeholder: 'ID de usuario del asignado (opcional)',
            timeline_action_placeholder: 'p.ej., Registros de acceso revisados',
            timeline_note_placeholder: 'Nota opcional',
            access_ids_placeholder: 'acceso-id-1, acceso-id-2',
            justification_ids_placeholder: 'id-justificación-1, id-justificación-2',
            findings_placeholder: 'Resuma los hallazgos de la investigación...',
            by_actor: 'by',
            close_investigation: 'Cerrar investigación',
            add_timeline_failed: 'Error al add timeline entry. Por favor, inténtelo de nuevo.',
            fetch_failed: 'Error al fetch investigations. Por favor, inténtelo de nuevo.',
            change_status: 'Cambiar estado de la investigación',
            details: 'Detalles de la investigación',
            link_evidence: 'Vincular evidencia',
            link_justifications: 'Vincular justificaciones',
            access_ids_label: 'IDs de acceso',
            justification_ids_label: 'IDs de justificación',
            access_ids_link_placeholder: 'acceso-id-1, acceso-id-2',
            justification_ids_link_placeholder: 'id-justificación-1, id-justificación-2',
            investigations_loaded: '{n} investigations cargados',
            status_changed_to: 'Estado cambiado a {status}',
            add_timeline_entry: 'Añadir entrada de cronología',
            add_access_ids: 'Añadir IDs de acceso',
            add_justification_ids: 'Añadir IDs de justificación',
            investigation_types: {
                anomaly_review: 'Revisión de anomalías',
                complaint_driven: 'Impulsado por quejas',
                scheduled_review: 'Revisión programada',
                whistleblower_report: 'Informe de denunciante',
            },
            severity_labels: {
                low: 'Bajo',
                medium: 'Medio',
                high: 'Alto',
                critical: 'Crítico',
            },
            status_labels: {
                open: 'Abierto',
                in_progress: 'En progreso',
                pending_review: 'Revisión pendiente',
                closed_substantiated: 'Cerrado (fundamentado)',
                closed_unsubstantiated: 'Cerrado (sin fundamento)',
                closed_inconclusive: 'Cerrado (no concluyente)',
            },
            disposition_labels: {
                substantiated: 'fundamentado',
                unsubstantiated: 'Sin fundamento',
                inconclusive: 'No concluyente',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Análisis de patrones',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Cargando pattern data...',
            anomalies_detected: 'Anomalías detectadas',
            patterns: 'Patrones',
            by_user: 'Por Usuario',
            by_resource: 'Por recurso',
            time_range: 'Rango de tiempo',
            load_failed: 'Error al analyze patterns. Por favor, inténtelo de nuevo.',
            top_resources: '10 mejores',
            access_by_hour: 'Acceso por hora del día',
            access_by_day: 'Acceso por Day de Semana',
            fetch_failed: 'Error al fetch pattern analysis. Por favor, inténtelo de nuevo.',
            analysis_loaded: 'Análisis de patrones cargado para {user}, {n} sesiones encontradas',
            date_format_placeholder: 'DD/MM/AAAA',
            access_by_hour_heading: 'Acceso por hora del día',
            access_by_day_heading: 'Acceso por día de la semana',
            by_classification: 'Por nivel de clasificación',
            top_accessed_resources: 'Recursos más consultados',
            user_access_summary: 'Resumen de acceso de usuario',
            days: {
                sunday: 'Domingo',
                monday: 'Lunes',
                tuesday: 'Martes',
                wednesday: 'Miércoles',
                thursday: 'Jueves',
                friday: 'Viernes',
                saturday: 'Sábado',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Estado de anclaje',
            loading: 'Cargando anchoring data...',
            anchored: 'Anclado',
            pending: 'Pendiente',
            failed: 'Falló',
            last_anchor: 'Último anclaje',
            no_records: 'No se encontraron anchoring records',
            status_load_failed: 'Error al load anchoring status. Por favor, inténtelo de nuevo.',
            proofs_load_failed: 'Error al load access proofs. Por favor, inténtelo de nuevo.',
            verify_failed: 'Error al verify proof chain. Por favor, inténtelo de nuevo.',
            anchor_failed: 'Error al anchor access proof. Por favor, inténtelo de nuevo.',
            batch_anchor_failed: 'Error al batch anchor access proofs. Por favor, inténtelo de nuevo.',
            proof_count: 'prueba',
            proof_count_plural: 'pruebas',
            anchor_single: 'Anclaje individual',
            anchor_batch: 'Anclaje por lote',
            access_id_placeholder: 'Introduzca access ID to anchor',
            batch_ids_placeholder: 'ID de acceso-1, ID de acceso-2, ID de acceso-3',
            verify_chain: 'Verificar cadena',
            chain_breaks: 'Rupturas de cadena',
            total_checked: 'Total verificado',
            anchor_placeholder: 'Introduzca access ID to anchor',
            fetch_failed: 'Error al fetch anchoring status. Por favor, inténtelo de nuevo.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Error al fetch proofs. Por favor, inténtelo de nuevo.',
            access_id: 'ID de acceso',
            chain_sequence: 'Sec. cadena',
            proof_hash_label: 'Hash de la prueba',
            batch_id: 'ID de lote',
            proofs_loaded: '{n} access proofs cargados',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Pendiente',
                confirmed: 'Confirmado',
                failed: 'Fallido',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Cadena',
                blockchain_anchor: 'Ancla de cadena de bloques',
                zkp_stub: 'Trozo ZKP',
            },
        },
        configuration: {
            title: 'Configuración',
            session_settings: 'Sesión Configuración',
            log_retention: 'Registro Retención (days)',
            anomaly_threshold: 'Umbral de anomalía',
            anchoring_enabled: 'Anclaje activado',
            save_config: 'Guardar Configuración',
            load_failed: 'Error al load configuration. Por favor, inténtelo de nuevo.',
            save_success: 'Configuración guardado con éxito',
            save_failed: 'Error al save configuration. Por favor, inténtelo de nuevo.',
            justification_rules: 'Reglas de justificación',
            reason_codes: 'Códigos de motivo',
            anchoring_settings: 'Configuración de anclaje',
            anchoring_method: 'Método de anclaje',
            anchoring_frequency: 'Frecuencia de anclaje',
            batch_interval: 'Lote Intervalo (minutes)',
            eligibility_criteria: 'Criterios de elegibilidad',
            add_criterion_placeholder: 'Agregar eligibility criterion...',
            retention_settings: 'Retención Configuración',
            standard_retention: 'Estándar Retención (days)',
            sensitive_retention: 'Sensible Retención (days)',
            working_hours: 'Horas de trabajo',
            work_start: 'Inicio Hora',
            work_end: 'Fin Hora',
            timezone: 'Zona horaria',
            timezone_placeholder: 'p. ej. Asia/Ho_Chi_Minh',
            working_days: 'Días laborables',
            concurrent_sessions: 'Límites de sesiones concurrentes',
            concurrent_threshold: 'Umbral de sesiones simultáneas',
            classification_level: 'Clasificación Nivel',
            prompt_mode: 'Modo de aviso',
            grace_period: 'Período de gracia (s)',
            code_placeholder: 'código',
            is_custom: 'Personalizado',
            label_placeholder: 'Etiqueta',
            description_placeholder: 'Descripción',
            custom: 'Personalizado',
            fetch_failed: 'Error al fetch configuration. Por favor, inténtelo de nuevo.',
            classification_levels: {
                public: 'Público',
                internal: 'Interno',
                confidential: 'Confidencial',
                restricted: 'Restringido',
                unclassified: 'sin clasificar',
            },
            prompt_modes: {
                reason_only: 'Sólo razón',
                reason_text: 'Razón + Texto',
                reason_text_supervisor: 'Motivo + Texto + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Cadena',
                blockchain_anchor: 'Ancla de cadena de bloques',
                zkp_stub: 'Trozo ZKP',
            },
            anchoring_frequencies: {
                realtime: 'En tiempo real',
                batched: 'por lotes',
                on_demand: 'Bajo demanda',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Políticas de acceso',
            create_policy: 'Crear política de acceso',
            policy_name: 'Nombre de la política',
            framework_code: 'Marco normativo',
            select_framework: 'Seleccionar un marco normativo...',
            review_frequency: 'Frecuencia de revisión',
            scope: 'Alcance',
            no_policies_found: 'No se encontraron políticas de acceso.',
            policy_created: 'Política de acceso creada',
            frequency_labels: {
                monthly: 'Mensual',
                quarterly: 'Trimestral',
                semi_annual: 'Semestral',
                annual: 'Anual',
            },
        },
        review_campaigns: {
            title: 'Campañas de revisión de acceso',
            create_campaign: 'Crear campaña de revisión',
            reviewer: 'Revisor',
            due_date: 'Fecha de vencimiento',
            certify: 'Certificar',
            no_campaigns_found: 'No se encontraron campañas de revisión de acceso.',
            status_open: 'Abierta',
            status_in_review: 'En revisión',
            status_completed: 'Completada',
            campaign_created: 'Campaña de revisión creada',
            review_certified: 'Revisión de acceso certificada',
            certify_campaign: 'Certificar campaña {id}',
            policy_id: 'ID de la política',
            select_policy: 'Seleccionar una política...',
            reviewer_ids_label: 'Revisores',
            select_reviewers: 'Seleccionar revisores...',
            status_labels: {
                open: 'Abierto',
                in_review: 'En revisión',
                in_progress: 'En curso',
                cancelled: 'Cancelada',
                completed: 'Completado',
            },
        },
        evidence_packages: {
            title: 'Paquetes de evidencia',
            generate_package: 'Generar paquete de evidencia',
            export_type: 'Tipo de exportación',
            package_type: 'Tipo de paquete',
            framework_scope: 'Alcance del marco',
            period: 'Período',
            generated_by: 'Generado por',
            generated_at: 'Generado el',
            no_packages_found: 'No se encontraron paquetes de evidencia.',
            package_generated: 'Paquete de evidencias generado',
            export_type_full: 'Exportación completa',
            export_type_summary: 'Resumen',
            export_type_certification_only: 'Solo certificación',
            package_type_access_review: 'Revisión de acceso',
            package_type_investigation: 'Investigación',
            package_type_certification: 'Certificación',
            package_type_authority_request: 'Solicitud de autoridad',
            period_start: 'Inicio del período',
            period_end: 'Fin del período',
            select_frameworks: 'Seleccionar marcos...',
            completeness_col: 'Completitud',
            status_col: 'Estado',
            generated_by_col: 'Generado por',
        },
        evidence_package_detail: {
            title: 'Detalle del paquete de evidencias',
            not_found: 'Paquete de evidencias no encontrado.',
            package_metadata: 'Metadatos del paquete',
            status: 'Estado',
            item_count: 'Número de elementos',
            disposition: 'Disposición',
            closed_at: 'Cerrado el',
            completeness_dashboard: 'Panel de completitud',
            no_completeness_data: 'No hay datos de completitud disponibles.',
            controls_covered: 'controles cubiertos',
            missing: 'Faltantes',
            sign_off_tracker: 'Seguimiento de aprobación',
            stage_framework_owner: 'Propietario del marco',
            stage_legal_compliance: 'Legal y Cumplimiento',
            stage_internal_audit: 'Auditoría interna',
            approved: 'Aprobado',
            returned: 'Devuelto',
            pending: 'Pendiente',
            approve: 'Aprobar',
            return_btn: 'Devolver',
            sign_off_submitted: 'Aprobación enviada',
            artifacts: 'Artefactos',
            no_artifacts: 'No se encontraron artefactos.',
            template_id: 'ID de plantilla',
            description: 'Descripción',
            classification: 'Clasificación',
            evidence_type: 'Tipo de evidencia',
            framework_section: 'Sección del marco',
            history: 'Historial de aprobación',
            expand: 'Expandir',
            collapse: 'Contraer',
            action: 'Acción',
            actor: 'Actor',
            timestamp: 'Marca de tiempo',
            comment: 'Comentario',
            close_case: 'Cerrar caso',
            confirm_close: 'Confirmar cierre',
            closure_notes: 'Notas de cierre',
            disposition_compliant: 'Conforme',
            disposition_non_compliant: 'No conforme',
            disposition_partial: 'Parcial',
            case_closed: 'Paquete de evidencias cerrado',
            export_package: 'Exportar paquete',
            export_complete: 'Paquete de evidencias exportado',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Panel de control',
            properties: 'Propiedades',
            groups: 'Grupos',
            bindings: 'Vinculaciones',
            change_requests: 'Solicitudes de cambio',
            comparison: 'Comparación',
            inconsistencies: 'Inconsistencias',
            snapshot_schedules: 'Programación de instantáneas',
            golden_templates: 'Plantillas de referencia',
            evidence_packs: 'Paquetes de evidencia',
            audit: 'Auditoría',
        },
        select_property: 'Seleccionar propiedad…',
        select_policy: 'Seleccionar política…',
        select_properties: 'Seleccionar propiedades…',
        select_target_properties: 'Seleccionar propiedades objetivo…',
        select_owner: 'Seleccionar propietario…',
        select_technical_contact: 'Seleccionar contacto técnico…',
        property_statuses: {
            active: 'Activo',
            inactive: 'Inactivo',
            pending: 'Pendiente',
            decommissioned: 'Fuera de servicio',
        },
        dashboard: {
            title: 'Panel de propiedades cruzadas',
            loading: 'Cargando dashboard data...',
            total_properties: 'Total de propiedades',
            active_properties: 'Propiedades activas',
            snapshot_coverage: 'Cobertura de instantáneas',
            properties_covered: 'propiedades cubiertas',
            total_bindings: 'Total de enlaces',
            deployed_bindings: 'Implementado',
            pending_bindings: 'Implementación pendiente',
            deployment_progress: 'Progreso de implementación',
            on_target: 'En el objetivo',
            below_target: 'Bajo el objetivo',
            target_label: 'Objetivo',
            total_groups: 'Total Grupos',
            pending_changes: 'Cambios pendientes',
            inconsistencies: 'Inconsistencias',
            recent_activity: 'Actividad reciente',
            no_recent_activity: 'Sin actividad reciente.',
            fetch_error: 'Error al load dashboard. Por favor, inténtelo de nuevo.',
        },
        properties: {
            title: 'Registro de propiedades',
            new: 'Nuevo Property',
            edit: 'Editar propiedad',
            property_name: 'Nombre',
            property_type: 'Tipo',
            identifier: 'Identificador',
            environment: 'Entorno',
            owner_id: 'Propietario ID',
            technical_contact_id: 'ID del contacto técnico',
            decommission: 'Desmantelar',
            confirm_decommission: '¿Está seguro de que desea desmantelar esta propiedad?',
            no_properties: 'No se encontraron properties',
            all_environments: 'Todos los entornos',
            all_types: 'Todos los tipos',
            cmp_instance_id: 'CMP ID de instancia',
            deployment_date: 'Fecha de implementación',
            identifier_placeholder: 'por ejemplo, www.example.com o com.example.app',
            owner_name: 'Propietario',
            technical_contact_name: 'Contacto técnico',
            sensitive_data_categories: 'Categorías de datos confidenciales',
            sensitive_data_categories_placeholder: 'Seleccione categorías de datos confidenciales',
            requires_enhanced_review: 'Requiere revisión mejorada',
            sensitive_review_status: 'Revisión de datos confidenciales',
            sensitive_review_pending: 'Revisión pendiente',
            sensitive_review_approved: 'Aprobado',
            sensitive_review_rejected: 'Rechazado',
            sensitive_review_submit: 'Enviar reseña',
            sensitive_review_approve: 'Aprobar',
            sensitive_review_reject: 'Rechazar',
            sensitive_review_notes: 'Notas de revisión',
            sensitive_review_notes_placeholder: 'Introducir notas de revisión...',
            record_withdrawal: 'Retiro de registros',
            withdrawal_subject_id: 'ID del sujeto',
            withdrawal_consent_id: 'Identificación de consentimiento',
            withdrawal_reason: 'Razón',
            withdrawal_downstream_systems: 'Sistemas aguas abajo',
            withdrawal_downstream_placeholder: 'sistema-1, sistema-2',
            withdrawal_success: 'La retirada se propagó con éxito.',
            withdrawal_error: 'No se pudo propagar el retiro.',
            withdrawal_data_subject_id: 'ID del interesado',
            withdrawal_data_subject_id_placeholder: 'p. ej. usr_abc123',
            withdrawal_consent_record_id: 'ID de registro de consentimiento',
            withdrawal_consent_record_id_placeholder: 'p. ej. consent_xyz789',
            withdrawal_purposes: 'Propósitos retirados',
            withdrawal_purposes_placeholder: 'Seleccionar propósitos para retirar el consentimiento para...',
            withdrawal_withdrawn_at: 'Fecha de retiro',
            withdrawal_tasks_created: 'Tareas creadas',
            withdrawal_affected_bindings: 'Enlaces afectados',
            withdrawal_affected_activities: 'Actividades de procesamiento afectadas',
            withdrawal_history: 'Historial de retiros',
        },
        groups: {
            title: 'Grupos de propiedades',
            new: 'Nuevo grupo',
            group_name: 'Grupo Nombre',
            member_count: 'Miembros',
            no_groups: 'No se encontraron property groups',
            policies: 'Políticas',
            confirm_delete: '¿Estás seguro de que deseas eliminar este grupo?',
        },
        bindings: {
            title: 'Matriz de vinculaciones',
            property: 'Propiedad',
            purpose: 'Finalidad',
            legal_basis: 'Base legal',
            bound: 'Vinculado',
            no_bindings: 'No se encontraron bindings',
            bind_policy: 'Política de vinculación',
            bulk_bind: 'Enlace masivo',
            bind_policy_to_property: 'Vincular la política a la propiedad',
            bulk_bind_policy: 'Política de enlace masivo',
            unbound: 'Sin consolidar',
            policy_id: 'ID de política',
            version_placeholder: 'por ejemplo, 1.0.0',
            property_ids_placeholder: 'id-prop-1, id-prop-2, id-prop-3',
            binding_name: 'Vinculación',
            property_name: 'Nombre de propiedad',
            policy_name: 'Nombre de política',
            bindings_list: 'Lista de vinculaciones',
            binding_actions: {
                start_deploy: 'Iniciar despliegue',
                mark_deployed: 'Marcar como desplegado',
                rollback: 'Revertir',
            },
        },
        change_requests: {
            title: 'Proceso de solicitudes de cambio',
            new: 'Nuevo Change Request',
            requester: 'Solicitante',
            change_type: 'Tipo de cambio',
            property: 'Propiedad',
            submitted_at: 'Enviado el',
            no_requests: 'No se encontraron change requests',
            fetch_pipeline_error: 'Error al fetch pipeline. Por favor, inténtelo de nuevo.',
            fetch_list_error: 'Error al fetch change requests. Por favor, inténtelo de nuevo.',
            create_error: 'Error al create change request. Por favor, inténtelo de nuevo.',
            action_error: 'Error al perform action. Por favor, inténtelo de nuevo.',
            approver_name: 'Aprobador',
            placeholder_target_props: 'ID-prop-1, ID-prop-2',
            view_all: 'Todos',
            view_my_properties: 'Mis Propiedades',
            stage_requests: 'Solicitudes {stage}',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Espacio de comparación',
            select_properties: 'Seleccione Properties to Compare',
            compare: 'Comparar',
            no_comparison: 'Seleccione at least two properties to compare.',
            fetch_error: 'Error al fetch comparison reports. Por favor, inténtelo de nuevo.',
            run_error: 'Error al run comparison. Por favor, inténtelo de nuevo.',
            save_notes_error: 'Error al save notes. Por favor, inténtelo de nuevo.',
            scan_complete: 'Inconsistency scan completado con éxito',
            scan_error: 'Error al run inconsistency scan. Por favor, inténtelo de nuevo.',
            min_two_properties: 'Proporcione al menos 2 IDs de propiedad para la comparación.',
            view_inconsistencies: 'Ver inconsistencias',
            click_to_select: 'Haga clic para seleccionar dos o más propiedades',
            no_properties_available: 'No hay propiedades disponibles',
            no_matching_properties: 'No hay propiedades coincidentes',
            select_at_least_two_props: 'Seleccione al menos dos propiedades del menú desplegable.',
            no_differences_found: 'No se encontraron diferencias entre las propiedades seleccionadas.',
            found_inconsistencies: 'Se encontraron {count} inconsistencias en {properties} propiedades y {bindings} enlaces.',
        },
        inconsistencies: {
            title: 'Lista de inconsistencias',
            property: 'Propiedad',
            field: 'Campo',
            expected: 'Esperado',
            actual: 'Real',
            severity: 'Gravedad',
            no_inconsistencies: 'No se encontraron inconsistencies',
            trend: 'Tendencia de inconsistencia',
            trend_chart_label: 'Gráfico de tendencias de inconsistencia que muestra recuentos a lo largo del tiempo',
            acknowledge: 'Reconocer',
            resolve: 'Resolver',
            suppress: 'Suprimir',
            cr_id_placeholder: 'ID CR',
            view_all: 'Todos',
            view_my_properties: 'Mis Propiedades',
            view_dpo_review: 'DPO Cola de revisión',
            change_request_id_required: 'Se requiere el ID de solicitud de cambio para resolver esta incoherencia.',
        },
        snapshot_schedules: {
            title: 'Programación de instantáneas',
            new: 'Nuevo programar',
            schedule_name: 'Programar Nombre',
            frequency: 'Frecuencia',
            next_run: 'Siguiente Ejecutar',
            last_run: 'Último Ejecutar',
            no_schedules: 'No se encontraron snapshot schedules',
            target_type: 'Tipo de objetivo',
            target_id: 'ID de objetivo',
            custom_frequency_days: 'Frecuencia personalizada (días)',
            time_of_day: 'Hora del día',
            time: 'tiempo',
            retention_days: 'Retención (días)',
            execute_now: 'Ejecutar ahora',
            placeholder_days: 'por ejemplo, 10',
            target_name: 'Nombre del objetivo',
        },
        golden_templates: {
            title: 'Plantillas de referencia',
            new: 'Nuevo plantilla',
            template_name: 'Plantilla Nombre',
            property_type: 'Tipo de propiedad',
            no_templates: 'No se encontraron golden templates',
            applicable_group_ids: 'ID de grupo aplicables (separados por comas)',
            applicable_groups: 'Grupos aplicables',
            purposes: 'Propósitos',
            legal_bases: 'Bases Legales',
            banner_config: 'Configuración de banner',
            consent_settings: 'Configuración de consentimiento',
            group_ids_placeholder: 'ID-grupo-1, ID-grupo-2',
            apply_to_property: 'Aplicar a Propiedad',
            apply_to_group: 'Aplicar a Grupo',
            apply_success: 'Plantilla aplicada correctamente.',
            applications: 'Historial de aplicaciones',
            no_applications: 'Aún no hay aplicaciones de plantilla.',
            no_configuration_details: 'La plantilla no tiene detalles de configuración. Edite la plantilla para añadir finalidades, bases legales, ajustes de banner o ajustes de consentimiento.',
        },
        rollouts: {
            title: 'Despliegues de plantillas',
            rollout_status: 'Estado del despliegue',
            confirmed: 'Confirmado',
            pending: 'Pendiente',
            failed: 'Fallido',
            confirm: 'Confirmar Propiedad',
            confirm_success: 'Propiedad confirmada en el despliegue.',
            can_close: 'Todas las propiedades confirmadas — despliegue completado',
            cannot_close: 'Esperando confirmaciones de propiedades',
            no_rollouts: 'No se encontraron despliegues.',
            in_progress: 'En progreso',
            completed: 'Completado',
            cancelled: 'Cancelado',
            reject: 'Rechazar Propiedad',
            reject_success: 'Propiedad rechazada del despliegue.',
            confirm_all: 'Confirmar Todos',
            reject_all: 'Rechazar Todos',
            confirm_label: 'Confirmar despliegue para',
            reject_label: 'Rechazar despliegue para',
            confirm_all_label: 'Confirmar Todos los Despliegues Pendientes',
            reject_all_label: 'Rechazar Todos los Despliegues Pendientes',
            no_properties_in_rollout: 'Este despliegue no tiene propiedades.',
        },
        analytics: {
            title: 'Análisis de propiedades',
            opt_in_rate: 'Tasa de aceptación',
            opt_out_rate: 'Tasa de rechazo',
            withdrawal_rate: 'Tasa de retirada',
            total_consents: 'Total de consentimientos',
            total_withdrawals: 'Total de retiradas',
            compliance_score: 'Puntuación de cumplimiento',
            non_compliant: 'Propiedades no conformes',
            no_non_compliant: 'Todas las propiedades son conformes.',
            threshold: 'Umbral de cumplimiento',
            property_id: 'ID de propiedad',
            compliant: 'Cumple',
            non_compliant_status: 'No conforme',
            compliance_help_summary: '¿Cómo se calcula la puntuación de cumplimiento y cómo lo soluciono?',
            compliance_help_intro: 'Una propiedad se marca como No Conforme cuando su puntuación es inferior al 80%. La puntuación se calcula como:',
            compliance_help_with_template_label: 'Si se aplica una Plantilla Dorada:',
            compliance_help_with_template_text: '% de claves de configuración (fines, legal_bases, banner_config, consent_settings) que coinciden exactamente con la plantilla.',
            compliance_help_no_template_label: 'Si no se aplica ninguna plantilla dorada:',
            compliance_help_no_template_text: '% de enlaces (tipos de consentimiento asignados a la propiedad) con deployment_status = implementado. Una propiedad sin enlaces tiene por defecto 100%.',
            compliance_help_fix_intro: 'Para eliminar una propiedad de esta lista, realice una de las siguientes acciones:',
            compliance_help_fix_deploy_label: 'Implementar sus enlaces',
            compliance_help_fix_deploy_text: 'Abra la página Binding Matrix y establezca el estado de implementación de cada enlace en Implementado.',
            compliance_help_fix_template_label: 'Aplicar una plantilla dorada',
            compliance_help_fix_template_text: 'use Aplicar al grupo en la plantilla y luego confirme la implementación de esta propiedad. Asegúrese de que la configuración de la propiedad coincida con la plantilla.',
            compliance_help_fix_decommission_label: 'Desmantelar la propiedad',
            compliance_help_fix_decommission_text: 'use el botón Desmantelar en el modal de detalles de la propiedad si ya no está en uso.',
            compliance_help_fix_no_bindings_label: 'Quitar todas las ataduras',
            compliance_help_fix_no_bindings_text: 'Si una propiedad no tiene plantilla ni enlaces, obtiene una puntuación del 100 % de forma predeterminada.',
        },
        evidence_packs: {
            title: 'Paquetes de evidencia',
            new: 'Nuevo Pack',
            pack_name: 'Nombre del paquete',
            properties: 'Propiedades',
            generated_at: 'Generado el',
            no_packs: 'No se encontraron evidence packs',
            generate: 'Generar',
            content_type_consent_records: 'Consentimiento Registros',
            content_type_policy_versions: 'Versiones de la política',
            content_type_binding_history: 'Historial de vinculación',
            content_type_change_requests: 'Solicitudes de cambio',
            content_type_inconsistency_reports: 'Informes de inconsistencia',
            content_type_snapshot_data: 'Datos de instantánea',
            content_type_audit_trail: 'Pista de auditoría',
        },
        audit: {
            title: 'Auditoría de propiedades de consentimiento',
            event_type: 'Evento Tipo',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de la entidad',
            actor: 'Actor',
            timestamp: 'Marca de tiempo',
            no_events: 'No se encontraron audit events',
            entity_type_property: 'Propiedad',
            entity_type_group: 'Grupo',
            entity_type_binding: 'Vinculación',
            entity_type_change_request: 'Solicitud de cambio',
            entity_type_golden_template: 'Plantilla dorada',
            entity_type_snapshot_schedule: 'Programación instantánea',
            entity_type_inconsistency: 'Inconsistencia',
            entity_type_evidence_pack: 'Paquete de evidencia',
            action_created: 'Creado',
            action_updated: 'Actualizado',
            action_deleted: 'Eliminado',
            action_decommissioned: 'Dado de baja',
            action_status_changed: 'Estado cambiado',
            action_submitted: 'Enviado',
            action_approved: 'Aprobado',
            action_rejected: 'Rechazado',
            action_deployed: 'Desplegado',
            action_rolled_back: 'Revertido',
            action_paused: 'Pausado',
            action_resumed: 'Reanudado',
            action_cancelled: 'Cancelado',
            action_acknowledged: 'Reconocido',
            action_resolved: 'Resuelto',
            action_suppressed: 'Suprimido',
            action_executed: 'Ejecutado',
        },
        property_types: {
            website: 'Sitio web',
            mobile_app: 'Aplicación móvil',
            ott: 'OTT',
            iot_device: 'Dispositivo IoT',
            kiosk: 'Quiosco',
            api_service: 'Servicio API',
            sdk: 'SDK',
        },
        environments: {
            production: 'Producción',
            staging: 'Preproducción',
            development: 'Desarrollo',
            testing: 'Pruebas',
        },
        group_types: {
            brand: 'Marca',
            region: 'Región',
            product_line: 'Línea de productos',
            business_unit: 'Unidad de negocio',
            custom: 'Personalizado',
        },
        target_types: {
            property: 'Propiedad',
            group: 'Grupo',
            all: 'Todos',
        },
        frequencies: {
            hourly: 'Cada hora',
            daily: 'Diario',
            weekly: 'Semanal',
            monthly: 'Mensual',
            custom: 'Personalizado',
        },
        binding_statuses: {
            current: 'Actual',
            outdated: 'Obsoleto',
            pending: 'Pendiente',
            deploying: 'Desplegando',
            rolled_back: 'Revertido',
        },
        sensitive_categories: {
            health: 'Datos de salud',
            biometric: 'Datos biométricos',
            genetic: 'Datos genéticos',
            racial_ethnic: 'Origen racial/étnico',
            political_opinions: 'Opiniones políticas',
            religious_beliefs: 'Creencias religiosas',
            trade_union: 'Membresía sindical',
            sexual_orientation: 'Orientación sexual',
            criminal_records: 'Antecedentes penales',
            financial: 'Datos financieros',
            children: 'Datos de los niños',
            geolocation: 'Geolocalización precisa',
        },
        export_csv: 'Exportar CSV',
        exporting: 'Exportando...',
        page_x_of_y: 'Pagina {page} de {total}',
        regulatory: {
            capture_method_guidance: 'PDPL requiere prueba de consentimiento para coincidir con el canal de recopilación. Los tipos de propiedad se asignan a los métodos de captura de consentimiento: sitio web/OTT = consentimiento basado en banner, aplicación móvil/SDK = flujo de consentimiento en la aplicación, quiosco = captura de consentimiento físico/en pantalla, API servicio = consentimiento programático, dispositivo IoT = mecanismo de consentimiento a nivel de dispositivo.',
            dashboard_metrics_info: 'Este panel proporciona al DPO una vista única de la postura del consentimiento en todas las propiedades digitales. Las métricas clave incluyen: porcentaje de cobertura de enlaces (implementados frente a enlaces totales), cumplimiento de la plantilla a través del widget de inconsistencias, solicitudes de cambios pendientes que requieren revisión y desglose del estado de las propiedades que muestra las propiedades con estados activos, inactivos o fuera de servicio. Las propiedades con categorías de datos confidenciales se rastrean a través del registro de la propiedad.',
            change_request_impact_info: 'Las solicitudes de cambio que afectan las propiedades del consentimiento deben documentar el impacto regulatorio: qué PDPL requisitos se ven afectados, si el cambio afecta el procesamiento de datos confidenciales (Art. 2.4) y si el cambio requiere un nuevo consentimiento de los interesados. El resumen de impacto en cada solicitud de cambio detalla las PDPL referencias regulatorias para evidencia de auditoría.',
            outdated_binding_warning: 'Los enlaces con estado "obsoleto" indican que una propiedad ejecuta una versión de política que no está actualizada. Es posible que estas propiedades no reflejen los últimos PDPL requisitos o actualizaciones de políticas organizativas. Revise y actualice los enlaces obsoletos rápidamente para mantener el cumplimiento de PDPL en todas las propiedades de consentimiento.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Panel de control',
            market_packs: 'Paquetes de mercado',
            language_packs: 'Paquetes de idiomas',
            frameworks: 'Marcos regulatorios',
            mappings: 'Asignaciones de módulos',
            submissions: 'Envíos de paquetes',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Vista previa de traducción',
            audit: 'Pista de auditoría',
        },
        dashboard: {
            title: 'Panel de localización',
            loading: 'Cargando dashboard data...',
            active_market_packs: 'Activo Mercado Packs',
            published_packs_available: 'Paquetes de mercado publicados disponibles',
            language_packs: 'Paquetes de idioma',
            total_language_packs: 'Total de paquetes de idioma registrados',
            active_regulatory_frameworks: 'Activo Regulatory Marcos',
            frameworks_active: 'Marcos con estado activo',
            pending_pack_submissions: 'Presentaciones de paquetes pendientes',
            submissions_awaiting_review: 'Presentaciones pendientes de revisión',
            recent_audit_events: 'Reciente Auditoría Eventos',
            no_recent_audit: 'Sin eventos de auditoría recientes.',
            event_type: 'Evento Tipo',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de la entidad',
            actor: 'Actor',
            timestamp: 'Marca de tiempo',
        },
        market_packs: {
            title: 'Catálogo de paquetes de mercado',
            new: 'Nuevo Market Pack',
            pack_name: 'Nombre del paquete',
            market: 'Mercado',
            locale: 'Configuración regional',
            no_packs: 'No se encontraron market packs',
        },
        language_packs: {
            title: 'Paquetes de idiomas',
            new: 'Nuevo Language Pack',
            language: 'Idioma',
            locale_code: 'Código de idioma',
            completeness: 'Completitud',
            completeness_detail: 'Detalle de integridad',
            overall_completeness: 'Integridad general',
            total_keys: 'Claves totales',
            translated_keys: 'Claves traducidas',
            missing_keys: 'llaves faltantes',
            namespace_breakdown: 'Desglose del espacio de nombres',
            namespace: 'Espacio de nombres',
            translated: 'Traducido',
            no_packs: 'No se encontraron language packs',
            install_language_pack: 'Instalar paquete de idioma',
            available_packs: 'Paquetes de idioma disponibles',
            install: 'Instalar',
            all_installed: 'Todos los paquetes de idioma ya están instalados.',
            install_success: 'Paquete de idioma instalado correctamente.',
            market: 'Mercado',
            featured: 'Destacado',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Marcos regulatorios',
            new: 'Nuevo marco',
            framework_name: 'Marco Nombre',
            jurisdiction: 'Jurisdicción',
            effective_date: 'Fecha de vigencia',
            no_frameworks: 'No se encontraron regulatory frameworks',
            view_requirements: 'Ver requisitos',
            hide_requirements: 'Ocultar requisitos',
            show_legacy: 'Mostrar marcos legales anteriores',
            effective_until: 'Vigente hasta',
            effective_on: 'Vigente desde',
            replaced_by: 'Reemplazado por',
            replaces_label: 'Reemplaza',
            status_active: 'Activo',
            status_draft: 'Borrador',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Anterior',
            status_upcoming: 'Próximo',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Lista de seguimiento',
            watchlist_empty: 'No hay borradores ni marcos próximos',
            watchlist_empty_desc: 'Los marcos con estado "borrador" o "próximo" aparecerán aquí para su seguimiento.',
            expected_effective: 'Efectivo esperado',
            effective_date_tbd: 'Fecha de entrada en vigor: por determinar',
            activate: 'Activar',
            ai_law_title: 'Vietnam AI Ley',
            ai_risk_classification: 'AI Clasificación de Riesgo',
            ai_system_dossier: 'AI Dossier del Sistema',
            ai_labeling_checklist: 'AI Etiquetado y divulgación',
            ai_incident_report: 'AI Informe de incidente',
            ai_impact_assessment: 'AI Evaluación de impacto',
            ai_sandbox_application: 'Aplicación de zona de pruebas',
            ai_sandbox_monitoring: 'Monitoreo de zona de pruebas',
            ai_sandbox_completion: 'Finalización de la zona de pruebas',
            ai_submission_tracker: 'Seguimiento de envíos',
            ai_evidence_capture: 'Captura de evidencia',
            risk_level_minimal: 'Riesgo mínimo',
            risk_level_limited: 'Riesgo limitado',
            risk_level_high: 'Alto riesgo',
            risk_level_unacceptable: 'Riesgo inaceptable',
            submission_status_draft: 'Borrador',
            submission_status_submitted: 'Enviado',
            submission_status_returned: 'Devuelto para enmienda',
            submission_status_accepted: 'Aceptado',
            submission_status_rejected: 'Rechazado',
            submission_status_closed: 'Cerrado',
            ecommerce_law_title: 'Ley de comercio electrónico de Vietnam',
            ecommerce_platform_classification: 'Clasificación de plataforma',
            ecommerce_seller_register: 'Registro de vendedor',
            ecommerce_complaint_register: 'Registro de quejas',
            ecommerce_takedown_register: 'Registro de eliminación',
            ecommerce_submission_tracking: 'Seguimiento de envíos',
            ecommerce_dossier_checklist: 'Lista de verificación del expediente',
            ecommerce_cross_border: 'Cumplimiento transfronterizo',
            ecommerce_trustmark: 'Programa de marca de confianza',
            ecommerce_notification: 'Notificación',
            ecommerce_registration: 'Registro',
            ecommerce_termination: 'Terminación',
            ecommerce_reporting_readiness: 'Preparación para informes',
            ecommerce_evidence_capture: 'Captura de evidencia',
            submission_status_approved: 'Aprobado',
            cybersec_law_2025_title: 'Ley de Ciberseguridad de Vietnam (2025)',
            cybersec_system_classification: 'Clasificación del sistema',
            cybersec_risk_assessment: 'Evaluación de riesgos',
            cybersec_incident_report: 'Informe de incidente',
            cybersec_authority_cooperation: 'Cooperación entre autoridades',
            cybersec_data_localization: 'Localización de datos',
            cybersec_vendor_security: 'Seguridad del proveedor',
            cybersec_submission_tracker: 'Seguimiento de envíos',
            cybersec_evidence_capture: 'Captura de evidencia',
            cybersec_dossier_checklist: 'Lista de verificación del expediente',
            cybersec_classification_general: 'generales',
            cybersec_classification_important: 'importante',
            cybersec_classification_critical: 'Crítico',
            data_law_title: 'Ley de datos de Vietnam',
            data_inventory: 'Inventario de datos',
            data_classification: 'Clasificación de datos',
            data_sharing: 'Compartir datos',
            data_governance: 'Gobernanza de datos',
            data_submission_tracker: 'Seguimiento de envíos',
            data_evidence_capture: 'Captura de evidencia',
            data_dossier_checklist: 'Lista de verificación del expediente',
            telecom_law_title: 'Ley de Telecomunicaciones de Vietnam',
            telecom_authority_request: 'Solicitud de autoridad',
            telecom_disclosure: 'Divulgación',
            telecom_confidentiality: 'Confidencialidad',
            telecom_submission_tracker: 'Seguimiento de envíos',
            telecom_evidence_capture: 'Captura de evidencia',
            telecom_dossier_checklist: 'Lista de verificación del expediente',
            pdpl_law_title: 'Ley de protección de datos personales de Vietnam',
            pdpl_breach_notification: 'Notificación de infracción',
            pdpl_dpo_notification: 'DPO Notificación de cita',
            pdpl_data_subject_notification: 'Notificación del interesado',
            pdpl_submission_tracker: 'Seguimiento de envíos',
            pdpl_evidence_capture: 'Captura de evidencia',
            pdpl_dossier_checklist: 'Lista de verificación del expediente',
            pdpl_authority_filing: 'Presentación de autoridad',
            pdpl_mops_filing: 'Presentación MoPS',
        },
        mappings: {
            title: 'Asignaciones de módulos',
            new: 'Nuevo mapeo',
            module: 'Módulo',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Consentimiento',
            module_dpia: 'DPIA',
            module_incident: 'Incidente',
            module_rights_request: 'Solicitud de derechos',
            module_data_mapping: 'Mapeo de datos',
            module_classification: 'Clasificación',
            module_monitoring: 'Monitoreo',
            module_governance: 'Gobernanza',
            module_vendor: 'vendedor',
            module_risk: 'Riesgo',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            market_pack: 'Paquete de mercado',
            mapping_status: 'Mapeo Estado',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'No se encontraron module mappings',
            incomplete_entries_warning: '{count} mapeo(s) oculto(s) por falta de ID de marco, ID de requisito o módulo.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Envíos de paquetes',
            new: 'Nuevo Submission',
            submitter: 'Presentador',
            pack: 'Paquete',
            submitted_at: 'Enviado el',
            no_submissions: 'No se encontraron pack submissions',
            installed_sector_overlays_title: 'Sector Overlays ({count}) [ES]',
            installed_sector_overlays_aria: 'Installed Sector Overlays [ES]',
            installed_col_name: 'Name [ES]',
            installed_col_code: 'Code [ES]',
            installed_col_sector: 'Sector [ES]',
            installed_col_mode: 'Mode [ES]',
            installed_col_status: 'Status [ES]',
            installed_refresh: 'Refresh [ES]',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Vista previa de traducción',
            select_locale: 'Seleccione configuración regional',
            select_module: 'Seleccione Module',
            key: 'Key',
            value: 'Valor',
            no_translations: 'No se encontraron translations',
        },
        audit: {
            title: 'Pista de auditoría de localización',
            event_type: 'Evento Tipo',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Tipo de entidad',
            entity_id: 'ID de la entidad',
            entity_name: 'Nombre de la entidad',
            actor: 'Actor',
            timestamp: 'Marca de tiempo',
            payload: 'Carga útil',
            collapse_payload: 'Contraer detalles de carga',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Expandir detalles de carga',
            collapse: 'Contraer',
            expand: 'Expandir',
            all_entity_types: 'Todos los tipos de entidad',
            no_events: 'No se encontraron audit events',
        },
        market_packs_detail: {
            installed_at: 'Instalado el',
            pack_details_label: 'Detalles del paquete',
            market_code: 'Código de mercado',
            supported_locales: 'Idiomas soportados',
            min_platform_version: 'Versión mínima de plataforma',
            pack_information: 'Información del paquete',
            pack_contents: 'Contenido del paquete',
            included_frameworks: 'Marcos incluidos',
            pack_artifacts: 'Artefactos del paquete',
            no_artifacts: 'No se encontraron artefactos para este paquete',
            pack_not_found: 'Paquete no encontrado',
        },
        mappings_form: {
            framework_id: 'ID del marco',
            requirement_id: 'ID del requisito',
            module_label: 'Módulo',
            mapping_type: 'Tipo de mapeo',
            configuration_json: 'Configuración (JSON)',
            conflict_resolution: 'Resolución de conflictos',
            is_override: 'Es una anulación',
            all_modules: 'Todos los módulos',
            all_mapping_types: 'Todos los tipos de mapeo',
        },
        submissions_detail: {
            submission_id: 'ID de presentación',
            validation: 'Validación',
            reviewers: 'Revisores',
            start_review: 'Iniciar revisión',
            hide_comments: 'Ocultar comentarios',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Comentarios de revisión',
            loading_comments: 'Cargando comentarios...',
            no_comments: 'Aún no hay comentarios.',
            add_comment: 'Añadir comentario',
            reject_submission: 'Rechazar presentación',
            reason_for_rejection: 'Motivo del rechazo',
            reviewer_ids: 'IDs de revisores (separados por comas)',
            submit_new_pack: 'Enviar nuevo paquete',
            pack_type: 'Tipo de paquete',
            market_code: 'Código de mercado',
            target_locales: 'Idiomas de destino',
            pack_file: 'Archivo del paquete',
            pack_format_info: 'Formato del paquete: Los paquetes de Market deben incluir archivos JSON específicos de la configuración regional con traducciones de espacios de nombres/claves. Los paquetes de idiomas deben proporcionar traducciones para las configuraciones regionales de destino especificadas. Consulte la documentación para obtener un esquema detallado.',
            accepted_formats: 'Formatos aceptados: .json o .zip. Los archivos JSON deben contener un objeto plano o con espacio de nombres de pares clave-valor de traducción. Los archivos ZIP deben contener uno o más archivos locales JSON.',
            comma_separated_locales: 'Códigos de localización separados por comas para este paquete de idioma',
            confirm_approval: 'Confirmar aprobación',
            approve_message: '¿Aprobar esta presentación de paquete?',
            submission_label: 'Presentación',
            all_pack_types: 'Todos los tipos de paquete',
            passed: 'aprobado',
            failed_label: 'fallido',
            market_code_required: 'El código de mercado es obligatorio',
            version_required: 'La versión es obligatoria',
            pack_type_regulatory: 'Regulatorio',
            framework_code: 'Código del marco',
            jurisdiction_code: 'Código de jurisdicción',
            pack_source: 'Fuente',
            pack_source_manual: 'manuales',
            pack_version: 'Versión del paquete',
            install_regulatory_pack: 'Instalar paquete regulatorio',
            regulatory_pack_desc: 'Subir un paquete de marco regulatorio (.zip)',
            framework_required: 'El código del marco normativo es obligatorio',
            jurisdiction_required: 'El código de jurisdicción es obligatorio',
        },
        catalog_detail: {
            packs_installed_of: 'de {total} paquetes instalados',
            install_all: 'Instalar todo',
            installing: 'Instalando...',
            removing: 'Eliminando...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Requiere',
            tab_catalog: 'Catálogo',
            tab_installed: 'Instalado',
            details: 'Detalles',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Idioma de origen',
            target_locale: 'Idioma de destino',
            locales_must_differ: 'Los idiomas de origen y destino deben ser diferentes.',
            all_keys_translated: 'Todas las claves traducidas',
            keys_missing: 'Faltan {count} clave(s)',
            show_missing_only: 'Mostrar solo las que faltan',
            namespace: 'Espacio de nombres',
            source: 'Fuente',
            target: 'Destino',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Marcos',
            tab_watchlist: 'Lista de seguimiento',
            authority: 'Autoridad',
            requirements_count: 'Requisitos',
            all_types: 'Todos los tipos',
            collapse_all: 'Contraer todo',
            expand_all: 'Expandir todo',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtrado a',
            clear_filter: 'Borrar filtro',
            no_requirements: 'No se encontraron requisitos para este marco.',
            requirements_label: 'Requisitos',
            status_updated: 'Estado actualizado correctamente',
            overview_tab: 'Resumen',
            forms_tab: 'Formularios',
            procedures_tab: 'Procedimientos',
            fines_risks_tab: 'Multas y riesgos',
            deadlines_tab: 'Plazos',
            guidance_tab: 'Orientación',
            guidance_empty: 'No hay contenido de orientación para este marco.',
            guidance_loading: 'Cargando orientación…',
            guidance_section: 'Sección de orientación',
            guidance_view: 'Ver orientación',
            guidance_map_to_workflow: 'Asignar al flujo de trabajo',
            guidance_link_evidence: 'Enlace de evidencia',
            guidance_filter_type: 'Tipo',
            guidance_filter_category: 'categoría',
            guidance_filter_module: 'Módulo',
            guidance_filter_status: 'Estado',
            guidance_filter_search: 'Guía de búsqueda',
            guidance_filter_all: 'Todos',
            guidance_table_reference: 'Referencia',
            guidance_table_name: 'Nombre de la guía',
            guidance_table_type: 'Tipo',
            guidance_table_category: 'categoría',
            guidance_table_effective_date: 'Fecha de vigencia',
            guidance_table_modules: 'Módulos relacionados',
            guidance_table_actions: 'Acciones',
            guidance_reference_only_banner: 'Solo referencia: se aplica al sector {{sector}} pero no a su organización.',
            guidance_open_official_source: 'Abrir fuente oficial',
            guidance_no_detail: 'No hay detalles adicionales disponibles para esta guía.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Orientación relacionada',
            deadlines_empty: 'No se encontraron plazos regulatorios para este marco.',
            deadlines_sla_type: 'Tipo de SLA',
            deadlines_value: 'Plazo',
            deadlines_unit: 'Unidad',
            deadlines_extension: 'Extensión',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Referencia legal',
            framework_stack: 'Estructura del marco',
            layer_parent_law: 'Ley principal',
            layer_implementing_decree: 'Decreto de implementación',
            layer_enforcement_decree: 'Decreto sancionador',
            layer_admin_procedures: 'Procedimientos administrativos',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Instrumentos subordinados',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Activo',
            status_draft: 'Borrador',
            draft_enforcement_badge: 'Borrador — aún no promulgado',
            effective_date_draft: 'Borrador — aún no vigente',
            effective_date: 'Fecha de vigencia',
            forms_table_id: 'ID del formulario',
            forms_table_name: 'Nombre',
            forms_table_source: 'Fuente',
            forms_table_category: 'Categoría',
            forms_table_type: 'Tipo',
            fill_form: 'Rellenar formulario',
            view_reference: 'Ver referencia',
            forms_empty: 'No se encontraron formularios para este marco.',
            forms_loading: 'Cargando formularios...',
            filter_by_source_layer: 'Filtrar por capa de origen',
            all_layers: 'Todas las capas',
            procedures_id: 'ID procedimiento',
            procedures_name: 'Procedimiento',
            procedures_trigger: 'Activador',
            procedures_steps: 'Pasos',
            procedures_outputs: 'Resultados',
            procedures_related_forms: 'Formularios relacionados',
            procedures_submission_channels: 'Canales de envío',
            procedures_empty: 'No se encontraron procedimientos para este marco.',
            fines_area: 'Área de infracción',
            fines_articles: 'Artículos',
            fines_individual: 'Multa (individual)',
            fines_organization: 'Multa (organización)',
            fines_revenue: 'Escalación por ingresos',
            fines_deadlines: 'Plazos',
            fines_remediation: 'Medidas correctivas',
            fines_related: 'Procedimientos relacionados',
            fines_empty: 'No hay datos de sanciones disponibles para este marco.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Tipo de riesgo',
            fines_risk_triggers: 'Qué lo desencadena',
            fines_risk_only_note: 'Estas entradas describen riesgos de cumplimiento y operativos, no sanciones económicas. Cuando aplican multas, se definen en el marco principal.',
            fines_enforcement_pending: 'Marco sancionador aún no publicado. Los datos de sanciones se añadirán cuando se promulgue el instrumento de aplicación.',
            view_details: 'Ver detalles',
            back_to_list: 'Volver a la lista',
            description: 'Descripción',
            jurisdiction: 'Jurisdicción',
            lineage: 'Linaje',
            replaces: 'Reemplaza',
            replaced_by: 'Reemplazado por',
            requirements: 'Requisitos',
            tab_navigation: 'Pestañas de detalle del marco',
            forms_table_actions: 'Acciones',
            create_form: 'Crear formulario',
            mark_promulgated: 'Marcar como promulgado',
            status_promulgated: 'Promulgado',
            promulgated_success: 'Estado de la capa actualizado a promulgado',
            transition_review: 'Revisión de transición',
            transition_review_desc: 'Este marco tiene una revisión de transición pendiente para la adopción de la ley sucesora.',
            transition_review_by: 'Fecha límite de revisión',
            transition_review_status: 'Estado de revisión',
            transition_review_notes: 'Notas',
            transition_review_pending: 'Pendiente',
            transition_review_in_progress: 'En curso',
            transition_review_completed: 'Completada',
            transition_review_successor: 'Marco sucesor',
            transition_review_lead_time: 'Tiempo de anticipación',
            transition_review_lead_time_days: '{days} días antes de la fecha de entrada en vigor',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Revisiones de transición de formularios',
            transition_review_forms_desc: 'Revise cada plantilla operativa para verificar su relevancia bajo el marco sucesor.',
            transition_review_outcome: 'Resultado',
            transition_review_outcome_carry_forward: 'Mantener',
            transition_review_outcome_carry_forward_revised: 'Mantener (revisado)',
            transition_review_outcome_retire: 'Retirar',
            transition_review_outcome_split: 'Dividir',
            transition_review_outcome_awaiting_decree: 'Esperando decreto',
            transition_review_no_forms: 'No hay revisiones de transición de formularios configuradas.',
            transition_review_update_success: 'Revisión de transición actualizada correctamente',
            transition_review_operational_note: 'Estas son plantillas operativas de apoyo al proceso, no conteos de formularios legales. La atribución legal permanece con el marco antiguo hasta que la ley sucesora entre en vigor.',
            rights_tab: 'Derechos',
            rights_empty: 'No hay derechos del interesado definidos para este marco.',
            rights_code: 'Código',
            rights_name: 'Derecho',
            rights_legal_basis: 'Base jurídica',
            rights_elaborating_articles: 'Artículos detallados',
            rights_description: 'Descripción',
            requirements_reference: 'Referencia',
            requirements_mandatory: 'Obligatorio',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Fuente',
            category_legal_basis: 'Base legal',
            category_data_subject_rights: 'Derechos del interesado',
            category_notification: 'Notificación',
            category_assessment: 'Evaluación',
            category_governance: 'Gobernanza',
            category_record_keeping: 'Mantenimiento de registros',
            category_cross_border: 'Transfronterizo',
            category_technical_measures: 'Medidas técnicas',
            category_incident_reporting: 'Informe de incidentes',
            category_risk_management: 'Gestión de riesgos',
            category_supply_chain: 'Cadena de suministro',
            category_certification: 'Certificación',
            child_frameworks: '{count} marco secundario',
            child_frameworks_plural: '{count} marcos secundarios',
            replaces_prefix: 'Reemplaza:',
            replaced_by_prefix: 'Reemplazado por:',
            responsible_prefix: 'Responsable:',
            watchlist_empty_hint: 'Los marcos con estado "borrador" o "próximo" aparecerán aquí para su seguimiento.',
            framework_count: '{count} marco',
            framework_count_plural: '{count} marcos',
            hiding_legacy: '(ocultando {count} legado)',
            label_VN_PDPD_DECREE_13: 'Decreto PDPD 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Ley 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Decreto 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'Pregunta 778/Pregunta-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Borrador de aplicación 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Ley de Datos',
            label_VN_DATA_LAW_DECREE_165_2025: 'Decreto Ley de Datos 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Borrador de Bolsas de Datos 2026',
            label_VN_TELECOM_LAW_2023: 'Ley de Telecomunicaciones 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Decreto de Telecomunicaciones 163/2024',
            label_VN_CYBERSEC_LAW: 'Ley de Ciberseguridad (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Decreto Ciberseguridad 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Ley de Ciberseguridad 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'Ley de Comercio Electrónico 122/2025',
            label_VN_AI_LAW_2026: 'AI Ley 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Empresa',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'autoridad',
            sla_type_rights_request_ack: 'Reconocimiento de solicitud de derechos',
            'sla_type_Access control review': 'Revisión de control de acceso',
            'sla_type_Annual governance attestation': 'Certificación anual de gobernanza',
            'sla_type_Audit review cycle': 'Ciclo de revisión de auditoría',
            'sla_type_Authority request response': 'respuesta a la solicitud de autoridad',
            'sla_type_Change notification processing': 'Cambiar el procesamiento de notificaciones',
            'sla_type_Complaint acknowledgment': 'Reconocimiento de queja',
            'sla_type_Complaint resolution': 'Resolución de quejas',
            'sla_type_Confidentiality policy review': 'Revisión de la política de confidencialidad',
            'sla_type_Cybersecurity incident full report': 'Informe completo del incidente de ciberseguridad',
            'sla_type_Cybersecurity incident initial report': 'Informe inicial del incidente de ciberseguridad',
            'sla_type_Data inventory review': 'Revisión del inventario de datos',
            'sla_type_Disclosure log review cycle': 'Ciclo de revisión del registro de divulgación',
            'sla_type_Incident classification decision': 'Decisión de clasificación de incidentes',
            'sla_type_Information system classification review': 'Revisión de clasificación del sistema de información.',
            'sla_type_Notification processing (sales website/app)': 'Procesamiento de notificaciones (sitio web/aplicación de ventas)',
            'sla_type_Pen test cycle': 'Ciclo de prueba de lápiz',
            'sla_type_Registration processing (service platform)': 'Tramitación de registro (plataforma de servicios)',
            'sla_type_Risk assessment cycle': 'Ciclo de evaluación de riesgos',
            'sla_type_Takedown decision (after review)': 'Decisión de eliminación (después de la revisión)',
            'sla_type_Termination notice (public)': 'Aviso de terminación (público)',
            sla_type_ai_impact_assessment_completion: 'AI finalización de la evaluación de impacto',
            sla_type_ai_sandbox_application_review: 'AI revisión de la aplicación sandbox',
            sla_type_ai_serious_incident_report: 'AI informe de incidente grave',
            sla_type_ai_system_registration: 'AI registro del sistema',
            sla_type_authority_request_response: 'respuesta a la solicitud de autoridad',
            sla_type_certificate_issuance_review: 'Revisión de emisión de certificados',
            sla_type_complaint_handling_approach_notice: 'Aviso de enfoque de manejo de quejas',
            sla_type_complaint_incomplete_info_request: 'Queja solicitud de información incompleta',
            sla_type_data_governance_attestation: 'Certificación de gobernanza de datos',
            sla_type_dsr_acknowledgment: 'reconocimiento de DSR',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Retirar/restringir/objetar',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Terceros',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Acceder / corregir / proporcionar',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Terceros',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Supresión',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Supresión, tercero',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Medidas de protección',
            sla_type_telecom_authority_request_response: 'Respuesta a la solicitud de la autoridad de telecomunicaciones',
            sla_type_rights_request_completion: 'Finalización de solicitud de derechos',
            sla_type_incident_authority_notification: 'Notificación a la autoridad de incidentes',
            sla_type_incident_subject_notification: 'Notificación del asunto del incidente',
            sla_type_dpia_completion: 'DPIA finalización',
            sla_type_data_retention_review: 'Revisión de retención de datos',
            sla_type_authority_filing_review: 'Revisión de presentación de autoridad',
            sla_type_authority_supplement_response: 'Respuesta del suplemento de autoridad',
            sla_type_authority_resubmission: 'Reenvío de autoridad',
            sla_type_authority_filing_supplement_response: 'Respuesta del suplemento de presentación de la autoridad',
            sla_type_authority_filing_resubmission: 'Reenvío de presentación de autoridad',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            unit_working_days: 'dias laborables',
            unit_calendar_days: 'días calendario',
            unit_hours: 'horas',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Consentimiento',
            form_category_privacy: 'Privacidad',
            form_category_breach: 'incumplimiento',
            form_category_dpia: 'DPIA',
            form_category_register: 'Registrarse',
            form_category_monitoring: 'Monitoreo',
            form_category_vendor: 'vendedor',
            form_category_org: 'organizacional',
            form_category_sector: 'Sector',
            form_category_data_mapping: 'Mapeo de datos',
            form_category_governance: 'Gobernanza',
            form_category_incident: 'Incidente',
            form_category_authority_submission: 'Presentación de autoridad',
            form_category_notice: 'Aviso',
            form_category_assessment_report: 'Informe de evaluación',
            form_category_request: 'Solicitar',
            form_category_dossier: 'Dossier',
            form_category_filing: 'Presentación',
            module_consent: 'Consentimiento',
            module_rights_request: 'Solicitud de derechos',
            module_classification: 'Clasificación',
            module_dpia: 'DPIA',
            module_governance: 'Gobernanza',
            module_monitoring: 'Monitoreo',
            module_register: 'Registrarse',
            module_risk: 'Riesgo',
            module_data_mapping: 'Mapeo de datos',
            module_incident: 'Incidente',
            module_assessment: 'Evaluación',
            module_vendor: 'vendedor',
            module_policy: 'Política',
            module_audit: 'Auditoría',
            module_training: 'Entrenamiento',
            module_discovery: 'Descubrimiento',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Guía de cálculo',
            enum_guidance_type_implementation_guidance: 'Guía de implementación',
            enum_guidance_type_interpretive_note: 'Nota interpretativa',
            enum_guidance_type_legal_guidance: 'Orientación jurídica',
            enum_guidance_type_procedural_guidance: 'Orientación procesal',
            enum_guidance_type_sector_guidance: 'Orientación sectorial',
            enum_guidance_type_technical_methodology: 'Metodología Técnica',
            enum_guidance_category_annual_reporting: 'Informe anual',
            enum_guidance_category_authority_cooperation: 'Cooperación entre autoridades',
            enum_guidance_category_authority_coordination: 'Coordinación de autoridades',
            enum_guidance_category_business_continuity: 'Continuidad del negocio',
            enum_guidance_category_capital_adequacy: 'Adecuación del capital',
            enum_guidance_category_complaint_handling: 'Manejo de quejas',
            enum_guidance_category_consent: 'Consentimiento',
            enum_guidance_category_consent_management: 'Gestión del consentimiento',
            enum_guidance_category_consumer_protection: 'Protección al consumidor',
            enum_guidance_category_control_environment: 'Entorno de control',
            enum_guidance_category_cross_border: 'Transfronterizo',
            enum_guidance_category_cross_border_transfers: 'Transferencias transfronterizas',
            enum_guidance_category_data_classification: 'Clasificación de datos',
            enum_guidance_category_data_localization: 'Localización de datos',
            enum_guidance_category_data_protection_impact_assessment: 'Evaluación de impacto de la protección de datos',
            enum_guidance_category_direct_marketing: 'Mercadotecnia directa',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Gobernanza',
            enum_guidance_category_ict_risk_management: 'Gestión de riesgos TIC',
            enum_guidance_category_incident_management: 'Gestión de Incidentes',
            enum_guidance_category_internal_audit: 'Auditoría Interna',
            enum_guidance_category_isms_documentation: 'Documentación SGSI',
            enum_guidance_category_lawful_basis: 'Base legal',
            enum_guidance_category_licensing: 'Licencias',
            enum_guidance_category_local_presence: 'Presencia local',
            enum_guidance_category_logging_and_retention: 'Registro y retención',
            enum_guidance_category_logical_and_physical_access: 'Acceso lógico y físico',
            enum_guidance_category_operational_resilience_testing: 'Pruebas de resiliencia operativa',
            enum_guidance_category_outsourcing: 'Subcontratación',
            enum_guidance_category_platform_registration: 'Registro de plataforma',
            enum_guidance_category_quality_of_service: 'Calidad de servicio',
            enum_guidance_category_risk_classification: 'Clasificación de riesgo',
            enum_guidance_category_risk_management: 'Gestión de riesgos',
            enum_guidance_category_scope_definition: 'Definición de alcance',
            enum_guidance_category_sectoral_localisation: 'Localización Sectorial',
            enum_guidance_category_subscriber_protection: 'Protección del suscriptor',
            enum_guidance_category_supply_chain: 'Cadena de suministro',
            enum_guidance_category_system_classification: 'Clasificación del sistema',
            enum_guidance_category_system_operations: 'Operaciones del sistema',
            enum_guidance_category_telecommunications_data: 'Datos de telecomunicaciones',
            enum_guidance_category_third_party_risk: 'Riesgo de terceros',
            enum_guidance_category_transitional_provisions: 'Disposiciones transitorias',
            enum_guidance_category_vulnerability_management: 'Gestión de vulnerabilidades',
            enum_guidance_status_active: 'Activo',
            enum_guidance_status_draft: 'Borrador',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Gestión de acceso',
            enum_module_ai_governance: 'AI Gobernanza',
            enum_module_asset_management: 'Gestión de Activos',
            enum_module_audit_trail: 'Pista de auditoría',
            enum_module_breach_notification: 'Notificación de infracción',
            enum_module_capital_adequacy: 'Adecuación del capital',
            enum_module_classification: 'Clasificación',
            enum_module_consent: 'Consentimiento',
            enum_module_consent_management: 'Gestión del consentimiento',
            enum_module_cross_border_transfers: 'Transferencias transfronterizas',
            enum_module_data_localization: 'Localización de datos',
            enum_module_data_mapping: 'Mapeo de datos',
            enum_module_data_protection: 'Protección de datos',
            enum_module_data_subject_rights: 'Derechos del interesado',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Cifrado',
            enum_module_governance: 'Gobernanza',
            enum_module_incident: 'Incidente',
            enum_module_incident_management: 'Gestión de Incidentes',
            enum_module_internal_audit: 'Auditoría Interna',
            enum_module_isms_documentation: 'Documentación SGSI',
            enum_module_isms_governance: 'Gobernanza del SGSI',
            enum_module_logging_and_monitoring: 'Registro y monitoreo',
            enum_module_monitoring: 'Monitoreo',
            enum_module_policy_management: 'Gestión de políticas',
            enum_module_risk: 'Riesgo',
            enum_module_risk_management: 'Gestión de riesgos',
            enum_module_training: 'Entrenamiento',
            enum_module_vendor: 'vendedor',
            enum_module_vendor_management: 'Gestión de proveedores',
            enum_module_vendor_risk: 'Riesgo del proveedor',
            enum_module_vulnerability_management: 'Gestión de vulnerabilidades',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Acceso',
            category_availability: 'Disponibilidad',
            category_change_management: 'Gestión de cambios',
            category_classification: 'Clasificación',
            category_communication_and_information: 'Comunicación e información',
            category_compliance: 'Cumplimiento',
            category_confidentiality: 'Confidencialidad',
            category_control_activities: 'Actividades de control',
            category_control_environment: 'Entorno de control',
            category_incident: 'Incidente',
            category_internal_audit: 'Auditoría Interna',
            category_internal_control: 'Control Interno',
            category_logical_and_physical_access: 'Acceso lógico y físico',
            category_mis_data: 'Datos del sistema de información de gestión',
            category_monitoring: 'Monitoreo',
            category_monitoring_activities: 'Actividades de seguimiento',
            category_outsourcing: 'Subcontratación',
            category_privacy: 'Privacidad',
            category_processing_integrity: 'Integridad del procesamiento',
            category_reporting: 'Informes',
            category_risk_assessment: 'Evaluación de riesgos',
            category_risk_mitigation: 'Mitigación de riesgos',
            category_sector_applicability: 'Aplicabilidad sectorial',
            category_system_operations: 'Operaciones del sistema',
            category_training: 'Entrenamiento',
            category_vendor: 'vendedor',
            guidance_related_forms_advisory: 'Algunos formularios tienen orientación relacionada: ver en el',
            guidance_related_procedures_advisory: 'Algunos procedimientos tienen orientación relacionada: ver en el',
            guidance_related_requirements_advisory: 'Algunos requisitos tienen orientación relacionada: ver en el',
        },
        channels: {
            title: 'Canales de presentación y plazos',
            channels: 'Canales de presentación',
            add_channel: 'Añadir canal',
            channel_type: 'Tipo',
            name: 'Nombre',
            instructions: 'Instrucciones',
            active: 'Activo',
            no_channels: 'Ningún canal de presentación configurado',
            portal: 'Portal',
            in_person: 'En persona',
            postal: 'Correos',
            email: 'Correo electrónico',
            add: 'Añadir',
            deadlines: 'Plazos regulatorios',
            add_deadline: 'Añadir plazo',
            label: 'Etiqueta',
            days: 'Días',
            day_type: 'Tipo de día',
            calendar_days: 'Días naturales',
            working_days: 'Días laborables',
            no_deadlines: 'No hay plazos configurados',
            description: 'Descripción',
            failed_load: 'Error al load channel data',
            channel_added: 'Canal añadido',
            channel_added_announce: 'Canal de presentación añadido',
            failed_add_channel: 'Error al add channel',
            deadline_added: 'Plazo añadido',
            deadline_added_announce: 'Plazo regulatorio añadido',
            failed_add_deadline: 'Error al add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Vinculación del marco',
            modules: 'Módulos vinculados',
            add_module: 'Añadir módulo',
            module_code: 'Código del módulo',
            required: 'Obligatorio',
            link: 'Vincular',
            no_modules: 'Ningún módulo vinculado',
            notes: 'Notas',
            personas: 'Personas vinculadas',
            add_persona: 'Añadir persona',
            persona_id: 'ID de persona',
            role_description: 'Descripción del rol',
            no_personas: 'Ninguna persona vinculada',
            failed_load: 'Error al load framework links',
            module_linked: 'Módulo vinculado',
            module_linked_announce: 'Módulo vinculado correctamente',
            failed_link_module: 'Error al link module',
            persona_linked: 'Persona vinculada',
            persona_linked_announce: 'Persona vinculada correctamente',
            failed_link_persona: 'Error al link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Pendiente',
                assessed: 'Evaluado',
                applied: 'Aplicado',
                dismissed: 'Descartado',
            },
            title: 'Actualizaciones regulatorias y panel de marcos',
            page_description: 'Coverage at a glance for every installed regulatory framework, plus the in-product change log of regulatory updates: amendments, effective dates, and impact assessments. [ES]',
            coverage_dashboard: 'Panel de cobertura del marco',
            no_frameworks: 'No se encontraron marcos',
            framework: 'Marco',
            modules: 'Módulos',
            templates: 'Plantillas',
            forms: 'Formularios',
            channels: 'Canales',
            deadlines: 'Plazos',
            pending: 'Actualizaciones pendientes',
            updates: 'Actualizaciones regulatorias',
            create_update: 'Crear actualización',
            framework_id: 'ID del marco',
            update_title: 'Título',
            description: 'Descripción',
            effective_date: 'Fecha de entrada en vigor',
            amendment_ref: 'Referencia de enmienda',
            create: 'Crear',
            no_updates: 'Sin actualizaciones regulatorias',
            status: 'Estado',
            impact: 'Impacto',
            change_summary: 'Resumen de cambios',
            assess: 'Evaluar',
            failed_load: 'Error al load data',
            update_created: 'Actualización creada',
            update_created_announce: 'Actualización regulatoria creada',
            failed_create: 'Error al create update',
            update_assessed: 'Actualización evaluada',
            failed_assess: 'Error al assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Marcos relacionados',
            panel_intro: 'Cómo se relaciona este marco con otros. Una capa opcional puede aplicarse bajo varios marcos superiores a la vez; cada uno aparece aquí exactamente una vez.',
            outgoing_heading: 'Este marco se aplica a',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Se aplica bajo este marco',
            outgoing_caption: 'Marcos a los que apunta este marco',
            incoming_caption: 'Marcos que dependen de este marco',
            col_framework: 'Marco',
            col_relationship: 'Relación',
            col_scope: 'Origen',
            scope_global: 'Del contenido del paquete',
            scope_org: 'Específico de la organización',
            load_failed: 'No se pudieron cargar los marcos relacionados',
            relationship_kinds: {
                parent: 'Marco superior',
                applies_to: 'Se aplica a',
                cross_reference: 'Referencia cruzada',
                supersedes: 'Sustituye a',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banking [ES]',
                insurance: 'Insurance [ES]',
                healthcare: 'Healthcare [ES]',
                education: 'Education',
                ict_critical: 'Critical ICT [ES]',
                telecom: 'Telecommunications [ES]',
                energy: 'Energy [ES]',
                transport: 'Transport [ES]',
                digital_services: 'Digital services [ES]',
                digital_technology_industry: 'Industria de tecnología digital',
                manufacturing: 'Manufacturing',
                public_administration: 'Public administration [ES]',
                payments: 'Payments [ES]',
                publishing: 'Edición',
                crypto_assets: 'Criptoactivos',
                other: 'Other [ES]',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sector [ES]',
            direct_scope_label: 'Direct legal scope [ES]',
            chip_direct: 'Directly applicable [ES]',
            chip_reference: 'Reference only [ES]',
            banner_direct_copy: 'Directly applicable to your organization. Sector reports and deadlines are active. [ES]',
            banner_reference_copy: 'Reference only — your organization is not classified in this sector. [ES]',
            edit_mode_button: 'Edit mode [ES]',
            edit_form_title: 'Change install mode [ES]',
            mode_direct_label: 'Direct — legally applicable [ES]',
            mode_direct_help: 'Schedules sector deadlines and dashboard widgets. [ES]',
            mode_reference_label: 'Reference only [ES]',
            mode_reference_help: 'Browseable but no deadlines or widgets. [ES]',
            reason_note_label: 'Reason (optional) [ES]',
            reason_note_placeholder: 'e.g. Bank subsidiary acquired [ES]',
            cancel: 'Cancel [ES]',
            confirm: 'Confirm [ES]',
            confirming: 'Saving… [ES]',
            save: 'Save [ES]',
            saving: 'Saving… [ES]',
            toast_mode_changed: 'Sector overlay mode updated. [ES]',
            toast_installed: 'Sector overlay installed. [ES]',
            toast_sectors_saved: 'Sector self-identification saved. [ES]',
            install_modal_title: 'Install sector overlay [ES]',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry. [ES]',
            install_modal_footer: 'You can change install mode later from the framework detail page. [ES]',
            install_button: 'Install [ES]',
            installed_label: 'Installed [ES]',
            uninstall_button: 'Desinstalar',
            uninstall_confirm: 'Confirmar desinstalación',
            uninstall_cancel: 'Cancelar',
            suggested_label: 'Suggested [ES]',
            catalog_page_title: 'Sector overlays [ES]',
            catalog_page_intro: 'Optional regulatory packs scoped to a single industry sector. [ES]',
            catalog_empty_title: 'No sector overlays available [ES]',
            catalog_empty_description: 'Sector overlay packs published in your platform will appear here. [ES]',
            authority_label: 'Authority [ES]',
            org_sectors_title: 'Industry sectors [ES]',
            org_sectors_intro: 'Select the industry sectors your organization operates in. [ES]',
            widget_title: 'Sector overlay reports due [ES]',
            widget_aria_label: 'Sector overlay reports due [ES]',
            widget_empty: 'No upcoming sector overlay deadlines. [ES]',
            audit_event_mode_changed: 'Sector overlay install mode changed [ES]',
            audit_event_installed: 'Sector overlay installed [ES]',
            view_details_button: 'View details → [ES]',
            banner_aria_label: 'Sector overlay status [ES]',
            toast_load_failed: 'Failed to load sector overlays [ES]',
            toast_install_failed: 'Failed to install sector overlay [ES]',
            toast_uninstall_confirm_required: 'Type "{packCode}" to confirm uninstall. [ES]',
            toast_uninstalled: 'Uninstalled {packCode}. [ES]',
            toast_uninstall_failed: 'Failed to uninstall sector overlay [ES]',
            uninstall_warning_destructive_label: 'Destructive action. [ES]',
            uninstall_warning_body: 'Uninstalling will delete the framework record and any persisted regulatory deadlines tied to {frameworkCode} from this organisation. [ES]',
            uninstall_warning_type_to_confirm: 'Type {packCode} to confirm. [ES]',
            uninstall_in_progress: 'Uninstalling… [ES]',
            widget_view_all: 'View all sector overlays → [ES]',
            widget_due_in_days: 'Due in {days}d [ES]',
            widget_overdue_days: 'Overdue by {days}d [ES]',
            widget_due_today: 'Due today [ES]',
            widget_due_unknown: '— [ES]',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Phased obligations [ES]',
            panel_aria_label: 'Phased obligations [ES]',
            panel_intro: 'Some clauses in this framework activate later than the framework itself. [ES]',
            phased_effective_date_label: 'Phased effective: [ES]',
            status_pending: 'Pending [ES]',
            status_active: 'Active [ES]',
            status_early: 'Active (early-implemented) [ES]',
            notice_window_label: 'Authority notice window: [ES]',
            days_label: 'days [ES]',
            recipient_label: 'Recipient: [ES]',
            early_implement_button: 'Mark early-implemented [ES]',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Mark phased obligation as early-implemented [ES]',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent. [ES]',
            effective_at_label: 'Effective from [ES]',
            reason_label: 'Reason (optional) [ES]',
            reason_placeholder: 'e.g. Board approved early adoption 2026-09-01 [ES]',
            confirm: 'Confirm [ES]',
            toast_early_implemented: 'Early implementation recorded. [ES]',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Phased obligation marked early-implemented [ES]',
            widget_rolling_window_label: 'Phased activations within 90 days [ES]',
            notice_due_label: 'Notice due by the end of [ES]',
            notice_due_time_suffix: '(organisation time) [ES]',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day. [ES]',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent. [ES]',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday. [ES]',
                day_after_is_day_one: 'Computed: the day after implementation is day 1. [ES]',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period. [ES]',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies. [ES]',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states. [ES]',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read. [ES]',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due. [ES]',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run. [ES]',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read. [ES]',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Panel de control',
            partner_orgs: 'Organizaciones asociadas',
            service_tiers: 'Niveles de servicio',
            deliverable_templates: 'Plantillas de entregables',
            deliverables: 'Entregables',
            brand_profiles: 'Perfiles de marca',
            support_tickets: 'Tickets de soporte',
            workflow_templates: 'Plantillas de flujo de trabajo',
            workflows: 'Flujos de trabajo',
            escalation: 'Escalamiento',
            reports: 'Informes',
            audit: 'Pista de auditoría',
        },
        dashboard: {
            title: 'Panel de socios',
            loading: 'Cargando dashboard data...',
            total_managed_tenants: 'Total de inquilinos gestionados',
            avg_health_score: 'Avg Salud Puntuación',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Tickets abiertos',
            upcoming_deliverables: 'Entregables próximos',
            escalated_tickets: 'Tickets escalados',
            managed_tenants: 'Inquilinos gestionados',
            partner_tier: 'Socio Nivel',
            managed_tenants_count: 'Número de inquilinos gestionados',
            onboarding_date: 'Fecha de incorporación',
            view_details: 'Ver detalles',
            no_partner_orgs: 'No se encontraron partner organizations',
        },
        partner_orgs: {
            title: 'Organizaciones asociadas',
            new: 'Nuevo Partner Org',
            contact_name: 'Contacto Nombre',
            contact_email: 'Contacto Correo electrónico',
            partner_tier: 'Socio Nivel',
            managed_orgs: 'Organizaciones gestionadas',
            onboarding_date: 'Fecha de incorporación',
            no_orgs: 'No se encontraron partner organizations',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contacto Teléfono',
            metadata_json: 'Metadatos (JSON)',
            change_status: 'Cambiar estado',
            new_status: 'Nuevo estado',
            invalid_json_metadata: 'JSON inválido en el campo de metadatos',
            suspended: 'Suspendido',
            offboarded: 'Desvinculado',
            all_tiers: 'Todos los niveles',
            silver: 'Plata',
            gold: 'Oro',
            platinum: 'Platino',
            status_offboarded: 'Desvinculado',
            status_suspended: 'Suspendido',
            tier_gold: 'Oro',
            tier_platinum: 'Platino',
            tier_silver: 'Plata',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Niveles de servicio',
            new: 'Nuevo nivel',
            tier_name: 'Nivel Nombre',
            monthly_fee: 'Cuota mensual',
            features: 'Funcionalidades',
            no_tiers: 'No se encontraron service tiers',
            tier_code: 'Código de nivel',
            response_time_hrs: 'Respuesta Hora (hrs)',
            resolution_time_hrs: 'Tiempo de resolución (horas)',
            review_frequency: 'Revisar Frecuencia',
            sla_response_time: 'SLA Respuesta Hora (hours)',
            sla_resolution_time: 'SLA Resolution Hora (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Comparando...',
            compare: 'Comparar',
            comparison: 'Comparación de niveles de servicio',
            attribute: 'Atributo',
            deprecate: 'Deprecar',
            deprecated: 'Obsoleto',
            select_compare: 'Seleccione at least 2 tiers to compare',
            comparison_title: 'Comparación de niveles de servicio',
            status_deprecated: 'Obsoleto',
            activate: 'Activar',
            resolution_time: 'Tiempo de resolución',
            response_time: 'Respuesta Hora',
            select_at_least_two: 'Seleccione at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Plantillas de entregables',
            new: 'Nuevo plantilla',
            template_name: 'Plantilla Nombre',
            deliverable_type: 'Tipo de entregable',
            no_templates: 'No se encontraron deliverable templates',
            frequency: 'Frecuencia',
            est_effort_hrs: 'Esfuerzo est. (h)',
            estimated_effort: 'Esfuerzo estimado (horas)',
            associated_module: 'Módulo asociado',
            linked_tiers_count: 'Número de niveles vinculados',
            evidence_requirements: 'Evidencia Requisitos (comma-separated)',
            tier_ids: 'IDs de niveles (separados por comas)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Una vez',
            frequency_monthly: 'Mensual',
            frequency_quarterly: 'Trimestral',
            frequency_annually: 'Anualmente',
            type_report: 'Informe',
            type_review: 'Revisar',
            type_assessment: 'Evaluación',
            type_meeting: 'Reunión',
            type_configuration: 'Configuración',
            freq_one_time: 'Una vez',
            freq_monthly: 'Mensual',
            freq_quarterly: 'Trimestral',
            freq_annually: 'Anualmente',
            status_deprecated: 'Obsoleto',
        },
        deliverables: {
            title: 'Entregables',
            new: 'Nuevo Deliverable',
            partner: 'Socio',
            deliverable_type: 'Tipo de entregable',
            due_date: 'Fecha de vencimiento',
            no_deliverables: 'No se encontraron deliverables',
            assigned_to: 'Asignado Hasta',
            completion_date: 'Fecha de finalización',
            update_status: 'Actualizar Estado',
            hide_details: 'Ocultar Detalles',
            view_details: 'Ver detalles',
            evidence_ids: 'IDs de evidencias',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Aprobación del cliente',
            bulk_update: 'Actualización masiva',
            bulk_update_status: 'Bulk Actualizar Estado',
            generate_schedule: 'Generar Programar',
            generate_deliverable_schedule: 'Generar Deliverable Programar',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organización ID',
            service_tier_id: 'ID del nivel de servicio',
            filter_assigned_to: 'Filtrar por Asignado Hasta',
            select_status: 'Seleccione status...',
            new_status: 'Nuevo estado',
            upcoming: 'Próximo',
            overdue: 'Vencido',
            status_deferred: 'Diferido',
            status_overdue: 'Vencido',
            status_upcoming: 'Próximo',
        },
        brand_profiles: {
            title: 'Perfiles de marca',
            new: 'Nuevo Brand Profile',
            partner: 'Socio',
            brand_name: 'Nombre de marca',
            primary_color: 'Color primario',
            logo_url: 'URL del logo',
            no_profiles: 'No se encontraron brand profiles',
            secondary_color: 'Color secundario',
            header_text: 'Texto del encabezado',
            footer_text: 'Texto del pie de página',
            contact_email: 'Contacto Correo electrónico',
            contact_phone: 'Contacto Teléfono',
            contact_website: 'Sitio web de contacto',
            contact_address: 'Contacto Dirección',
            is_default: 'Es Predeterminado',
            set_default: 'Establecer predeterminado',
            preview: 'Vista previa',
            report_header_preview: 'Vista previa del encabezado del informe',
            report_footer_preview: 'Vista previa del pie de informe',
            preview_description: 'Esta es una vista previa del encabezado del informe con los colores de marca seleccionados.',
            contact_website_short: 'Sitio web',
            logo_preview: 'Vista previa del logo',
        },
        support_tickets: {
            title: 'Tickets de soporte',
            new: 'Nuevo Ticket',
            partner: 'Socio',
            subject: 'Sujeto',
            assigned_to: 'Asignado Hasta',
            no_tickets: 'No se encontraron support tickets',
            category: 'Categoría',
            priority: 'Prioridad',
            escalation_tier: 'Escalamiento Nivel',
            sla_status: 'SLA Estado',
            update_status: 'Actualizar Estado',
            escalate: 'Escalar',
            add_note: 'Agregar Note',
            escalate_ticket: 'Escalar ticket',
            escalate_to_tier: 'Escalar Hasta Nivel',
            notes_timeline: 'Cronología de notas',
            no_notes: 'Sin notas aún.',
            attachments: 'Adjuntos',
            no_attachments: 'Sin adjuntos.',
            client_org_id: 'ID Org. del cliente',
            content: 'Contenido',
            note_type: 'Tipo',
            view_detail: 'Ver Detail',
            hide: 'Ocultar',
            new_status: 'Nuevo estado',
            all_categories: 'Todas las categorías',
            all_priorities: 'Todas las prioridades',
            all_tiers: 'Todos los niveles',
            ticket_not_found: 'Ticket no encontrado',
            ticket_id: 'ID del ticket',
            client_org: 'Org. del cliente',
            deescalate: 'Desescalar',
            deescalate_ticket: 'Desescalar el ticket',
            deescalate_to_tier: 'De-escalate Hasta Nivel',
            add_attachment: 'Agregar adjunto',
            file: 'Archivo',
            linked_entity: 'Entidad vinculada',
            sla_target: 'Destino',
            breached: 'Violado',
            remaining_hours: 'restantes',
            na: 'N/A',
            tickets_found: 'tickets encontrados',
            reason: 'Motivo',
            target: 'Destino',
            upload: 'Subir',
        },
        workflow_templates: {
            title: 'Plantillas de flujo de trabajo',
            new: 'Nuevo plantilla',
            template_name: 'Plantilla Nombre',
            steps: 'Pasos',
            no_templates: 'No se encontraron workflow templates',
            category: 'Categoría',
            view_steps: 'Ver Steps',
            hide_steps: 'Ocultar pasos',
            step: 'Paso',
            step_title: 'Título',
            step_description: 'Descripción',
            assignee_role: 'Asignado Rol',
            sla_hours: 'Horas SLA',
            evidence_required: 'Evidencia Obligatorio',
            guidance_notes: 'Notas de orientación',
            add_step: 'Agregar Step',
            no_steps: 'Sin pasos definidos.',
            no_steps_defined: 'Sin pasos definidos.',
            templates_found: 'plantillas encontradas',
            all_categories: 'Todas las categorías',
        },
        workflows: {
            client_org: 'Org. del cliente',
            title: 'Instancias de flujo de trabajo',
            new: 'Nuevo flujo de trabajo',
            template: 'Plantilla',
            partner: 'Socio',
            current_step: 'Paso actual',
            progress: 'Progreso',
            no_workflows: 'No se encontraron workflow instances',
            instances_found: 'instancias encontradas',
        },
        escalation: {
            title: 'Escalamiento Panel de control',
            ticket: 'Boleto',
            partner: 'Socio',
            escalated_at: 'Escalado En',
            reason: 'Motivo',
            no_escalations: 'No se encontraron escalations',
            time_in_tier: 'Hora en Nivel',
            auto_evaluate: 'Evaluación auto.',
            priority_critical: 'Crítico',
            priority_high: 'Alto',
            priority_medium: 'Medio',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Desescalado desde el panel',
            sla_breached: 'SLA incumplido',
            sla_remaining: 'SLA restante: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Org. del cliente',
            title: 'Informes',
            partner: 'Socio',
            report_type: 'Informe Tipo',
            generated_at: 'Generado el',
            no_reports: 'No se encontraron reports',
            generate: 'Generar Informe',
        },
        audit: {
            title: 'Socio Auditoría Trail',
            event_type: 'Evento Tipo',
            entity_type: 'Tipo de entidad',
            entity: 'Entidad',
            entity_id: 'ID de la entidad',
            actor: 'Actor',
            timestamp: 'Marca de tiempo',
            no_events: 'No se encontraron audit events',
            events_found: 'eventos encontrados',
            client_org: 'Organización cliente',
            all_actors: 'Todos los actores',
            event_type_search: 'Buscar tipo de evento',
            event_type_created: 'Creado',
            event_type_updated: 'Actualizado',
            event_type_deleted: 'Eliminado',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Organización asociada',
            entity_type_service_tier: 'Nivel de servicio',
            entity_type_deliverable_template: 'Plantilla entregable',
            entity_type_deliverable_instance: 'Instancia entregable',
            entity_type_brand_profile: 'Perfil de marca',
            entity_type_support_ticket: 'Boleto de soporte',
            entity_type_workflow_template: 'Plantilla de flujo de trabajo',
            entity_type_workflow_instance: 'Instancia de flujo de trabajo',
            entity_type_generated_report: 'Informe generado',
            entity_type_audit_export: 'Exportación de auditoría',
        },
        no_permission_title: 'Permisos insuficientes',
        no_permission_message: 'No tiene permiso para acceder al espacio de trabajo de socios.',
        status_active: 'Activo',
        status_suspended: 'suspendido',
        status_offboarded: 'Fuera de borda',
        status_completed: 'Completado',
        status_cancelled: 'Cancelado',
        status_deprecated: 'En desuso',
        status_new: 'Nuevo',
        status_triaged: 'clasificado',
        status_in_progress: 'En progreso',
        status_waiting_on_client: 'Esperando al cliente',
        status_waiting_on_vendor: 'Esperando al vendedor',
        status_escalated: 'escalado',
        status_resolved: 'Resuelto',
        status_closed: 'Cerrado',
        status_pending: 'Pendiente',
        status_skipped: 'Saltado',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Incorporación',
        category_incident_escalation: 'Escalada de incidentes',
        category_config_change: 'Cambio de configuración',
        category_periodic_review: 'Revisión periódica',
        category_offboarding: 'Offboarding',
        category_custom: 'personalizado',
        category_configuration: 'Configuración',
        category_incident: 'Incidente',
        category_compliance_question: 'Pregunta de cumplimiento',
        category_feature_request: 'Solicitud de función',
        category_bug_report: 'Informe de error',
        priority_critical: 'Crítico',
        priority_high: 'Alto',
        priority_medium: 'Medio',
        priority_low: 'Bajo',
        tier_silver: 'Plata',
        tier_gold: 'oro',
        tier_platinum: 'Platino',
        report_type_compliance_summary: 'Resumen de cumplimiento',
        report_type_risk_overview: 'Descripción general del riesgo',
        report_type_incident_report: 'Informe de incidente',
        report_type_consent_status: 'Estado de consentimiento',
        report_type_program_maturity: 'Madurez del programa',
        report_type_executive_pack: 'Paquete Ejecutivo',
        note_type_internal: 'Interno',
        note_type_client_visible: 'Cliente visible',
        status_updated: 'Estado actualizado exitosamente',
        deliverable_status_updated: 'Estado de entrega actualizado exitosamente',
        ticket_status_updated: 'Estado del ticket actualizado exitosamente',
        ticket_escalated: 'El ticket se elevó exitosamente',
        note_added: 'Nota agregada exitosamente',
        attachment_uploaded: 'Adjunto cargado exitosamente',
        ticket_deescalated: 'Ticket desescalado exitosamente',
        auto_evaluation_completed: 'Autoevaluación completada',
        template_created: 'Plantilla de flujo de trabajo creada correctamente',
        template_updated: 'Plantilla de flujo de trabajo actualizada correctamente',
        brand_set_default: 'Perfil de marca configurado como predeterminado correctamente',
        brand_created: 'Perfil de marca creado exitosamente',
        brand_updated: 'Perfil de marca actualizado con éxito',
        tier_created: 'Nivel de servicio creado correctamente',
        tier_updated: 'Nivel de servicio actualizado correctamente',
        tier_deprecated: 'Nivel de servicio obsoleto exitosamente',
        tier_activated: 'Nivel de servicio activado correctamente',
        period: 'Periodo',
        format: 'Formato',
        period_start: 'Inicio del periodo',
        period_end: 'Fin del período',
        reports_count: '{count} informes',
        reports_count_one: '{count} informe',
        metric: 'Métrica',
        value: 'Valor',
        showing_chart_view: 'Mostrando vista de gráfico',
        showing_table_view: 'Mostrando vista de tabla de datos',
        evidence_ids_placeholder: 'por ej. ev-001, ev-002',
        confirm_cancel_workflow: '¿Está seguro de que desea cancelar este flujo de trabajo? Esta acción no se puede deshacer.',
        export_csv: 'Exportar CSV',
        compliance_qualifications_title: 'Cualificaciones del servicio de cumplimiento',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Alineación de resultados regulatorios',
        regulatory_output_desc: 'Cada tipo de plantilla se asigna a una categoría de resultados regulatorios: los informes producen documentación de cumplimiento, las revisiones validan la efectividad del control, las evaluaciones cubren evaluaciones de riesgos (DPIA, análisis de brechas), las reuniones documentan sesiones de gobernanza. Los requisitos de evidencia definen los artefactos a los que debe hacer referencia cada entregable para estar preparado para la auditoría.',
        sla_compliance_title: 'SLA Seguimiento de cumplimiento',
        sla_compliance_desc: 'Los SLA de tiempo de respuesta y resolución son contractualmente vinculantes para la prestación de servicios de cumplimiento. La frecuencia de revisión determina la frecuencia con la que se evalúan los resultados de cumplimiento de los socios. Las infracciones de SLA en tickets críticos para el cumplimiento desencadenan una escalada automática y se registran en el registro de auditoría de socios como evidencia regulatoria.',
        regulatory_engagement_title: 'Patrones de participación regulatoria',
        regulatory_engagement_desc: 'Las categorías del flujo de trabajo se asignan a los tipos de compromiso regulatorio: la incorporación cubre la configuración inicial del cumplimiento y el análisis de brechas, la revisión periódica maneja evaluaciones de cumplimiento recurrentes, la escalada de incidentes administra la respuesta a las infracciones regulatorias, la salida garantiza que se cumplan las obligaciones de retención de datos. Cada paso puede requerir la recopilación de evidencia para completar el registro de auditoría.',
        compliance_escalation_title: 'Procedimientos de escalamiento críticos para el cumplimiento',
        compliance_escalation_desc: 'L1 maneja problemas operativos estándar. L2 cubre fallas que afectan el cumplimiento (incumplimiento de plazos regulatorios, brechas de control). L3 está reservado para escenarios de incumplimiento regulatorio que requieren atención ejecutiva inmediata. La infracción de SLA en tickets L2/L3 genera entradas de seguimiento de auditoría automáticamente. Todas las acciones de escalada y desescalada se registran permanentemente.',
        audit_regulatory_title: 'Rastro de evidencia regulatoria',
        audit_regulatory_desc: 'Esta pista de auditoría proporciona registros a prueba de manipulaciones para el cumplimiento normativo. Cada acción del socio (crear, actualizar, eliminar, escalar, cambiar de estado) se registra con la identidad del actor, la marca de tiempo y la diferencia de carga útil completa. Utilice filtros de tipo de entidad para aislar evidencia por dominio de cumplimiento. Exporte resultados filtrados para producir documentación lista para auditorías para inspecciones regulatorias.',
        evidence_trail_title: 'Rastro de evidencia entre módulos',
        evidence_trail_desc: 'Cada paso del flujo de trabajo puede recopilar ID de evidencia que se vinculan a entregables, eventos de auditoría y artefactos de cumplimiento en todos los módulos. Avanzar un paso con ID de evidencia crea una cadena verificable que conecta los flujos de trabajo de los socios con la documentación regulatoria. Los flujos de trabajo completos con cadenas de evidencia completas sirven como prueba del cumplimiento del compromiso de cumplimiento.',
        evidence_ids_label: 'ID de evidencia',
    },
    org_groups: {
        title: 'Grupos de organizaciones',
        new: 'Agregar grupo',
        group_name: 'Grupo Nombre',
        member_count: 'Número de miembros',
        root_org: 'Organización raíz',
        no_groups: 'No se encontraron org groups',
        add_organization: 'Agregar Organización',
        select_organization: 'Seleccione una organización...',
        no_available_orgs: 'No hay organizaciones disponibles para agregar.',
        no_members: 'No hay organizaciones miembro en este grupo.',
        member_added: 'Organización agregada al grupo.',
        member_removed: 'Organización eliminada del grupo.',
        already_in_group: 'Esta organización ya pertenece a un grupo.',
        org_not_found: 'Organización no encontrada.',
        add_member_error: 'No se pudo agregar la organización al grupo.',
        remove_member_error: 'No se pudo eliminar la organización del grupo.',
        confirm_remove_member: '¿Está seguro de que desea eliminar "{name}" de este grupo?',
        load_error: 'No se pudieron cargar los detalles del grupo de organizaciones.',
        page_title_list: 'Grupos organizativos',
        page_title_detail: 'Detalles del grupo organizativo',
        page_title_create: 'Crear grupo organizativo',
        page_title_settings: 'Configuración del grupo organizativo',
        page_title_users: 'Usuarios del grupo',
        page_title_reports: 'Informes grupales',
        page_title_dashboard: 'Panel de control del grupo',
        page_title_audit: 'Auditoría de grupo',
        page_title_org_audit: 'Auditoría del grupo organizacional',
        page_title_shared_vendors: 'Proveedores compartidos',
        page_title_shared_frameworks: 'Marcos compartidos',
        page_title_shared_forms: 'Plantillas de formularios compartidos',
        page_title_merged_vendors: 'Proveedores fusionados',
        page_title_settings_inheritance: 'Herencia de configuración',
        announce_loaded_groups: '{count} grupos organizativos cargados',
        announce_loaded_group: 'Grupo cargado {name}',
        announce_loaded_users: 'Usuarios del grupo {count} cargados',
        announce_loaded_reports: 'Informes {count} cargados',
        announce_loaded_vendors: '{count} proveedores compartidos cargados',
        announce_loaded_frameworks: '{count} marcos compartidos cargados',
        announce_loaded_templates: '{count} plantillas de formulario compartidas cargadas',
        announce_loaded_audit: '{count} eventos de auditoría cargados',
        announce_loaded_merged: 'Se cargaron {shared} proveedores compartidos y {local} locales',
        announce_dashboard_loaded: 'Panel cargado.',
        announce_dashboard_refreshed: 'Panel actualizado.',
        announce_dashboard_refresh_failed: 'No se pudo actualizar el panel.',
        announce_refreshing: 'Panel de control actualizado...',
        announce_settings_loaded: 'Herencia de configuración cargada.',
        announce_settings_saved: 'La configuración se guardó correctamente.',
        announce_saving_settings: 'Guardando configuración...',
        announce_error_saving: 'Error al guardar la configuración.',
        announce_group_dissolved: 'Grupo disuelto.',
        announce_creating_group: 'Creando grupo organizativo...',
        announce_group_created: 'El grupo organizativo se creó correctamente.',
        announce_error_creating: 'Error al crear el grupo organizativo.',
        announce_vendor_published: 'Proveedor publicado correctamente.',
        announce_vendor_retired: 'Vendedor jubilado.',
        announce_framework_published: 'Marco publicado con éxito.',
        announce_framework_retired: 'Marco retirado.',
        announce_template_published: 'Plantilla de formulario publicada correctamente.',
        announce_template_retired: 'Plantilla de formulario retirada.',
        announce_role_assigned: 'Rol asignado exitosamente.',
        announce_error_assign_role: 'Error al asignar rol.',
        announce_role_revoked: 'Rol revocado exitosamente.',
        announce_report_generating: 'Generando informe...',
        announce_report_requested: 'Se solicita generación de informe.',
        announce_error_report: 'Error al solicitar informe.',
        announce_report_deleted: 'Informe eliminado.',
        announce_search_applied: 'Búsqueda aplicada.',
        announce_search_cleared: 'Búsqueda borrada.',
        announce_tab_selected: '{tab} pestaña seleccionada',
        announce_error_add_member: 'Error al agregar miembro al grupo.',
        announce_category_reverted: '{category} volvió a ser heredado del padre.',
        announce_category_overridden: '{category} configurado en anulación local.',
        announce_override_saved: '{category} anulación guardada.',
        announce_export_started: 'Exportando auditoría como {format}...',
        announce_export_completed: 'Exportación como {format} completada.',
        announce_export_failed: 'La exportación falló.',
        announce_filter_org: 'Filtrado por organización {name}',
        announce_filter_org_cleared: 'Filtro de organización borrado',
        announce_filter_event: 'Filtrado por tipo de evento {type}',
        announce_filter_cleared: 'Filtro borrado',
        announce_download: 'Descargando {name} como {format}',
        error_load_groups: 'No se pudieron cargar los grupos organizativos. Por favor inténtalo de nuevo.',
        error_load_users: 'No se pudieron cargar los usuarios del grupo.',
        error_load_reports: 'No se pudieron cargar los informes del grupo.',
        error_load_dashboard: 'No se pudo cargar el panel del grupo.',
        error_load_settings: 'No se pudo cargar la configuración del grupo.',
        error_load_vendors: 'No se pudieron cargar los proveedores fusionados.',
        error_save_settings: 'No se pudo guardar la configuración.',
        error_dissolve_group: 'No se pudo disolver el grupo.',
        error_assign_role: 'No se pudo asignar el rol.',
        error_create_group: 'No se pudo crear el grupo organizativo. Por favor inténtalo de nuevo.',
        error_request_report: 'No se pudo solicitar el informe.',
        error_group_name_required: 'El nombre del grupo es obligatorio.',
        success_settings_saved: 'La configuración se guardó correctamente.',
        confirm_retire_vendor: '¿Está seguro de que desea retirar el proveedor "{name}"?',
        confirm_retire_framework: '¿Está seguro de que desea retirar el marco "{name}"?',
        confirm_retire_template: '¿Está seguro de que desea retirar la plantilla de formulario "{name}"?',
        confirm_dissolve_group: '¿Está seguro de que desea disolver "{name}"? Esta acción no se puede deshacer.',
        confirm_revoke_role: '¿Está seguro de que desea revocar el rol "{role}" de "{name}"?',
        confirm_delete_report: '¿Está seguro de que desea eliminar el informe "{name}"?',
        dissolve_group: 'Disolver grupo',
        dissolve_warning: 'Esto disolverá permanentemente el grupo organizativo. Todas las organizaciones miembros serán eliminadas.',
        count_groups: '{count} grupo',
        count_groups_plural: '{count} grupos',
        count_users: '{count} usuario',
        count_users_plural: '{count} usuarios con roles grupales',
        count_events: '{count} evento',
        count_events_plural: '{count} eventos',
        count_reports: '{count} informe',
        count_reports_plural: '{count} informes',
        count_vendors: '{count} proveedor compartido en todo el grupo',
        count_vendors_plural: '{count} proveedores compartidos en todo el grupo',
        count_frameworks: '{count} marco compartido en todo el grupo',
        count_frameworks_plural: '{count} marcos compartidos en todo el grupo',
        count_templates: '{count} plantilla compartida en todo el grupo',
        count_templates_plural: '{count} plantillas compartidas en todo el grupo',
        count_shared_vendors: '{count} proveedores compartidos',
        count_local_vendors: '{count} proveedores locales',
        merged_combined_view: 'Vista combinada de {shared} proveedores compartidos y {local} locales',
        dashboard: {
            title: 'Panel del grupo',
            loading: 'Cargando dashboard data...',
            total_groups: 'Total Grupos',
            total_members: 'Total de miembros',
            active_groups: 'Activo Grupos',
            overall_compliance_score: 'Puntuación de cumplimiento general',
            group_risk_level: 'Nivel de riesgo del grupo',
            sla_compliance: 'SLA Cumplimiento',
            compliance_posture: 'Postura de cumplimiento',
            risk_heatmap: 'Mapa de calor de riesgo',
            sla_performance: 'SLA Rendimiento',
            recent_incidents: 'Incidentes recientes',
            recent_activity: 'Actividad reciente',
            refresh_label: 'Actualizar datos del panel',
            score_good: 'bueno',
            score_needs_attention: 'necesita atencion',
            score_critical: 'Crítico',
            trend_up: 'Tendencia ascendente',
            trend_down: 'Tendencia a la baja',
            trend_stable: 'Estable',
            risk_low: 'Low',
            risk_medium: 'Medio',
            risk_high: 'Alto',
            risk_critical: 'Crítico',
            risk_unknown: 'Desconocido',
            col_organization: 'Organización',
            col_score: 'Puntuación',
            col_trend: 'Tendencia',
            col_open_issues: 'Problemas abiertos',
            col_critical_issues: 'Cuestiones críticas',
            col_health: 'Salud',
            col_vendor_risk: 'Riesgo del proveedor',
            col_data_risk: 'Riesgo de datos',
            col_compliance_risk: 'Riesgo de cumplimiento',
            col_total_cases: 'Casos totales',
            col_on_time: 'A tiempo',
            col_breached: 'violado',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Promedio de días',
            sr_vendor_risk: 'Puntuación de riesgo del proveedor: {score}',
            sr_data_risk: 'Puntuación de riesgo de datos: {score}',
            sr_compliance_risk: 'Puntuación de riesgo de cumplimiento: {score}',
            sr_sla_breaches: 'Advertencia: {count} SLA infracciones',
            sr_severity: 'Gravedad: {severity}',
        },
        detail: {
            title: 'Detalle del grupo',
            members: 'Miembros',
            settings: 'Configuración',
            audit: 'Auditoría',
            sr_loading: 'Cargando detalles del grupo organizativo',
            sr_loading_hierarchy: 'Cargando jerarquía',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'organización miembro',
            root_org_label: 'organización raíz',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Usuarios del grupo',
            add_user: 'Agregar usuario',
            no_users: 'Sin usuarios en este grupo.',
            role_group_admin: 'Administrador de grupo',
            role_group_reader: 'Lector de grupo',
            sr_loading: 'Cargando usuarios del grupo',
        },
        reports: {
            title: 'Informes del grupo',
            no_reports: 'No se encontraron reports',
            generate_report: 'Generar nuevo informe',
            report_type_compliance: 'Resumen de cumplimiento',
            report_type_risk: 'Descripción general del riesgo',
            report_type_sla: 'SLA Rendimiento',
            report_type_incident: 'Informe de incidente',
            report_type_executive: 'Informe ejecutivo',
            report_type_board: 'Informe de la Junta',
            status_queued: 'En cola',
            status_generating: 'generando',
            status_completed: 'Completado',
            status_failed: 'Fallido',
            period_start: 'Inicio del periodo',
            period_end: 'Fin del período',
            sr_loading: 'Cargando informes de grupo',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Proveedores compartidos',
            no_vendors: 'No se encontraron shared vendors',
            merged_vendors: 'Proveedores fusionados',
            publish_form_label: 'Publicar proveedor compartido',
            sr_loading: 'Cargando proveedores compartidos',
            sr_loading_merged: 'Cargando proveedores fusionados',
            local_vendors: 'Proveedores locales',
            source_shared: 'Compartido del grupo',
            source_local: 'Proveedor local',
            category_technology: 'Tecnología',
            category_consulting: 'Consultoría',
            category_outsourcing: 'Subcontratación',
            category_cloud: 'nube',
            category_data_processing: 'Procesamiento de datos',
            category_other: 'Otro',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Medio',
            risk_tier_high: 'Alto',
            risk_tier_critical: 'Crítico',
            compliance_compliant: 'Cumple',
            compliance_non_compliant: 'No conforme',
            compliance_pending: 'Pendiente',
            compliance_under_review: 'En revisión',
            status_active: 'Activo',
            status_retired: 'jubilado',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Código del marco',
            title: 'Marcos compartidos',
            no_frameworks: 'No se encontraron shared frameworks',
            publish_form_label: 'Publicar marco compartido',
            sr_loading: 'Cargando marcos compartidos',
            status_active: 'Activo',
            status_retired: 'jubilado',
        },
        shared_forms: {
            title: 'Plantillas de formularios compartidas',
            no_forms: 'No se encontraron shared form templates',
            publish_form_label: 'Publicar plantilla de formulario compartido',
            sr_loading: 'Cargando plantillas de formulario compartido',
            status_active: 'Activo',
            status_retired: 'jubilado',
        },
        audit: {
            title: 'Auditoría del grupo',
            consolidated_title: 'Pista de auditoría consolidada',
            consolidated_subtitle: 'Eventos de auditoría entre organizaciones para este grupo',
            audit_log_title: 'Grupo Auditoría Registro',
            event_type: 'Evento Tipo',
            actor: 'Actor',
            timestamp: 'Marca de tiempo',
            source_org: 'Org. de origen',
            org: 'Org',
            summary: 'Resumen',
            details: 'Detalles',
            no_events: 'No se encontraron audit events',
            all_events: 'Todos Eventos',
            all_orgs: 'Todas las org.',
            organization: 'Organización',
            from: 'Desde',
            to: 'To',
            search_events: 'Buscar eventos...',
            clear: 'Borrar',
            events_24h: 'Eventos (24h)',
            events_7d: 'Eventos (7d)',
            active_orgs: 'Org. activas',
            exporting: 'Exportando...',
            export_csv: 'Exportar CSV',
            export_json: 'Exportar JSON',
            back_to_group: 'Volver a Grupo',
            back_to_detail: 'Volver al detalle',
            sr_loading: 'Cargando eventos de auditoría',
            sr_loading_summary: 'Cargando resumen de auditoría',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Grupo Configuración',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Herencia de configuración',
            inheritance_subtitle: 'Configurar qué categorías de configuración heredan del grupo versus usar anulaciones locales',
            sla_settings: 'SLA Configuración',
            calendar_settings: 'Calendario Configuración',
            notification_settings: 'Notificación Configuración',
            intake_settings: 'Configuración de entrada',
            cmp_integration: 'Integración CMP',
            overridden: 'Anulado',
            inheriting: 'Heredando',
            hide_defaults: 'Ocultar valores predeterminados',
            show_group_defaults: 'Mostrar Grupo Defaults',
            group_defaults: 'Valores predeterminados del grupo',
            inherit_from_group: 'Inherit de Grupo',
            use_local_override: 'Usar anulación local',
            local_override_json: 'Anulación local (JSON)',
            save_override: 'Guardar anulación',
            saving: 'Guardando...',
            sr_loading: 'Cargando herencia de configuración',
            sr_loading_settings: 'Cargando configuración de grupo',
            sr_enabled: 'Habilitado',
            sr_disabled: 'Discapacitado',
            sr_override_active: 'Anulación local activa',
            sr_inherited: 'Heredado de los padres',
            danger_zone: 'Zona de peligro',
            sharing_options: 'Opciones para compartir',
            advanced_options: 'Opciones avanzadas',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Activo',
        status_dissolved: 'disuelto',
        status_pending: 'Pendiente',
        status_suspended: 'suspendido',
    },
    integrations: {
        nav: {
            webhooks: 'Ganchos web',
            domains: 'Dominios',
            export_schedules: 'Programación de exportaciones',
            email_log: 'Registro de correos',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Usuarios',
            roles: 'Roles',
            profile: 'Mi perfil',
        },
        security: {
            mfa: 'Configuración MFA',
            sso: 'Configuración SSO',
            api_keys: 'Claves de API',
            service_accounts: 'Cuentas de servicio',
            ip_allowlist: 'Lista blanca de IP',
            delegations: 'Delegaciones',
            break_glass: 'Acceso de emergencia',
            session_settings: 'Configuración de sesión',
            active_sessions: 'Sesiones activas',
            access_reviews: 'Revisiones de acceso',
            my_reviews: 'Mis revisiones',
            role_analytics: 'Analítica de roles',
            attestation_schedules: 'Programación de atestaciones',
            sod_rules: 'Reglas de SoD',
            sod_violations: 'Violaciones de SoD',
        },
        module_config: {
            title: 'Configuración de módulos',
            subtitle: 'Habilite o deshabilite módulos funcionales para su organización. Los módulos deshabilitados se ocultan de la navegación.',
            enabled: 'Habilitado',
            disabled: 'Deshabilitado',
            save: 'Guardar configuración',
            saving: 'Guardando...',
            saved: 'Configuración guardada correctamente',
            nav_label: 'Configuración de módulos',
            settings: 'Configuración',
            webhooks: 'Ganchos web',
            domains: 'Dominios',
            export_schedules: 'Programación de exportaciones',
            email_log: 'Registro de correos',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Archivos',
            configuration: 'Configuración',
            browser: 'Explorador de archivos',
            dashboard: 'Panel de archivos',
        },
        immutable_audit_trails: {
            nav_group_label: 'Auditoría y prueba',
            nav_settings: 'Configuración',
            nav_status: 'Estado de prueba',
            nav_integrity_center: 'Centro de integridad',
            nav_usage: 'Uso y créditos',
            settings_title: 'Seguimientos de auditoría inmutables: configuración',
            settings_subtitle: 'Configuración por organización para el complemento Immutable Audit Trails.',
            settings_banner: 'El complemento Immutable Audit Trails no está habilitado para su organización. Habilítelo aquí para comenzar a anclar pruebas para eventos de auditoría de Nivel A y B.',
            status_title: 'Seguimientos de auditoría inmutables: estado de prueba',
            status_subtitle: 'Relaciones de prueba por evento entre módulos.',
            status_banner: 'El complemento Immutable Audit Trails no está habilitado para su organización. Visita Configuración para habilitarlo.',
            integrity_center_title: 'Seguimientos de auditoría inmutables: Centro de integridad',
            integrity_center_subtitle: 'Verificar pruebas y exportar Integrity Packs.',
            integrity_center_banner: 'El complemento Immutable Audit Trails no está habilitado para su organización. Visite Configuración para habilitarlo antes de ejecutar la verificación o exportar Integrity Packs.',
            usage_title: 'Seguimientos de auditoría inmutables: uso y créditos',
            usage_subtitle: 'Saldo de crédito, compras de paquetes e historial de uso.',
            usage_banner: 'Habilite Seguimientos de auditoría inmutables en Configuración para comenzar a consumir créditos de prueba.',
            module_tips_placeholder: 'Consejos y orientación para esta superficie están disponibles en el centro de ayuda.',
            column_label: 'Prueba',
            no_proof_aria: 'No se han registrado pruebas',
            proof_status_receipt_label: 'recibo {id}',
            in_progress_hint: 'En progreso: se actualiza automáticamente',
            status_pills: {
                pending: 'Pendiente',
                submitted: 'Enviado',
                anchored: 'Anclado',
                confirmed: 'Confirmado',
                retrying: 'Reintentando',
                paused_cap: 'En pausa (tapa)',
                failed: 'Fallido',
                failed_auth: 'Error de autenticación',
            },
            status_tooltips: {
                pending: 'Pendiente: en cola para el servicio de prueba. Pasará a Enviado en breve; no se necesita ninguna acción.',
                submitted: 'Enviado: su evento fue enviado al servicio de pruebas. Para el modo por lotes, está esperando ser sellado y anclado en la cadena. Se actualiza automáticamente; no se necesita ninguna acción.',
                anchored: 'Anclado: el lote se ha escrito en la cadena y está esperando la confirmación final. Se actualiza automáticamente; no se necesita ninguna acción.',
                confirmed: 'Confirmado: la prueba está anclada en la cadena y es verificable.',
                retrying: 'Reintentando: se produjo un error transitorio. El reconciliador vuelve a intentarlo automáticamente; no se necesita ninguna acción.',
                paused_cap: 'En pausa: se alcanzó su límite mensual de crédito de prueba. Canjee más créditos o espere hasta el próximo mes de facturación.',
                failed: 'Fallido: el anclaje no se realizó correctamente. El reconciliador volverá a intentarlo automáticamente; póngase en contacto con el soporte si persiste.',
                failed_auth: 'La autenticación falló: el servicio de prueba rechazó nuestras credenciales. Consulte la página Configuración de seguimientos de auditoría inmutables.',
            },
            mode_pills: {
                direct: 'directo',
                batch: 'lote',
            },
            drawer: {
                title: 'Detalles de la prueba',
                close: 'Cerrar',
                section_proof: 'Prueba',
                section_batch: 'Membresía por lotes',
                status: 'Estado',
                proof_reference: 'Referencia de prueba',
                receipt_id: 'ID de recibo',
                taxonomy_class: 'clase de taxonomía',
                units_consumed: 'Unidades consumidas',
                created_at: 'Creado',
                verification_ref: 'Referencia de verificación de cadena',
                chain: 'cadena',
                tx_hash: 'hash de transmisión',
                block_ref: 'Bloquear referencia',
                batch_reference: 'Referencia de lote',
                leaf_index: 'Índice de hojas',
                local_event_hash: 'hash de evento local',
                show_inclusion: 'Mostrar ruta de inclusión',
                hide_inclusion: 'Ocultar ruta de inclusión',
                privacy_note: 'La carga útil tiene hash; No se dejaron datos personales en ComplianceOne para este evento.',
                verify_now: 'Verificar ahora',
                verify_pending: 'Verificación en curso...',
                verify_verified: 'Verificado: se aprobaron los controles locales + del lado de la cadena.',
                verify_issue_prefix: 'Problema de verificación:',
                verify_failed: 'La llamada de verificación falló.',
                verify_result_prefix: 'Veredicto de verificación: ',
                verify_pending_note: 'Verificación pendiente: esta prueba aún está pendiente de anclaje en la cadena. Inténtelo de nuevo después de sellar el lote.',
                awaiting_anchor_note: 'Esta prueba es parte de un lote abierto. La verificación está disponible una vez que el lote se sella y se ancla en la cadena, generalmente dentro de la ventana de lotes configurada de la organización. La insignia de estado se actualizará automáticamente.',
                terminal_error_note: 'Esta prueba no logró consolidarse. El conciliador del estado de la prueba volverá a intentarlo automáticamente; La verificación manual no tiene nada que comprobar todavía.',
            },
            integrity_center: {
                section_run: 'Ejecutar verificación',
                section_progress: 'Ejecutar progreso',
                section_issues: 'Problemas',
                period_start: 'Inicio del período',
                period_end: 'Fin del período',
                module_filter: 'Módulos (separados por comas, opcionales)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Ejecutar verificación',
                export_button: 'Paquete de integridad de exportación',
                export_success: 'Paquete de integridad exportado.',
                export_failed: 'La exportación falló.',
                empty_disabled: 'Habilite Seguimientos de auditoría inmutables en Configuración para ejecutar pases de verificación.',
                error_period_required: 'Elija una fecha de inicio y finalización.',
                error_start_run: 'No se pudo iniciar la ejecución.',
                processed: 'Procesado: {processed} de {total}',
                col_verdict: 'Veredicto',
                col_count: 'contar',
                col_proof_link: 'Enlace de prueba',
                col_module: 'Módulo',
                col_event_type: 'Tipo de evento',
                col_expected: 'hash esperado',
                col_recomputed: 'hash recalculado',
                col_raised_at: 'Criado en',
                any_verdict: 'cualquier veredicto',
                no_issues: 'No se encontraron problemas.',
            },
            settings_page: {
                load_error: 'No se pudo cargar la configuración.',
                save: 'Guardar',
                save_success: 'Configuración guardada.',
                save_error: 'Error al guardar.',
                discard: 'Descartar cambios',
                read_only_banner: 'Tienes acceso de solo lectura. Comuníquese con un administrador de la plataforma para realizar cambios.',
                section_status: 'Estado y habilitación',
                enabled_label: 'Complemento habilitado',
                enabled_help: 'Cuando está habilitado, cada evento de auditoría de Nivel A y Nivel B está anclado y codificado.',
                section_mode: 'Modo de prueba predeterminado',
                mode_direct: 'Directo (un presentador por evento)',
                mode_batch: 'Lote (anclaje de raíz de Merkle por grupo)',
                mode_hybrid: 'Híbrido (predeterminado de registro por clase)',
                batch_window_label: 'Ventana de lote (minutos, 1 a 60)',
                section_taxonomy: 'Taxonomía de eventos protegidos',
                taxonomy_help: 'Anulaciones de clases por evento. Nivel A → modo directo, Nivel B → modo por lotes, Desactivado → no probado.',
                taxonomy_search: 'Anulaciones de búsqueda',
                taxonomy_reset: 'Restablecer los valores predeterminados',
                taxonomy_event_class: 'clase de evento',
                taxonomy_override: 'Anular',
                taxonomy_actions: 'Acciones',
                taxonomy_clear: 'Borrar',
                taxonomy_no_overrides: 'Sin anulaciones: utilizando los valores predeterminados del registro.',
                taxonomy_add_class: 'event_class.código',
                taxonomy_add: 'Agregar anulación',
                section_caps: 'Límites y límites',
                cap_label: 'Límite mensual (unidades; en blanco = ilimitado)',
                warn_label: 'Umbral de advertencia (unidades; debe ser inferior al límite)',
                auto_pause_label: 'Pausar automáticamente nuevas pruebas una vez que se alcanza el límite',
                section_credentials: 'Credenciales',
                credential_id: 'ID de credencial',
                credential_rotated_at: 'Última rotación',
                rotate_button: 'Rotar credencial',
                rotate_success: 'Credencial rotada. Credencial antigua válida por {n} minutos más.',
                rotate_failed: 'Error en la rotación de credenciales.',
                rotate_modal_title: '¿Rotar la credencial AesirX?',
                rotate_modal_body: 'La rotación emite una nueva credencial. La credencial anterior sigue siendo aceptada por AesirX durante un período de gracia de 60 minutos para que los trabajadores en vuelo puedan terminar sus trabajos actuales. Después de 60 minutos se revoca la antigua credencial.',
                rotate_modal_confirm: 'Girar ahora',
                tier_a_off_modal_title: '¿Desactivar la revisión para una clase de evento?',
                tier_a_off_modal_body: 'Establecer una clase de evento en "Desactivado" evita que el grifo de prueba ancle esos eventos. La arquitectura §6 explica la implicación regulatoria: los eventos de Nivel A son relevantes para el regulador; desactivarlos puede romper la defensa de su pista de auditoría.',
                tier_a_off_modal_confirm: 'Entiendo: aplicar anulación',
                error_warn_lt_cap: 'El umbral de advertencia debe ser inferior al límite mensual.',
                error_window_range: 'La ventana del lote debe estar entre 1 y 60 minutos.',
                error_run_in_progress: 'Se está realizando una ejecución de verificación; No puedo desactivar el complemento en este momento.',
            },
            status_page: {
                filter_status: 'Estado',
                filter_mode: 'Modo de prueba',
                filter_module: 'Módulo',
                filter_source_collection: 'Colección fuente',
                filter_from_date: 'De',
                filter_to_date: 'a',
                filter_search: 'Buscar',
                any_status: 'Cualquier estado',
                any_mode: 'cualquier modo',
                col_module: 'Módulo',
                col_event_type: 'Tipo de evento',
                col_proof_status: 'Estado',
                col_proof_mode: 'Modo',
                col_created_at: 'Creado',
                col_units: 'Unidades',
                empty_disabled: 'El complemento Immutable Audit Trails no está habilitado para su organización. Visita Configuración para habilitarlo.',
                empty_enabled_no_rows: 'Ningún enlace de prueba coincide con los filtros actuales.',
                export_csv: 'Exportar CSV',
                export_xlsx: 'Exportar XLSX',
                export_success: 'Exportación completa',
                export_failed: 'Exportación fallida',
            },
            usage_page: {
                section_alerts: 'Alertas',
                alert_dismiss: 'Descartar',
                alert_dismiss_aria: 'Descartar alerta {id}',
                alert_dismissed: 'Alerta descartada',
                alert_dismiss_failed: 'No se pudo descartar la alerta',
                section_balance: 'Saldo crediticio',
                balance_label: 'Créditos disponibles',
                balance_hint: 'Unidades restantes (1 unidad = 1 prueba de evento protegido).',
                section_current_month: 'Mes actual',
                consumed_label: 'consumido',
                granted_label: 'Concedido',
                refunded_label: 'Reembolsado',
                net_label: 'neto',
                section_history: 'Historial de uso',
                history_kind: 'amable',
                history_units: 'Unidades',
                history_balance_after: 'Saldo después',
                history_notes: 'Notas',
                history_at: 'cuando',
                cap_warning_banner: 'Ha cruzado su umbral de advertencia para este mes de facturación. Considere canjear créditos adicionales.',
                cap_paused_banner: 'Se alcanzó el límite mensual. El envío de pruebas se suspende hasta el próximo mes o hasta que se aumente el límite.',
                section_redeem: 'Canjear paquete de crédito',
                redeem_purchase_id: 'Número de factura',
                redeem_package: 'Paquete',
                redeem_button: 'Canjear',
                redeem_pending: 'Canjeando…',
                redeem_success: 'Créditos canjeados',
                redeem_failed: 'Canje fallido',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'unidades',
                alert_dismissing: 'Descartando…',
                alert_kind: {
                    cap_reached: 'Límite mensual alcanzado',
                    low_balance: 'saldo bajo',
                    auth_failure: 'Fallo de autenticación',
                    merkle_mismatch: 'Merkle no coincide',
                    batch_terminal_failure: 'Fallo del terminal de lote',
                    direct_terminal_failure: 'Fallo del terminal de envío directo',
                },
                usage_kind: {
                    consume: 'consumir',
                    refund: 'Reembolso',
                    grant: 'subvención',
                    adjustment: 'Ajuste',
                    reservation: 'Reserva',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'entrante',
                package_growth: 'Crecimiento',
                package_enterprise: 'Empresa',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Comprar más créditos',
                    purchase_title: 'Comprar más créditos',
                    purchase_subtitle: 'Elija un paquete. Se le dirigirá al pago seguro de Stripe y su factura llegará por correo electrónico.',
                    checkout_button: 'Continuar al pago',
                    checkout_pending: 'Abriendo el pago…',
                    cancel_button: 'Cancelar',
                    purchase_error: 'No se pudo iniciar el pago. Vuelva a intentarlo en breve.',
                    redeem_subtitle: '¿Ya pagó? Seleccione el paquete que compró e introduzca el ID de factura de su correo electrónico para añadir los créditos.',
                    error_not_found: 'No se encontró ninguna factura con ese número. Comprueba el número de factura e inténtalo de nuevo.',
                    error_not_paid: 'Esta factura aún no está marcada como pagada.',
                    error_org_mismatch: 'Esta factura no se emitió a su organización.',
                    error_package_mismatch: 'Esta factura no coincide con el paquete seleccionado.',
                    error_billing_unavailable: 'No se pudo contactar con el servicio de facturación. Vuelva a intentarlo en breve.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Configuración de archivos',
            subtitle: 'Configure límites de carga de archivos, políticas de retención y anulaciones por tipo de propietario para la superficie de archivos adjuntos de la plataforma.',
            section_global: 'Valores predeterminados globales',
            section_global_help: 'Estos límites se aplican a todos los archivos cargados por los usuarios de su organización, a menos que una anulación por tipo de propietario a continuación los cambie.',
            section_overrides: 'Anulaciones por tipo de propietario',
            section_overrides_help: 'Anule los límites globales para un tipo de propietario específico. Los valores por tipo de propietario siempre prevalecen sobre la configuración global.',
            max_file_size_label: 'Tamaño máximo de archivo',
            max_file_size_hint: 'Por carga individual (MiB)',
            max_total_attachments_label: 'Máximo total de archivos adjuntos',
            max_total_attachments_hint: 'En toda la organización',
            max_total_size_label: 'Almacenamiento total máximo',
            max_total_size_hint: 'En toda la organización (MiB)',
            allowed_mime_types_label: 'Tipos de archivos permitidos',
            allowed_mime_types_placeholder: 'Seleccionar tipos MIME',
            retention_days_label: 'Periodo de retención (días)',
            retention_days_hint: 'Cuánto tiempo se deben conservar los archivos antes de que sean elegibles para su eliminación. Déjelo en blanco si no hay política de retención.',
            legal_hold_label: 'Retención legal',
            legal_hold_hint: 'Cuando está habilitado, la eliminación definitiva de un archivo de este tipo de propietario requiere el permiso de anulación de retención legal y la participación explícita del operador.',
            save: 'Guardar cambios',
            saving: 'Guardando…',
            reset: 'Reiniciar',
            save_success: 'Configuración guardada',
            empty_state_title: 'Usar los valores predeterminados de la plataforma',
            empty_state_body: 'Actualmente, su organización utiliza la política de archivos predeterminada de la plataforma. Edite cualquier valor a continuación para crear una anulación a nivel de organización.',
            read_only_banner: 'Tiene acceso de solo lectura a la configuración del archivo. Comuníquese con un administrador de la plataforma para realizar cambios.',
            owner_type_default_badge: 'Predeterminado',
            owner_type_overridden_badge: 'Anulado',
            owner_type_label: 'Tipo de propietario',
            owner_type_collapse: 'Colapso',
            owner_type_expand: 'Expandir',
            override_save: 'Guardar anulación',
            override_remove: 'Eliminar anulación',
            mime_labels: {
                'application/pdf': 'PDF documento',
                'image/png': 'imagen PNG',
                'image/jpeg': 'imagen JPEG',
                'image/jpg': 'imagen JPG',
                'application/msword': 'Documento de Word (heredado)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'documento de palabra',
                'application/vnd.ms-excel': 'Hoja de cálculo de Excel (heredada)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'hoja de cálculo de excel',
                'text/csv': 'CSV archivo',
                'text/plain': 'Texto sin formato',
                'message/rfc822': 'Mensaje de correo electrónico',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Explorador de archivos',
            subtitle: 'Busque, filtre y administre todos los archivos adjuntos cargados en toda la organización.',
            filter_owner_type: 'Tipo de propietario',
            filter_uploader: 'Subido por',
            filter_from: 'Subido después',
            filter_to: 'Subido antes',
            filter_size_min: 'Tamaño mínimo (MiB)',
            filter_size_max: 'Tamaño máximo (MiB)',
            filter_mime: 'tipo de archivo',
            filter_deleted_state: 'Estado de eliminación',
            filter_deleted_any: 'Todos los archivos',
            filter_deleted_only: 'Sólo eliminado',
            filter_deleted_exclude: 'Excluir eliminado',
            filter_orphaned: 'Solo huérfano',
            filter_search_placeholder: 'Buscar nombre de archivo…',
            filter_apply: 'Aplicar filtros',
            filter_clear: 'Limpiar filtros',
            col_filename: 'Nombre de archivo',
            col_owner_type: 'Tipo de propietario',
            col_owner_entity: 'propietario',
            col_uploader: 'Subido por',
            col_uploaded_at: 'subido',
            col_size: 'Tamaño',
            col_mime: 'Tipo',
            col_state: 'Estado',
            col_actions: 'Acciones',
            state_active: 'Activo',
            state_deleted: 'Eliminado',
            state_orphaned: 'huérfano',
            action_view_detail: 'Ver detalles',
            action_download: 'Descargar',
            action_ai_edits: 'AI Ediciones',
            action_soft_delete: 'eliminación suave',
            action_restore: 'Restaurar',
            action_hard_delete: 'Eliminación definitiva',
            bulk_soft_delete: 'Eliminación temporal seleccionada',
            bulk_export_csv: 'Exportar CSV',
            bulk_select_all: 'Seleccionar todo',
            reason_label: 'Razón',
            reason_min_10_chars: 'El motivo es obligatorio (al menos 10 caracteres)',
            reason_min_20_chars: 'El motivo es obligatorio (al menos 20 caracteres)',
            legal_hold_override_label: 'Anular retención legal',
            legal_hold_override_warning: 'Este archivo está bajo retención legal. La eliminación definitiva se registrará en el registro de auditoría con su motivo y el indicador de anulación.',
            empty_state_title: 'Ningún archivo coincide con tus filtros',
            empty_state_body: 'Ajuste sus filtros para ver resultados o bórrelos para ver todos los archivos.',
            pagination_prev: 'Anterior',
            pagination_next: 'Siguiente',
            pagination_page: 'Página {page} de {total}',
            pagination_size_label: 'Por página',
            pagination_total: 'Total: {count}',
            toast_soft_deleted: 'Archivo eliminado temporalmente',
            toast_restored: 'Archivo restaurado',
            toast_hard_deleted: 'Archivo eliminado permanentemente',
            toast_bulk_deleted: '{count} archivo(s) eliminado(s)',
            toast_error: 'Operación fallida',
            confirm_soft_delete_title: 'archivo de eliminación temporal',
            confirm_restore_title: 'Restaurar archivo',
            confirm_hard_delete_title: 'Eliminar archivo permanentemente',
            confirm_bulk_soft_delete_title: 'Eliminación temporal de archivos seleccionados',
            confirm_submit: 'Confirmar',
            confirm_cancel: 'Cancelar',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Detalles del archivo',
            close: 'Cerrar',
            meta_filename: 'Nombre de archivo',
            meta_size: 'Tamaño',
            meta_mime: 'Tipo',
            meta_owner_type: 'Tipo de propietario',
            meta_owner_entity: 'Entidad propietaria',
            meta_uploader: 'Subido por',
            meta_uploaded_at: 'subido',
            meta_storage_path: 'Ruta de almacenamiento',
            meta_deleted_state: 'Estado de eliminación',
            audit_section_title: 'Pista de auditoría',
            audit_load_more: 'Cargar más eventos',
            audit_empty: 'No se registraron eventos de auditoría para este archivo.',
            audit_config_changed: 'Configuración cambiada',
            audit_soft_deleted: 'Eliminado temporalmente',
            audit_restored: 'Restaurado',
            audit_hard_deleted: 'Eliminado permanentemente',
            audit_downloaded: 'Descargado',
            audit_quota_breached: 'Intento de incumplimiento de cuota',
            audit_owner_type_download_denied: 'Recuperación denegada',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} por {actor} el {at}',
            action_download: 'Descargar',
            action_soft_delete: 'eliminación suave',
            action_restore: 'Restaurar',
            action_hard_delete: 'Eliminación definitiva',
            effective_settings_title: 'Política efectiva',
            effective_max_file_size: 'Tamaño máximo de archivo',
            effective_retention_days: 'Retención',
            effective_legal_hold: 'Retención legal',
            effective_legal_hold_yes: 'Activo',
            effective_legal_hold_no: 'Ninguno',
        },
        files_dashboard: {
            title: 'Panel de archivos',
            subtitle: 'Uso de almacenamiento, crecimiento y archivos huérfanos en toda la organización.',
            kpi_total_files: 'Archivos totales',
            kpi_total_size: 'Almacenamiento total',
            kpi_near_quota: 'Uso de cuota',
            kpi_orphaned: 'Archivos huérfanos',
            chart_by_owner_type: 'Archivos por tipo de propietario',
            chart_growth_30d: 'crecimiento de 30 días',
            toggle_count: 'por conteo',
            toggle_size: 'Por tamaño',
            orphaned_panel_title: 'Archivos huérfanos (top 20)',
            orphaned_view_all: 'Ver todos los archivos huérfanos',
            orphaned_empty: 'No se encontraron archivos huérfanos.',
            growth_empty: 'No hay actividad de carga en los últimos 30 días.',
            retention_n_days: '{days} día(s)',
            retention_none: 'Sin política',
        },
    },
    category_labels: {
        'user-guide': 'Guía del usuario',
        'admin-guide': 'Guía de administración',
        'getting-started': 'Empezando',
        faq: 'Preguntas frecuentes',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Claves',
        webhook: 'gancho web',
        webhooks: 'Ganchos web',
        admin: 'administrador',
        aesirx: 'AesirX',
        purposes: 'Propósitos',
        preferences: 'Preferencias',
        onboarding: 'Incorporación',
        'first-steps': 'Primeros pasos',
        login: 'Iniciar sesión',
        navigation: 'Navegación',
        users: 'Usuarios',
        user: 'Usuario',
        roles: 'Roles',
        role: 'Rol',
        'access-control': 'Control de acceso',
        'access-management': 'Gestión de acceso',
        permissions: 'Permisos',
        permission: 'permiso',
        consent: 'Consentimiento',
        'consent-management': 'Gestión del consentimiento',
        privacy: 'Privacidad',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Auditoría',
        auditing: 'Auditoría',
        breach: 'incumplimiento',
        incident: 'Incidente',
        incidents: 'Incidentes',
        notification: 'Notificación',
        notifications: 'Notificaciones',
        reporting: 'Informes',
        risk: 'Riesgo',
        vendor: 'vendedor',
        vendors: 'Vendedores',
        'vendor-risk': 'Riesgo del proveedor',
        assessment: 'Evaluación',
        assessments: 'Evaluaciones',
        dpia: 'DPIA',
        'cross-border': 'Transfronterizo',
        transfer: 'Transferir',
        transfers: 'Transferencias',
        deletion: 'Eliminación',
        retention: 'Retención',
        'data-mapping': 'Mapeo de datos',
        discovery: 'Descubrimiento',
        classification: 'Clasificación',
        workflow: 'Flujo de trabajo',
        workflows: 'Flujos de trabajo',
        template: 'Plantilla',
        templates: 'Plantillas',
        catalog: 'Catálogo',
        monitoring: 'Monitoreo',
        tasks: 'Tareas',
        task: 'Tarea',
        'task-management': 'Gestión de tareas',
        organizations: 'Organizaciones',
        org: 'Organización',
        governance: 'Gobernanza',
        compliance: 'Cumplimiento',
        forms: 'Formularios',
        form: 'Formulario',
        connectors: 'Conectores',
        connector: 'Conector',
        integration: 'Integración',
        integrations: 'Integraciones',
        security: 'Seguridad',
        encryption: 'Cifrado',
        backup: 'Copia de seguridad',
        recovery: 'Recuperación',
        maintenance: 'Mantenimiento',
        settings: 'Configuración',
        configuration: 'Configuración',
        sla: 'SLA',
        escalation: 'Escalada',
        remediation: 'Remediación',
        evidence: 'evidencia',
        'case-management': 'Gestión de casos',
        profile: 'Perfil',
        delegation: 'Delegación',
        'service-accounts': 'Cuentas de servicio',
        license: 'Licencia',
        'license-management': 'Gestión de licencias',
        plan: 'Planificar',
        plans: 'Planes',
        dashboard: 'Panel de control',
        'web-consent': 'Consentimiento web',
        'partner-marketplace': 'Mercado de socios',
        partner: 'Socio',
        partners: 'Socios',
        deliverables: 'Entregables',
        'service-tiers': 'Niveles de servicio',
        'support-tickets': 'Boletos de soporte',
        'audit-trail': 'Pista de auditoría',
        'first-time-setup': 'Configuración por primera vez',
        setup: 'Configuración',
        quickstart: 'Inicio rápido',
        tour: 'Gira',
        tours: 'Visitas guiadas',
        'sample-data': 'Datos de muestra',
        'data-subject': 'Titular de los datos',
        'data-subjects': 'Sujetos de datos',
        pseudonymous: 'Seudónimo',
        identified: 'Identificado',
        'help-center': 'Centro de ayuda',
        article: 'Artículo',
        articles: 'Artículos',
        search: 'Buscar',
        'platform-settings': 'Configuración de la plataforma',
        'platform-config': 'Configuración de la plataforma',
        'module-config': 'Configuración del módulo',
        'module-configuration': 'Configuración del módulo',
        'rights-requests': 'Solicitudes de derechos',
        'access-accountability': 'Acceso a la responsabilidad',
        'data-discovery': 'Descubrimiento de datos',
        'incident-ops': 'Operaciones de incidentes',
        'deletion-orchestration': 'Orquestación de eliminación',
        'compliance-forms': 'Formularios de cumplimiento',
        'program-governance': 'Gobernanza del programa',
        localization: 'Localización',
        'privacy-scanner': 'Escáner de privacidad',
        'consent-properties': 'Propiedades de consentimiento',
        acceptances: 'Aceptaciones',
        access: 'Acceso',
        'access-review': 'Revisión de acceso',
        'access-reviews': 'Acceder a reseñas',
        accountability: 'Responsabilidad',
        acknowledgment: 'Reconocimiento',
        'action-plans': 'Planes de acción',
        activation: 'Activación',
        activations: 'Activaciones',
        'active-response': 'Respuesta activa',
        activity: 'Actividad',
        adequacy: 'Adecuación',
        'admin-guide': 'Guía de administración',
        aggregation: 'Agregación',
        agreements: 'Acuerdos',
        ai: 'AI',
        alerts: 'Alertas',
        analytics: 'Analítica',
        anchoring: 'Anclaje',
        approval: 'Aprobación',
        approvals: 'Aprobaciones',
        apps: 'Aplicaciones',
        assets: 'Activos',
        assignments: 'Asignaciones',
        attestation: 'atestación',
        'audit-log': 'Registro de auditoría',
        'audit-pack': 'Paquete de auditoría',
        authentication: 'Autenticación',
        authority: 'autoridad',
        'auto-play': 'Reproducción automática',
        automated: 'Automatizado',
        automation: 'Automatización',
        baseline: 'Línea de base',
        batches: 'Lotes',
        bidirectional: 'Bidireccional',
        bilingual: 'Bilingue',
        bindings: 'Fijaciones',
        blockchain: 'cadena de bloques',
        board: 'tablero',
        brand: 'Marca',
        brds: 'BRD',
        'break-glass': 'Rompecristales',
        builder: 'constructor',
        bulk: 'A granel',
        'bulk-sync': 'Sincronización masiva',
        bundles: 'Paquetes',
        calendar: 'Calendario',
        campaigns: 'Campañas',
        cases: 'Casos',
        categories: 'Categorías',
        category: 'categoría',
        certification: 'Certificación',
        chain: 'cadena',
        change: 'Cambiar',
        'change-awareness': 'Conciencia del cambio',
        'change-log': 'Registro de cambios',
        'change-management': 'Gestión de cambios',
        'change-requests': 'Solicitudes de cambio',
        'cmp-validation': 'CMP Validación',
        collection: 'Colección',
        comparison: 'Comparación',
        completeness: 'integridad',
        completion: 'Finalización',
        'compliance-posture': 'Postura de cumplimiento',
        'compliance-proof': 'Prueba de cumplimiento',
        'compliance-reviews': 'Revisiones de cumplimiento',
        config: 'configuración',
        confirmation: 'Confirmación',
        conflicts: 'Conflictos',
        consolidation: 'Consolidación',
        continuous: 'Continuo',
        contracts: 'Contratos',
        controls: 'Controles',
        cookies: 'galletas',
        cooperation: 'Cooperación',
        coordination: 'Coordinación',
        'corporate-governance': 'Gobierno Corporativo',
        'corrective-actions': 'Acciones correctivas',
        coverage: 'Cobertura',
        'credential-vault': 'Bóveda de credenciales',
        credentials: 'Credenciales',
        cycles: 'Ciclos',
        dashboards: 'Paneles de control',
        'data-assets': 'Activos de datos',
        'data-flows': 'Flujos de datos',
        'data-sensitivity': 'Sensibilidad de los datos',
        'data-steward': 'Administrador de datos',
        deadlines: 'Plazos',
        'decree-356': 'Decreto 356',
        dedup: 'deduplicación',
        definitions: 'Definiciones',
        deliverable: 'Entregable',
        departments: 'Departamentos',
        'dependency-order': 'Orden de dependencia',
        deployment: 'Implementación',
        detection: 'Detección',
        diff: 'diferencia',
        'digital-properties': 'Propiedades digitales',
        dimensions: 'Dimensiones',
        disable: 'Desactivar',
        'disaster-recovery': 'Recuperación ante desastres',
        disclosure: 'Divulgación',
        documentation: 'Documentación',
        documents: 'Documentos',
        domains: 'Dominios',
        dossier: 'Dossier',
        dpa: 'DPA',
        drift: 'Deriva',
        'drift-detection': 'Detección de deriva',
        'due-diligence': 'Debida diligencia',
        email: 'Email',
        emergency: 'Emergencia',
        enable: 'Habilitar',
        'enterprise-map': 'Mapa empresarial',
        erasure: 'borrado',
        evaluation: 'Evaluación',
        'event-bus': 'Autobús de eventos',
        exceptions: 'Excepciones',
        execution: 'Ejecución',
        executive: 'Ejecutivo',
        exercises: 'Ejercicios',
        export: 'Exportar',
        exports: 'Exportaciones',
        filing: 'Presentación',
        filter: 'Filtrar',
        filtering: 'Filtrado',
        findings: 'Hallazgos',
        flows: 'Flujos',
        'follow-up': 'Seguimiento',
        forensic: 'forense',
        forensics: 'forense',
        'form-filler': 'Rellenador de formularios',
        framework: 'Marco',
        frameworks: 'Marcos',
        'gap-analysis': 'Análisis de brechas',
        'gap-report': 'Informe de brecha',
        gaps: 'Brechas',
        gates: 'Puertas',
        'getting-started': 'Empezando',
        'golden-templates': 'Plantillas doradas',
        grc: 'GRC',
        'group-management': 'Gestión de grupos',
        groups: 'Grupos',
        'guided-tours': 'Visitas guiadas',
        guides: 'Guías',
        handling: 'Manejo',
        'hands-on': 'Manos a la obra',
        health: 'Salud',
        help: 'Ayuda',
        hierarchy: 'Jerarquía',
        history: 'Historia',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Proveedor de identidad',
        implementation: 'Implementación',
        import: 'Importar',
        improvement: 'Mejora',
        'incident-response': 'Respuesta a incidentes',
        inconsistencies: 'Inconsistencias',
        instances: 'Instancias',
        intake: 'ingesta',
        'intake-key': 'Clave de admisión',
        interactive: 'Interactivo',
        international: 'Internacional',
        inventory: 'inventario',
        investigation: 'investigación',
        investigations: 'Investigaciones',
        'ip-allowlist': 'Lista de direcciones IP permitidas',
        issues: 'Problemas',
        justification: 'Justificación',
        justifications: 'Justificaciones',
        kanban: 'Kanban',
        keyboard: 'Teclado',
        keys: 'llaves',
        'knowledge-base': 'Base de conocimientos',
        koncernstyring: 'Gobierno Corporativo',
        kpi: 'KPI',
        labeling: 'Etiquetado',
        language: 'Idioma',
        'language-packs': 'Paquetes de idiomas',
        languages: 'Idiomas',
        lawfulness: 'Legalidad',
        layout: 'Diseño',
        'legal-basis': 'Base legal',
        'lessons-learned': 'Lecciones aprendidas',
        levels: 'Niveles',
        library: 'Biblioteca',
        lifecycle: 'Ciclo de vida',
        lineage: 'Linaje',
        links: 'Enlaces',
        listings: 'Listados',
        live: 'en vivo',
        locale: 'Localidad',
        log: 'Log',
        logging: 'Registro',
        'managed-service': 'Servicio Gestionado',
        mapping: 'Mapeo',
        mappings: 'Mapeos',
        'market-packs': 'Paquetes de mercado',
        marketplace: 'Mercado',
        maturity: 'Madurez',
        metrics: 'Métricas',
        models: 'Modelos',
        module: 'Módulo',
        modules: 'Módulos',
        mps: 'MPS',
        'multi-factor': 'Multifactor',
        'multi-market': 'Multimercado',
        'multi-org': 'Multiorganización',
        'multi-property': 'Multipropiedad',
        'my-tasks': 'Mis tareas',
        obligations: 'Obligaciones',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orquestación',
        'org-groups': 'Grupos organizativos',
        organization: 'Organización',
        overdue: 'Vencido',
        overview: 'Descripción general',
        'owner-review': 'Revisión del propietario',
        package: 'Paquete',
        packages: 'Paquetes',
        packs: 'Paquetes',
        pagination: 'Paginación',
        'parent-child': 'Padre-hijo',
        patterns: 'Patrones',
        performance: 'Rendimiento',
        periodic: 'periódico',
        periods: 'Periodos',
        personal: 'personales',
        'personal-data': 'Datos personales',
        personas: 'Personas',
        planning: 'Planificación',
        platform: 'Plataforma',
        playbooks: 'Libros de jugadas',
        poc: 'PoC',
        'point-in-time': 'Punto en el tiempo',
        policies: 'Políticas',
        portfolio: 'Portafolio',
        'post-incident': 'Después del incidente',
        'power-user': 'Usuario avanzado',
        practice: 'Practica',
        pricing: 'Precios',
        'privacy-impact': 'Impacto en la privacidad',
        procedures: 'Trámites',
        processes: 'Procesos',
        processing: 'Procesamiento',
        'processing-activities': 'Actividades de procesamiento',
        procurement: 'Adquisiciones',
        productivity: 'Productividad',
        profiles: 'Perfiles',
        programs: 'Programas',
        progress: 'Progreso',
        proof: 'Prueba',
        propagation: 'Propagación',
        properties: 'Propiedades',
        'pub-sub': 'pub-sub',
        'public-form': 'Formulario público',
        quality: 'Calidad',
        questionnaires: 'Cuestionarios',
        'quick-links': 'Enlaces rápidos',
        'quick-start': 'Inicio rápido',
        'quick-starts': 'Inicios rápidos',
        raci: 'RACI',
        readiness: 'Preparación',
        'real-time': 'En tiempo real',
        reassessments: 'Reevaluaciones',
        reconciliation: 'reconciliación',
        record: 'Registro',
        records: 'Registros',
        recurring: 'recurrente',
        register: 'Registrarse',
        registry: 'Registro',
        regulations: 'Reglamentos',
        regulator: 'Regulador',
        regulatory: 'Regulador',
        'regulatory-frameworks': 'Marcos regulatorios',
        remap: 'Reasignar',
        reminders: 'Recordatorios',
        reports: 'Informes',
        requests: 'Solicitudes',
        requirements: 'Requisitos',
        response: 'Respuesta',
        'response-plans': 'Planes de respuesta',
        'response-team': 'Equipo de respuesta',
        'response-templates': 'Plantillas de respuesta',
        'response-time': 'Tiempo de respuesta',
        responses: 'Respuestas',
        responsibility: 'Responsabilidad',
        results: 'Resultados',
        review: 'Revisión',
        reviews: 'Reseñas',
        'right-to-delete': 'Derecho a eliminar',
        rights: 'Derechos',
        'risk-tiers': 'Niveles de riesgo',
        rollouts: 'Lanzamientos',
        rollup: 'resumen',
        ropa: 'ROPA',
        routing: 'Enrutamiento',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Reglas',
        runs: 'Corre',
        saml: 'SAML',
        scan: 'Escanear',
        scanning: 'Escaneo',
        scc: 'SCC',
        schedule: 'Horario',
        scheduled: 'Programado',
        schedules: 'Horarios',
        scheduling: 'Programación',
        scores: 'Puntuaciones',
        scoring: 'Puntuación',
        scripts: 'Guiones',
        'security-reviews': 'Revisiones de seguridad',
        'sensitive-data': 'Datos confidenciales',
        sensitivity: 'Sensibilidad',
        'separation-of-duties': 'Separación de deberes',
        service: 'Servicio',
        sessions: 'Sesiones',
        'setup-wizard': 'Asistente de configuración',
        severity: 'Gravedad',
        'shared-governance': 'Gobernanza compartida',
        'shared-settings': 'Configuración compartida',
        shortcuts: 'Atajos',
        sidebar: 'Barra lateral',
        signals: 'Señales',
        'single-sign-on': 'Inicio de sesión único',
        snapshots: 'Instantáneas',
        sod: 'SoD',
        sorting: 'Ordenar',
        sources: 'Fuentes',
        staging: 'Puesta en escena',
        standards: 'Estándares',
        status: 'Estado',
        structure: 'Estructura',
        'sub-processor': 'Subprocesador',
        'sub-processors': 'Subprocesadores',
        submissions: 'Envíos',
        subprocessors: 'Subprocesadores',
        subscription: 'Suscripción',
        suggestions: 'Sugerencias',
        summaries: 'Resúmenes',
        supplements: 'Suplementos',
        support: 'Soporte',
        sync: 'Sincronizar',
        system: 'Sistema',
        systems: 'Sistemas',
        tables: 'Mesas',
        tagging: 'Etiquetado',
        'tamper-proof': 'A prueba de manipulaciones',
        taxonomies: 'Taxonomías',
        taxonomy: 'Taxonomía',
        tenants: 'inquilinos',
        termination: 'Terminación',
        'third-party': 'Tercero',
        'ticket-sync': 'Sincronización de entradas',
        tickets: 'Entradas',
        tiers: 'Niveles',
        tokens: 'Fichas',
        totp: 'TOTP',
        traceability: 'Trazabilidad',
        tracking: 'Seguimiento',
        trail: 'Sendero',
        training: 'Entrenamiento',
        translation: 'Traducción',
        translations: 'Traducciones',
        transparency: 'Transparencia',
        trends: 'Tendencias',
        unified: 'unificado',
        updates: 'Actualizaciones',
        validation: 'Validación',
        vault: 'Bóveda',
        'vendor-requests': 'Solicitudes de proveedores',
        verification: 'Verificación',
        versioning: 'Versionado',
        versions: 'Versiones',
        vietnam: 'Vietnam',
        violations: 'Violaciones',
        visualization: 'Visualización',
        waivers: 'Exenciones',
        'web-records': 'Registros web',
        website: 'Sitio web',
        websites: 'Sitios web',
        weights: 'Pesos',
        'white-label': 'Marca blanca',
        withdrawal: 'Retiro',
    },
    module_labels: {
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Solicitudes de derechos',
        data_mapping: 'Registro de actividades de tratamiento',
        governance: 'Gobernanza',
        consent: 'Gobernanza del consentimiento',
        consent_properties: 'Propiedades de consentimiento',
        assessments: 'EIPD y evaluaciones',
        classification: 'Clasificación de datos',
        monitoring: 'Programas de supervisión',
        program: 'Programa de supervisión',
        incidents: 'Respuesta ante incidentes',
        connectors: 'Conectores',
        vendor_risk: 'Riesgo del proveedor',
        deletion_orchestration: 'Orquestación de eliminación',
        discovery: 'Descubrimiento de datos',
        incident_ops: 'Operaciones de incidentes',
        program_governance: 'Gobernanza de programas',
        access_accountability: 'Responsabilidad de acceso',
        localization: 'Localización',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Espacio de trabajo de socios',
        privacy_scanner: 'Escáner de privacidad',
        onboarding: 'Incorporación',
        compliance_forms: 'Formularios de Cumplimiento',
        audit_trail: 'Pista de Auditoría',
        data_subjects: 'Interesados',
        organizations: 'Organizaciones',
        task_management: 'Gestión de tareas',
        deletion_orch: 'Orquestación de eliminación',
        forms: 'Formularios de cumplimiento',
        partner_marketplace: 'Mercado de socios',
        dashboard: 'Panel de control',
        platform_settings: 'Configuración de la plataforma',
        help_center: 'Centro de ayuda',
    },
    module_descriptions: {
        rights_requests: 'Gestione solicitudes de derechos de los interesados, incluyendo acceso, supresión y portabilidad. Realice seguimiento del ciclo de vida de las solicitudes, asigne responsables y cumpla los plazos normativos.',
        data_mapping: 'Mapee los flujos de datos en toda su organización. Realice seguimiento de actividades de tratamiento, categorías de datos, finalidades, políticas de retención e integraciones de sistemas.',
        governance: 'Supervisión y la evidencia que la respalda. Gestione el registro de proveedores y su ciclo de vida, apruebe las decisiones que este módulo controla, vigile las desviaciones de cumplimiento y mantenga los registros de confianza e identidad, de artefactos regulatorios, de pagos y eKYC, y de evidencia de la plataforma.',
        consent: 'Gestione la recopilación, almacenamiento y retirada del consentimiento. Realice seguimiento de registros de consentimiento, finalidades e integración con plataformas de gestión del consentimiento.',
        consent_properties: 'Configure propiedades de consentimiento, grupos y plantillas maestras. Defina configuraciones de consentimiento reutilizables para un despliegue consistente.',
        assessments: 'Realice Evaluaciones de Impacto en la Protección de Datos (EIPD) y evaluaciones de riesgo de privacidad. Evalúe las actividades de tratamiento en busca de riesgos de cumplimiento.',
        classification: 'Clasifique activos de datos por nivel de sensibilidad. Aplique etiquetas taxonómicas, realice seguimiento de registros del ciclo de vida e identifique brechas de clasificación.',
        monitoring: 'Configure programas de supervisión para rastrear métricas de cumplimiento, indicadores clave de riesgo y eficacia de los controles a lo largo del tiempo.',
        incidents: 'Reporte y gestione incidentes de privacidad y brechas de datos. Realice seguimiento de los flujos de investigación, notificación y remediación.',
        connectors: 'Configure conectores de fuentes de datos e integraciones. Conecte sistemas externos para la detección automatizada de datos y la supervisión.',
        vendor_risk: 'Evalúe y supervise riesgos de proveedores externos. Puntúe proveedores, realice seguimiento de la mitigación de riesgos y gestione configuraciones de riesgo de proveedores.',
        deletion_orchestration: 'Orqueste la eliminación de datos en todos los sistemas. Realice seguimiento de solicitudes de eliminación, verifique cadenas de pruebas y asegure la eliminación completa de datos.',
        discovery: 'Descubra datos personales en sistemas conectados. Escanee fuentes de datos para construir un inventario de datos completo.',
        incident_ops: 'Gestión operativa de incidentes con manuales de procedimientos, flujos de escalamiento y seguimiento de SLA para la resolución de incidentes.',
        program_governance: 'Realice seguimiento de la madurez general del programa de privacidad, puntuaciones de cumplimiento, brechas de evidencia e informes ejecutivos en toda su organización.',
        access_accountability: 'Investigue y audite patrones de acceso de usuarios. Realice seguimiento de configuraciones de acceso, supervise actividad sospechosa y aplique la rendición de cuentas.',
        localization: 'Instale y gestione paquetes de localización de mercado. Añada marcos regulatorios, paquetes de idioma y contenido de cumplimiento específico por región.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Colabore con socios externos. Comparta datos de cumplimiento, realice seguimiento de obligaciones de socios y gestione relaciones con socios.',
        privacy_scanner: 'Escanee sitios web en busca de problemas de cumplimiento de privacidad. Supervise el uso de cookies, scripts de seguimiento y la presencia de políticas de privacidad en tiempo real.',
        onboarding: 'Guíe a nuevas organizaciones a través de la configuración de la plataforma. Asistente de incorporación paso a paso para la configuración inicial e importación de datos.',
        compliance_forms: 'Cree y gestione formularios de cumplimiento a partir de un catálogo de plantillas. Complete formularios regulatorios, realice seguimiento de envíos y genere informes.',
        audit_trail: 'Vea una pista de auditoría completa de todas las acciones del sistema. Realice seguimiento de quién hizo qué, cuándo y dónde en todos los módulos.',
        organizations: 'Gestione organizaciones y configuraciones multi-inquilino. Configure perfiles de organización, jerarquías y relaciones entre organizaciones.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Explicar anomalías',
            explaining: 'Explicando…',
            results_heading: 'Explicaciones de anomalías ({severity})',
            tokens_label: '≈ {count} fichas',
            items_label: '{count} elementos',
            remediation_label: 'Pasos de remediación',
            items_detail_label: 'Artículos afectados',
            accept_label: 'Aceptar explicación',
            reject_label: 'Rechazar explicación',
            error_generic: 'No se pudieron explicar las anomalías. Inténtelo de nuevo o verifique que el escaneo se haya completado exitosamente.',
        },
        cookie_insight: {
            insight_label: 'Información sobre cookies y rastreadores AI',
            producing: 'Produciendo conocimiento...',
            results_heading: 'Información sobre cookies y rastreadores ({severity})',
            tokens_label: '≈ {count} fichas',
            items_label: '{count} elementos',
            classification_label: 'Clasificación',
            severity_label: 'Gravedad',
            banner_copy_label: 'Copia de banner sugerida',
            retention_label: 'Consejos de retención',
            citations_label: 'Citas',
            kind_cookie: 'galleta',
            kind_tracker: 'Rastreador',
            class_necessary: 'Necesario',
            class_analytics: 'Analítica',
            class_marketing: 'Comercialización',
            class_social: 'sociales',
            accept_label: 'Aceptar conocimiento',
            reject_label: 'Rechazar conocimiento',
            no_items: 'No se detectaron cookies ni rastreadores en este análisis.',
            error_generic: 'No se pudo generar la información sobre las cookies. Inténtelo de nuevo o verifique que el escaneo se haya completado exitosamente.',
        },
        monitoring: {
            title: 'Privacidad Monitoreo',
            new_scan: 'Nuevo escanear',
            scan_url: 'URL a Escanear',
            scan_frequency: 'Escanear Frecuencia',
            last_scan: 'Último Escanear',
            score: 'Privacidad Puntuación',
            issues: 'Problemas encontrados',
            history: 'Escanear Historial',
            settings: 'Monitoreo Configuración',
        },
        real_time: {
            title: 'Escáner en tiempo real',
            scan_now: 'Escanear Ahora',
            scanning: 'Escaneando...',
            results: 'Escanear Resultados',
            no_results: 'Sin resultados de escaneo aún.',
            cookies: 'galletas',
            trackers: 'Rastreadores',
            consent_banners: 'Banners de consentimiento',
            third_party: 'Scripts de terceros',
            archive: 'Resultados Archivar',
            settings: 'Configuración del escáner',
        },
        dashboard: {
            page_title: 'Panel de control de privacidad',
            privacy_monitoring: 'Monitoreo de privacidad',
            plan_label: 'plan {package}',
            expires_label: 'caduca {date}',
            settings: 'Configuración',
            refresh: 'Actualizar',
            refreshing_dashboard: 'Panel de control actualizado',
            refresh_aria: 'Actualizar el panel de monitoreo',
            not_configured_title: 'Monitoreo de privacidad no configurado',
            not_configured_desc: 'Conecte su servicio de monitoreo de privacidad AesirX para comenzar a rastrear el cumplimiento del sitio web.',
            configure_monitoring: 'Configurar monitoreo',
            license_expired_title: 'Licencia caducada',
            license_expired_desc: 'Su licencia de AesirX Monitoreo de privacidad ha caducado. Por favor renuévelo para evitar la interrupción del servicio.',
            compliance_score: 'Puntuación de cumplimiento',
            cookies_detected: 'Cookies detectadas',
            trackers_label: 'Rastreadores',
            open_issues: 'Problemas abiertos',
            score_good: 'bueno',
            score_needs_improvement: 'Necesita mejorar',
            score_critical: 'Crítico',
            action_required: 'Acción requerida',
            cookies_sr: 'cookies detectadas',
            trackers_sr: 'rastreadores encontrados',
            open_issues_sr: 'cuestiones abiertas',
            action_required_sr: 'acción requerida',
            metric_label: 'Métrica',
            value_label: 'Valor',
            status_label: 'Estado',
            aria_metrics: 'Métricas de monitoreo de privacidad',
            plan_limit_warning: 'Escaneando URL: {current}/{max} — has alcanzado el límite de tu plan.',
            manage_domains: 'Administre dominios o actualice su plan.',
            monitored_domains: 'Dominios monitoreados',
            domain_added: 'Dominio {domain} agregado',
            domain_removed: 'Dominio {domain} eliminado',
            failed_add_domain: 'No se pudo agregar el dominio',
            failed_remove_domain: 'No se pudo eliminar el dominio',
            failed_load: 'No se pudo cargar el panel de monitoreo',
            new_domain_aria: 'Nuevo dominio URL',
            add_domain: 'Agregar dominio',
            adding_domain: 'Añadiendo…',
            no_domains: 'Aún no se han configurado dominios monitoreados.',
            aria_domains_table: 'Dominios monitoreados',
            col_domain: 'Dominio',
            col_compliance_score: 'Puntuación de cumplimiento',
            col_status: 'Estado',
            col_cookies: 'galletas',
            col_trackers: 'Rastreadores',
            col_issues: 'Problemas',
            col_last_scan: 'Último escaneo',
            col_action: 'acción',
            compliance_status_sr: 'estado de cumplimiento',
            showing_chart: 'Mostrando vista de gráfico',
            showing_table: 'Mostrando vista de tabla de datos',
            loading_sr: 'Cargando panel de monitoreo',
            col_framework_tags: 'Etiquetas del marco',
            col_last_monitored: 'último monitoreado',
            framework_tags_sr: 'marcos regulatorios aplicables',
            last_monitored_sr: 'marca de tiempo de monitoreo continuo',
            audit_trail_heading: 'Pista de auditoría',
            audit_trail_info: 'Cada análisis de monitoreo tiene una marca de tiempo y se registra. Los cambios de cumplimiento a nivel de dominio producen un registro de auditoría inmutable que se puede exportar para revisión regulatoria.',
            cybersecurity_law_info_heading: 'Detección de leyes de ciberseguridad',
            cybersecurity_law_info: 'Los dominios monitoreados se verifican automáticamente según los requisitos de la ley de ciberseguridad (por ejemplo, la Ley de ciberseguridad de Vietnam, la Directiva NIS2). Las etiquetas del marco indican qué regulaciones se aplican. Los problemas se clasifican por marco regulatorio para una remediación específica.',
        },
        scan: {
            page_title: 'Escaneo en tiempo real',
            title: 'Escáner de privacidad en tiempo real',
            settings: 'Configuración',
            results_archive: 'Archivo de resultados',
            launch_scan: 'Iniciar escaneo',
            target_url: 'Objetivo URL',
            protocol_aria: 'Protocolo',
            url_required: 'URL es obligatorio',
            url_whitespace: 'Elimine los espacios en blanco del URL',
            url_invalid: 'Introduzca un dominio válido (por ejemplo, ejemplo.com)',
            add_subpages: 'Agregar subpáginas',
            add_subpages_aria: 'Agregar subpáginas, {count} agregado',
            start_scan: 'Iniciar escaneo',
            launching: 'Lanzando…',
            scan_launched: 'El escaneo se inició exitosamente',
            scan_failed: 'No se pudo iniciar el escaneo',
            scan_progress: 'Progreso del escaneo',
            scanning_in_progress: 'Escaneo en progreso…',
            scan_id: 'Escanear ID',
            status_label: 'Estado',
            status_unknown: 'unknown',
            progress_label: 'Progreso',
            pages_label: 'paginas',
            refresh_status: 'Actualizar estado',
            new_scan: 'Nuevo escaneo',
            scan_completed: 'Escaneo completado, resultados disponibles',
            failed_check_status: 'No se pudo verificar el estado del escaneo',
            scan_results: 'Resultados del escaneo',
            main_page: 'Página principal',
            page_n: 'Página {n}',
            result_pages_nav: 'Páginas de resultados',
            page_url: 'Página URL',
            risk_score: 'Puntuación de riesgo',
            risk_score_sr: 'puntuación de riesgo',
            cookies_label: 'galletas',
            cookies_sr: 'cookies detectadas',
            scripts_label: 'Guiones',
            scripts_sr: 'guiones detectados',
            issues_label: 'Problemas',
            issues_sr: 'problemas encontrados',
            issues_found: 'Problemas encontrados',
            aria_issues_table: 'Problemas encontrados durante el escaneo',
            col_severity: 'Gravedad',
            col_category: 'categoría',
            col_title: 'Título',
            col_remediation: 'Remediación',
            severity_sr: 'severity',
            scan_result_metrics: 'Métricas de resultados de escaneo',
            view_full_detail: 'Ver detalles completos',
            col_consent_category: 'Categoría de consentimiento',
            consent_category_sr: 'categorización del consentimiento',
            compliance_evidence_heading: 'Evidencia de cumplimiento',
            compliance_evidence_info: 'Los resultados del análisis se vinculan automáticamente a los casos de cumplimiento. Cada problema se puede rastrear hasta una categoría de consentimiento (esencial, funcional, analítico, marketing) y produce evidencia lista para auditoría para revisión regulatoria.',
            rule_config_heading: 'Configuración de reglas',
            rule_config_info: 'Las reglas de escaneo se pueden configurar según el marco regulatorio. Las comprobaciones de cookies, la detección de scripts y el análisis de proveedores se pueden habilitar o deshabilitar a través de Configuración. Se pueden definir reglas personalizadas para que coincidan con los requisitos de cumplimiento de su organización.',
            case_linkage_heading: 'Vinculación de casos de cumplimiento',
            case_linkage_info: 'Los problemas encontrados durante los análisis en tiempo real se pueden vincular a casos de cumplimiento para su seguimiento y solución. Navegue a la página Detalles del resultado del análisis para ver las asociaciones de casos.',
            subpages_modal_title: 'Agregar subpáginas',
            subpages_modal_desc: 'Agregue hasta 5 URL de subpágina para escanear junto con el dominio principal.',
            subpage_n: 'Subpágina {n}',
        },
        result_detail: {
            page_title: 'Detalle del resultado del escaneo',
            title: 'Resultado del escaneo',
            back_to_archive: 'Volver al archivo',
            failed_load: 'No se pudo cargar el resultado del escaneo',
            loading_sr: 'Cargando resultado del escaneo…',
            scan_id: 'Escanear ID',
            target_url: 'Objetivo URL',
            status_label: 'Estado',
            status_sr: 'status',
            started: 'iniciado',
            completed: 'Completado',
            pages_scanned: 'Páginas escaneadas',
            https_label: 'HTTPS',
            highest_risk_score: 'Puntuación de riesgo más alta',
            most_cookies: 'La mayoría de las galletas',
            most_vendors: 'La mayoría de los proveedores externos',
            most_issues: 'La mayoría de los problemas',
            across_pages: 'a través de páginas',
            highest_values_aria: 'Valores más altos en todas las páginas',
            scanned_pages_nav: 'Páginas escaneadas',
            main_page: 'Página principal',
            page_n: 'Página {n}',
            page_url: 'Página URL',
            risk_score: 'Puntuación de riesgo',
            overall_risk_sr: 'puntuación de riesgo general',
            cookies_label: 'galletas',
            cookies_sr: 'cookies detectadas',
            vendors_label: 'Proveedores externos',
            vendors_sr: 'proveedores externos detectados',
            issues_label: 'Problemas',
            issues_sr: 'problemas encontrados',
            risk_breakdown_aria: 'Desglose de la puntuación de riesgo',
            issues_found: 'Problemas encontrados',
            aria_issues_table: 'Problemas encontrados durante el escaneo',
            col_severity: 'Gravedad',
            col_category: 'categoría',
            col_title: 'Título',
            col_remediation: 'Remediación',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies detectadas',
            col_name: 'Nombre',
            col_domain: 'Dominio',
            col_path: 'Camino',
            col_secure: 'Seguro',
            col_httponly: 'Sólo Http',
            col_samesite: 'Mismo sitio',
            vendors_heading: 'Proveedores externos ({count})',
            aria_vendors_table: 'Proveedores externos detectados',
            col_hostname: 'Nombre de host',
            scripts_heading: 'Scripts/Beacons ({count})',
            aria_scripts_table: 'Scripts y balizas detectadas',
            col_url: 'URL',
            col_type: 'Tipo',
            links_heading: 'Enlaces',
            first_party: 'Primera parte',
            third_party: 'Tercero',
            col_risk_category: 'Categoría de riesgo',
            col_regulatory_framework: 'Marco regulatorio',
            col_actionable_detail: 'Detalle procesable',
            risk_category_sr: 'categoría de riesgo regulatorio',
            risk_category_mapping_heading: 'Mapeo de categorías de riesgo',
            risk_category_mapping_info: 'Cada hallazgo del escaneo se asigna a una categoría de riesgo regulatorio (recopilación de datos, transferencia transfronteriza, gestión del consentimiento, retención de datos, controles de seguridad). Este mapeo permite una remediación específica priorizada por el impacto regulatorio.',
            compliance_case_heading: 'Asociación de casos de cumplimiento',
            compliance_case_info: 'Los resultados del escaneo están vinculados a casos de cumplimiento para un seguimiento de un extremo a otro. Los problemas se pueden escalar directamente desde los resultados del análisis al flujo de trabajo del caso de cumplimiento.',
        },
        results_archive: {
            page_title: 'Archivo de resultados del escaneo',
            title: 'Archivo de resultados del escaneo',
            back_to_scanner: 'Volver al escáner',
            failed_load: 'No se pudieron cargar los resultados del escaneo',
            results_loaded: 'Resultados del escaneo cargados, {count} registros',
            total_label: 'Total: {count}',
            aria_table: 'Archivo de resultados del escaneo',
            col_scan_date: 'Fecha de escaneo',
            col_target_url: 'Objetivo URL',
            col_duration: 'Duración',
            col_issues_found: 'Problemas encontrados',
            col_risk_score: 'Puntuación de riesgo',
            col_status: 'Estado',
            col_actions: 'Acciones',
            scan_status_sr: 'estado de escaneo',
            view_result: 'Ver',
            no_results: 'No se encontraron resultados de escaneo.',
            loading_sr: 'Cargando resultados del análisis',
            pagination_aria: 'Paginación de resultados de escaneo',
            col_risk_category: 'Categoría de riesgo',
            risk_category_sr: 'categoría de riesgo regulatorio',
            archive_retention_heading: 'Retención de archivos',
            archive_retention_info: 'Los resultados del análisis se conservan durante el tiempo especificado en la política de retención de datos de su organización. Los resultados archivados incluyen el contexto de escaneo completo (etiquetas de marco, categorías de riesgo, orientación de remediación) y se pueden exportar como CSV para obtener evidencia regulatoria.',
            export_context_info: 'Los archivos CSV exportados incluyen el contexto regulatorio: categoría de riesgo, marcos aplicables y detalles de remediación procesables para cada hallazgo.',
        },
        scanner_settings: {
            page_title: 'Configuración del escáner',
            title: 'Configuración del escáner',
            back_to_scanner: 'Volver al escáner',
            failed_load: 'No se pudo cargar la configuración del escáner',
            endpoint_url: 'Punto final URL',
            save_success: 'La configuración del escáner se guardó correctamente.',
            save_failed: 'No se pudo guardar la configuración del escáner',
            test_connection: 'Conexión de prueba',
            testing: 'Probando...',
            saving: 'Guardando...',
            loading_sr: 'Cargando la configuración del escáner',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Configuración de monitoreo de privacidad',
            title: 'Configuración de monitoreo',
            back_to_dashboard: 'Volver al panel',
            failed_load: 'No se pudo cargar la configuración de monitoreo',
            credentials_heading: 'AesirX Credenciales',
            connected_as: 'Conectado como {username}',
            credentials_desc: 'Ingrese las credenciales de su cuenta AesirX para conectarse al Monitoreo de privacidad. Encuentre su ID de miembro en la página de perfil de su cuenta AesirX.',
            client_id: 'ID de cliente',
            client_secret: 'Secreto del cliente',
            username: 'AesirX Nombre de usuario (correo electrónico)',
            password: 'AesirX Contraseña',
            placeholder_keep: '(déjelo en blanco para seguir existiendo)',
            credential_form_aria: 'AesirX formulario de credencial',
            test_connection: 'Conexión de prueba',
            testing: 'Probando…',
            test_aria: 'Probar la conexión AesirX',
            testing_aria: 'Conexión de prueba',
            save_configuration: 'Guardar configuración',
            saving: 'Guardando…',
            save_aria: 'Guardar credenciales',
            saving_aria: 'Guardar credenciales',
            credentials_saved: 'AesirX credenciales guardadas exitosamente.',
            credentials_saving: 'AesirX credenciales guardadas. Recargando configuración…',
            credentials_save_failed: 'No se pudieron guardar las credenciales',
            client_id_required: 'Se requiere identificación de cliente y nombre de usuario.',
            all_fields_required: 'Todos los campos de credenciales son obligatorios.',
            email_heading: 'Notificaciones por correo electrónico',
            receive_emails: 'Recibir correos electrónicos con informes de escaneo',
            email_toggle_aria: 'Alternar correos electrónicos de informes de análisis',
            only_on_change: 'Solo envíe un correo electrónico cuando cambie el estado del riesgo',
            only_on_change_aria: 'Alternar correo electrónico solo en caso de cambio de estado de riesgo',
            emails_enabled: 'Escanear correos electrónicos de informes habilitados',
            emails_disabled: 'Correos electrónicos de informes de escaneo deshabilitados',
            only_change_on: 'Solo enviar correos electrónicos sobre cambios de riesgo',
            only_change_off: 'Envío de correo electrónico en cada escaneo',
            email_failed: 'No se pudo actualizar la configuración de correo electrónico',
            email_settings_aria: 'Configuración de notificaciones por correo electrónico',
            loading_sr: 'Cargando configuración de monitoreo',
        },
        entity_types: {
            user: 'Usuario',
            case: 'Caso',
            incident: 'Incidente',
            assessment: 'Evaluación',
            vendor: 'vendedor',
            consent: 'Consentimiento',
            policy: 'Política',
            data_map: 'Mapa de datos',
        },
        actions: {
            create: 'crear',
            update: 'Actualizar',
            delete: 'Eliminar',
            view: 'Ver',
            export: 'Exportar',
            status_change: 'Cambio de estado',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Panel de formularios',
            total_templates: 'Total Plantillas',
            installed: 'Instalado',
            available: 'Disponible',
            by_market: 'Por Mercado',
            back_dashboard: 'Volver a Panel de control',
            browse_templates: 'Explorar plantillas',
            catalog_coverage: 'Cobertura del catálogo',
            quick_start: 'Inicio rápido',
            recent_instances: 'Instancias recientes',
            total_instances: 'Total de instancias',
            fetch_failed: 'Error al fetch dashboard data. Por favor, inténtelo de nuevo.',
            no_instances: 'No se encontraron instances',
            form_metrics: 'Resumen de métricas de formularios',
            templates_total: 'plantillas en total',
            instances_total: 'instancias en total',
            installed_of_available: '{installed} de {available} instalado',
            templates_in_category: 'plantillas en {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Plantillas de formularios',
            new: 'Nuevo plantilla',
            edit: 'Editar Plantilla',
            name: 'Plantilla Nombre',
            form_type: 'Tipo de formulario',
            market: 'Mercado',
            language: 'Idioma',
            schema: 'Esquema del formulario',
            preview: 'Vista previa',
            duplicate: 'Duplicar Plantilla',
            grid_view: 'Cuadrícula Ver',
            list_view: 'Lista Ver',
            fetch_failed: 'Error al fetch templates. Por favor, inténtelo de nuevo.',
            filter_templates: 'Filtrar plantillas',
            version_history: 'Historial de versiones',
            no_version_history: 'Sin historial de versiones — esta es la versión inicial.',
            form_id: 'ID del formulario',
            source_instrument: 'Instrumento de origen',
            issuer: 'Emisor',
            all_frameworks: 'Todos los marcos',
            view_mode: 'Modo de visualización',
            classification: 'Clasificación',
        },
        catalog: {
            title: 'Catálogo de formularios',
            install: 'Instalar',
            uninstall: 'Desinstalar',
            open_form: 'Open form',
            install_all: 'Instalar Todos',
            installed_tab: 'Instalado',
            catalog_tab: 'Catálogo',
            search_placeholder: 'Buscar plantillas...',
            no_templates: 'No se encontraron templates',
            template_count: '{count} plantillas',
            all_markets: 'Todos los mercados',
            sections: 'Secciones',
            all_categories: 'Todas las categorías',
            category: 'Categoría',
            category_install_failed: 'Error al install category templates. Por favor, inténtelo de nuevo.',
            category_install_success: 'Plantillas de categoría instaladas.',
            fetch_failed: 'Error al fetch templates. Por favor, inténtelo de nuevo.',
            fetch_installed_failed: 'Error al fetch installed templates. Por favor, inténtelo de nuevo.',
            fields: 'Campos',
            install_failed: 'Error al install template. Por favor, inténtelo de nuevo.',
            install_success: 'Plantilla installed con éxito.',
            installing: 'Instalando...',
            removing: 'Eliminando...',
            uninstall_confirm: '¿Está seguro de que desea desinstalar esta plantilla?',
            uninstall_failed: 'Error al uninstall template. Por favor, inténtelo de nuevo.',
            uninstall_success: 'Plantilla uninstalled con éxito.',
            installation_summary: 'Resumen de instalación',
            filter_catalog: 'Filtrar catálogo',
            all_frameworks: 'Todos los marcos',
            templates_installed_count: '{count} plantilla(s) instalada(s) correctamente',
            all_already_installed: 'Todas las plantillas de esta categoría ya están instaladas',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'ID del formulario',
            issuer: 'Emisor',
            catalog_tabs: 'Pestañas del catálogo de formularios',
        },
        filler: {
            back_templates: 'Volver a Plantillas',
            no_sections: 'No se encontraron sections',
            draft_created: 'Borrador creado.',
            draft_saved: 'Borrador guardado.',
            fetch_template_failed: 'Error al fetch template. Por favor, inténtelo de nuevo.',
            save_draft: 'Guardar Borrador',
            save_draft_failed: 'Error al save draft. Por favor, inténtelo de nuevo.',
            saving: 'Guardando...',
            select_option: 'Seleccione option',
            submit: 'Enviar',
            submit_failed: 'Error al submit. Por favor, inténtelo de nuevo.',
            submitting: 'Enviando...',
            title: 'Asistente de formulario',
            form_id_label: 'ID del formulario',
            schema_label: 'Esquema',
            form_number_label: 'Form',
            framework_label: 'Marco',
            issuer_label: 'Emisor',
            source_label: 'Fuente',
            market_label: 'Mercado',
            section_of: 'Sección {current} de {total}',
            form_navigation: 'Navegación del formulario',
            form_progress: 'Progreso del formulario: Sección {current} de {total}',
            removed_item: 'Elemento {index} eliminado de {field}',
            added_item: 'Nuevo elemento añadido a {field}',
            go_to_previous: 'Ir a la sección anterior, sección {current} de {total}',
            go_to_next: 'Ir a la sección siguiente, sección {current} de {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Estado de presentación',
            advance: 'Actualizar estado de presentación...',
            not_started: 'No iniciado',
            drafting: 'Borrador',
            internal_review: 'Revisión interna',
            approved_for_submission: 'Aprobado para presentación',
            submitted_to_authority: 'Presentado a la autoridad',
            supplement_requested: 'Complemento solicitado',
            supplement_in_progress: 'Complemento en progreso',
            resubmitted: 'Reenviado',
            accepted: 'Aceptado',
            rejected: 'Rechazado',
            closed: 'Cerrado',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Aprobado Por',
            form_answers: 'Respuestas del formulario',
            column_header: 'Form',
            no_answers: 'No se encontraron answers',
            view_outputs: 'Ver Outputs',
            approve: 'Aprobar',
            approve_failed: 'Error al approve. Por favor, inténtelo de nuevo.',
            approving: 'Aprobando...',
            detail_title: 'Detalle de la instancia',
            fetch_failed: 'Error al fetch instances. Por favor, inténtelo de nuevo.',
            generate_failed: 'Error al generate output. Por favor, inténtelo de nuevo.',
            generate_output: 'Generar salida',
            generating: 'Generando...',
            submitted_at: 'Enviado el',
            instance_metadata: 'Metadatos de la instancia del formulario',
            filing_status_steps: 'Pasos del estado de archivo',
            current_status: 'estado actual',
            filing_status_update_failed: 'Error al actualizar el estado de la presentación',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Volver a la instancia',
            no_outputs_msg: 'No outputs tener sido generated aún.',
            no_outputs_title: 'Sin resultados',
            output_name: 'Nombre de salida',
            download: 'Descargar',
            download_failed: 'Error al download. Por favor, inténtelo de nuevo.',
            fetch_failed: 'Error al fetch outputs. Por favor, inténtelo de nuevo.',
            format: 'Formato',
            generated_at: 'Generado el',
            generated_by: 'Generado por',
            size: 'Tamaño',
            title: 'Resultados del formulario',
            print: 'Imprimir',
            print_outputs: 'Imprimir salidas del formulario',
            printing: 'Imprimiendo salidas del formulario',
            pdf_preview: 'Vista previa PDF',
            downloading: 'Descargando {name}',
            preview_in_new_window: 'Vista previa de {name} ({format}) en nueva ventana',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Alcance y aplicabilidad',
            risk: 'Evaluación de riesgos y mapeo de controles',
            policy: 'Políticas de seguridad organizativa',
            localization: 'Localización de datos y presencia local',
            vendor: 'Terceros y cadena de suministro',
            authority: 'Gestión de solicitudes de autoridades',
            incident: 'Respuesta a incidentes y evidencias',
            training: 'Formación y preparación',
        },
        market_finalization_domains: {
            data_law: 'Ley de Datos — Gobernanza y Controles Operativos',
            pdpl_2025: 'PDPL 2025 — Gobernanza de Privacidad y Derechos de los Interesados',
            decree_356: 'Decreto 356/2025 — Controles de Proveedores y Transfronterizos',
            telecom: 'Ley de Telecomunicaciones — Confidencialidad y Solicitudes de Autoridades',
        },
        nav: {
            filing_case_links: 'Enlaces de expediente',
            section_routing: 'Enrutamiento de secciones',
            submission_packages: 'Paquetes de presentación',
        },
        filing_case_links: {
            title: 'Enlaces de expediente',
            link_form: 'Vincular formulario al expediente',
            filing_case_type: 'Tipo de expediente',
            form_instance: 'Instancia de formulario',
            select_form_instance: 'Seleccionar una instancia de formulario...',
            select_filing_case: 'Seleccionar un expediente...',
            no_links_found: 'No se encontraron enlaces de expediente.',
            linked: 'Vinculado',
            filing_case_id: 'ID del caso de archivo',
            link_created: 'Enlace de caso de archivo creado',
            link_deleted: 'Enlace de expediente eliminado',
            aria_delete_link: 'Eliminar enlace {name}',
            case_type_dpia_dossier: 'Expediente EIPD',
            case_type_cross_border_filing: 'Archivo transfronterizo',
            case_type_breach_notification: 'Notificación de brecha',
            no_instances_guidance: 'Aún no hay instancias de formulario. Cree una desde Plantillas de formulario.',
            no_filings_guidance: 'Aún no hay expedientes. Cree uno desde Evaluaciones.',
        },
        section_routing: {
            title: 'Enrutamiento de secciones',
            assign_section: 'Asignar sección',
            department: 'Departamento',
            select_department: 'Seleccionar un departamento...',
            assignee: 'Asignado a',
            select_assignee: 'Seleccionar un asignado...',
            section: 'Sección',
            select_section: 'Seleccionar una sección...',
            select_form_instance: 'Seleccionar una instancia de formulario...',
            no_assignments_found: 'No se encontraron asignaciones de sección.',
            select_instance_prompt: 'Seleccione una instancia de formulario para ver las asignaciones de sección.',
            complete_section: 'Completar sección',
            status_pending: 'Pendiente',
            status_in_progress: 'En progreso',
            status_completed: 'Completado',
            status_reviewed: 'Revisado',
            form_instance_id: 'ID de instancia del formulario',
            assignment_created: 'Asignación de sección creada',
            assignment_deleted: 'Asignación de sección eliminada',
            status_updated: 'Estado de la sección actualizado',
            aria_delete_assignment: 'Eliminar asignación {name}',
            deadline: 'Fecha límite',
            deadline_help: 'Opcional. Las asignaciones posteriores a esta fecha se muestran como atrasadas.',
            overdue: 'Atrasada',
            days_remaining: 'días restantes',
            approval_requested: 'Enviado para revisión',
            form_approved: 'Formulario aprobado',
            form_rejected: 'Formulario rechazado',
            no_departments_guidance: 'No hay departamentos configurados. Configúrelos en Configuración de la organización.',
            no_sections_guidance: 'No hay secciones definidas en la plantilla. Asegúrese de que la plantilla tenga secciones.',
            select_instance_first: 'Seleccione primero una instancia de formulario para cargar las secciones disponibles.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Paquetes de presentación',
            assemble_package: 'Ensamblar paquete de presentación',
            dossier: 'Expediente',
            select_dossier: 'Seleccionar un expediente...',
            package_label: 'Etiqueta del paquete',
            cover_letter: 'Carta de presentación',
            checklist: 'Lista de verificación',
            evidence_index: 'Índice de evidencia',
            completeness: 'Completitud',
            incomplete_warning: 'No se puede ensamblar: el formulario tiene secciones incompletas.',
            no_packages_found: 'No se encontraron paquetes de presentación.',
            version: 'Versión',
            package_assembled: 'Paquete de presentación ensamblado',
            package_deleted: 'Paquete de presentación eliminado',
            aria_delete_package: 'Eliminar paquete {name}',
            status_draft: 'Borrador',
            status_complete: 'Completo',
            status_submitted: 'Enviado',
            status_accepted: 'Aceptado',
            status_rejected: 'Rechazado',
            package_updated: 'Paquete de presentación actualizado',
            document_generated: 'Documento generado',
            documents: 'Documentos',
            generate: 'Generar',
            preview: 'Vista previa',
            download: 'Descargar',
            download_all: 'Descargar todo',
            no_dossiers_guidance: 'Aún no hay expedientes. Cree uno desde Evaluaciones.',
            package_plan_help: 'Incluir en el plan del paquete (generación automática de documentos disponible):',
            cover_letter_help: 'Carta de presentación oficial dirigida a la autoridad receptora',
            checklist_help: 'Lista de verificación de cumplimiento que verifica todos los requisitos de presentación',
            evidence_index_help: 'Índice de documentos probatorios referenciados en la presentación',
            completeness_skipped: 'Verificación de completitud omitida — no hay instancia de formulario vinculada a este expediente.',
            completeness_unavailable: 'Verificación de completitud no disponible — se procede con el ensamblaje del paquete.',
            duplicate_package_error: 'Ya existe un paquete de presentación para este expediente.',
            doc_cover_letter: 'Carta de presentación',
            doc_checklist: 'Lista de verificación',
            doc_evidence_index: 'Índice de evidencias',
            export_csv: 'Exportar CSV',
            export_success: 'Enlaces de casos de archivo exportados con éxito',
            no_assignments_guidance: 'Aún no hay asignaciones de secciones. Asigne secciones a departamentos y miembros del equipo para comenzar el enrutamiento.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Seleccionar una plantilla',
            hint: 'Elija una plantilla de formulario normativo o comience con un formulario en blanco.',
            market_filter: 'Filtrar por mercado',
            framework_filter: 'Filtrar por marco',
            all_markets: 'Todos los mercados',
            all_frameworks: 'Todos los marcos',
            search_placeholder: 'Buscar plantillas...',
            blank_form: 'Formulario en blanco / genérico',
            blank_form_desc: 'Crear sin plantilla — rellenar los campos manualmente.',
            no_templates: 'No se encontraron plantillas para este filtro.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Seleccionar',
            link_error: 'Error al vincular la plantilla — puede vincular una plantilla más tarde desde la vista de Edición.',
            internal_badge: 'Plantilla interna',
            legacy_badge: 'Obsoleta',
            draft_badge: 'Borrador',
            upcoming_badge: 'Próximo',
            hidden_badge: 'Oculta',
            show_hidden_toggle: 'Mostrar plantillas ocultas / obsoletas',
            show_hidden_toggle_hint: 'Vista de administrador: incluye plantillas marcadas como internal_only, hidden, legacy o draft.',
        },
        cover_letter_modal: {
            title: 'Carta de presentación: vista previa y edición',
            iframe_title: 'Vista previa de la carta de presentación',
            stage_label: 'Etapa',
            stage_initial: 'Envío inicial',
            stage_supplement: 'Respuesta suplementaria',
            stage_resubmission: 'Reenvío',
            subject_label: 'Asunto',
            body_label: 'Párrafo principal',
            notes_label: 'Notas adicionales',
            refresh_preview: 'Actualizar vista previa',
            confirm: 'Confirmar y guardar',
            regenerate: 'Regenerar',
            snapshot_saved: 'Carta de presentación guardada',
        },
        related_panel: {
            title: 'Formularios normativos relacionados',
            open_form: 'Rellenar formulario',
            no_forms: 'No se encontraron formularios relacionados.',
            hint_vn: 'Formularios normativos específicos de VN disponibles — seleccione un formulario para abrir el asistente de cumplimiento.',
            internal_badge: 'Interno',
            official_badge: 'Oficial',
            loading: 'Cargando formularios relacionados...',
            error: 'No se pudieron cargar los formularios relacionados.',
            form_id: 'ID del formulario',
            form_name: 'Nombre del formulario',
            framework: 'Marco regulatorio',
        },
        view_form: 'Ver formulario',
        edit_form: 'Editar formulario',
        save_draft: 'Guardar borrador',
        module_settings: 'Configuración del módulo',
        validation_errors: 'Por favor, corrige los errores de validación',
        custom_name_help: 'Opcional — por defecto, el nombre de la plantilla si se deja en blanco',
        select_placeholder: 'Seleccionar...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Anterior',
        next_section: 'Siguiente',
        loading: 'Cargando...',
        array_add: 'Añadir',
        array_remove: 'Eliminar',
        array_item_label: '{label} elemento {index}',
        array_add_item: 'Añadir elemento a {label}',
        array_remove_item: 'Eliminar elemento {index} de {label}',
        legacy_banner: 'Este registro se creó antes de los formularios unificados. Algunos datos pueden diferir entre la entidad y la instancia del formulario.',
        field_required: '{label} es obligatorio',
        section_announcement: 'Sección {current} de {total}: {title}',
        sections_nav_label: 'Secciones de formulario',
        no_sections: 'No hay secciones de plantilla disponibles.',
        section_label_tpl: 'Sección {index}: {title}',
        field_shown_announcement: '{field} ahora se muestra.',
        field_required_announcement: '{field} ahora es obligatorio.',
        submit_blocked_hint_one: '1 campo obligatorio todavía necesita una respuesta.',
        submit_blocked_hint_many: '{count} campos obligatorios todavía necesitan una respuesta.',
        module_settings_auto_applied: 'La configuración específica del módulo se aplica automáticamente.',
        operation_failed: 'Operación fallida',
        failed_to_load_form: 'No se pudo cargar el formulario',
        failed_to_save_form: 'No se pudo guardar el formulario',
        failed_to_submit_form: 'No se pudo enviar el formulario',
        failed_to_load_template: 'No se pudo cargar la plantilla',
        failed_to_save_draft: 'No se pudo guardar el borrador',
        failed_to_create: 'No se pudo crear',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Catálogo de paquetes',
        install: 'Instalar paquete',
        uninstall: 'Desinstalar paquete',
        install_batch: 'Instalar clúster',
        installed_tab: 'Instalado',
        catalog_tab: 'Catálogo',
        pack_details: 'Detalles del paquete',
        frameworks: 'Marcos',
        requirements: 'Requisitos',
        artifacts: 'Artefactos',
        language_pack: 'Paquete de idioma',
        dependencies: 'Dependencias',
        no_packs: 'No se encontraron packs',
    },
    platform_pages: {
        users: {
            title: 'Usuarios',
            new: 'Nuevo usuario',
            invite: 'Invitar usuario',
            edit: 'Editar Usuario',
            name: 'Nombre',
            email: 'Correo electrónico',
            role: 'Rol',
            status: 'Estado',
            identity_type: 'Identidad Tipo',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Último inicio de sesión',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Desactivar',
            reactivate: 'Reactivar',
            unlock: 'Desbloquear',
            reset_password: 'Restablecer Contraseña',
            remove: 'Eliminar',
            remove_confirm: '¿Eliminar "{name}"? Esta acción es permanente y el usuario perderá todo acceso. El registro se conserva con fines de auditoría.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Roles',
            new: 'Nuevo rol',
            edit: 'Editar Rol',
            name: 'Rol Nombre',
            description: 'Descripción',
            permissions: 'Permisos',
            users_count: 'Usuarios',
            detail: 'Detalle del rol',
            all_tiers: 'Todos los niveles',
            tier: 'Nivel',
            type_system: 'Sistema',
            type_custom: 'Personalizado',
            role_name_placeholder: 'p. ej. responsable regional de cumplimiento',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Mi Perfil',
            name: 'Nombre',
            display_name: 'Nombre para mostrar',
            email: 'Correo electrónico',
            phone: 'Teléfono',
            department: 'Departamento',
            job_title: 'Cargo',
            language: 'Idioma',
            role: 'Rol',
            persona: 'Persona detectada',
            last_login: 'Último inicio de sesión',
            save: 'Guardar cambios',
            save_success: 'Perfil actualizado correctamente',
            module_tips_section: 'Consejos de módulo',
            tips_toggle: 'Mostrar consejos normativos en los módulos',
            dismissed_tips: 'Consejos descartados',
            reset_dismissed: 'Restablecer todos los consejos descartados',
            notification_prefs: 'Preferencias de notificación',
            notification_prefs_link: 'Gestionar preferencias de notificación →',
            change_password: 'Cambiar contraseña',
            current_password: 'Actual Contraseña',
            new_password: 'Nuevo contraseña',
            confirm_password: 'Confirmar Contraseña',
            accessibility: 'Accesibilidad',
            high_contrast: 'Modo de alto contraste',
            high_contrast_desc: 'Aumentar el contraste para mejor legibilidad. Cumple WCAG 2.1 AAA.',
            passwords_no_match: 'Las contraseñas no coinciden',
            password_changed_logout: 'Contraseña cambiada. Se cerrará su sesión.',
            failed_update: 'Error al actualizar el perfil',
            failed_change_password: 'Error al cambiar la contraseña',
            none: 'Ninguno',
            never: 'Nunca',
            password_requirements: 'Mín. 12 car., mayúscula, minúscula, dígito, carácter especial',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'Configuración MFA',
            mfa_enable: 'Habilitar MFA',
            mfa_disable: 'Deshabilitar MFA',
            mfa_setup: 'Escanee este código QR con su aplicación de autenticación',
            sso_title: 'Configuración SSO',
            sso_provider: 'Proveedor',
            sso_client_id: 'Cliente ID',
            sso_redirect: 'URI de redirección',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API Claves',
            api_keys_new: 'Nuevo api clave',
            api_keys_name: 'Clave Nombre',
            api_keys_last_used: 'Último uso',
            api_keys_expires: 'Caduca',
            api_keys_revoke: 'Revocar',
            service_accounts_title: 'Cuentas de servicio',
            ip_allowlist_title: 'Lista blanca de IP',
            ip_add: 'Agregar ip dirección',
            ip_address: 'IP Dirección',
            ip_description: 'Descripción',
            delegations_title: 'Delegaciones',
            delegations_new: 'Nuevo Delegation',
            delegations_from: 'Desde Usuario',
            delegations_to: 'Hasta Usuario',
            delegations_expires: 'Caduca',
            break_glass_title: 'Break-Glass Acceso',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Solicitud Acceso',
            break_glass_reason: 'Reason para Acceso',
            break_glass_duration: 'Duración',
            session_settings_title: 'Sesión Configuración',
            eviction_policy_section: 'Política de expulsión',
            session_timeout: 'Tiempo de espera de sesión (minutos)',
            session_max_concurrent: 'Sesiones concurrentes máx.',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Sesiones activas',
            active_sessions_device: 'Dispositivo',
            active_sessions_ip: 'IP Dirección',
            active_sessions_started: 'Iniciado',
            active_sessions_revoke: 'Revocar sesión',
            active_sessions_confirm_terminate: '¿Está seguro de que desea finalizar esta sesión?',
            active_sessions_confirm_terminate_current: 'Esta es su sesión actual. Al finalizarlo, cerrará sesión inmediatamente. ¿Continuar?',
            active_sessions_confirm_terminate_all: '¿Está seguro de que desea finalizar todas las demás sesiones? Sólo su sesión actual permanecerá activa.',
            active_sessions_terminated: 'La sesión finalizó exitosamente.',
            active_sessions_all_terminated: 'Todas las demás sesiones han sido canceladas.',
            active_sessions_terminate_all: 'Terminar todas las demás sesiones',
            active_sessions_last_activity: 'Última actividad',
            active_sessions_current: 'Sesión actual',
            active_sessions_revoke_selected: 'Revocar seleccionadas',
            active_sessions_confirm_terminate_selected: '¿Está seguro de que desea terminar las sesiones seleccionadas?',
            active_sessions_selected_terminated: 'Sesiones seleccionadas terminadas correctamente.',
            ip_allowlist_desc: 'Administre las direcciones IP permitidas para el acceso de API.',
            ip_no_entries: 'No hay direcciones IP configuradas.',
            session_settings_desc: 'Configure el tiempo de espera de la sesión y los límites de las sesiones simultáneas.',
            session_settings_save_policy: 'Guardar política de sesión',
            session_settings_saved: 'La configuración de la sesión se actualizó correctamente.',
            session_settings_idle_timeout: 'Tiempo de inactividad (minutos)',
            session_settings_absolute_timeout: 'Tiempo de espera absoluto (minutos)',
            session_settings_help_idle: 'La sesión caducará después de este período de inactividad.',
            session_settings_help_absolute: 'La sesión caducará después de esta duración total independientemente de la actividad.',
            session_settings_help_max: 'Número máximo de sesiones simultáneas por usuario.',
            sso_note: 'Los cambios en la configuración de SSO pueden requerir que los usuarios se vuelvan a autenticar.',
            service_accounts_warning: 'Esta clave API solo se mostrará una vez. Cópialo ahora y guárdalo de forma segura.',
            service_accounts_new: 'Nueva cuenta de servicio',
            service_accounts_name: 'Nombre de cuenta',
            service_accounts_role: 'Rol',
            service_accounts_created: 'Creado',
            service_accounts_desc: 'Administre cuentas de servicio de máquina a máquina para integraciones de API.',
            break_glass_warning: 'El acceso contra rotura de cristales se audita y registra. Úselo sólo para emergencias genuinas.',
            break_glass_min_chars: '{count} / {min} caracteres mínimos',
            break_glass_permission_count: '{count} permiso(s) seleccionado(s)',
            break_glass_submitted: 'Solicitud de rotura de cristales enviada. En espera de aprobación.',
            break_glass_approved: 'Solicitud de rotura de cristales aprobada.',
            break_glass_denied: 'Solicitud de rotura de cristales denegada.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Confirmar activación',
            break_glass_pending: 'Solicitudes pendientes',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Tiempo restante',
            break_glass_expired: 'Caducado',
            break_glass_approved_denied_by: 'Aprobado/Rechazado por',
            delegations_desc: 'Gestionar delegaciones de permisos temporales entre usuarios.',
            delegations_my: 'Mis delegaciones',
            delegations_to_me: 'Delegado a mí',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Todos los permisos',
            delegations_scope_specific: 'Permisos específicos',
            delegations_created: 'La delegación se creó correctamente.',
            delegations_revoked: 'Delegación revocada.',
            delegations_confirm_revoke: '¿Está seguro de que desea revocar esta delegación?',
            delegations_confirm_revoke_title: 'Confirmar revocar',
            delegations_permission_count: '{count} permiso(s)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Estado',
            api_keys_prefix: 'Prefijo clave',
            api_keys_permissions_count: 'Permisos',
            api_keys_ip_count: 'Restricciones de propiedad intelectual',
            api_keys_created: 'Creado',
            api_keys_confirm_revoke_title: 'Confirmar revocar',
            api_keys_confirm_revoke_message: '¿Estás seguro de que deseas revocar la clave API "{name}"? Esta acción no se puede deshacer.',
            api_keys_confirm_rotate_title: 'Confirmar rotación',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'La clave API se creó correctamente.',
            api_keys_revoked_success: 'La clave API "{name}" ha sido revocada.',
            api_keys_rotated_success: 'La clave API "{name}" ha sido rotada. Copie la nueva clave a continuación.',
            api_keys_load_error: 'No se pudieron cargar las claves API.',
            api_keys_create_error: 'No se pudo crear la clave API.',
            api_keys_revoke_error: 'No se pudo revocar la clave API.',
            api_keys_rotate_error: 'No se pudo rotar la clave API.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutos',
            break_glass_duration_30: '30 minutos',
            break_glass_duration_60: '60 minutos (1h)',
            break_glass_duration_120: '120 minutos (2h)',
            break_glass_duration_240: '240 minutos (4h)',
            sod_rules_title: 'Reglas de segregación de funciones',
            sod_violations_report: 'Informe de violaciones',
            sod_create_rule: 'Crear regla',
            sod_col_name: 'Nombre',
            sod_col_description: 'Descripción',
            sod_col_conflict_count: 'Conteo de conflictos',
            sod_col_severity: 'Gravedad',
            sod_col_status: 'Estado',
            sod_col_actions: 'Acciones',
            sod_no_rules: 'No se encontraron reglas de SoD',
            sod_severity_warning: 'Advertencia',
            sod_severity_blocking: 'Bloqueo',
            sod_active: 'Activo',
            sod_inactive: 'Inactivo',
            sod_edit: 'Editar',
            sod_exceptions: 'Excepciones',
            sod_deactivate: 'Desactivar',
            sod_confirm_deactivate_title: 'Confirmar desactivación',
            sod_confirm_deactivate_message: '¿Desactivar la regla "{name}"? Ya no señalará violaciones.',
            sod_deactivate_error: 'No se pudo desactivar la regla',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Revisiones de acceso',
            campaigns: 'Campañas de revisión',
            new_campaign: 'Nuevo Campaign',
            campaign_name: 'Nombre de campaña',
            scope: 'Alcance',
            reviewers: 'Revisores',
            due_date: 'Fecha de vencimiento',
            progress: 'Progreso',
            my_reviews: 'Mis revisiones',
            approve: 'Aprobar',
            revoke: 'Revocar',
            analytics: 'Rol Análisis',
            analytics_title: 'Rol Análisis',
            permission_usage: 'Uso de permisos',
            role_coverage: 'Cobertura de roles',
            campaign_detail: 'Detalles de la campaña',
            campaign_description: 'Descripción',
            campaign_status: 'Estado',
            campaign_deadline: 'Fecha límite',
            campaign_scope_type: 'Tipo de alcance',
            campaign_completion: 'Finalización',
            campaign_total_items: 'Artículos totales',
            campaign_pending_items: 'Pendiente',
            campaign_confirmed_items: 'Confirmado',
            campaign_revoked_items: 'Revocado',
            campaign_escalated_items: 'escalado',
            campaign_launch: 'Campaña de lanzamiento',
            campaign_cancel: 'Cancelar campaña',
            campaign_confirm_launch: '¿Estás seguro de que quieres lanzar esta campaña?',
            campaign_confirm_cancel: '¿Estás seguro de que deseas cancelar esta campaña?',
            campaign_launched: 'Campaña lanzada exitosamente.',
            campaign_cancelled: 'Campaña cancelada.',
            review_items: 'Revisar artículos',
            review_item_user: 'Usuario',
            review_item_role: 'Rol',
            review_item_reviewer: 'Revisor',
            review_item_decision: 'decisión',
            review_item_justify: 'Justificación',
            review_item_confirm: 'Confirmar',
            review_item_revoke: 'Revocar',
            review_item_escalate: 'escalar',
            all_decisions: 'Todas las decisiones',
            bulk_action: 'Acción masiva',
            bulk_confirm: 'Confirmación masiva',
            bulk_revoke: 'Revocación masiva',
            selected_count: '{count} seleccionado',
            justify_required: 'Se requiere justificación',
            scope_all_users: 'Todos los usuarios',
            scope_role_owner: 'Propietario del rol',
            scope_department: 'Departamento',
            back_to_campaigns: 'Volver a Campañas',
            recommendations: 'Recomendaciones',
            refreshing: 'Refrescante...',
            decide: 'decidir',
            decision_by: 'Decisión por',
            loading_items: 'Cargando artículos...',
            loading_review_items: 'Cargando elementos de reseña...',
            campaign_not_found: 'Campaña no encontrada',
            loading_campaign: 'Cargando detalles de la campaña...',
            campaign_completed: 'Campaña completada con éxito.',
            confirm_cancel_title: 'Confirmar Cancelar',
            justify_bulk_confirm: 'Justificación de la confirmación masiva:',
            justify_bulk_revoke: 'Justificación de la revocación masiva:',
            items_selected: '{count} elemento(s) seleccionado(s)',
            justify_bulk_required: 'Se requiere justificación para acciones masivas',
            create_campaign_title: 'Crear campaña de revisión de acceso',
            name_required: 'El nombre es obligatorio',
            scope_type_required: 'Se requiere tipo de alcance',
            reviewer_strategy_required: 'Se requiere estrategia de revisor',
            deadline_required: 'Se requiere plazo',
            reviewer_id_required: 'Se requiere ID de revisor para una estrategia de usuario específica',
            invalid_json: 'JSON no válido',
            scope_filter_label: 'Filtro de alcance (JSON)',
            reviewer_strategy_label: 'Estrategia del revisor',
            reviewer_id_label: 'ID del revisor',
            deadline_label: 'Fecha límite',
            create_campaign_btn: 'Crear campaña',
            scope_type_label: 'Tipo de alcance',
            scope_by_role: 'Por rol',
            scope_by_department: 'Por departamento',
            scope_custom: 'personalizado',
            reviewer_manager: 'Gerente',
            reviewer_specific_user: 'Usuario específico',
            enter_json_filter: 'Ingrese el filtro JSON',
            hint_label: 'Pista:',
            role_usage: 'Uso de roles',
            over_privileged_users: 'Usuarios con demasiados privilegios',
            role_name_col: 'Nombre del rol',
            assigned_users: 'Usuarios asignados',
            active_users_30d: 'Usuarios activos (30d)',
            total_perms: 'Total de permanentes',
            used_perms: 'Permanentes usadas',
            unused_perms: 'Permanentes no utilizadas',
            no_data_available: 'No hay datos disponibles',
            roles_col: 'Roles',
            total_col: 'totales',
            used_col: 'Usado',
            unused_pct: '% no utilizado',
            current_count: 'Conteo actual',
            recommended_count: 'Recuento recomendado',
            rationale: 'Justificación',
            loading_analytics: 'Cargando análisis...',
            loading_role_analytics: 'Cargando datos de análisis de roles...',
            computed_at: 'Calculado en:',
            form_errors: 'El formulario tiene {count} errores. Por favor corríjalos.',
            scope_filter_invalid_json: 'El filtro de alcance contiene JSON no válido',
            scope_hint_all_users: 'No se necesita filtro: se incluirán todos los usuarios.',
            review_type_label: 'Tipo de revisión',
            review_type_required: 'El tipo de revisión es obligatorio',
            reviewer_ids_label: 'IDs de revisores',
            reviewer_ids_hint: 'Introduzca IDs de usuario separados por comas',
            reviewer_ids_required: 'Se requiere al menos un ID de revisor',
            start_date_label: 'Fecha de inicio',
            start_date_required: 'La fecha de inicio es obligatoria',
            end_date_label: 'Fecha de fin',
            end_date_required: 'La fecha de fin es obligatoria',
            review_type_user_access: 'Acceso de usuario',
            review_type_role_permissions: 'Permisos de rol',
            review_type_service_accounts: 'Cuentas de servicio',
            review_type_privileged_access: 'Acceso privilegiado',
            review_type_application_access: 'Acceso a aplicación',
            auto_revoke_label: 'Revocar automáticamente al denegar',
            campaigns_title: 'Acceder a campañas de revisión',
            create_campaign_label: '+ Crear campaña',
            no_campaigns_found: 'No se encontraron campañas',
            loading_campaigns: 'Cargando campañas...',
            no_reviews_assigned: 'No hay reseñas asignadas a ti',
            loading_reviews: 'Cargando reseñas...',
            review_decision_title: 'Decisión de revisión',
            justification_label: 'Justificación',
            announce_loaded_review_items: '{count} elementos de revisión cargados',
            announce_loaded_campaigns: '{count} campañas cargadas',
            announce_decision_submitted: 'Decisión "{decision}" enviada para revisión',
            announce_decision_submitted_for: 'Decisión "{decision}" presentada para {name}',
            announce_bulk_decision: '{decision} masivo aplicado a {count} elementos',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Programación de atestaciones',
            new: 'Nuevo programar',
            edit: 'Editar Programar',
            create_title: 'Crear programación de atestación',
            edit_title: 'Editar programación de atestación',
            schedule_history: 'Historial de programación',
            next_run_date: 'Fecha de la próxima ejecución',
            schedule_name: 'Programar Nombre',
            frequency: 'Frecuencia',
            next_run: 'Siguiente Ejecutar',
            last_run: 'Último Ejecutar',
            history: 'Historial de atestación',
            description: 'Descripción',
            scope: 'Alcance',
            status: 'Estado',
            created_at: 'Creado',
            updated_at: 'Actualizado',
            trigger_now: 'Activar ahora',
            back_to_schedules: 'Volver a Horarios',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'La programación se activó correctamente.',
            deleted: 'Horario eliminado.',
            saved: 'Horario guardado exitosamente.',
            campaigns_generated: 'Campañas generadas',
            run_date: 'Fecha de ejecución',
            run_status: 'Estado de ejecución',
            create_schedule: 'Crear horario',
            view_history: 'Ver historial',
            deactivate: 'Desactivar',
            overdue: 'OVERDUE',
            overdue_sr: 'El calendario está retrasado',
            confirm_trigger_title: 'Confirmar acción',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Confirmar desactivación',
            confirm_deactivate_message: '¿Estás seguro de que quieres desactivar este horario?',
            triggered_success: 'La programación se activó correctamente.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Horario desactivado.',
            name_label: 'Nombre *',
            frequency_label: 'Frecuencia *',
            scope_type_label: 'Tipo de alcance *',
            scope_filter_label: 'Filtro de alcance (JSON)',
            reviewer_strategy_label: 'Estrategia del revisor *',
            reviewer_id_label: 'ID del revisor *',
            frequency_quarterly: 'Trimestral',
            frequency_semi_annual: 'Semestral',
            frequency_annual: 'Anual',
            scope_all_users: 'Todos los usuarios',
            scope_by_role: 'Por rol',
            scope_by_department: 'Por departamento',
            scope_custom: 'personalizado',
            reviewer_manager: 'Gerente',
            reviewer_role_owner: 'Propietario del rol',
            reviewer_specific_user: 'Usuario específico',
            computed_next_run_date: 'Fecha de próxima ejecución calculada',
            current_scheduled_date: 'Fecha programada actual',
            recomputed_frequency_changed: '(recalculado - frecuencia cambiada)',
            estimated_days_hint: 'Estimado en base a {days} días a partir de hoy',
            validation_name_required: 'El nombre es obligatorio',
            validation_frequency_required: 'Se requiere frecuencia',
            validation_scope_type_required: 'Se requiere tipo de alcance',
            validation_reviewer_strategy_required: 'Se requiere estrategia de revisor',
            validation_reviewer_id_required: 'Se requiere ID de revisor para una estrategia de usuario específica',
            validation_invalid_json: 'JSON no válido',
            validation_scope_filter_invalid_json: 'El filtro de alcance contiene JSON no válido',
            form_errors: 'El formulario tiene {count} errores. Por favor corríjalos.',
            create_schedule_btn: 'Crear horario',
            schedule_history_title: 'Historial de horarios',
            campaign_name_col: 'Nombre de la campaña',
            items_col: 'Artículos',
            no_campaigns_yet: 'Aún no se han generado campañas',
            loading_history: 'Cargando historial...',
            loading_schedule: 'Cargando agenda...',
            total_campaigns: 'Campañas totales: {count}',
            results_per_page: 'Resultados por página',
            page_size_option: '{size} / página',
        },
        sod: {
            title: 'Reglas de separación de funciones',
            new: 'Nuevo Rule',
            edit: 'Editar regla',
            create_title: 'Crear regla SoD',
            edit_title: 'Editar regla SoD',
            rule_name: 'Nombre de la regla',
            conflicting_permissions: 'Permisos en conflicto',
            permission_a: 'Permiso Un',
            permission_b: 'Permiso B',
            violations: 'Violaciones SoD',
            violation_user: 'Usuario',
            violation_rule: 'Regla violada',
            violation_date: 'Detectado el',
            exceptions: 'Excepciones SoD',
            exception_reason: 'Motivo de excepción',
            exception_approver: 'Aprobado Por',
            exception_status: 'Estado de excepción',
            has_exception: 'Tiene excepción',
            no_exception: 'Sin excepción',
            grant_exception: 'Excepción de concesión',
            granting: 'Concediendo...',
            grant_new_exception: 'Conceder nueva excepción',
            exceptions_for: 'Excepciones para: {name}',
            back_to_rules: 'Volver a las reglas',
            no_violations: 'No se encontraron violaciones',
            no_exceptions_found: 'No se encontraron excepciones para esta regla.',
            all_exception_statuses: 'Todos los estados de excepción',
            user_id_label: 'ID de usuario',
            justification_label: 'Justificación',
            compensating_controls_label: 'Controles de compensación',
            expires_at_label: 'Vence en',
            granting_exception_for: 'Otorgar una excepción para la regla: {name}',
            severity: 'Gravedad',
            severity_warning: 'Advertencia',
            severity_blocking: 'Bloqueo',
            violation_count: '{count} infracción(es)',
            exception_count: '{count} excepción(es)',
            rule_description: 'Descripción',
            confirm_delete: '¿Estás seguro de que deseas eliminar esta regla?',
            deleted: 'Regla eliminada.',
            framework_code: 'Marco normativo',
            name_label: 'Nombre *',
            severity_label: 'Gravedad *',
            conflict_pairs_legend: 'Pares de permisos en conflicto',
            conflict_pairs_hint: 'Cada par define dos permisos que no deben tener el mismo usuario.',
            placeholder_permission_a: '--Permiso A--',
            placeholder_permission_b: '--Permiso B--',
            permission_a_for_pair: 'Permiso A para el par {index}',
            permission_b_for_pair: 'Permiso B para el par {index}',
            conflict_pair_label: 'Par de conflictos {index}',
            remove_conflict_pair: 'Eliminar par de conflictos {index}',
            add_pair: '+ Agregar par',
            placeholder_name: 'por ej. Separación de aprobación de pago',
            create_rule: 'Crear regla',
            loading_rule: 'Cargando regla SoD...',
            failed_load_rule: 'No se pudo cargar la regla.',
            failed_create_rule: 'No se pudo crear la regla SoD.',
            failed_update_rule: 'No se pudo actualizar la regla SoD.',
            validation_name_required: 'El nombre es obligatorio.',
            validation_pair_required: 'Se requiere al menos un par de permisos de conflicto.',
            validation_pair_both_required: 'Par {index}: Se deben seleccionar ambos permisos.',
            validation_pair_must_differ: 'Par {index}: El permiso A y el permiso B deben ser diferentes.',
            added_conflict_pair: 'Par de conflictos agregado {index}',
            removed_conflict_pair: 'Se eliminó el par de conflictos {index}',
            announce_loaded_violations: 'Se cargaron {count} infracciones',
            announce_loaded_exceptions: '{count} excepciones cargadas',
            announce_loaded_rules: 'Cargado {count} reglas SoD',
            announce_loading_exceptions: 'Cargando excepciones…',
            regulatory_info: {
                sod_context: 'Las reglas SoD aplican la separacion regulatoria de roles requerida por la PDPL. Las violaciones vinculadas a un framework_code pueden incluirse en los paquetes de evidencia (UC-VN-09).',
                violation_export_context: 'Las exportaciones de violaciones SoD incluyen framework_code, roles en conflicto, gravedad, creador y fecha de deteccion para los paquetes de evidencia (P-VN-10).',
                exception_context: 'Las excepciones SoD producen evidencia: aprobador, justificacion, controles compensatorios y fecha de vencimiento. Las excepciones vencidas permanecen visibles (UC-VN-09 Paso 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'Integración CMP',
            status: 'Conexión Estado',
            endpoint: 'Endpoint CMP',
            last_sync: 'Último Sincronizar',
            configure: 'Configurar',
        },
        purpose_mapping: {
            title: 'Finalidad Mapeo',
            cmp_purpose: 'Propósito CMP',
            grc_purpose: 'Propósito GRC',
            mapping_status: 'Mapeo Estado',
            auto_map: 'Mapeo auto.',
            new_mapping: 'Nuevo mapeo',
        },
        webhook: {
            title: 'Webhook Configuración',
            webhook_url: 'URL del webhook',
            secret: 'Webhook Secreto',
            events: 'Tipos de eventos',
            test_webhook: 'Probar Webhook',
        },
        bulk_sync: {
            title: 'Sincronización masiva',
            sync_now: 'Sincronizar Ahora',
            last_run: 'Último Ejecutar',
            records_synced: 'Registros Sincronizado',
            sync_history: 'Sincronizar Historial',
            syncing: 'Sincronizando...',
        },
        sync_settings: {
            sync_failures: 'Fallos de sincronización',
            sync_secret_warning: 'Sincronizar Secreto (save este ahora — ello será no ser shown again):',
            push_description: 'Configure GRC Suite para enviar actualizaciones de estado de casos a AesirX CMP.',
            technical_description: 'Esta configuración controla cómo GRC Suite envía actualizaciones de estado de casos a AesirX CMP.',
            direction_label: 'Dirección',
            direction_value: 'GRC Suite → AesirX CMP (saliente)',
            auth_label: 'Autenticación',
            auth_value: 'Firma HMAC-SHA256 en el encabezado X-GRC-Signature',
            retry_label: 'Reintentar Política',
            retry_value: '3 reintentos con retroceso exponencial (30s, 2min, 10min)',
            inbound_label: 'Config. entrante',
            inbound_value: 'El webhook entrante (CMP → GRC) está configurado en',
            cmp_webhook_settings: 'CMP Webhook Configuración',
            page_title: 'Configuración de Sincronización CMP',
            outbound_sync_configured: 'Sincronización saliente configurada.',
            configure_sync_failed: 'Error al configurar la sincronización',
            configuration_updated: 'Configuración actualizada.',
            update_configuration_failed: 'Error al actualizar la configuración',
            connection_test_failed: 'Prueba de conexión fallida',
            test_result_message: 'Prueba enviada a {endpoint}. Estado: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Nuevo → Under Review (assigned)',
                under_review_to_identity_verification: 'Under Revisar → Identidad Verificación',
                under_review_to_in_progress: 'Under Revisar → En curso',
                in_progress_to_completed: 'En curso → Completado',
                in_progress_to_denied: 'En curso → Denegado',
                any_to_closed: 'Cualquier → Cerrado',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Ganchos web',
            new: 'Nuevo Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Eventos',
            secret: 'Secreto',
            active: 'Activo',
            test: 'Probar',
            create_failed: 'Error al create webhook. Por favor, inténtelo de nuevo.',
            create_success: 'Webhook creado con éxito',
            deactivate_failed: 'Error al deactivate webhook. Por favor, inténtelo de nuevo.',
            deactivate_success: 'Webhook desactivado.',
            fetch_failed: 'Error al fetch webhooks. Por favor, inténtelo de nuevo.',
            send_test_failed: 'Error al send test. Por favor, inténtelo de nuevo.',
            test_failed: 'Webhook test falló.',
            test_success: 'Prueba del webhook exitosa.',
            edit_title: 'Editar webhook',
            announce_loaded: 'Webhooks cargados',
            update_success: 'Webhook actualizado correctamente.',
            update_failed: 'Error al actualizar el webhook.',
            activate_success: 'Webhook activado.',
            activate_failed: 'Error al activar el webhook.',
        },
        domains: {
            title: 'Dominios',
            new: 'Nuevo dominio',
            domain_name: 'Dominio Nombre',
            verified: 'Verificado',
            verify: 'Verificar',
            add_failed: 'Error al add domain. Por favor, inténtelo de nuevo.',
            add_success: 'Dominio added con éxito',
            domain_placeholder: 'p. ej. grc.example.com',
            fetch_failed: 'Error al fetch domains. Por favor, inténtelo de nuevo.',
            remove_failed: 'Error al remove domain. Por favor, inténtelo de nuevo.',
            remove_success: 'Dominio removed con éxito',
            txt_record: 'Registro TXT',
            verify_dns_failed: 'La verificación DNS falló.',
            verify_failed: 'Error al verify domain. Por favor, inténtelo de nuevo.',
            verify_success: 'Dominio verificado con éxito',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Programación de exportaciones',
            new: 'Nuevo programar',
            schedule_name: 'Nombre',
            frequency: 'Frecuencia',
            format: 'Formato',
            last_run: 'Último Ejecutar',
            next_run: 'Siguiente Ejecutar',
            create_failed: 'Error al create schedule. Por favor, inténtelo de nuevo.',
            create_success: 'Programar creado con éxito',
            day_of_month: 'Día del mes',
            day_of_week: 'Día de la semana',
            deactivate_failed: 'Error al deactivate schedule. Por favor, inténtelo de nuevo.',
            deactivate_success: 'Programación desactivada.',
            fetch_failed: 'Error al fetch schedules. Por favor, inténtelo de nuevo.',
            freq_daily: 'Diario',
            freq_monthly: 'Mensual',
            freq_weekly: 'Semanal',
            recipients: 'Destinatarios',
            time_utc: 'Hora (UTC)',
            trigger_failed: 'Error al trigger export. Por favor, inténtelo de nuevo.',
            trigger_success: 'Exportar triggered con éxito.',
            type_audit_trail: 'Pista de auditoría',
            type_compliance_report: 'Cumplimiento Informe',
            type_custom: 'Personalizado',
            type_incident_report: 'Incidente Informe',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Registro de correos',
            recipient: 'Destinatario',
            subject: 'Sujeto',
            sent_at: 'Enviado En',
            status: 'Estado',
            resend: 'Reenviar',
            fetch_failed: 'Error al fetch email logs. Por favor, inténtelo de nuevo.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organizaciones',
        create: 'Agregar organización',
        edit: 'Editar Organización',
        name: 'Nombre',
        slug: 'babosa',
        timezone: 'Zona horaria',
        locale: 'Predeterminado Configuración regional',
        parent: 'Organización matriz',
        group: 'Grupo',
        org_group: 'Grupo de Organizaciones',
        no_group: '(Ninguno - no pertenece a un grupo)',
        created: 'Creado',
        no_parent: '(Ninguno - independiente)',
        no_orgs: 'No se encontraron organizations',
        load_error: 'Error al load organizations. Por favor, inténtelo de nuevo.',
        save_error: 'Error al save organization. Por favor, inténtelo de nuevo.',
        created_msg: 'Organización creado',
        updated: 'Organización actualizado',
        deactivate: 'Desactivar',
        deactivated: 'Organización desactivada',
        confirm_deactivate: '¿Está seguro de que desea desactivar esta organización?',
        loaded_results: 'Organizations cargados, {count} resultados',
        form_loaded: 'Formulario de Organization cargado',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Inglés (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Todos los marcos',
        assigned_at: 'Asignado el',
    },
    intake_configs: {
        title: 'Configuraciones de entrada públicas',
        create: 'Agregar Config',
        edit: 'Editar config. de entrada',
        label_col: 'Etiqueta',
        key: 'Clave de entrada',
        key_hint: 'Público URL será ser: /rights-request?key=',
        mapped_org: 'Organización de destino',
        status: 'Estado',
        enabled: 'Habilitado',
        disabled: 'Deshabilitado',
        request_types: 'Habilitado Solicitud Types',
        language: 'Predeterminado Idioma',
        public_url: 'Público URL',
        no_configs: 'No se encontraron configs de entrada. Cree una para habilitar formularios públicos de solicitud de derechos.',
        load_error: 'Error al load intake configs. Por favor, inténtelo de nuevo.',
        save_error: 'Error al save. Por favor, inténtelo de nuevo.',
        delete_error: 'Error al delete. Por favor, inténtelo de nuevo.',
        created_msg: 'Configuración creada',
        updated: 'Configuración actualizada',
        deleted: 'Config. de entrada eliminada',
        confirm_delete: '¿Eliminar esta configuración de entrada?',
        select_types: 'Seleccione at least one request type',
        select_org: 'Seleccione target organization',
        routing_mode: 'Modo de Enrutamiento',
        single_org: 'Organización Única',
        org_group_mode: 'Grupo de Organizaciones',
        org_group: 'Grupo de Organizaciones',
        select_group: 'Seleccione un grupo de organizaciones...',
        group_root_hint: 'Se establece automáticamente a la organización raíz del grupo cuando se selecciona el grupo.',
        loaded_results: 'Intake configurations cargados, {count} resultados',
        form_loaded: 'Formulario de Intake configuration cargado',
    },
    frequency: {
        weekly: 'Semanal',
        monthly: 'Mensual',
        quarterly: 'Trimestral',
        semi_annually: 'Semestral',
        annually: 'Anual',
        custom: 'Personalizada',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Calendario',
            all_tasks: 'Todas las tareas',
            my_tasks: 'Mis tareas',
            overdue: 'Atrasadas',
            board: 'Tablero',
            workflows: 'Flujos de trabajo',
        },
        list: {
            title: 'Todas las tareas',
            my_title: 'Mis tareas',
            overdue_title: 'Tareas atrasadas',
            new_task: 'Crear tarea',
            no_tasks: 'No se encontraron tareas.',
            empty_state: 'No se encontraron tareas. Las tareas se generan a partir de flujos de trabajo de módulos como evaluaciones, incidentes y campañas de revisión.',
            total: 'Total de tareas',
            search_placeholder: 'Buscar tareas...',
            bulk_actions: 'Acciones masivas',
            change_status: 'Cambiar estado',
            change_priority: 'Cambiar prioridad',
            assign: 'Asignar',
            sr_my_tasks_count: '{count} tareas asignadas a ti',
            sr_days_overdue: '{count} días de retraso',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Detalle de la tarea',
            description: 'Descripción',
            workflow_progress: 'Progreso del flujo de trabajo',
            checklist: 'Lista de verificación',
            dependencies: 'Dependencias',
            comments: 'Comentarios',
            activity_timeline: 'Cronología de actividad',
            assignees: 'Asignados',
            add_assignee: 'Añadir asignado',
            remove_assignee: 'Eliminar asignado',
            due_date: 'Fecha de vencimiento',
            start_date: 'Fecha de inicio',
            sla_status: 'Estado SLA',
            sla_on_track: 'En plazo',
            sla_at_risk: 'En riesgo',
            sla_breached: 'Incumplido',
            tags: 'Etiquetas',
            source_entity: 'Entidad de origen',
            view_source: 'Ver origen',
            add_dependency: 'Añadir dependencia',
            remove_dependency: 'Eliminar dependencia',
            write_comment: 'Escribir un comentario...',
            post_comment: 'Publicar',
            edit_comment: 'Editar',
            delete_comment: 'Eliminar',
            add_checklist_item: 'Añadir elemento',
            no_dependencies: 'Sin dependencias',
            no_comments: 'Sin comentarios aún',
            blocks: 'Bloquea',
            blocked_by: 'Bloqueado por',
            related: 'Relacionado',
            working_days_remain: 'días laborables restantes',
            created_by: 'Creado por',
            completed_at: 'Completado el',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Sin asignados',
            add_assignee_placeholder: 'Introducir ID de usuario...',
            edited: 'editado',
            timeline: 'Cronología de actividad',
            no_timeline: 'Sin actividad aún',
            timeline_reason: 'Reason',
            no_workflow: 'Sin flujo de trabajo asignado',
            add_dependency_placeholder: 'Introducir ID de tarea...',
            sr_status_updated: 'Estado actualizado a {status}',
            sr_priority_updated: 'Prioridad actualizada a {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Ejecutar como flujo de trabajo',
            convert_modal_title: 'Convertir tarea en ejecución de flujo de trabajo',
            convert_workflow_label: 'Flujo de trabajo',
            convert_workflow_placeholder: 'Elija un flujo de trabajo...',
            convert_attachments_label: 'Adjuntos',
            convert_attachments_empty: 'No hay archivos adjuntos en esta tarea.',
            convert_persona_label: 'Anulación de persona (opcional)',
            convert_submit: 'Iniciar la ejecución del flujo de trabajo',
            convert_starting: 'Iniciando la ejecución del flujo de trabajo...',
            convert_started: 'Ejecución del flujo de trabajo iniciada: ejecución de apertura...',
            convert_disabled_in_flight: 'Ya hay en curso un flujo de trabajo ejecutado desde esta tarea.',
            linked_workflow_chip: 'De {workflow_title}',
            linked_chat_chip: 'Desde AI Chat',
            linked_more: '+{count} más',
        },
        calendar: {
            title: 'Calendario de tareas',
            page_title: 'Calendario',
            create_task: 'Crear tarea',
            month: 'Mes',
            week: 'Semana',
            day: 'Día',
            today: 'Hoy',
            more_tasks: '+{count} más',
            no_tasks_day: 'Sin tareas este día',
            all_day: 'Todo el día',
            quick_create_tooltip: 'Crear tarea para este día',
            view_changed: 'Vista del calendario cambiada a {view}',
            tasks_in_view: '{count} tareas en vista',
            task_in_view: '{count} tarea a la vista',
            filter_my_tasks: 'Mis tareas',
            filter_overdue: 'Vencidas',
            filter_high_priority: 'Prioridad alta',
            filter_all: 'Todas',
        },
        filters: {
            assignee: 'Asignado',
            me: 'Yo',
            role: 'Rol',
            status: 'Estado',
            priority: 'Prioridad',
            task_type: 'Tipo de tarea',
            source_module: 'Módulo de origen',
            organization: 'Organización',
            sla_breached_only: 'Mostrar solo SLA incumplido',
            clear_filters: 'Borrar filtros',
            apply_filters: 'Aplicar',
            source_label: 'Fuente',
            source_manual: 'manuales',
            source_module_kind: 'Generado por módulo',
            source_chat: 'Desde AI Chat',
            source_workflow_run: 'Desde AI Ejecución del flujo de trabajo',
        },
        status: {
            pending: 'Pendiente',
            in_progress: 'En curso',
            blocked: 'Bloqueado',
            completed: 'Completado',
            overdue: 'Atrasadas',
            escalated: 'Escalado',
            cancelled: 'Cancelado',
        },
        priority: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Medio',
            low: 'Bajo',
        },
        task_types: {
            manual_task: 'Tarea manual',
            dsr_case: 'Caso DSR',
            dsr_review: 'Revisión DSR',
            dsr_escalation: 'Escalada DSR',
            dpia_review: 'Revisión EIPD',
            dpia_approval: 'Aprobación EIPD',
            transfer_review: 'Revisión de transferencia',
            incident_task: 'Tarea de incidente',
            incident_notification: 'Notificación de incidente',
            playbook_step: 'Paso del manual',
            regulator_deadline: 'Plazo regulador',
            monitoring_task: 'Tarea de supervisión',
            kpi_alert: 'Alerta KPI',
            vendor_review: 'Revisión de proveedor',
            vendor_questionnaire: 'Cuestionario de proveedor',
            governance_review: 'Revisión de gobernanza',
            drift_alert: 'Alerta de desviación',
            governance_approval: 'Aprobación de gobernanza',
            deletion_task: 'Tarea de eliminación',
            deletion_verification: 'Verificación de eliminación',
            access_review: 'Revisión de acceso',
            sod_alert: 'Alerta SoD',
            consent_workflow: 'Flujo de consentimiento',
            consent_alert: 'Alerta de consentimiento',
            property_review: 'Revisión de propiedad',
            inconsistency_alert: 'Alerta de inconsistencia',
            form_review: 'Revisión de formulario',
            dossier_deadline: 'Plazo de expediente',
            submission_task: 'Tarea de envío',
            translation_review: 'Revisión de traducción',
            governance_task: 'Tarea de gobernanza',
            reporting_deadline: 'Plazo de informe',
            scanner_alert: 'Alerta de escáner',
            classification_review: 'Revisión de clasificación',
            lifecycle_task: 'Tarea de ciclo de vida',
            discovery_review: 'Revisión de descubrimiento',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Asignación de formación',
        task_type_groups: {
            training: 'Formación',
            manual: 'manuales',
            dsr: 'DSR / Solicitudes de derechos',
            assessments: 'Evaluaciones',
            incidents: 'Incidentes',
            incident_ops: 'Operaciones de incidentes',
            monitoring: 'Supervisión',
            vendor_risk: 'Riesgo de proveedores',
            governance: 'Gobernanza',
            deletion: 'Eliminación',
            access_review: 'Revisión de acceso',
            consent: 'Consentimiento',
            consent_properties: 'Propiedades de consentimiento',
            forms: 'Formularios',
            localization: 'Localización',
            program_governance: 'Gobernanza de programas',
            privacy_scanner: 'Escáner de privacidad',
            classification: 'Clasificación',
            discovery: 'Descubrimiento',
            forseti: 'Forseti',
        },
        create: {
            title: 'Crear tarea',
            task_title: 'Título',
            task_description: 'Descripción',
            task_type: 'Tipo de tarea',
            task_type_label: 'Tipo de tarea',
            task_type_placeholder: 'Seleccionar un tipo de tarea',
            priority: 'Prioridad',
            assignees: 'Asignados',
            assignee_search_placeholder: 'Buscar usuarios...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Quitar a {name}',
            due_date: 'Fecha de vencimiento',
            start_date: 'Fecha de inicio',
            checklist: 'Lista de verificación',
            tags: 'Etiquetas',
            add_checklist_item: 'Añadir elemento de lista',
            validation: {
                title_required: 'El título es obligatorio',
                title_max_length: 'El título debe tener 500 caracteres o menos',
                description_max_length: 'La descripción debe tener 5000 caracteres o menos',
                due_date_before_start: 'La fecha de vencimiento debe ser posterior a la de inicio',
                max_reminders: 'Máximo 5 recordatorios por tarea',
            },
            reminders: {
                title: 'Recordatorios',
                add: 'Añadir recordatorio',
                at_due_date: 'En la fecha de vencimiento',
                minutes_before: '{count} minutos antes',
                hours_before: '{count} horas antes',
                days_before: '{count} días antes',
                weeks_before: '{count} semanas antes',
                custom: 'Personalizado',
                channel_platform: 'Plataforma',
                channel_email: 'Correo electrónico',
                channel_both: 'Ambos',
                offset_label: 'Recordatorio {n}: momento',
                channel_label: 'Recordatorio {n}: canal',
                remove_label: 'Eliminar recordatorio {n}',
            },
            workflow: {
                label: 'Flujo de trabajo',
                none: 'Ninguno (gestión manual de estado)',
                steps_count: '{count} pasos',
                preview_label: 'Pasos del flujo',
            },
        },
        errors: {
            fetch_failed: 'Error al cargar las tareas.',
            save_failed: 'Error al guardar la tarea.',
            delete_failed: 'Error al eliminar la tarea.',
            status_change_failed: 'Error al cambiar el estado de la tarea.',
            assign_failed: 'Error al asignar la tarea.',
            comment_failed: 'Error al guardar el comentario.',
            checklist_failed: 'Error al actualizar la lista de verificación.',
            dependency_failed: 'Error al actualizar la dependencia.',
            workflow_failed: 'Error al guardar el flujo de trabajo.',
            workflow_activate_failed: 'Error al activar el flujo.',
            workflow_delete_failed: 'Error al eliminar el flujo.',
            template_install_failed: 'Error al instalar la plantilla.',
            search_failed: 'Error al buscar tareas.',
            convert_failed: 'No se pudo iniciar la ejecución del flujo de trabajo.',
            convert_quota_exhausted: 'Cubo de fichas bajo. Recarga antes de volver a ejecutar.',
        },
        dependency_types: {
            blocks: 'Bloquea',
            blocked_by: 'Bloqueado por',
            related: 'Relacionado',
        },
        workflow: {
            current_step: 'Paso actual',
            transition: 'Transición',
            history: 'Historial de pasos',
            no_workflow: 'Sin flujo de trabajo asignado',
            detail_title: 'Detalles del flujo de trabajo',
            list_title: 'Definiciones de flujos de trabajo',
            create_workflow: 'Crear flujo de trabajo',
            browse_templates: 'Explorar plantillas',
            template_catalog_title: 'Catálogo de plantillas',
            name: 'Nombre',
            description: 'Descripción',
            task_types: 'Tipos de tarea',
            steps: 'Pasos',
            version: 'Versión',
            created: 'Creado',
            actions: 'Acciones',
            status_draft: 'Borrador',
            status_active: 'Activo',
            status_archived: 'Archivado',
            activate: 'Activar',
            archive: 'Archivar',
            save: 'Guardar',
            edit: 'Editar',
            delete: 'Eliminar',
            clone: 'Clonar',
            install: 'Instalar',
            installed: 'Instalado',
            system: 'Sistema',
            general_settings: 'Configuración general',
            default_assignment: 'Asignación predeterminada',
            default_sla: 'SLA predeterminado',
            workflow_steps: 'Pasos del flujo de trabajo',
            add_step: 'Añadir paso',
            step_name: 'Nombre del paso',
            step_status: 'Valor de estado',
            assignment_strategy: 'Estrategia de asignación',
            assignment_role: 'Rol de asignación',
            sla_hours: 'Horas SLA',
            sla_calendar_type: 'Tipo de calendario',
            working_days: 'Días laborables',
            calendar_days: 'Días naturales',
            is_terminal: 'Paso terminal',
            required_fields: 'Campos obligatorios',
            checklist_template: 'Plantilla de lista',
            transitions: 'Transiciones',
            add_transition: 'Añadir transición',
            from_step: 'Desde',
            to_step: 'Hasta',
            permission: 'Permiso',
            sla_action: 'Acción SLA',
            notify: 'Notificar',
            escalation_rules: 'Reglas de escalado',
            add_rule: 'Añadir regla',
            trigger: 'Disparador',
            threshold: 'Umbral',
            action: 'Acción',
            target_role: 'Rol objetivo',
            trigger_sla_threshold: 'Umbral SLA',
            trigger_sla_breach: 'Incumplimiento SLA',
            trigger_blocked_duration: 'Duración bloqueada',
            trigger_manual: 'manuales',
            action_notify: 'Notificar',
            action_escalate: 'Escalar nivel',
            action_reassign: 'Reasignar',
            sla_start: 'Iniciar',
            sla_pause: 'Pausar',
            sla_resume: 'Reanudar',
            sla_stop: 'Detener',
            strategy_manual: 'manuales',
            strategy_role_based: 'Basado en rol',
            strategy_round_robin: 'Ronda Robin',
            strategy_least_loaded: 'Menos cargado',
            strategy_creator: 'Creador',
            confirm_delete: '¿Está seguro de que desea eliminar este flujo de trabajo?',
            confirm_archive: '¿Está seguro de que desea archivar este flujo de trabajo?',
            cannot_delete_active: 'Los flujos activos no se pueden eliminar. Archive primero.',
            validation_errors: 'Errores de validación del flujo',
            step_count: '{count} pasos',
            no_workflows: 'No se encontraron flujos.',
            no_templates: 'No hay plantillas disponibles.',
            sr_templates_count: '{count} plantillas disponibles',
            template_installed: 'Plantilla instalada correctamente.',
            activate_success: 'Flujo activado correctamente.',
            archive_success: 'Flujo archivado correctamente.',
            save_success: 'Flujo guardado correctamente.',
            delete_success: 'Flujo eliminado correctamente.',
            applicable_task_types: 'Tipos de tarea aplicables',
            steps_column: 'Pasos',
            status_value_hint: 'Clave de estado del flujo usada por las transiciones. Letras minúsculas, números y guiones bajos.',
            from_status: 'Desde estado',
            to_status: 'Hasta estado',
            required_permissions: 'Permisos requeridos',
            transition_required_fields: 'Campos obligatorios (transición)',
            auto_assign: 'Asignación automática',
            auto_assign_role: 'Rol de asignación automática',
            threshold_percent: 'Umbral (%)',
            blocked_hours: 'Horas bloqueadas',
            notify_roles: 'Roles a notificar',
            escalate_to_role: 'Escalar al rol',
            notification_template: 'Plantilla de notificación',
            action_escalate_tier: 'Nivel de escalado',
            create_modal_title: 'Crear flujo de trabajo',
            create_modal_description: 'Defina pasos, transiciones y reglas de escalado. El flujo de trabajo se crea como borrador.',
            create_modal_footer_note: 'Creado como borrador. Actívelo una vez que el grafo se valide.',
            validation_name_required: 'El nombre del flujo de trabajo es obligatorio.',
            validation_task_types_required: 'Seleccione al menos un tipo de tarea aplicable.',
            validation_min_one_step: 'Agregue al menos un paso.',
            validation_min_one_transition: 'Agregue al menos una transición.',
            validation_step_name_required: 'El nombre del paso es obligatorio.',
            validation_status_value_required: 'El valor del estado es obligatorio.',
            validation_status_value_duplicate: 'El valor del estado debe ser único entre los pasos.',
            validation_transition_endpoints_required: 'Seleccione tanto un estado de origen como uno de destino.',
            validation_transition_unknown_status: 'Esta transición hace referencia a un estado que ningún paso define.',
            validation_sla_positive: 'Las horas de SLA deben ser mayores que cero. Deje vacío para no usar SLA.',
            validation_threshold_percent_range: 'El umbral debe estar entre 0 y 100.',
            validation_blocked_hours_positive: 'Las horas bloqueadas deben ser mayores que cero.',
            warning_no_terminal_step: 'No hay paso terminal. La activación fallará hasta que un paso se marque como terminal.',
            warning_step_no_outgoing: 'Pasos no terminales sin transiciones salientes: {statuses}',
            warning_unreachable_steps: 'Pasos inalcanzables desde el primer paso: {statuses}',
            orphaned_transitions_warning: '{count} transiciones hacen referencia a un paso eliminado.',
            remove_orphaned_transitions: 'Eliminar transiciones huérfanas',
            transitions_renamed_announcement: '{count} transiciones actualizadas al nuevo valor de estado.',
            add_checklist_item: 'Agregar elemento de lista de verificación',
            checklist_item_label: 'Elemento de lista de verificación',
        },
        columns: {
            priority: 'Prioridad',
            title: 'Título',
            status: 'Estado',
            type: 'Tipo',
            assignees: 'Asignado(s)',
            due_date: 'Fecha de vencimiento',
            source: 'Origen',
            actions: 'Acciones',
        },
        groups: {
            overdue: 'Atrasadas',
            due_today: 'Vence hoy',
            due_this_week: 'Vence esta semana',
            due_later: 'Vence después',
            no_due_date: 'Sin fecha de vencimiento',
            closed: 'Completadas o canceladas',
        },
        board: {
            title: 'Tablero de tareas',
            drag_hint: 'Arrastre tareas entre columnas para cambiar el estado',
            empty_column: 'Sin tareas',
            keyboard_hint: 'Teclado: flechas para navegar, espacio para recoger tarea, flechas izquierda/derecha para seleccionar columna, Enter para soltar, Escape para cancelar.',
            sr_moving: 'Moviendo {title}. Sobre {column}. Pulse Enter para soltar, Escape para cancelar.',
            sr_tasks_shown: '{count} tareas mostradas',
            sr_picked_up: 'Se recogió {title}',
            sr_returned: '{title} regresó a {column}',
            sr_dropped: '{title} soltado en {column}, posición {position} de {total}',
            sr_move_cancelled: 'Movimiento cancelado',
            sr_over_column: 'Sobre {column}',
            sr_moved_to: '{title} movido a {column}',
            invalid_transition: 'No se puede mover la tarea de {from} a {to}.',
            terminal_status: 'Esta tarea esta en un estado terminal y no se puede mover.',
            invalid_drop_target: 'Destino no valido',
        },
        team: {
            title: 'Carga de trabajo del equipo',
            assignee: 'Asignado',
            active: 'Activo',
            overdue: 'Atrasadas',
            blocked: 'Bloqueado',
            completed_7d: 'Completado (7d)',
            load: 'Carga',
            load_high: 'Alto',
            load_medium: 'Medio',
            load_normal: 'normales',
        },
        graph: {
            title: 'Gráfico de dependencia',
            list_view: 'Vista de lista',
            graph_view: 'Vista gráfica',
            zoom_in: 'Acercar',
            zoom_out: 'Alejar',
            reset_zoom: 'Reiniciar',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'No hay dependencias para visualizar.',
        },
        add_dependency_modal: {
            title: 'Añadir dependencia',
            search_placeholder: 'Buscar tareas por título o ID...',
            no_results: 'No se encontraron tareas.',
            this_blocks: 'Esta tarea bloquea seleccionados',
            this_blocked_by: 'Esta tarea está bloqueada por seleccionados',
            related: 'Relacionado',
            max_dependencies: 'Máximo 10 dependencias por tarea.',
            circular_error: 'No se puede agregar: crearía una dependencia circular.',
        },
        conflict_detection: {
            title: 'Detección de conflictos',
            unresolved: 'sin resolver',
            no_conflicts: 'No se detectaron conflictos',
            section: 'Sección',
            field: 'Campo',
            dept_a: 'Departamento A',
            value_a: 'Valor A',
            dept_b: 'Departamento B',
            value_b: 'Valor B',
            severity: 'Gravedad',
            status: 'Estado',
            resolved: 'Resuelto',
            open: 'Abierto',
            resolve: 'Resolver',
            load_failed: 'Error al load conflicts',
            resolve_success: 'Conflicto resuelto correctamente',
            resolve_failed: 'Error al resolve conflict',
            severity_critical: 'Crítico',
            severity_warning: 'Advertencia',
            severity_info: 'Información',
        },
        consolidation: {
            title: 'Progreso de consolidación',
            add_child: 'Añadir subtarea',
            child_task_id: 'ID de subtarea',
            department_id: 'ID del departamento',
            link: 'Vincular',
            overall_completion: 'Completado general',
            departments_complete: 'Departamentos completados',
            overdue: 'Vencido',
            ready: 'Listo',
            not_ready: 'No listo',
            master_status: 'Estado principal',
            department_breakdown: 'Desglose por departamentos',
            department: 'Departamento',
            status: 'Estado',
            completion: 'Completado',
            due_date: 'Fecha de vencimiento',
            overdue_flag: 'Vencido',
            child_tasks: 'Subtareas',
            task: 'Tarea',
            load_failed: 'Error al load consolidation data',
            child_linked: 'Subtarea vinculada correctamente',
            link_failed: 'Error al link child task',
            confirm_unlink: '¿Está seguro de que desea desvincular esta subtarea?',
            child_unlinked: 'Subtarea desvinculada',
            unlink_failed: 'Error al desvincular la subtarea',
            unlink: 'Desvincular',
            status_completed: 'Completado',
            status_in_progress: 'En progreso',
            status_pending: 'Pendiente',
            select_child_task: 'Seleccionar subtarea',
            select_department: 'Seleccionar departamento',
        },
        section_assignments: {
            title: 'Asignaciones de sección',
            assign_section: 'Asignar sección',
            section_ref: 'Ref. de sección',
            section_label: 'Etiqueta de sección',
            department_id: 'ID del departamento',
            department: 'Departamento',
            assignee_id: 'ID del asignado',
            assignee: 'Cesionario',
            mandatory: 'Obligatorio',
            assign: 'Asignar',
            no_assignments: 'Sin asignaciones de sección',
            status: 'Estado',
            completion: 'Completado',
            mark_complete: 'Completar',
            load_failed: 'Error al load section assignments',
            assign_success: 'Sección asignada correctamente',
            assign_failed: 'Error al assign section',
            status_updated: 'Estado actualizado',
            status_update_failed: 'Error al actualizar el estado',
            section_ref_placeholder: 'por ej. 3.2',
            status_completed: 'Completado',
            status_in_progress: 'En progreso',
            status_pending: 'Pendiente',
            select_department: 'Seleccionar departamento',
            select_assignee: 'Seleccionar cesionario',
        },
        overdue_columns: {
            days_overdue: 'Dias de retraso',
        },
        regulatory_info: {
            deadline_context: 'Al crear tareas DPIA, las fechas de vencimiento deben alinearse con los plazos de presentacion PDPL: entrada departamental = 10 dias habiles. Los plazos de ciberseguridad y telecomunicaciones tambien aplican.',
            overdue_deadline_context: 'Plazos regulatorios de datos de paquete: entrada departamental PDPL = 10 dias habiles, respuesta complementaria = 15 dias habiles, reenvio = 10 dias habiles. Las tareas con SLA incumplido se distinguen visualmente.',
            section_assignment_context: 'Flujo DPIA multidepartamental (PDPL-P1): el DPO asigna secciones del expediente a departamentos. Plazo de entrada departamental: 10 dias habiles.',
            template_catalog_context: 'Las plantillas del sistema cubren procesos especificos del marco: DPIA PDPL, certificacion de Ley de Datos, respuesta a incidentes de ciberseguridad, solicitudes de autoridad de telecomunicaciones y registro de sistemas de IA. Las plantillas del sistema no son editables.',
            workflow_instantiation_context: 'Las plantillas instaladas pueden instanciarse en flujos de trabajo activos. Las tareas se crean automaticamente con fechas de vencimiento calculadas.',
        },
        overdue: {
            days_overdue: 'días de retraso',
        },
    },
    notifications: {
        bell: {
            title: 'Notificaciones',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notificaciones',
            mark_all_read: 'Marcar todo como leído',
            no_notifications: 'Sin notificaciones',
            filter: {
                all: 'All',
                unread: 'No leído',
                tasks: 'Tareas',
                approvals: 'Aprobaciones',
                alerts: 'Alertas',
            },
        },
        preferences: {
            title: 'Preferencias de notificación',
            description: 'Configura cómo recibes notificaciones',
            category: {
                label: 'categoría',
                task_assigned: 'Tarea asignada',
                task_sla_approaching: 'SLA Acercándose',
                task_overdue: 'Tarea vencida',
                task_completed: 'Tarea completada',
                task_escalated: 'Tarea escalada',
                workflow_transition: 'Transición del flujo de trabajo',
                approval_requested: 'Aprobación solicitada',
                comment_added: 'Comentario añadido',
                weekly_digest: 'Resumen semanal',
                sla_breach_report: 'SLA Informe de infracción',
                system_alert: 'Alerta del sistema',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plataforma',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Horas tranquilas',
                start: 'Iniciar',
                end: 'End',
                timezone: 'Zona horaria',
            },
            digest: {
                title: 'Frecuencia de resumen',
                daily: 'Diariamente',
                weekly: 'Semanal',
                none: 'Ninguno',
            },
            watched: {
                title: 'Entidades vigiladas',
                empty: 'No hay entidades vigiladas',
            },
            save: 'Guardar preferencias',
            saving: 'Guardando...',
            saved_success: 'Preferencias guardadas correctamente',
            load_error: 'No se pudieron cargar las preferencias guardadas. Mostrando valores predeterminados: los cambios se guardarán cuando haga clic en Guardar.',
            enabled: 'Habilitado',
            disabled: 'Discapacitado',
            unwatch: 'Dejar de mirar',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Cola de administración de notificaciones',
            description: 'Supervisar el estado de entrega de correos y reintentar mensajes fallidos',
            load_error: 'No se pudieron cargar los datos de la cola de correo',
            retry_error: 'No se pudo reintentar el correo',
            filter_status: 'Filtrar por estado',
            all_statuses: 'Todos',
            refresh: 'Actualizar',
            status_queued: 'En cola',
            status_sent: 'Enviado',
            status_failed: 'Fallido',
            status_bounced: 'Rebotado',
            status_sending: 'Sending',
            col_recipient: 'Destinatario',
            col_category: 'Categoría',
            col_template: 'Plantilla',
            col_status: 'Estado',
            col_retries: 'Reintentos',
            col_created: 'Creado',
            col_sent: 'Enviado el',
            col_actions: 'Acciones',
            no_items: 'No se encontraron elementos en la cola de correo',
            retry: 'Reintentar',
            retrying: 'Reintentando...',
            prev: 'Anterior',
            next: 'Siguiente',
            access_denied_title: 'Acceso de administrador requerido',
            access_denied: 'Esta página solo está disponible para administradores. Contacte a su administrador si necesita acceso.',
            announce_queue_loaded: 'Cola de correo electrónico cargada, {count} elementos',
            announce_processed: '{count} correos electrónicos procesados',
            process_now: 'Procesar ahora',
            processing_ellipsis: 'Procesando…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'Asistencia de interfaz de usuario',
            activate: 'Activar',
            deactivate: 'Desactivar',
            connected: 'Conectado',
            disconnected: 'desconectado',
            disconnected_hint: 'Desconectado: ejecute `npm run Relay` para conectarse a Claude CLI',
            open_panel: 'Abrir el panel de asistencia de UI',
            history: 'Historia',
            settings: 'Configuración',
        },
        prompt: {
            placeholder: 'Describe el cambio que deseas realizar...',
            send: 'enviar',
            cancel: 'Cancelar',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Procesando…',
            complete: 'completo',
            error: 'error',
            abort: 'Abortar',
            abort_confirm: '¿Estás seguro de que quieres abortar?',
            aborted_hint: 'La sesión fue interrumpida — puedes continuar desde aquí.',
            undo: 'Deshacer',
            redo: 'Rehacer',
            retry: 'Reintentar',
            dismiss: 'Descartar',
        },
        label: {
            component: 'Componente',
            element: 'Elemento',
            file: 'Archivo',
        },
        response: {
            you: 'You',
            claude: 'claudio',
            empty: 'Aún no hay mensajes. Seleccione elementos y envíe un mensaje para comenzar.',
        },
        panel: {
            tab_conversation: 'Charla',
            tab_tasks: 'Tareas',
            tab_sprint: 'correr',
            tab_sessions: 'Sesiones',
            tab_lobby: 'vestíbulo',
            follow_up: 'Seguimiento',
            followup_placeholder: 'Haga una pregunta de seguimiento...',
            interrupt_placeholder: 'Escribe para interrumpir y redirigir…',
            send_interrupt: 'Enviar e Interrumpir',
            will_interrupt: 'Interrumpirá la respuesta actual y enviará esto en su lugar',
            expand: 'Ampliar a pantalla completa',
            collapse: 'Reducir al panel lateral',
            no_sessions: 'No hay sesiones anteriores. Seleccione elementos y envíe una instrucción para comenzar.',
            session_active: 'Activa',
            session_ended: 'Finalizada',
            session_messages: '{count} mensajes',
            switch_session: 'Cambiar a esta sesión',
            load_session: 'Cargar historial de sesión',
            session_restored: 'Sesión restaurada del historial.',
            archive_session: 'Sesión de archivo',
            archive_confirm: '¿Archivar esta sesión? El archivo de sesión se eliminará.',
            new_session: 'Nueva sesión',
            new_session_desc: 'Utilice Activar para seleccionar un elemento de la interfaz de usuario e iniciar una nueva sesión',
            start_new_session: 'Iniciar nueva sesión',
            no_active_session_chat: 'Inicie una nueva sesión o use Activar para seleccionar un elemento de la interfaz de usuario en la página para comenzar.',
            no_active_session_tasks: 'Inicie una nueva sesión o seleccione una sesión existente para crear tareas.',
            no_active_session_sprint: 'Inicie una nueva sesión o seleccione una sesión existente para planificar un sprint.',
            previous_sessions: 'Sesiones anteriores',
        },
        tasks: {
            none_found: 'No se encontraron tareas en la respuesta. Pídale a Claude que sugiera tareas.',
            selected: 'selected',
            create_selected: 'Crear tareas',
            execute_tasks: 'Ejecutar tareas',
            creating: 'Creando…',
            created: 'tareas creadas',
            create_error: 'No se pudo crear la tarea',
            plan_sprint: 'Planificar sprint',
            priority_critical_desc: 'Bloqueante — corregir inmediatamente',
            priority_high_desc: 'Importante — resolver en este sprint',
            priority_medium_desc: 'Normal — planificar para próximo sprint',
            priority_low_desc: 'Deseable — candidato para backlog',
            add_custom: 'Añadir tarea',
            add_title_placeholder: 'Título de la tarea…',
            add_desc_placeholder: 'Descripción (opcional)',
            add_save: 'Añadir',
            add_cancel: 'Cancelar',
        },
        sprint: {
            title: 'Planificador de velocidad',
            assigned: 'assigned',
            new_placeholder: 'Nombre del sprint (por ejemplo, Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Trabajo pendiente',
            move_to: 'Mover a…',
            empty: 'Crea un grupo de sprint y asígnale tareas.',
            create_all: 'Crear todas las tareas',
            remove: 'Quitar',
            drop_here: 'Asigne tareas del trabajo pendiente a continuación',
            add_task: 'Agregar tarea desde el trabajo pendiente...',
        },
        feature_disabled: 'La asistencia de UI no está habilitada',
        permission_denied: 'No tienes permiso para usar UI Assist',
    },
    license: {
        title: 'Licencia y paquete',
        current_license: 'Licencia actual',
        license_key: 'Clave de licencia',
        activate: 'Activar licencia',
        deactivate: 'Desactivar licencia',
        validate_now: 'Validar ahora',
        status: 'Estado de la licencia',
        active: 'Activo',
        expired: 'Caducado',
        suspended: 'suspendido',
        invalid: 'Inválido',
        pending: 'Pendiente',
        no_license: 'Sin licencia',
        expires_at: 'Vence',
        last_validated: 'Última validación',
        domain: 'Dominio con licencia',
        activation_success: 'Licencia activada exitosamente.',
        activation_failed: 'La activación de la licencia falló.',
        domain_mismatch: 'El dominio de licencia no coincide con el dominio actual.',
        deactivation_success: 'Licencia desactivada.',
        validation_success: 'Licencia validada exitosamente.',
        validation_failed: 'La validación de la licencia falló.',
        enter_key: 'Ingrese su clave de licencia',
        confirm_deactivate: '¿Está seguro de que desea desactivar esta licencia? Se aplicarán restricciones de módulo según el nivel predeterminado.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Paquete',
        module_column: 'Módulo',
        current_tier: 'Paquete actual',
        light: 'Luz',
        standard: 'Estándar',
        enterprise_unlimited: 'Empresa ilimitada',
        change_package: 'Cambiar paquete',
        upgrade: 'Actualizar',
        downgrade: 'Rebajar',
        target_tier: 'Paquete objetivo',
        new_license_key: 'Clave de licencia para el paquete Target',
        confirm_change: 'Confirmar cambio de paquete',
        change_success: 'El paquete se cambió correctamente.',
        change_failed: 'Error al cambiar el paquete.',
        modules_included: 'Módulos incluidos',
        modules_restricted: 'No disponible en el paquete actual',
        upgrade_to_enable: 'Actualice para habilitar este módulo.',
        module_availability: 'Disponibilidad del módulo',
        comparison: 'Comparación de paquetes',
        power_users: 'Usuarios avanzados',
        unlimited: 'Ilimitado',
        impact_warning: 'La degradación deshabilitará los siguientes módulos:',
        invalid_license: 'La clave de licencia proporcionada no es válida para este paquete.',
        tier_mismatch: 'La clave de licencia es para un nivel de paquete diferente.',
    },
    board_report: {
        title: 'Informe de Cumplimiento para el Consejo',
        subtitle: 'Resumen ejecutivo de cumplimiento en todos los módulos',
        date_from: 'Fecha desde',
        date_to: 'Fecha hasta',
        language: 'Idioma del informe',
        generate: 'Generar informe',
        generating: 'Generando informe...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Descargar PDF',
        close: 'Cerrar informe',
        open_board_report: 'Generar informe del consejo',
        close_board_report: 'Cerrar informe del consejo',
        report_period: 'Período del informe',
        generated_at: 'Generado el',
        generated_by: 'Generado por',
        organization: 'Organización',
        section_rights_requests: 'Solicitudes de derechos',
        section_vendors: 'Proveedores',
        section_dpias: 'EIPD',
        section_consent: 'Políticas de consentimiento',
        section_data_mapping: 'Mapeo de datos',
        section_incidents: 'Incidentes',
        section_classification: 'Clasificación de datos',
        section_governance: 'Gobernanza',
        open: 'Abierto',
        closed: 'Cerrado',
        sla_compliance: 'Cumplimiento SLA',
        total: 'totales',
        expired_dpa: 'DPA vencido',
        high_risk: 'Alto riesgo',
        completed: 'Completado',
        overdue: 'Vencido',
        policies_active: 'Políticas activas',
        coverage: 'Cobertura',
        systems: 'Sistemas',
        completeness: 'Completitud',
        avg_resolution_days: 'Prom. resolución (días)',
        labeled: 'Etiquetado',
        unlabeled_systems: 'Sistemas sin etiquetar',
        compliance_score: 'Puntuación de cumplimiento',
        drift_events_open: 'Desviaciones abiertas',
        no_data: 'No hay datos disponibles para el período seleccionado.',
        error_date_range: 'Seleccione fecha de inicio y de fin.',
        table_category: 'Categoría',
        table_metric: 'Métrica',
        table_value: 'Valor',
        showing_chart_view: 'Mostrando vista de gráfico',
        showing_table_view: 'Mostrando vista de tabla de datos',
        error_generate: 'No se pudo generar el informe del consejo.',
        playbook_evidence_title: 'Evidencia de ejecución de playbooks',
        playbook_evidence_description: 'Actividad de ejecución por playbook y archivos de evidencia adjuntos para el período del informe.',
        playbook_name: 'Libro de jugadas',
        playbook_execution_count: 'Ejecuciones',
        playbook_steps_total: 'Pasos',
        playbook_steps_required: 'Evidencia requerida',
        playbook_steps_attached: 'Evidencia adjunta',
        playbook_coverage_pct: 'Cobertura',
        playbook_evidence_files: 'Archivos de evidencia',
        playbook_no_evidence: 'No hay ejecuciones de playbook en este período.',
        playbook_download_file: 'Descargar archivo',
    },
    framework_module_links: {
        title: 'Vinculación de Marco Regulatorio',
        modules: 'Módulos Vinculados',
        add_module: 'Agregar Módulo',
        module_code: 'Código de Módulo',
        required: 'Requerido',
        notes: 'Notas',
        link: 'Vincular',
        no_modules: 'No hay módulos vinculados',
        personas: 'Personas Vinculadas',
        add_persona: 'Agregar Persona',
        persona_id: 'ID de Persona',
        role_description: 'Descripción del Rol',
        no_personas: 'No hay personas vinculadas',
    },
    framework_channels: {
        title: 'Canales de Presentación & Plazos',
        channels: 'Canales de Presentación',
        add_channel: 'Agregar Canal',
        channel_type: 'Tipo',
        name: 'Nombre',
        instructions: 'Instrucciones',
        active: 'Activo',
        no_channels: 'No hay canales de presentación configurados',
        portal: 'Portal',
        in_person: 'En Persona',
        postal: 'Correos',
        email: 'Correo electrónico',
        add: 'Agregar',
        deadlines: 'Plazos Regulatorios',
        add_deadline: 'Agregar Plazo',
        label: 'Etiqueta',
        days: 'Días',
        day_type: 'Tipo de Día',
        calendar_days: 'Días Calendario',
        working_days: 'Días Hábiles',
        description: 'Descripción',
        no_deadlines: 'No hay plazos configurados',
    },
    regulatory_updates: {
        title: 'Actualizaciones Regulatorias & Panel de Marco',
        coverage_dashboard: 'Panel de Cobertura de Marco',
        no_frameworks: 'No se encontraron marcos',
        framework: 'Marco Regulatorio',
        status: 'Estado',
        modules: 'Módulos',
        templates: 'Plantillas',
        forms: 'Formularios',
        channels: 'Canales',
        deadlines: 'Plazos',
        pending: 'Actualizaciones Pendientes',
        updates: 'Actualizaciones Regulatorias',
        create_update: 'Crear Actualización',
        framework_id: 'ID de Marco',
        update_title: 'Título',
        description: 'Descripción',
        effective_date: 'Fecha de Vigencia',
        create: 'Crear',
        no_updates: 'No hay actualizaciones regulatorias',
        impact: 'Impacto',
        assess: 'Evaluar',
    },
    module_tips: esModuleTips,
    framework_rights: {
        be_informed: 'Derecho a ser informado',
        consent: 'Derecho al consentimiento',
        refuse_consent: 'Derecho a rechazar el consentimiento',
        withdraw_consent: 'Derecho a retirar el consentimiento',
        access: 'Derecho de acceso',
        rectification: 'Derecho de rectificación',
        data_provision: 'Derecho a la provisión de datos',
        deletion: 'Derecho de supresión',
        restriction_of_processing: 'Derecho a la limitación del tratamiento',
        object_to_processing: 'Derecho de oposición al tratamiento',
        complaint: 'Derecho a presentar una reclamación',
        denunciation: 'Derecho de denuncia',
        lawsuit: 'Derecho a ejercer acciones legales',
        claim_damages: 'Derecho a reclamar daños y perjuicios',
        request_protection_measures: 'Derecho a solicitar medidas de protección',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Directiva 2002/58/CE (Directiva de privacidad electrónica)',
            description: 'Directiva principal para las transposiciones nacionales de ePrivacy. Cubre cookies, privacidad de comunicaciones electrónicas, marketing directo. Cada estado miembro debe transponerlo a su legislación nacional.',
        },
        EU_GDPR: {
            display_name: 'Reglamento General de Protección de Datos (UE) 2016/679',
            description: 'El Reglamento General de Protección de Datos (GDPR) es un reglamento de la UE sobre protección de datos y privacidad. Se aplica directamente en todos los estados miembros de la UE/EEE.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Directiva (UE) 2022/2555 (NIS2)',
            description: 'Directiva matriz para las transposiciones nacionales NIS2. Cubre la gestión de riesgos de ciberseguridad, informes de incidentes y seguridad de la cadena de suministro para entidades esenciales e importantes.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Ley AI de Vietnam (134/2025/QH15) — En vigor desde el 1 de marzo de 2026',
            description: 'Ley de Vietnam AI (Luật Trí tuệ nhân tạo 134/2025/QH15) que rige el desarrollo, despliegue y uso de sistemas de inteligencia artificial. Establece obligaciones de clasificación de riesgos, transparencia y etiquetado, requisitos de notificación de incidentes, obligaciones de evaluación de impacto y un mecanismo de pruebas controladas (sandbox) para las actividades de AI en Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Ley de Ciberseguridad de Vietnam (era 24/2018)',
            description: 'Ley de Ciberseguridad de Vietnam (Ley No. 24/2018/QH14) que establece requisitos para la ciberseguridad, la localización de datos y la notificación de incidentes. Activo hasta que sea reemplazado por la Ley 116/2025/QH15 vigente a partir del 01-07-2026.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Decreto 53 — Implementación de la Ley de Ciberseguridad',
            description: 'Decreto 53/2022/ND-CP que implementa la Ley de Ciberseguridad, que cubre la determinación del alcance, los activadores de localización de datos, los requisitos de presencia local y los flujos de trabajo de cooperación de las autoridades.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Ley de Ciberseguridad (116/2025/QH15)',
            description: 'Ley consolidada de Ciberseguridad de Vietnam (Ley 116/2025/QH15), que reemplaza la Ley 24/2018/QH14, vigente desde el 1 de julio de 2026. Consolida el alcance del Decreto 53/2022/NĐ-CP (ver VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Ley de datos de Vietnam',
            description: 'Ley de Datos de Vietnam (Ley 60/2024/QH15) que establece requisitos integrales de gobernanza de datos, incluido el inventario de datos, la clasificación, la gobernanza del intercambio y la gobernanza de la transferencia transfronteriza de datos. Se superpone con VN_PDPL_LAW_2025 (aspectos de datos personales) y VN_CYBERSEC_LAW_2025 (localización/clasificación de datos).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Decreto 165/2025/NĐ-CPĐ-CP (Implementación de la Ley de Datos)',
            description: 'Decreto 165/2025/NĐ-CP que implementa la Ley de Datos de Vietnam (Ley 60/2024/QH15). Proporciona formularios oficiales para la evaluación del impacto de la transferencia transfronteriza de datos, informes anuales de evaluación de riesgos y acceso a bases de datos nacionales. Define 11 formas oficiales de Mẫu số.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Proyecto de Decreto sobre Operaciones de Bolsas de Datos 2026',
            description: 'Proyecto de decreto sobre el funcionamiento de las plataformas de intercambio de datos (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regula la clasificación de plataformas, la elegibilidad de los participantes, los controles de listado de productos y servicios de datos, las pruebas de espacio aislado, el flujo de trabajo de transacciones, la contratación electrónica, los certificados de derechos de propiedad sobre los datos y el manejo de quejas/disputas. Actualmente en consulta pública; aún no promulgado. Cita la Ley de Datos 60/2024/QH15, la Ley de Protección de Datos Personales 91/2025/QH15, la Ley de Ciberseguridad 116/2025/QH15 y la Ley de Transacciones Electrónicas 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Ley de Comercio Electrónico (122/2025/QH15)',
            description: 'Ley de Comercio Electrónico de Vietnam (Luật Thương mại điện tử 122/2025/QH15) que establece un régimen nacional unificado para el comercio electrónico, que cubre operaciones de plataformas nacionales y transfronterizas, gobernanza y responsabilidad de la plataforma, protección al consumidor, controles de vendedores y afiliados, manejo de quejas, moderación de contenido y cumplimiento de eliminación, y preparación de procedimientos administrativos para el sistema estatal de gestión de comercio electrónico.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Decreto 13/2023/NĐ-CP',
            description: 'Decreto 13/2023/ND-CP, el instrumento operativo para PDPL funciones que incluyen la presentación de expedientes, artefactos de gobernanza, mecanismos transfronterizos y procedimientos administrativos del MoPS.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Ley de Protección de Datos Personales (91/2025/QH15)',
            description: 'Ley de Protección de Datos Personales de Vietnam (Ley 91/2025/QH15) que establece un marco integral de protección de datos personales, vigente a partir del 1 de enero de 2026. Cubre los temas de cumplimiento A a J: bases legales de procesamiento, consentimiento de datos sensibles, derechos de los interesados (acceso/rectificación/borrado/restricción/objeción/portabilidad), notificación de incumplimiento a la autoridad y a los interesados, DPIA para procesamiento de alto riesgo, gobernanza de transferencias transfronterizas, DPO designación, registros de actividades de procesamiento, obligaciones contractuales del procesador. Obligaciones de presentación de autoridades vía MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Referencias cruzadas: VN_PDPL_DECREE_356_2025 (decreto de implementación), VN_CYBERSEC_LAW_2025 (superposiciones en materia de ciberseguridad), VN_DATA_LAW (gobernanza de datos).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Decreto 356/2025/ND-CP (PDPL Implementación)',
            description: 'Decreto 356/2025/ND-CP que implementa la Ley de Protección de Datos Personales (Ley 91/2025/QH15). Cubre la presentación de expedientes, artefactos de gobernanza, mecanismos transfronterizos y certificación de proveedores de servicios. Inventario del formulario anexo: Phụ lục I–VIII (evaluación de impacto, transferencia transfronteriza, certificación). Trámites administrativos del MoPS vía VN_PDPL_ADMIN_PROCEDURES_778_2026 (Decisión 778/QĐ-BCA-A05). Flujo de trabajo de certificación: solicitud → emisión → solicitud de cambio → reemisión → revocación. Referencias cruzadas: VN_PDPL_LAW_2025 (ley matriz), VN_CYBERSEC_LAW_2025 (localización de datos), VN_DATA_LAW (superposiciones de gobernanza de datos).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Procedimientos Administrativos (Decisión 778/QĐ-BCA-A05)',
            description: 'Decisión 778/QĐ-BCA-A05 (febrero de 2026) — Procedimientos administrativos para la protección de datos personales según A05 (MPS). Define los procedimientos de presentación, procesamiento y emisión de resultados de expedientes, incluidos los formularios DLCN-HC y los formularios legales.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Proyecto de Decreto de Aplicación 2026',
            description: 'Proyecto de Decreto de ejecución y sanciones por infracciones a la Ley de Protección de Datos Personales (Ley 91/2025/QH15). Define rangos de sanciones, factores agravantes/mitigantes y procedimientos de aplicación. Actualmente en estado de borrador pendiente de promulgación.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Ley de Telecomunicaciones de Vietnam 2023',
            description: 'Ley de Telecomunicaciones de Vietnam de 2023 (Luật Viễn thông), un sector superpuesto para empresas de telecomunicaciones/ISP/plataformas con obligaciones de confidencialidad, control de divulgación y manejo de solicitudes de autoridad. Referencias cruzadas: VN_CYBERSEC_LAW_2025 (seguridad del sistema de información), VN_PDPL_LAW_2025 (procesamiento de datos de usuarios), VN_DATA_LAW (gobernanza de datos).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Decreto 163/2024/NĐ-CPĐ-CP (Implementación de la Ley de Telecomunicaciones)',
            description: 'Decreto 163/2024/NĐ-CP que implementa la Ley de Telecomunicaciones (Ley 24/2023/QH15). Proporciona formularios oficiales para notificaciones de servicios de telecomunicaciones, licencias, registros y planes de desarrollo de centros de datos. Define 7 formas oficiales de Mẫu số.',
        },
    },
    framework_requirements: esFrameworkRequirements,
    framework_rights_descriptions: {
        be_informed: 'El interesado tiene derecho a ser informado sobre las actividades de tratamiento de datos personales.',
        consent: 'El interesado tiene derecho a dar su consentimiento para el tratamiento de sus datos personales.',
        refuse_consent: 'El interesado tiene derecho a no consentir el tratamiento de sus datos personales.',
        withdraw_consent: 'El interesado tiene derecho a solicitar la retirada del consentimiento prestado previamente.',
        access: 'El interesado tiene derecho a ver y acceder a sus datos personales en poder del responsable del tratamiento.',
        rectification: 'El interesado tiene derecho a corregir o solicitar la corrección de datos personales inexactos.',
        data_provision: 'El interesado tiene derecho a solicitar que se le proporcionen sus datos personales (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'El interesado tiene derecho a solicitar la supresión o destrucción de sus datos personales, salvo las excepciones de la ley.',
        restriction_of_processing: 'El interesado tiene derecho a solicitar la limitación del tratamiento de sus datos personales.',
        object_to_processing: 'El interesado tiene derecho a enviar una solicitud de oposición al tratamiento de sus datos personales.',
        complaint: 'El interesado tiene derecho a presentar una queja (khiếu nại) sobre el procesamiento de datos personales.',
        denunciation: 'El interesado tiene derecho a presentar una denuncia (tố cáo) sobre violaciones a la protección de datos personales.',
        lawsuit: 'El interesado tiene derecho a emprender acciones legales (khởi kiện) por violaciones de sus derechos en materia de protección de datos.',
        claim_damages: 'El interesado tiene derecho a reclamar una indemnización por los daños causados por violaciones de la protección de datos personales.',
        request_protection_measures: 'El interesado tiene derecho a solicitar a las autoridades competentes u organizaciones/individuos pertinentes que implementen medidas para proteger sus datos personales.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Gestión y seguimiento de riesgos (borrador)',
            steps: {
                '1': 'Identificar categorías de riesgo',
                '2': 'Mantener controles de alerta temprana',
                '3': 'Revisar los riesgos de recopilación, almacenamiento, procesamiento, intercambio y transacciones.',
                '4': 'Riesgos de privacidad, ciberseguridad y gestión de acceso de registros',
                '5': 'Activar acciones de remediación',
                '6': 'Preservar el historial de reseñas',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Quejas y disputas (borrador)',
            steps: {
                '1': 'Recibir admisión de quejas/disputas',
                '2': 'Verificar que la información requerida esté completa; solicitar suplemento dentro de las 24 horas si está incompleto',
                '3': 'Dentro de los 3 días hábiles posteriores a la recepción de una solicitud completa, notifique a las partes sobre el enfoque de manejo y el cronograma esperado.',
                '4': 'Solicitar respuestas y documentos de respaldo',
                '5': 'Evaluar los hechos de la disputa y la responsabilidad de la plataforma',
                '6': 'Resultado de resolución récord',
                '7': 'Preservar pruebas y cerrar el caso.',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Entrega y certificado de derechos de propiedad sobre datos (borrador)',
            steps: {
                '1': 'Entregar producto/servicio según el método de contrato.',
                '2': 'Registrar evento y tiempo de entrega',
                '3': 'Generar certificado de derechos de propiedad sobre datos',
                '4': 'Registrar la finalización de la transacción',
                '5': 'Habilitar búsqueda/verificación posterior cuando sea necesario',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Iniciación de transacciones y contratación (borrador)',
            steps: {
                '1': 'Revisar el listado y el resultado de la evaluación.',
                '2': 'Iniciar el flujo de trabajo de compra, negociación o subasta.',
                '3': 'Generar contrato electrónico',
                '4': 'Asegurar la aceptación de ambas partes.',
                '5': 'Registro de finalización de pago',
                '6': 'Autorizar la entrega',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Acceso al entorno de pruebas/zona de pruebas (borrador)',
            steps: {
                '1': 'Enviar solicitud de zona de pruebas',
                '2': 'Definir el alcance, los datos permitidos, el período de tiempo y los permisos de procesamiento.',
                '3': 'Proporcionar un entorno técnico aislado',
                '4': 'Monitorear la seguridad y el acceso durante las pruebas.',
                '5': 'Revisar el resultado antes de la extracción',
                '6': 'Aprobar la extracción solo cuando esté permitido',
                '7': 'Cerrar la sesión de prueba y limpiar el entorno.',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Listado de productos/servicios de datos (borrador)',
            steps: {
                '1': 'Crear solicitud de listado',
                '2': 'Capture evidencia de fuente y propiedad/derecho de uso',
                '3': 'Registro de características técnicas, formato, API detalles y restricciones',
                '4': 'Divulgaciones de calidad récord',
                '5': 'Enviar expediente de listado para revisión de plataforma',
                '6': 'La plataforma realiza comprobaciones técnicas, de legalidad y de integridad',
                '7': 'Aprobar o rechazar listado',
                '8': 'Si se aprueba, publicar el listado con el resultado de la evaluación adjunto',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Incorporación de participantes (borrador)',
            steps: {
                '1': 'Determinar el tipo de participante: vendedor o comprador',
                '2': 'Verificar personalidad jurídica y capacidad jurídica',
                '3': 'Verificar condiciones adicionales si se aplican líneas de negocio condicionales',
                '4': 'Registrar cuenta de transacción',
                '5': 'Vincular cuenta de pago',
                '6': 'Aprobar la participación en la plataforma',
                '7': 'Almacenar registro de participantes y seguimiento de auditoría',
            },
        },
        AI_A1: {
            name: 'AI Registro / Notificación del sistema',
            steps: {
                '1': 'Comprobación de aplicabilidad y clasificación del sistema AI',
                '2': 'Construir expediente de registro/notificación',
                '3': 'Aprobación interna',
                '4': 'Enviar por canal',
                '5': 'Bucle de enmienda: si se devuelve, actualice y vuelva a enviar',
                '6': 'Cerrar y guardar confirmación',
            },
        },
        AI_A2: {
            name: 'AI Flujo de trabajo de clasificación de riesgos',
            steps: {
                '1': 'Crear AI entrada de registro del sistema',
                '2': 'Ejecutar asistente de clasificación',
                '3': 'Adjuntar evidencia',
                '4': 'Aprobar clasificación',
                '5': 'Vincular salidas al expediente del sistema',
            },
        },
        AI_A3: {
            name: 'Cumplimiento de etiquetado y divulgación',
            steps: {
                '1': 'Identificar las obligaciones de etiquetado por sistema y caso de uso.',
                '2': 'Implementar avisos de etiquetado',
                '3': 'Captura de evidencia: capturas de pantalla, copia de la interfaz de usuario, metadatos técnicos',
                '4': 'Manejar exenciones y registrar justificación.',
                '5': 'Aprobar y versionar',
            },
        },
        AI_A4: {
            name: 'Informe de incidentes graves',
            steps: {
                '1': 'Crear AI Registro de incidente',
                '2': 'Clasificar la gravedad',
                '3': 'Ejecutar acciones de contención y captura de evidencia.',
                '4': 'Generar exportación de plantilla de informe de incidentes graves',
                '5': 'Enviar a la autoridad a través del canal',
                '6': 'Seguimiento de las respuestas y cierres de las autoridades',
            },
        },
        AI_A5: {
            name: 'AI Evaluación de impacto',
            steps: {
                '1': 'Crear registro de evaluación',
                '2': 'Completar las secciones requeridas',
                '3': 'Adjuntar evidencia: flujos de datos, documentación de modelos, controles',
                '4': 'Aprobación interna',
                '5': 'Exportar informe y almacenar en dossier',
            },
        },
        AI_A6: {
            name: 'Pruebas controladas/Participación en Sandbox',
            steps: {
                '1': 'Registro completo de elegibilidad y preparación.',
                '2': 'Preparar solicitud y exportar',
                '3': 'Enviar por canal',
                '4': 'Seguimiento de aprobación',
                '5': 'Ejecutar registros y puntos de control de monitoreo',
                '6': 'Informe de finalización completo y paquete de cierre',
            },
        },
        CYBERSEC_C1: {
            name: 'Evaluación de seguridad del sistema de información crítica',
            steps: {
                '1': 'Clasificar los sistemas de información según los requisitos nacionales de ciberseguridad.',
                '2': 'Realizar evaluaciones de seguridad y evaluación de riesgos para sistemas clasificados.',
                '3': 'Implementar las medidas de seguridad requeridas y los controles básicos.',
                '4': 'Documentar evidencia de cumplimiento y programar revisiones periódicas.',
            },
        },
        CYBERSEC_C2: {
            name: 'Notificación de incidentes de ciberseguridad',
            steps: {
                '1': 'Detectar, clasificar y evaluar el incidente de ciberseguridad',
                '2': 'Ejecutar acciones de respuesta inmediata y preservación de evidencia.',
                '3': 'Informar a la autoridad y participar en la coordinación nacional de respuesta a incidentes.',
                '4': 'Realizar una revisión posterior al incidente y documentar las lecciones aprendidas.',
            },
        },
        CYBERSEC_C3: {
            name: 'Verificación del cumplimiento de la localización de datos',
            steps: {
                '1': 'Evaluar si se aplican activadores de localización de datos según el tipo de servicio y las categorías de datos.',
                '2': 'Verificar que la arquitectura de almacenamiento local y los controles de flujo transfronterizo estén implementados.',
                '3': 'Evaluar los requisitos de presencia local (sucursal/oficina de representación) cuando corresponda.',
                '4': 'Documentar la evidencia de cumplimiento y mantenerla para la revisión de la autoridad.',
            },
        },
        CYBERSEC_C4: {
            name: 'Respuesta a la solicitud de cooperación de la autoridad',
            steps: {
                '1': 'Recibir y verificar la legitimidad y alcance de la solicitud de autoridad.',
                '2': 'Recopilar y preparar la información solicitada o ejecutar las acciones requeridas.',
                '3': 'Enviar respuesta a la autoridad dentro del plazo requerido',
                '4': 'Registre la divulgación y cierre el caso.',
            },
        },
        CYBERSEC_C5: {
            name: 'Verificación de Cualificación del Personal de Ciberseguridad',
            steps: {
                '1': 'Evaluar la postura de seguridad de los proveedores utilizando el marco de evaluación de ciberseguridad',
                '2': 'Verifique el alcance del acceso a los datos del proveedor y regístrese en el registro de acceso a los datos del proveedor',
                '3': 'Revisar la cláusula de notificación de incidentes del proveedor y los requisitos contractuales de ciberseguridad.',
                '4': 'Programe revisiones periódicas de seguridad de los proveedores y supervise el cumplimiento',
            },
        },
        DATA_D1: {
            name: 'Configuración de clasificación e inventario de datos',
            steps: {
                '1': 'Catalogar todos los activos de datos con tipos y fuentes en el registro de inventario de datos.',
                '2': 'Clasifique cada activo por nivel de sensibilidad',
                '3': 'Definir reglas de manejo por nivel de clasificación.',
                '4': 'Capture evidencia: capturas de pantalla, diagramas de arquitectura, controles de acceso',
                '5': 'Programar revisiones periódicas',
            },
        },
        DATA_D2: {
            name: 'Gobernanza del intercambio de datos',
            steps: {
                '1': 'Evaluar nuevos acuerdos de intercambio a través de una evaluación de riesgos de intercambio de datos',
                '2': 'Flujo de trabajo de aprobación interno completo',
                '3': 'Registrar el evento de intercambio en el registro de divulgación/intercambio de datos',
                '4': 'Captura de evidencia: registros de aprobación, documentos contractuales',
                '5': 'Revisión periódica de los acuerdos de reparto',
            },
        },
        DATA_D3: {
            name: 'Evaluación de transferencia de datos transfronteriza',
            steps: {
                '1': 'Identificar transferencias transfronterizas en el inventario de datos.',
                '2': 'Realizar una evaluación del riesgo de transferencia',
                '3': 'Aplicar salvaguardias y medidas de gobernanza',
                '4': 'Documento en la sección transfronteriza del certificado de gobernanza',
                '5': 'Paquete de evaluación de exportaciones para auditoría',
            },
        },
        DATA_D4: {
            name: 'Certificación Anual de Gobernanza',
            steps: {
                '1': 'Revisar la matriz RACI de gobernanza',
                '2': 'Verificar el estado de la política (gobernanza, clasificación, intercambio, retención)',
                '3': 'Paquete de atestación completo',
                '4': 'Obtener aprobación interna',
                '5': 'Exportar y archivar atestación',
            },
        },
        DATA_D5: {
            name: 'Manejo de incidentes de datos',
            steps: {
                '1': 'Clasificar el incidente utilizando el registro de decisiones.',
                '2': 'Ejecutar respuesta a incidentes según el manual',
                '3': 'Captura de evidencia: registros, capturas de pantalla, comunicaciones',
                '4': 'Realizar una revisión posterior al incidente',
                '5': 'Si se requiere notificación a la autoridad: cree un caso de envío y envíelo a MIC',
                '6': 'Cerrar y archivar',
            },
        },
        ECOM_P1: {
            name: 'Notificación (sitio web/aplicación de ventas)',
            steps: {
                '1': 'Determinar la clasificación: sitio/aplicación de ventas frente a mercado/plataforma de servicio',
                '2': 'Cree un registro de expediente interno con la identidad de la empresa, información del sitio web/aplicación y las divulgaciones requeridas.',
                '3': 'Generar paquete de evidencia: capturas de pantalla de divulgaciones, URL de políticas, historial de versiones',
                '4': 'Envíe a través de Online.gov.vn: cargue documentos, registre la marca de tiempo y la confirmación',
                '5': 'Seguimiento del procesamiento: si se devuelve para modificación, capture los motivos y vuelva a enviarlo',
                '6': 'Cerrar: confirmación de la tienda, evidencia de vínculo y registro de auditoría',
            },
        },
        ECOM_P2: {
            name: 'Registro (Plataformas de Servicios / Marketplaces)',
            steps: {
                '1': 'Determinar el modelo de plataforma y las obligaciones: mercado, comercio mixto o comercio social.',
                '2': 'Crear expediente de registro: formulario de solicitud, esquema de prestación de servicios, términos y condiciones, reglas de la plataforma',
                '3': 'Aprobaciones internas: asignar propietario legal/de cumplimiento, aprobar para envío',
                '4': 'Envíelo a través de Online.gov.vn: cargue el expediente, registre la marca de tiempo',
                '5': 'Bucle de procesamiento: si se devuelve para modificarlo, actualizarlo y volver a enviarlo',
                '6': 'Aprobación y puesta en funcionamiento: almacenar confirmación, mantener registros de cumplimiento continuos',
            },
        },
        ECOM_P3: {
            name: 'Notificación de actualización/cambio',
            steps: {
                '1': 'Identificar el desencadenante del cambio: cambio sustancial en los elementos del expediente',
                '2': 'Generar paquete de cambios: resumen de cambios, documentos actualizados, actualizaciones de evidencia',
                '3': 'Enviar actualización y registrar el estado de envío',
                '4': 'Seguimiento del procesamiento y cierre',
            },
        },
        ECOM_P4: {
            name: 'Terminación / Desactivación',
            steps: {
                '1': 'Preparar el paquete de rescisión: fecha de cese, motivo, plan de manejo de protección al consumidor, prueba de aviso público',
                '2': 'Enviar terminación: registrar la marca de tiempo y la confirmación',
                '3': 'Cerrar: almacenar el estado final y la evidencia',
            },
        },
        ECOM_P5: {
            name: 'Programa de calificación de credibilidad/marca de confianza',
            steps: {
                '1': 'Revisión de elegibilidad y preparación',
                '2': 'Elaborar expediente mediante formulario oficial.',
                '3': 'Enviar y realizar un seguimiento',
                '4': 'Mantener evidencia de cumplimiento continua',
            },
        },
        PDPL_P1: {
            name: 'DPIA Presentación de presentación a MPS',
            steps: {
                '1': 'Complete DPIA utilizando el formulario del Decreto 356 (Mẫu số 02a/02b)',
                '2': 'Reunir documentos de cobertura y evidencia de respaldo.',
                '5': 'Conservar el expediente y las pruebas de presentación para el seguimiento de auditoría.',
            },
        },
        PDPL_P2: {
            name: 'Solicitud de transferencia transfronteriza',
            steps: {
                '1': 'Completar expediente de traslado transfronterizo mediante formularios del Decreto 356 (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Solicitud de Certificado de Servicio de Procesamiento de Datos',
            steps: {
                '1': 'Elaborar expediente de solicitud de certificación según requisitos del Decreto 356',
                '2': 'Envíe la solicitud a MPS con los documentos de respaldo requeridos.',
                '3': 'Responder a MPS revisar comentarios y solicitudes de complementos',
            },
        },
        PDPL_P4: {
            name: 'Procesamiento de solicitudes de derechos del interesado',
            steps: {
                '1': 'Recibir y registrar la solicitud; verificar la identidad del interesado en un plazo de 2 días hábiles',
                '2': 'Evaluar el tipo de solicitud y determinar la fecha límite aplicable (10/15/20 días por tipo)',
                '3': 'Ejecutar acciones de cumplimiento en todos los sistemas y departamentos relevantes.',
                '4': 'Preparar y enviar respuesta al interesado con el resultado y la justificación.',
                '5': 'Cerrar el caso y conservar la evidencia para la auditoría; documentar cualquier motivo de rechazo',
            },
        },
        PDPL_P5: {
            name: 'Notificación de infracción a MPS',
            steps: {
                '1': 'Detectar y evaluar el incumplimiento; determinar si se aplica la obligación de notificación y resolver el plazo de notificación aplicable a partir de los registros de plazos instalados',
                '2': 'Prepare el paquete de notificación según los requisitos de PDPL',
                '3': 'Enviar la notificación a MPS dentro del plazo de notificación resuelto a partir de los registros de plazos instalados, contado desde el descubrimiento',
                '4': 'Responder a MPS solicitudes de seguimiento; presentar complementos y pruebas de coordinación',
            },
        },
        TELECOM_T1: {
            name: 'Manejo de solicitudes de autoridad',
            steps: {
                '1': 'Recibir y registrar la solicitud de autoridad a través del registro de admisión',
                '2': 'Verificar la identidad de la autoridad, la base legal y el alcance de la solicitud.',
                '3': 'Realizar una revisión legal interna y obtener la aprobación de la gerencia.',
                '4': 'Preparar paquete de respuesta con evidencia reunida',
                '5': 'Redactar y aprobar carta de respuesta.',
                '6': 'Crear caso de envío y enviar respuesta a través del canal',
                '7': 'Registrar la divulgación y realizar un seguimiento del reconocimiento de autoridad',
                '8': 'Cerrar el caso y conservar la evidencia para el seguimiento de auditoría',
            },
        },
        TELECOM_T2: {
            name: 'Gestión de divulgación',
            steps: {
                '1': 'Recibir solicitud de divulgación (consentimiento del usuario, obligación legal o solicitud de autoridad)',
                '2': 'Flujo de trabajo completo de aprobación de divulgación',
                '3': 'Ejecutar divulgación y registrar en el registro de registro.',
                '4': 'Captura de evidencia: registros de aprobación, registros de comunicación',
                '5': 'Revisión periódica del registro de divulgación',
            },
        },
        TELECOM_T3: {
            name: 'Configuración de política de confidencialidad y control de acceso',
            steps: {
                '1': 'Redactar y aprobar la política de confidencialidad.',
                '2': 'Redactar y aprobar la política de control de acceso y registro.',
                '3': 'Actividades de procesamiento de documentos en el registro de procesamiento de información del usuario.',
                '4': 'Captura de evidencia: documentos de políticas, evidencia de implementación',
                '5': 'Programar revisiones periódicas',
            },
        },
        TELECOM_T4: {
            name: 'Evaluación de incidentes de telecomunicaciones',
            steps: {
                '1': 'Clasificar y valorar el incidente.',
                '2': 'Determinar los requisitos de notificación.',
                '3': 'Si se requiere notificación a la autoridad: crear caso de envío',
                '4': 'Capturar y preservar evidencia',
                '5': 'Resolución y cierre',
            },
        },
        TELECOM_T5: {
            name: 'Gestión de retención y eliminación',
            steps: {
                '1': 'Definir el cronograma de retención y eliminación por categoría de datos',
                '2': 'Implementar procedimientos de eliminación',
                '3': 'Capturar evidencia: eliminar registros',
                '4': 'Revisión periódica de horarios.',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registro / Notificación / Preparación Administrativa',
            'ECOM-FINE-B': 'Quejas y Protección al Consumidor',
            'ECOM-FINE-C': 'Control y aplicación de la eliminación',
            'ECOM-FINE-D': 'Operaciones transfronterizas',
            'ECOM-FINE-E': 'Reglas de la plataforma y transparencia',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Fallos en el manejo de solicitudes de autoridad',
            'TELECOM-FINE-B': 'Confidencialidad y divulgación no autorizada',
            'TELECOM-FINE-C': 'Fallos de control de acceso y registro',
            'TELECOM-FINE-D': 'Infracciones de retención y eliminación',
            'TELECOM-FINE-E': 'Fallos en la evaluación de incidentes de telecomunicaciones',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Hasta 100 millones de VND',
                fine_org: 'Hasta 200 millones de VND',
                deadlines: 'Inmediatamente después de la detección',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Hasta 100 millones de VND',
                fine_org: 'Hasta 200 millones de VND',
                deadlines: 'Obligación continua',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Hasta 100 millones de VND',
                fine_org: 'Hasta 200 millones de VND',
                deadlines: 'Obligación continua',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Hasta 100 millones de VND',
                fine_org: 'Hasta 200 millones de VND',
                deadlines: 'Obligación continua',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Hasta 100 millones de VND',
                fine_org: 'Hasta 200 millones de VND',
                deadlines: 'Según lo especificado en la solicitud de autoridad.',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Hasta 80 millones de VND',
                fine_org: 'Hasta 160 millones de VND',
                deadlines: 'Obligación continua',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Hasta 80 millones de VND',
                fine_org: 'Hasta 160 millones de VND',
                deadlines: 'Obligación continua',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Hasta 80 millones de VND',
                fine_org: 'Hasta 160 millones de VND',
                deadlines: 'Obligación continua',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Hasta 80 millones de VND',
                fine_org: 'Hasta 160 millones de VND',
                deadlines: 'Según lo especificado por las obligaciones de manejo de quejas',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Hasta 80 millones de VND',
                fine_org: 'Hasta 160 millones de VND',
                deadlines: 'Antes de iniciar operaciones de comercio electrónico',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Operación continua de la plataforma.',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de Plataforma / Riesgo',
                '2': 'Operador de Plataforma / Seguridad',
                '3': 'Operador de Plataforma / Riesgo',
                '4': 'Operador de Plataforma / Seguridad',
                '5': 'Operador de plataforma',
                '6': 'Cumplimiento',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Un participante envía una queja o solicitud de disputa',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de plataforma',
                '2': 'Operador de plataforma',
                '3': 'Operador de plataforma',
                '4': 'Operador de plataforma',
                '5': 'Operador de Plataforma / Legal',
                '6': 'Operador de plataforma',
                '7': 'Cumplimiento',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'La entrega se completa después de que se cumplan el contrato y las condiciones de pago.',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Vendedor / Operador de plataforma',
                '2': 'Operador de plataforma',
                '3': 'Operador de plataforma',
                '4': 'Operador de plataforma',
                '5': 'Operador de plataforma',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Un comprador inicia una transacción por datos, producto o servicio.',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Comprador',
                '2': 'Comprador / Operador de Plataforma',
                '3': 'Operador de plataforma',
                '4': 'Comprador / Vendedor',
                '5': 'Operador de Plataforma / Pago',
                '6': 'Operador de plataforma',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Un participante quiere evaluar productos/servicios de datos en un entorno aislado.',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Participante',
                '2': 'Operador de Plataforma / Participante',
                '3': 'Operador de Plataforma / TI',
                '4': 'Operador de Plataforma / Seguridad',
                '5': 'Operador de plataforma',
                '6': 'Operador de plataforma',
                '7': 'Operador de Plataforma / TI',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Un vendedor busca incluir datos, productos o servicios de datos para comercializar.',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Vendedor',
                '2': 'Vendedor / Legal',
                '3': 'Vendedor / TI',
                '4': 'Vendedor',
                '5': 'Vendedor',
                '6': 'Operador de plataforma',
                '7': 'Operador de plataforma',
                '8': 'Operador de plataforma',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Un vendedor o comprador se une a la plataforma de datos',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de plataforma',
                '2': 'Operador de Plataforma / Legal',
                '3': 'Operador de Plataforma / Legal',
                '4': 'Operador de plataforma',
                '5': 'Operador de plataforma',
                '6': 'Operador de plataforma',
                '7': 'Cumplimiento',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Inicio del tratamiento de datos personales que requiere evaluación de impacto.',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Equipo de Privacidad',
                '2': 'Equipo de Privacidad',
                '3': 'Legal / Cumplimiento',
                '4': 'DPO / Equipo de Privacidad',
                '5': 'Equipo de Cumplimiento',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intención de transferir datos personales fuera de Vietnam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Equipo de Privacidad',
                '2': 'Legal / Cumplimiento',
                '3': 'DPO / Equipo de Privacidad',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organización que proporciona servicios de procesamiento de datos que requieren certificación.',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Legal / Cumplimiento',
                '2': 'Legal / Cumplimiento',
                '3': 'DPO / Equipo de Privacidad',
                '4': 'Equipo de Cumplimiento',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Recepción de una solicitud de derechos del interesado',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Equipo de Privacidad / Servicio al Cliente',
                '2': 'DPO / Equipo de Privacidad',
                '3': 'Líderes de Departamento / TI',
                '4': 'DPO / Equipo de Privacidad',
                '5': 'Equipo de Cumplimiento',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Descubrimiento de una violación o incumplimiento de la protección de datos personales',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Equipo de respuesta a incidentes / DPO',
                '2': 'DPO / Equipo de Privacidad',
                '3': 'Legal / Cumplimiento',
                '4': 'DPO / Equipo de respuesta a incidentes',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Decreto 13 art. 24',
        'MoPS Admin Procedures': 'Procedimientos de administración de MoPS',
        'Annual cycle': 'Ciclo anual',
        'Decree 13/2023/NĐ-CP Art. 24': 'Decreto 13/2023/NĐ-CPĐ-CP art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Decreto 356/2025/NĐ-CPĐ-CP art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Decreto 356/2025/NĐ-CPĐ-CP art. 5cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Decreto 356/2025/NĐ-CPĐ-CP art. 5cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Decreto 356/2025/NĐ-CPĐ-CP art. 5cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Decreto 356/2025/NĐ-CPĐ-CP art. 5cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Decreto 356/2025/NĐ-CPĐ-CP art. 5cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Decreto 356/2025/NĐ-CPĐ-CP art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Proyecto de Decreto sobre Plataformas de Datos 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Ley 122/2025/QH15 Cap. III; NĐ 98/2020; Fecha 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Ley 122/2025/QH15 Cap. IV (Protección al Consumidor)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Ley 122/2025/QH15 — Procedimiento operativo de Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Ley 134/2025/QH15 Artículo 12',
        'Law 134/2025/QH15 Article 21': 'Ley 134/2025/QH15 Artículo 21',
        'Law 134/2025/QH15 Article 27': 'Ley 134/2025/QH15 Artículo 27',
        'Law 134/2025/QH15 Articles 9–10': 'Ley 134/2025/QH15 Artículos 9 y 10',
        'Law 24/2023/QH15 — article mapping pending': 'Ley 24/2023/QH15 — mapeo de artículos pendiente',
        'Law 60/2024/QH15 (internal control target)': 'Ley 60/2024/QH15 (objetivo de control interno)',
        'Law 91/2025/QH15 Art. 21': 'Ley 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Ley 91/2025/QH15 Art. 23',
        'Policy-defined': 'Definido por políticas',
        Quarterly: 'Trimestral',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 procedimiento administrativo',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'objetivo de control interno (Ley 116/2025/QH15 Art. 24(2)(b), 41(5) — cronograma exacto pendiente del instrumento de implementación)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'objetivo de control interno (Ley 116/2025/QH15 Art. 41(3) — cronograma exacto pendiente del instrumento de implementación)',
        'source verification pending': 'verificación de fuente pendiente',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C) [ES]',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A) [ES]",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A) [ES]',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A) [ES]',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D) [ES]',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D) [ES]',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D) [ES]',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B) [ES]',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A) [ES]',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Internal Audit Governance [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D) [ES]',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (Layer 4) [ES]',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3) [ES]',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G) [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G) [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcing Controls [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G) [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G) [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G) [ES]',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G) [ES]',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01 [ES]',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5) [ES]',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1) [ES]',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C) [ES]',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A) [ES]',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E) [ES]',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01 [ES]',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A) [ES]',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F) [ES]',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B) [ES]',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2) [ES]',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D) [ES]',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Asamblea Nacional)',
        'Ministry of Public Security / A05 Department': 'Ministerio de Seguridad Pública / Departamento A05',
        'Ministry of Public Security': 'Ministerio de Seguridad Pública',
        'Ministry of Information and Communications': 'Ministerio de Información y Comunicaciones',
        'Government of Vietnam': 'Gobierno de Vietnam',
        'European Data Protection Board (EDPB)': 'Consejo Europeo de Protección de Datos (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / CSIRT nacionales',
        'State Bank of Vietnam': 'Banco Estatal de Vietnam',
    },
    tour_titles: {
        access_accountability: 'Acceda al recorrido de rendición de cuentas',
        assessments: 'Tour de Evaluaciones',
        audit_trail: 'Tour de seguimiento de auditoría',
        classification: 'Gira de clasificación',
        compliance_forms: 'Tour de formularios de cumplimiento',
        connectors: 'Tour de conectores',
        consent_properties: 'Tour de propiedades de consentimiento',
        consent: 'Tour de consentimiento',
        dashboard: 'Recorrido por el panel',
        data_mapping: 'Tour de mapeo de datos',
        data_subjects: 'Tour de interesados',
        deletion_orchestration: 'Tour de orquestación de eliminación',
        discovery: 'Tour de descubrimiento',
        governance: 'Gira de gobernanza',
        help_center: 'Visita al Centro de Ayuda',
        incident_ops: 'Tour de operaciones de incidentes',
        incidents: 'Tour de Incidentes',
        localization: 'Tour de localización',
        monitoring: 'Tour de Monitoreo',
        onboarding: 'Tour de incorporación',
        organizations: 'Tour de Organizaciones',
        partner_workspace: 'Tour por el espacio de trabajo de los socios',
        platform_settings: 'Tour de configuración de la plataforma',
        privacy_scanner: 'Tour del escáner de privacidad',
        program_governance: 'Gira sobre la gobernanza del programa',
        rights_requests: 'Gira de solicitudes de derechos',
        task_management: 'Tour de gestión de tareas',
        vendor_risk: 'Tour de riesgo del proveedor',
    },
    tour_descriptions: {
        access_accountability: 'Como líder de auditoría interna (P-VN-10), revise la evidencia de acceso, audite el uso de permisos e investigue patrones anómalos para demostrar la integridad y confidencialidad del GDPR artículo 5(1)(f).',
        assessments: 'Como Empresa DPO (P-VN-01), realizar EIPD siguiendo el Decreto 356 Mau so 02a, identificar brechas de cumplimiento y evaluar los riesgos de transferencia transfronteriza bajo PDPL.',
        audit_trail: 'Como líder de auditoría interna (P-VN-10), consulte evidencia entre módulos, cree paquetes de auditoría y exporte registros a prueba de manipulaciones para presentaciones regulatorias.',
        classification: 'Como administrador de datos (P-VN-05), defina taxonomías de clasificación de datos, asigne etiquetas de confidencialidad, haga cumplir las políticas de retención y resuelva las brechas de cobertura necesarias para el cumplimiento de PDPL.',
        compliance_forms: 'Como empresa DPO (P-VN-01), explore, complete y envíe los formularios reglamentarios oficiales vietnamitas, incluidas las plantillas Phu luc I, Phu luc II y Mau so para las presentaciones del Comité PDP.',
        connectors: 'Como líder de sistemas de TI (P-VN-05), capture los requisitos comerciales para las integraciones, asigne conectores a flujos de datos de cumplimiento y verifique la preparación de POC antes de su puesta en marcha.',
        consent_properties: 'Como empresa DPO (P-VN-01), configure la implementación de CMP: registro de propiedad digital, plantillas doradas para banners de consentimiento PDPL consistentes y matrices vinculantes que vinculan las propiedades con las actividades de procesamiento.',
        consent: 'Como empresa DPO (P-VN-01), administre el ciclo de vida de la política de consentimiento: defina los propósitos de recopilación, haga cumplir los derechos de retiro de PDPL e intégrelo con su plataforma de administración de consentimiento.',
        dashboard: 'Como Empresa DPO (P-VN-01), oriéntese en torno al centro de comando de la postura de cumplimiento que muestra los PDPL plazos, el incumplimiento de los SLA y los KPI de preparación para la auditoría.',
        data_mapping: 'Como administrador de datos (P-VN-05), cree su inventario ROPA, mapee los flujos de datos transfronterizos y mantenga el registro de actividades de procesamiento requerido por PDPL.',
        data_subjects: 'Como empresa DPO o líder de cumplimiento educativo, administre perfiles unificados de interesados que vinculen registros de consentimiento, solicitudes de derechos y actividad cronológica en toda la plataforma.',
        deletion_orchestration: 'Como DPO (P-VN-01), verificar la eliminación de datos de un extremo a otro en sistemas internos y proveedores externos, con cadenas de prueba criptográficas para el GDPR cumplimiento del derecho de borrado del artículo 17.',
        discovery: 'Como administrador de datos (P-VN-05), ejecute análisis automatizados para descubrir datos personales en los sistemas conectados, revise los hallazgos por etapas e introduzca los resultados en el módulo de Clasificación para el etiquetado de PDPL.',
        governance: 'Como Gerente de Riesgos de Proveedores (P-VN-08), mantenga el registro de proveedores, realice un seguimiento de las cadenas de subprocesadores, evalúe los riesgos de transferencias transfronterizas y haga cumplir los manuales de gobernanza conforme a PDPL.',
        help_center: 'Para todas las personas (DPO, auditores, líderes de TI y gerentes de cumplimiento), encuentre respuestas, guías prácticas y documentación específica de módulos para respaldar sus flujos de trabajo de cumplimiento diarios.',
        incident_ops: 'Como comandante de incidentes (P-VN-09), active los manuales de respuesta, realice un seguimiento del cumplimiento de SLA con respecto a la ventana de notificación de PDPL de 72 horas, gestione las escaladas y coordine la respuesta de los proveedores.',
        incidents: 'Como comandante de incidentes (P-VN-09), detecte e informe violaciones de datos con obligaciones de notificación de 72 horas PDPL al Comité de PDP, clasifique por gravedad y cree expedientes de incidentes con nivel de auditoría.',
        localization: 'Como Empresa DPO (P-VN-01), administre el ciclo de vida del marco regulatorio: instale paquetes del mercado vietnamita, active los marcos PDPL y el Decreto 13, asigne requisitos a los módulos de la plataforma y administre las traducciones.',
        monitoring: 'Como Empresa DPO (P-VN-01), establezca programas continuos de monitoreo de cumplimiento, realice un seguimiento de los cambios regulatorios, mida los KPI y gestione los casos de interacción regulatoria bajo PDPL.',
        onboarding: 'Como instructor de cumplimiento (P-VN-02), brinde capacitación mediante visitas guiadas, cargue datos de muestra realistas para ejercicios prácticos y ejecute el asistente de configuración para escenarios de inicio rápido.',
        organizations: 'Como DPO (P-VN-01), configure estructuras de cumplimiento de múltiples organizaciones, establezca jerarquías de departamentos y enrute las solicitudes de los interesados en todo su grupo corporativo.',
        partner_workspace: 'Como administrador de cuentas de socios, incorpore organizaciones asociadas, configure niveles de servicio, realice un seguimiento de los resultados de cumplimiento y administre el mercado de socios.',
        platform_settings: 'Como administrador de la plataforma, administre toda la configuración operativa: identidad, seguridad, integraciones y acceso a módulos que sustentan su programa de cumplimiento.',
        privacy_scanner: 'Como analista de privacidad (P-VN-06), ejecute análisis de monitoreo continuos para detectar violaciones del consentimiento de cookies, rastreadores no autorizados y brechas en las políticas, generando evidencia de ePrivacy y cumplimiento de GDPR.',
        program_governance: 'Navegue entre modelos de madurez, evaluaciones, lagunas de evidencia, resúmenes ejecutivos y más. Cada submódulo contribuye a una visión holística del estado de su programa interno.',
        rights_requests: 'Como empresa DPO (P-VN-01), gestione las solicitudes de derechos de los interesados en PDPL con plazos legales de 15 días, verificación de identidad y seguimientos de resolución de nivel de auditoría.',
        task_management: 'Enrute las tareas de cumplimiento entre personas, realice un seguimiento de los SLA plazos y automatice flujos de trabajo que mantengan a su equipo responsable.',
        vendor_risk: 'Como administrador de riesgos de proveedores (P-VN-08), califique el riesgo de terceros, haga cumplir las revisiones de entrada y active reevaluaciones, a diferencia de la gobernanza del programa, que rastrea su madurez interna.',
    },
    consolidation: {
        title: 'Casos de consolidacion',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Crear caso de consolidación',
        case_count: 'case',
        created: 'Caso de consolidación creado',
        create: 'Crear caso',
        creating: 'Creando...',
        cancel: 'Cancelar',
        no_cases: 'No se encontraron casos de consolidación.',
        all_statuses: 'Todos los estados',
        all_purposes: 'Todos los propósitos',
        filter_status: 'Filtrar por estado',
        filter_purpose: 'Filtrar por propósito',
        col_title: 'Título',
        col_purpose: 'Propósito',
        col_status: 'Estado',
        col_progress: 'Progreso',
        col_deadline: 'Fecha límite',
        col_department: 'Departamento',
        col_version: 'Versión',
        col_evidence: 'evidencia',
        sections_accepted: 'accepted',
        prev: 'Anterior',
        next: 'Siguiente',
        page: 'Página',
        prev_page: 'Pagina anterior',
        next_page: 'Página siguiente',
        field_title: 'Título',
        field_purpose: 'Propósito',
        field_framework: 'Código marco',
        field_deadline: 'Fecha límite',
        field_reviewer: 'ID de usuario del revisor',
        title_placeholder: 'Introduzca el título del caso',
        framework_placeholder: 'por ejemplo, PDPD-2023',
        reviewer_placeholder: 'Introduzca el ID de usuario del revisor',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Presentación',
            cross_border_filing: 'Presentación transfronteriza',
            annual_governance_attestation: 'Certificación Anual de Gobernanza',
            breach_package: 'Paquete de incumplimiento',
            audit_inspection_pack: 'Paquete de inspección de auditoría',
        },
        statuses: {
            draft: 'Borrador',
            collecting: 'Coleccionando',
            consolidating: 'Consolidando',
            review: 'Revisión',
            corrections: 'Correcciones',
            pending_approval: 'Pendiente de aprobación',
            approved: 'Aprobado',
            completed: 'Completado',
            closed: 'Cerrado',
            rejected: 'Rechazado',
        },
        section_statuses: {
            pending: 'Pendiente',
            in_progress: 'En progreso',
            submitted: 'Enviado',
            accepted: 'Aceptado',
            needs_correction: 'Necesita corrección',
            corrected: 'Corregido',
        },
        flag_types: {
            missing_section: 'Sección faltante',
            conflicting_entry: 'Entrada conflictiva',
            stale_entry: 'Entrada obsoleta',
            missing_evidence: 'Evidencia faltante',
            incomplete_data: 'Datos incompletos',
            duplicate_entry: 'Entrada duplicada',
        },
        flag_severities: {
            critical: 'Crítico',
            warning: 'Advertencia',
            info: 'Información',
        },
        sections_title: 'Secciones',
        no_sections: 'Aún no hay secciones asignadas.',
        flags_title: 'Banderas',
        no_flags: 'No se detectaron banderas.',
        open: 'open',
        approval_chain: 'Cadena de aprobación',
        pending: 'Pendiente',
        audit_trail: 'Pista de auditoría',
        no_audit: 'Sin eventos de auditoría.',
        auto_assign: 'Asignar secciones automáticamente',
        start_collection: 'Iniciar colección',
        review_case: 'Caso de revisión',
        generate_export: 'Generar Exportar',
        review_title: 'Revisión de consolidación',
        advance_approval: 'Avance a la aprobación',
        back_to_case: 'Volver al caso',
        accept: 'Aceptar',
        return_section: 'Regresar',
        return_comment: 'Devolver comentario...',
        resolve: 'resolver',
        dismiss: 'Descartar',
        resolution_notes: 'Notas de resolución...',
        notes: 'Notas',
        section_accepted: 'Sección aceptada',
        section_returned: 'Sección devuelta para corrección',
        advanced_to_approval: 'Caso avanzado a aprobación pendiente',
        section_data: 'Datos de sección',
        save_progress: 'Guardar progreso',
        submit_section: 'Enviar sección',
        submitting: 'Enviando...',
        resubmit: 'Reenviar',
        progress_saved: 'Progreso guardado',
        section_submitted: 'Sección enviada',
        correction_required: 'Corrección requerida',
        evidence_title: 'evidencia',
        no_evidence: 'No se adjunta ninguna prueba.',
        add_evidence: 'Agregar evidencia',
        evidence_added: 'Evidencia agregada',
        evidence_files: 'archivos de evidencia',
        uploaded_by: 'subido por',
        comments_title: 'Comentarios',
        no_comments: 'Aún no hay comentarios.',
        comment_placeholder: 'Añade un comentario...',
        add_comment: 'Agregar comentario',
        comment_added: 'Comentario añadido',
        view_data: 'Ver datos enviados',
        export_title: 'Exportación de dossier',
        export_generated: 'Paquete de exportación generado',
        regenerate: 'regenerar',
        regenerating: 'Regenerante...',
        generated_at: 'Generado en',
        artifacts_title: 'Exportar artefactos',
        artifact_type: 'artefacto',
        file_name: 'Nombre de archivo',
        file_size: 'Tamaño',
        generated: 'generado',
        download: 'Descargar',
        artifacts: {
            master_report: 'Informe maestro',
            evidence_bundle: 'Paquete de pruebas (ZIP)',
            contributor_lineage: 'Linaje de contribuyentes',
            conflict_resolution: 'Resolución de conflictos',
            approval_history: 'Historial de aprobación',
        },
        official_forms: 'Formularios Oficiales',
        forms_description: 'Los siguientes formularios oficiales se pueden generar a partir de los datos consolidados. Los formularios oficiales están separados de los documentos de trabajo internos.',
        announce_loaded_cases: 'Cargado {count} casos de consolidación cargados',
        announce_case_detail_loaded: 'Detalles del caso cargados',
        announce_case_transitioned: 'Caso transferido a {status}',
        announce_flag_resolved: 'Bandera resuelta',
        announce_flag_dismissed: 'Bandera despedida',
        case_detail_loaded: 'Detalles del caso cargados',
        sections_auto_assigned: 'Secciones asignadas automáticamente desde la jerarquía de departamentos',
        errors: {
            load_failed: 'No se pudieron cargar los datos. Por favor inténtalo de nuevo.',
            create_failed: 'No se pudo crear el caso. Por favor inténtalo de nuevo.',
            required_fields: 'Por favor complete todos los campos requeridos.',
            not_found: 'No encontrado.',
            comment_required: 'Se requieren comentarios al devolver una sección.',
            notes_required: 'Se requieren notas al resolver o descartar una bandera.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importar casos',
            nav_title: 'Importación masiva',
            description: 'Suba un archivo CSV para importar en masa casos de solicitud de derechos.',
            select_file: 'Seleccionar archivo CSV',
            file_help: 'CSV con encabezados que coincidan con los campos del caso.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Archivo demasiado grande. El tamaño máximo es 2MB.',
            preview: 'Vista previa',
            columns_detected: 'Columnas detectadas: {count}',
            column_mapping: 'Asignación de columnas',
            import_button: 'Importar',
            import_complete: 'Importación completada',
            import_error: 'La importación falló',
            result: 'Resultado de la importación',
            total: 'totales',
            imported: 'Importado',
            skipped: 'Omitido',
            failed: 'Fallido',
            errors: 'Errores',
            error_row: 'Fila',
            error_field: 'Campo',
            error_message: 'Mensaje',
            history: 'Historial de importación',
            no_history: 'Sin historial de importación.',
            history_file: 'Nombre del archivo',
            history_date: 'Fecha',
        },
        batch: {
            nav_title: 'Proceso por lotes',
            page_title: 'Proceso por lotes DSR',
            page_subtitle: 'Sube un CSV de DSR pendientes. Forseti redacta una respuesta de reconocimiento por solicitud y pone en cola una tarea de verificación por fila. El DPO revisa + acepta cada uno antes de enviarlo.',
            upload_heading: 'Subir',
            csv_hint: 'Columnas obligatorias: request_id, requester_email, request_type. Opcional: fecha límite (ISO-8601), notas.',
            rows_heading: '{count} filas analizadas',
            submit_label: 'Ejecutar lote',
            submitting: 'Redacción…',
            col_request_id: 'Solicitar identificación',
            col_email: 'Correo electrónico del solicitante',
            col_type: 'Tipo',
            col_status: 'Estado',
            col_actions: 'Acciones',
            status_queued: 'En cola',
            status_drafted: 'redactado',
            status_failed: 'Fallido',
            status_accepted: 'Aceptado',
            status_rejected: 'Rechazado',
            status_parsing_error: 'error de análisis',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Aceptar',
            reject: 'Rechazar',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Desencadenantes de la evaluación',
        subtitle: 'Configure la creación de evaluaciones automatizadas y notificaciones DPO para flujos de trabajo de privacidad.',
        auto_create_dpia_label: 'Crear automáticamente DPIA al procesar la actividad marcada como DPIA requerida',
        auto_notify_new_purpose_label: 'Notificar automáticamente a DPO cuando se cree un nuevo propósito con categorías de datos confidenciales',
        auto_notify_cross_border_label: 'Notificar automáticamente a DPO cuando se agregue una transferencia transfronteriza a la actividad de procesamiento',
        default_dpo_label: 'Predeterminado DPO',
        default_dpo_placeholder: 'Seleccione DPO predeterminado...',
        notification_channels_label: 'Canales de notificación',
        channel_in_app: 'En la aplicación',
        channel_email: 'Email',
        save_success: 'Se guardaron los ajustes del activador de evaluación.',
        save_error: 'No se pudo guardar la configuración del activador de evaluación.',
        fetch_error: 'No se pudo cargar la configuración del activador de evaluación.',
        fetch_users_error: 'No se pudieron cargar los usuarios.',
        announce_loaded: 'Configuración del activador de evaluación cargada',
        dpia_status: 'DPIA Estado',
        dpia_linked: 'DPIA Vinculado',
        dpia_required_no_dpia: 'DPIA Requerido — No creado',
        create_dpia: 'Crear DPIA',
        create_dpia_success: 'DPIA creado y vinculado a la actividad.',
        create_dpia_error: 'No se pudo crear DPIA para la actividad.',
        view_dpia: 'Ver DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Perfiles de Interesados',
        nav_title: 'Interesados',
        nav: {
            profiles: 'Perfiles',
        },
        create: 'Crear Perfil',
        edit: 'Editar Perfil',
        delete: 'Eliminar Perfil',
        delete_confirm: '¿Está seguro de que desea eliminar este perfil del interesado?',
        display_name: 'Nombre para mostrar',
        email: 'Email',
        phone: 'Teléfono',
        employee_id: 'ID de empleado',
        student_id: 'Identificación de estudiante',
        notes: 'Notas',
        external_refs: 'Referencias externas',
        cases_count: 'Casos',
        workflows_count: 'Registros de consentimiento',
        last_activity: 'Última actividad',
        timeline: 'Línea de tiempo',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'No se encontraron perfiles de interesados.',
        search_placeholder: 'Busca por nombre, correo electrónico, teléfono o DNI...',
        create_success: 'Perfil del interesado creado.',
        create_error: 'No se pudo crear el perfil del interesado.',
        update_success: 'Perfil del interesado actualizado.',
        update_error: 'No se pudo actualizar el perfil del interesado.',
        delete_success: 'Perfil del interesado eliminado.',
        delete_error: 'No se pudo eliminar el perfil del interesado.',
        link_case_success: 'Caso vinculado al perfil.',
        link_case_error: 'No se pudo vincular el caso al perfil.',
        link_workflow_success: 'Flujo de trabajo vinculado al perfil.',
        link_workflow_error: 'No se pudo vincular el flujo de trabajo al perfil.',
        load_error: 'No se pudieron cargar los perfiles de los interesados.',
        detail_load_error: 'No se pudo cargar el perfil del interesado.',
        timeline_load_error: 'No se pudo cargar la línea de tiempo.',
        tab_cases: 'Casos DSR',
        tab_consent: 'Registros de consentimiento',
        tab_timeline: 'Línea de tiempo',
        tab_consent_workflows: 'Flujos de trabajo de consentimiento',
        tab_web_consent: 'CMP Consentimiento',
        web_consent_empty: 'No se encontraron CMP registros de consentimiento para este sujeto.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'No se pudieron cargar los registros de consentimiento CMP.',
        col_policy_version: 'Versión de la política',
        col_versions: 'Versiones',
        col_consent_version: 'Consentimiento',
        col_cookie_declaration_version: 'Declaración de cookies',
        col_purposes: 'Propósitos',
        purpose_granted: 'Concedido',
        purpose_denied: 'denegado',
        granted_domains: 'Concedido',
        denied_domains: 'denegado',
        col_action: 'acción',
        col_timestamp: 'Marca de tiempo',
        col_domain: 'Dominio',
        no_cases: 'No hay casos vinculados.',
        no_workflows: 'No hay flujos de trabajo de consentimiento vinculados.',
        no_timeline: 'Sin eventos en la línea de tiempo.',
        case_id: 'Identificación del caso',
        case_status: 'Estado',
        case_type: 'Tipo de solicitud',
        case_created: 'Creado',
        workflow_id: 'ID de flujo de trabajo',
        workflow_type: 'Tipo',
        workflow_status: 'Estado',
        workflow_created: 'Creado',
        timeline_date: 'Fecha',
        timeline_event: 'Evento',
        timeline_details: 'Detalles',
        timeline_time: 'tiempo',
        timeline_type: 'Tipo',
        timeline_summary: 'Resumen',
        type_case: 'Caso DSR',
        type_consent_workflow: 'Flujo de trabajo de consentimiento',
        profile_info: 'Información de perfil',
        announce_loaded: 'Perfiles de interesados cargados',
        announce_detail_loaded: 'Perfil del interesado cargado',
        select_case: '— Seleccionar un caso —',
        select_workflow: '— Seleccionar un flujo de trabajo —',
        no_cases_hint: 'Vincule casos para verlos aquí.',
        no_workflows_hint: 'Vincule flujos de consentimiento para verlos aquí.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} sistemas · {flows} flujos · {crossBorder} transferencias internacionales · {highRisk} riesgo alto/crítico',
            switch_to_list: 'Cambiar a vista de lista',
            switch_to_list_aria: 'Cambiar a vista de tabla',
            svg_aria: 'Mapa de flujos de datos: {systems} sistemas, {flows} flujos, {crossBorder} transferencias internacionales',
            selected_system: 'Sistema seleccionado',
            drill_hint: 'Abra la pestaña «Lista» para editar los flujos, EIPD y asignaciones de proveedores vinculados a este sistema.',
        },
    },
    packages: {
        light: {
            name: 'Luz',
            description: 'Herramientas esenciales de cumplimiento de privacidad para equipos pequeños',
        },
        standard: {
            name: 'Estándar',
            description: 'Plataforma de cumplimiento completa para organizaciones en crecimiento',
        },
        enterprise_unlimited: {
            name: 'Empresa ilimitada',
            description: 'Cumplimiento empresarial con soporte multi-organización y servicios dedicados',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Configuración del proveedor',
            subtitle: 'Configure el proveedor Claude para el Asesor Legal de Forseti AI.',
            healthHeading: 'Salud del proveedor',
            statusLabel: 'Estado',
            statusOk: 'bien',
            statusFailed: 'No disponible',
            providerLabel: 'Proveedor',
            providerClaudeApi: 'Claudio API',
            providerCliRelay: 'Relé Claude CLI',
            providerAesirManaged: 'Tokens administrados por Aesir',
            lastCheckedLabel: 'Última comprobación',
            reasonLabel: 'Razón',
            refreshButton: 'Actualizar',
            apiKeyHeading: 'Claude API clave',
            apiKeyHint: 'Establecer una tecla API activa el modo Claude API. Déjelo sin configurar para usar la retransmisión CLI local (modo desarrollador/hospedado propio).',
            apiKeyLabel: 'Tecla API',
            apiKeyMinLength: 'Mínimo 32 caracteres. Cifrado en reposo; nunca más se muestra.',
            apiKeyTooShort: 'La clave API debe tener al menos 32 caracteres.',
            apiKeySaved: 'API clave guardada. Salud refrescante…',
            apiKeyCleared: 'Tecla API borrada. Forseti ahora utiliza el relé CLI local.',
            setKeyButton: 'Guardar clave API',
            clearKeyButton: 'Borrar la tecla API (use el relé CLI)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Uso',
            recentHeading: 'Actividad reciente',
            historyHeading: 'Últimos 12 períodos',
            noActivity: 'Aún no hay actividad en este período.',
            featureLabel: 'Característica',
            whenLabel: 'cuando',
            periodLabel: 'Periodo',
            byFeatureHeading: 'Este mes por característica',
            byFeatureEmpty: 'Aún no hay actividad de funciones en este período.',
            featureColumn: 'Característica',
            featureUnitsColumn: 'Unidades',
            featureRunsColumn: 'Corre',
            byUserHeading: 'Este mes por usuario (vista de administrador)',
            byUserEmpty: 'Aún no hay actividad por usuario en este período.',
            userColumn: 'Usuario',
            reconciliationLinkLabel: 'Ver informe de desviación de conciliación',
            reconciliationLinkHint: 'Solo administrador interno de Aesir.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Charla',
                workflow: 'Ejecución del flujo de trabajo',
                tabular: 'extracción tabular',
                redline: 'Revisión de línea roja',
                title: 'Título de la conversación',
                unknown: 'Otro',
            },
        },
        reconciliation: {
            title: 'Conciliación de cuotas de Forseti',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Volver al uso de Forseti',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Esta página está restringida a administradores internos de Aesir. Si cree que debería tener acceso, comuníquese con su representante de AesirX.',
        },
        projects: {
            title: 'Proyectos',
            subtitle: 'Espacios de trabajo para documentos, conversaciones, flujos de trabajo y revisiones.',
            modeLabel: 'Ver',
            tabMine: 'Mis proyectos',
            tabShared: 'compartido conmigo',
            searchLabel: 'Buscar proyectos',
            searchPlaceholder: 'Buscar…',
            newButton: 'Nuevo proyecto',
            colName: 'Nombre',
            colVisibility: 'Visibilidad',
            colDocs: 'Documentos',
            colMembers: 'Miembros',
            colUpdated: 'Actualizado',
            visibilityOwn: 'Solo propio',
            visibilityShared: 'Compartido',
            emptyMine: 'Aún no hay proyectos. Crea uno para comenzar.',
            emptyShared: 'No se ha compartido ningún proyecto contigo.',
            pageHint: 'Mostrando {shown} de {total}.',
            createTitle: 'Nuevo proyecto',
            createSubmit: 'Crear proyecto',
            fieldName: 'Nombre',
            fieldDescription: 'Descripción (opcional)',
            fieldCmNumber: 'Número de caso/asunto (opcional)',
            fieldVisibility: 'Visibilidad',
            errorNameRequired: 'El nombre es obligatorio.',
            errorSelectorRequired: 'Seleccione un usuario o grupo organizativo para agregar.',
            renameButton: 'Cambiar nombre',
            renamePrompt: 'Cambiar nombre del proyecto',
            deleteButton: 'Eliminar',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: '¿Eliminar esta carpeta? Los documentos que se encuentran dentro permanecerán en el proyecto, sin archivar.',
            removeMemberConfirm: '¿Quitar a este miembro?',
            foldersHeading: 'Carpetas',
            foldersEmpty: 'Aún no hay carpetas.',
            addFolderButton: 'Nueva carpeta',
            addFolderTitle: 'Nueva carpeta',
            documentsHeading: 'Documentos',
            documentsEmpty: 'Aún no hay documentos adjuntos.',
            colFilename: 'Nombre de archivo',
            colAdded: 'Añadido',
            unattachButton: 'Desconectar',
            membersHeading: 'Miembros',
            membersEmpty: 'Sólo propietario.',
            addMemberButton: 'Agregar miembro',
            addMemberTitle: 'Agregar miembro',
            addMemberSubmit: 'Agregar miembro',
            colMember: 'Miembro',
            colRole: 'Rol',
            selectorTypeLabel: 'Agregar por',
            selectorUser: 'ID de usuario',
            selectorGroup: 'ID del grupo organizativo',
            fieldUserId: 'Usuario',
            fieldGroupId: 'grupo organizativo',
            fieldRole: 'Rol',
            roleAdmin: 'administrador',
            roleEditor: 'Redactor',
            roleViewer: 'Visor',
            notFound: 'Proyecto no encontrado.',
            selectUserPlaceholder: 'Seleccione un usuario…',
            selectGroupPlaceholder: 'Seleccione un grupo organizativo...',
            noUsersAvailable: 'No hay usuarios disponibles.',
            noGroupsAvailable: 'No hay grupos organizativos disponibles.',
            loadFailed: 'No se pudieron cargar las opciones.',
            deleteProjectTitle: '¿Eliminar proyecto?',
            deleteFolderTitle: '¿Eliminar carpeta?',
            removeMemberTitle: '¿Quitar miembro?',
            removeMemberConfirmGeneric: '¿Eliminar a este miembro del proyecto?',
            addDocumentButton: 'Agregar documento',
            addDocumentTitle: 'Agregar documento al proyecto',
            fieldDocument: 'Seleccionar documento',
            selectDocPlaceholder: 'Buscar un documento...',
            ownerLabel: 'Propietario del sistema',
        },
        chat: {
            sidebarLabel: 'Charlas',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Su AI Asesor legal y de cumplimiento, basado en los datos de su inquilino y sus paquetes regulatorios instalados.',
            personaSelectorLabel: 'persona',
            personaHeading: 'Elige una persona',
            startChatButton: 'Iniciar un nuevo chat',
            startingChat: 'Iniciando chat…',
            recentHeading: 'Chats recientes',
            recentEmpty: 'Aún no hay chats: inicia uno arriba.',
            untitledChat: 'Nuevo chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Conversación',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'enviar un mensaje',
            composeLabel: 'Mensaje Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'persona',
            presetPersona: 'Persona preestablecida',
            changePersona: 'Cambiar de persona',
            hidePersonaOptions: 'Ocultar opciones de persona',
            starterHeading: 'Empiece con una de estas acciones',
            renameButton: 'Cambiar nombre',
            renamePrompt: 'Cambiar el nombre del chat',
            deleteButton: 'Eliminar',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chat no encontrado.',
            emptyChat: 'Aún no hay mensajes: saluda para iniciar una conversación.',
            roleUser: 'tu',
            roleAssistant: 'Forseti',
            roleSystem: 'Sistema',
            thinkingLabel: 'Pensando',
            toolCallPlaceholder: 'Llamada de herramienta: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Ediciones de documentos propuestas (V{version}).',
            disclaimer: 'Forseti respalda su criterio; no reemplaza a los abogados autorizados.',
            sendButton: 'enviar',
            sending: 'Enviando…',
            composePlaceholder: 'Pregúntale a Forseti...',
            composingPlaceholder: 'Forseti está respondiendo...',
            streamingPlaceholder: 'Forseti está escribiendo...',
            promptInjectionTitle: 'Forseti dejó caer algunas fichas de control.',
            promptInjectionBody: 'Patrones despojados: {patterns}. Forseti solo verá el texto de su mensaje.',
            rateLimitTitle: 'Estás enviando mensajes demasiado rápido.',
            rateLimitRetryAfter: 'Inténtalo de nuevo en {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti no está seguro aquí.',
            counselReviewBody: 'Confirme con un abogado autorizado antes de actuar sobre esta respuesta.',
            legalConclusionRefused: 'Forseti se negó a emitir una conclusión jurídica sin fundamento. Pruebe con search_regulatory primero o consulte con un abogado.',
            toolCallDone: 'Hecho.',
            toolCallArgsLabel: 'Argumentos',
            toolCallResultLabel: 'Resultado',
            toolResultLogged: 'Resultado de la herramienta capturado en el registro de auditoría.',
            toolResultAria: 'resultado de la herramienta',
            documentEditAria: 'editar documento',
            createTaskFromMessage: 'Crear tarea a partir de esto',
            createTaskHeading: 'Crear tarea a partir de este mensaje',
            createTaskTitleLabel: 'Título',
            createTaskTitleRequired: 'Se requiere título.',
            createTaskDescriptionLabel: 'Descripción',
            createTaskAssigneeLabel: 'Cesionario',
            createTaskAssigneePlaceholder: 'Seleccionar cesionario...',
            createTaskAssigneeNone: '- no asignado -',
            createTaskDueDateLabel: 'fecha de vencimiento',
            createTaskWorkflowLabel: 'Flujo de trabajo vinculado (opcional)',
            createTaskWorkflowPlaceholder: 'Seleccione un flujo de trabajo...',
            createTaskWorkflowNone: '- ninguno -',
            renameModalSubmit: 'Cambiar nombre',
            renameModalSaving: 'Guardando…',
            deleteConfirmTitle: '¿Eliminar chat?',
            createTaskSubmit: 'Crear tarea',
            createTaskSubmitting: 'Creando…',
            cancelButton: 'Cancelar',
            autoRouteHintMessage: 'Esta pregunta está más en la timonera de {persona}.',
            autoRouteSwitchButton: 'Cambiar a {persona}',
            autoRouteSwitching: 'Cambiando…',
            handoffSuggestionMessage: 'O abre un nuevo chat con {persona} para continuar con esta pregunta.',
            handoffSuggestionButton: 'Iniciar un nuevo chat con {persona}',
            handoffSuggestionWorking: 'Abriendo…',
            handoffSuggestionDismiss: 'Ahora no',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Especialista en cumplimiento',
                'forseti-legal': 'Asesor legal',
                'forseti-dpo': 'Asesor DPO',
                'forseti-risk': 'Analista de riesgos',
                'forseti-auditor': 'Perspectiva del auditor',
            },
            exportButton: 'Exportar PDF',
            exportRunning: 'Exportando…',
            exportSuccess: 'Exportado: {filename} (abrir el explorador de archivos).',
            exportOpenFile: 'Abrir en el explorador de archivos',
            exportDownload: 'Descargar PDF',
            citation: {
                verifyBadge: '[VERIFICAR]',
                verifyAria: 'no se pudo verificar la cita: revise antes de confiar',
                popoverAria: 'Fuente de {label}',
                openSource: 'Página de código abierto',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-evento generado a partir del seguimiento de auditoría de Forseti.',
        },
        rowAction: {
            ask: 'Pregúntale a Forseti',
            tooltipOpen: 'Pregúntale a Forseti sobre esto {recordType}',
            tooltipLocked: 'Este {recordType} está bloqueado: Forseti puede responder preguntas pero no puede realizar cambios.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — acerca de este disco',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Resumen',
            suggestedPersona: 'Persona sugerida',
            askForseti: 'Pregúntale a Forseti',
            runWorkflow: 'Ejecutar un flujo de trabajo',
            viewLineage: 'Ver linaje de auditoría',
            createPdplEvidencePack: 'Crear paquete de evidencias PDPL',
            prepareBreachDraft: 'Preparar borrador de notificación',
            primaryActions: {
                projectSummary: 'Resumir documento del proyecto',
                supplementSummary: 'Resumir solicitud suplementaria',
                authoritySummary: 'Resumir solicitud de autoridad',
                rightsRequestResponse: 'Redactar respuesta DSR',
                dpiaQuickReview: 'Ejecutar revisión rápida de DPIA',
                taskControlWalkthrough: 'Recorrer evidencia de control',
                vendorRiskNarrative: 'Generar narrativa de riesgo del proveedor',
                incidentLetter: 'Redactar carta de brecha PDPL',
                ropaSufficiency: 'Ejecutar comprobación de suficiencia RoPA',
                vendorDpaReview: 'Ejecutar revisión DPA del proveedor',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti solo prepara contenido preliminar para el Mẫu số 08 vinculado. No decide si la notificación es obligatoria ni presenta o envía el formulario.',
            breachDraftReady: 'Borrador del Mẫu số 08 listo para revisión',
            breachDraftReviewBeforeSelecting: 'Revisar antes de seleccionar',
            workflowHeading: 'Flujos de trabajo relevantes',
            workflowLoading: 'Cargando flujos de trabajo relevantes…',
            workflowEmpty: 'No hay flujos de trabajo relevantes disponibles para este registro.',
            workflowError: 'No se pudieron cargar los flujos de trabajo relevantes.',
            selectWorkflow: 'Seleccionar {title}',
            workflowSelected: 'Flujo de trabajo seleccionado. El ejecutor integrado se abre en el paso siguiente.',
            workflowRunSelected: 'Ejecutar el flujo de trabajo seleccionado',
            workflowStarting: 'Iniciando el flujo de trabajo…',
            workflowRunError: 'No se pudo iniciar este flujo de trabajo.',
            workflowCreateGuided: 'Crear un flujo de trabajo guiado para este registro',
            lineageHeading: 'Linaje de auditoría de este registro',
            lineageLoading: 'Cargando linaje de auditoría…',
            lineageEmpty: 'No hay eventos de linaje de Forseti registrados para este registro.',
            lineageError: 'No se pudo cargar el linaje de auditoría.',
            retry: 'Reintentar',
            chatContextLoading: 'Cargando el contexto del registro…',
            chatGuidance: {
                general: 'Forseti puede resumir este registro, detectar información que falta y sugerir pasos de revisión.',
                privacy: 'Forseti puede revisar los hechos de privacidad, comprobar las obligaciones de los marcos instalados y ayudar a preparar un borrador para revisión humana.',
                compliance: 'Forseti puede detectar brechas de cumplimiento, fundamentar los requisitos en fuentes instaladas y sugerir próximos pasos con responsables claros.',
                legal: 'Forseti puede resumir el registro, localizar fuentes fundamentadas y ayudar a preparar un borrador para revisión jurídica.',
                risk: 'Forseti puede evaluar los riesgos registrados, señalar los hechos que faltan y sugerir pasos de revisión proporcionales.',
                audit: 'Forseti puede examinar brechas de evidencia, relacionar el registro con los controles instalados y sugerir un seguimiento listo para auditoría.',
            },
            starterPrompts: {
                identifyGaps: '¿Qué información o evidencia falta en este registro?',
                reviewFrameworks: '¿Qué obligaciones de nuestros marcos instalados debo revisar para este registro?',
                prepareDraft: 'Ayúdeme a preparar un borrador basado únicamente en este registro y en fuentes instaladas debidamente fundamentadas.',
                nextSteps: '¿Qué pasos de revisión debo seguir a continuación?',
                summarizeRecord: 'Resuma este registro y señale los puntos que requieren revisión humana.',
                assessRisk: 'Evalúe los riesgos registrados, detecte los hechos que faltan y sugiera pasos de revisión.',
            },
            disclaimer: 'Forseti respalda su criterio; no reemplaza a los abogados autorizados.',
            recordType: {
                project: 'Proyecto',
                dpia: 'DPIA',
                dossier_filing: 'Presentación de expediente',
                transfer_assessment: 'Transferencia transfronteriza',
                vendor: 'vendedor',
                vendor_contract: 'Contrato de vendedor',
                vendor_acceptance: 'Aceptación del riesgo del proveedor',
                incident: 'Incidente',
                incident_op: 'Operación de incidente',
                breach_notification: 'Notificación de infracción',
                authority_request: 'Solicitud de autoridad',
                supplement_request: 'Solicitud de suplemento',
                rights_request: 'Solicitud de derechos',
                contract: 'Contrato',
                task: 'Tarea',
                data_mapping_activity: 'Actividad de RoPA',
                compliance_rule: 'Regla de cumplimiento',
                program: 'Programa',
                governance_document: 'Documento de gobernanza',
                remediation_plan: 'Plan de remediación',
                cross_border_filing: 'Presentación transfronteriza',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Pídale a Forseti que redacte este',
            drafting: 'Redacción…',
            hint_pick_activity: 'Elija una actividad de procesamiento vinculada para habilitar la redacción de Forseti.',
            draft_origin_label: 'Borrador de Forseti: revisión por campo',
            tokens_label: '≈ {count} tokens de tu depósito mensual',
            accept_selected: 'Aceptar seleccionado',
            reject_all: 'Rechazar borrador',
            field_checkbox_label: 'Aplicar borrador de Forseti para {field}',
            field_labels: {
                description: 'Descripción',
                purpose: 'Propósito',
                data_subjects: 'Interesados',
                data_types: 'tipos de datos',
                necessity_justification: 'Necesidad y proporcionalidad',
                consultation_notes: 'Notas de consulta',
                dpo_opinion: 'DPO opinión',
                system_ids: 'Sistemas vinculados',
                vendor_ids: 'Proveedores vinculados',
            },
            error_generic: 'Forseti no pudo redactar este DPIA. Inténtelo de nuevo o refine el registro de actividad.',
            variant_legend: 'Marco marco',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'No hay ningún marco instalado disponible',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Aviso: {count} conflictos entre marcos se aplican a esta variante. Revisar antes de aceptar.',
        },
        formDraft: {
            button_label: 'Pedir a Forseti que redacte este formulario',
            drafting: 'Redactando…',
            draft_origin_label: 'Borrador de Forseti: revise cada campo',
            tokens_label: '≈ {count} tokens de su cupo mensual',
            accept_selected: 'Aceptar seleccionados',
            reject_all: 'Rechazar borrador',
            field_checkbox_label: 'Aplicar el borrador de Forseti para {field}',
            excluded_note: 'No se pueden redactar {count} campo(s) (referencias, adjuntos o solo lectura).',
            error_generic: 'Forseti no pudo redactar este formulario. Inténtelo de nuevo.',
            empty_draft: 'Forseti no generó borradores de campo para este formulario.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Pídale a Forseti que redacte este TIA',
            drafting: 'Redacción TIA…',
            hint_pick_destination: 'Establezca el país de destino en el registro de transferencia antes de que Forseti pueda realizar el giro.',
            draft_origin_label: 'Borrador de Forseti TIA: revisión por campo',
            tokens_label: '≈ {count} tokens de tu depósito mensual',
            accept_selected: 'Aceptar seleccionado',
            reject_all: 'Rechazar borrador',
            field_checkbox_label: 'Aplicar borrador de Forseti para {field}',
            error_generic: 'Forseti no pudo redactar este TIA. Inténtelo de nuevo o refine el registro de transferencia.',
            usage_details: 'Detalles de uso de la IA',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Actualizaciones de registro recomendadas',
            mechanism_recommendation: 'Mecanismo de transferencia: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Establezca un plazo de revisión antes de actualizar esta transferencia sensible.',
            processing_activity_warning: 'Vincule al menos una actividad de tratamiento / registro ROPA antes de completar la TIA.',
            go_to_field: 'Ir al campo',
            adequacy_label: 'Régimen de destino',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Atajo transitorio/EEE',
            adequacy_status_unknown: 'Desconocido: establecer destino',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Aviso: {count} conflictos entre marcos se aplican a este régimen de transferencia. Revisar antes de aceptar.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Datos obligatorios que faltan',
            missing_facts_hint: 'Forseti detectó datos que no figuran en el registro. Los campos que dependen de ellos no se preseleccionan: aporte los datos o acepte el campo de forma deliberada.',
            risk_label: 'Riesgo calibrado',
            risk_low: 'Bajo',
            risk_medium: 'Medio',
            risk_high: 'Alto',
            jurisdiction_label: 'Desencadenantes de jurisdicción',
            jurisdiction_none_installed: 'Ningún marco instalado cubre esta evaluación: no se evaluó ningún régimen jurídico. Instale un paquete normativo adecuado antes de basarse en las conclusiones jurídicas de este borrador.',
            jurisdiction_none_assessed: 'Forseti no devolvió ninguna evaluación de jurisdicción. No se evaluó nada: esto no es una conclusión de que no se aplique ningún régimen.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Cargando los nombres de los regímenes evaluados…',
            trigger_applicable: 'Aplica',
            trigger_not_applicable: 'No activado',
            trigger_unknown: 'Desconocido — falta el dato',
            recipient_role_label: 'Rol del destinatario',
            recipient_role_controller: 'Responsable del tratamiento',
            recipient_role_processor: 'Encargado del tratamiento',
            recipient_role_joint_controller: 'Corresponsable',
            recipient_role_unknown: 'Desconocido — no indicado',
            opt_in_hint: 'Conclusión jurídica: actívela solo si la acepta con los datos disponibles.',
        },
        mcp: {
            pageTitle: 'Teclas del conector MCP',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Emitir nueva clave',
            issueModalTitle: 'Emitir clave MCP API',
            labelField: 'Etiqueta',
            labelHint: 'Una breve descripción (por ejemplo, "AI laboratorio de capacidad - investigación regulatoria").',
            permittedToolsField: 'Herramientas permitidas',
            expiresAtField: 'Caduca (opcional)',
            expiresAtHint: 'Déjelo en blanco para decir que nunca caduca.',
            submit: 'Clave de problema',
            cancel: 'Cancelar',
            keyDisplayedOnce: 'Copie esta clave ahora; NO podrá volver a verla. Guárdelo en su administrador secreto (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'Lo copié - cerrar',
            colLabel: 'Etiqueta',
            colPrefix: 'Prefijo',
            colStatus: 'Estado',
            colTools: 'Herramientas',
            colCreatedAt: 'Creado',
            colLastUsed: 'último usado',
            colCalls: 'llamadas',
            colActions: 'Acciones',
            statusActive: 'Activo',
            statusRevoked: 'Revocado',
            statusExpired: 'Caducado',
            revokeAction: 'Revocar',
            rotateAction: 'rotar',
            confirmRevokeTitle: '¿Revocar esta clave MCP?',
            confirmRevokeBody: 'Los clientes externos de Claude que utilicen esta clave perderán el acceso inmediatamente. Esto no se puede deshacer.',
            confirmRevoke: 'Revocar clave',
            rotatedTitle: 'Llave girada',
            rotatedBody: 'La nueva clave de texto sin formato se encuentra a continuación. La clave anterior continúa funcionando durante 5 minutos para que finalicen las llamadas durante el vuelo.',
            emptyState: 'Aún no se han emitido claves MCP.',
            errorIssue: 'No se pudo emitir la clave MCP.',
            errorList: 'No se pudieron cargar las claves MCP.',
            errorRevoke: 'No se pudo revocar la clave.',
            errorRotate: 'No se pudo girar la llave.',
            neverUsed: '—',
            neverExpires: 'nunca',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'memoria forseti',
            pageSubtitle: 'Explore y seleccione el contexto específico de la organización que Forseti ha acumulado a través de conversaciones y decisiones. Las entradas seleccionadas se bloquean contra la retención nocturna y permanecen disponibles para todas las personas.',
            filterCategory: 'categoría',
            filterAllCategories: 'Todas las categorias',
            totalCount: '{count} entradas',
            colAnchor: 'ancla',
            colCategory: 'categoría',
            colSource: 'Fuente',
            colContent: 'Contenido',
            colCreatedAt: 'Creado',
            colActions: 'Acciones',
            editButton: 'Editar',
            deleteButton: 'Eliminar',
            promoteButton: 'Promocionar a curado',
            emptyState: 'Aún no hay recuerdos: Forseti acumulará contexto mientras chateas.',
            categories: {
                framework_interpretation: 'Interpretación del marco',
                process_preference: 'Preferencia de proceso',
                escalation_threshold: 'Umbral de escalada',
                decision_context: 'Contexto de decisión',
                glossary_term: 'Término del glosario',
                auto_inferred: 'Autoinferido',
            },
            sources: {
                admin_curated: 'Curado',
                user_explicit: 'Explícito',
                auto: 'inferido',
            },
            deleteConfirmTitle: '¿Eliminar este recuerdo?',
            deleteConfirmBody: 'Al eliminarlo, se eliminará este ancla de cada inicio futuro del chat de Forseti. Ancla: {anchor}',
            deleteConfirmLabel: 'Eliminar',
            deleteCancelLabel: 'Cancelar',
            editModalTitle: 'Editar memoria',
            editAnchorLabel: 'ancla',
            editCategoryLabel: 'categoría',
            editContentLabel: 'Contenido',
            editContentCount: '{n}/{max} caracteres',
            editEmptyError: 'El contenido no puede estar vacío.',
            editTooLongError: 'El contenido debe tener ≤1000 caracteres.',
            editAutoPromoteHint: 'Al guardar esta memoria autoinferida, se marcará como confirmada por el usuario y se eliminará del barrido de retención.',
            editCancelButton: 'Cancelar',
            editSaveButton: 'Guardar',
            editSaving: 'Guardando…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Linaje de auditoría',
            subtitle: 'Rastree cada artefacto generado por AI adjunto a un registro. Chats, llamadas de herramientas, tareas, borradores de evidencia y versiones de documentos.',
            filterForm: 'Filtros',
            recordType: 'Tipo de registro',
            recordId: 'ID de registro',
            dateStart: 'De',
            dateEnd: 'a',
            search: 'Buscar',
            loading: 'Cargando…',
            exportPdf: 'Exportar PDF',
            exportCsv: 'Exportar CSV',
            nodesHeading: 'Nodos de linaje',
            colType: 'Tipo',
            colLabel: 'Etiqueta',
            colOccurredAt: 'Ocurrió en',
            colActor: 'Actor',
            colId: 'identificación',
            noResults: 'No hay actividad de asesor para este registro.',
            draftBadge: 'AI-borrador generado',
            prev: 'Anterior',
            next: 'Siguiente',
            viewToggleTable: 'Ver como tabla',
            viewToggleGraph: 'Ver como gráfico',
            graphAriaLabel: 'Gráfico de linaje (visual). Utilice la vista de tabla para tecnología de asistencia.',
            graphLegendDraft: 'Borrador de AI evidencia',
            graphEmpty: 'No hay nodos para graficar.',
            paginationAriaLabel: 'Paginación',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Error al exportar: {message}',
            truncatedNotice: 'Este linaje no está completo. La plataforma lee como máximo un número fijo de filas de cada fuente y se alcanzó el límite para: {sources}. Las filas más allá de un límite, si las hay, no se muestran. Acote el intervalo de fechas para incluirlas.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Proyecto',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'vendedor',
                incident: 'Incidente',
                incident_op: 'Operación de incidente',
                rights_request: 'Solicitud de derechos',
                contract: 'Contrato',
                task: 'Tarea',
                data_mapping_activity: 'Actividad de mapeo de datos',
                compliance_rule: 'Regla de cumplimiento',
                program: 'Programa',
            },
            nodeTypes: {
                chat: 'Charla',
                tool_call: 'llamada de herramienta',
                workflow_run: 'Ejecución del flujo de trabajo',
                task: 'Tarea',
                evidence: 'evidencia',
                document_version: 'Versión del documento',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'bien',
                drifted: 'a la deriva',
                hash_mismatch: 'No coincide el hash',
                merkle_mismatch: 'Merkle no coincide',
                source_missing: 'Falta la fuente',
                not_found: 'No encontrado',
                chain_unreachable: 'Cadena inalcanzable',
                service_unreachable: 'Servicio inalcanzable',
                pending: 'Pendiente de fondear',
            },
        },
        evidenceDraft: {
            draftBadge: 'BORRADOR generado por AI',
            pendingReviewBadge: 'Pendiente de revisión',
            approvedBadge: 'Aprobado',
            rejectedBadge: 'Rechazado',
            moveToReviewButton: 'Marcar listo para revisión',
            approveButton: 'Aprobar',
            rejectButton: 'Rechazar',
            rejectReasonLabel: 'Motivo del rechazo',
            humanSignoffBanner: 'Forseti respalda su criterio; no reemplaza a los abogados autorizados. La evidencia generada por AI requiere la aprobación humana antes de que cuente como evidencia formal.',
            pageTitle: 'AI borradores de evidencia',
            pageSubtitle: 'Revisar, aprobar o rechazar la evidencia generada por AI. Los borradores deben ser aprobados por un humano antes de que cuenten como evidencia formal.',
            filterAll: 'Todo procesable',
            filterDrafts: 'Solo borradores',
            filterPending: 'Pendiente de revisión',
            filterApproved: 'Aprobado recientemente',
            filterRejected: 'Rechazado recientemente',
            colId: 'Identificación de evidencia',
            colStatus: 'Estado',
            colSource: 'Fuente',
            colCreatedAt: 'Creado',
            colActions: 'Acciones',
            empty: 'No hay AI borradores de evidencia en este estado.',
            confirmApprove: '¿Aprobar esta evidencia generada por AI como formal?',
            confirmReject: 'Rechace este borrador de evidencia. Esto no se puede deshacer.',
            reasonRequired: 'Se requiere un motivo de rechazo.',
            rejectModalTitle: 'Rechazar borrador de evidencia',
            approveModalTitle: 'Aprobar borrador de evidencia',
            operationFailed: 'La operación falló. Por favor inténtalo de nuevo.',
            moveToReviewSuccess: 'Marcado listo para revisión.',
            approveSuccess: 'Aprobado.',
            rejectSuccess: 'Rechazado.',
            sourceLabelWorkflowRunTitle: 'Flujo de trabajo: {title}',
            sourceLabelWorkflowRunId: 'Ejecución del flujo de trabajo {id}',
            sourceLabelChatTitle: 'Charla: {title}',
            sourceLabelChatId: 'Chat forseti {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Estado del filtro',
        },
        workflows: {
            pageTitle: 'Flujos de trabajo de IA',
            pageSubtitle: 'Plantillas de prompts de IA guardadas que producen tareas y borradores de evidencias.',
            newWorkflow: 'Nuevo flujo de trabajo',
            newWorkflowHeading: 'Nuevo flujo de trabajo de IA',
            tabAll: 'Todos',
            tabBuiltin: 'Paquete integrado',
            tabMine: 'Mis flujos',
            tabDetail: 'Detalles',
            tabLinkedTasks: 'Tareas vinculadas',
            builtinBadge: 'Integrado',
            builtinSubtitle: 'Flujo de trabajo integrado.',
            userSubtitle: 'Flujo de trabajo personalizado.',
            emptyHint: 'Aún no hay flujos de trabajo.',
            loadTimeoutError: 'No se pudieron cargar los flujos de trabajo después de 10 segundos. Reintente, o revise la pestaña de Red del navegador para una solicitud colgada.',
            loadError: 'No se pudieron cargar los flujos de trabajo.',
            hideError: 'Error al ocultar.',
            unhideError: 'Error al mostrar.',
            retry: 'Reintentar',
            producesTasks: 'Produce tareas',
            producesEvidence: 'Produce borradores de evidencia',
            hideButton: 'Ocultar de mi lista',
            unhideButton: 'Mostrar',
            runButton: 'Ejecutar',
            editButton: 'Editar',
            shareButton: 'Compartir',
            backToList: 'Volver a la lista',
            requireTitleAndPrompt: 'Se requieren el título y el prompt.',
            fieldTitle: 'Título',
            fieldDescription: 'Descripción',
            fieldType: 'Tipo',
            fieldPersona: 'Afinidad de persona',
            fieldPractice: 'Práctica',
            fieldPrompt: 'Aviso (rebaja)',
            fieldProducesTasks: 'Produce tareas',
            fieldProducesEvidence: 'Produce borradores de evidencia',
            fieldProjectId: 'Proyecto (opcional)',
            fieldAttachmentIds: 'Adjuntos',
            fieldParentRecord: 'Registro padre (opcional)',
            fieldPersonaOverride: 'Anulación de persona (opcional)',
            typeAssistant: 'Asistente',
            typeTabular: 'tabular',
            personaNone: '— ninguno —',
            parentNone: '— ninguno —',
            parentIdPlaceholder: 'ID de registro padre',
            selectProjectPlaceholder: 'Seleccionar un proyecto…',
            selectProjectFirst: 'Seleccione un proyecto para elegir adjuntos.',
            selectAttachmentsPlaceholder: 'Seleccionar documentos…',
            noAttachmentsInProject: 'Este proyecto no tiene documentos adjuntos.',
            selectParentRecordPlaceholder: 'Seleccionar un registro…',
            selectParentRecordFirst: 'Seleccione primero un tipo de registro.',
            noRecordsForType: 'No hay registros de este tipo disponibles.',
            shareUserSelectorLabel: 'Destinatarios (usuarios)',
            shareGroupSelectorLabel: 'Destinatarios (grupos de organización)',
            selectUsersPlaceholder: 'Seleccionar usuarios…',
            selectOrgGroupsPlaceholder: 'Seleccionar grupos de organización…',
            loadingOptions: 'Cargando…',
            loadFailed: 'Error al cargar opciones.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Proveedor',
                incident: 'Incidente',
                rights_request: 'Solicitud de derechos',
                contract: 'Contrato',
                data_mapping_activity: 'Actividad RoPA',
                compliance_rule: 'Regla de cumplimiento',
                program: 'Programa',
            },
            cancelButton: 'Cancelar',
            closeButton: 'Cerrar',
            createButton: 'Crear',
            creating: 'Creando…',
            saveButton: 'Guardar',
            saving: 'Guardando…',
            shareWorkflowHeading: 'Compartir flujo de trabajo',
            shareIntro: 'Compartiendo: {title}',
            shareUserIds: 'IDs de usuario (separados por espacio o coma)',
            shareGroupIds: 'IDs de grupo de organización (separados por espacio o coma)',
            shareAllowEdit: 'Permitir a los destinatarios editar',
            shareRequireRecipient: 'Proporcione al menos un id de usuario o de grupo.',
            shareSuccess: 'Compartido.',
            sharing: 'Compartiendo…',
            runWorkflowHeading: 'Ejecutar: {title}',
            runStarting: 'Iniciando…',
            runStatusLabel: 'Estado: {status}',
            runProducedCounts: 'Tareas: {tasks} · Borradores de evidencia: {evidence}',
            runPanelHeading: 'Ejecución del flujo de trabajo',
            runPanelHeadingWithTitle: 'Ejecución del flujo de trabajo: {title}',
            runViewTasks: 'Ver las tareas generadas',
            runViewEvidence: 'Ver evidencia en borrador',
            runResultHeading: 'Resultado del asesor',
            runNoResult: 'La ejecución finalizó pero no devolvió un resultado revisable. No se crearon tareas, evidencias ni adjuntos. Vuelve a intentarlo con el registro o los documentos requeridos seleccionados.',
            runCitationWarning: 'Algunas citas no se pudieron verificar automáticamente. Revísalas antes de confiar en este resultado.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'No se pudo cargar la ejecución del flujo de trabajo.',
            runStreamFailed: 'Falló la conexión con el progreso del flujo de trabajo.',
            runStatusValues: {
                pending: 'Pendiente',
                running: 'En ejecución',
                completed: 'Completado',
                succeeded: 'Exitoso',
                failed: 'Fallido',
                cancelled: 'Cancelado',
            },
            runProgressStatus: 'Estado: {status}',
            runProgressTaskCreated: 'Tarea creada.',
            runProgressEvidenceDrafted: 'Borrador de evidencia creado.',
            runProgressAttachmentGenerated: 'Archivo adjunto generado.',
            guidedWorkflowTitle: 'Flujo de trabajo guiado para {recordType}',
            guidedWorkflowDescription: 'Creado para registros de tipo {recordType}. Revise y personalice este flujo de trabajo antes de guardarlo.',
            guidedWorkflowPrompt: 'Revise el registro de tipo {recordType} proporcionado, detecte los hechos que faltan y proponga próximos pasos fundamentados para revisión humana.',
            promptHeading: 'rápido',
            howItWorksHeading: 'Qué ocurre cuando ejecutas este flujo de trabajo',
            builtinGuidance: 'Forseti revisa los registros y documentos que selecciones, devuelve un resultado revisable por el operador y solo crea tareas o evidencias en borrador cuando el resultado lo justifica. Nada se aprueba ni se envía automáticamente.',
            editWorkflowHeading: 'Editar flujo de trabajo',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Flujo de trabajo no encontrado.',
            schedules: {
                tabTitle: 'Programaciones',
                sectionHeading: 'Ejecuciones programadas',
                empty: 'Aún no hay programaciones. Cree una para ejecutar este flujo de trabajo automáticamente.',
                addButton: 'Añadir programación',
                addHeading: 'Programar flujo de trabajo',
                editHeading: 'Editar programación',
                fieldFrequency: 'Frecuencia',
                fieldDayOfWeek: 'Día de la semana',
                fieldDayOfMonth: 'Día del mes',
                fieldTime: 'Hora (UTC)',
                fieldTimezone: 'Zona horaria',
                fieldProject: 'Proyecto (opcional)',
                fieldAttachments: 'Adjuntos por defecto',
                fieldPersonaOverride: 'Anulación de persona (opcional)',
                frequencyDaily: 'Diario',
                frequencyWeekly: 'Semanal',
                frequencyMonthly: 'Mensual',
                weekdaySun: 'Domingo',
                weekdayMon: 'Lunes',
                weekdayTue: 'Martes',
                weekdayWed: 'Miércoles',
                weekdayThu: 'Jueves',
                weekdayFri: 'Viernes',
                weekdaySat: 'Sábado',
                statusActive: 'Activo',
                statusPaused: 'En pausa',
                nextRunLabel: 'Próxima ejecución',
                lastRunLabel: 'Última ejecución',
                pauseButton: 'Pausar',
                resumeButton: 'Reanudar',
                deleteButton: 'Eliminar',
                triggerButton: 'Ejecutar ahora',
                savingButton: 'Guardando…',
                saveButton: 'Guardar',
                triggered: 'Ejecución iniciada — revise el historial de ejecuciones.',
            },
            branching: {
                helpHeading: 'Sintaxis de prompt condicional',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Conflictos entre marcos',
            pageSubtitle: 'Catálogo seleccionado de dónde interactúan sus paquetes regulatorios instalados de maneras contradictorias. Superficies Forseti; los humanos deciden.',
            noInstalledPacks: 'No hay paquetes regulatorios instalados para esta organización: instale al menos dos paquetes para sacar a la luz los conflictos entre marcos.',
            noConflicts: 'Ningún conflicto coincide con los filtros actuales.',
            allAcknowledged: 'Todos los conflictos visibles han sido reconocidos. Buen trabajo.',
            columnSeverity: 'Gravedad',
            columnTitle: 'Título',
            columnFrameworks: 'Marcos',
            columnTopic: 'Tema',
            columnStatus: 'Estado',
            filterFrameworkPlaceholder: 'Todos los marcos',
            filterTopicPlaceholder: 'Todos los temas',
            filterSeverityPlaceholder: 'Todas las gravedades',
            filterStatusPlaceholder: 'Todos los estados',
            filterStatusAcknowledged: 'Reconocido',
            filterStatusUnacknowledged: 'No reconocido',
            severityCritical: 'Crítico',
            severityHigh: 'Alto',
            severityMedium: 'Medio',
            severityLow: 'Bajo',
            statusAcknowledged: 'Reconocido',
            statusInReview: 'En revisión',
            statusResolved: 'Resuelto',
            statusWontFix: 'no arreglará',
            statusUnacknowledged: 'No reconocido',
            drawerSummaryHeading: 'Resumen',
            drawerSideAHeading: 'Lado A',
            drawerSideBHeading: 'Lado B',
            drawerRequirementsLabel: 'Requisitos citados',
            drawerProvisionsLabel: 'Disposiciones citadas',
            drawerSuggestedResolutionHeading: 'Resolución sugerida',
            drawerLegalCounselBadge: 'Se requiere revisión del asesor legal',
            drawerAckHeading: 'Reconocimiento',
            drawerAckStatusLabel: 'Estado',
            drawerAckAssigneeLabel: 'Asignado a',
            drawerAckDueDateLabel: 'fecha de vencimiento',
            drawerAckNotesLabel: 'Notas',
            drawerAckButtonAcknowledge: 'Reconocer',
            drawerAckButtonUpdate: 'Actualizar',
            drawerAckButtonUnacknowledge: 'Desconocer',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Buscar título o resumen…',
            searchButton: 'Buscar',
            searchButtonAriaLabel: 'Ejecutar búsqueda de conflictos',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Error al exportar: {message}',
            exportAriaLabel: 'Conflictos de exportación',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Revisiones tabulares',
            pageSubtitle: 'Compare N documentos a través de M atributos con citas fundamentadas.',
            newReview: 'Nueva revisión',
            newReviewHeading: 'Nueva revisión tabular',
            emptyHint: 'Aún no hay revisiones tabulares.',
            docsLabel: '{n} documentos',
            colsLabel: '{n} columnas',
            detailSubtitle: '{docs} documentos · {cols} columnas',
            addColumn: 'Añadir columna',
            addColumnHeading: 'Añadir columna',
            columnInfo: 'Ver indicación y formato',
            columnInfoAria: 'Mostrar la indicación y el formato de la columna {name}',
            columnInfoPrompt: 'Indicación',
            columnInfoFormat: 'Formato de retorno',
            columnInfoPresets: 'Valores permitidos',
            columnInfoCitations: 'Citas',
            columnInfoCitationsRequired: 'Obligatorio',
            removeColumn: 'Eliminar columna',
            removeColumnAria: 'Eliminar columna {name}',
            removeColumnConfirm: '¿Eliminar la columna "{name}"? Se eliminarán sus celdas extraídas.',
            deleteReview: 'Eliminar',
            deleteReviewAria: 'Eliminar revisión {title}',
            deleteReviewConfirm: '¿Eliminar la revisión "{title}"? Esto elimina permanentemente sus columnas, celdas y debates.',
            runAll: 'Ejecutar todos',
            running: 'Ejecutando…',
            cancel: 'Cancelar',
            exportXlsx: 'Exportar Excel',
            exportCsv: 'Exportar CSV',
            exporting: 'Exportando…',
            backToList: 'Volver a la lista',
            notFound: 'Revisión tabular no encontrada.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Adjunte documentos para iniciar la revisión.',
            noColumns: 'Añada columnas para definir qué extraer.',
            tableAriaLabel: 'Cuadrícula de extracción',
            threadsCountAria: '{n} publicación(es) de discusión',
            startDiscussionAria: 'Iniciar discusión',
            docColumn: 'Documento',
            statusPending: 'Pendiente',
            statusRunning: 'Ejecutando…',
            statusFailed: 'Fallido: {msg}',
            statusCancelled: 'Cancelado',
            citationsCount: '{n} cita(s)',
            citationsView: 'Ver pasajes de origen',
            citationPage: 'Página {n}',
            titleRequired: 'Se requiere un título.',
            fieldTitle: 'Título',
            fieldProjectId: 'Proyecto (opcional)',
            fieldAttachmentIds: 'Adjuntos',
            fieldColumnName: 'Nombre de columna',
            fieldColumnNameHelp: 'Una etiqueta corta y descriptiva para esta columna en la cuadrícula de extracción.',
            columnNamePlaceholder: 'p. ej. Fecha de inicio del contrato',
            fieldExtractionPrompt: 'Prompt de extracción',
            fieldExtractionPromptHelp: 'Describa la información que desea extraer. Sea específico para obtener mejores resultados.',
            columnPromptPlaceholder: '¿Qué debería buscar Forseti en los documentos?',
            fieldReturnFormat: 'Formato de retorno',
            fieldReturnFormatHelp: 'Controla cómo la IA formatea su respuesta (por ejemplo, como fecha o número).',
            fieldPresets: 'Valores permitidos (uno por línea)',
            fieldPresetsPlaceholder: 'sí\nno\nparcial',
            fieldCitationsRequired: 'Cita obligatoria',
            formatShortText: 'Texto corto',
            formatLongText: 'Texto largo',
            formatEnum: 'enumeración',
            formatNumber: 'Número',
            formatDate: 'Fecha',
            formatList: 'Lista',
            columnRequireFields: 'Se requieren el nombre y el prompt.',
            addColumnError: 'Error al añadir la columna.',
            cancelButton: 'Cancelar',
            createButton: 'Crear',
            creating: 'Creando…',
            addButton: 'Añadir',
            adding: 'Añadiendo…',
            runConfirmHeading: 'Ejecutar extracción',
            estimateBody: 'Estimación de ~{units} unidades facturables ({confidence} confianza).',
            starting: 'Iniciando…',
            confirmRun: 'Confirmar ejecución',
            charts: {
                title: 'Gráficos de columna',
                tabsAria: 'Columnas de gráficos',
                enumAria: 'Distribución de valores enum',
                numberAria: 'Histograma de distribución numérica',
                dateAria: 'Línea de tiempo de fechas',
                enumEmpty: 'No hay celdas completadas.',
                numberEmpty: 'No hay valores numéricos.',
                numberExcluded: '{n} celda(s) excluidas (vacías / no numéricas / no completadas).',
                dateEmpty: 'No hay valores de fecha.',
                dateExcluded: '{n} celda(s) excluidas.',
                unsupportedFormat: 'Formato de columna no soportado.',
            },
            threads: {
                drawerTitle: 'Discusión',
                loading: 'Cargando…',
                empty: 'Aún no hay publicaciones. Inicie la conversación.',
                composerLabel: 'Nuevo comentario',
                composerPlaceholder: 'Añadir un comentario…',
                post: 'Publicar',
                posting: 'Publicando…',
                edit: 'Editar',
                editLabel: 'Editar publicación',
                delete: 'Eliminar',
                save: 'Guardar',
                cancel: 'Cancelar',
                close: 'Cerrar',
                editedBadge: 'editado',
                loadError: 'Error al cargar el hilo',
                postError: 'Error al publicar',
                editError: 'Error al editar',
                deleteError: 'Error al eliminar',
            },
            compare: {
                pageTitle: 'Comparar revisiones',
                leftReviewLabel: 'Revisión izquierda',
                rightReviewLabel: 'Revisión derecha',
                pickReview: 'Elija una revisión para comparar',
                noSharedDocuments: 'Estas revisiones no comparten documentos — la comparación requiere al menos un adjunto común.',
                sharedDocumentsHeading: 'Documentos compartidos',
                leftOnlyHeading: 'Columnas solo en la revisión izquierda',
                rightOnlyHeading: 'Columnas solo en la revisión derecha',
                differs: 'Difiere',
                identical: 'Idéntico',
                emptyValue: '—',
                compareButton: 'Comparar con…',
                documentHeading: 'Documento',
                errorMissingIds: 'Se requieren los ids de revisión izquierda y derecha.',
                errorComparisonFailed: 'La comparación falló.',
                errorLoadFailed: 'Error al cargar las revisiones.',
                pickerLoading: 'Cargando…',
                pickerNoOtherReviews: 'No hay otras revisiones disponibles para comparar.',
                pickerCancel: 'Cancelar',
            },
        },
        documentReview: {
            pageTitle: 'Revisión de documentos',
            pageSubtitle: 'Acepte o rechace las ediciones sugeridas por AI para este documento.',
            sidePanelLabel: 'AI líneas rojas',
            sidePanelHeading: 'AI líneas rojas',
            noPendingEdits: 'No hay ediciones pendientes.',
            pendingCount: '{count} pendiente',
            acceptAllButton: 'aceptar todo',
            rejectAllButton: 'Rechazar todo',
            acceptAllTitle: '¿Aceptar todas las ediciones pendientes?',
            acceptAllConfirm: 'Esto aceptará {count} ediciones pendientes y creará una o más versiones nuevas. ¿Continuar?',
            editsHeading: 'Ediciones',
            editsEmpty: 'No hay ediciones para revisar.',
            versionsHeading: 'Historial de versiones',
            versionsEmpty: 'Aún no hay versiones.',
            editLabel: 'Editar',
            editLabelWithId: 'Editar #{id}',
            acceptButton: 'Aceptar',
            rejectButton: 'Rechazar',
            accepting: 'Aceptando…',
            rejecting: 'Rechazando…',
            diffDeletedAria: 'Eliminado',
            diffInsertedAria: 'insertado',
            statusPending: 'Pendiente',
            statusAccepted: 'Aceptado',
            statusRejected: 'Rechazado',
            versionLabel: 'Versión',
            versionAriaLabel: 'Versión {n}',
            versionAriaLabelCurrent: 'Versión {n} ({current})',
            versionCurrentTag: 'actual',
            versionSourceLabel: 'Fuente',
            versionCreatedLabel: 'Creado',
            versionModelLabel: 'modelo',
            sourceUpload: 'Subir',
            sourceUserUpload: 'Carga de usuario',
            sourceAssistantEdit: 'Asistente de edición',
            sourceUserAccept: 'Edición aceptada por el usuario',
            sourceUserReject: 'El usuario rechazó la edición',
            sourceGenerated: 'generado',
            extractionExtracted: 'Texto extraído',
            extractionFailed: 'Error al extraer el texto',
            extractionUnsupported: 'No se pudo extraer el texto',
            extractionPages: '{n} páginas',
            extractionWords: '{n} palabras',
            extractionReasonScanned: 'Esto parece un documento escaneado o solo de imágenes. Forseti aún no puede leerlo: suba un archivo de texto o pegue el contenido directamente.',
            extractionReasonEncrypted: 'El archivo está protegido con contraseña. Quite la protección y vuelva a subirlo.',
            extractionReasonMalformed: 'No se pudo analizar el archivo y puede estar dañado.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'El archivo es demasiado grande para procesarlo.',
            extractionReasonTimeout: 'El procesamiento tardó demasiado. Pruebe con un archivo más pequeño o sencillo.',
            extractionReasonUnknown: 'No hay más detalles disponibles.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Texto del documento',
            textPaneTitle: 'Texto del documento',
            textPaneVersion: 'Versión {n}',
            textPaneEmpty: 'No hay texto disponible para esta versión. Los PDF escaneados y algunos tipos de documento no tienen texto extraíble.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Revisión de documentos',
            pageTitle: 'Revisión de documentos',
            pageSubtitle: 'Documentos en su organización que tienen ediciones sugeridas por IA pendientes de revisión o versiones de origen Forseti.',
            colDocument: 'Documento',
            colLinkedRecord: 'Registro vinculado',
            colPendingEdits: 'Ediciones pendientes',
            colLastActivity: 'Última actividad',
            colOpen: 'Abrir',
            open: 'Abrir',
            empty: 'Aún no hay documentos en revisión por Forseti. Los documentos entran en esta cola cuando Forseti sugiere una edición durante un chat, cuando un flujo de IA produce una marca de revisión, o cuando envía un documento a revisión usando el botón superior.',
            loading: 'Cargando…',
            loadError: 'Error al cargar los documentos en revisión.',
            retryButton: 'Reintentar',
            noLinkedRecord: '—',
            pendingZero: 'Ninguno',
            sendForReviewButton: 'Enviar un documento para revisión',
            sendForReviewTitle: 'Enviar un documento para revisión',
            sendForReviewSubtitle: 'Suba un documento .txt, .md, .pdf o .docx (hasta 50 MB). Se añade a la cola de revisión y Forseti producirá una pasada de revisión sobre él.',
            sendForReviewTabUpload: 'Subir nuevo',
            sendForReviewPickerLabel: 'Buscar adjuntos',
            sendForReviewPickerPlaceholder: 'Escriba para buscar…',
            sendForReviewPickerEmpty: 'No hay adjuntos coincidentes. Pruebe otra búsqueda.',
            sendForReviewSubmit: 'Enviar para revisión',
            sendForReviewCancel: 'Cancelar',
            sendForReviewSubmitting: 'Enviando…',
            sendForReviewError: 'No se pudo enviar el documento para revisión. Por favor, inténtelo de nuevo.',
            sendForReviewSuccess: 'Documento enviado para revisión.',
            sendForReviewAcceptedExt: 'Aceptados: {ext}.',
            sendForReviewMaxSize: 'Tamaño máximo {mb} MB.',
        },
        chats: {
            exportError: 'La exportación falló.',
        },
    },
    pdplEvidencePacks: {
        title: 'Paquetes de pruebas PDPL',
        subtitle: 'Envío controlado por el cliente a MoPS A05',
        nav: { emailProviders: 'Proveedores de correo' },
        providers: {
            pageTitle: 'Proveedores de correo',
            pageSubtitle: 'Configure el proveedor de correo saliente de su organización para el envío de paquetes de pruebas PDPL a MoPS A05.',
            architectureRuleHeading: 'Regla de arquitectura (bloqueada)',
            architectureRuleBody: 'AesirX no es el remitente de los correos de paquete de pruebas. Cada envío utiliza un proveedor de su propiedad y bajo su control. AesirX nunca envía en su nombre.',
            empty: 'Aún no se han configurado proveedores.',
            createCta: 'Crear proveedor',
            statusPendingConsent: 'Pendiente de consentimiento',
            statusPendingConnectionCheck: 'Pendiente de prueba de conexión',
            statusActive: 'Activo',
            statusConnectionCheckFailed: 'Prueba de conexión fallida',
            statusDisabled: 'Desactivado',
            acceptConsentCta: 'Aceptar consentimiento',
            runConnectionCheckCta: 'Ejecutar prueba de conexión',
            disableCta: 'Desactivar',
            disableConfirm: 'Desactivar este proveedor detiene los envíos directos hasta que se configure un nuevo proveedor y se vuelva a confirmar el consentimiento. ¿Continuar?',
            columnDisplayName: 'Nombre',
            columnFromAddress: 'Dirección de remite',
            columnStatus: 'Estado',
            columnLastCheck: 'Última prueba',
            columnActions: 'Acciones',
            createModalTitle: 'Nuevo proveedor SMTP',
            createDisplayNameLabel: 'Nombre visible',
            createFromAddressLabel: 'Dirección de remite',
            createReplyToLabel: 'Dirección de respuesta (opcional)',
            createSmtpHostLabel: 'Host SMTP',
            createSmtpPortLabel: 'Puerto SMTP',
            createSmtpUsernameLabel: 'Usuario SMTP',
            createSmtpPasswordLabel: 'Contraseña SMTP',
            createSmtpEncryptionLabel: 'Cifrado',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implícito)',
            createSmtpEncryptionNone: 'Ninguno',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Próximamente',
            createOauthDisabledHint: 'Los proveedores OAuth aún no están habilitados. Use SMTP por ahora.',
            createSubmitCta: 'Crear proveedor',
            createCancelCta: 'Cancelar',
            createErrorInvalidFromAddress: 'La dirección de remite debe ser un buzón válido.',
            createErrorDuplicateFromAddress: 'Ya hay otro proveedor configurado para esta dirección de remite.',
            createErrorGeneric: 'No se pudo crear el proveedor. Revise el formulario e inténtelo de nuevo.',
            consentModalTitle: 'Confirmar regla de arquitectura',
            consentLocaleToggleEn: 'Inglés',
            consentLocaleToggleVi: 'Vietnamita',
            consentAcknowledgeCta: 'Confirmo — aceptar consentimiento',
            consentCancelCta: 'Cancelar',
            consentErrorMismatch: 'El texto del consentimiento se modificó y ya no coincide con el aviso canónico. Vuelva a abrir el diálogo y envíelo de nuevo.',
            testModalTitle: 'Prueba de conexión',
            testRunningCta: 'Probando conexión…',
            testRunCta: 'Ejecutar prueba de conexión',
            testRetryCta: 'Reintentar prueba',
            testSuccessHeading: 'Conexión correcta',
            testSuccessBody: 'Autenticado contra {host}:{port} en {latencyMs} ms. El proveedor está listo para envíos directos.',
            testFailureHeading: 'Conexión fallida',
            testFailureBody: 'No se pudo completar la prueba. Revise el motivo a continuación e inténtelo de nuevo tras corregirlo.',
            testFailureReasonLabel: 'Motivo del fallo',
            testCloseCta: 'Cerrar',
            detailDrawerTitle: 'Detalles del proveedor',
            detailDrawerHistoryHeading: 'Historial de pruebas de conexión',
            detailDrawerConsentHeading: 'Confirmación del consentimiento',
            detailDrawerNoHistory: 'No se han registrado pruebas de conexión todavía.',
            detailDrawerNoConsent: 'El consentimiento aún no se ha confirmado.',
            outcomeSuccess: 'Éxito',
            outcomeFailure: 'Error',
        },
        builder: {
            pageTitle: 'Ensamblar paquete de pruebas',
            pageSubtitle: 'Suba los 6 archivos de partes, elija el tipo de paquete y ensamble el ZIP determinista de 8 partes para el envío.',
            packTypeLabel: 'Tipo de paquete',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Parte 01 — Carta de presentación (PDF)',
            part02AuthorityForm: 'Parte 02 — Formulario de la autoridad (PDF)',
            part03Supporting: 'Parte 03 — Pruebas justificativas (≥1)',
            part04Safeguards: 'Parte 04 — Salvaguardas',
            part04SafeguardsRequiredOtia: '(obligatorio para OTIA)',
            part05PriorCorrespondence: 'Parte 05 — Correspondencia previa (opcional)',
            part06Signature: 'Parte 06 — Pruebas de firma y sello (≥1)',
            uploadPickFile: 'Elegir archivo',
            uploadPickFiles: 'Elegir archivos',
            uploadRemoveFile: 'Quitar',
            requiredMissing: 'Falta parte obligatoria',
            assembleCta: 'Ensamblar paquete',
            assembling: 'Ensamblando…',
            assembleSuccessHeading: 'Paquete ensamblado',
            assembleSuccessSummary: '{fileCount} archivos · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'Redactar borrador de correo',
            warningsHeading: 'Avisos de validación',
            assembleError: 'No se pudo ensamblar el paquete. Revise las entradas e inténtelo de nuevo.',
            forsetiAssistHeading: 'Asistencia IA Forseti (opcional)',
            forsetiAssistHint: 'Ejecute el PDPL Evidence Pack Assembly Advisor para resaltar las partes que faltan, redactar la prosa de la carta de presentación y señalar las brechas de salvaguarda. AesirX no es el remitente — la IA solo asesora; usted aprueba cada artefacto.',
            forsetiAssistCta: 'Abrir biblioteca de flujos Forseti',
            forsetiAiRunIdLabel: 'ID de ejecución IA Forseti vinculada (pegar para registrar la asistencia IA)',
        },
        draft: {
            pageTitle: 'Borrador de correo',
            pageSubtitle: 'Redacte y edite el borrador de correo. El envío directo llegará en una versión posterior.',
            composeCta: 'Redactar borrador',
            composing: 'Redactando…',
            noDraftYet: 'Aún no hay borrador. Redacte uno para empezar.',
            localeToggleLabel: 'Idioma',
            localeEn: 'Inglés',
            localeVi: 'Vietnamita',
            subjectLabel: 'Asunto',
            bodyLabel: 'Cuerpo',
            saveCta: 'Guardar cambios',
            saving: 'Guardando…',
            saveError: 'No se pudo guardar el borrador. Inténtelo de nuevo.',
            manifestSummaryLabel: 'Resumen del manifiesto',
            coverLetterLabel: 'Carta de presentación (PDF)',
            coverLetterDownloadCta: 'Descargar PDF',
            editHistoryHeading: 'Historial de ediciones',
            editHistoryEmpty: 'Aún no hay ediciones.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 aviso de canal no oficial',
            uploadSentProofCta: 'Subir prueba de envío manual',
            versionLabel: 'Versión',
            statusDraft: 'Borrador',
            statusEdited: 'Editado',
            statusFinalized: 'Finalizado',
        },
        sentProof: {
            modalTitle: 'Subir prueba de envío manual',
            architectureLockNote: 'AesirX no es el remitente. Esta prueba registra el envío manual que realizó a través de su propio cliente de correo.',
            fileLabel: 'Archivo de prueba (.eml, .png, .jpg)',
            envelopeFromLabel: 'De',
            envelopeToLabel: 'Para',
            envelopeSubjectLabel: 'Asunto (al enviar)',
            envelopeSentAtLabel: 'Enviado el',
            envelopeAttachmentShaLabel: 'SHA-256 del adjunto (al enviar)',
            uploadCta: 'Subir prueba',
            uploading: 'Subiendo…',
            uploadSuccess: 'Prueba de envío subida.',
            uploadErrorInvalidMime: 'Tipo de archivo no permitido. Use .eml, .png, .jpg o .jpeg.',
            uploadErrorGeneric: 'No se pudo subir la prueba. Inténtelo de nuevo.',
            existingProofsHeading: 'Pruebas de envío registradas',
            existingProofsEmpty: 'Aún no hay pruebas de envío registradas.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Paquete de pruebas',
            notFoundMessage: 'Paquete no encontrado: puede haber sido eliminado o el enlace es incorrecto.',
            manifestHeading: 'Manifiesto',
            dossierLabel: 'Expediente',
            packShaLabel: 'SHA-256 del paquete',
            fileCountLabel: 'Archivos',
            bytesLabel: 'Tamaño',
            composeDraftCta: 'Redactar borrador',
            sendComingSoonCta: 'Envío directo: próximamente',
            sendComingSoonHint: 'El flujo de envío directo está en desarrollo activo. Use "Subir prueba de envío manual" en la página del borrador hasta que se publique.',
            sendCta: 'Enviar a través del proveedor configurado',
            sendDisabledNoProviderHint: 'Configure primero un proveedor de correo controlado por el cliente. AesirX nunca envía en su nombre.',
            timelineHeading: 'Historial de envíos',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Enviar paquete de pruebas',
            providerPickerLabel: 'Proveedor de correo configurado',
            providerPickerHint: 'AesirX nunca envía en su nombre. Seleccione uno de los proveedores configurados a continuación.',
            providerPickerEmpty: 'No hay proveedores activos configurados. Configure uno en la página de Proveedores de correo antes de enviar.',
            providerPickerEmptyCta: 'Abrir Proveedores de correo',
            draftPickerLabel: 'Borrador de correo',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Todavía no se ha redactado ningún borrador para este paquete. Redacte uno primero en la página del borrador.',
            recipientToLabel: 'Para',
            recipientToHint: 'Una o más direcciones de correo electrónico, separadas por comas.',
            recipientCcLabel: 'Cc (separados por comas)',
            recipientCcHint: 'Opcional. Lista de direcciones de correo electrónico separadas por comas.',
            recipientBccLabel: 'Cco (separados por comas)',
            recipientBccHint: 'Opcional. Lista de direcciones de correo electrónico separadas por comas.',
            consentReaffirmHeading: 'Reafirmar el consentimiento del proveedor',
            submitCta: 'Enviar ahora',
            sending: 'Enviando…',
            cancelCta: 'Cancelar',
            sendSuccess: 'Envío registrado. El paquete se ha transmitido mediante el proveedor configurado.',
            sendErrorGeneric: 'No se pudo enviar el paquete. Revise el error a continuación y vuelva a intentarlo.',
            sendErrorAlreadyFinalized: 'Este borrador ya se ha utilizado para un envío exitoso. Redacte un nuevo borrador antes de volver a enviar.',
            sendErrorInvalidRecipients: 'Una o más direcciones de destinatario no son buzones de correo válidos.',
            // Timeline
            timelineEmpty: 'Todavía no se han registrado envíos. Utilice "Enviar a través del proveedor configurado" arriba para transmitir mediante su proveedor SMTP / Microsoft 365 / Google Workspace controlado por el cliente.',
            timelineProviderLabel: 'Enviado mediante el proveedor configurado',
            timelineSentAtLabel: 'Enviado',
            timelineRecipientLabel: 'Destinatario',
            timelineOutcomeSent: 'Enviado',
            timelineOutcomeFailed: 'Fallido',
            timelineViewDetailsCta: 'Ver detalles',
            // Authority-response chips
            authorityChipNone: 'A la espera de respuesta',
            authorityChipAcknowledgement: 'Acuse de recibo',
            authorityChipAcceptance: 'Aceptación',
            authorityChipRejection: 'Rechazo',
            authorityChipSupplementRequest: 'Solicitud de complemento',
            authorityChipNoResponse: 'Sin respuesta',
            // Hard-copy chips
            hardCopyChipNone: 'Sin seguimiento en papel',
            hardCopyChipPending: 'Enviado — entrega pendiente',
            hardCopyChipDelivered: 'Entregado',
            // Drawer
            drawerTitle: 'Registro de envío',
            drawerEnvelopeHeading: 'Sobre',
            drawerEnvelopeFromLabel: 'De',
            drawerEnvelopeToLabel: 'Para',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Cco',
            drawerEnvelopeSubjectLabel: 'Asunto',
            drawerEnvelopeMessageIdLabel: 'ID del mensaje',
            drawerOutcomeHeading: 'Resultado',
            drawerSnapshotHeading: 'Instantánea de la cadena de custodia',
            drawerSnapshotPackShaLabel: 'SHA-256 del paquete en el envío',
            drawerSnapshotDraftVersionLabel: 'Versión del borrador en el envío',
            drawerSnapshotAttachmentLabel: 'ZIP transmitido',
            drawerSnapshotAttachmentDownloadCta: 'Descargar ZIP',
            drawerAuthorityResponseHeading: 'Respuesta de la autoridad',
            drawerHardCopyHeading: 'Seguimiento en papel',
            drawerSentByLabel: 'Enviado por',
            drawerSendIdLabel: 'ID del registro de envío',
            drawerSmtpResponseLabel: 'Respuesta SMTP',
            drawerFailureReasonLabel: 'Motivo del fallo',
            drawerLatencyLabel: 'Latencia',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Aceptado',
            drawerRejectedLabel: 'Rechazado',
            drawerCloseCta: 'Cerrar',
            // Authority-response panel
            authorityRecordCta: 'Registrar respuesta',
            authorityReclassifyCta: 'Reclasificar',
            authorityClassificationLabel: 'Clasificación',
            authorityReceivedAtLabel: 'Recibido el',
            authorityFreeTextLabel: 'Notas (opcional)',
            authorityEvidenceLabel: 'Archivo de prueba (.eml / .pdf / .png / .jpg — opcional)',
            authorityReasonLabel: 'Motivo de la reclasificación',
            authorityHistoryHeading: 'Historial de reclasificaciones',
            authorityHistoryEmpty: 'No se han registrado reclasificaciones.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Guardar respuesta',
            authoritySubmitting: 'Guardando…',
            authorityErrorInvalidMime: 'Tipo de archivo de prueba no permitido. Utilice .eml, .pdf, .png o .jpg.',
            authorityErrorGeneric: 'No se pudo guardar la respuesta. Inténtelo de nuevo.',
            authorityRecordedAtLabel: 'Registrado el',
            authorityRecordedByLabel: 'Registrado por',
            authorityEvidenceFilenameLabel: 'Prueba',
            authorityEvidenceDownloadCta: 'Descargar evidencia',
            // Hard-copy panel
            hardCopyRecordCta: 'Registrar seguimiento en papel',
            hardCopyConfirmCta: 'Confirmar entrega',
            hardCopyEditNotesCta: 'Editar notas de entrega',
            hardCopyPostedAtLabel: 'Enviado el',
            hardCopyCourierLabel: 'Mensajería',
            hardCopyTrackingLabel: 'Número de seguimiento',
            hardCopyRecipientLabel: 'Destinatario en la etiqueta',
            hardCopyReceiptLabel: 'Prueba de recepción (.pdf / .png / .jpg — opcional)',
            hardCopyReceiptDownloadCta: 'Descargar comprobante',
            hardCopyDeliveredAtLabel: 'Entregado el',
            hardCopyDeliveryNotesLabel: 'Notas de entrega (opcional)',
            hardCopySubmitCta: 'Guardar seguimiento',
            hardCopySubmitting: 'Guardando…',
            hardCopyErrorInvalidMime: 'Tipo de archivo de recepción no permitido. Utilice .pdf, .png o .jpg.',
            hardCopyErrorGeneric: 'No se pudo guardar el seguimiento. Inténtelo de nuevo.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=es.js.map